'use client'

import { Fragment, FormEvent, createContext, memo, startTransition, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState, useSyncExternalStore, useDeferredValue } from 'react'
import { createPortal } from 'react-dom'
import Link from 'next/link'
import dynamic from 'next/dynamic'
import {
  GoogleAuthProvider,
  browserLocalPersistence,
  deleteUser,
  getRedirectResult,
  onAuthStateChanged,
  sendEmailVerification,
  setPersistence,
  signInWithCredential,
  signInWithPopup,
  signOut,
  type User as FirebaseUser,
} from 'firebase/auth'
import {
  addDoc,
  arrayRemove,
  arrayUnion,
  collection,
  deleteDoc,
  doc,
  getCountFromServer,
  getDoc,
  getDocs,
  increment,
  limit,
  onSnapshot,
  orderBy,
  query,
  runTransaction,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  writeBatch,
  firestore,
  checkIsAdmin,
  getServerClockSkewMs,
} from '@/lib/db'
import { firebaseAuth } from '@/lib/firebase'
import { friendlyError, cleanToast } from '@/lib/errors'
import { sendToFormspree } from '@/lib/formspree'
import type { ContactCategory } from '@/components/legal'
import { PriceTag } from '@/components/price-tag'
import type { CoinsApi, PayMethod, MySubscription } from '@/components/premium-store'
import { checkUsername, usernameChangeCost, PREMIUM_USERNAME_MAX_LENGTH, PREMIUM_USERNAME_PRICES } from '@/lib/username-policy'
import { USERNAME_CHANGE_LIMIT } from '@/lib/premium-plans'
import { CoinIcon } from '@/components/coin-icon'
import { EmptyState } from '@/components/empty-state'
import { WatchAdCard, CoinAdContext, CoinShortAd } from '@/components/watch-ad-card'
import { CountryFlag } from '@/components/country-flag'
import { normalizeCountry } from '@/lib/country'
import { HOME_THEMES, DEFAULT_HOME_THEME_ID, getHomeTheme, homeThemeVars, resolveHomeThemeId, type HomeTheme } from '@/lib/home-themes'
import { NameFx, PrizeBadge, PrizeBanner, PrizeFrameLayers } from '@/components/prize-frame'
import { cleanEquipped, cleanOwned, type Equipped } from '@/lib/prizes'
import { setPrizeState } from '@/lib/prize-store'
import { PREMIUM_CHAT_THEME_IDS, GIFT_CATALOG, giftVisualFor, SUBSCRIPTION_PLANS, FREE_STICKER_LIMIT, PREMIUM_STICKER_LIMIT, FREE_GUILD_MEMBER_LIMIT, PREMIUM_GUILD_MEMBER_LIMIT, GROUP_MEMBER_BONUS_INCREMENT, GROUP_MEMBER_BONUS_PRICE_COINS, GREEN_TICK_PRICE_COINS, GREEN_TICK_DAYS, BOOST_PLANS, BOOST_LIMITS } from '@/lib/premium-plans'
import { TICK_MILESTONE_ALLIES, TICK_MILESTONE_VIEWS, achievementCoins } from '@/lib/rewards-policy'
import { GreenTick } from '@/components/green-tick'
import { HEADER_INPUT_THEMES, getHeaderInputTheme, type HeaderInputTheme } from '@/lib/header-themes'
import { isNativeApp } from '@/lib/native-app'
import { startBilling, buyBoost, buyUsername, billingAvailable, usePlayPrice, billingErrorText } from '@/lib/play-billing'
import { playProductForUsernameLength } from '@/lib/billing-products'
import { playProductForBoostPlan } from '@/lib/billing-products'
import { useBackLayer, useBackGuard } from '@/lib/back-stack'
import type { CurrencyCode } from '@/lib/currency'
import { AuthScreen } from '@/components/auth-screen'
import { usePathname, useRouter } from 'next/navigation'
import { presetForSeed } from '@/lib/avatars'
import { Emoji3D, RichEmojiText } from '@/components/emoji3d'
import { PollBubble, PollComposer, GroupVoiceRoom } from '@/components/hub'
import { isJumboText } from '@/lib/emoji3d'
import { askForNotificationPermission, notificationIconFor, showBrandedNotification, watchWebPush } from '@/lib/push-notifications'
import { animoraNotifyPlugin, detectNativeActions } from '@/lib/native-notify'
import { VAPID_PUBLIC_KEY } from '@/lib/vapid-public'
import { useCalls, useGroupCalls } from '@/components/call'
import { minutesToTime, timeToMinutes } from '@/lib/call-dnd'
import type { LudoFriend, LudoInviteInfo, LudoLaunch } from '@/components/ludo'
import type { ChessFriend, ChessInviteInfo, ChessLaunch } from '@/components/chess'
import type { CarromFriend, CarromInviteInfo, CarromLaunch } from '@/components/carrom'
import { warmScreens, enableTapFeedback } from '@/lib/warm'
import { setAdAudience, startAdPreload } from '@/lib/admob'
import { safeHttpUrl, safeMediaUrl } from '@/lib/safe-url'
import { getDeviceHash } from '@/lib/device-fingerprint'
import { parseDob, ageFromDob, MIN_AGE } from '@/lib/age-policy'
import { normalizeReferralCode } from '@/lib/referral-policy'
import { shareText } from '@/lib/share'
import { cleanFilterWords, COMMENT_FILTER_MAX_WORDS, COMMENT_FILTER_MAX_WORD_LEN } from '@/lib/comment-filter'
import {
  EMPTY_ACHIEVEMENT_STATS,
  findNewlyCompleted,
  localDayKey,
  totalXp,
  type AchievementDef,
  type AchievementStats,
  type AchievementUnlockMap,
} from '@/lib/achievements'
import {
  ArrowLeft,
  Ban,
  BarChart3,
  Bell,
  BellOff,
  CheckCheck,
  PinOff,
  Flame,
  LifeBuoy,
  Check,
  ChevronLeft,
  ChevronRight,
  Layers,
  Clapperboard,
  Clock,
  CircleUserRound,
  Copy,
  Crown,
  CircleDot,
  Edit3,
  AlertTriangle,
  Eye,
  EyeOff,
  Bookmark,
  BookmarkCheck,
  Gauge,
  ThumbsDown,
  Flag,
  Forward,
  Gem,
  Globe,
  Grid3x3,
  Heart,
  Home,
  ImagePlus,
  Image as ImageIcon,
  Camera as CameraIcon,
  Video,
  Lock,
  Megaphone,
  MessageCircle,
  Mic,
  Moon,
  Music,
  Palette,
  Pause,
  Pencil,
  Phone,
  Gamepad2,
  Dices,
  Pin,
  Play,
  Plus,
  RefreshCw,
  Reply,
  Rocket,
  Search,
  Send,
  Settings,
  Info,
  Sticker,
  Share2,
  Gift,
  Sun,
  ShieldAlert,
  ShieldCheck,
  Sparkles,
  SquarePlay,
  Trash2,
  Trophy,
  UploadCloud,
  UserMinus,
  UserPlus,
  Users,
  UserX,
  Volume2,
  VolumeX,
  X,
  Zap,
  Coins,
  Upload,
  ChevronUp,
  ChevronDown,
  MoreHorizontal,
  Camera,
  Video as VideoIcon,
  Target as TargetIcon,
  Link2 as LinkIcon,
} from 'lucide-react'
import { PosterIcon } from '@/components/poster-icons'

import { useBoostEnabled, setBoostEnabledFlag } from '@/lib/boost-flag'

// =========================================================================
// TYPES & CONSTANTS
// =========================================================================
type Tab = 'home' | 'reels' | 'contacts' | 'settings' | 'profile'
type Post = {
  id: string
  authorId: string
  authorName: string
  authorAvatar: string
  imageUrl: string
  mediaType?: 'image' | 'video'
  caption: string
  title?: string
  tags?: string[]
  likes: string[]
  commentCount: number
  time: string
  privacy: 'public' | 'private'
  viewers: Record<string, any> // uid -> server timestamp of that viewer's last counted view
  views: number
  audioUrl?: string
  audioTitle?: string
  soundOwnerName?: string // remix: who made the original sound
  remixOf?: string // remix: id of the post whose sound was reused
  audioShared?: boolean // remix: audioUrl belongs to ANOTHER post - never delete that file with this post
  muteOriginal?: boolean // remix video: its own audio is silenced, the remixed sound plays instead
  promoted?: boolean
  promotedUntil?: number
  boostGoal?: 'profile' | 'website'
  websiteUrl?: string
  ctaText?: string // custom "Learn More" button text on a Website-Clicks boosted post (defaults to "Learn More")
  bannerImageUrl?: string // legacy: optional custom banner image shown behind the promoted card's CTA
  bannerColor?: string // preferred: solid brand color shown behind the promoted card's CTA, picked instead of uploading an image
  previewOnly?: boolean // stickers / chat photos: show just the media, no author header, likes or comments
  shareCount?: number // times shared to a chat / story
  imageUrls?: string[] // multi-photo post: every photo in order (imageUrl is always the first one)
  thumbnailUrl?: string // video / reel cover image, already cropped to the video's own ratio
  mediaRatio?: number // video width / height, so feed cards can size themselves without cropping
  postKind?: 'post' | 'reel' // where the upload lives: 'reel' = Reels only (9:16), 'post' = Radar/feed only. Old videos have none and show in both, as before.
  extraImageUrls?: string[] // photos added next to a VIDEO post (swipe: video first, then these photos)
}
type CommentReplyMeta = { threadId: string; name: string; authorId: string }
type PostComment = { id: string; authorId: string; authorName: string; authorAvatar: string; text: string; time: string; likes?: string[]; edited?: boolean; pinned?: boolean; threadId?: string; replyToName?: string; giftLabel?: string; giftAmount?: number }
type Story = {
  id: string
  authorId: string
  authorName: string
  authorAvatar: string
  mediaUrl: string
  mediaType: 'image' | 'video'
  time: string
  likes: string[]
  privacy: 'public' | 'private'
  viewers: string[]
  caption?: string
  audioUrl?: string
  audioTitle?: string
  commentCount?: number
  websiteUrl?: string // link opened by the "Learn More" button when the story is boosted for website clicks
  ctaText?: string // custom "Learn More" button text (defaults to "Learn More")
  bannerImageUrl?: string // legacy: optional custom banner image shown behind the promoted story's CTA
  bannerColor?: string // preferred: solid brand color shown behind the promoted story's CTA, picked instead of uploading an image
  boostGoal?: 'profile' | 'website'
  promoted?: boolean
  promotedUntil?: number
  sourcePostId?: string // set when the story was made from a post/reel ("Add to story")
  soundOwnerName?: string // who made the song / original audio (shown as "Artist · Title" on the story)
  remixOf?: string
  audioShared?: boolean
}
type AppNotification = { id: string; fromId: string; fromName: string; fromAvatar: string; type: 'like' | 'comment' | 'friend_request' | 'gift' | 'mention' | 'admin' | 'group_request' | 'missed_call'; message: string; targetId?: string; time: string; createdAtMs?: number; createdAt?: unknown; read?: boolean; giftLabel?: string }
// One row in the Wallet page's "Payment History" - merged from coinTxns (every spend /
// gift / subscription / boost, written by /api/wallet/transfer) and processedPayments

type WalletTxn = {
  id: string
  amountRupees: number
  createdAtMs: number
  direction: 'in' | 'out' // 'in' = coins landed in this wallet, 'out' = coins left it
  kind: string // 'topup' | 'boost' | 'gift' | 'subscription' | 'subscription_renewal' | 'green_tick' | 'website_link' | 'chat_theme' | 'username' | 'spend' | ...
}
type Profile = {
  country?: string // ISO code from the person's IP (users/{uid}.country) - shown as a flag
  callsEnabled?: boolean // "Allow Calls" switch (Edit Profile); undefined = on
  hideLastSeen?: boolean // "Hide online status": stops this person's presence heartbeat
  callDnd?: { on?: boolean; start?: number; end?: number; tz?: number } // Call Do-Not-Disturb window (minutes after local midnight + UTC offset)
  uid: string; name: string; username: string; usernameLower: string; email: string; avatar: string; coverUrl?: string; equipped?: Equipped; prizesOwned?: Record<string, number>; bio: string; followersCount?: number; followingCount?: number; gender?: 'male' | 'female' | 'other'; postCount?: number; chatBackground?: string; appBackground?: string; gamesTheme?: string; bubbleColor?: string; receivedBubbleColor?: string; coinsSpent?: number; private?: boolean; deactivated?: boolean; deletionScheduledAt?: number; banned?: boolean; banType?: 'temporary' | 'permanent'; bannedUntil?: number | null; banReason?: string; chatBanUntil?: number; chatBanReason?: string; uploadHoldUntil?: number; uploadHoldReason?: string
  isAdmin?: boolean // true only for the Google account matching the ADMIN_EMAIL secret (see checkIsAdmin() in lib/db.ts)
  // --- Monetization (Premium, Username, Chat Themes, Analytics) ---
  isPremium?: boolean
  premiumUntil?: number // ms epoch; feature stays active while Date.now() < premiumUntil
  usernameChangeUsed?: boolean // free change already used up
  customUsernameCredits?: number // extra paid changes available
  usernameChangeCount?: number // renames since the last lock (server-written)
  usernameChangeLockUntil?: number // ms epoch - no rename before this time (server-written)
  unlockedChatThemes?: string[] // premium wallpaper ids bought individually
  headerTheme?: string // chosen header/composer theme id ('auto' = follow chat wallpaper, default)
  unlockedHeaderThemes?: string[] // premium header/composer theme ids bought with XP
  xpSpent?: number // XP spent in the Achievements "Theme Store"
  savedStickers?: string[] // stickers the person saved from chats / uploaded (sticker keyboard -> "My stickers")
  analyticsUnlocked?: boolean
  analyticsUntil?: number
  giftsReceivedRupees?: number // running total of gifts/tips received, for the profile's own stats
  referralCoins?: number // coins earned from the referral program (server-credited only, /api/referral)
  rewardCoins?: number // FREE coins from achievements / streaks / snaps (server-credited only, /api/rewards)
  greenTickMilestone?: boolean // permanent free Green Tick: 1 million Allies + 30 lakh reel views (server-set, /api/rewards)
  walletTopUpRupees?: number // money the user has added to their own wallet (Google Play coin packs)
  groupMemberBonus?: number // extra +GROUP_MEMBER_BONUS_INCREMENT blocks bought (see lib/premium-plans.ts) - a permanent account entitlement, not tied to any one group
  // --- Achievements (free for everyone) ---
  achievementsUnlocked?: AchievementUnlockMap // id -> unlocked-at (ms). `undefined` = not loaded yet
  messagesSent?: number
  groupMessagesSent?: number
  activityStreak?: number
  bestStreak?: number
  lastActiveDay?: string
  publicCoins?: number // coin balance shown on the public profile (derived from the user's doc)
}
// Someone asking to join a PUBLIC guild that uses manual approval. Doc id = `${groupId}_${uid}` (see lib/d1-rules.ts -> groupJoinRequests).
type GroupJoinRequest = { id: string; uid: string; groupId: string; groupName: string; name: string; username: string; avatar: string }
type FriendRequest = { id: string; from: string; to: string; status: 'pending' | 'accepted' | 'rejected'; fromName?: string; fromUsername?: string; fromAvatar?: string }
type Chat = { id: string; name: string; handle: string; avatar: string; preview: string; time: string; online?: boolean; kind?: 'direct' | 'group'; members?: string[]; admins?: string[]; ownerId?: string; privacy?: 'public' | 'private'; joinMode?: 'auto' | 'manual'; sortAt?: number; hidden?: boolean; dm?: 'request-sent' | 'accepted' }
// Message request (first DM from someone who is not a friend / follower). Doc id = `${from}_${to}`.
type DmRequest = { id: string; from: string; to: string; otherId: string; status: 'pending' | 'accepted' | 'declined'; lastMessage: string; sortAt: number; time: string; name: string; handle: string; avatar: string; hiddenFor?: string[] }
type CallLog = { id: string; type: 'voice' | 'video'; status: 'completed' | 'missed' | 'declined' | 'busy'; secs: number; callerId: string }
type Message = { id: string; text: string; time: string; at?: number; own: boolean; read: boolean; readAt?: number; senderId?: string; senderName?: string; senderAvatar?: string; reactions?: Record<string, string[]>; replyToText?: string; replyToSender?: string; replyToImage?: string; replyToId?: string; deleted?: boolean; forwarded?: boolean; sharedImageUrl?: string; sharedThumbnailUrl?: string; sharedPostId?: string; sharedMediaType?: 'image' | 'video'; sharedAuthorId?: string; sharedAuthorName?: string; sharedAuthorUsername?: string; sharedAuthorAvatar?: string; audioUrl?: string; audioDuration?: number; edited?: boolean; editCount?: number; viewOnce?: boolean; viewedBy?: string[]; seenBy?: string[]; sticker?: boolean; blast?: { emoji: string; by: string; at: number }; ludo?: { roomId: string; code: string; stake: number; size: number }; chess?: { roomId: string; code: string; stake: number; tc: string }; carrom?: { roomId: string; code: string; stake: number; size: number }; call?: CallLog; poll?: { question: string; options: string[] } }

// Presence: a beat every 45 s; someone counts as online for 100 s after their last beat.
const friendProfileCache = new Map<string, { at: number; data: Record<string, any> }>()
const PRESENCE_BEAT_MS = 45_000
const PRESENCE_ONLINE_MS = 100_000

const QUICK_EMOJIS = ['❤️', '🔥', '😂', '😮', '😢', '👍']
const STORY_REACTIONS = ['🔥', '❤️', '😂', '👏', '😮', '⚡']

// Grace window for a scheduled permanent account deletion - logging back in
// before this elapses cancels the deletion and restores the account.
const ACCOUNT_DELETION_GRACE_MS = 3 * 24 * 60 * 60 * 1000

// SVG chat wallpapers. "none" keeps the default solid chat background.
// group: 'dark' = black themes, 'light' = white themes (drives the picker tabs and makes
// received bubbles switch to a dark style on dark wallpapers).
// Which ones are premium is decided by PREMIUM_CHAT_THEME_IDS in lib/premium-plans.ts -
// everything not listed there is free.
type ChatBgGroup = 'default' | 'dark' | 'light'
const CHAT_BACKGROUNDS: readonly { id: string; label: string; url: string; group: ChatBgGroup }[] = [
  { id: 'none', label: 'Default', url: '', group: 'default' },
  // --- Black / dark ---
  { id: 'deep-black', label: 'Deep Black', url: '/chat-bgs/deep-black.svg', group: 'dark' },
  { id: 'onyx-dots', label: 'Onyx Dots', url: '/chat-bgs/onyx-dots.svg', group: 'dark' },
  { id: 'carbon-weave', label: 'Carbon Weave', url: '/chat-bgs/carbon-weave.svg', group: 'dark' },
  { id: 'midnight-lines', label: 'Midnight Lines', url: '/chat-bgs/midnight-lines.svg', group: 'dark' },
  { id: 'black-hex', label: 'Black Hex', url: '/chat-bgs/black-hex.svg', group: 'dark' },
  { id: 'neon-skyline', label: 'Neon Skyline', url: '/chat-bgs/neon-skyline.svg', group: 'dark' },
  { id: 'aurora-night', label: 'Aurora Night', url: '/chat-bgs/aurora-night.svg', group: 'dark' },
  { id: 'galaxy-dust', label: 'Galaxy Dust', url: '/chat-bgs/galaxy-dust.svg', group: 'dark' },
  { id: 'crimson-waves', label: 'Crimson Waves', url: '/chat-bgs/crimson-waves.svg', group: 'dark' },
  { id: 'cyber-circuit', label: 'Cyber Circuit', url: '/chat-bgs/cyber-circuit.svg', group: 'dark' },
  { id: 'gold-noir', label: 'Gold Noir', url: '/chat-bgs/gold-noir.svg', group: 'dark' },
  // --- Clean styled (new) ---
  { id: 'midnight-ink', label: 'Midnight Ink', url: '/chat-bgs/midnight-ink.svg', group: 'dark' },
  { id: 'emerald-mist', label: 'Emerald Mist', url: '/chat-bgs/emerald-mist.svg', group: 'dark' },
  { id: 'violet-haze', label: 'Violet Haze', url: '/chat-bgs/violet-haze.svg', group: 'dark' },
  { id: 'rose-ember', label: 'Rose Ember', url: '/chat-bgs/rose-ember.svg', group: 'dark' },
  { id: 'ocean-depth', label: 'Ocean Depth', url: '/chat-bgs/ocean-depth.svg', group: 'dark' },
  { id: 'graphite-grid', label: 'Graphite Grid', url: '/chat-bgs/graphite-grid.svg', group: 'dark' },
  // --- White / light ---
  { id: 'neon-grid', label: 'Paper Grid', url: '/chat-bgs/neon-grid.svg', group: 'light' },
  { id: 'sakura-drift', label: 'Sakura Drift', url: '/chat-bgs/sakura-drift.svg', group: 'light' },
  { id: 'starfield-orbit', label: 'Starfield Orbit', url: '/chat-bgs/starfield-orbit.svg', group: 'light' },
  { id: 'wave-flow', label: 'Wave Flow', url: '/chat-bgs/wave-flow.svg', group: 'light' },
  { id: 'pearl-dots', label: 'Pearl Dots', url: '/chat-bgs/pearl-dots.svg', group: 'light' },
  { id: 'linen-lines', label: 'Linen Lines', url: '/chat-bgs/linen-lines.svg', group: 'light' },
  { id: 'snow-facets', label: 'Snow Facets', url: '/chat-bgs/snow-facets.svg', group: 'light' },
  { id: 'snowfall', label: 'Snowfall', url: '/chat-bgs/snowfall.svg', group: 'light' },
  { id: 'cloud-drift', label: 'Cloud Drift', url: '/chat-bgs/cloud-drift.svg', group: 'light' },
  { id: 'lotus-mandala', label: 'Lotus Mandala', url: '/chat-bgs/lotus-mandala.svg', group: 'light' },
  { id: 'pastel-aurora', label: 'Pastel Aurora', url: '/chat-bgs/pastel-aurora.svg', group: 'light' },
  { id: 'marble-veil', label: 'Marble Veil', url: '/chat-bgs/marble-veil.svg', group: 'light' },
  { id: 'botanical-line', label: 'Botanical Line', url: '/chat-bgs/botanical-line.svg', group: 'light' },
  { id: 'paper-waves', label: 'Paper Waves', url: '/chat-bgs/paper-waves.svg', group: 'light' },
  { id: 'terrazzo', label: 'Terrazzo', url: '/chat-bgs/terrazzo.svg', group: 'light' },
  // --- Clean styled (new) ---
  { id: 'mint-fresh', label: 'Mint Fresh', url: '/chat-bgs/mint-fresh.svg', group: 'light' },
  { id: 'peach-glow', label: 'Peach Glow', url: '/chat-bgs/peach-glow.svg', group: 'light' },
  { id: 'lavender-mist', label: 'Lavender Mist', url: '/chat-bgs/lavender-mist.svg', group: 'light' },
  { id: 'sky-breeze', label: 'Sky Breeze', url: '/chat-bgs/sky-breeze.svg', group: 'light' },
  { id: 'butter-cream', label: 'Butter Cream', url: '/chat-bgs/butter-cream.svg', group: 'light' },
  { id: 'rose-quartz', label: 'Rose Quartz', url: '/chat-bgs/rose-quartz.svg', group: 'light' },
]
// Admin account: whoever is signed in with the Google account matching the ADMIN_EMAIL
// secret gets admin access - not tied to any username, and not tied to any hardcoded
// value here either. The browser never knows that email; it just asks the server "am I
// the admin?" via checkIsAdmin() (see lib/db.ts + the `whoami` op in app/api/db/route.ts).
// The real check lives server-side in lib/d1-rules.ts, read from the ADMIN_EMAIL secret
// (Cloudflare Worker Settings -> Runtime variables and secrets).
// Green Tick shown next to a @username: active premium window, or the always-on admin account.
const hasGreenTick = (p?: { isAdmin?: boolean; isPremium?: boolean; premiumUntil?: number; greenTickMilestone?: boolean } | null) =>
  Boolean(p && (p.isAdmin || (p.isPremium && p.premiumUntil && p.premiumUntil > Date.now())))
// =============================================================================================
// LIVE PROFILE PICTURES (fix: "I changed my profile picture / banner but old one still shows everywhere").
// Posts, comments, chat messages, notifications, story cards ... each store a COPY of the author's avatar from the day
// they were written, so after a profile change every one of those places kept showing the OLD picture. Instead of
// trusting those copies, <Avatar uid={...}> now asks this small store for the person's CURRENT avatar:
//   - your own avatar is always live (pushed in from your profile state the moment you save), so it changes everywhere at once
//   - other people's avatars are read from their users/{uid} doc once, kept for LIVE_AVATAR_TTL, then re-read when the
//     avatar is shown again - so a changed picture shows up everywhere without any one-by-one rewriting of old data
//   - anywhere the app already loads a user doc (friends, author lookups, leaderboards, opened profiles) feeds the store too
// The stored copy is still used as the fallback while loading or if the live picture fails to load.
// =============================================================================================
const LIVE_AVATAR_TTL = 60_000
const LIVE_AVATAR_OWN = Number.MAX_SAFE_INTEGER // marker: this is the signed-in user's own avatar, never overwritten by a fetch
const liveAvatars = new Map<string, { avatar: string; at: number; eq?: Equipped }>()
const NO_EQ: Equipped = {}
const liveAvatarListeners = new Set<() => void>()
const liveAvatarPending = new Set<string>()
const emitLiveAvatars = () => { liveAvatarListeners.forEach((l) => l()) }
// Gives up on a slow request so a loading spinner can never stay stuck: resolves with the real result, or rejects after `ms`.
function raceTimeout<T>(p: Promise<T>, ms = 9000): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const t = setTimeout(() => reject(new Error('timeout')), ms)
    p.then((v) => { clearTimeout(t); resolve(v) }, (e) => { clearTimeout(t); reject(e) })
  })
}
function rememberLiveAvatar(uid: string, avatar: unknown, own = false, equipped?: unknown) {
  if (!uid || typeof avatar !== 'string' || !avatar) return
  const cur = liveAvatars.get(uid)
  if (cur && cur.at === LIVE_AVATAR_OWN && !own) return
  // `equipped` (users/{uid}.equipped) carries the person's cosmetics (frame, name effect, bubble, ring...); undefined = keep what we know
  let eq = cur?.eq
  if (equipped !== undefined) { const next = cleanEquipped(equipped); eq = JSON.stringify(next) === JSON.stringify(cur?.eq ?? NO_EQ) ? cur?.eq : next }
  liveAvatars.set(uid, { avatar, at: own ? LIVE_AVATAR_OWN : Date.now(), eq })
  if (!cur || cur.avatar !== avatar || cur.eq !== eq) emitLiveAvatars()
}
/** Sender's profile picture for an OS notification icon ('' = use the Animora logo). Uses what the app already knows, no extra request. */
const pfpFor = (uid?: string) => notificationIconFor(uid ? liveAvatars.get(uid)?.avatar : '')
function fetchLiveAvatar(uid: string) {
  const cur = liveAvatars.get(uid)
  if (liveAvatarPending.has(uid) || (cur && Date.now() - cur.at < LIVE_AVATAR_TTL)) return
  liveAvatarPending.add(uid)
  getDoc(doc(firestore, 'users', uid))
    .then((snap) => {
      const d = snap.exists() ? snap.data() : null
      if (d && typeof d.avatar === 'string' && d.avatar) rememberLiveAvatar(uid, d.avatar, false, d.equipped ?? null)
      else if (!liveAvatars.has(uid)) liveAvatars.set(uid, { avatar: '', at: Date.now() }) // nothing to show: do not re-ask every render
    })
    .catch(() => { /* private / offline: the stored copy keeps being used */ })
    .finally(() => { liveAvatarPending.delete(uid) })
}
/** A person's equipped cosmetics (frame, ring, name effect, bubble, aura...). Stable object between changes; {} when unknown. */
function useLiveEquipped(uid?: string): Equipped {
  const eq = useSyncExternalStore(
    (cb) => { liveAvatarListeners.add(cb); return () => { liveAvatarListeners.delete(cb) } },
    () => (uid ? liveAvatars.get(uid)?.eq : undefined) ?? NO_EQ,
    () => NO_EQ,
  )
  useEffect(() => { if (uid) fetchLiveAvatar(uid) }, [uid])
  return eq
}

function useLiveAvatar(uid?: string): string {
  const live = useSyncExternalStore(
    (cb) => { liveAvatarListeners.add(cb); return () => { liveAvatarListeners.delete(cb) } },
    () => (uid ? liveAvatars.get(uid)?.avatar || '' : ''),
    () => '',
  )
  useEffect(() => { if (uid) fetchLiveAvatar(uid) }, [uid])
  return live
}
// Post cards show the author's @username (not the display name) plus the Green Tick when that
// author currently has one. Posts only store authorName/authorAvatar, so the page looks up each
// visible author's user doc once per session and shares the result through this context.
type AuthorInfo = { username: string; usernameLower: string; isPremium?: boolean; premiumUntil?: number; isAdmin?: boolean; greenTickMilestone?: boolean }
const AuthorInfoContext = createContext<Record<string, AuthorInfo>>({})
function usePostAuthor(post: { authorId: string; authorName?: string }) {
  const info = useContext(AuthorInfoContext)[post.authorId]
  return { label: info?.username ? `@${info.username}` : (post.authorName || ''), tick: hasGreenTick(info) }
}
// Story rings: which people currently have a live story ('unseen' = bright ring, 'seen' = dimmed
// because you already watched all of it) and a way to open their story from any avatar.
type StoryRingApi = { statusOf: (uid: string) => 'unseen' | 'seen' | null; open: (uid: string) => void }
const StoryRingContext = createContext<StoryRingApi>({ statusOf: () => null, open: () => {} })
function PostAuthorName({ post, tickSize = 17 }: { post: { authorId: string; authorName?: string }; tickSize?: number }) {
  const { label, tick } = usePostAuthor(post)
  const eq = useLiveEquipped(post.authorId)
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, minWidth: 0 }}>
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}><NameFx id={eq.namefx}>{label}</NameFx></span>
      {tick && <GreenTick size={tickSize} />}
    </span>
  )
}
const isPremiumChatTheme = (id: string) => (PREMIUM_CHAT_THEME_IDS as readonly string[]).includes(id)

// Header brand-logo ring glow, keyed to the same ids as CHAT_BACKGROUNDS, so the
// top header's theme follows whichever chat wallpaper the user has active. Colors
// are pulled from each wallpaper's own SVG palette. Falls back to the default
// brand purple/cyan when no wallpaper is set.
const BG_THEME_GLOW: Record<string, [string, string]> = {
  none: ['#b84dff', '#ff4d6d'],
  'neon-grid': ['#ff4d6d', '#c084fc'],
  'sakura-drift': ['#ff6fb5', '#ffd9ec'],
  'starfield-orbit': ['#b84dff', '#ff4d6d'],
  'wave-flow': ['#ff4d6d', '#b84dff'],
  'deep-black': ['#e5e7eb', '#9ca3af'],
  'onyx-dots': ['#9ca3af', '#e5e7eb'],
  'carbon-weave': ['#a1a1aa', '#71717a'],
  'midnight-lines': ['#818cf8', '#6366f1'],
  'black-hex': ['#38bdf8', '#a78bfa'],
  'neon-skyline': ['#f472b6', '#22d3ee'],
  'aurora-night': ['#34d399', '#818cf8'],
  'galaxy-dust': ['#a78bfa', '#f472b6'],
  'crimson-waves': ['#ef4444', '#fb7185'],
  'cyber-circuit': ['#22d3ee', '#67e8f9'],
  'gold-noir': ['#c9a24a', '#f6e3a0'],
  'pearl-dots': ['#818cf8', '#f9a8d4'],
  'linen-lines': ['#b8a98a', '#8b7f6a'],
  'snow-facets': ['#93b4e6', '#c7d2fe'],
  snowfall: ['#7dd3fc', '#bfdbfe'],
  'cloud-drift': ['#60a5fa', '#93c5fd'],
  'lotus-mandala': ['#e6906f', '#fbbf9a'],
  'pastel-aurora': ['#c084fc', '#f9a8d4'],
  'marble-veil': ['#c9a24a', '#9ca3af'],
  'botanical-line': ['#6f9a7c', '#a7c4a0'],
  'paper-waves': ['#f472b6', '#93c5fd'],
  terrazzo: ['#f2a48f', '#9bc4cb'],
  'midnight-ink': ['#6366f1', '#60a5fa'],
  'emerald-mist': ['#10b981', '#2dd4bf'],
  'violet-haze': ['#8b5cf6', '#c084fc'],
  'rose-ember': ['#f43f5e', '#fb923c'],
  'ocean-depth': ['#22d3ee', '#38bdf8'],
  'graphite-grid': ['#94a3b8', '#cbd5e1'],
  'mint-fresh': ['#34d399', '#5eead4'],
  'peach-glow': ['#fb923c', '#fda4af'],
  'lavender-mist': ['#a78bfa', '#f0abfc'],
  'sky-breeze': ['#60a5fa', '#7dd3fc'],
  'butter-cream': ['#eab308', '#fdba74'],
  'rose-quartz': ['#f472b6', '#fda4af'],
}

// App-wide theme palette (header palette button): `tone` = dark card color, `a`/`b` = accents.
// Picked to match each wallpaper's own colors so cards, header and nav all read as that theme.
const APP_THEME_PALETTE: Record<string, { tone: string; a: string; b: string }> = {
  none: { tone: '#0b0b12', a: '#b84dff', b: '#ff4d6d' },
  'neon-grid': { tone: '#140c26', a: '#a855f7', b: '#22d3ee' },
  'sakura-drift': { tone: '#2b1026', a: '#ff6fb5', b: '#ffb3d9' },
  'starfield-orbit': { tone: '#0f0b33', a: '#7c5cff', b: '#c084fc' },
  'wave-flow': { tone: '#062430', a: '#14b8a6', b: '#38bdf8' },
}

// Sent-message bubble colors. `violet` is the original pink/purple look. Any other
// value saved on the profile is either one of these preset ids or a custom "#rrggbb".
const BUBBLE_COLORS = [
  { id: 'violet', label: 'Pink Violet', colors: ['#9333ea', '#c084fc', '#db2777'] },
  { id: 'ember', label: 'Ember', colors: ['#ea580c', '#fb923c', '#dc2626'] },
  { id: 'ocean', label: 'Ocean', colors: ['#0369a1', '#38bdf8', '#4f46e5'] },
  { id: 'emerald', label: 'Emerald', colors: ['#047857', '#34d399', '#0d9488'] },
  { id: 'rose', label: 'Rose', colors: ['#be123c', '#fb7185', '#e11d48'] },
  { id: 'sakura', label: 'Sakura', colors: ['#be185d', '#f472b6', '#a855f7'] },
  { id: 'gold', label: 'Gold', colors: ['#b45309', '#f59e0b', '#dc2626'] },
  { id: 'slate', label: 'Slate', colors: ['#334155', '#64748b', '#1e293b'] },
] as const

const HEX_COLOR_RE = /^#[0-9a-fA-F]{6}$/

// Returns the CSS variables the chat body uses to paint a bubble's colors. Used for both
// the user's own sent bubbles (prefix 'own') and, when the person picks a manual color for
// the other side, the messages they receive (prefix 'other'). Empty object = keep whatever
// the stylesheet/theme already set (no override).
// Picks the text colour (white or near-black) with the better WCAG contrast on a given bubble colour.
// The old rule only looked at a rough brightness cut-off and ignored presets entirely, so white text landed on
// bright cyan / pink bubbles (about 2:1 contrast) and was hard to read on dark wallpapers.
const readableTextOn = (hex: string): string => {
  const n = parseInt(hex.slice(1), 16)
  const lin = (v: number) => { const c = v / 255; return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4) }
  const L = 0.2126 * lin((n >> 16) & 255) + 0.7152 * lin((n >> 8) & 255) + 0.0722 * lin(n & 255)
  const onWhite = 1.05 / (L + 0.05)
  const onDark = (L + 0.05) / (0.004 + 0.05) // #0b0d1e is about L = 0.004
  return onDark > onWhite ? '#0b0d1e' : '#ffffff'
}
// White or near-black text, whichever stays readable on BOTH ends of a bubble gradient (picks the better worst-case contrast).
const readableTextOnBoth = (hexA: string, hexB: string): string => {
  const lum = (hex: string) => {
    const n = parseInt(hex.slice(1), 16)
    const lin = (v: number) => { const c = v / 255; return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4) }
    return 0.2126 * lin((n >> 16) & 255) + 0.7152 * lin((n >> 8) & 255) + 0.0722 * lin(n & 255)
  }
  const ok = (h: string) => /^#[0-9a-fA-F]{6}$/.test(h)
  if (!ok(hexA) || !ok(hexB)) return '#ffffff'
  const ls = [lum(hexA), lum(hexB)]
  const white = Math.min(...ls.map((L) => 1.05 / (L + 0.05)))
  const dark = Math.min(...ls.map((L) => (L + 0.05) / 0.054))
  return dark > white ? '#0b0d1e' : '#ffffff'
}
const bubbleColorVars = (value: string | undefined, prefix: 'own' | 'other' = 'own'): React.CSSProperties => {
  if (!value || value === 'violet') return {}
  let b: string
  const preset = BUBBLE_COLORS.find((p) => p.id === value)
  if (preset) {
    b = (preset.colors as unknown as string[])[1]
  } else if (HEX_COLOR_RE.test(value)) {
    b = value
  } else {
    return {}
  }
  return {
    [`--${prefix}-bubble-bg`]: b,
    [`--${prefix}-bubble-text`]: /^#[0-9a-fA-F]{6}$/.test(b) ? readableTextOn(b) : '#ffffff',
  } as React.CSSProperties
}


// Giphy public API key for the in-chat sticker/GIF picker (Settings → your app's
// key at giphy.com/settings). Safe to ship client-side - it's Giphy's public
// search key, same as any client-side Giphy SDK integration.
const GIPHY_API_KEY = process.env.NEXT_PUBLIC_GIPHY_API_KEY || 'jfW9bQ9PPwl3ptvnn8oiLwOblRsk1fAq'

// Max stickers one person can keep in "My stickers": FREE_STICKER_LIMIT normally,
// PREMIUM_STICKER_LIMIT while the Green Tick is active (see lib/premium-plans.ts).

// A chat message is a sticker when it came from the sticker keyboard (sticker: true) or is an
// older Giphy sticker sent before that flag existed. Stickers render small - no card, no bubble,
// no "tap to view" preview.
const MAX_MSG_EDITS = 2 // a text message can be edited at most this many times
const isStickerMsg = (m: { sharedImageUrl?: string; sharedPostId?: string; sticker?: boolean; deleted?: boolean }) =>
  Boolean(m.sharedImageUrl && !m.sharedPostId && !m.deleted && (m.sticker || /giphy\.com/i.test(m.sharedImageUrl)))
// A normal photo sent from the gallery (not a shared post / sticker / view-once photo): shown as a plain picture in the bubble.
const isPlainPhotoMsg = (m: { sharedImageUrl?: string; sharedPostId?: string; sticker?: boolean; deleted?: boolean; viewOnce?: boolean }) =>
  Boolean(m.sharedImageUrl && !m.sharedPostId && !m.deleted && !m.viewOnce && !isStickerMsg(m))

const fallbackAvatarSvg = (seed: string) => {
  const hash = seed.split('').reduce((acc, c) => acc + c.charCodeAt(0), 0)
  const colors = ['%2338bdf8', '%23a78bfa', '%23f472b6', '%23fbbf24', '%2334d399']
  const color = colors[hash % colors.length]
  return `data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><rect width="100" height="100" fill="%23eceef1"/><circle cx="50" cy="40" r="22" fill="${color}"/><path d="M20 90 Q50 62 80 90 Z" fill="${color}" opacity="0.65"/><circle cx="44" cy="38" r="2.5" fill="%230f172a"/><circle cx="56" cy="38" r="2.5" fill="%230f172a"/><path d="M46 45 Q50 48 54 45" stroke="%230f172a" stroke-width="1.5" fill="none" stroke-linecap="round"/></svg>`
}

// "Today" / "Yesterday" / "12 Oct" label used for the date separators inside a chat.
const dayLabel = (ms: number) => {
  const d = new Date(ms)
  const startOf = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime()
  const diff = Math.round((startOf(new Date()) - startOf(d)) / 86400000)
  if (diff === 0) return 'Today'
  if (diff === 1) return 'Yesterday'
  return new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', ...(d.getFullYear() !== new Date().getFullYear() ? { year: 'numeric' } : {}) }).format(d)
}
const formatTime = (date?: Date) => date ? new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit' }).format(date) : 'now'

// ---- "Message sent" chime -------------------------------------------------------------
// A soft, watery blip that plays the instant a chat message is sent. Synthesized on the fly
// with the Web Audio API rather than an mp3 - no file to bundle/host, stays tiny, and is
// always available offline. One AudioContext is created lazily (must happen from a user
// gesture like tapping Send, per browser autoplay rules) and reused for every chime.
let uiAudioCtx: AudioContext | null = null
const getUiAudioCtx = (): AudioContext | null => {
  if (typeof window === 'undefined') return null
  if (!uiAudioCtx) {
    const AudioCtxCls = window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
    if (!AudioCtxCls) return null
    uiAudioCtx = new AudioCtxCls()
  }
  if (uiAudioCtx.state === 'suspended') uiAudioCtx.resume().catch(() => undefined)
  return uiAudioCtx
}
const playSendSound = () => {
  try {
    const ctx = getUiAudioCtx()
    if (!ctx) return
    const now = ctx.currentTime

    // Gentle lowpass so the tone stays soft/rounded instead of buzzy.
    const filter = ctx.createBiquadFilter()
    filter.type = 'lowpass'
    filter.frequency.value = 2200
    filter.connect(ctx.destination)

    const master = ctx.createGain()
    master.gain.value = 0.16
    master.connect(filter)

    // Main droplet: quick downward pitch-slide, like a single water "plink".
    const osc1 = ctx.createOscillator()
    osc1.type = 'sine'
    osc1.frequency.setValueAtTime(880, now)
    osc1.frequency.exponentialRampToValueAtTime(440, now + 0.16)
    const g1 = ctx.createGain()
    g1.gain.setValueAtTime(0.0001, now)
    g1.gain.exponentialRampToValueAtTime(1, now + 0.012)
    g1.gain.exponentialRampToValueAtTime(0.0001, now + 0.28)
    osc1.connect(g1)
    g1.connect(master)
    osc1.start(now)
    osc1.stop(now + 0.3)

    // A faint, higher second droplet just after - gives it a watery "shimmer" instead
    // of one flat beep.
    const osc2 = ctx.createOscillator()
    osc2.type = 'sine'
    osc2.frequency.setValueAtTime(1320, now + 0.07)
    osc2.frequency.exponentialRampToValueAtTime(760, now + 0.2)
    const g2 = ctx.createGain()
    g2.gain.setValueAtTime(0.0001, now + 0.07)
    g2.gain.exponentialRampToValueAtTime(0.5, now + 0.08)
    // Tail stretched a touch (was 0.32/0.34) to land with the bubble's pop-in
    // animation, which is now 0.45s instead of 0.32s - keeps the chime and the
    // bubble settling at roughly the same moment instead of the sound cutting off early.
    g2.gain.exponentialRampToValueAtTime(0.0001, now + 0.42)
    osc2.connect(g2)
    g2.connect(master)
    osc2.start(now + 0.07)
    osc2.stop(now + 0.44)
  } catch {
    // The chime is a nice-to-have; never let it block or break sending a message.
  }
}

// Compact view-count display for the Explore grid (208K, 8.8M, etc.)
const formatCount = (n: number) => {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(n % 1_000_000 === 0 ? 0 : 1)}M`
  if (n >= 1_000) return `${(n / 1_000).toFixed(n % 1_000 === 0 ? 0 : 1)}K`
  return String(n)
}
// Coin balance (free in-app coins, NO cash value) = coins earned - coins spent. Same formula
// as the header / wallet; the Hall of Fame ranks by it.
const coinBalanceOf = (p: { giftsReceivedRupees?: number; walletTopUpRupees?: number; referralCoins?: number; rewardCoins?: number; coinsSpent?: number }) =>
  Math.max(0, Number(p.giftsReceivedRupees || 0) + Number(p.walletTopUpRupees || 0) + Number(p.referralCoins || 0) + Number(p.rewardCoins || 0) - Number(p.coinsSpent || 0))

// Users often type a website without "https://" - Firestore/href just needs a valid
// absolute URL, so quietly add the scheme instead of rejecting the input.
const normalizeUrl = (raw: string) => /^https?:\/\//i.test(raw) ? raw : `https://${raw}`

// Rejects junk input (no dot, spaces, no real domain/TLD) before it's ever saved as an
// ad/website link - used by the Boost sheet and the composer's website-link unlock sheet.
const isValidWebsiteUrl = (raw: string) => {
  const trimmed = raw.trim()
  if (!trimmed || /\s/.test(trimmed)) return false
  let parsed: URL
  try {
    parsed = new URL(normalizeUrl(trimmed))
  } catch {
    return false
  }
  if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return false
  const host = parsed.hostname
  // Needs an actual domain with a real-looking TLD (allow localhost/IPs to fail - this is
  // for public ad links, not dev testing) - e.g. "yourstore.com", not "asdkjaskjd".
  return /^([a-z0-9]([a-z0-9-]*[a-z0-9])?\.)+[a-z]{2,}$/i.test(host)
}

// Pulls the R2 object key back out of one of our own public bucket URLs, e.g.
// "https://pub-xxxx.r2.dev/posts/post_abc_123.webp" -> "posts/post_abc_123.webp".
// Used both to know when two URLs point at the same underlying file (dedup checks)
// and to tell the DELETE endpoint what to remove.
const extractStorageKey = (url: string) => {
  try {
    const u = new URL(url)
    const key = decodeURIComponent(u.pathname.replace(/^\/+/, ''))
    return key || null
  } catch {
    return null
  }
}

// Name shown on the full-screen video section (top bar + bottom nav). Change it here.
const REELS_BRAND = 'Shonen'

// Upload limits for the post / reel composer (keeps feed cards short and clean).
const MAX_POST_PHOTOS = 10 // photos per post (swipeable carousel)
const POST_TITLE_MAX = 60
const POST_CAPTION_MAX = 220
const POST_TAGS_MAX = 4
const POST_TAG_MAX_LEN = 24

// Media now lives in Cloudflare R2 and is served directly from R2's public URL
// (Range requests for video/audio seeking work natively there, no proxy needed),
// so there's nothing left to rewrite here.
// NOTE: any post/story/message/avatar uploaded BEFORE this migration was stored on
// Google Drive and streamed through the old /api/drive-upload proxy - that route no
// longer exists, so old videos/audio (and any old-domain image links) will now be
// broken. Only brand-new uploads (R2) will play/load.
const resolveMediaUrl = <T extends string | undefined>(url: T): T => url

// ===== SOUNDS (reel music + remix) =====
// A "sound" is identified by the URL of its audio file: a song attached to a post, or (for a video without a
// song) the video file itself, whose own audio track is the "Original audio". A remix post reuses that SAME url
// as its audioUrl, so "every reel that uses this sound" is simply: posts whose audioUrl == key (+ the source video).
type SoundInfo = { key: string; playUrl: string; title: string; owner: string; sourceId: string }
const soundInfoOf = (post: any): SoundInfo => {
  const isVideo = post?.mediaType === 'video'
  const playUrl: string = post?.audioUrl || (isVideo ? post?.imageUrl : '') || ''
  const isOriginalAudio = !post?.audioUrl
  return {
    key: playUrl,
    playUrl,
    title: isOriginalAudio ? 'Original audio' : (post?.audioTitle || 'Attached Audio Track'),
    owner: String(post?.soundOwnerName || post?.authorName || ''),
    sourceId: String(post?.remixOf || post?.id || ''),
  }
}

// Turns one posts/{id} Firestore doc into a Post the UI can render - shared by the main
// feed listener and myPosts (the signed-in user's own posts, queried separately so an
// active poster's profile/wallet stats aren't capped by the feed's recent-50 limit).
// Keeping this in one place means both stay consistent (same defaults, same media URL
// resolution) instead of two hand-rolled mappings quietly drifting apart.
const mapPostDoc = (id: string, data: any): Post & { _createdAtMs: number } => ({
  id,
  authorId: data.authorId,
  authorName: data.authorName || 'Traveler',
  authorAvatar: data.authorAvatar || '',
  imageUrl: resolveMediaUrl(data.imageUrl),
  mediaType: data.mediaType || 'image',
  caption: data.caption || '',
  title: data.title || '',
  tags: Array.isArray(data.tags) ? data.tags : [],
  likes: data.likes || [],
  commentCount: Number(data.commentCount || 0),
  time: formatTime(data.createdAt?.toDate?.()),
  privacy: data.privacy || 'public',
  viewers: (data.viewers && typeof data.viewers === 'object' && !Array.isArray(data.viewers)) ? data.viewers : {},
  views: data.views || 0,
  shareCount: Number(data.shareCount || 0),
  imageUrls: Array.isArray(data.imageUrls) && data.imageUrls.length > 1 ? data.imageUrls.filter((u: unknown) => typeof u === 'string').map((u: string) => resolveMediaUrl(u)) : undefined,
  thumbnailUrl: typeof data.thumbnailUrl === 'string' && data.thumbnailUrl ? resolveMediaUrl(data.thumbnailUrl) : undefined,
  mediaRatio: typeof data.mediaRatio === 'number' && data.mediaRatio > 0 ? data.mediaRatio : undefined,
  postKind: data.postKind === 'reel' ? 'reel' : data.postKind === 'post' ? 'post' : undefined,
  extraImageUrls: Array.isArray(data.extraImageUrls) && data.extraImageUrls.length ? data.extraImageUrls.filter((u: unknown) => typeof u === 'string').map((u: string) => resolveMediaUrl(u)) : undefined,
  audioUrl: resolveMediaUrl(data.audioUrl),
  audioTitle: data.audioTitle,
  soundOwnerName: typeof data.soundOwnerName === 'string' ? data.soundOwnerName : undefined,
  remixOf: typeof data.remixOf === 'string' ? data.remixOf : undefined,
  audioShared: data.audioShared === true ? true : undefined,
  muteOriginal: data.muteOriginal === true ? true : undefined,
  promoted: Boolean(data.promotedUntil && data.promotedUntil.toMillis?.() > Date.now()) || Boolean(typeof data.promotedUntil === 'number' && data.promotedUntil > Date.now()),
  promotedUntil: data.promotedUntil?.toMillis?.() || (typeof data.promotedUntil === 'number' ? data.promotedUntil : undefined),
  boostGoal: data.boostGoal || 'profile',
  websiteUrl: data.websiteUrl || '',
  ctaText: typeof data.ctaText === 'string' && data.ctaText.trim() ? data.ctaText.trim() : undefined,
  bannerImageUrl: typeof data.bannerImageUrl === 'string' && data.bannerImageUrl ? resolveMediaUrl(data.bannerImageUrl) : undefined,
  bannerColor: typeof data.bannerColor === 'string' && data.bannerColor ? data.bannerColor : undefined,
  _createdAtMs: data.createdAt?.toMillis?.() ?? 0, // sort key only, not part of the Post type shown in the UI
})

// =========================================================================
// FEED RANKING (Post Feed + Reel Feed)
// Instead of a flat upload-order list, organic content is scored by
// engagement/quality/tag-relevance signals so higher-potential content
// surfaces sooner, and paid/promoted content is woven naturally through
// the feed (every ~4-8 organic items) instead of clumping at the top.
// =========================================================================

// Deterministic 0..1 pseudo-random derived from a string - stable across
// re-renders (so ad placement/tie-breaks don't jump around on every
// Firestore update) while still varying per post/ad instead of being fixed.
const hashToUnit = (input: string) => {
  let h = 0
  for (let i = 0; i < input.length; i++) h = (h * 31 + input.charCodeAt(i)) | 0
  return (Math.abs(h) % 1000) / 1000
}

// Composite relevance/quality/viral-potential score for one organic post.
// `recencyRank` is its index in the newest-first feed order (0 = newest).
const scoreOrganicPost = (post: Post, recencyRank: number, totalOrganic: number) => {
  const engagement = (post.likes?.length || 0) * 3 + (post.commentCount || 0) * 4 + (post.views || 0) * 0.05
  const recency = (totalOrganic - recencyRank) * 2
  const tagRelevance = Math.min(post.tags?.length || 0, 5) * 1.5
  const richness = (post.caption ? 1 : 0) + (post.title ? 1 : 0) + (post.audioUrl ? 1 : 0)
  const jitter = hashToUnit(post.id) * 3 // small tie-breaker so the order isn't perfectly static
  return engagement + recency + tagRelevance + richness + jitter
}

// =========================================================================
// PERSONALIZED REELS ("For You")
// The Reels feed learns what each user likes: what they LIKE, how long they WATCH,
// and what they open comments / share on. Every reel is auto-classified into a topic
// (anime, music, gaming, comedy... or "general" = normal reels) from its tags, title and
// caption - no extra field is needed on old posts. Signals pile up in a "pending" bucket;
// once every 24 hours the pending signals are merged into the user's interest profile
// (older interest fades a little, so recent taste wins) and THAT profile drives the feed
// until the next 24h check. Ads in the reel feed are matched to the same profile.
// The profile is saved on the user's own doc (users/{uid}.reelInterests).
// =========================================================================
const REEL_INTEREST_EVAL_MS = 24 * 60 * 60 * 1000 // re-evaluate taste every 24 hours
const REEL_INTEREST_BOOTSTRAP = 8   // brand-new user: first evaluation may run early, once this much signal exists
const REEL_INTEREST_MIN_READY = 6   // below this total the feed is not personalized yet
const REEL_INTEREST_DECAY = 0.6     // how much of the old profile survives each 24h evaluation
const REEL_EXPLORE_EVERY = 4        // every 4th reel is off-interest, so new taste can be discovered

// Single-word keywords (lowercase). A word matches exactly, or - for keywords of 5+
// letters - when a hashtag/word contains it (so "animeedit" hits "anime").
const REEL_CATEGORY_KEYWORDS: Record<string, string[]> = {
  anime: ['anime', 'manga', 'manhwa', 'manhua', 'webtoon', 'otaku', 'shonen', 'shounen', 'seinen', 'isekai', 'naruto', 'boruto', 'onepiece', 'luffy', 'goku', 'dragonball', 'bleach', 'jujutsu', 'jjk', 'demonslayer', 'tanjiro', 'nezuko', 'aot', 'titan', 'deathnote', 'gojo', 'sukuna', 'itachi', 'sasuke', 'zoro', 'sololeveling', 'hunterxhunter', 'fairytail', 'tokyoghoul', 'chainsaw', 'mha', 'bnha', 'haikyuu', 'bluelock', 'spyxfamily', 'amv', 'weeb', 'waifu', 'nakama', 'senpai', 'ghibli', 'berserk', 'vinland', 'saitama', 'eren', 'levi', 'kawaii', 'cosplay', 'donghua', 'lightnovel', 'animeedit', 'animereels'],
  music: ['song', 'songs', 'music', 'lyrics', 'lofi', 'remix', 'rap', 'hiphop', 'singer', 'singing', 'guitar', 'piano', 'dj', 'cover', 'playlist', 'spotify', 'bieber', 'justinbieber', 'arijit', 'taylorswift', 'weeknd', 'edsheeran', 'gaana', 'gana', 'album', 'melody', 'beats'],
  gaming: ['game', 'games', 'gaming', 'gamer', 'gameplay', 'pubg', 'bgmi', 'freefire', 'minecraft', 'valorant', 'fortnite', 'gta', 'roblox', 'esports', 'ps5', 'mlbb', 'streamer', 'cod', 'genshin'],
  comedy: ['funny', 'comedy', 'meme', 'memes', 'joke', 'jokes', 'prank', 'humor', 'humour', 'roast', 'lol', 'standup', 'fail', 'fails', 'mazak', 'masti'],
  sports: ['cricket', 'football', 'soccer', 'ipl', 'fifa', 'nba', 'wwe', 'ufc', 'kabaddi', 'messi', 'ronaldo', 'kohli', 'dhoni', 'sports', 'match', 'goal'],
  fitness: ['gym', 'workout', 'fitness', 'bodybuilding', 'yoga', 'health', 'exercise', 'calisthenics', 'diet'],
  motivation: ['motivation', 'motivational', 'quotes', 'quote', 'success', 'mindset', 'hustle', 'grind', 'inspiration', 'sigma', 'shayari'],
  travel: ['travel', 'trip', 'nature', 'mountains', 'vlog', 'hiking', 'beach', 'roadtrip', 'adventure', 'scenery'],
  food: ['food', 'recipe', 'cooking', 'foodie', 'chef', 'streetfood', 'biryani', 'baking', 'dessert'],
  fashion: ['fashion', 'makeup', 'outfit', 'beauty', 'skincare', 'ootd', 'model', 'style', 'hairstyle'],
  tech: ['tech', 'coding', 'programming', 'ai', 'gadget', 'gadgets', 'iphone', 'android', 'developer', 'software', 'robot'],
  movies: ['movie', 'movies', 'film', 'series', 'netflix', 'webseries', 'bollywood', 'hollywood', 'trailer', 'kdrama', 'drama', 'marvel', 'cinema', 'actor', 'actress'],
  pets: ['cat', 'cats', 'dog', 'dogs', 'pets', 'pet', 'animal', 'animals', 'puppy', 'kitten'],
  art: ['art', 'drawing', 'sketch', 'painting', 'artist', 'digitalart', 'illustration', 'design'],
  dance: ['dance', 'dancing', 'dancer', 'choreography', 'dancecover'],
}
const REEL_CATEGORY_EXACT: Record<string, Set<string>> = {}
const REEL_CATEGORY_CONTAINS: Record<string, string[]> = {}
for (const [cat, words] of Object.entries(REEL_CATEGORY_KEYWORDS)) {
  REEL_CATEGORY_EXACT[cat] = new Set(words)
  REEL_CATEGORY_CONTAINS[cat] = words.filter((w) => w.length >= 5)
}

// Topic(s) of one reel: up to 2 categories (strongest first) plus its normalized hashtags.
// A reel that matches nothing counts as "general" (normal reels).
const classifyReel = (post: Post): { cats: string[]; tags: string[] } => {
  const tags = Array.from(new Set(
    (post.tags || [])
      .map((t) => String(t).toLowerCase().replace(/[^a-z0-9\u0900-\u097f]/g, ''))
      .filter((t) => t.length >= 2 && t.length <= 30),
  )).slice(0, 8)
  const text = `${post.title || ''} ${post.caption || ''} ${(post.tags || []).join(' ')}`.toLowerCase()
  const tokens = Array.from(new Set(text.split(/[^a-z0-9]+/).filter((t) => t.length >= 2)))
  const hits: Record<string, number> = {}
  for (const token of tokens) {
    for (const cat of Object.keys(REEL_CATEGORY_KEYWORDS)) {
      if (REEL_CATEGORY_EXACT[cat].has(token) || REEL_CATEGORY_CONTAINS[cat].some((kw) => token.includes(kw))) {
        hits[cat] = (hits[cat] || 0) + 1
      }
    }
  }
  const cats = Object.entries(hits).sort((a, b) => b[1] - a[1]).slice(0, 2).map(([c]) => c)
  return { cats: cats.length ? cats : ['general'], tags }
}

type ReelInterestState = {
  cats: Record<string, number>        // evaluated profile - this is what ranks the feed
  tags: Record<string, number>
  pendingCats: Record<string, number> // signals collected since the last 24h evaluation
  pendingTags: Record<string, number>
  updatedAt: number                   // when the last 24h evaluation ran (0 = never)
}
type ReelInterestSnapshot = { ready: boolean; cats: Record<string, number>; tags: Record<string, number>; top: string[] }

const emptyReelInterestState = (): ReelInterestState => ({ cats: {}, tags: {}, pendingCats: {}, pendingTags: {}, updatedAt: 0 })

const cleanNumberMap = (raw: unknown, maxKeys = 80): Record<string, number> => {
  const out: Record<string, number> = {}
  if (!raw || typeof raw !== 'object') return out
  Object.entries(raw as Record<string, unknown>)
    .filter(([k, v]) => k && !k.startsWith('__') && typeof v === 'number' && isFinite(v))
    .sort((a, b) => Math.abs(b[1] as number) - Math.abs(a[1] as number))
    .slice(0, maxKeys)
    .forEach(([k, v]) => { out[k] = v as number })
  return out
}

const normalizeReelInterestState = (raw: any): ReelInterestState => ({
  cats: cleanNumberMap(raw?.cats),
  tags: cleanNumberMap(raw?.tags),
  pendingCats: cleanNumberMap(raw?.pendingCats),
  pendingTags: cleanNumberMap(raw?.pendingTags),
  updatedAt: typeof raw?.updatedAt === 'number' ? raw.updatedAt : 0,
})

const sumPositive = (m: Record<string, number>) => Object.values(m).reduce((t, v) => t + Math.max(0, v), 0)

// Adds one engagement signal (like +3, comment/share +2, watched 10s = +1, quick skip = negative).
const addReelSignal = (state: ReelInterestState, post: Post, weight: number) => {
  const { cats, tags } = classifyReel(post)
  cats.forEach((c, i) => { state.pendingCats[c] = (state.pendingCats[c] || 0) + weight * (i === 0 ? 1 : 0.6) })
  tags.slice(0, 5).forEach((t) => { state.pendingTags[t] = (state.pendingTags[t] || 0) + weight * 0.5 })
  state.pendingCats = cleanNumberMap(state.pendingCats)
  state.pendingTags = cleanNumberMap(state.pendingTags)
}

const mergeInterestMaps = (old: Record<string, number>, pending: Record<string, number>, maxKeys: number) => {
  const merged: Record<string, number> = {}
  new Set([...Object.keys(old), ...Object.keys(pending)]).forEach((k) => {
    const v = (old[k] || 0) * REEL_INTEREST_DECAY + (pending[k] || 0)
    if (v > 0.1) merged[k] = Math.round(v * 100) / 100
  })
  return cleanNumberMap(merged, maxKeys)
}

// The "test every 24 hours" step. Returns true when it actually re-evaluated (so the caller saves it).
const evaluateReelInterests = (state: ReelInterestState, now: number): boolean => {
  const pendingTotal = sumPositive(state.pendingCats)
  if (pendingTotal <= 0) return false // no new activity -> keep the current profile as is
  const due = state.updatedAt > 0 ? now - state.updatedAt >= REEL_INTEREST_EVAL_MS : pendingTotal >= REEL_INTEREST_BOOTSTRAP
  if (!due) return false
  state.cats = mergeInterestMaps(state.cats, state.pendingCats, 30)
  state.tags = mergeInterestMaps(state.tags, state.pendingTags, 60)
  state.pendingCats = {}
  state.pendingTags = {}
  state.updatedAt = now
  return true
}

const snapshotReelInterests = (state: ReelInterestState): ReelInterestSnapshot => ({
  ready: sumPositive(state.cats) >= REEL_INTEREST_MIN_READY,
  cats: { ...state.cats },
  tags: { ...state.tags },
  top: Object.entries(state.cats).sort((a, b) => b[1] - a[1]).slice(0, 3).map(([c]) => c),
})

// 0..1: how well one reel (or ad) fits the user's evaluated taste.
const reelInterestMatch = (post: Post, snap: ReelInterestSnapshot): number => {
  const maxCat = Math.max(0, ...Object.values(snap.cats))
  if (maxCat <= 0) return 0
  const maxTag = Math.max(0, ...Object.values(snap.tags))
  const { cats, tags } = classifyReel(post)
  const catScore = Math.max(0, ...cats.map((c) => (snap.cats[c] || 0) / maxCat))
  const tagScore = maxTag > 0 ? Math.max(0, ...tags.map((t) => (snap.tags[t] || 0) / maxTag)) : 0
  return Math.min(1, 0.7 * catScore + 0.3 * tagScore)
}

// Ranks organic content by score, then threads promoted/ad posts back in
// every 4-8 organic items (never at the very front) with a non-fixed gap.
// adFree (Premium users) skips weaving in promoted/boosted posts entirely -
// they still see organic content ranked the same way, just no sponsored cards.
// Exception: a post/reel you boosted yourself always still shows in your own feed -
// adFree only ever hides OTHER people's ads, never your own paid promotion, or
// boosting something would look like it vanished the moment you paid for it.
// `interests` (Reels tab only) personalizes the order and the ads; without it the
// ranking is exactly the old engagement/recency one (Radar feed).
// RANDOM REELS (Instagram-style): `shuffle` adds a fresh random value to every reel's score,
// so each reload / each time the Reels tab opens gives a different order. Reels the user
// already watched recently (`seen`) get pushed back so the feed never restarts from the same reel.
const REEL_SEEN_KEY = 'animora_seen_reels_v1'
const REEL_SEEN_MAX = 300
const readSeenReels = (): Set<string> => {
  try {
    const raw = typeof window !== 'undefined' ? window.localStorage.getItem(REEL_SEEN_KEY) : null
    const arr = raw ? JSON.parse(raw) : []
    return new Set(Array.isArray(arr) ? arr.filter((x) => typeof x === 'string') : [])
  } catch { return new Set() }
}
const markReelSeen = (id: string) => {
  try {
    if (typeof window === 'undefined' || !id) return
    const raw = window.localStorage.getItem(REEL_SEEN_KEY)
    const arr: string[] = raw ? JSON.parse(raw) : []
    const next = [...arr.filter((x) => x !== id), id].slice(-REEL_SEEN_MAX)
    window.localStorage.setItem(REEL_SEEN_KEY, JSON.stringify(next))
  } catch { /* storage unavailable - random order still works */ }
}
type ReelShuffle = { seen: Set<string> }
const SEEN_PENALTY = 0.6 // seen reels drop behind unseen ones (but still appear once unseen run out)

const buildRankedFeed = (list: Post[], adFree = false, interests?: ReelInterestSnapshot | null, userId?: string, shuffle?: ReelShuffle | null): Post[] => {
  const organic = list.filter((p) => !p.promoted)
  const promotedRaw = list.filter((p) => p.promoted && (!adFree || p.authorId === userId))
  const personalized = Boolean(interests?.ready)

  const base = organic.map((post, i) => ({ post, score: scoreOrganicPost(post, i, organic.length) }))
  let ranked: Post[]
  if (personalized && interests) {
    const scores = base.map((b) => b.score)
    const min = Math.min(...scores)
    const span = (Math.max(...scores) - min) || 1
    const scored = base.map((b) => {
      const quality = (b.score - min) / span
      const match = reelInterestMatch(b.post, interests)
      // shuffle: random term + seen penalty, so the interest-matched reels still come first
      // but in a different order on every open.
      const rnd = shuffle ? Math.random() * 0.8 - (shuffle.seen.has(b.post.id) ? SEEN_PENALTY : 0) : 0
      return { post: b.post, quality: shuffle ? quality * 0.3 + rnd : quality, match, final: 0.35 * quality + 0.65 * match + rnd }
    })
    const liked = scored.filter((x) => x.match >= 0.25).sort((a, b) => b.final - a.final)
    const explore = scored.filter((x) => x.match < 0.25).sort((a, b) => b.quality - a.quality)
    ranked = []
    let li = 0, ei = 0
    while (li < liked.length || ei < explore.length) {
      const exploreSlot = ranked.length % REEL_EXPLORE_EVERY === REEL_EXPLORE_EVERY - 1
      if (exploreSlot && ei < explore.length) ranked.push(explore[ei++].post)
      else if (li < liked.length) ranked.push(liked[li++].post)
      else ranked.push(explore[ei++].post)
    }
  } else {
    if (shuffle) {
      const scores = base.map((b) => b.score)
      const min = Math.min(...scores)
      const span = (Math.max(...scores) - min) || 1
      ranked = base
        .map((b) => ({ post: b.post, v: 0.2 * ((b.score - min) / span) + Math.random() - (shuffle.seen.has(b.post.id) ? SEEN_PENALTY : 0) }))
        .sort((a, b) => b.v - a.v)
        .map((x) => x.post)
    } else {
      ranked = base.sort((a, b) => b.score - a.score).map((x) => x.post)
    }
  }

  // Ads matching the user's taste go first; non-matching ones only fill every 3rd ad slot.
  let promoted = promotedRaw
  if (personalized && interests && promotedRaw.length > 1) {
    const withMatch = promotedRaw.map((p) => ({ p, m: reelInterestMatch(p, interests) }))
    const matched = withMatch.filter((x) => x.m >= 0.35).sort((a, b) => b.m - a.m).map((x) => x.p)
    const other = withMatch.filter((x) => x.m < 0.35).map((x) => x.p)
    if (matched.length && other.length) {
      promoted = []
      let mi = 0, oi = 0
      while (mi < matched.length || oi < other.length) {
        if (mi < matched.length) promoted.push(matched[mi++])
        if (mi < matched.length) promoted.push(matched[mi++])
        if (oi < other.length) promoted.push(other[oi++])
      }
    } else {
      promoted = [...matched, ...other]
    }
  }

  if (promoted.length === 0) return ranked

  const gapFor = (adIndex: number) => 4 + Math.floor(hashToUnit(`${promoted[adIndex % promoted.length].id}:${adIndex}`) * 5) // 4-8

  const result: Post[] = []
  let promoIdx = 0
  let sinceLastAd = 0
  let gap = gapFor(0)
  for (const post of ranked) {
    result.push(post)
    sinceLastAd++
    if (sinceLastAd >= gap && promoIdx < promoted.length) {
      result.push(promoted[promoIdx % promoted.length])
      promoIdx++
      sinceLastAd = 0
      gap = gapFor(promoIdx)
    }
  }
  // A short feed (fewer organic posts than one ad-gap, e.g. a brand-new account) never
  // reaches the "sinceLastAd >= gap" threshold above, so any boosted/promoted post would
  // otherwise never get inserted at all - it would look like it "disappeared" the moment
  // it was boosted. Always show every promoted post: whatever didn't find a natural gap
  // is appended at the end instead of being silently dropped from the feed.
  for (; promoIdx < promoted.length; promoIdx++) {
    result.push(promoted[promoIdx])
  }
  return result
}

// ---- Session mood: the last few reels the viewer reacted to steer what comes NEXT in this visit ----
// The long-term taste profile (24h check) stays untouched. On top of it, every swipe re-orders only the
// reels that are still far ahead (never the ones on / right next to the screen, so nothing jumps under the
// thumb). Likes / long watches pull that topic forward, quick skips push it back. Session only: it starts
// fresh every time the Reels tab opens.
type ReelMoodEvent = { keys: Array<[string, number]>; w: number }
const REEL_MOOD_EVENTS = 12 // how many recent reactions count
const REEL_MOOD_DECAY = 0.85 // each older reaction counts 15% less
const REEL_MOOD_MIN = 2 // a like (+3) or ~20s of watching (+2) is needed before the mood kicks in
const REEL_MOOD_FROZEN_AHEAD = 3 // current reel + next 3 never move
const REEL_MOOD_OFFTOPIC_EVERY = 4 // every 4th upcoming slot stays off-mood (variety)

const reelMoodKeys = (post: Post): Array<[string, number]> => {
  const { cats, tags } = classifyReel(post)
  const keys: Array<[string, number]> = []
  cats.forEach((c, i) => { if (c !== 'general') keys.push([`c:${c}`, i === 0 ? 1 : 0.6]) })
  tags.slice(0, 5).forEach((t) => keys.push([`t:${t}`, 0.5]))
  return keys
}

const computeReelMood = (events: ReelMoodEvent[]): Record<string, number> => {
  const mood: Record<string, number> = {}
  events.forEach((e, i) => {
    const decay = Math.pow(REEL_MOOD_DECAY, events.length - 1 - i)
    const w = (e.w < 0 ? e.w * 2 : e.w) * decay // a skip in the middle of a session is a strong "not now"
    e.keys.forEach(([k, share]) => { mood[k] = (mood[k] || 0) + w * share })
  })
  return mood
}

// Returns the new id order, or null when the mood is not clear yet / nothing would change.
const reorderReelsByMood = (reels: Post[], frozenEnd: number, events: ReelMoodEvent[]): string[] | null => {
  if (events.length < 2) return null
  const mood = computeReelMood(events)
  const maxPos = Math.max(0, ...Object.values(mood))
  if (maxPos < REEL_MOOD_MIN) return null
  const head = reels.slice(0, Math.max(0, frozenEnd) + 1)
  const tail = reels.slice(Math.max(0, frozenEnd) + 1)
  if (tail.length < 3) return null
  // Ads keep their exact slots; only normal reels are shuffled among the remaining slots.
  const slots: number[] = []
  tail.forEach((p, i) => { if (!p.promoted) slots.push(i) })
  const movable = slots.map((i) => tail[i])
  const matched: Post[] = []
  const neutral: Post[] = []
  const disliked: Post[] = []
  for (const p of movable) {
    const keys = reelMoodKeys(p)
    const known = keys.map(([k]) => mood[k]).filter((v) => v !== undefined)
    const top = known.length ? Math.max(...known) : 0
    if (top >= 0.6 * maxPos) matched.push(p)
    else if (top <= -0.8) disliked.push(p)
    else neutral.push(p)
  }
  const out: Post[] = []
  let mi = 0, ni = 0, di = 0
  while (out.length < movable.length) {
    const offTopicSlot = out.length % REEL_MOOD_OFFTOPIC_EVERY === REEL_MOOD_OFFTOPIC_EVERY - 1
    if (offTopicSlot && ni < neutral.length) out.push(neutral[ni++])
    else if (mi < matched.length) out.push(matched[mi++])
    else if (ni < neutral.length) out.push(neutral[ni++])
    else out.push(disliked[di++])
  }
  const nextTail = tail.slice()
  slots.forEach((slot, i) => { nextTail[slot] = out[i] })
  const ids = [...head, ...nextTail].map((p) => p.id)
  return ids.join('|') === reels.map((p) => p.id).join('|') ? null : ids
}

type ReelRow = { kind: 'reel'; post: Post; realIndex: number } | { kind: 'friends'; key: string; slot: number }
const buildReelRows = (reels: Post[]): ReelRow[] => reels.map((post, i) => ({ kind: 'reel', post, realIndex: i }))

// Drops a full-screen "people you may know" slide (2x2 grid of 4 random users) into the Reels feed:
// after the 3rd reel, then again every 10 reels. `slot` picks which 4 users of the suggestion pool it shows.
const REEL_FRIENDS_FIRST_AFTER = 3
const REEL_FRIENDS_EVERY = 10
const weaveFriendRows = (rows: ReelRow[], enabled: boolean): ReelRow[] => {
  if (!enabled) return rows
  const out: ReelRow[] = []
  let realSeen = 0
  let slot = 0
  let nextAt = REEL_FRIENDS_FIRST_AFTER
  const totalReal = rows.filter((r) => r.kind === 'reel').length
  for (const row of rows) {
    out.push(row)
    if (row.kind !== 'reel') continue
    realSeen++
    if (realSeen === nextAt || (realSeen === totalReal && slot === 0)) {
      out.push({ kind: 'friends', key: `friends-reel-${slot}`, slot })
      slot++
      nextAt = realSeen + REEL_FRIENDS_EVERY
    }
  }
  return out
}

// Client-Side WebP Compression for Photos
const compressMediaFile = async (file: File, maxDim = 1080, quality = 0.8): Promise<File> => {
  if (!file.type.startsWith('image/') || file.type === 'image/gif') return file
  return new Promise((resolve) => {
    const reader = new FileReader()
    reader.onload = (e) => {
      const img = new Image()
      img.onload = () => {
        let { width, height } = img
        if (width > maxDim || height > maxDim) {
          if (width > height) {
            height = Math.round((height * maxDim) / width)
            width = maxDim
          } else {
            width = Math.round((width * maxDim) / height)
            height = maxDim
          }
        }
        const canvas = document.createElement('canvas')
        canvas.width = width
        canvas.height = height
        const ctx = canvas.getContext('2d')
        if (!ctx) return resolve(file)
        ctx.drawImage(img, 0, 0, width, height)

        canvas.toBlob(
          (blob) => {
            if (!blob) return resolve(file)
            const cleanName = file.name.replace(/\.[^/.]+$/, '') + '.webp'
            resolve(new File([blob], cleanName, { type: 'image/webp' }))
          },
          'image/webp',
          quality
        )
      }
      img.onerror = () => resolve(file)
      img.src = e.target?.result as string
    }
    reader.onerror = () => resolve(file)
    reader.readAsDataURL(file)
  })
}

// Raw-PCM-to-WAV encoder. Kept as a universal fallback for browsers that can't
// record compressed audio (see trimAudioTo60s below).
const audioBufferToWav = (buffer: AudioBuffer): Blob => {
  const numOfChan = buffer.numberOfChannels
  const frameCount = buffer.length
  const length = frameCount * numOfChan * 2 + 44
  const out = new DataView(new ArrayBuffer(length))
  const channels: Float32Array[] = []
  let pos = 0

  const setUint16 = (data: number) => { out.setUint16(pos, data, true); pos += 2 }
  const setUint32 = (data: number) => { out.setUint32(pos, data, true); pos += 4 }

  setUint32(0x46464952); setUint32(length - 8); setUint32(0x45564157); setUint32(0x20746d66)
  setUint32(16); setUint16(1); setUint16(numOfChan); setUint32(buffer.sampleRate)
  setUint32(buffer.sampleRate * 2 * numOfChan); setUint16(numOfChan * 2); setUint16(16)
  setUint32(0x61746164); setUint32(length - pos - 4)

  for (let i = 0; i < numOfChan; i++) channels.push(buffer.getChannelData(i))
  let offset = 0
  while (pos < length) {
    for (let i = 0; i < numOfChan; i++) {
      const sample = Math.max(-1, Math.min(1, channels[i][offset]))
      out.setInt16(pos, (0.5 + sample < 0 ? sample * 32768 : sample * 32767) | 0, true)
      pos += 2
    }
    offset++
  }
  return new Blob([out.buffer], { type: 'audio/wav' })
}

const trimBufferToWav = (audioCtx: AudioContext, decoded: AudioBuffer, startSec: number, durationSec: number): Blob => {
  const sampleRate = decoded.sampleRate
  const startOffset = Math.floor(startSec * sampleRate)
  const endOffset = Math.floor(Math.min(startSec + durationSec, decoded.duration) * sampleRate)
  const frameCount = Math.max(0, endOffset - startOffset)
  const trimmedBuffer = audioCtx.createBuffer(decoded.numberOfChannels, frameCount, sampleRate)
  for (let i = 0; i < decoded.numberOfChannels; i++) {
    const channelData = decoded.getChannelData(i).subarray(startOffset, endOffset)
    trimmedBuffer.copyToChannel(channelData, i, 0)
  }
  return audioBufferToWav(trimmedBuffer)
}

// Client-Side Audio Trim + Compress via Web Audio API.
// Trims to the chosen window (max 60s) and re-encodes as small Opus/WebM instead of
// raw WAV (~10MB/minute). A big uncompressed WAV forces the multi-request chunked
// upload path, which is the most likely reason a song-attached post would upload fine
// on good wifi but silently never finish (and so never show up) on slower mobile data.
// Falls back to plain WAV if compressed recording isn't supported on this browser.
const trimAudioTo60s = async (file: File, startSec: number, durationSec = 60): Promise<Blob> => {
  try {
    const arrayBuffer = await file.arrayBuffer()
    const AudioCtxCls = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext
    const audioCtx = new AudioCtxCls()
    const decoded = await audioCtx.decodeAudioData(arrayBuffer)

    const startOffset = Math.max(0, Math.min(startSec, decoded.duration))
    const clipDuration = Math.max(0.1, Math.min(durationSec, decoded.duration - startOffset))

    const canCompress =
      typeof MediaRecorder !== 'undefined' &&
      typeof audioCtx.createMediaStreamDestination === 'function' &&
      (MediaRecorder.isTypeSupported('audio/webm;codecs=opus') || MediaRecorder.isTypeSupported('audio/webm'))

    if (!canCompress) return trimBufferToWav(audioCtx, decoded, startOffset, clipDuration)

    return await new Promise<Blob>((resolve) => {
      const dest = audioCtx.createMediaStreamDestination()
      const src = audioCtx.createBufferSource()
      src.buffer = decoded
      src.connect(dest)

      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus') ? 'audio/webm;codecs=opus' : 'audio/webm'
      let recorder: MediaRecorder
      try {
        recorder = new MediaRecorder(dest.stream, { mimeType, audioBitsPerSecond: 96000 })
      } catch {
        resolve(trimBufferToWav(audioCtx, decoded, startOffset, clipDuration))
        return
      }

      const chunks: BlobPart[] = []
      recorder.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunks.push(e.data) }

      let settled = false
      const finish = () => {
        if (settled) return
        settled = true
        try { recorder.stop() } catch {}
      }

      recorder.onstop = () => {
        const blob = new Blob(chunks, { type: 'audio/webm' })
        resolve(blob.size > 0 ? blob : trimBufferToWav(audioCtx, decoded, startOffset, clipDuration))
      }

      recorder.start()
      src.start(0, startOffset, clipDuration)
      src.onended = () => setTimeout(finish, 200)
      setTimeout(finish, (clipDuration + 1.5) * 1000) // safety cutoff
    })
  } catch {
    // Absolute last resort: upload the original file untouched rather than blocking the post.
    return file
  }
}

// Client-Side Video Compression (canvas + MediaRecorder re-encode) before Story upload.
// Targets a small output size (~targetSizeMB) so story video uploads are fast and
// reliable on slow mobile connections. Always falls back to the original file if
// anything here is unsupported or fails - compression should never block a story post.
const compressVideoFile = (file: File, targetSizeMB = 3): Promise<File> => {
  return new Promise((resolve) => {
    if (typeof window === 'undefined' || typeof MediaRecorder === 'undefined') {
      resolve(file)
      return
    }

    const video = document.createElement('video')
    video.muted = true
    video.playsInline = true
    video.preload = 'auto'
    const objectUrl = URL.createObjectURL(file)
    video.src = objectUrl

    let settled = false
    const finishWithFallback = () => {
      if (settled) return
      settled = true
      URL.revokeObjectURL(objectUrl)
      resolve(file)
    }
    // This only needs to catch "metadata never loads" (corrupt/unsupported file) - the
    // real, duration-aware safety timer gets set below once we know how long the video
    // actually is. A fixed short timeout here used to be the ONLY guard, which meant any
    // clip longer than it got silently uploaded raw, uncompressed, at full size.
    let guardTimer = setTimeout(finishWithFallback, 10000)
    video.onerror = finishWithFallback

    video.onloadedmetadata = () => {
      try {
        const duration = Math.min(Math.max(video.duration || 10, 1), 65)

        // Re-encoding here works by literally playing the video and capturing canvas
        // frames in real time, so it takes at least `duration` seconds of wall-clock
        // time to finish - the safety cutoff must give it that much room (plus a solid
        // buffer for encoder/draw overhead) or it'll get killed mid-compression and fall
        // back to uploading the untouched original.
        clearTimeout(guardTimer)
        guardTimer = setTimeout(finishWithFallback, (duration + 12) * 1000)

        const maxDim = 720
        let w = video.videoWidth || 640
        let h = video.videoHeight || 360
        if (w > maxDim || h > maxDim) {
          if (w > h) { h = Math.round((h * maxDim) / w); w = maxDim } else { w = Math.round((w * maxDim) / h); h = maxDim }
        }

        const canvas = document.createElement('canvas')
        canvas.width = w
        canvas.height = h
        const ctx = canvas.getContext('2d')
        if (!ctx || typeof (canvas as any).captureStream !== 'function') { finishWithFallback(); return }

        // 24fps instead of 30 - a smaller, more reliably-sustainable target on a mid-range
        // phone's browser. Asking for a frame rate the device can't actually keep drawing
        // is what caused the choppy/stuttery playback: the recorder still stamps frames as
        // if 30 were being hit, so a device that can only really manage ~20 ends up with
        // uneven frame timing baked into the file. Dropping to 24 leaves real headroom.
        const canvasStream: MediaStream = (canvas as any).captureStream(24)
        try {
          const mediaStream: MediaStream | undefined = (video as any).captureStream?.() || (video as any).mozCaptureStream?.()
          const audioTrack = mediaStream?.getAudioTracks?.()[0]
          if (audioTrack) canvasStream.addTrack(audioTrack)
        } catch { /* fine - compress video-only */ }

        const mimeCandidates = ['video/webm;codecs=vp8,opus', 'video/webm;codecs=vp8', 'video/webm']
        const mimeType = mimeCandidates.find((m) => MediaRecorder.isTypeSupported(m)) || ''
        if (!mimeType) { finishWithFallback(); return }

        // Bitrate floor raised well above the old 150kbps - that low a bitrate is what
        // produced the blocky, stuttery-looking motion (the encoder has to throw away so
        // much detail between frames that playback itself looks laggy even though the
        // frame rate is fine). Ceiling raised too so shorter clips get real headroom.
        const targetBits = targetSizeMB * 8 * 1024 * 1024
        const totalBitrate = Math.max(800_000, Math.min(6_000_000, Math.floor(targetBits / duration)))
        const audioBitrate = 96_000
        const videoBitrate = Math.max(700_000, totalBitrate - audioBitrate)

        let recorder: MediaRecorder
        try {
          recorder = new MediaRecorder(canvasStream, { mimeType, videoBitsPerSecond: videoBitrate, audioBitsPerSecond: audioBitrate })
        } catch { finishWithFallback(); return }

        const chunks: BlobPart[] = []
        recorder.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunks.push(e.data) }

        let rafId = 0
        let stopped = false
        const stopAll = () => {
          if (stopped) return
          stopped = true
          cancelAnimationFrame(rafId)
          try { recorder.stop() } catch {}
        }

        recorder.onstop = () => {
          clearTimeout(guardTimer)
          settled = true
          video.pause()
          URL.revokeObjectURL(objectUrl)
          const blob = new Blob(chunks, { type: 'video/webm' })
          if (!blob.size) { resolve(file); return }
          const cleanName = file.name.replace(/\.[^/.]+$/, '') + '.webm'
          resolve(new File([blob], cleanName, { type: 'video/webm' }))
        }

        const drawFrame = () => {
          if (stopped) return
          try { ctx.drawImage(video, 0, 0, w, h) } catch {}
          rafId = requestAnimationFrame(drawFrame)
        }

        video.onended = stopAll
        video.currentTime = 0
        recorder.start(250)
        video.play().then(drawFrame).catch(stopAll)
        setTimeout(stopAll, (duration + 2) * 1000) // stop shortly after playback should end
      } catch {
        finishWithFallback()
      }
    }
  })
}

// Videos are compressed to land somewhere between 4MB and 20MB depending on how long the
// clip is (roughly 1MB per 3s of footage) - this, combined with the higher bitrate floor
// in compressVideoFile above, is what keeps playback smooth instead of laggy: too small a
// target starves the encoder of bits and the video ends up choppy even at a normal frame
// rate.
//
// Anything already at/under 20MB is left completely untouched. The re-encode path below
// (canvas draw + MediaRecorder) plays the source video in real time and re-captures it -
// on a well-encoded CapCut/phone export this is what caused the crackling audio and
// hangs people were seeing: the browser can't always keep drawing/encoding in perfect
// lockstep with playback, so the captured audio and video drift out of sync. A modern
// phone or CapCut export at normal quality is already efficiently encoded (H.264/HEVC),
// so skipping the browser re-encode for anything reasonably sized both avoids that bug
// and keeps the original quality - exactly why videos forwarded via WhatsApp (already
// small) always uploaded smoothly, while big, untouched CapCut exports didn't.
// Android WebView bug: a file picked from certain apps (screen recorder exports, CapCut's
// export folder, some OEM gallery/"recent files" pickers) comes back as a File backed by a
// content:// URI whose .size reports 0 until the underlying bytes are actually read - the
// WebView only resolves the real size once something touches the blob. Any code that trusts
// .size right after picking (compression decisions, upload chunking) sees "0 bytes" and either
// skips compression it should have done or ships an effectively empty body, which the network
// layer then aborts as "Failed to fetch". Call this once, right after picking, to force a real
// read and get back a File with an accurate size.
const materializeFileIfEmpty = async (file: File): Promise<File> => {
  if (file.size > 0) return file
  // Android content:// URI files (CapCut exports, screen-recorder saves, some gallery
  // pickers) can keep reporting 0 bytes for a short window right after the system picker
  // closes, even though the video plays fine from its object URL - the content provider
  // hasn't finished handing real bytes to the browser yet. A SINGLE read attempted too
  // early can genuinely come back empty even for a perfectly valid file, which is exactly
  // the "size shows 0MB, upload never starts" bug: it looks fine in the <video> preview
  // (that goes through a different, more forgiving playback path) but the raw byte read
  // used for uploading comes back empty. Retry the read a few times with growing delays -
  // the SAME file often reads correctly a moment later once the provider has settled.
  for (let attempt = 0; attempt < 5; attempt++) {
    await new Promise((r) => setTimeout(r, 300 * (attempt + 1)))
    if (file.size > 0) return file
    try {
      const buf = await file.arrayBuffer()
      if (buf.byteLength > 0) return new File([buf], file.name || `file_${Date.now()}`, { type: file.type })
    } catch {
      /* transient content-provider hiccup - keep retrying rather than giving up immediately */
    }
    // Some content:// files go further than "slow" - Blob.arrayBuffer()/slice() on them
    // keeps returning 0 bytes indefinitely even though a <video>/<img> element can stream
    // them fine (that's a completely different, more permissive OS-level read path, which
    // is exactly why the preview plays perfectly while the upload sees "0MB"). Re-fetching
    // the SAME file through its own object URL forces it through the network/blob stack
    // instead of the plain FileReader path, and reliably succeeds where arrayBuffer() alone
    // is stuck at 0 for this class of file.
    let objectUrl: string | null = null
    try {
      objectUrl = URL.createObjectURL(file)
      const res = await fetch(objectUrl)
      const blob = await res.blob()
      if (blob.size > 0) return new File([blob], file.name || `file_${Date.now()}`, { type: file.type || blob.type })
    } catch {
      /* still nothing this round - loop again, or fall through to the original file below */
    } finally {
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
  }
  // Genuinely unreadable after several tries and both read strategies - return the
  // original; downstream size checks will surface a clear "try picking it again" error
  // instead of hanging silently.
  return file
}

const compressVideoIfNeeded = async (rawFile: File, onProgress?: (fraction: number) => void): Promise<File> => {
  const file = await materializeFileIfEmpty(rawFile)
  // Real WebCodecs transcode: long edge capped at 1080p, 30fps, H.264/AAC MP4 with the index up
  // front (see lib/video-transcode.ts). It decides by RESOLUTION / codec / bitrate, not by file
  // size, and it tries several encoder sizes before giving up. Already web-friendly files
  // ('skipped') are uploaded as they are.
  try {
    const { prepareVideoForWeb } = await import('@/lib/video-transcode')
    const prepared = await prepareVideoForWeb(file, onProgress)
    if (prepared.status === 'done') return prepared.file
    if (prepared.status === 'skipped') return file
  } catch { /* fall through: upload the original */ }
  // 'unsupported' / 'failed' (this device cannot decode or encode it, e.g. 4K HEVC on an old
  // phone): upload the ORIGINAL untouched. The old fallback here re-encoded the video in real
  // time through a canvas (compressVideoFile above) - on 4K that hangs, drops frames and bakes
  // stutter / low quality into the file. uploadWithProgress now handles files of any size
  // (chunked, per-part retry), so the original always goes up, in full quality.
  return file
}

// Reads a video file's duration (seconds) without playing it - used to enforce the
// 60-second cap on Story and Post video uploads before we bother compressing/uploading.
const readVideoDuration = (file: File): Promise<number> => {
  return new Promise((resolve) => {
    const v = document.createElement('video')
    v.preload = 'metadata'
    const objectUrl = URL.createObjectURL(file)
    v.src = objectUrl
    v.onloadedmetadata = () => {
      URL.revokeObjectURL(objectUrl)
      resolve(v.duration || 0)
    }
    v.onerror = () => {
      URL.revokeObjectURL(objectUrl)
      resolve(0)
    }
  })
}

// Video size + length without playing it (needed for the cover image and the feed card ratio).
const readVideoInfo = (file: File): Promise<{ duration: number; width: number; height: number }> => {
  return new Promise((resolve) => {
    const v = document.createElement('video')
    v.preload = 'metadata'
    const objectUrl = URL.createObjectURL(file)
    const done = (r: { duration: number; width: number; height: number }) => { URL.revokeObjectURL(objectUrl); resolve(r) }
    v.onloadedmetadata = () => done({ duration: v.duration || 0, width: v.videoWidth || 0, height: v.videoHeight || 0 })
    v.onerror = () => done({ duration: 0, width: 0, height: 0 })
    v.src = objectUrl
  })
}

// Video covers are always saved as a JPEG with the SAME aspect ratio as the video. That is what
// makes one cover fit everywhere: full-screen Reels, the feed card and the Discover tile all show
// it without stretching, and it lines up exactly with the video when playback starts.
const COVER_MAX_SIDE = 960
const canvasToJpeg = (c: HTMLCanvasElement): Promise<Blob | null> =>
  new Promise((resolve) => { try { c.toBlob((b) => resolve(b), 'image/jpeg', 0.86) } catch { resolve(null) } })

// A video is a "reel" when it was uploaded as one (or is an old video with no kind set); post-videos stay in Radar only.
// Every VIDEO (reel or video posted as a normal post) opens in the Reels feed when tapped from a profile grid / notification / shared link.
const isReelPost = (p: { mediaType?: string; postKind?: string }) => p.mediaType === 'video'
// Feed posts that ALSO play in the Reels feed: post-videos (video posted as a normal post, incl. video + extra
// photos) and multi-photo slide posts. postKind is NOT changed, so Radar/feed layout and reel rewards stay as they were.
const isPostVideo = (p: { mediaType?: string; postKind?: string; promoted?: boolean; previewOnly?: boolean }) => p.mediaType === 'video' && p.postKind === 'post' && !p.promoted && !p.previewOnly
// Shonen is now the ONLY feed: every post (reel, post video, single/multi photo, boosted post) plays in it.
const isReelFeedPost = (p: any) => !p.previewOnly
// ===== Admin restrictions: chat ban + upload hold (set by the admin, enforced in lib/d1-rules.ts + /api/upload) =====
const RESTRICT_FOREVER = 32503680000000 // year 3000 = "permanent"
let adminModeFlag = false // mirrors isAdminAccount so deep components can show admin-only buttons
let selfRestrict = { chatBanUntil: 0, chatBanReason: '', uploadHoldUntil: 0, uploadHoldReason: '' } // the signed-in user's own restrictions
const restrictLeft = (until: unknown): number => (typeof until === 'number' && until > Date.now() ? until : 0)
const restrictWhen = (until: number) => (until >= RESTRICT_FOREVER ? 'permanently' : `until ${new Date(until).toLocaleString()}`)
const chatBanMessage = (): string => {
  const u = restrictLeft(selfRestrict.chatBanUntil)
  return u ? `Chat is disabled on your account ${restrictWhen(u)}.${selfRestrict.chatBanReason ? ` Reason: ${selfRestrict.chatBanReason}` : ''}` : ''
}
const uploadHoldMessage = (): string => {
  const u = restrictLeft(selfRestrict.uploadHoldUntil)
  return u ? `Uploads are on hold for your account ${restrictWhen(u)}.${selfRestrict.uploadHoldReason ? ` Reason: ${selfRestrict.uploadHoldReason}` : ''}` : ''
}
const REEL_RATIO = 9 / 16
// Instagram-style fit for the full-screen Reels feed. A portrait (9:16-ish) video fills the whole screen even on
// tall 20:9 phones (a little of the sides is trimmed, like Instagram does) instead of sitting letterboxed with
// black bars. Landscape / square / 4:5 media and anything that would lose too much of its sides stay fully visible.
// `mediaRatio` / `boxRatio` are width / height.
const REEL_FILL_MAX_CROP = 0.25 // never trim more than 25% of the picture to fill the screen
const pickReelFit = (mediaRatio: number, boxRatio: number): 'cover' | 'contain' => {
  if (!(mediaRatio > 0) || !(boxRatio > 0)) return 'contain'
  const r = mediaRatio / boxRatio
  if (Math.abs(r - 1) <= 0.05) return 'cover' // already the same shape as the screen
  if (mediaRatio > 0.7) return 'contain' // 4:5 / square / landscape: show the whole picture
  const kept = r > 1 ? 1 / r : r // share of the picture that stays visible after centre-cropping
  return 1 - kept <= REEL_FILL_MAX_CROP ? 'cover' : 'contain'
}
const POST_RATIO_MIN = 0.75
const POST_RATIO_MAX = 1.91
const clampPostRatio = (r: number) => Math.min(POST_RATIO_MAX, Math.max(POST_RATIO_MIN, r > 0 ? r : 1))
// Home feed media runs edge to edge in the two Instagram feed shapes: square 1:1 (1080x1080) and tall portrait
// 4:5 (1080x1350). Anything squarer than ~9:10 (square + landscape) becomes the square; everything else (portrait,
// 3:4, 9:16) becomes the 4:5. The picture fills the shape (centre-crop), so there are never black bars.
// ===== Reliable send =====================================================================================
// A message used to be written exactly ONCE with no time limit: one dropped packet / network switch (5G <-> Wi-Fi) / slow
// server answer and the person saw "Message send failed." even though nothing was really wrong. Now every send:
//   1. gives each attempt a short deadline (a hung connection is abandoned quickly instead of waiting a minute),
//   2. retries on a fresh connection when the failure is transient (network / timeout / write conflict),
//   3. before every retry asks the server whether the previous attempt actually landed - the message id is fixed, so a
//      message that did go through is never sent twice and never reported as failed.
// Only real refusals (banned, not allowed, rate limited ...) surface as "failed".
const SEND_ATTEMPT_TIMEOUTS_MS = [7000, 9000, 12000, 15000]
const SEND_RETRY_DELAYS_MS = [250, 700, 1500]
const isTransientSendError = (e: any) => {
  const c = e?.code
  if (c === 'unavailable' || c === 'aborted' || c === 'internal' || c === 'timeout' || c === 'deadline-exceeded') return true
  return !c && /network|fetch|timeout|failed to load|load failed/i.test(String(e?.message || ''))
}
async function sendWithRetry(write: () => Promise<unknown>, landed: () => Promise<boolean>): Promise<void> {
  let lastErr: unknown
  for (let i = 0; i < SEND_ATTEMPT_TIMEOUTS_MS.length; i++) {
    if (i > 0) {
      await new Promise((r) => setTimeout(r, SEND_RETRY_DELAYS_MS[Math.min(i - 1, SEND_RETRY_DELAYS_MS.length - 1)]))
      try { if (await landed()) return } catch { /* cannot tell - writing the same id again is harmless */ }
    }
    let timer: ReturnType<typeof setTimeout> | undefined
    try {
      await Promise.race([
        write(),
        new Promise((_, rej) => { timer = setTimeout(() => rej(Object.assign(new Error('send-timeout'), { code: 'timeout' })), SEND_ATTEMPT_TIMEOUTS_MS[i]) }),
      ])
      return
    } catch (e) {
      lastErr = e
      if (!isTransientSendError(e)) {
        // A refusal right after a lost answer usually means the first attempt DID land (the same id now "already exists").
        if (i > 0) { try { if (await landed()) return } catch { /* fall through */ } }
        throw e
      }
    } finally { if (timer) clearTimeout(timer) }
  }
  try { if (await landed()) return } catch { /* ignore */ }
  throw lastErr
}
const FEED_SQUARE_RATIO = 1
const FEED_TALL_RATIO = 4 / 5
const snapFeedRatio = (r: number) => (r > 0 && r >= 0.9 ? FEED_SQUARE_RATIO : FEED_TALL_RATIO)

// Grabs a frame from the video itself (used when the person does not pick a cover).
// Rather than always grabbing the very first available frame - which on edits, anime
// clips and memes is very often a black fade-in or a plain title card, i.e. a bad,
// unrepresentative cover - this samples a few points across the first half of the clip
// and keeps whichever one looks the most "alive" (highest pixel variance, i.e. not a
// flat/black/solid-color frame), so the auto-picked cover actually looks like the video.
const FRAME_SCORE_SIZE = 64 // tiny offscreen canvas used only to score candidates, never saved/uploaded

const frameInterestScore = (v: HTMLVideoElement): number => {
  try {
    const c = document.createElement('canvas')
    c.width = FRAME_SCORE_SIZE
    c.height = FRAME_SCORE_SIZE
    const ctx = c.getContext('2d', { willReadFrequently: true })
    if (!ctx) return 0
    ctx.drawImage(v, 0, 0, FRAME_SCORE_SIZE, FRAME_SCORE_SIZE)
    const { data } = ctx.getImageData(0, 0, FRAME_SCORE_SIZE, FRAME_SCORE_SIZE)
    let sum = 0, sumSq = 0
    const n = FRAME_SCORE_SIZE * FRAME_SCORE_SIZE
    for (let i = 0; i < data.length; i += 4) {
      const lum = data[i] * 0.299 + data[i + 1] * 0.587 + data[i + 2] * 0.114
      sum += lum
      sumSq += lum * lum
    }
    const mean = sum / n
    const variance = sumSq / n - mean * mean
    // Near-pure-black or near-pure-white frames (fades, plain title cards) get pushed
    // down even if they have some text/logo detail pushing their variance up.
    const brightnessPenalty = mean < 18 || mean > 235 ? 0.25 : 1
    return variance * brightnessPenalty
  } catch { return 0 }
}

// `ratio` centre-crops the frame to a fixed shape (reel = 9:16, post = the video's own shape).
const captureVideoFrame = (file: File, ratio?: number): Promise<Blob | null> => {
  return new Promise((resolve) => {
    const v = document.createElement('video')
    v.muted = true
    v.playsInline = true
    v.preload = 'auto'
    // Off-screen but attached: iOS Safari won't seek/decode a video that is not in the page.
    v.style.cssText = 'position:fixed;left:-9999px;top:0;width:2px;height:2px;opacity:0;pointer-events:none'
    document.body.appendChild(v)
    const objectUrl = URL.createObjectURL(file)
    let settled = false
    const finish = (b: Blob | null) => {
      if (settled) return
      settled = true
      clearTimeout(timer)
      v.remove()
      URL.revokeObjectURL(objectUrl)
      resolve(b)
    }
    const timer = setTimeout(() => finish(null), 12000)
    v.onerror = () => finish(null)

    const seekTo = (t: number) => new Promise<void>((res) => {
      const onSeeked = () => { v.removeEventListener('seeked', onSeeked); res() }
      v.addEventListener('seeked', onSeeked)
      try { v.currentTime = t } catch { onSeeked() }
    })

    const captureAt = async (targetRatio?: number): Promise<Blob | null> => {
      const w = v.videoWidth, h = v.videoHeight
      if (!w || !h) return null
      let sx = 0, sy = 0, sw = w, sh = h
      if (targetRatio && targetRatio > 0) {
        if (w / h > targetRatio) { sw = h * targetRatio; sx = (w - sw) / 2 }
        else { sh = w / targetRatio; sy = (h - sh) / 2 }
      }
      const scale = Math.min(1, COVER_MAX_SIDE / Math.max(sw, sh))
      const c = document.createElement('canvas')
      c.width = Math.max(1, Math.round(sw * scale))
      c.height = Math.max(1, Math.round(sh * scale))
      c.getContext('2d')?.drawImage(v, sx, sy, sw, sh, 0, 0, c.width, c.height)
      return canvasToJpeg(c)
    }

    v.onloadedmetadata = async () => {
      try {
        const duration = v.duration || 0
        // Spread candidates across the first half of the clip - a fade-in/title card is
        // almost always within the first couple of seconds, and going too far into a
        // short clip risks landing past the part that's actually worth showing.
        const raw = duration > 1.2
          ? [0.15, duration * 0.15, duration * 0.35, duration * 0.55]
          : [Math.min(0.6, Math.max(0.05, duration * 0.5 || 0.3))]
        const candidates = Array.from(new Set(raw.map((t) => Math.min(Math.max(duration - 0.05, 0.05), Math.max(0.05, t)))))

        let bestTime = candidates[0]
        let bestScore = -1
        for (const t of candidates) {
          await seekTo(t)
          const score = frameInterestScore(v)
          if (score > bestScore) { bestScore = score; bestTime = t }
        }
        await seekTo(bestTime)
        finish(await captureAt(ratio))
      } catch { finish(null) }
    }
    v.src = objectUrl
  })
}

// Centre-crops a picked image to the video's ratio (what you see in the preview is what is saved).
const cropImageToRatio = (file: File, ratio: number): Promise<Blob | null> => {
  return new Promise((resolve) => {
    const img = new Image()
    const objectUrl = URL.createObjectURL(file)
    img.onload = async () => {
      try {
        let sw = img.naturalWidth, sh = img.naturalHeight
        if (!sw || !sh) { resolve(null); return }
        let sx = 0, sy = 0
        if (sw / sh > ratio) { const nw = sh * ratio; sx = (sw - nw) / 2; sw = nw }
        else { const nh = sw / ratio; sy = (sh - nh) / 2; sh = nh }
        const outW = ratio >= 1 ? COVER_MAX_SIDE : Math.round(COVER_MAX_SIDE * ratio)
        const outH = Math.max(1, Math.round(outW / ratio))
        const c = document.createElement('canvas')
        c.width = Math.max(1, outW)
        c.height = outH
        c.getContext('2d')?.drawImage(img, sx, sy, sw, sh, 0, 0, c.width, c.height)
        resolve(await canvasToJpeg(c))
      } catch { resolve(null) }
      finally { URL.revokeObjectURL(objectUrl) }
    }
    img.onerror = () => { URL.revokeObjectURL(objectUrl); resolve(null) }
    img.src = objectUrl
  })
}

// Retries a fetch several times before giving up. Mobile Chrome/WebView commonly throws a
// bare "TypeError: Failed to fetch" (no HTTP response at all) for the FIRST network request
// made right after the tab regains focus from the system file/app picker (screen recorder,
// CapCut, Files app...) - the OS hasn't fully restored the socket/radio yet. Camera photos
// rarely hit this because that picker is lightweight and near-instant; a heavier app switch
// (CapCut, a screen-recording gallery) leaves more of a gap, and CapCut in particular tends
// to hold onto the radio/camera hardware longer than a plain gallery picker, so the old
// 2-retry / ~2.1s total window wasn't always enough for the connection to come back before
// we gave up. Bumped to 4 retries with growing delays (~7s total) so we wait out that gap
// instead of surfacing "Upload failed (Failed to fetch)" on an otherwise-fine connection.
// Also waits for the document to be visible again first, since a request fired while the
// WebView is still backgrounded (mid app-switch) is essentially guaranteed to fail.
async function fetchWithRetry(input: RequestInfo, init?: RequestInit, retries = 4, delayMs = 700): Promise<Response> {
  if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
    await new Promise<void>((resolve) => {
      const onVisible = () => {
        if (document.visibilityState === 'visible') {
          document.removeEventListener('visibilitychange', onVisible)
          resolve()
        }
      }
      document.addEventListener('visibilitychange', onVisible)
      setTimeout(() => { document.removeEventListener('visibilitychange', onVisible); resolve() }, 3000)
    })
  }
  let lastErr: unknown
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      return await fetch(input, init)
    } catch (err) {
      lastErr = err
      if (attempt < retries) await new Promise((r) => setTimeout(r, delayMs * (attempt + 1)))
    }
  }
  throw lastErr
}

// Upload to Cloudflare R2 (Preserves Video Quality & Sound, No Vercel 4.5MB limits).
// Small files (<=4MB, covers every image) go through our own /api/upload so the
// server can run the SafeSearch check first. Bigger files (video/long audio) use an
// S3 multipart upload where each chunk is PUT directly from the browser to R2 via a
// presigned URL - faster, and it doesn't burn Vercel function time/bandwidth.
const uploadWithProgress = async (
  rawFile: File | Blob,
  kind: string,
  uid: string,
  onProgress?: (loadedMB: string, totalMB: string, percent: number) => void,
  // Optional human-readable name (usually the post's Title) used for the Content-Disposition
  // filename R2 hands back on download - so "Save video" gets a short, meaningful name
  // instead of the gallery/export filename (IMG_1234.mp4, a CapCut export name, etc.) or the
  // random internal storage key. Falls back to the original filename server-side if omitted.
  displayName?: string
): Promise<string> => {
  // Pulls the server's { error } text out of a failed response so the real reason
  // (e.g. "r2-not-configured (missing R2_BUCKET_NAME)") reaches the user instead of a
  // generic "check your connection".
  const serverError = async (res: Response) => {
    try { return String((await res.json())?.error || res.status) } catch { return String(res.status) }
  }
  // /api/upload requires a signed-in user (see requireUid in that route) - without this
  // token every request would be rejected with 401 invalid-auth/missing-auth.
  const idToken = await firebaseAuth.currentUser?.getIdToken()
  if (!idToken) throw new Error('missing-auth')

  const isVideo = rawFile.type.startsWith('video/') || (rawFile instanceof File && /\.(mp4|mov|webm|m4v|mkv|3gp|3g2|avi|mpg|mpeg|wmv|flv|mts|m2ts)$/i.test(rawFile.name))
  const isAudio = rawFile.type.startsWith('audio/') || kind === 'audio' || kind === 'music'

  // Only compress images with WebP. Videos and audios are kept untouched so sound and medium/high quality are preserved!
  let file: File | Blob = (rawFile instanceof File && !isVideo && !isAudio)
    ? await (kind === 'cover' ? compressMediaFile(rawFile, 1500, 0.85) : compressMediaFile(rawFile))
    : rawFile

  // Same content:// 0-byte quirk as compressVideoIfNeeded above - fix it here too, since a
  // video/photo can also arrive at uploadWithProgress directly (chat photos, avatars, group
  // photos, voice notes) without going through that function first.
  if (file.size === 0 && rawFile instanceof File) file = await materializeFileIfEmpty(rawFile)

  const totalSize = file.size
  if (totalSize === 0) throw new Error('empty-file: this video could not be read from your gallery - try picking it again, or re-save/re-export it once and re-select it')
  // R2/S3 multipart upload REQUIRES every part except the very last one to be at least
  // 5MiB (5 * 1024 * 1024 bytes) - a smaller non-final part is rejected by R2 on
  // "complete" with exactly "r2-complete-failed: ... smaller than the minimum allowed
  // object size". The old 3MB chunk size violated this on any file over ~4MB-6MB (every
  // part except the last was under 5MB), which is why uploads kept failing. 8MB keeps a
  // safe margin above the 5MB floor.
  const CHUNK_SIZE = 8 * 1024 * 1024 // 8MB chunks

  // Standard single upload for files <= 4MB
  if (totalSize <= 4 * 1024 * 1024) {
    const attemptUpload = () => new Promise<string>((resolve, reject) => {
      const xhr = new XMLHttpRequest()
      const form = new FormData()
      form.append('file', file)
      form.append('kind', kind)
      form.append('uid', uid)
      if (displayName) form.append('displayName', displayName)

      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable && onProgress) {
          onProgress((e.loaded / 1048576).toFixed(1), (e.total / 1048576).toFixed(1), Math.round((e.loaded / e.total) * 100))
        }
      }
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            const res = JSON.parse(xhr.responseText)
            if (res.url) resolve(res.url)
            else reject(new Error('no-url-returned'))
          } catch { reject(new Error('invalid-json')) }
        } else {
          let detail = ''
          try { detail = JSON.parse(xhr.responseText)?.error || '' } catch { /* not JSON */ }
          const failure = new Error(`upload-failed-${xhr.status}${detail ? ': ' + detail : ''}`)
          // 408 / 425 / 429 / 5xx are the server or network having a moment - worth another try, same as a dropped connection.
          if ([0, 408, 425, 429, 500, 502, 503, 504].includes(xhr.status)) (failure as any).transient = true
          reject(failure)
        }
      }
      xhr.onerror = () => reject(new Error('network-error'))
      xhr.timeout = 45000 // a stalled connection used to hang forever - now it is dropped and retried
      xhr.ontimeout = () => reject(new Error('network-error'))
      xhr.open('POST', '/api/upload')
      xhr.setRequestHeader('Authorization', `Bearer ${idToken}`)
      xhr.send(form)
    })
    // Same "returned from the file picker with the network not quite back yet" hiccup as
    // fetchWithRetry above can hit this XHR path too (small photos/voice notes) - retry a
    // few times, with growing delays, on a pure network error before giving up.
    let lastNetworkErr: unknown
    for (let attempt = 0; attempt <= 3; attempt++) {
      try {
        return await attemptUpload()
      } catch (err) {
        if (err instanceof Error && (err.message === 'network-error' || (err as any).transient)) {
          lastNetworkErr = err
          if (attempt < 3) await new Promise((r) => setTimeout(r, 400 * (attempt + 1)))
          continue
        }
        throw err
      }
    }
    throw lastNetworkErr
  }

  // S3 Multipart Upload to R2 for Files > 4MB (Videos / Long Audio).
  // Each part is PUT straight from this browser to R2 using a presigned URL - our
  // server only hands out the URLs and stitches the parts together at the end, it
  // never sees the file bytes for big uploads.
  const filename = (file as File).name || `${kind}_${Date.now()}.${isVideo ? 'mp4' : isAudio ? 'mp3' : 'webp'}`
  const mimeType = file.type || (isVideo ? 'video/mp4' : isAudio ? 'audio/mpeg' : 'application/octet-stream')

  const initRes = await fetchWithRetry('/api/upload', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
    body: JSON.stringify({ action: 'init', kind, filename, mimeType, totalSize, uid, displayName }),
  })
  if (!initRes.ok) throw new Error(`r2-init-failed: ${await serverError(initRes)}`)
  const { uploadId, key, url: expectedUrl } = await initRes.json()
  if (!uploadId || !key) throw new Error('r2-init-failed')

  const parts: { partNumber: number; etag: string }[] = []
  let start = 0
  let partNumber = 1

  try {
    while (start < totalSize) {
      const end = Math.min(start + CHUNK_SIZE, totalSize)
      const chunkBlob = file.slice(start, end)

      // One bad moment (weak signal, a 5xx from R2, a 429) must not kill a big upload that is
      // already 90% done: retry THIS part a few times with a fresh presigned URL.
      let etag: string | null = null
      let partErr: Error | null = null
      for (let tryNo = 0; tryNo < 4 && !etag; tryNo++) {
        if (tryNo > 0) await new Promise((r) => setTimeout(r, 1200 * tryNo))
        try {
          const partUrlRes = await fetchWithRetry('/api/upload', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
            body: JSON.stringify({ action: 'part-url', uploadId, key, partNumber }),
          })
          if (!partUrlRes.ok) throw new Error(`r2-part-url-failed: ${await serverError(partUrlRes)}`)
          const { url: partUrl } = await partUrlRes.json()

          const putRes = await fetchWithRetry(partUrl, { method: 'PUT', body: chunkBlob })
          if (!putRes.ok) throw new Error(`r2-part-upload-failed (R2 replied ${putRes.status})`)
          etag = putRes.headers.get('ETag') || putRes.headers.get('etag')
          if (!etag) throw new Error('r2-part-missing-etag')
        } catch (e) {
          partErr = e instanceof Error ? e : new Error('r2-part-upload-failed')
          etag = null
        }
      }
      if (!etag) throw partErr || new Error('r2-part-upload-failed')
      parts.push({ partNumber, etag })

      start = end
      partNumber += 1
      if (onProgress) {
        onProgress((start / 1048576).toFixed(1), (totalSize / 1048576).toFixed(1), Math.round((start / totalSize) * 100))
      }
    }

    // Finalize the multipart upload. This is where the "video is actually sitting in R2
    // just fine, but the app still shows Upload failed" bug lives: every PART has already
    // landed in R2 by this point, and if this request's response gets lost on the way back
    // (app backgrounded, screen locked, radio hiccup right as the server finishes) the
    // browser sees a plain network error even though the server-side CompleteMultipartUpload
    // already succeeded. Don't trust that error at face value - check whether the object
    // actually exists at its known (deterministic) URL before deciding this really failed.
    try {
      const completeRes = await fetchWithRetry('/api/upload', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
        body: JSON.stringify({ action: 'complete', uploadId, key, parts }),
      })
      if (!completeRes.ok) throw new Error(`r2-complete-failed: ${await serverError(completeRes)}`)
      const { url } = await completeRes.json()
      return url as string
    } catch (completeErr) {
      if (expectedUrl) {
        for (let i = 0; i < 4; i++) {
          if (i > 0) await new Promise((r) => setTimeout(r, 1000 * i))
          try {
            const headRes = await fetch(expectedUrl, { method: 'HEAD', cache: 'no-store' })
            if (headRes.ok) return expectedUrl as string
          } catch { /* not there yet, or a genuine network drop - keep trying */ }
        }
      }
      throw completeErr
    }
  } catch (err) {
    // Best-effort cleanup so a failed upload doesn't leave an orphaned multipart
    // session sitting in R2 forever. Not awaited-for-UI-purposes, just fired off.
    void fetch('/api/upload', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
      body: JSON.stringify({ action: 'abort', uploadId, key }),
    }).catch(() => {})
    throw err
  }
}

const deleteUploadedFile = async (url?: string) => {
  const key = url ? extractStorageKey(url) : null
  if (!key) return
  try {
    const idToken = await firebaseAuth.currentUser?.getIdToken()
    if (!idToken) return
    await fetch('/api/upload', {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
      body: JSON.stringify({ key }),
    })
  } catch { /* ignore */ }
}

// A post and a story made from it ("Add to story") point at the SAME uploaded file,
// so a file may only be erased when nothing else still uses it. Otherwise deleting
// the story wipes the video of the original reel/post (and the other way round).
// When a check fails we keep the file - an orphan is better than a broken reel.
const storageKeyOf = (u?: string | null) => (u ? extractStorageKey(u) : null)

// Does a post still exist that owns this story's media?
const storyMediaBelongsToPost = async (s: { authorId: string; mediaUrl: string; sourcePostId?: string }): Promise<boolean> => {
  try {
    if (s.sourcePostId) {
      const snap = await getDoc(doc(firestore, 'posts', s.sourcePostId))
      return snap.exists()
    }
    // Stories created before `sourcePostId` existed: match by uploaded file id instead.
    const fid = storageKeyOf(s.mediaUrl)
    if (!fid) return false
    const snap = await getDocs(query(collection(firestore, 'posts'), where('authorId', '==', s.authorId), limit(300)))
    return snap.docs.some((d) => storageKeyOf(d.data().imageUrl) === fid)
  } catch { return true }
}

// Is a still-live (<24h) story showing this post's media?
// A post's video / song file can be the "sound" other posts remixed. Never erase it while they still use it.
const urlStillUsedAsSound = async (url: string | undefined, exceptPostId: string): Promise<boolean> => {
  if (!url) return false
  try {
    const snap = await getDocs(query(collection(firestore, 'posts'), where('audioUrl', '==', url), limit(3)))
    return snap.docs.some((d) => d.id !== exceptPostId)
  } catch { return true } // could not check -> keep the file (an orphan is better than a broken remix)
}

const postMediaUsedByLiveStory = async (p: { id: string; authorId: string; imageUrl: string }): Promise<boolean> => {
  try {
    const fid = storageKeyOf(p.imageUrl)
    const cutoff = Date.now() - 24 * 60 * 60 * 1000
    const snap = await getDocs(query(collection(firestore, 'stories'), where('authorId', '==', p.authorId), limit(100)))
    return snap.docs.some((d) => {
      const x = d.data()
      const t = x.createdAt?.toMillis?.() ?? 0
      return t > cutoff && (x.sourcePostId === p.id || (fid && storageKeyOf(x.mediaUrl) === fid))
    })
  } catch { return true }
}

// =========================================================================
// MAIN COMPONENT
// =========================================================================
// One row of users/{me}/blockedUsers (written only by /api/block): my block / restrict / hide state for another account.
type BlockMode = 'block' | 'restrict' | 'hide'
type BlockEntry = { uid: string; mode: BlockMode; name: string; username: string; avatar: string }
type BlockAction = 'block' | 'unblock' | 'restrict' | 'unrestrict' | 'hide' | 'unhide'

// A ban is "active" while it is permanent, or temporary and not yet expired.
// Used for the banned user's own blocking screen AND to hide banned accounts
// from everybody else (feed, stories, chats, search, profiles...).
const isBanActive = (p?: { banned?: unknown; banType?: unknown; bannedUntil?: unknown } | null, now: number = Date.now()): boolean => {
  if (!p || !p.banned) return false
  if (p.banType === 'permanent') return true
  const raw = p.bannedUntil as any
  const until = typeof raw === 'number' ? raw : raw?.toMillis?.()
  return p.banType === 'temporary' && typeof until === 'number' && now < until
}

// ===== One video at a time (Radar feed) =====
// Every PostVideoPlayer registers here. reconcileVideos() picks exactly ONE
// winner (the clip the user just tapped, else the most-visible clip that is
// >= 60% on screen) and pauses every other clip, so two videos can never play
// together no matter how they scroll or which one was started manually.
type PostVideoEntry = {
  el: HTMLVideoElement
  ratio: number
  suspended: boolean
  userPaused: boolean
  play: () => void
  stop: () => void
}
const postVideoEntries = new Set<PostVideoEntry>()
let manualPostVideo: PostVideoEntry | null = null
// Feed song (the music attached to a photo post). Page registers the real stopper here so that
// components far away from it (a video starting to play) can switch the song off.
let stopFeedPostAudio: () => void = () => {}

const reconcileVideos = () => {
  const entries = Array.from(postVideoEntries)
  let winner: PostVideoEntry | null = null
  if (manualPostVideo && postVideoEntries.has(manualPostVideo) && manualPostVideo.ratio > 0 && !manualPostVideo.suspended) {
    winner = manualPostVideo
  } else {
    manualPostVideo = null
    winner = entries
      .filter((e) => e.ratio >= 0.6 && !e.suspended && !e.userPaused)
      .sort((a, b) => (b.ratio - a.ratio) || (a.el.getBoundingClientRect().top - b.el.getBoundingClientRect().top))[0] || null
  }
  entries.forEach((e) => { if (e === winner) e.play(); else e.stop() })
}

// ---- Sign-in / account-load diagnostics -------------------------------------
// Turns Firebase's error codes into a message that says WHAT is wrong and how to fix it,
// instead of a generic "Google Sign-in failed." that hides the real cause.
const describeAuthError = (err: any, host: string): string => {
  const code: string = err?.code || ''
  switch (code) {
    case 'auth/unauthorized-domain':
      return `This website (${host}) is not allowed for Google sign-in yet. Add "${host}" in Firebase Console → Authentication → Settings → Authorized domains, then try again.`
    case 'auth/operation-not-allowed':
      return 'Google sign-in is switched off for this project. Enable it in Firebase Console → Authentication → Sign-in method → Google.'
    case 'auth/popup-closed-by-user':
    case 'auth/cancelled-popup-request':
      return 'Sign-in was cancelled. Tap "Continue with Google" to try again.'
    case 'auth/popup-blocked':
      return 'Your browser blocked the Google sign-in window. Allow pop-ups for this site and try again.'
    case 'auth/network-request-failed':
      return 'Network problem while signing in. Check your internet and try again.'
    case 'auth/web-storage-unsupported':
    case 'auth/operation-not-supported-in-this-environment':
      return 'This browser is blocking storage/cookies needed for sign-in. Open the site in Chrome or Safari (not inside another app, not private mode) and try again.'
    case 'auth/user-disabled':
      return 'This Google account has been disabled.'
    case 'auth/too-many-requests':
      return 'Too many attempts. Wait a few minutes and try again.'
    case 'auth/internal-error':
      return 'Google sign-in hit an internal error. Try again in a moment.'
    // --- Email/password specific codes ---
    case 'auth/invalid-email':
      return 'That email address looks invalid. Please check and try again.'
    case 'auth/missing-password':
      return 'Please enter a password.'
    case 'auth/user-not-found':
    case 'auth/wrong-password':
    case 'auth/invalid-credential':
      // Deliberately the SAME message for "no such email" and "wrong password" - if these
      // differed, an attacker could feed in emails one at a time and learn which ones have
      // an Animora account just from the error text (user enumeration). Same reasoning as
      // the password-reset flow below, which never reveals whether an email is registered.
      return 'Incorrect email or password. Please try again.'
    case 'auth/email-already-in-use':
      return 'An account with this email already exists. Try logging in instead.'
    case 'auth/weak-password':
      return 'Password is too weak - use at least 6 characters.'
    default:
      return 'Sign-in failed. Please try again.'
  }
}

const describeProfileLoadError = (err: any): string => {
  const code: string = err?.code || ''
  if (code === 'permission-denied') {
    return 'Could not open your account right now. Please try again in a moment.'
  }
  if (code === 'timeout' || code === 'unavailable' || /offline|network/i.test(err?.message || '')) {
    return 'Could not reach the server. Check your internet connection and try again.'
  }
  return 'Could not load your account. Please try again.'
}

// Reads users/{uid} with a timeout + one retry. Before, a slow/blocked connection left the
// app on an endless spinner, and any failure was mistaken for "brand-new account".
// Waits for a promise, but never longer than `ms` - a write that hangs on a bad connection must not keep the spinner up forever.
const settleWithin = (p: Promise<unknown>, ms = 6000) => Promise.race([p.catch(() => {}), new Promise((r) => setTimeout(r, ms))])

const getUserDocWithRetry = async (uid: string) => {
  let lastErr: any
  for (let attempt = 0; attempt < 2; attempt++) {
    let timer: ReturnType<typeof setTimeout> | undefined
    try {
      return await Promise.race([
        getDoc(doc(firestore, 'users', uid)),
        new Promise<never>((_, reject) => { timer = setTimeout(() => reject(Object.assign(new Error('timeout'), { code: 'timeout' })), 10000) }),
      ])
    } catch (err: any) {
      lastErr = err
      if (err?.code === 'permission-denied') break // retrying won't help
      await new Promise((r) => setTimeout(r, 800))
    } finally {
      if (timer) clearTimeout(timer)
    }
  }
  throw lastErr
}

// ---------------------------------------------------------------------------
// Shared post / reel inside a chat: a slim glass card with a tall thumbnail. The post author's profile
// picture + @username sit on the thumbnail. New messages carry the author; older ones look it up once
// (post -> author -> user doc) and remember it for the session.
// ---------------------------------------------------------------------------
type SharedAuthor = { name: string; username: string; avatar: string }
const sharedAuthorCache = new Map<string, SharedAuthor>()
const isSharedPostMsg = (m: { sharedImageUrl?: string; sharedPostId?: string; sticker?: boolean; deleted?: boolean }) =>
  Boolean(m.sharedImageUrl && m.sharedPostId && !m.deleted && !isStickerMsg(m))

// Emoji at the start of a message preview ("🎲 Ludo invite", "📷 Photo" ...) is drawn as a line icon (SVG) instead.
// Display only - the stored text is untouched, so old messages show the same way.
const PREVIEW_ICONS: Record<string, React.ComponentType<{ size?: number }>> = {
  '✨': Sparkles, '👁': Eye, '🎵': Mic, '🎬': Clapperboard, '📷': CameraIcon, '🎲': Dices, '♟': Crown, '📹': VideoIcon, '📞': Phone, '🎯': TargetIcon, '📊': BarChart3, '🔗': LinkIcon,
}
function IconText({ text, size = 14 }: { text: string; size?: number }) {
  const m = /^(✨|👁\uFE0F?|🎵|🎬|📷|🎲|♟\uFE0F?|📹|📞|🎯|📊|🔗)\s*([\s\S]*)$/.exec(text || '')
  if (!m) return <>{text}</>
  const Icon = PREVIEW_ICONS[m[1].replace('\uFE0F', '')]
  if (!Icon) return <>{text}</>
  return <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, minWidth: 0, maxWidth: '100%' }}><span style={{ display: 'inline-flex', flex: 'none' }}><Icon size={size} /></span><span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>{m[2]}</span></span>
}

// What a reply quote shows for ANY kind of message. Stickers / photos / voice notes have no text, so the
// quote used to come out blank (or not appear at all). Stickers and photos also carry a small thumbnail.
const replyPreviewOf = (m: Message): { text: string; image?: string } => {
  if (isStickerMsg(m)) return { text: '✨ Sticker', image: m.sharedImageUrl }
  if (m.viewOnce) return { text: '👁️ View Once Photo' }
  if (m.audioUrl) return { text: '🎵 Voice note' }
  if (isSharedPostMsg(m)) return { text: '🎬 Shared post', image: m.sharedMediaType === 'image' ? m.sharedImageUrl : (m.sharedThumbnailUrl || undefined) }
  if (isPlainPhotoMsg(m)) return { text: m.text ? m.text.slice(0, 90) : '📷 Photo', image: m.sharedImageUrl }
  if (m.ludo) return { text: '🎲 Ludo invite' }
  if (m.chess) return { text: '♟️ Chess invite' }
  if (m.carrom) return { text: '🎯 Carrom invite' }
  if (m.call) return { text: m.call.type === 'video' ? '📹 Video call' : '📞 Voice call' }
  return { text: (m.text || '').slice(0, 90) }
}
// Only a real caption is shown ("Shared reel: <caption>"); the generic "Shared a reel transmission" line is hidden.
const sharedCaptionOf = (text: string) => {
  const m = /^Shared (?:reel|post): ([\s\S]+)$/.exec(text || '')
  return m ? m[1].trim() : ''
}

function SharedPostCard({ item, onOpen }: { item: Message; onOpen: () => void }) {
  const postId = String(item.sharedPostId || '')
  const [author, setAuthor] = useState<SharedAuthor | null>(() => {
    if (item.sharedAuthorName || item.sharedAuthorUsername) {
      return { name: item.sharedAuthorName || '', username: item.sharedAuthorUsername || '', avatar: item.sharedAuthorAvatar || '' }
    }
    return sharedAuthorCache.get(postId) || null
  })
  useEffect(() => {
    let off = false
    const cached = sharedAuthorCache.get(postId)
    if (cached && cached.username) { setAuthor(cached); return }
    void (async () => {
      try {
        let uid = item.sharedAuthorId || ''
        let name = item.sharedAuthorName || ''
        let avatar = item.sharedAuthorAvatar || ''
        if (!uid) {
          const ps = await getDoc(doc(firestore, 'posts', postId))
          if (!ps.exists()) return
          const pd = ps.data()
          uid = String(pd.authorId || ''); name = String(pd.authorName || ''); avatar = String(pd.authorAvatar || '')
        }
        let username = ''
        if (uid) {
          try {
            const us = await getDoc(doc(firestore, 'users', uid))
            if (us.exists()) { const ud = us.data(); username = String(ud.username || ''); avatar = String(ud.avatar || avatar); name = String(ud.name || name) }
          } catch { /* private / unavailable: fall back to the stored name */ }
        }
        const found = { name, username, avatar }
        if (!found.name && !found.username) return
        sharedAuthorCache.set(postId, found)
        if (!off) setAuthor(found)
      } catch { /* post deleted or hidden: card still opens without the author chip */ }
    })()
    return () => { off = true }
  }, [postId, item.sharedAuthorId, item.sharedAuthorName, item.sharedAuthorAvatar])

  const isVideo = item.sharedMediaType === 'video'
  const caption = sharedCaptionOf(item.text)
  const label = author?.username ? `@${author.username}` : (author?.name || '')
  return (
    <div className="shared-card" onClick={onOpen} role="button" aria-label={isVideo ? 'Open shared reel' : 'Open shared post'}>
      <div className={`shared-card-media${isVideo ? ' is-reel' : ''}`}>
        {isVideo && !item.sharedThumbnailUrl ? (
          <video src={item.sharedImageUrl} muted playsInline preload="metadata" poster={item.sharedThumbnailUrl} />
        ) : (
          <img decoding="async" src={isVideo ? item.sharedThumbnailUrl : item.sharedImageUrl} alt="" draggable={false} loading="lazy" />
        )}
        {label && (
          <div className="shared-card-author">
            <Avatar src={author?.avatar || ''} name={author?.name || label} size="xs" showStatus={false} uid={item.sharedAuthorId || undefined} />
            <span>{label}</span>
          </div>
        )}
        {isVideo && (
          <div className="shared-card-play"><Play size={20} color="#fff" fill="#fff" /></div>
        )}
      </div>
      {caption && <p className="shared-card-caption">{caption}</p>}
    </div>
  )
}

// Instant start: the last signed-in profile is kept in localStorage so a returning user sees the
// app immediately (no full-screen loader). The real profile still loads in the background and
// replaces this copy a moment later. Cleared on sign-out.
const PROFILE_CACHE_KEY = 'animora_profile_cache_v1'
const readProfileCache = (): { uid: string; profile: any; isAdmin: boolean } | null => {
  try {
    if (typeof window === 'undefined') return null
    const raw = localStorage.getItem(PROFILE_CACHE_KEY)
    if (!raw) return null
    const c = JSON.parse(raw)
    return c && typeof c.uid === 'string' && c.profile && c.profile.uid === c.uid ? c : null
  } catch { return null }
}
// ---------------------------------------------------------------------------
// HOME SNAPSHOT CACHE (stale-while-revalidate). The last home feed + stories row are kept in localStorage, so the next time the app
// opens the home screen shows INSTANTLY from this snapshot while the live data loads behind it and silently replaces it (the
// listeners below overwrite it within a moment). Chats, the chat list and coins are NOT part of this: they load as before (coins come
// from the saved profile). Per account (uid), size-capped, and wiped on sign-out.
// ---------------------------------------------------------------------------
const HOME_CACHE_KEY = 'animora_home_cache_v1'
const HOME_CACHE_MAX_CHARS = 400_000
const HOME_POSTS_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000
const HOME_STORIES_MAX_AGE_MS = 6 * 60 * 60 * 1000 // stories live 24 h: don't resurrect an old snapshot of them
type HomeCache = { uid: string; at: number; posts: any[]; stories: any[] }
const slimForCache = (list: any[], max: number) => JSON.parse(JSON.stringify(list.slice(0, max), (_k, v) => {
  if (typeof v === 'string' && v.length > 2000 && v.startsWith('data:')) return '' // big inline images would blow the storage quota
  if (Array.isArray(v) && v.length > 300) return v.slice(0, 300)
  return v
}))
// Messages of chats opened during this session (memory only). Reopening a chat shows them at once while the
// live poll catches up, instead of a skeleton. Capped so it can never grow without bound.
const chatMsgCache = new Map<string, Message[]>()
// How many of the newest messages an open chat loads at first (older ones come on demand). Weak phones get fewer.
const MSG_WINDOW_STEP = typeof navigator !== 'undefined' && (((navigator as any).deviceMemory && (navigator as any).deviceMemory <= 3) || (navigator.hardwareConcurrency && navigator.hardwareConcurrency <= 4)) ? 40 : 70
const MSG_WINDOW_DEFAULT = MSG_WINDOW_STEP
const rememberChatMsgs = (chatId: string, list: Message[]) => {
  chatMsgCache.delete(chatId)
  chatMsgCache.set(chatId, list)
  while (chatMsgCache.size > 12) { const oldest = chatMsgCache.keys().next().value; if (oldest === undefined) break; chatMsgCache.delete(oldest) }
}
const readHomeCache = (uid: string): HomeCache | null => {
  try {
    const raw = localStorage.getItem(HOME_CACHE_KEY)
    if (!raw) return null
    const c = JSON.parse(raw)
    return c && c.uid === uid && Array.isArray(c.posts) && Array.isArray(c.stories) ? c : null
  } catch { return null }
}
let homeCacheTimer: ReturnType<typeof setTimeout> | null = null
let homeCachePending: { uid: string; posts?: any[]; stories?: any[] } | null = null
const writeHomeCache = (uid: string, part: { posts?: any[]; stories?: any[] }) => {
  if (typeof window === 'undefined') return
  homeCachePending = { ...(homeCachePending && homeCachePending.uid === uid ? homeCachePending : { uid }), ...part }
  if (homeCacheTimer) clearTimeout(homeCacheTimer)
  homeCacheTimer = setTimeout(() => { // debounced: the listeners fire often, storage writes should not
    const p = homeCachePending; homeCachePending = null; homeCacheTimer = null
    if (!p) return
    try {
      const old = readHomeCache(p.uid)
      let posts = p.posts ? slimForCache(p.posts, 20) : (old?.posts || [])
      let stories = p.stories ? slimForCache(p.stories, 25) : (old?.stories || [])
      let json = JSON.stringify({ uid: p.uid, at: Date.now(), posts, stories })
      while (json.length > HOME_CACHE_MAX_CHARS && (posts.length > 3 || stories.length > 3)) {
        posts = posts.slice(0, Math.max(3, Math.floor(posts.length / 2))); stories = stories.slice(0, Math.max(3, Math.floor(stories.length / 2)))
        json = JSON.stringify({ uid: p.uid, at: Date.now(), posts, stories })
      }
      localStorage.setItem(HOME_CACHE_KEY, json)
    } catch { /* storage full / blocked: the cache is only a speed-up */ }
  }, 1500)
}

const clearProfileCache = () => {
  try { localStorage.removeItem(HOME_CACHE_KEY); homeCachePending = null } catch { /* ignore */ }
  try { localStorage.removeItem(PROFILE_CACHE_KEY); localStorage.removeItem('animora_bg_hint') } catch { /* ignore */ }
  try { document.documentElement.classList.remove('has-session'); document.documentElement.style.backgroundColor = '' } catch { /* ignore */ }
}

// ---------------------------------------------------------------------------
// Confirm / Cancel popup - used everywhere something destructive or hard-to-undo
// happens: logout, account delete, removing a group member, transferring/granting
// a role, deleting a post/comment/story, clearing a chat, unlinking a friend, etc.
// Replaces the old browser-native window.confirm() with a themed in-app dialog.
// ---------------------------------------------------------------------------
// Shown when the rename limit is hit (5 renames -> locked for 15 days).
function usernameLockText(until: number): string {
  const when = until > 0 ? new Date(until).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : ''
  return `You changed your username ${USERNAME_CHANGE_LIMIT.maxChanges} times. Username changes are locked for ${USERNAME_CHANGE_LIMIT.lockDays} days${when ? ` (until ${when})` : ''}.`
}

type ConfirmOptions = {
  title: string
  message?: string
  confirmLabel?: string
  cancelLabel?: string
  danger?: boolean
  action: () => void | Promise<void>
}

function ConfirmDialog({ options, busy, onCancel, onConfirm }: {
  options: ConfirmOptions | null
  busy: boolean
  onCancel: () => void
  onConfirm: () => void
}) {
  if (!options) return null
  const danger = options.danger !== false
  return (
    <div className="modal-backdrop" style={{ zIndex: 600 }} onClick={busy ? undefined : onCancel}>
      <div className="modal-card" style={{ maxWidth: 340, textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
        <div style={{
          width: 46, height: 46, borderRadius: '50%', margin: '0 auto .8rem',
          display: 'grid', placeItems: 'center',
          background: danger ? 'rgba(239,68,68,.12)' : 'var(--accent-tint)',
        }}>
          <AlertTriangle size={20} color={danger ? 'var(--danger)' : 'var(--cyan)'} />
        </div>
        <h2 style={{ fontSize: '1.02rem', fontWeight: 700, color: 'var(--ink)', margin: '0 0 0.5rem' }}>{options.title}</h2>
        {options.message && (
          <p style={{ fontSize: '.82rem', color: 'var(--muted)', margin: '0 0 1rem', lineHeight: 1.45 }}>{options.message}</p>
        )}
        <div style={{ display: 'flex', gap: '0.5rem', marginTop: options.message ? 0 : '.9rem' }}>
          <button type="button" className="text-button" style={{ flex: 1, justifyContent: 'center' }} onClick={onCancel} disabled={busy}>
            {options.cancelLabel || 'Cancel'}
          </button>
          <button
            type="button"
            className={`text-button ${danger ? 'danger' : 'primary'}`}
            style={{ flex: 1, justifyContent: 'center' }}
            onClick={onConfirm}
            disabled={busy}
          >
            {busy ? '…' : (options.confirmLabel || 'Confirm')}
          </button>
        </div>
      </div>
    </div>
  )
}

// PERFORMANCE: heavy, rarely-opened screens are code-split so they are NOT part of the first JS download
// (PageSpeed: "Reduce unused JavaScript" / "Minimise main-thread work"). Each one loads only when opened.
const ContactForm = dynamic(() => import('@/components/legal').then((m) => m.ContactForm), { ssr: false })
const GreenTickSheet = dynamic(() => import('@/components/premium-store').then((m) => m.GreenTickSheet), { ssr: false })
const ThemePurchaseSheet = dynamic(() => import('@/components/premium-store').then((m) => m.ThemePurchaseSheet), { ssr: false })
const CreatorSupportModal = dynamic(() => import('@/components/premium-store').then((m) => m.CreatorSupportModal), { ssr: false })
const SubscriptionsList = dynamic(() => import('@/components/premium-store').then((m) => m.SubscriptionsList), { ssr: false })
const UnsubscribeConfirm = dynamic(() => import('@/components/premium-store').then((m) => m.UnsubscribeConfirm), { ssr: false })
const WalletPage = dynamic(() => import('@/components/wallet').then((m) => m.WalletPage), { ssr: false })
const AvatarPicker = dynamic(() => import('@/components/avatar-picker').then((m) => m.AvatarPicker), { ssr: false })
const LudoHub = dynamic(() => import('@/components/ludo').then((m) => m.LudoHub), { ssr: false })
const ChessHub = dynamic(() => import('@/components/chess').then((m) => m.ChessHub), { ssr: false })
const CarromHub = dynamic(() => import('@/components/carrom').then((m) => m.CarromHub), { ssr: false })
import { GameThemeContext } from '@/components/games'
const GamesPage = dynamic(() => import('@/components/games').then((m) => m.GamesPage), { ssr: false })
// Game-invite bubbles in chat load their code only when such a message is actually on screen
// (they used to drag the whole Ludo + Chess modules into the first download).
const LudoInviteBubble = dynamic(() => import('@/components/ludo').then((m) => m.LudoInviteBubble), { ssr: false })
const ChessInviteBubble = dynamic(() => import('@/components/chess').then((m) => m.ChessInviteBubble), { ssr: false })
const CarromInviteBubble = dynamic(() => import('@/components/carrom').then((m) => m.CarromInviteBubble), { ssr: false })

// Call history line inside a 1:1 chat: who called, answered or not, how long (written by components/call.tsx when a call ends).
function CallLogBubble({ call, currentUserId }: { call: CallLog; currentUserId: string }) {
  const iCalled = call.callerId === currentUserId
  const video = call.type === 'video'
  const word = video ? 'video call' : 'voice call'
  const bad = call.status !== 'completed'
  const title = call.status === 'completed'
    ? (video ? 'Video call' : 'Voice call')
    : call.status === 'declined'
      ? (iCalled ? 'Call declined' : `Declined ${word}`)
      : call.status === 'busy'
        ? (iCalled ? 'User was busy' : `Missed ${word}`)
        : (iCalled ? 'No answer' : `Missed ${word}`)
  const m = Math.floor(call.secs / 60)
  const sec = call.secs % 60
  const len = call.secs >= 3600
    ? `${Math.floor(call.secs / 3600)}h ${Math.floor((call.secs % 3600) / 60)}m`
    : `${m}:${String(sec).padStart(2, '0')}`
  const sub = call.status === 'completed' ? `${iCalled ? 'Outgoing' : 'Incoming'} · ${len}` : (iCalled ? 'Outgoing' : 'Incoming')
  const color = bad && !iCalled ? '#ef4444' : 'currentColor'
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 190, padding: '2px 2px 0' }}>
      <span aria-hidden style={{ width: 40, height: 40, borderRadius: '50%', display: 'grid', placeItems: 'center', flexShrink: 0, background: bad && !iCalled ? 'rgba(239,68,68,.16)' : 'rgba(255,255,255,.14)', color }}>
        {video ? (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="6" width="13" height="12" rx="3" /><path d="M22 8v8l-5-3.5z" /></svg>
        ) : (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.1 4.2 2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1 1 .4 1.9.7 2.8a2 2 0 0 1-.5 2.1L8.1 9.9a16 16 0 0 0 6 6l1.3-1.3a2 2 0 0 1 2.1-.4c.9.3 1.8.6 2.8.7a2 2 0 0 1 1.7 2z" /></svg>
        )}
      </span>
      <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <b style={{ fontSize: '.95rem', fontWeight: 700, color }}>{title}</b>
        <span style={{ fontSize: '.78rem', opacity: .75 }}>{iCalled ? '↗' : '↙'} {sub}</span>
      </span>
    </div>
  )
}

// Builds the Profile shown in ProfileViewModal from a users/{uid} document. Used by the signed-in app AND the guest app,
// so both show exactly the same card (a guest's document simply has no private / wallet fields).
function profileFromUserDoc(uid: string, d: any, isUnavailable: boolean): Profile {
  return {
    uid,
    name: String(d.name || 'Traveler'),
    username: String(d.username || ''),
    usernameLower: String(d.usernameLower || '').toLowerCase(),
    email: '',
    avatar: String(d.avatar || fallbackAvatarSvg(d.name || 'Traveler')),
    bio: String(d.bio || ''),
    coverUrl: typeof d.coverUrl === 'string' && !isBanActive(d) ? d.coverUrl : '',
    equipped: isUnavailable ? {} : cleanEquipped(d.equipped),
    followersCount: Number(Math.max(0, d.followersCount || 0)),
    followingCount: Number(Math.max(0, d.followingCount || 0)),
    gender: d.gender || 'male',
    private: Boolean(d.private),
    country: normalizeCountry(d.country) || undefined,
    isPremium: Boolean(d.isPremium),
    premiumUntil: typeof d.premiumUntil === 'number' ? d.premiumUntil : undefined,
    isAdmin: Boolean(d.isAdmin),
    achievementsUnlocked: (d.achievementsUnlocked || {}) as AchievementUnlockMap,
    activityStreak: Number(d.activityStreak || 0),
    lastActiveDay: typeof d.lastActiveDay === 'string' ? d.lastActiveDay : undefined,
    publicCoins: coinBalanceOf(d),
    deactivated: isUnavailable,
    chatBanUntil: typeof d.chatBanUntil === 'number' ? d.chatBanUntil : 0,
    chatBanReason: String(d.chatBanReason || ''),
    uploadHoldUntil: typeof d.uploadHoldUntil === 'number' ? d.uploadHoldUntil : 0,
    uploadHoldReason: String(d.uploadHoldReason || ''),
    ...(isBanActive(d) ? { name: 'Unavailable', username: 'unavailable', avatar: fallbackAvatarSvg('?'), bio: '', followersCount: 0, followingCount: 0, activityStreak: 0, publicCoins: 0 } : {}),
  } as Profile
}

// =========================================================================
// SHARED LINKS (/reel/<id>, /@name)
// Signed-out visitors no longer get a public preview: every address shows the sign-in screen. The link they opened is saved
// (RETURN_TO_KEY) and, right after signing in, they are taken to that exact reel / profile.
// =========================================================================
const RETURN_TO_KEY = 'animora_return_to'
const decodeSafe = (v: string) => { try { return decodeURIComponent(v) } catch { return v } }
/** /reel/<id> and /@name (or /u/name) are real app URLs: they open that reel / profile inside the app. */
function parseDeepLink(): { reel?: string; user?: string } {
  if (typeof window === 'undefined') return {}
  const path = decodeSafe(window.location.pathname)
  const r = /^\/reel\/([A-Za-z0-9_-]{6,64})\/?$/.exec(path)
  if (r) return { reel: r[1] }
  const u = /^\/(?:@|u\/)([A-Za-z0-9_.]{1,20})\/?$/.exec(path)
  if (u) return { user: u[1].toLowerCase() }
  return {}
}
const safeReturnTo = (v: string | null): string =>
  v && /^\/(?:|reel\/[A-Za-z0-9_-]{6,64}|@[A-Za-z0-9_.]{1,20}|u\/[A-Za-z0-9_.]{1,20})$/.test(v) ? v : '/'
function rememberReturnTo() {
  try {
    const path = decodeSafe(window.location.pathname)
    if (path !== '/login' && path !== '/sign-up') sessionStorage.setItem(RETURN_TO_KEY, path)
  } catch { /* private mode: they simply land on the home page after signing up */ }
}

export default function Page() {
  const pathname = usePathname()
  const router = useRouter()
  const [user, setUser] = useState<FirebaseUser | null>(null)
  const [profile, setProfile] = useState<Profile | null>(null)
  // No full-screen loader for first-time / signed-out visitors: they see the home page at once.
  // Only a remembered signed-in user waits (a blank screen, a few ms) for Firebase to restore the session.
  const [authBusy, setAuthBusy] = useState(false)
  useLayoutEffect(() => {
    const c = readProfileCache()
    if (!c) return
    // Paint the saved theme while the (blank) session-restore screen is up, then the app opens with no flash.
    setProfile(c.profile)
    setIsAdminAccount(Boolean(c.isAdmin))
    setAuthBusy(true)
  }, [])
  const [activeTab, setActiveTab] = useState<Tab>('home')
  // 'home' is Comms (chats + story tray). 'reels' (Shonen) is the one and only feed: reels,
  // post videos and photo posts, full-screen, scroll to move on. Tapping a video inside
  // Discover jumps there with that reel focused.
  const [focusedReelId, setFocusedReelId] = useState<string | null>(null)
  const deepPostRef = useRef<(Post & { _createdAtMs: number }) | null>(null) // a reel opened from a /reel/<id> link that is older than the 50 newest
  const [activeChat, setActiveChat] = useState<Chat | null>(null)
  // Reconciles the optimistic "pending-..." bubble with the real Firestore doc that
  // replaces it, so the chat's pop-in entrance animation plays ONCE per message
  // instead of once for the temp id and again for the real id (see pendingOwnSendsRef
  // / noAnimateMsgIdsRef usage below and in the Active Chat Messages Listener).
  const pendingOwnSendsRef = useRef<{ text: string; at: number; matched: boolean }[]>([])
  const noAnimateMsgIdsRef = useRef<Set<string>>(new Set())
  const [rawChats, setChats] = useState<Chat[]>([])
  const [rawContacts, setContacts] = useState<Profile[]>([])
  const [following, setFollowing] = useState<Set<string>>(new Set())
  const [sentRequests, setSentRequests] = useState<Set<string>>(new Set())
  const [followBusy, setFollowBusy] = useState<string | null>(null)
  const [rawRequests, setRequests] = useState<FriendRequest[]>([])
  // Message requests: dmIn = people who wrote to me first, dmOut = people I wrote to first.
  const [dmIn, setDmIn] = useState<DmRequest[]>([])
  const [dmOut, setDmOut] = useState<DmRequest[]>([])
  // Chats the person removed from their list. Kept on this device too (so it survives an app restart even on a
  // slow network) and on the server (dmRequests.hiddenFor / friends.hidden) so it survives a reinstall.
  const [hiddenDmIds, setHiddenDmIds] = useState<Set<string>>(new Set())
  useEffect(() => {
    if (!user?.uid) { setHiddenDmIds(new Set()); return }
    try {
      const raw = localStorage.getItem(`animora:hiddendm:${user.uid}`)
      const list = raw ? JSON.parse(raw) : []
      setHiddenDmIds(new Set(Array.isArray(list) ? list.map(String) : []))
    } catch { setHiddenDmIds(new Set()) }
  }, [user?.uid])
  const saveHiddenDm = (next: Set<string>) => {
    if (!user?.uid) return
    try { localStorage.setItem(`animora:hiddendm:${user.uid}`, JSON.stringify(Array.from(next))) } catch { /* storage full / blocked */ }
  }
  const unhideDm = (otherId: string) => {
    setHiddenDmIds((prev) => {
      if (!prev.has(otherId)) return prev
      const next = new Set(prev); next.delete(otherId); saveHiddenDm(next); return next
    })
    if (!user?.uid) return
    for (const r of [...dmIn, ...dmOut]) {
      if (r.otherId === otherId && r.hiddenFor?.includes(user.uid)) void updateDoc(doc(firestore, 'dmRequests', r.id), { hiddenFor: arrayRemove(user.uid) }).catch(() => undefined)
    }
  }
  const [showMsgRequests, setShowMsgRequests] = useState(false)
  const [dmBusy, setDmBusy] = useState(false)
  const [rawNotifications, setNotifications] = useState<AppNotification[]>([])
  const [showNotifications, setShowNotifications] = useState(false)
  const [incomingGift, setIncomingGift] = useState<{ fromName: string; label: string } | null>(null)
  useEffect(() => {
    if (!incomingGift) return
    const t = setTimeout(() => setIncomingGift(null), 5000)
    return () => clearTimeout(t)
  }, [incomingGift])
  // Browser notifications (branded alerts with Reply / Accept buttons) + the bell's unread dot.
  const activeChatRef = useRef<Chat | null>(null)
  // True while a direct chat screen is open (texting a friend) - an incoming call auto-declines
  // as "busy" while this is true, instead of interrupting the conversation. See useCalls below.
  // "Busy" only counts while you are really in the conversation: a direct chat is open AND you opened it or typed/sent
  // something in the last 30 seconds. Just leaving a chat open on the screen no longer makes every call miss.
  const openDirectChatRef = useRef(false)
  const lastChatActivityRef = useRef(0)
  const chattingRef = useMemo(() => ({ get current() { return openDirectChatRef.current && Date.now() - lastChatActivityRef.current < 30000 } }), [])
  const chatMsgSeenRef = useRef<Map<string, number>>(new Map()) // "direct:uid" / "group:id" -> newest message time already handled
  const pendingNavRef = useRef<{ k: string; c?: string; ck?: string; d?: string; at: number } | null>(null)
  const pushRef = useRef<{ chatActivity: (kind: 'direct' | 'group', id: string, ts: number, pending: boolean) => void; alert: (n: AppNotification) => void; action: (m: any) => Promise<void> }>({
    chatActivity: () => undefined, alert: () => undefined, action: async () => undefined,
  })
  const [seenRequestIds, setSeenRequestIds] = useState<Set<string>>(new Set())
  const [showWallet, setShowWallet] = useState(false)
  const [showGames, setShowGames] = useState(false)
  const [ludoLaunch, setLudoLaunch] = useState<LudoLaunch | null>(null)
  const [chessLaunch, setChessLaunch] = useState<ChessLaunch | null>(null)
  const [carromLaunch, setCarromLaunch] = useState<CarromLaunch | null>(null)
  // Wallet "Payment History": loaded once per open, from the two places money actually
  // moves - coinTxns (every spend/gift/subscription/boost, see /api/wallet/transfer) and
  // Both collections are written only by the Admin SDK; the client just reads its own rows.
  const [walletTxns, setWalletTxns] = useState<WalletTxn[] | null>(null)
  const [walletTxnsLoading, setWalletTxnsLoading] = useState(false)
  // Personalized reels: the learned taste profile lives in a ref (updated on every like/watch
  // without re-rendering) and is saved to users/{uid}.reelInterests in the background.
  const reelInterestRef = useRef<ReelInterestState>(emptyReelInterestState())
  const reelInterestTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [rawMessages, setMessages] = useState<Message[]>([])
  // Which chat `rawMessages` belongs to. Without this, opening chat B kept showing chat A's messages until B's first load landed.
  const [messagesFor, setMessagesFor] = useState('')
  // Low-end phones: only the newest N messages of the open chat are downloaded + drawn; "Earlier messages" raises N.
  const [msgWindow, setMsgWindow] = useState(MSG_WINDOW_DEFAULT)
  const [hasOlderMsgs, setHasOlderMsgs] = useState(false)
  const [search, setSearch] = useState('')
  const [peopleSearch, setPeopleSearch] = useState('')
  // The typed draft lives OUTSIDE React state: every keystroke used to re-render this whole 5000-line component.
  // Only the chat composer subscribes to it (see ChatView), so typing now re-renders just the chat screen.
  const draftRef = useRef('')
  const draftSubs = useRef<Set<(v: string) => void>>(new Set())
  const setMessage = useCallback((v: string) => { lastChatActivityRef.current = Date.now() /* typing/sending keeps "busy" alive for calls */; draftRef.current = v; draftSubs.current.forEach((f) => f(v)) }, [])
  const draftStore = useMemo(() => ({ ref: draftRef, subs: draftSubs.current }), [])
  const [status, setStatus] = useState('')
  // Cool gold reward pill (snap coins etc.) shown instead of a plain text toast.
  const [coinReward, setCoinReward] = useState<{ amount: number; label: string; key: number } | null>(null)
  const showCoinReward = (amount: number, label: string) => {
    const key = Date.now()
    setCoinReward({ amount, label, key })
    window.setTimeout(() => setCoinReward((c) => (c && c.key === key ? null : c)), 3200)
  }
  const [busy, setBusy] = useState(false)
  // Age gate for accounts created before it existed: 'checking' -> 'needs' (ask DOB) | 'ok' | 'under-age'.
  const [ageStatus, setAgeStatus] = useState<'checking' | 'needs' | 'ok' | 'under-age'>('checking')
  const [loginBusy, setLoginBusy] = useState(false)
  const [loginError, setLoginError] = useState('')
  const [profileLoadError, setProfileLoadError] = useState('')

  // 1. Hall of Fame (Leaderboard) First | 2. Search | 3. Discover 10
  const [discoverSubTab, setDiscoverSubTab] = useState<'coins'>('coins')
  const [rawLeaderboardUsers, setLeaderboardUsers] = useState<Profile[]>([])
  const [rawCoinLeaders, setCoinLeaders] = useState<Profile[]>([])
  const [boardsLoaded, setBoardsLoaded] = useState({ allies: false, coins: false })
  const [rawRandomUsers, setRandomUsers] = useState<Profile[]>([])
  const [randomLoading, setRandomLoading] = useState(false)
  // People-you-may-know shown inside the Reels feed (2x2 grid slide). Fetched once when Reels opens.
  const [rawReelSuggest, setReelSuggest] = useState<Profile[]>([])
  const [reelSuggestLoading, setReelSuggestLoading] = useState(false)

  // Pinned Chats
  const [pinnedChatIds, setPinnedChatIds] = useState<Set<string>>(() => {
    if (typeof window !== 'undefined') {
      try {
        const saved = localStorage.getItem('animechat_pinned_chats')
        return saved ? new Set(JSON.parse(saved)) : new Set()
      } catch { return new Set() }
    }
    return new Set()
  })

  // Muted Chats (notifications off) - keyed "direct:<id>" / "group:<id>" so the two never collide.
  const [mutedChatIds, setMutedChatIds] = useState<Set<string>>(() => {
    if (typeof window !== 'undefined') {
      try {
        const saved = localStorage.getItem('animechat_muted_chats')
        return saved ? new Set(JSON.parse(saved)) : new Set()
      } catch { return new Set() }
    }
    return new Set()
  })

  // Modals & Panels
  const [showGroup, setShowGroup] = useState(false)
  const [showAddFriend, setShowAddFriend] = useState(false)   // full-screen Add friend page (opened from Comms)
  const [showProfileEdit, setShowProfileEdit] = useState(false)
  const [showUsernameSetup, setShowUsernameSetup] = useState(false)
  const [rawAllFriends, setAllFriends] = useState<Chat[]>([])
  const [showFriendsList, setShowFriendsList] = useState(false)
  const [showChats, setShowChats] = useState(false) // full-screen Comms (chat list), opened from the message icon in the header
  const [showGroupInfo, setShowGroupInfo] = useState(false)
  const [showPublicGuilds, setShowPublicGuilds] = useState(false)
  const [showDiscoverPeople, setShowDiscoverPeople] = useState(false)
  const [groupJoinRequests, setGroupJoinRequests] = useState<GroupJoinRequest[]>([])
  const [contactPreset, setContactPreset] = useState<ContactCategory | null>(null)
  const [showDeleteAccount, setShowDeleteAccount] = useState(false)
  const [deleteAccountBusy, setDeleteAccountBusy] = useState(false)
  const [groupMemberProfiles, setGroupMemberProfiles] = useState<Profile[]>([])
  const [groupMemberProfilesLoading, setGroupMemberProfilesLoading] = useState(false)

  // Themed confirm/cancel popup (replaces window.confirm everywhere in the app -
  // logout, account delete, kicking a member, promoting/transferring a role,
  // deleting a post/comment/story, clearing a chat, unlinking a friend, etc.)
  const [confirmOptions, setConfirmOptions] = useState<ConfirmOptions | null>(null)
  const [confirmBusy, setConfirmBusy] = useState(false)
  const askConfirm = (options: ConfirmOptions) => setConfirmOptions(options)
  const closeConfirm = () => { if (!confirmBusy) setConfirmOptions(null) }
  const runConfirm = async () => {
    if (!confirmOptions) return
    setConfirmBusy(true)
    try {
      await confirmOptions.action()
    } catch (err) {
      // An action that throws must never fail silently or leak a raw error: show a plain message instead.
      notify(friendlyError(err, 'Could not complete that. Please try again.'))
    } finally {
      setConfirmBusy(false)
      setConfirmOptions(null)
    }
  }
  const confirmSignOut = () => {
    askConfirm({
      title: 'Log out?',
      message: 'You\'ll need to sign in again to keep using this session.',
      confirmLabel: 'Log out',
      action: async () => { await signOut(firebaseAuth) },
    })
  }
  // Plain, immediate sign-out with no confirmation popup. Used on the screens that
  // render before <ConfirmDialog /> exists in the tree (account-setup, banned,
  // "couldn't load account") - those return early, so calling confirmSignOut there
  // sets the confirm state but the dialog itself never mounts to show it: nothing
  // seems to happen on tap, and then the dialog pops up later once the user reaches
  // a screen that does render it (e.g. right after finishing account setup).
  const directSignOut = () => { signOut(firebaseAuth) }

  // Reports & Admin (only the Google account matching the ADMIN_EMAIL secret sees the admin console)
  // targetUsername starts as whatever name the screen had (display name) and is replaced by the real @username
  // as soon as it is looked up (see lookupUsername) - `resolved` flips true when that has happened.
  const [reportTarget, setReportTarget] = useState<{ targetUid: string; targetUsername: string; targetName?: string; resolved?: boolean; contentType: 'post' | 'message' | 'account'; contentId: string; contentText: string } | null>(null)
  const [reportBusy, setReportBusy] = useState(false)
  const [showAdminPanel, setShowAdminPanel] = useState(false)

  // Chat Actions
  const [replyTarget, setReplyTarget] = useState<Message | null>(null)
  const [forwardTarget, setForwardTarget] = useState<Message | null>(null)
  const [editingMsg, setEditingMsg] = useState<Message | null>(null)
  const [chatLongPressTarget, setChatLongPressTarget] = useState<Chat | null>(null)

  // Posts, Stories, Previews & Sharing Options Sheet
  const [rawPosts, setPosts] = useState<Post[]>([])
  const rawPostsRef = useRef<Post[]>([])
  const rawStoriesRef = useRef<Story[]>([])
  const storyLikeBusyRef = useRef<Set<string>>(new Set())
  rawPostsRef.current = rawPosts
  const [rawStories, setStories] = useState<Story[]>([])
  useEffect(() => { rawStoriesRef.current = rawStories }, [rawStories])
  // `reelDraft` is the video / photo picked with the "+" button in Shonen (opens the composer sheet).
  const [reelDraft, setReelDraft] = useState<File | null>(null)
  const [showNewPost, setShowNewPost] = useState(false) // "+" opens the New post page right away, no gallery first
  const [remixSound, setRemixSound] = useState<SoundInfo | null>(null) // set when the draft was started with "Remix this sound"
  const storyRemixPickRef = useRef<HTMLInputElement | null>(null)
  const storyRemixRef = useRef<SoundInfo | null>(null)
  const handleStoryRemixPick = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0]
    e.target.value = ''
    const remix = storyRemixRef.current
    storyRemixRef.current = null
    if (!f || !remix) return
    const heldMsg = uploadHoldMessage()
    if (heldMsg) { alert(heldMsg); return }
    if (!f.type.startsWith('image/')) {
      if (!(f.type.startsWith('video/') || /\.(mp4|mov|webm|m4v)$/i.test(f.name))) { alert('Please choose a photo or a video.'); return }
      const duration = await readVideoDuration(f)
      if (duration > 65) { alert(`Video length is ${Math.round(duration)}s. Reels support up to 60 seconds. Please select a video up to 60 seconds.`); return }
    }
    setRemixSound(remix)
    setReelDraft(f)
  }
  const [previewPost, setPreviewPost] = useState<Post | null>(null)
  // Tapping a post inside a profile's post grid opens that person's feed (scrolled to the
  // tapped post) instead of the old single-post popup - see UserFeedModal below.
  const [feedTarget, setFeedTarget] = useState<{ authorId: string; postId: string } | null>(null)
  const [commentsFor, setCommentsFor] = useState<Post | null>(null)
  const [rawComments, setComments] = useState<PostComment[]>([])
  const [commentsForStory, setCommentsForStory] = useState<Story | null>(null)
  const [rawStoryComments, setStoryComments] = useState<PostComment[]>([])
  const [viewingStory, setViewingStory] = useState<Story | null>(null)
  const [storyGroups, setStoryGroups] = useState<Story[]>([])
  const [storyIndex, setStoryIndex] = useState(0)
  const storyGroupsRef = useRef<Story[]>([])
  const storyIndexRef = useRef(0)
  storyGroupsRef.current = storyGroups
  storyIndexRef.current = storyIndex
  const [showCreateStory, setShowCreateStory] = useState(false)
  // Story ids this device has already watched (own + other people's). Saved on the phone per account, so a ring
  // that went grey STAYS grey after closing/reopening the app - and it also covers the cases where the server-side
  // "viewers" write was slow or rejected. A newer story has a new id, so its ring lights up again by itself.
  const [viewedStoryIds, setViewedStoryIds] = useState<Set<string>>(new Set())
  const seenStoriesLoadedFor = useRef<string | null>(null)
  useEffect(() => {
    const uid = user?.uid
    if (!uid) { seenStoriesLoadedFor.current = null; return }
    try {
      const raw = localStorage.getItem(`animora_seen_stories_${uid}`)
      const arr = raw ? JSON.parse(raw) : []
      if (Array.isArray(arr)) setViewedStoryIds((prev) => new Set([...arr.filter((x: unknown) => typeof x === 'string'), ...prev]))
    } catch { /* storage unavailable: falls back to the server's viewers list */ }
    seenStoriesLoadedFor.current = uid
  }, [user?.uid])
  useEffect(() => {
    const uid = user?.uid
    if (!uid || seenStoriesLoadedFor.current !== uid) return
    try {
      const arr = [...viewedStoryIds].slice(-400) // oldest entries drop off first (stories only live 24h anyway)
      localStorage.setItem(`animora_seen_stories_${uid}`, JSON.stringify(arr))
    } catch { /* ignore */ }
  }, [viewedStoryIds, user?.uid])
  const [shareActionPost, setShareActionPost] = useState<Post | null>(null)
  const [sharingPost, setSharingPost] = useState<Post | null>(null)
  const [sharingStory, setSharingStory] = useState<Story | null>(null)
  const [boostTarget, setBoostTarget] = useState<{ kind: 'post' | 'story'; item: Post | Story } | null>(null)
  // Boost on/off is controlled live by the owner (Admin Console -> Boost lock).
  const boostEnabled = useBoostEnabled()
  const BOOST_COMING_SOON = !boostEnabled

  // Global Playing Audio for Feed Posts
  const [playingPostAudioId, setPlayingPostAudioId] = useState<string | null>(null)
  const postAudioRef = useRef<HTMLAudioElement | null>(null)
  // While the Post / Reel / Story upload sheet is open, every background reel, feed video
  // and post song is silenced so it can't clash with what the user is doing.
  const uploadOpen = Boolean(reelDraft) || showNewPost || showCreateStory

  // Profile View
  const [viewingProfile, setViewingProfile] = useState<Profile | null>(null)
  const [viewingProfileLoading, setViewingProfileLoading] = useState(false)
  const [viewingProfilePosts, setViewingProfilePosts] = useState<Post[]>([])
  const [followListing, setFollowListing] = useState<{ kind: 'followers' | 'following'; uid: string; name: string; items: Profile[] } | null>(null)
  const [followListingLoading, setFollowListingLoading] = useState(false)

  // Real-Time Presence & Typing Map
  const [presenceMap, setPresenceMap] = useState<Record<string, number>>({})
  const [typingMap, setTypingMap] = useState<Record<string, boolean>>({})
  // Latest message of every direct chat: did I send it, and has the other person seen it yet?
  // Drives the saffron "Seen" / "Sent" tag under each name in the Comms list.
  const [lastMsgMeta, setLastMsgMeta] = useState<Record<string, { own: boolean; read: boolean }>>({})
  // How many messages from each person are still unread by me (WhatsApp-style green count on the Comms list).
  // Keyed by the friend's uid. Capped at 99 so a huge backlog never costs a huge read.
  const [unreadMap, setUnreadMap] = useState<Record<string, number>>({})
  // Muted chats stay out of the total shown on the Comms tab, same as WhatsApp.
  const totalUnread = Object.entries(unreadMap).reduce((sum, [fid, n]) => sum + (mutedChatIds.has(`direct:${fid}`) ? 0 : n), 0)
  const [nowTick, setNowTick] = useState(() => Date.now())

  // ===== Ban enforcement (everyone ELSE's view of a banned account) =====
  // Live list of every account whose `banned` flag is set. Active bans (permanent,
  // or temporary and not yet expired) are collapsed into a stable string key so the
  // filtered lists below only change identity when the banned SET really changes.
  const [bannedRecords, setBannedRecords] = useState<Record<string, Profile>>({})
  const bannedKey = useMemo(
    () => Object.values(bannedRecords).filter((p) => isBanActive(p, nowTick)).map((p) => p.uid).sort().join(','),
    [bannedRecords, nowTick]
  )
  // ===== Block / restrict / hide (MY view of accounts I acted on; see app/api/block/route.ts) =====
  // `bannedUids` below now means "accounts I should not see at all": real bans PLUS accounts I
  // fully blocked, so every list that already runs through dropBanned() hides blocked people
  // (chats, friends, requests, notifications, messages, posts, stories, comments...) for free.
  const [blockRecords, setBlockRecords] = useState<Record<string, BlockEntry>>({})
  const keyOfMode = (modes: BlockMode[]) => Object.values(blockRecords).filter((e) => modes.includes(e.mode)).map((e) => e.uid).sort().join(',')
  const blockedKey = keyOfMode(['block'])
  const hiddenKey = keyOfMode(['hide'])
  const restrictedKey = keyOfMode(['restrict'])
  const bannedUids = useMemo(() => new Set([...(bannedKey ? bannedKey.split(',') : []), ...(blockedKey ? blockedKey.split(',') : [])]), [bannedKey, blockedKey])
  const hiddenFeedUids = useMemo(() => new Set(hiddenKey ? hiddenKey.split(',') : []), [hiddenKey])
  const restrictedUids = useMemo(() => new Set(restrictedKey ? restrictedKey.split(',') : []), [restrictedKey])
  const dropBanned = <T,>(list: T[], uidOf: (item: T) => string | undefined): T[] =>
    bannedUids.size ? list.filter((item) => !bannedUids.has(uidOf(item) || '')) : list
  const dropUids = <T,>(list: T[], uidOf: (item: T) => string | undefined, uids: Set<string>): T[] =>
    uids.size ? list.filter((item) => !uids.has(uidOf(item) || '')) : list

  const chats = useMemo(() => dropBanned(rawChats, (c) => (c.kind === 'group' ? undefined : c.id)), [rawChats, bannedUids])
  const allFriends = useMemo(() => dropBanned(rawAllFriends, (c) => c.id), [rawAllFriends, bannedUids])
  const contacts = useMemo(() => dropBanned(rawContacts, (c) => c.uid), [rawContacts, bannedUids])
  // Restricted accounts: their alerts / requests never reach my bell (they aren't told).
  const requests = useMemo(() => dropUids(dropBanned(rawRequests, (r) => r.from), (r) => r.from, restrictedUids), [rawRequests, bannedUids, restrictedUids])
  const notifications = useMemo(() => dropUids(dropBanned(rawNotifications, (n) => n.fromId), (n) => n.fromId, restrictedUids), [rawNotifications, bannedUids, restrictedUids])
  // Red dot on the bell: only for alerts / friend requests the person hasn't looked at yet.
  const hasUnreadAlerts = notifications.some((n) => !n.read) || requests.some((r) => !seenRequestIds.has(r.id))
  const activeChatKey = user && activeChat ? (activeChat.kind === 'group' ? activeChat.id : [user.uid, activeChat.id].sort().join('_')) : ''
  const messagesLoading = Boolean(activeChatKey) && messagesFor !== activeChatKey
  // Never hand ChatView messages that belong to another conversation.
  const messages = useMemo(() => (messagesFor === activeChatKey ? dropBanned(rawMessages, (m) => m.senderId) : []), [rawMessages, bannedUids, messagesFor, activeChatKey])
  const leaderboardUsers = useMemo(() => dropBanned(rawLeaderboardUsers, (u) => u.uid), [rawLeaderboardUsers, bannedUids])
  // My own row always uses the SAME live number as the wallet (coinBalanceOf(profile)); the fetched list can be up to a
  // poll old and is the only reason the board and the wallet used to disagree. Re-sorted so my rank is right too.
  const myLiveCoins = profile ? coinBalanceOf(profile) : 0
  const coinLeaders = useMemo(() => {
    const base = dropBanned(rawCoinLeaders, (u) => u.uid)
    if (!user || !profile) return base
    const mine = base.find((u) => u.uid === user.uid)
    const merged = mine
      ? base.map((u) => (u.uid === user.uid ? { ...u, publicCoins: myLiveCoins } : u))
      : myLiveCoins > 0 && (base.length < 25 || myLiveCoins > (base[base.length - 1]?.publicCoins || 0))
        ? [...base, { ...(profile as any), uid: user.uid, publicCoins: myLiveCoins } as Profile]
        : base
    return merged.filter((u) => (u.publicCoins || 0) > 0).sort((x, y) => (y.publicCoins || 0) - (x.publicCoins || 0)).slice(0, 25)
  }, [rawCoinLeaders, bannedUids, myLiveCoins, user?.uid])
  const randomUsers = useMemo(() => dropBanned(rawRandomUsers, (u) => u.uid), [rawRandomUsers, bannedUids])
  // Hidden accounts: posts / reels / stories vanish from MY feeds only (their profile is still reachable).
  const posts = useMemo(() => dropUids(dropBanned(rawPosts, (x) => x.authorId), (x) => x.authorId, hiddenFeedUids), [rawPosts, bannedUids, hiddenFeedUids])
  // The signed-in user's OWN posts - deliberately a separate live query, not derived
  // from `posts` above. That feed is capped at the 50 most recent posts APP-WIDE (see
  // the onSnapshot a few lines up), so filtering it for authorId === user.uid only
  // catches whichever of the user's posts happen to still be in that recent-50 window -
  // this was quietly wrong for anyone who posts a lot, or once the app has >50 posts
  // total: their own profile grid, post count, and view/like stats would all
  // undercount, and so would the wallet's view-based monetization gate. This is used
  // for both.
  const [myPosts, setMyPosts] = useState<Post[]>([])
  useEffect(() => {
    if (!user) { setMyPosts([]); return }
    return onSnapshot(query(collection(firestore, 'posts'), where('authorId', '==', user.uid)), (snap) => {
      // Sorted client-side on purpose: adding orderBy('createdAt') to this query would
      // need a new Firestore composite index (authorId + createdAt) that doesn't exist
      // yet, so this matches how the "view someone else's profile" query a bit further
      // down already avoids that same requirement.
      const list = snap.docs.map((d) => mapPostDoc(d.id, d.data()))
      list.sort((a, b) => b._createdAtMs - a._createdAtMs)
      setMyPosts(list)
    })
  }, [user])
  const stories = useMemo(() => dropUids(dropBanned(rawStories, (x) => x.authorId), (x) => x.authorId, hiddenFeedUids), [rawStories, bannedUids, hiddenFeedUids])
  const comments = useMemo(() => dropBanned(rawComments, (x) => x.authorId), [rawComments, bannedUids])
  const storyComments = useMemo(() => dropBanned(rawStoryComments, (x) => x.authorId), [rawStoryComments, bannedUids])

  // author uid -> { username, premium fields } for every author whose post is on screen.
  const [authorInfo, setAuthorInfo] = useState<Record<string, AuthorInfo>>({})
  const authorRequestedRef = useRef<Set<string>>(new Set())
  useEffect(() => {
    const ids = new Set<string>(posts.map((x) => x.authorId))
    stories.forEach((x) => ids.add(x.authorId))
    if (previewPost?.authorId) ids.add(previewPost.authorId)
    const todo = [...ids].filter((id) => id && id !== user?.uid && !authorRequestedRef.current.has(id))
    if (!todo.length) return
    todo.forEach((id) => authorRequestedRef.current.add(id))
    void Promise.all(todo.map(async (id) => {
      try {
        const snap = await getDoc(doc(firestore, 'users', id))
        const d = snap.exists() ? snap.data() : null
        if (!d || typeof d.username !== 'string') return null
        rememberLiveAvatar(id, d.avatar, false, d.equipped ?? null)
        return [id, {
          username: d.username,
          usernameLower: String(d.usernameLower || d.username).toLowerCase(),
          isPremium: Boolean(d.isPremium),
          premiumUntil: typeof d.premiumUntil === 'number' ? d.premiumUntil : undefined,
          isAdmin: Boolean(d.isAdmin),
          greenTickMilestone: d.greenTickMilestone === true,
        } as AuthorInfo] as const
      } catch {
        authorRequestedRef.current.delete(id) // try again next time
        return null
      }
    })).then((entries) => {
      const found = entries.filter(Boolean) as (readonly [string, AuthorInfo])[]
      if (found.length) setAuthorInfo((prev) => ({ ...prev, ...Object.fromEntries(found) }))
    })
  }, [posts, stories, previewPost?.authorId, user?.uid])
  // Your own posts use your live profile so a fresh purchase / rename shows straight away.
  const authorInfoAll = useMemo(() => (
    user && profile
      ? { ...authorInfo, [user.uid]: { username: profile.username, usernameLower: profile.usernameLower, isPremium: profile.isPremium, premiumUntil: profile.premiumUntil, isAdmin: profile.isAdmin, greenTickMilestone: profile.greenTickMilestone } }
      : authorInfo
  ), [authorInfo, user?.uid, profile?.username, profile?.usernameLower, profile?.isPremium, profile?.premiumUntil, profile?.isAdmin, profile?.greenTickMilestone])

  // Your own avatar is the source of truth for every <Avatar uid={you}> on screen: the moment a profile save (or a change made on
  // another device) updates `profile.avatar`, all your old posts, comments, chat bubbles, notifications and leaderboard rows follow.
  useEffect(() => {
    const uid = user?.uid
    const avatar = profile?.avatar
    if (!uid || !avatar) return
    rememberLiveAvatar(uid, avatar, true, profile?.equipped ?? {})
    return () => {
      const cur = liveAvatars.get(uid)
      if (cur) liveAvatars.set(uid, { avatar: cur.avatar, at: 0 }) // signed out / switched account: other people's view of this uid can refresh again
    }
  }, [user?.uid, profile?.avatar, JSON.stringify(profile?.equipped ?? {})]) // eslint-disable-line react-hooks/exhaustive-deps

  // Prizes: keep the shared prize store (owned items + equipped set, read by the Prize store and the game boards) in sync with the
  // live profile, and collect any drops earned by winning online games a moment after sign-in.
  const prizeKey = JSON.stringify([profile?.prizesOwned ?? {}, profile?.equipped ?? {}])
  useEffect(() => { setPrizeState(profile?.prizesOwned, profile?.equipped) }, [prizeKey]) // eslint-disable-line react-hooks/exhaustive-deps

  // Story ring state per person. Someone's ring is 'seen' (dimmed) only once you've watched every
  // one of their live stories; your own ring dims the same way, tracked locally in viewedStoryIds
  // since you can't be recorded as a "viewer" of your own story server-side.
  const storyRingApi = useMemo<StoryRingApi>(() => {
    const me = user?.uid || ''
    const isSeen = (st: Story) => viewedStoryIds.has(st.id) || (st.authorId !== me && (st.viewers || []).includes(me))
    const status: Record<string, 'unseen' | 'seen'> = {}
    for (const st of stories) {
      const seen = isSeen(st)
      if (!status[st.authorId]) status[st.authorId] = seen ? 'seen' : 'unseen'
      else if (!seen) status[st.authorId] = 'unseen'
    }
    return {
      statusOf: (uid: string) => status[uid] || null,
      open: (uid: string) => {
        // Oldest -> newest (the list itself is newest-first). Opens at the first story you have NOT seen yet,
        // so a person who posted a new story is never replayed from their old one; all seen = start from the first.
        const group = stories.filter((st) => st.authorId === uid).reverse()
        if (!group.length) return
        const first = Math.max(0, group.findIndex((st) => !isSeen(st)))
        setPreviewPost(null) // the post preview sits above the story viewer, so close it first
        setStoryGroups(group)
        setStoryIndex(first)
        setViewingStory(group[first])
      },
    }
  }, [stories, user?.uid, viewedStoryIds])

  // Tapping a post / reel on a profile opens the Reels feed at that exact item (not a separate page).
  // Only if the item is not part of the loaded feed does it fall back to that person's own feed page.
  // A reel-type post ALWAYS opens in the Reels feed, at that exact reel (even when it is not in the loaded list: it is added
  // to the feed for this visit). Photo / text posts open that person's feed, scrolled to the tapped post.
  const [extraReel, setExtraReel] = useState<Post | null>(null)
  const openPostInReelFeed = (p: Post) => {
    if (isReelPost(p) && !p.previewOnly) {
      if (!posts.some((x) => x.id === p.id)) setExtraReel(p)
      setFocusedReelId(p.id)
      setActiveTab('reels')
    } else setFeedTarget({ authorId: p.authorId, postId: p.id })
  }

  const notify = (text: string) => { setStatus(cleanToast(text)); window.setTimeout(() => setStatus(''), 3000) }
  // Voice / video calls (WebRTC, signalled through Firestore) - see components/call.tsx
  const {
    startCall, callUI, inCall: dmCallActive,
    answerIncoming, declineIncoming, incomingCallId,
  } = useCalls({
    uid: user?.uid,
    me: { name: profile?.name || 'Friend', avatar: profile?.avatar || '' },
    notify,
    callsEnabled: profile?.callsEnabled !== false,
    chattingRef,
    dnd: profile?.callDnd,
  })
  // Group calls (guild chats): same WebRTC idea but mesh-connected to every member in the call - see useGroupCalls in components/call.tsx
  const {
    startGroupCall, groupCallUI,
    answerIncomingGroup, declineIncomingGroup, incomingGroupCallId,
  } = useGroupCalls({
    uid: user?.uid,
    me: { name: profile?.name || 'Friend', avatar: profile?.avatar || '' },
    notify,
    callsEnabled: profile?.callsEnabled !== false,
    chattingRef,
    dnd: profile?.callDnd,
  })
  // Shared by the feed post menu AND the post-preview modal (opened from the profile
  // grid) so deleting your own post works from either place - no need to scroll the
  // feed to find it.
  const deletePost = (p: Post) => {
    // Admin removing SOMEONE ELSE'S post: ask for a reason, tell the author, write the audit log.
    if (isAdminAccount && p.authorId !== user?.uid) { setAdminRemoveTarget(p); return }
    askConfirm({
      title: 'Delete this post?',
      message: 'This will permanently remove it from your profile and everyone\'s feed. This can\'t be undone.',
      confirmLabel: 'Delete',
      danger: true,
      action: async () => {
        if (postAudioRef.current && playingPostAudioId === p.id) stopPostAudio() // its song must not play on after the delete
        await deleteDoc(doc(firestore, 'posts', p.id))
        // Keep the uploaded file if a live story was made from this post - the story
        // still needs it (it is erased later, when that story is deleted).
        if (!(await postMediaUsedByLiveStory(p))) {
          if (!(await urlStillUsedAsSound(p.imageUrl, p.id))) void deleteUploadedFile(p.imageUrl)
          if (p.audioUrl && !p.audioShared && !(await urlStillUsedAsSound(p.audioUrl, p.id))) void deleteUploadedFile(p.audioUrl)
        }
        // Extra carousel photos and the video cover are never used by a story, so always clean them up.
        ;(p.imageUrls || []).slice(1).forEach((u) => void deleteUploadedFile(u))
        ;(p.extraImageUrls || []).forEach((u) => void deleteUploadedFile(u))
        if (p.thumbnailUrl) void deleteUploadedFile(p.thumbnailUrl)
        notify('Transmission deleted.')
      },
    })
  }

  // Lets a post owner rename/re-tag a post/reel after it's already live, and for
  // videos also swap the cover (thumbnail). Photo posts only get title + tags per the
  // Edit sheet's own fields - there's no separate thumbnail for a photo, it IS the photo.
  const [editTarget, setEditTarget] = useState<Post | null>(null)
  const saveEditedPost = async (p: Post, updates: { title: string; caption?: string; tags: string[]; thumbnailFile?: File | null }) => {
    const patch: Record<string, unknown> = {
      title: updates.title.trim() || null,
      tags: updates.tags,
    }
    if (updates.caption !== undefined) patch.caption = updates.caption.trim()
    if (updates.thumbnailFile) {
      const newThumb = await uploadWithProgress(updates.thumbnailFile, 'post', p.authorId)
      if (p.thumbnailUrl) void deleteUploadedFile(p.thumbnailUrl)
      patch.thumbnailUrl = newThumb
    }
    await updateDoc(doc(firestore, 'posts', p.id), patch)
    setEditTarget(null)
    notify('Changes saved.')
  }

  // Submit a report on any post or message. Writes the report to Firestore
  // (admin-only read, per firestore.rules) AND sends the complete report to the
  // admin inbox through the Formspree form (see lib/formspree.ts).
  // Reports must name the @username (that is what the admin searches/bans by), never the
  // display name - posts and chat messages only carry the display name, so look up the real
  // username from the user's own document.
  const lookupUsername = async (uid: string): Promise<string | null> => {
    try {
      const snap = await getDoc(doc(firestore, 'users', uid))
      const uname = snap.exists() ? snap.data()?.username : null
      return typeof uname === 'string' && uname ? uname : null
    } catch { return null }
  }

  const submitReport = async (rawPayload: { targetUid: string; targetUsername: string; targetName?: string; resolved?: boolean; contentType: 'post' | 'message' | 'account'; contentId: string; contentText: string; reason: string }) => {
    if (!user || !profile) return
    setReportBusy(true)
    try {
      // Safety net: if the submit happens before the background lookup finished, resolve it now.
      const realUsername = rawPayload.resolved ? rawPayload.targetUsername : ((await lookupUsername(rawPayload.targetUid)) || rawPayload.targetUsername)
      const payload = { ...rawPayload, targetUsername: realUsername }
      // Deterministic id = one report per person per item. A repeat is an `update` on an existing
      // doc, which the rules deny for non-admins (see the `reports` case in lib/d1-rules.ts).
      const reportId = `${user.uid}_${payload.contentType}_${payload.contentId}`.replace(/\//g, '_').slice(0, 300)
      await setDoc(doc(firestore, 'reports', reportId), {
        reportedByUid: user.uid,
        reportedByUsername: profile.username,
        targetUid: payload.targetUid,
        targetUsername: payload.targetUsername,
        targetName: payload.targetName || '',
        contentType: payload.contentType,
        contentId: payload.contentId,
        contentText: (payload.contentText || '').slice(0, 500),
        reason: payload.reason,
        status: 'pending',
        createdAt: serverTimestamp(),
      })

      // Full report -> Formspree -> the admin inbox. The report is already saved
      // to Firestore above, so a Formspree hiccup never loses it.
      const reportedPost = payload.contentType === 'post' ? rawPosts.find((x) => x.id === payload.contentId) : undefined
      const emailResult = await sendToFormspree({
        _subject: `[Animora Report] @${payload.targetUsername} - ${payload.contentType} - ${payload.reason.slice(0, 60)}`,
        form_type: 'report',
        report_status: 'pending',
        reason: payload.reason,
        content_type: payload.contentType,
        content_id: payload.contentId,
        content_text: payload.contentText || '(no text content)',
        post_title: reportedPost?.title,
        post_caption: reportedPost?.caption,
        post_media_type: reportedPost?.mediaType,
        post_media_url: reportedPost?.imageUrl,
        post_audio_url: reportedPost?.audioUrl,
        reported_username: payload.targetUsername,
        reported_display_name: payload.targetName || '',
        reported_user_id: payload.targetUid,
        reported_by_username: profile.username,
        reported_by_name: profile.name,
        reported_by_user_id: user.uid,
        reported_by_email: profile.email || user.email || '',
        submitted_at: new Date().toISOString(),
        admin_note: 'Open the Admin Console in Animora to review this account or take action.',
      })
      if (!emailResult.ok) console.warn('[report] email delivery failed:', emailResult.error)

      notify('Report submitted. Admins will review it.')
      setReportTarget(null)
    } catch (err) {
      if ((err as { code?: string })?.code === 'permission-denied') {
        notify("You've already reported this. Our admins will review it.")
        setReportTarget(null)
      } else notify('Could not submit report. Please try again.')
    } finally {
      setReportBusy(false)
    }
  }

  // As soon as a report sheet opens, swap the display name for the reported user's real @username.
  useEffect(() => {
    if (!reportTarget || reportTarget.resolved) return
    let cancelled = false
    const { targetUid, contentId } = reportTarget
    void lookupUsername(targetUid).then((uname) => {
      if (cancelled) return
      setReportTarget((t) => t && t.targetUid === targetUid && t.contentId === contentId ? { ...t, targetUsername: uname || t.targetUsername, resolved: true } : t)
    })
    return () => { cancelled = true }
  }, [reportTarget?.targetUid, reportTarget?.contentId])

  // ===== Admin-only: user search + temporary/permanent bans =====
  // Gated purely by lib/d1-rules.ts server-side (isAdmin() == the signed-in
  // Google account's email == the ADMIN_EMAIL secret); this client-side flag only
  // controls whether the button and panel are shown - the real enforcement lives
  // in the rules, keyed off request.auth.token.email, not a username. Filled in by
  // checkIsAdmin() in the auth listener below - the browser never holds the admin
  // email itself, only this true/false answer from the server.
  const [isAdminAccount, setIsAdminAccount] = useState(false)
  // Premium is time-limited (premiumUntil), so re-check the expiry on every
  // render instead of trusting the isPremium flag alone. The admin account
  // always gets every paid feature for free, no purchase required.
  const isPremiumActive = isAdminAccount || Boolean(profile?.isPremium && profile?.premiumUntil && profile.premiumUntil > Date.now())
  adminModeFlag = isAdminAccount
  selfRestrict = {
    chatBanUntil: profile?.chatBanUntil || 0, chatBanReason: profile?.chatBanReason || '',
    uploadHoldUntil: profile?.uploadHoldUntil || 0, uploadHoldReason: profile?.uploadHoldReason || '',
  }
  // Admin tools opened from a profile / the Admin Console
  const [restrictSheet, setRestrictSheet] = useState<{ uid: string; username: string; kind: 'chat' | 'upload'; until: number; reason: string } | null>(null)
  const [adminRemoveTarget, setAdminRemoveTarget] = useState<Post | null>(null)
  const [profileBanTarget, setProfileBanTarget] = useState<Profile | null>(null)
  // Premium themes unlock through the Green Tick (coins) or by spending coins / referrals on a theme.
  // There is no free creator milestone any more. Green Tick no longer removes ads.
  const themesUnlocked = isPremiumActive

  const [showBlockedList, setShowBlockedList] = useState(false)
  const [showSaved, setShowSaved] = useState(false)
  const [showNotifSettings, setShowNotifSettings] = useState(false)
  const [hashtagView, setHashtagView] = useState<string | null>(null)
  useEffect(() => {
    const onTag = (e: Event) => setHashtagView(String((e as CustomEvent).detail || '') || null)
    window.addEventListener('animora:hashtag', onTag)
    return () => window.removeEventListener('animora:hashtag', onTag)
  }, [])
  const [showCommentFilter, setShowCommentFilter] = useState(false)
  const [showReferralCode, setShowReferralCode] = useState(false)
  const [refCongrats, setRefCongrats] = useState<number | null>(null) // coins just earned through referral -> "Congratulations" popup
  const prevRefCoins = useRef<{ uid: string; coins: number } | null>(null)
  const [showAppealPanel, setShowAppealPanel] = useState(false)
  const [achServerStats, setAchServerStats] = useState<Pick<AchievementStats, 'posts' | 'reels' | 'stories' | 'reelViews' | 'likes' | 'giftsSent' | 'giftsReceived'> | null>(null)

  // Numbers that live in Firestore collections (own posts/reels, stories, gifts).
  // Fetched on login, when the page opens, and after publishing/gifting - not on a
  // timer, so it stays cheap. Each piece is fetched independently: if one fails,
  // the others still update and the failed one keeps its previous value.
  const refreshAchievementStats = async () => {
    if (!user) return
    const uid = user.uid
    const [postsRes, storiesRes, sentRes, recvRes, recvDocsRes] = await Promise.allSettled([
      getDocs(query(collection(firestore, 'posts'), where('authorId', '==', uid), limit(1000))),
      getCountFromServer(query(collection(firestore, 'stories'), where('authorId', '==', uid))),
      getCountFromServer(query(collection(firestore, 'gifts'), where('fromId', '==', uid))),
      getCountFromServer(query(collection(firestore, 'gifts'), where('toId', '==', uid))),
      // The gifts log is the source of truth for "Gifts Received": summed here so the
      // total is right even if the sender's running-total update on our profile failed.
      getDocs(query(collection(firestore, 'gifts'), where('toId', '==', uid), limit(500))),
    ])
    if (recvDocsRes.status === 'fulfilled') {
      const total = recvDocsRes.value.docs.reduce((sum, g) => sum + Number(g.data().amountRupees || 0), 0)
      setProfile((c) => c && total > (c.giftsReceivedRupees || 0) ? { ...c, giftsReceivedRupees: total } : c)
      // NOTE: this used to also push `total` back into the users/{uid} doc directly if it was
      // ahead of the stored giftsReceivedRupees. That write is now denied by firestore.rules -
      // giftsReceivedRupees only ever changes via /api/wallet/transfer (Admin SDK), which credits
      // it atomically at gift-send time, so it should never actually fall behind the gifts log.
      // The local-only setProfile above just keeps the UI showing the higher of the two numbers.
    }
    setAchServerStats((prev) => {
      const next = { posts: 0, reels: 0, stories: 0, reelViews: 0, likes: 0, giftsSent: 0, giftsReceived: 0, ...(prev || {}) }
      if (postsRes.status === 'fulfilled') {
        let reels = 0, reelViews = 0, likes = 0
        postsRes.value.docs.forEach((d) => {
          const data = d.data()
          likes += Array.isArray(data.likes) ? data.likes.length : 0
          if (data.mediaType === 'video' && data.postKind !== 'post') { reels += 1; reelViews += Number(data.views || 0) }
        })
        next.posts = postsRes.value.size
        next.reels = reels
        next.reelViews = reelViews
        next.likes = likes
      }
      if (storiesRes.status === 'fulfilled') next.stories = storiesRes.value.data().count
      if (sentRes.status === 'fulfilled') next.giftsSent = sentRes.value.data().count
      if (recvRes.status === 'fulfilled') next.giftsReceived = recvRes.value.data().count
      return next
    })
  }


  // Mirror the app theme onto <body> so portal modals (purchase sheets, Wallet, Achievements...) get the dark colours too.
  // Home themes (10 free + 10 premium): premium ones fall back to plain light/dark if Premium is not active.
  // Home themes: 6 free + 6 premium dark themes (lib/home-themes.ts). A premium theme falls back to the default while Green Tick is not active.
  // (Chat wallpapers/themes are separate and untouched.)
  const rawHomeTheme = getHomeTheme(profile?.appBackground)
  const activeHomeTheme: HomeTheme | undefined = rawHomeTheme && (!rawHomeTheme.premium || themesUnlocked) ? rawHomeTheme : getHomeTheme(DEFAULT_HOME_THEME_ID)
  const [showHomeThemes, setShowHomeThemes] = useState(false)
  const darkTheme = activeHomeTheme ? activeHomeTheme.dark : true
  useEffect(() => {
    document.body.classList.toggle('theme-dark', darkTheme)
    const vars = activeHomeTheme ? homeThemeVars(activeHomeTheme) : null
    const applied: string[] = []
    if (vars) {
      Object.entries(vars).forEach(([k, v]) => { if (k !== '--home-pattern') { document.body.style.setProperty(k, v); applied.push(k) } })
    }
    return () => {
      document.body.classList.remove('theme-dark')
      applied.forEach((k) => document.body.style.removeProperty(k))
    }
  }, [darkTheme, activeHomeTheme?.id])

  // Phone status bar (clock / battery / signal) follows the app theme: dark icons on light themes, light icons on
  // dark themes and on the always-black Shonen (reels) screen. Also keeps the browser theme-color in sync.
  useEffect(() => {
    if (typeof document === 'undefined') return
    const reelsOpen = activeTab === 'reels'
    const lightIcons = reelsOpen || darkTheme   // light icons = for dark backgrounds
    const barColor = reelsOpen ? '#000000' : (activeHomeTheme?.bg || '#08080b')
    try { (window as any).Capacitor?.Plugins?.StatusBar?.setStyle?.({ style: lightIcons ? 'DARK' : 'LIGHT' }) } catch { /* plain browser */ }
    document.querySelector('meta[name="theme-color"]')?.setAttribute('content', barColor)
  }, [activeTab, darkTheme, activeHomeTheme?.id])

  useEffect(() => {
    if (user?.uid && profile?.uid) void refreshAchievementStats()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.uid, profile?.uid])



  // ---- FREE coins: achievements (streaks included), snaps, and the permanent Green Tick milestone ----
  // All of it is granted by /api/rewards (server side, once each, daily caps) - the browser only asks.
  const callRewards = async (body: Record<string, unknown>): Promise<any> => {
    if (!user) return null
    try {
      const res = await fetch('/api/rewards', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${await user.getIdToken()}` },
        body: JSON.stringify(body),
      })
      return await res.json().catch(() => null)
    } catch { return null }
  }


  const [showTickSheet, setShowTickSheet] = useState(false) // Green Tick purchase sheet (no store - bought directly)
  const [supportCreator, setSupportCreator] = useState<{ uid: string; name: string; postId?: string } | null>(null)
  const [adminSearchResults, setAdminSearchResults] = useState<Profile[]>([])
  const [adminSearchBusy, setAdminSearchBusy] = useState(false)
  const [adminActionBusy, setAdminActionBusy] = useState(false)

  const adminSearchUsers = async (term: string) => {
    const clean = term.trim().toLowerCase().replace(/^@/, '')
    if (clean.length < 2) { setAdminSearchResults([]); return }
    setAdminSearchBusy(true)
    try {
      const snap = await getDocs(query(
        collection(firestore, 'users'),
        where('usernameLower', '>=', clean),
        where('usernameLower', '<=', `${clean}\uf8ff`),
        limit(20)
      ))
      // .data() alone has no `uid` field (users/{uid} docs never store their own id inside
      // themselves) - without attaching d.id here, every ban/action below silently no-ops
      // on doc(firestore, 'users', undefined) and falls into the generic catch as "failed".
      setAdminSearchResults(snap.docs.map((d) => ({ ...(d.data() as Profile), uid: d.id })))
    } catch { notify('Search failed.') } finally { setAdminSearchBusy(false) }
  }

  const adminBanUser = async (target: Profile, mode: 'temporary' | 'permanent', durationMs?: number, reason?: string): Promise<boolean> => {
    if (target.uid === user?.uid) { notify("You can't ban your own admin account."); return false }
    setAdminActionBusy(true)
    try {
      await updateDoc(doc(firestore, 'users', target.uid), {
        banned: true,
        banType: mode,
        bannedUntil: mode === 'temporary' ? Date.now() + (durationMs || 0) : null,
        banReason: reason || '',
      })
      setAdminSearchResults((list) => list.map((u) => u.uid === target.uid
        ? { ...u, banned: true, banType: mode, bannedUntil: mode === 'temporary' ? Date.now() + (durationMs || 0) : null, banReason: reason || '' }
        : u))
      notify(`@${target.username} ${mode === 'permanent' ? 'permanently banned' : 'temporarily banned'}.`)
      void writeModLog({ by: profile?.username || 'admin', action: mode === 'permanent' ? 'ban_permanent' : 'ban_temporary', targetUid: target.uid, targetUsername: target.username, note: reason })
      return true
    } catch (err) {
      // Show the real Firestore reason (permission-denied, not-found, etc.) instead of a
      // generic message - that's the only way to tell a rules problem apart from
      // something else from the toast alone.
      console.error('adminBanUser failed', err)
      const code = (err as { code?: string })?.code
      notify(code ? `Ban failed (${code}).` : 'Ban failed.')
      return false
    } finally { setAdminActionBusy(false) }
  }

  // ---- Admin "Boost lock": one flag in settings/app (boostEnabled), watched live by every
  // signed-in user (see the onSnapshot above). Off = Boost shows "Coming soon" everywhere,
  // on = Boost works normally. Same switch for the website and the app.
  const [adminBoostLockBusy, setAdminBoostLockBusy] = useState(false)
  const adminToggleBoostLock = async (enable: boolean) => {
    setAdminBoostLockBusy(true)
    try {
      await setDoc(doc(firestore, 'settings', 'app'), { boostEnabled: enable }, { merge: true })
      notify(enable ? 'Boost is now ON for all users.' : 'Boost locked (Coming soon) for all users.')
    } catch (err) {
      console.error('adminToggleBoostLock failed', err)
      const code = (err as { code?: string })?.code
      notify(code ? `Could not update Boost lock (${code}).` : 'Could not update Boost lock.')
    } finally { setAdminBoostLockBusy(false) }
  }

  const adminUnbanUser = async (target: Profile) => {
    setAdminActionBusy(true)
    try {
      await updateDoc(doc(firestore, 'users', target.uid), { banned: false, banType: null, bannedUntil: null, banReason: '' })
      setAdminSearchResults((list) => list.map((u) => u.uid === target.uid ? { ...u, banned: false, banType: undefined, bannedUntil: null, banReason: '' } : u))
      notify(`@${target.username} unbanned.`)
      void writeModLog({ by: profile?.username || 'admin', action: 'unban', targetUid: target.uid, targetUsername: target.username })
    } catch { notify('Unban failed.') } finally { setAdminActionBusy(false) }
  }

  // ---- Admin: remove any user's post, chat ban, upload hold (days / permanent / lift) ----
  const adminRemovePost = async (p: Post, reason: string) => {
    setAdminActionBusy(true)
    try {
      await deleteDoc(doc(firestore, 'posts', p.id))
      if (!(await postMediaUsedByLiveStory(p))) {
        if (!(await urlStillUsedAsSound(p.imageUrl, p.id))) void deleteUploadedFile(p.imageUrl)
        if (p.audioUrl && !p.audioShared && !(await urlStillUsedAsSound(p.audioUrl, p.id))) void deleteUploadedFile(p.audioUrl)
      }
      ;(p.imageUrls || []).slice(1).forEach((u) => void deleteUploadedFile(u))
      ;(p.extraImageUrls || []).forEach((u) => void deleteUploadedFile(u))
      if (p.thumbnailUrl) void deleteUploadedFile(p.thumbnailUrl)
      void sendAppNotification(p.authorId, 'admin', `removed your post for breaking community guidelines.${reason ? ` Reason: ${reason}` : ''}`)
      void writeModLog({ by: profile?.username || 'admin', action: 'post_removed', targetUid: p.authorId, targetUsername: p.authorName, note: reason })
      setViewingProfilePosts((list) => list.filter((x) => x.id !== p.id))
      setAdminRemoveTarget(null)
      notify('Post removed.')
    } catch (err) {
      console.error('adminRemovePost failed', err)
      const code = (err as { code?: string })?.code
      notify(code ? `Could not remove post (${code}).` : 'Could not remove post.')
    } finally { setAdminActionBusy(false) }
  }

  // days: number of days (>0), 'forever', or 0 = lift the restriction.
  const adminSetRestriction = async (t: { uid: string; username: string }, kind: 'chat' | 'upload', days: number | 'forever', reason: string): Promise<boolean> => {
    if (t.uid === user?.uid) { notify("You can't restrict your own admin account."); return false }
    const until = days === 'forever' ? RESTRICT_FOREVER : days > 0 ? Date.now() + Math.round(days * 86_400_000) : 0
    const patch = kind === 'chat'
      ? { chatBanUntil: until, chatBanReason: until ? reason : '' }
      : { uploadHoldUntil: until, uploadHoldReason: until ? reason : '' }
    setAdminActionBusy(true)
    try {
      await updateDoc(doc(firestore, 'users', t.uid), patch)
      setAdminSearchResults((list) => list.map((u) => u.uid === t.uid ? { ...u, ...patch } : u))
      setViewingProfile((v) => v && v.uid === t.uid ? { ...v, ...patch } : v)
      const label = kind === 'chat' ? 'Chat ban' : 'Upload hold'
      notify(until ? `${label} on @${t.username} ${days === 'forever' ? 'permanently' : `for ${days} day${days === 1 ? '' : 's'}`}.` : `${label} lifted for @${t.username}.`)
      void writeModLog({
        by: profile?.username || 'admin', action: `${kind === 'chat' ? 'chat_ban' : 'upload_hold'}${until ? '' : '_lifted'}`,
        targetUid: t.uid, targetUsername: t.username,
        note: until ? `${days === 'forever' ? 'permanent' : `${days}d`}${reason ? ` - ${reason}` : ''}` : undefined,
      })
      return true
    } catch (err) {
      console.error('adminSetRestriction failed', err)
      const code = (err as { code?: string })?.code
      notify(code ? `Restriction failed (${code}).` : 'Restriction failed.')
      return false
    } finally { setAdminActionBusy(false) }
  }

  // ---- Admin "Danger Zone": wipe the entire database back to a fresh install. ----
  // Single step, no Firebase email round trip: the admin types the exact confirmation
  // phrase into the Admin Console's "Clear Entire Database" panel (see WipeDatabaseSheet
  // below) and taps Delete. adminWipeDatabase() sends that typed text straight to the
  // server (app/api/admin/wipe/route.ts), which independently re-checks it matches before
  // erasing every document (and best-effort clears R2 media) - so this is not just a
  // client-side gate. The admin still has to be signed in as ADMIN_EMAIL to reach this at
  // all (normal app login, checked server-side via requireAuth).
  const WIPE_CONFIRM_PHRASE = 'DELETE DATABASE'
  const [adminWipeBusy, setAdminWipeBusy] = useState(false)
  const [adminWipeError, setAdminWipeError] = useState('')

  const adminWipeDatabase = async (confirmText: string) => {
    if (!user) return
    setAdminWipeBusy(true)
    setAdminWipeError('')
    try {
      const idToken = await user.getIdToken(true)
      const res = await fetch('/api/admin/wipe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
        body: JSON.stringify({ action: 'confirm', confirmText }),
      })
      const d = await res.json().catch(() => ({}))
      if (!res.ok || !d.ok) throw new Error(d?.error || 'Could not wipe the database.')
      // Every document (including this admin's own profile) is gone now - sign out and
      // reload straight into a clean "no account yet" state, same as a brand-new install.
      notify('Database wiped. Restarting…')
      try { await firebaseAuth.signOut() } catch { /* ignore */ }
      window.location.reload()
    } catch (err: any) {
      setAdminWipeError(friendlyError(err, 'Could not wipe the database.'))
      setAdminWipeBusy(false)
    }
  }

  // Stops the song of a photo post (if any is playing) and forgets the audio element.
  const stopPostAudio = useCallback(() => {
    const a = postAudioRef.current
    if (a) { try { a.pause() } catch { /* ignore */ } postAudioRef.current = null }
    setPlayingPostAudioId(null)
  }, [])
  useEffect(() => {
    stopFeedPostAudio = stopPostAudio
    return () => { if (stopFeedPostAudio === stopPostAudio) stopFeedPostAudio = () => {} }
  }, [stopPostAudio])

  // Toggle Global Audio for Post in Feed
  const togglePlayPostAudio = (postId: string, audioUrl?: string) => {
    if (!audioUrl) return
    if (playingPostAudioId === postId) {
      stopPostAudio()
      return
    }
    if (postAudioRef.current) postAudioRef.current.pause()
    // Starting a song pauses any feed video that is playing, so they never run together.
    postVideoEntries.forEach((e) => { if (!e.el.paused) e.stop() })
    const audio = new Audio(audioUrl)
    audio.loop = true
    postAudioRef.current = audio
    setPlayingPostAudioId(postId)
    audio.play().catch(() => { if (postAudioRef.current === audio) { postAudioRef.current = null; setPlayingPostAudioId(null) } })
  }

  // The song must stop whenever the person leaves the post they were listening to: another tab,
  // a chat, a profile, a story / upload sheet, games, wallet, notifications, Reels... It used to
  // stop only for stories and the upload sheet, so it kept playing everywhere else.
  useEffect(() => {
    if (playingPostAudioId) stopPostAudio()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, activeChat, viewingProfile, viewingStory, uploadOpen, showCreateStory, reelDraft, showGames, ludoLaunch, chessLaunch, carromLaunch, showWallet, showNotifications, showMsgRequests, showProfileEdit, showFriendsList, showGroup, showAdminPanel, showHomeThemes, focusedReelId])

  // App sent to the background / screen locked -> song off.
  useEffect(() => {
    const onHide = () => { if (document.hidden) stopPostAudio() }
    document.addEventListener('visibilitychange', onHide)
    return () => document.removeEventListener('visibilitychange', onHide)
  }, [stopPostAudio])

  // Post deleted (from the feed, a profile, the preview, by an admin, from another device...) while
  // its song is playing -> song off. Detected as "was in one of the post lists a moment ago, now gone",
  // so a post that simply lives somewhere else never gets its song cut by mistake.
  const knownPostIdsRef = useRef<Set<string>>(new Set())
  useEffect(() => {
    const now = new Set<string>()
    rawPosts.forEach((x: Post) => now.add(x.id))
    myPosts.forEach((x: Post) => now.add(x.id))
    viewingProfilePosts.forEach((x: Post) => now.add(x.id))
    if (previewPost) now.add(previewPost.id)
    const id = playingPostAudioId
    if (id && knownPostIdsRef.current.has(id) && !now.has(id)) stopPostAudio()
    knownPostIdsRef.current = now
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawPosts, myPosts, viewingProfilePosts, previewPost, playingPostAudioId])

  // Chat-list swipe actions: "mark unread" is a local flag (like WhatsApp); "mark read" really marks their messages read.
  const [manualUnread, setManualUnread] = useState<Set<string>>(() => {
    try { return new Set(JSON.parse(localStorage.getItem('animora_manual_unread') || '[]')) } catch { return new Set() }
  })
  const setManualUnreadFlag = (chatId: string, on: boolean) => {
    setManualUnread((prev) => {
      if (prev.has(chatId) === on) return prev
      const next = new Set(prev)
      if (on) next.add(chatId); else next.delete(chatId)
      try { localStorage.setItem('animora_manual_unread', JSON.stringify(Array.from(next))) } catch {}
      return next
    })
  }
  const markChatRead = async (chat: Chat) => {
    setManualUnreadFlag(chat.id, false)
    if (!user || chat.kind === 'group' || dmPendingFromRef.current.has(chat.id)) return
    try {
      const chatId = [user.uid, chat.id].sort().join('_')
      const snap = await getDocs(query(collection(firestore, 'chats', chatId, 'messages'), where('userId', '==', chat.id), where('read', '==', false), limit(99)))
      await Promise.all(snap.docs.map((m) => updateDoc(m.ref, { read: true, readAt: Date.now() })))
    } catch { notify('Could not mark as read.') }
  }


  // Toggle Pinned Chat
  const togglePinChat = (chatId: string) => {
    setPinnedChatIds((prev) => {
      const next = new Set(prev)
      if (next.has(chatId)) {
        next.delete(chatId)
        notify('Channel unpinned.')
      } else {
        next.add(chatId)
        notify('Channel pinned to top')
      }
      try { localStorage.setItem('animechat_pinned_chats', JSON.stringify(Array.from(next))) } catch {}
      return next
    })
    setChatLongPressTarget(null)
  }

  const toggleMuteChat = (chat: Chat) => {
    const key = `${chat.kind === 'group' ? 'group' : 'direct'}:${chat.id}`
    setMutedChatIds((prev) => {
      const next = new Set(prev)
      if (next.has(key)) {
        next.delete(key)
        notify('Notifications turned back on.')
      } else {
        next.add(key)
        notify('Chat muted - you will not get notifications for it.')
      }
      try { localStorage.setItem('animechat_muted_chats', JSON.stringify(Array.from(next))) } catch {}
      return next
    })
    setChatLongPressTarget(null)
  }

  // Remove a chat from MY list (works for friends, message requests and chats I started). The other person is not
  // affected and the messages stay; start a new chat or message again and it comes back.
  const hideChatFromList = async (chat: Chat) => {
    if (!user) return
    setChatLongPressTarget(null)
    if (chat.kind === 'group') { notify('To leave a group, open Group Info and tap Leave.'); return }
    // 1) Gone from the screen immediately and remembered on this device.
    setChats((curr) => curr.filter((c) => c.kind === 'group' || c.id !== chat.id))
    setAllFriends((curr) => curr.map((f) => (f.id === chat.id ? { ...f, hidden: true } : f)))
    setHiddenDmIds((prev) => { const next = new Set(prev); next.add(chat.id); saveHiddenDm(next); return next })
    notify(`${chat.name} removed from your chats.`)
    if (activeChat && activeChat.kind !== 'group' && activeChat.id === chat.id) setActiveChat(null)
    // 2) Saved on the server so it also stays removed on a new phone / after reinstall. A failure here is not shown
    //    as an error: the chat is already removed on this device.
    const jobs: Promise<unknown>[] = []
    if (allFriends.some((f) => f.id === chat.id)) {
      jobs.push(setDoc(doc(firestore, 'friends', user.uid, 'list', chat.id), { hidden: true }, { merge: true }))
    }
    for (const r of [...dmIn, ...dmOut]) {
      if (r.otherId === chat.id) jobs.push(updateDoc(doc(firestore, 'dmRequests', r.id), { hiddenFor: arrayUnion(user.uid) }))
    }
    await Promise.allSettled(jobs)
  }

  // Add post to story (WITH POST MUSIC ATTACHED)
  // Share counter shown on posts & reels. Best-effort: never blocks or fails the share itself.
  const bumpShareCount = async (p: Post) => {
    if (!p?.id || p.id === 'shared' || p.previewOnly) return
    try { await updateDoc(doc(firestore, 'posts', p.id), { shareCount: increment(1) }) }
    catch (err) { console.warn('share count update failed', err) }
  }

  const addPostToStory = async (p: Post) => {
    if (!user) return
    try {
      await addDoc(collection(firestore, 'stories'), {
        authorId: user.uid,
        authorName: profile?.name || 'Traveler',
        authorAvatar: profile?.avatar || '',
        mediaUrl: p.imageUrl,
        sourcePostId: p.id, // marks the media as owned by the post (see storyMediaBelongsToPost)
        mediaType: p.mediaType === 'video' ? 'video' : 'image',
        likes: [],
        privacy: 'public',
        viewers: [],
        caption: p.caption || '',
        audioUrl: p.audioUrl || null,
        audioTitle: p.audioTitle || null,
        // Sound info travels with the story, so the story can show "Artist · Title" and open the same Sound sheet as a reel.
        ...(p.soundOwnerName || p.authorName ? { soundOwnerName: String(p.soundOwnerName || p.authorName).slice(0, 60) } : {}),
        ...(p.remixOf ? { remixOf: p.remixOf } : {}),
        ...(p.audioShared ? { audioShared: true } : {}),
        createdAt: serverTimestamp(),
      })
      void bumpShareCount(p)
      notify('Post & Music broadcasted to your Story!')
      setShareActionPost(null)
    } catch {
      notify('Could not add to story.')
    }
  }

  // Fetch Leaderboard / Hall of Fame - ranked by ALLIES (followersCount), highest first. Coins do not
  // count any more. A few extra rows are pulled so banned accounts (dropped later) never leave it short.
  const fetchLeaderboard = async () => {
    try {
      const snap = await getDocs(query(collection(firestore, 'users'), orderBy('followersCount', 'desc'), limit(40)))
      const list = snap.docs
        .map((d) => ({ uid: d.id, ...d.data() } as Profile))
        .sort((a, b) => (b.followersCount || 0) - (a.followersCount || 0))
        .slice(0, 25)
      list.forEach((u) => rememberLiveAvatar(u.uid, u.avatar))
      setLeaderboardUsers(list)
    } catch { /* ignore */ } finally { setBoardsLoaded((b) => ({ ...b, allies: true })) }
  }

  // Coins Leaderboard - richest users first. Coin balance is derived from several fields (gifts +
  // top-ups + rewards + referrals - spent), so pull a wide batch and rank it on the client.
  const coinLeadersBusyRef = useRef(false)
  const fetchCoinLeaders = async () => {
    if (coinLeadersBusyRef.current) return // already loading: two screens asking at once must not double the queries
    coinLeadersBusyRef.current = true
    try {
      // Not "the first 300 users" any more (that ignored everyone after #300). Take the biggest earners of both coin sources and merge.
      // Each query has its own 9 s limit, so one slow query can no longer keep the whole board on the loading skeleton forever.
      const [a, b, c, e] = await Promise.all([
        raceTimeout(getDocs(query(collection(firestore, 'users'), orderBy('rewardCoins', 'desc'), limit(80)))).catch(() => null),
        raceTimeout(getDocs(query(collection(firestore, 'users'), orderBy('giftsReceivedRupees', 'desc'), limit(40)))).catch(() => null),
        // Balance also includes referral coins and top-ups: people whose coins come mostly from those were missing from the board.
        raceTimeout(getDocs(query(collection(firestore, 'users'), orderBy('referralCoins', 'desc'), limit(20)))).catch(() => null),
        raceTimeout(getDocs(query(collection(firestore, 'users'), orderBy('walletTopUpRupees', 'desc'), limit(20)))).catch(() => null),
      ])
      const byUid = new Map<string, Profile>()
      for (const snap of [a, b, c, e]) {
        snap?.docs.forEach((d) => {
          if (byUid.has(d.id)) return
          const data = d.data() as any
          byUid.set(d.id, { uid: d.id, ...data, publicCoins: coinBalanceOf(data) } as Profile)
        })
      }
      const list = [...byUid.values()]
        .filter((u) => (u.publicCoins || 0) > 0)
        .sort((x, y) => (y.publicCoins || 0) - (x.publicCoins || 0))
        .slice(0, 25)
      list.forEach((u) => rememberLiveAvatar(u.uid, u.avatar))
      // All four queries failed / timed out: keep whatever the board already shows instead of blanking it.
      if (list.length || [a, b, c, e].some(Boolean)) {
        setCoinLeaders(list)
        try { localStorage.setItem('animora:coinleaders', JSON.stringify(list.slice(0, 25))) } catch { /* storage full / blocked */ }
      }
    } catch { /* ignore */ } finally { coinLeadersBusyRef.current = false; setBoardsLoaded((b) => ({ ...b, coins: true })) }
  }

  // Fetch 50 random people for the Add friend page (Shuffle re-rolls the whole list).
  // Pulls a bigger pool than 50 so every Shuffle can actually show different people.
  const fetchRandomUsers = async () => {
    if (!user) return
    setRandomLoading(true)
    try {
      const snap = await raceTimeout(getDocs(query(collection(firestore, 'users'), limit(200))), 10000)
      const all = snap.docs.map(d => ({ uid: d.id, ...d.data() } as Profile)).filter(p => p.uid !== user.uid && p.name)
      for (let i = all.length - 1; i > 0; i--) { // Fisher-Yates: a real shuffle
        const j = Math.floor(Math.random() * (i + 1))
        ;[all[i], all[j]] = [all[j], all[i]]
      }
      setRandomUsers(all.slice(0, 50))
    } catch { notify('Failed to randomize.') } finally { setRandomLoading(false) }
  }

  // Reels feed friend suggestions: random people I'm NOT already allied with / waiting on.
  // The list is fixed at fetch time (not re-filtered live) so the grid never shifts under the viewer's thumb.
  const fetchReelSuggest = async () => {
    if (!user) return
    setReelSuggestLoading(true)
    try {
      const snap = await getDocs(query(collection(firestore, 'users'), limit(60)))
      const friendSet = new Set(chats.filter((c) => c.kind !== 'group').map((c) => c.id))
      const all = snap.docs
        .map((d) => ({ uid: d.id, ...d.data() } as Profile))
        .filter((p) => p.uid !== user.uid && p.name && !friendSet.has(p.uid) && !sentRequests.has(p.uid))
      for (let i = all.length - 1; i > 0; i--) { // Fisher-Yates: a real shuffle
        const j = Math.floor(Math.random() * (i + 1))
        ;[all[i], all[j]] = [all[j], all[i]]
      }
      setReelSuggest(all.slice(0, 12))
    } catch { /* suggestions are optional - stay silent */ } finally { setReelSuggestLoading(false) }
  }
  const reelSuggest = useMemo(() => dropBanned(rawReelSuggest, (u) => u.uid), [rawReelSuggest, bannedUids])

  // Add friend page: load a fresh set of 50 random people every time it opens.
  useEffect(() => {
    if (showAddFriend) void fetchRandomUsers()
  }, [showAddFriend])

  // Hall of Fame: show the last saved board at once when the app opens (fresh data replaces it a moment later),
  // and pre-load it quietly a few seconds after login so the tab is already filled when you open it.
  useEffect(() => {
    if (!user) return
    try {
      const raw = localStorage.getItem('animora:coinleaders')
      const cached = raw ? JSON.parse(raw) : null
      if (Array.isArray(cached) && cached.length) {
        setCoinLeaders((cur) => (cur.length ? cur : cached))
        setBoardsLoaded((b) => (b.coins ? b : { ...b, coins: true }))
      }
    } catch { /* corrupt cache: ignore */ }
    const t = window.setTimeout(() => { void fetchCoinLeaders() }, 5000)
    return () => window.clearTimeout(t)
  }, [user?.uid])

  useEffect(() => {
    // (Coin leaders are loaded by the effect below - this one used to start the same 4 queries a second time.)
    if (activeTab === 'reels' && rawReelSuggest.length === 0 && !reelSuggestLoading) void fetchReelSuggest()
  }, [activeTab])

  // Coin Kings stays fresh while it is on screen (it used to be fetched once and then go stale vs the wallet).
  useEffect(() => {
    if (activeTab !== 'contacts' || discoverSubTab !== 'coins') return
    void fetchCoinLeaders()
    const t = setInterval(() => { if (typeof document === 'undefined' || document.visibilityState === 'visible') void fetchCoinLeaders() }, 25000)
    return () => clearInterval(t)
  }, [activeTab, discoverSubTab])

  // 0. Register the Service Worker (push notifications + clears stale cached data on updates)
  useEffect(() => {
    if (typeof window !== 'undefined' && 'serviceWorker' in navigator) {
      navigator.serviceWorker.register('/sw.js').catch(() => {})
    }
  }, [])

  // (Google sign-in never uses the redirect flow, so there is no redirect result to read. Reading it used to show a
  // confusing "browser is blocking storage" error on login for some phones/WebViews.)

  // Logged in while on /sign-up or /login -> go to the home page ("/").
  useEffect(() => {
    if (user && profile && (pathname === '/sign-up' || pathname === '/login')) {
      // A guest who was sent here from a reel / profile goes back to exactly that page.
      let to = '/'
      try { to = safeReturnTo(sessionStorage.getItem(RETURN_TO_KEY)); sessionStorage.removeItem(RETURN_TO_KEY) } catch { /* ignore */ }
      router.replace(to)
    }
  }, [user, profile, pathname, router])

  // Keep the instant-start copy fresh.
  useEffect(() => {
    if (!user || !profile || profile.uid !== user.uid) return
    try {
      localStorage.setItem(PROFILE_CACHE_KEY, JSON.stringify({ uid: user.uid, profile, isAdmin: isAdminAccount }))
      localStorage.setItem('animora_bg_hint', activeHomeTheme ? activeHomeTheme.bg : '#08080b')
    } catch { /* storage full / blocked */ }
  }, [user, profile, isAdminAccount, activeHomeTheme])

  // 1. Auth Listener
  useEffect(() => {
    return onAuthStateChanged(firebaseAuth, async (nextUser) => {
      setUser(nextUser)
      if (!nextUser) { clearProfileCache(); setProfile(null); setProfileLoadError(''); setIsAdminAccount(false); reelInterestRef.current = emptyReelInterestState(); setAuthBusy(false); return }
      // Hold the spinner while the profile loads, so an existing user never flashes the
      // "Set up your account" screen (and can't overwrite their profile from it).
      const cached = readProfileCache()
      if (cached && cached.uid === nextUser.uid) {
        // Show the app right away with the saved profile; the code below refreshes it in the background.
        setProfile(cached.profile)
        setIsAdminAccount(Boolean(cached.isAdmin))
        // Home screen from the saved snapshot (feed + stories row) - the live listeners replace it a moment later.
        const hc = readHomeCache(nextUser.uid)
        if (hc) {
          const age = Date.now() - Number(hc.at || 0)
          if (age < HOME_POSTS_MAX_AGE_MS && hc.posts.length) setPosts((p) => (p.length ? p : hc.posts))
          if (age < HOME_STORIES_MAX_AGE_MS && hc.stories.length) setStories((s) => (s.length ? s : hc.stories))
        }
        setAuthBusy(false)
      } else {
        setAuthBusy(true)
      }
      setProfileLoadError('')
      // Ask the server "am I the admin?" once per sign-in (cheap - no DB access, see the
      // `whoami` op in app/api/db/route.ts). Kept in a local var too, so the signup flow
      // below (a brand-new account, before `isAdminAccount` state has re-rendered) and the
      // self-heal check further down both use this same answer instead of racing separate
      // requests or falling back to a guess.
      const adminNow = await checkIsAdmin()
      setIsAdminAccount(adminNow)
      try {
        const snap = await getUserDocWithRetry(nextUser.uid)
        if (snap.exists()) {
          const d = snap.data()

          // Account lifecycle check - runs on every login.
          const deletionScheduledAt = typeof d.deletionScheduledAt === 'number'
            ? d.deletionScheduledAt
            : d.deletionScheduledAt?.toMillis?.()

          if (deletionScheduledAt) {
            if (Date.now() - deletionScheduledAt >= ACCOUNT_DELETION_GRACE_MS) {
              // 3-day grace window passed without a login - permanently erase everything.
              try {
                const postsSnap = await getDocs(query(collection(firestore, 'posts'), where('authorId', '==', nextUser.uid)))
                await Promise.all(postsSnap.docs.map((p) => deleteDoc(p.ref)))
              } catch {}
              try { await deleteDoc(doc(firestore, 'users', nextUser.uid)) } catch {}
              try { await deleteUser(nextUser) } catch {}
              await signOut(firebaseAuth)
              setProfile(null)
              setAuthBusy(false)
              alert('This account passed its 3-day deletion window and has been permanently erased.')
              return
            }
            // Logged back in inside the grace window - cancel the scheduled deletion.
            d.deletionScheduledAt = null
            d.deactivated = false
            await settleWithin(updateDoc(doc(firestore, 'users', nextUser.uid), { deletionScheduledAt: null, deactivated: false }))
          } else if (d.deactivated) {
            // Temporarily-deactivated accounts reactivate automatically the moment
            // the owner logs back in.
            d.deactivated = false
            await settleWithin(updateDoc(doc(firestore, 'users', nextUser.uid), { deactivated: false }))
          }

          reelInterestRef.current = normalizeReelInterestState(d.reelInterests)
          // Admin access follows the signed-in Google account's email (checked server-side
          // against the ADMIN_EMAIL secret, see adminNow above), not the username. Self-heal
          // the stored display flag so other users' clients can show this account's badge too;
          // the write itself is re-checked by lib/d1-rules.ts, so a non-admin sending this
          // can never actually flip it to true.
          if (adminNow !== Boolean(d.isAdmin)) {
            void settleWithin(updateDoc(doc(firestore, 'users', nextUser.uid), { isAdmin: adminNow }))
          }
          setProfile({
            uid: nextUser.uid,
            name: String(d.name || nextUser.displayName || 'Anime Fan'),
            username: String(d.username || `user_${nextUser.uid.slice(0, 7)}`),
            usernameLower: String(d.usernameLower || d.username || '').toLowerCase(),
            email: nextUser.email || '',
            isAdmin: adminNow,
            avatar: String(d.avatar || nextUser.photoURL || fallbackAvatarSvg(nextUser.displayName || 'Fan')),
            bio: String(d.bio || 'Connected to the anime multiverse.'),
            coverUrl: typeof d.coverUrl === 'string' ? d.coverUrl : '',
            equipped: cleanEquipped(d.equipped),
            prizesOwned: cleanOwned(d.prizesOwned),
            followersCount: Number(Math.max(0, d.followersCount || 0)),
            followingCount: Number(Math.max(0, d.followingCount || 0)),
            gender: d.gender || 'male',
            chatBackground: String(d.chatBackground || 'none'),
            appBackground: String(d.appBackground || 'none'),
            gamesTheme: typeof d.gamesTheme === 'string' ? d.gamesTheme : '',
            coinsSpent: Number(d.coinsSpent || 0),
            giftsReceivedRupees: Number(d.giftsReceivedRupees || 0), // part of the coin balance - without it the balance first showed only a few coins after a restart
            walletTopUpRupees: Number(d.walletTopUpRupees || 0),
            referralCoins: Number(d.referralCoins || 0),
        rewardCoins: Number(d.rewardCoins || 0),
        greenTickMilestone: d.greenTickMilestone === true,
            country: normalizeCountry(d.country) || undefined,
            bubbleColor: String(d.bubbleColor || 'violet'),
            receivedBubbleColor: String(d.receivedBubbleColor || ''),
            private: Boolean(d.private),
            headerTheme: String(d.headerTheme || 'auto'),
            unlockedHeaderThemes: Array.isArray(d.unlockedHeaderThemes) ? d.unlockedHeaderThemes : [],
            xpSpent: Number(d.xpSpent || 0),
            deactivated: false,
            deletionScheduledAt: undefined,
            banned: Boolean(d.banned),
            banType: d.banType || undefined,
            bannedUntil: typeof d.bannedUntil === 'number' ? d.bannedUntil : (d.bannedUntil?.toMillis?.() ?? null),
            banReason: String(d.banReason || ''),
            chatBanUntil: typeof d.chatBanUntil === 'number' ? d.chatBanUntil : 0,
            chatBanReason: String(d.chatBanReason || ''),
            uploadHoldUntil: typeof d.uploadHoldUntil === 'number' ? d.uploadHoldUntil : 0,
            uploadHoldReason: String(d.uploadHoldReason || ''),
          })
        } else {
          clearProfileCache()
          setProfile(null)
          setShowUsernameSetup(true)
        }
      } catch (err) {
        // Loading failed (rules / network). Do NOT treat this as a new account - show a retry screen.
        console.warn(err)
        setProfileLoadError(describeProfileLoadError(err))
      }
      setAuthBusy(false)
    })
  }, [])

  // Safety net: if the first load is still spinning after 15s (blocked storage, dead connection, a hung request),
  // stop the spinner. Signed-in users get the "Try again" screen; everyone else gets the login screen.
  useEffect(() => {
    if (!authBusy) return
    const t = setTimeout(() => {
      setAuthBusy(false)
      if (!firebaseAuth.currentUser) clearProfileCache()
      if (firebaseAuth.currentUser) setProfileLoadError((prev) => prev || 'Loading is taking longer than usual. Check your internet connection and try again.')
    }, 15000)
    return () => clearTimeout(t)
  }, [authBusy])

  // 1b. Live ban status for the signed-in user. Before, the ban flags were never
  // copied into `profile` at all (so the "Account Banned" screen could never show)
  // and were only read once at login - now a ban lands instantly, mid-session.
  const streakChecked = useRef(false)
  useEffect(() => {
    if (!user) return
    streakChecked.current = false
    return onSnapshot(doc(firestore, 'users', user.uid), (snap) => {
      const d = snap.data()
      if (!d) return
      const bannedUntil = typeof d.bannedUntil === 'number' ? d.bannedUntil : (d.bannedUntil?.toMillis?.() ?? null)
      setProfile((p) => p ? {
        ...p,
        banned: Boolean(d.banned), banType: d.banType || undefined, bannedUntil, banReason: String(d.banReason || ''),
        chatBanUntil: typeof d.chatBanUntil === 'number' ? d.chatBanUntil : 0, chatBanReason: String(d.chatBanReason || ''),
        uploadHoldUntil: typeof d.uploadHoldUntil === 'number' ? d.uploadHoldUntil : 0, uploadHoldReason: String(d.uploadHoldReason || ''),
        achievementsUnlocked: (d.achievementsUnlocked || {}) as AchievementUnlockMap,
        messagesSent: Number(d.messagesSent || 0),
        groupMessagesSent: Number(d.groupMessagesSent || 0),
        activityStreak: Number(d.activityStreak || 0),
        bestStreak: Number(d.bestStreak || 0),
        lastActiveDay: typeof d.lastActiveDay === 'string' ? d.lastActiveDay : undefined,
        // Paid / earned fields. These were written to Firestore but never read back into
        // `profile`, so e.g. a received gift always showed as ₹0. Never let the total go
        // down here (the gifts-log fallback in refreshAchievementStats may already be higher).
        giftsReceivedRupees: Math.max(Number(d.giftsReceivedRupees || 0), p.giftsReceivedRupees || 0),
        isPremium: Boolean(d.isPremium),
        premiumUntil: typeof d.premiumUntil === 'number' ? d.premiumUntil : undefined,
        unlockedChatThemes: Array.isArray(d.unlockedChatThemes) ? d.unlockedChatThemes : [],
        headerTheme: typeof d.headerTheme === 'string' ? d.headerTheme : (p.headerTheme || 'auto'),
        unlockedHeaderThemes: Array.isArray(d.unlockedHeaderThemes) ? d.unlockedHeaderThemes : [],
        xpSpent: Number(d.xpSpent || 0),
        savedStickers: Array.isArray(d.savedStickers) ? d.savedStickers.filter((u: unknown) => typeof u === 'string') : [],
        customUsernameCredits: Number(d.customUsernameCredits || 0),
        usernameChangeUsed: Boolean(d.usernameChangeUsed),
        usernameChangeCount: Number(d.usernameChangeCount || 0),
        usernameChangeLockUntil: Number(d.usernameChangeLockUntil || 0),
        coinsSpent: Number(d.coinsSpent || 0),
        walletTopUpRupees: Number(d.walletTopUpRupees || 0),
        referralCoins: Number(d.referralCoins || 0),
        rewardCoins: Number(d.rewardCoins || 0),
        greenTickMilestone: d.greenTickMilestone === true,
        groupMemberBonus: Number(d.groupMemberBonus || 0),
        country: normalizeCountry(d.country) || undefined,
        coverUrl: typeof d.coverUrl === 'string' ? d.coverUrl : '',
        equipped: cleanEquipped(d.equipped),
        prizesOwned: cleanOwned(d.prizesOwned),
        avatar: String(d.avatar || p.avatar),
      } : p)

      // Daily activity streak: first snapshot of each session checks whether today is a
      // new day; consecutive days extend the streak, a gap resets it to 1.
      if (!streakChecked.current) {
        streakChecked.current = true
        const today = localDayKey(0)
        if (d.lastActiveDay !== today) {
          const cur = d.lastActiveDay === localDayKey(-1) ? Number(d.activityStreak || 0) + 1 : 1
          updateDoc(doc(firestore, 'users', user.uid), {
            lastActiveDay: today,
            activityStreak: cur,
            bestStreak: Math.max(Number(d.bestStreak || 0), cur),
          }).catch(() => { streakChecked.current = false })
        }
      }
    }, () => undefined)
  }, [user])

  // 1c. Live list of every banned account, used to hide them from everyone else.
  useEffect(() => {
    if (!user) return
    return onSnapshot(query(collection(firestore, 'users'), where('banned', '==', true)), (snap) => {
      const next: Record<string, Profile> = {}
      snap.docs.forEach((d) => {
        const data = d.data()
        next[d.id] = {
          uid: d.id,
          name: String(data.name || ''),
          username: String(data.username || ''),
          usernameLower: String(data.usernameLower || ''),
          email: '',
          avatar: '',
          bio: '',
          banned: Boolean(data.banned),
          banType: data.banType || undefined,
          bannedUntil: typeof data.bannedUntil === 'number' ? data.bannedUntil : (data.bannedUntil?.toMillis?.() ?? null),
        }
      })
      setBannedRecords(next)
    }, () => undefined)
  }, [user])

  // My block / restrict / hide list (owner-only doc collection, written by /api/block).
  useEffect(() => {
    if (!user) { setBlockRecords({}); return }
    return onSnapshot(collection(firestore, 'users', user.uid, 'blockedUsers'), (snap) => {
      const next: Record<string, BlockEntry> = {}
      snap.docs.forEach((d) => {
        const x = d.data()
        const mode: BlockMode = x.mode === 'restrict' || x.mode === 'hide' ? x.mode : 'block'
        next[d.id] = { uid: d.id, mode, name: String(x.name || ''), username: String(x.username || ''), avatar: String(x.avatar || '') }
      })
      setBlockRecords(next)
    }, () => undefined)
  }, [user])

  // Close an open DM the moment the other person gets banned.
  useEffect(() => {
    if (activeChat && activeChat.kind !== 'group' && bannedUids.has(activeChat.id)) setActiveChat(null)
  }, [bannedUids, activeChat])

  // Invite link (?ref=CODE): remember the code so it can be attached when the new account is created.
  // Also tells the server (no login needed) so the code can be auto-filled after a Play Store install,
  // where the browser's localStorage is not shared with the installed app.
  useEffect(() => {
    const remember = (raw: string | null) => {
      const c = normalizeReferralCode(raw)
      if (!c) return
      try { localStorage.setItem('animora_ref', c) } catch { /* storage blocked: the code can still be typed on the sign-up form */ }
      const isNative = !!(window as any).Capacitor?.isNativePlatform?.()
      if (!isNative) void fetch('/api/referral', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action: 'visit', code: c }) }).catch(() => undefined)
    }
    try { remember(new URLSearchParams(window.location.search).get('ref')) } catch { /* ignore */ }
    // Android app opened by tapping an invite link (App Links): read ?ref= from the URL it was opened with.
    let handle: { remove?: () => void } | undefined
    try {
      const appPlugin = (window as any).Capacitor?.Plugins?.App
      const onUrl = (ev: { url?: string }) => { try { remember(new URL(String(ev?.url || '')).searchParams.get('ref')) } catch { /* ignore */ } }
      Promise.resolve(appPlugin?.addListener?.('appUrlOpen', onUrl)).then((h: any) => { handle = h }).catch(() => undefined)
      Promise.resolve(appPlugin?.getLaunchUrl?.()).then((r: any) => { if (r?.url) onUrl(r) }).catch(() => undefined)
    } catch { /* plugin not installed */ }
    return () => { try { handle?.remove?.() } catch { /* ignore */ } }
  }, [])

  // Settle any referral rewards waiting for me as soon as I open the app (not only when the Wallet is opened).
  useEffect(() => {
    if (!user || !profile || ageStatus !== 'ok') return
    let cancelled = false
    ;(async () => {
      try {
        await fetch('/api/referral', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${await user.getIdToken()}` },
          body: JSON.stringify({ action: 'sync' }),
        })
      } catch { /* the Wallet retries later */ }
      void cancelled
    })()
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.uid, profile?.uid, ageStatus])

  // "Congratulations" popup whenever referral coins go up (inviter got a friend counted, or new user got the welcome bonus).
  useEffect(() => {
    if (!profile) { prevRefCoins.current = null; return }
    const now = Number(profile.referralCoins || 0)
    const prev = prevRefCoins.current
    if (prev && prev.uid === profile.uid && now > prev.coins) setRefCongrats(now - prev.coins)
    prevRefCoins.current = { uid: profile.uid, coins: now }
  }, [profile?.uid, profile?.referralCoins])

  // Age gate check for an existing profile (new signups answer it inside UsernameSetupModal, before the profile exists).
  useEffect(() => {
    if (!user || !profile) { setAgeStatus('checking'); return }
    let cancelled = false
    ;(async () => {
      try {
        const res = await fetch('/api/account/age', { headers: { Authorization: `Bearer ${await user.getIdToken()}` } })
        const d = await res.json().catch(() => ({}))
        if (cancelled) return
        setAdAudience({ minor: d?.band === 'teen' }) // 13-17: no personalised ads, content rating capped (see lib/admob.ts)
        setAgeStatus(d?.status === 'under-age' ? 'under-age' : d?.status === 'none' ? 'needs' : 'ok')
        if (d?.status !== 'under-age' && d?.status !== 'none') startAdPreload(user.uid) // audience is known now: load the rewarded ad in the background so gift boxes open instantly
      } catch { if (!cancelled) { setAdAudience({ minor: true }); setAgeStatus('ok'); startAdPreload(user.uid) } /* network hiccup: treat as a minor for ads (safe default): don't lock everyone out; gifts stay server-blocked until verified */ }
    })()
    return () => { cancelled = true }
  }, [user, profile?.uid])

  // 2. Notifications Listener
  useEffect(() => {
    if (!user) return
    let seeded = false // the first server snapshot is what was already there - never pop notifications for it
    return onSnapshot(query(collection(firestore, 'notifications', user.uid, 'items'), orderBy('createdAt', 'desc'), limit(40)), (snap) => {
      const list: AppNotification[] = snap.docs.map((d) => {
        const data = d.data()
        return {
          id: d.id,
          fromId: String(data.fromId || ''),
          // also fixes older admin notices that were saved with the admin's own name/photo
          fromName: data.type === 'admin' ? 'Animora' : String(data.fromName || 'Someone'),
          fromAvatar: data.type === 'admin' ? '/icon-192.png' : String(data.fromAvatar || ''),
          type: data.type || 'like',
          message: String(data.message || ''),
          targetId: data.targetId,
          time: formatTime(data.createdAt?.toDate?.()),
          createdAtMs: Number(data.createdAt?.toMillis?.() || 0),
          read: data.read === true,
          giftLabel: typeof data.giftLabel === 'string' ? data.giftLabel : undefined,
        }
      })
      setNotifications(list)

      if (!seeded) { if (!snap.metadata.fromCache) seeded = true; return }
      snap.docChanges().forEach((ch) => {
        if (ch.type !== 'added') return
        const n = list.find((x) => x.id === ch.doc.id)
        if (!n || n.read) return
        const created = ch.doc.data().createdAt?.toMillis?.()
        if (created && Math.abs(Date.now() - created) > 15 * 60 * 1000) return // an old alert sliding into the 40-item window
        pushRef.current.alert(n)
      })
    })
  }, [user])

  // 3. Friend Requests Listener
  useEffect(() => {
    if (!user) return
    return onSnapshot(query(collection(firestore, 'friendRequests'), where('to', '==', user.uid), where('status', '==', 'pending')), async (snap) => {
      const list = await Promise.all(snap.docs.map(async (item) => {
        const data = item.data()
        const sender = await getDoc(doc(firestore, 'users', data.from)).catch(() => null)
        const sData = sender?.data() || {}
        return {
          id: item.id,
          from: data.from,
          to: data.to,
          status: data.status,
          fromName: String(sData.name || 'Friend'),
          fromUsername: String(sData.username || ''),
          fromAvatar: String(sData.avatar || ''),
        } as FriendRequest
      }))
      setRequests(list)
    })
  }, [user])

  // 3b. Message requests: the first DM from someone who is not a friend / follower lands in a separate Requests folder.
  useEffect(() => {
    if (!user) return
    const watch = (field: 'to' | 'from', set: (l: DmRequest[]) => void) =>
      onSnapshot(query(collection(firestore, 'dmRequests'), where(field, '==', user.uid)), async (snap) => {
        const list = await Promise.all(snap.docs.map(async (item) => {
          const data = item.data()
          const otherId = String(field === 'to' ? data.from : data.to)
          const u = await getDoc(doc(firestore, 'users', otherId)).catch(() => null)
          const ud = u?.data() || {}
          const at = data.lastMessageAt?.toDate?.()
          return {
            id: item.id,
            from: String(data.from || ''),
            to: String(data.to || ''),
            otherId,
            status: data.status === 'accepted' ? 'accepted' : data.status === 'declined' ? 'declined' : 'pending',
            lastMessage: String(data.lastMessage || ''),
            hiddenFor: Array.isArray(data.hiddenFor) ? data.hiddenFor.map(String) : [],
            sortAt: at?.getTime?.() ?? 0,
            time: at ? formatTime(at) : '',
            name: String(ud.name || 'User'),
            handle: `@${String(ud.username || '').replace(/^@/, '')}`,
            avatar: String(ud.avatar || fallbackAvatarSvg(ud.name || 'User')),
          } as DmRequest
        }))
        set(list)
      }, () => undefined)
    const u1 = watch('to', setDmIn)
    const u2 = watch('from', setDmOut)
    return () => { u1(); u2() }
  }, [user])
  const dmPendingIn = useMemo(
    () => dmIn.filter((r) => r.status === 'pending' && !bannedUids.has(r.otherId)).sort((a, b) => b.sortAt - a.sortAt),
    [dmIn, bannedUids],
  )
  // Accepted request chats + chats where I sent a request that is still waiting. Friends' chats come from the friends list instead.
  const dmChatEntries = useMemo<Chat[]>(() => {
    const friendIds = new Set(allFriends.map((f) => f.id))
    const byOther = new Map<string, Chat>()
    for (const r of [...dmIn, ...dmOut]) {
      if (bannedUids.has(r.otherId) || friendIds.has(r.otherId) || r.status === 'declined') continue
      if (hiddenDmIds.has(r.otherId) || (user?.uid && r.hiddenFor?.includes(user.uid))) continue // removed from my list
      const incoming = r.to === user?.uid
      if (incoming && r.status === 'pending') continue // those live in the Requests folder
      const entry: Chat = {
        id: r.otherId, name: r.name, handle: r.handle, avatar: r.avatar,
        preview: r.lastMessage || 'Tap to chat', time: r.time, kind: 'direct', sortAt: r.sortAt,
        dm: r.status === 'accepted' ? 'accepted' : 'request-sent',
      }
      const cur = byOther.get(r.otherId)
      if (!cur || (cur.dm !== 'accepted' && entry.dm === 'accepted') || (cur.dm === entry.dm && (entry.sortAt || 0) > (cur.sortAt || 0))) byOther.set(r.otherId, entry)
    }
    return [...byOther.values()]
  }, [dmIn, dmOut, allFriends, bannedUids, hiddenDmIds, user?.uid])
  // Sorted Chats with Pinned on Top (memoised: before, every render of the page re-sorted + re-filtered all chats).
  // Hooks live here, BEFORE the early returns for login / ban screens, so the hook order never changes.
  const sortedChats = useMemo(() => [...chats, ...dmChatEntries.filter((d) => !chats.some((c) => c.kind !== 'group' && c.id === d.id))].filter((c) => c.kind === 'group' || !(c.hidden || hiddenDmIds.has(c.id))).sort((a, b) => {
    const aPin = pinnedChatIds.has(a.id) ? 1 : 0
    const bPin = pinnedChatIds.has(b.id) ? 1 : 0
    if (aPin !== bPin) return bPin - aPin
    return (b.sortAt || 0) - (a.sortAt || 0)
  }), [chats, dmChatEntries, hiddenDmIds, pinnedChatIds])
  // Typing in the search box stays instant; the list filters one beat later without blocking the keyboard.
  const deferredSearch = useDeferredValue(search)
  const filteredChats = useMemo(() => {
    const q = deferredSearch.toLowerCase()
    return q ? sortedChats.filter((c) => `${c.name} ${c.handle}`.toLowerCase().includes(q)) : sortedChats
  }, [sortedChats, deferredSearch])
  // Pending request whose messages are open on screen must not be marked "read" (the sender would see "Seen").
  const dmPendingFromRef = useRef<Set<string>>(new Set())
  dmPendingFromRef.current = new Set(dmPendingIn.map((r) => r.otherId))

  // 4. Friends & Direct Chats
  useEffect(() => {
    if (!user) return
    return onSnapshot(query(collection(firestore, 'friends', user.uid, 'list'), orderBy('createdAt', 'desc')), async (snap) => {
      // A newer lastMessageAt on a friend doc = a new message in that chat (see push notifications below).
      snap.docChanges().forEach((ch) => {
        const d = ch.doc.data()
        if (d.hidden || d.status === 'removed') return
        pushRef.current.chatActivity('direct', ch.doc.id, d.lastMessageAt?.toMillis?.() ?? 0, ch.doc.metadata.hasPendingWrites)
      })
      const active = snap.docs.filter((d) => d.data().status !== 'removed')
      const items = await Promise.all(active.map(async (item) => {
        // Profile lookups are cached (5 min): a new message re-fires this listener, and re-fetching every friend each time made the list slow.
        const cached = friendProfileCache.get(item.id)
        let d: Record<string, any> | undefined = cached && Date.now() - cached.at < 300_000 ? cached.data : undefined
        if (!d) {
          try {
            const f = await getDoc(doc(firestore, 'users', item.id))
            const fd = f.exists() ? f.data() : undefined
            if (fd) { d = fd; friendProfileCache.set(item.id, { at: Date.now(), data: fd }); rememberLiveAvatar(item.id, fd.avatar, false, (fd as any).equipped) }
            else if (cached) d = cached.data
          } catch { d = cached?.data }
        }
        if (!d) return null
        const cData = item.data()
        return {
          id: item.id,
          name: String(d.name || 'Friend'),
          handle: `@${String(d.username || '').replace(/^@/, '')}`,
          avatar: String(d.avatar || fallbackAvatarSvg(d.name || 'Friend')),
          preview: String(cData.lastMessage || 'Tap to chat'),
          time: cData.lastMessageAt ? formatTime(cData.lastMessageAt.toDate?.()) : '',
          kind: 'direct' as const,
          hidden: Boolean(cData.hidden),
          sortAt: cData.lastMessageAt ? cData.lastMessageAt.toDate?.()?.getTime?.() ?? 0 : 0,
        }
      }))
      const valid = items.filter(Boolean) as Chat[]
      setAllFriends(valid)
      setChats((curr) => {
        const groups = curr.filter((c) => c.kind === 'group')
        return [...groups, ...valid.filter((c) => !c.hidden)]
      })
      try { localStorage.setItem(`animora:chatlist:${user.uid}`, JSON.stringify(valid)) } catch { /* storage full / blocked */ }
    })
  }, [user])

  // Instant first paint after reopening the app: show the last known chat list right away, live data replaces it a moment later.
  useEffect(() => {
    if (!user) return
    try {
      const raw = localStorage.getItem(`animora:chatlist:${user.uid}`)
      if (!raw) return
      const cachedList = JSON.parse(raw) as Chat[]
      if (!Array.isArray(cachedList) || !cachedList.length) return
      setAllFriends((cur) => (cur.length ? cur : cachedList))
      setChats((cur) => (cur.some((c) => c.kind !== 'group') ? cur : [...cur, ...cachedList.filter((c) => !c.hidden)]))
    } catch { /* ignore a corrupt cache */ }
  }, [user?.uid])

  // 4b. Seen status of the last message in each direct chat (one tiny listener per chat, newest message only)
  const friendIdsKey = Array.from(new Set([...allFriends.map((f) => f.id), ...dmChatEntries.map((c) => c.id)])).sort().join(',')
  useEffect(() => {
    if (!user || !friendIdsKey) return
    const unsubs = friendIdsKey.split(',').flatMap((fid) => {
      const chatId = [user.uid, fid].sort().join('_')
      const seenListener = onSnapshot(
        query(collection(firestore, 'chats', chatId, 'messages'), orderBy('createdAt', 'desc'), limit(1)),
        (snap) => {
          const d = snap.docs[0]?.data()
          setLastMsgMeta((prev) => {
            const next = d ? { own: d.userId === user.uid, read: d.read === true } : undefined
            const cur = prev[fid]
            if (!next) { if (!cur) return prev; const { [fid]: _drop, ...rest } = prev; return rest }
            if (cur && cur.own === next.own && cur.read === next.read) return prev
            return { ...prev, [fid]: next }
          })
        },
        () => undefined,
      )
      // Unread count: their messages that I have not opened yet (the open chat marks them read).
      const unreadListener = onSnapshot(
        query(collection(firestore, 'chats', chatId, 'messages'), where('userId', '==', fid), where('read', '==', false), limit(99)),
        (snap) => {
          // Deleted-for-me messages should not count.
          const n = snap.docs.filter((m) => !(m.data().deletedFor || []).includes(user.uid) && !m.data().deleted).length
          setUnreadMap((prev) => {
            if ((prev[fid] || 0) === n) return prev
            if (n === 0) { const { [fid]: _drop, ...rest } = prev; return rest }
            return { ...prev, [fid]: n }
          })
        },
        () => undefined,
      )
      return [seenListener, unreadListener]
    })
    return () => unsubs.forEach((u) => u())
  }, [user?.uid, friendIdsKey])

  // 5. Group Chats
  useEffect(() => {
    if (!user) return
    return onSnapshot(query(collection(firestore, 'groupChats'), where('members', 'array-contains', user.uid)), (snap) => {
      snap.docChanges().forEach((ch) => {
        pushRef.current.chatActivity('group', ch.doc.id, ch.doc.data().lastMessageAt?.toMillis?.() ?? 0, ch.doc.metadata.hasPendingWrites)
      })
      const list = snap.docs.map((d) => {
        const data = d.data()
        return {
          id: d.id,
          name: String(data.name || 'Group'),
          handle: `${(data.members || []).length} members`,
          avatar: String(data.avatar || fallbackAvatarSvg(data.name || 'Group')),
          preview: String(data.lastMessage || 'Group created'),
          time: data.lastMessageAt ? formatTime(data.lastMessageAt.toDate?.()) : '',
          kind: 'group' as const,
          members: data.members || [],
          admins: data.admins || [],
          ownerId: data.ownerId,
          privacy: data.privacy === 'public' ? 'public' : 'private',
          joinMode: data.joinMode === 'auto' ? 'auto' : 'manual',
          sortAt: data.lastMessageAt ? data.lastMessageAt.toDate?.()?.getTime?.() ?? 0 : 0,
        } as Chat
      })
      setChats((curr) => {
        const directs = curr.filter((c) => c.kind !== 'group')
        return [...list, ...directs]
      })
    })
  }, [user])

  // 5b. Join requests waiting on a PUBLIC guild I admin - only watched while its Guild Channel screen is open.
  useEffect(() => {
    setGroupJoinRequests([])
    if (!user || !showGroupInfo || !activeChat || activeChat.kind !== 'group' || activeChat.privacy !== 'public' || !(activeChat.admins || []).includes(user.uid)) return
    return onSnapshot(
      query(collection(firestore, 'groupJoinRequests'), where('groupId', '==', activeChat.id), where('status', '==', 'pending')),
      (snap) => {
        setGroupJoinRequests(snap.docs.map((d) => {
          const x = d.data()
          return { id: d.id, uid: String(x.uid || ''), groupId: String(x.groupId || ''), groupName: String(x.groupName || ''), name: String(x.name || 'Someone'), username: String(x.username || ''), avatar: String(x.avatar || '') }
        }).filter((r) => r.uid))
      },
      () => undefined,
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.uid, showGroupInfo, activeChat?.id, activeChat?.privacy, (activeChat?.admins || []).join(',')])

  // 6. Presence Heartbeat
  // "Hide online status" (Edit Profile -> Privacy): no heartbeat is written, so friends see no green dot / last seen.
  const hideSeenRef = useRef(false)
  hideSeenRef.current = profile?.hideLastSeen === true
  useEffect(() => {
    if (!user) return
    // Every heartbeat is 1 Firestore write PLUS a read for every friend who is watching it, so the old
    // 15-second beat used up the free daily quota fast (-> "resource-exhausted" on coins/gifts/etc.).
    // Now: one beat every 45 s, and none while the app is in the background (a beat is sent again as soon as it comes back).
    const writePresence = () => {
      if (typeof document !== 'undefined' && document.hidden) return
      if (hideSeenRef.current) return
      void setDoc(doc(firestore, 'presence', user.uid), { lastSeen: serverTimestamp() }, { merge: true }).catch(() => undefined)
    }
    writePresence()
    const interval = window.setInterval(writePresence, PRESENCE_BEAT_MS)
    document.addEventListener('visibilitychange', writePresence)
    window.addEventListener('focus', writePresence)
    window.addEventListener('online', writePresence)
    window.addEventListener('pageshow', writePresence)
    return () => {
      window.clearInterval(interval)
      document.removeEventListener('visibilitychange', writePresence)
      window.removeEventListener('focus', writePresence)
      window.removeEventListener('online', writePresence)
      window.removeEventListener('pageshow', writePresence)
    }
  }, [user])

  useEffect(() => {
    // Was every 10 s: re-rendered the whole app 6x a minute. 30 s is enough for online dots / ban expiry,
    // and nothing runs while the app is in the background.
    const tick = () => { if (!document.hidden) setNowTick(Date.now()) }
    const interval = window.setInterval(tick, 30000)
    document.addEventListener('visibilitychange', tick)
    return () => { window.clearInterval(interval); document.removeEventListener('visibilitychange', tick) }
  }, [])

  // App-wide settings (currently the owner's Boost lock). Everyone signed in listens live,
  // so the Admin Console's toggle applies instantly on the website and in the app.
  useEffect(() => {
    if (!user) return
    return onSnapshot(doc(firestore, 'settings', 'app'), (snap) => {
      setBoostEnabledFlag(snap.data()?.boostEnabled === true)
    }, () => undefined)
  }, [user])

  // 7. Presence Tracking (re-subscribes only when the SET of friends changes - not on every new message)
  // Guild members are watched too, so a guild can show "Online" when ANY member is online. Capped (active guild first) because
  // every watched person costs one presence read; guilds can have 100+ members.
  const GROUP_PRESENCE_CAP = 60
  const groupPresenceIds: string[] = (() => {
    const out = new Set<string>()
    const add = (list?: string[]) => { for (const m of list || []) { if (out.size >= GROUP_PRESENCE_CAP) return; if (m && m !== user?.uid) out.add(m) } }
    if (activeChat?.kind === 'group') add(activeChat.members)
    for (const c of chats) if (c.kind === 'group') add(c.members)
    return Array.from(out)
  })()
  const presenceIdsKey = Array.from(new Set([
    ...chats.filter((c) => c.kind !== 'group').map((c) => c.id),
    ...dmChatEntries.map((c) => c.id),
    ...groupPresenceIds,
  ])).sort().join(',')
  useEffect(() => {
    if (!user || !presenceIdsKey) return
    const unsubs = presenceIdsKey.split(',').map((id) => {
      return onSnapshot(doc(firestore, 'presence', id), (snap) => {
        if (snap.exists()) {
          // lastSeen is stamped by the SERVER clock; shift it onto this device's clock so a wrong phone clock can't make everyone look offline/online.
          const rawSeen = snap.data().lastSeen?.toDate?.()?.getTime()
          const lastSeen = rawSeen ? rawSeen - getServerClockSkewMs() : 0
          if (lastSeen) setPresenceMap((prev) => (prev[id] === lastSeen ? prev : { ...prev, [id]: lastSeen }))
        }
      }, () => undefined)
    })
    return () => { unsubs.forEach((u) => u()) }
  }, [presenceIdsKey, user])

  // 8. Following & Sent Requests
  useEffect(() => {
    if (!user) return
    const u1 = onSnapshot(query(collection(firestore, 'following', user.uid, 'list'), where('status', '==', 'active')), (snap) => {
      setFollowing(new Set(snap.docs.map((d) => d.id)))
    })
    const u2 = onSnapshot(query(collection(firestore, 'friendRequests'), where('from', '==', user.uid), where('status', '==', 'pending')), (snap) => {
      setSentRequests(new Set(snap.docs.map((d) => String(d.data().to))))
    })
    return () => { u1(); u2() }
  }, [user])

  // 9. Posts Listener
  useEffect(() => {
    if (!user) return
    return onSnapshot(query(collection(firestore, 'posts'), orderBy('createdAt', 'desc'), limit(50)), (snap) => {
      const allPosts = snap.docs.map((d) => mapPostDoc(d.id, d.data()))
      const visible = allPosts
        .filter(p => p.privacy === 'public' || p.authorId === user.uid || following.has(p.authorId))
      // Ranking (relevance/engagement scoring + natural ad placement) now
      // happens in ReelFeedTab, since Reels need their own
      // ad cadence separate from the mixed Post Feed.
      const deep = deepPostRef.current
      // transition = low priority: a tap / scroll is always handled first, the feed refresh renders right after
      startTransition(() => { setPosts(deep && !visible.some((p) => p.id === deep.id) ? [deep, ...visible] : visible) })
      writeHomeCache(user.uid, { posts: visible })
    })
  }, [user, following])

  // 10. Stories Listener
  useEffect(() => {
    if (!user) return
    const oneDayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000)
    return onSnapshot(query(collection(firestore, 'stories'), where('createdAt', '>', oneDayAgo), orderBy('createdAt', 'desc')), (snap) => {
      const allStories = snap.docs.map((d) => {
        const data = d.data()
        return {
          id: d.id,
          authorId: data.authorId,
          authorName: data.authorName,
          authorAvatar: data.authorAvatar,
          mediaUrl: resolveMediaUrl(data.mediaUrl),
          mediaType: data.mediaType || 'image',
          time: formatTime(data.createdAt?.toDate?.()),
          likes: data.likes || [],
          privacy: data.privacy || 'public',
          viewers: data.viewers || [],
          caption: data.caption || '',
          audioUrl: resolveMediaUrl(data.audioUrl),
          audioTitle: data.audioTitle,
          commentCount: Number(data.commentCount || 0),
          sourcePostId: data.sourcePostId || undefined,
          soundOwnerName: typeof data.soundOwnerName === 'string' && data.soundOwnerName ? data.soundOwnerName : undefined,
          remixOf: typeof data.remixOf === 'string' && data.remixOf ? data.remixOf : undefined,
          audioShared: data.audioShared === true ? true : undefined,
          websiteUrl: typeof data.websiteUrl === 'string' && data.websiteUrl ? data.websiteUrl : undefined,
          ctaText: typeof data.ctaText === 'string' && data.ctaText.trim() ? data.ctaText.trim() : undefined,
          bannerImageUrl: typeof data.bannerImageUrl === 'string' && data.bannerImageUrl ? resolveMediaUrl(data.bannerImageUrl) : undefined,
          bannerColor: typeof data.bannerColor === 'string' && data.bannerColor ? data.bannerColor : undefined,
          boostGoal: data.boostGoal === 'website' ? 'website' : 'profile',
          promoted: Boolean(data.promotedUntil && data.promotedUntil.toMillis?.() > Date.now()) || Boolean(typeof data.promotedUntil === 'number' && data.promotedUntil > Date.now()),
          promotedUntil: data.promotedUntil?.toMillis?.() || (typeof data.promotedUntil === 'number' ? data.promotedUntil : undefined),
        } as Story
      })
      const visible = allStories.filter(s => s.privacy === 'public' || s.authorId === user.uid || following.has(s.authorId))
      setStories(visible)
      writeHomeCache(user.uid, { stories: visible })
    })
  }, [user, following])

  // 10a. Notes: a short friends-only status (60 chars, 24 h) shown at the top of Comms. One doc per person at notes/{uid};
  // the server only ever returns a note to its author and their Allies, and the list below is limited to Allies as well.
  const [rawNotes, setRawNotes] = useState<Record<string, { text: string; at: number }>>({})
  useEffect(() => {
    if (!user) return
    const oneDayAgo = new Date(Date.now() - NOTE_TTL_MS)
    return onSnapshot(query(collection(firestore, 'notes'), where('createdAt', '>', oneDayAgo), orderBy('createdAt', 'desc')), (snap) => {
      const next: Record<string, { text: string; at: number }> = {}
      snap.docs.forEach((d) => {
        const x: any = d.data()
        const text = typeof x.text === 'string' ? x.text.trim().slice(0, NOTE_MAX_LEN) : ''
        if (text) next[d.id] = { text, at: x.createdAt?.toMillis?.() || Date.now() }
      })
      setRawNotes(next)
    }, () => { /* notes are optional: a failing listener must never break Comms */ })
  }, [user])
  const myNote = useMemo(() => {
    const n = user ? rawNotes[user.uid] : null
    return n && n.at > nowTick - NOTE_TTL_MS ? n : null
  }, [rawNotes, user, nowTick])
  const friendNotes = useMemo(() => allFriends
    .filter((f) => rawNotes[f.id] && rawNotes[f.id].at > nowTick - NOTE_TTL_MS)
    .map((f) => ({ id: f.id, name: f.name, avatar: f.avatar, text: rawNotes[f.id].text, at: rawNotes[f.id].at }))
    .sort((x, y) => y.at - x.at), [rawNotes, allFriends, nowTick])
  const saveMyNote = async (raw: string): Promise<boolean> => {
    const text = raw.replace(/\s+/g, ' ').trim().slice(0, NOTE_MAX_LEN)
    if (!user || !text) return false
    try {
      await setDoc(doc(firestore, 'notes', user.uid), { authorId: user.uid, text, createdAt: serverTimestamp() })
      setRawNotes((p) => ({ ...p, [user.uid]: { text, at: Date.now() } }))
      return true
    } catch (err) {
      notify(friendlyError(err, 'Could not share your note. Please try again.'))
      return false
    }
  }
  const deleteMyNote = async () => {
    if (!user) return
    try {
      await deleteDoc(doc(firestore, 'notes', user.uid))
      setRawNotes((p) => { const n = { ...p }; delete n[user.uid]; return n })
    } catch (err) {
      notify(friendlyError(err, 'Could not delete your note. Please try again.'))
    }
  }

  // 10b. Keep the open Story Viewer's data live. storyGroups/viewingStory used to be a
  // one-time snapshot taken at the moment the viewer opened, so liking a story (or any other
  // update) never showed up on screen until you closed and reopened it - this re-derives both
  // from the live `stories` list whenever it changes.
  useEffect(() => {
    if (!viewingStory) return
    const freshGroup = stories.filter((item) => item.authorId === viewingStory.authorId).reverse() // oldest -> newest
    if (!freshGroup.length) { setViewingStory(null); return }
    // Keep the viewer on the SAME story while the list refreshes (a new story arriving must not shift the index).
    const curId = storyGroupsRef.current[storyIndexRef.current]?.id ?? viewingStory.id
    setStoryGroups(freshGroup)
    const ni = freshGroup.findIndex((item) => item.id === curId)
    if (ni >= 0) { setStoryIndex(ni); setViewingStory(freshGroup[ni]) }
    else setStoryIndex((i) => Math.min(i, freshGroup.length - 1))
  }, [stories, viewingStory?.authorId])

  // 11. Comments Listener
  useEffect(() => {
    if (!commentsFor) { setComments([]); return }
    return onSnapshot(query(collection(firestore, 'posts', commentsFor.id, 'comments'), orderBy('createdAt', 'asc')), (snap) => {
      setComments(snap.docs.map((d) => {
        const data = d.data()
        return {
          id: d.id,
          authorId: data.authorId,
          authorName: data.authorName,
          authorAvatar: data.authorAvatar,
          text: data.text,
          pinned: Boolean(data.pinned),
          time: formatTime(data.createdAt?.toDate?.()),
          likes: Array.isArray(data.likes) ? data.likes : [],
          edited: Boolean(data.edited),
          threadId: typeof data.threadId === 'string' ? data.threadId : undefined,
          replyToName: typeof data.replyToName === 'string' ? data.replyToName : undefined,
          giftLabel: typeof data.giftLabel === 'string' ? data.giftLabel : undefined,
          giftAmount: typeof data.giftAmount === 'number' ? data.giftAmount : undefined,
        }
      }))
    })
  }, [commentsFor])

  // 11b. Story Comments Listener
  useEffect(() => {
    if (!commentsForStory) { setStoryComments([]); return }
    return onSnapshot(query(collection(firestore, 'stories', commentsForStory.id, 'comments'), orderBy('createdAt', 'asc')), (snap) => {
      setStoryComments(snap.docs.map((d) => {
        const data = d.data()
        return {
          id: d.id,
          authorId: data.authorId,
          authorName: data.authorName,
          authorAvatar: data.authorAvatar,
          text: data.text,
          pinned: Boolean(data.pinned),
          time: formatTime(data.createdAt?.toDate?.()),
          likes: Array.isArray(data.likes) ? data.likes : [],
          edited: Boolean(data.edited),
          threadId: typeof data.threadId === 'string' ? data.threadId : undefined,
          replyToName: typeof data.replyToName === 'string' ? data.replyToName : undefined
        }
      }))
    })
  }, [commentsForStory])

  // Never keep one account's cached chats around for the next person who signs in on this phone.
  useEffect(() => { if (!user) chatMsgCache.clear() }, [user])

  // Weak phones (<=3 GB RAM or <=4 CPU cores): a class on <html> lets the CSS drop the costliest effects inside the chat
  // (blur behind every bar / sheet, bubble pop-in) - the chat looks the same, it just stops stuttering.
  useEffect(() => {
    try {
      const n = navigator as any
      const low = Boolean((n.deviceMemory && n.deviceMemory <= 3) || (n.hardwareConcurrency && n.hardwareConcurrency <= 4))
      document.documentElement.classList.toggle('lowend', low)
    } catch { /* ignore */ }
  }, [])

  // Warm the screens people open most while the phone is idle, so tapping them feels instant.
  useEffect(() => {
    if (!user || typeof window === 'undefined') return
    const warm = () => {
      void import('@/components/wallet').catch(() => undefined)
      void import('@/components/games').catch(() => undefined)
      void import('@/components/avatar-picker').catch(() => undefined)
    }
    const ric = (window as any).requestIdleCallback as undefined | ((cb: () => void, o?: { timeout: number }) => number)
    if (ric) { const id = ric(warm, { timeout: 6000 }); return () => (window as any).cancelIdleCallback?.(id) }
    const t = setTimeout(warm, 4000)
    return () => clearTimeout(t)
  }, [user?.uid])

  // 12. Active Chat Messages Listener
  // PERF (weak phones): (1) only the NEWEST `msgWindow` messages are downloaded and drawn, older ones load on demand;
  // (2) a message whose data did not change keeps the SAME object between polls, so React.memo(ChatMessageRow) really
  // skips it (before, every poll rebuilt every message -> the whole chat re-rendered -> the phone froze);
  // (3) "mark as read" is a few batched writes and is never repeated for a message that is already being marked.
  useEffect(() => { setMsgWindow(MSG_WINDOW_DEFAULT); setHasOlderMsgs(false) }, [activeChat?.id, activeChat?.kind])
  useEffect(() => {
    if (!user || !activeChat) { setMessages([]); setMessagesFor(''); return }
    const chatId = activeChat.kind === 'group' ? activeChat.id : [user.uid, activeChat.id].sort().join('_')
    // New conversation: drop the previous chat's messages immediately (bug: they stayed visible, in the wrong DM, while loading).
    const cachedMsgs = chatMsgCache.get(chatId)
    if (cachedMsgs && cachedMsgs.length) { setMessages(cachedMsgs); setMessagesFor(chatId) } else { setMessages([]); setMessagesFor('') }
    let live = true
    const objs = new Map<string, { sig: string; msg: Message }>()
    const marking = new Set<string>()
    const windowSize = msgWindow
    // GROUP "seen by": when I have this guild open (app visible), I add my uid to `seenBy` of the NEWEST message someone else sent.
    // Only that one message is written (not every message) so a 100-member guild does not cost 100 writes per message.
    // Members who turned on "Hide online status" do not leave a seen mark (same privacy switch as the presence beat).
    let latestDocs: any[] = []
    const markGroupSeen = () => {
      if (!live || activeChat.kind !== 'group' || hideSeenRef.current) return
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return
      const target = [...latestDocs].reverse().find((item) => { const x = item.data(); return x.userId !== user.uid && !x.deleted })
      if (!target) return
      const key = `seen:${target.id}`
      const sb = target.data().seenBy
      if (marking.has(key) || (Array.isArray(sb) && sb.includes(user.uid))) return
      marking.add(key)
      void updateDoc(target.ref, { seenBy: arrayUnion(user.uid) }).catch(() => { marking.delete(key) })
    }
    const onVisible = () => markGroupSeen()
    document.addEventListener('visibilitychange', onVisible)
    window.addEventListener('focus', onVisible)
    const stop = onSnapshot(query(collection(firestore, 'chats', chatId, 'messages'), orderBy('createdAt', 'desc'), limit(windowSize)), (snap) => {
      if (!live) return // a late poll result from a chat we already left
      const docsAsc = [...snap.docs].reverse() // newest-first query -> oldest-first list
      latestDocs = docsAsc
      // Match each incoming own-message doc against a still-unmatched optimistic send
      // (same text, sent within the last 15s) so its real id is flagged as "don't
      // replay the pop-in animation" - the optimistic bubble already animated it in once.
      const pending = pendingOwnSendsRef.current
      if (pending.length) {
        const now = Date.now()
        for (const d of docsAsc) {
          const data = d.data()
          if (data.userId !== user.uid) continue
          const match = pending.find((p) => !p.matched && p.text === String(data.text || '') && now - p.at < 15000)
          if (match) {
            match.matched = true
            noAnimateMsgIdsRef.current.add(d.id)
          }
        }
        pendingOwnSendsRef.current = pending.filter((p) => !p.matched && now - p.at < 15000)
      }
      setHasOlderMsgs(snap.docs.length >= windowSize)
      setMessagesFor(chatId)
      const nextObjs = new Map<string, { sig: string; msg: Message }>()
      const mappedMsgs: Message[] = []
      for (const d of docsAsc) {
        const data = d.data()
        if ((data.deletedFor || []).includes(user.uid)) continue
        const sig = (d as any)._json ?? JSON.stringify(data)
        const hit = objs.get(d.id)
        if (hit && hit.sig === sig) { nextObjs.set(d.id, hit); mappedMsgs.push(hit.msg); continue }
        const msg: Message = {
          id: d.id,
          text: data.deleted ? '' : String(data.text || ''),
          // a call log sits on the CALLER's side (right = you called, left = they called), whichever phone wrote it
          own: data.call && typeof data.call.callerId === 'string' ? data.call.callerId === user.uid : data.userId === user.uid,
          read: Boolean(data.read),
          readAt: typeof data.readAt === 'number' ? data.readAt : undefined,
          senderId: String(data.userId || ''),
          senderName: data.senderName,
          senderAvatar: data.senderAvatar,
          sharedImageUrl: data.sharedImageUrl,
          sharedThumbnailUrl: data.sharedThumbnailUrl ? resolveMediaUrl(data.sharedThumbnailUrl) : undefined,
          sharedPostId: data.sharedPostId,
          sharedMediaType: data.sharedMediaType === 'video' ? 'video' : 'image',
          sharedAuthorId: data.sharedAuthorId,
          sharedAuthorName: data.sharedAuthorName,
          sharedAuthorUsername: data.sharedAuthorUsername,
          sharedAuthorAvatar: data.sharedAuthorAvatar,
          audioUrl: resolveMediaUrl(data.audioUrl),
          audioDuration: data.audioDuration,
          time: formatTime(data.createdAt?.toDate?.()),
          at: data.createdAt?.toMillis?.() ?? undefined,
          reactions: data.reactions || {},
          blast: data.blast && typeof data.blast.emoji === 'string' && typeof data.blast.at === 'number' ? { emoji: data.blast.emoji, by: String(data.blast.by || ''), at: data.blast.at } : undefined,
          replyToText: data.replyToText,
          replyToSender: data.replyToSender,
          replyToImage: typeof data.replyToImage === 'string' ? resolveMediaUrl(data.replyToImage) : undefined,
          replyToId: typeof data.replyToId === 'string' ? data.replyToId : undefined,
          deleted: Boolean(data.deleted),
          forwarded: Boolean(data.forwarded),
          edited: Boolean(data.edited),
          editCount: Number(data.editCount) || (data.edited ? 1 : 0),
          viewOnce: Boolean(data.viewOnce),
          sticker: Boolean(data.sticker),
          viewedBy: data.viewedBy || [],
          seenBy: Array.isArray(data.seenBy) ? data.seenBy.filter((x: unknown) => typeof x === 'string') : [],
          ludo: data.ludo && typeof data.ludo.code === 'string' ? { roomId: String(data.ludo.roomId || ''), code: String(data.ludo.code), stake: Number(data.ludo.stake || 0), size: Number(data.ludo.size || 2) } : undefined,
          chess: data.chess && typeof data.chess.code === 'string' ? { roomId: String(data.chess.roomId || ''), code: String(data.chess.code), stake: Number(data.chess.stake || 0), tc: String(data.chess.tc || '5+0') } : undefined,
          carrom: data.carrom && typeof data.carrom.code === 'string' ? { roomId: String(data.carrom.roomId || ''), code: String(data.carrom.code), stake: Number(data.carrom.stake || 0), size: Number(data.carrom.size || 2) } : undefined,
          poll: data.poll && typeof data.poll.question === 'string' && Array.isArray(data.poll.options) ? { question: String(data.poll.question).slice(0, 200), options: data.poll.options.map((o: unknown) => String(o).slice(0, 80)).slice(0, 6) } : undefined,
          call: data.call && typeof data.call.callerId === 'string' && ['completed', 'missed', 'declined', 'busy'].includes(data.call.status)
            ? { id: String(data.call.id || ''), type: data.call.type === 'video' ? 'video' : 'voice', status: data.call.status, secs: Math.max(0, Number(data.call.secs) || 0), callerId: String(data.call.callerId) }
            : undefined,
        }
        nextObjs.set(d.id, { sig, msg })
        mappedMsgs.push(msg)
      }
      objs.clear(); nextObjs.forEach((v, k) => objs.set(k, v))
      rememberChatMsgs(chatId, mappedMsgs)
      setMessages(mappedMsgs)
      // Mark what the other side sent as read - in small batches, once per message (not one request per message on every poll).
      const unread = docsAsc.filter((item) => { const x = item.data(); return x.userId !== user.uid && !x.read && !marking.has(item.id) })
      if (unread.length && !(activeChat.kind !== 'group' && dmPendingFromRef.current.has(activeChat.id))) {
        for (let i = 0; i < unread.length; i += 40) {
          const part = unread.slice(i, i + 40)
          part.forEach((item) => marking.add(item.id))
          const batch = writeBatch(firestore)
          part.forEach((item) => batch.update(item.ref, { read: true, readAt: Date.now() }))
          void batch.commit().catch(() => { part.forEach((item) => marking.delete(item.id)) })
        }
      }
      markGroupSeen()
    })
    return () => { live = false; stop(); document.removeEventListener('visibilitychange', onVisible); window.removeEventListener('focus', onVisible) }
    // Keyed on the conversation itself (not the whole chat object): renaming a group etc. must not blank the list.
  }, [activeChat?.id, activeChat?.kind, user, msgWindow])

  // 13. Active Chat Typing Listener
  useEffect(() => {
    if (!user || !activeChat) return
    const chatId = activeChat.kind === 'group' ? activeChat.id : [user.uid, activeChat.id].sort().join('_')
    const peerId = activeChat.id
    return onSnapshot(collection(firestore, 'typing', chatId, 'users'), (snap) => {
      const now = Date.now()
      snap.docs.forEach((item) => {
        if (item.id !== user.uid) {
          const d = item.data()
          const isTyping = Boolean(d.typing && d.updatedAt?.toDate?.() && now - d.updatedAt.toDate().getTime() < 8000)
          // PERF: only touch state when the value really changed (a new object every poll re-rendered this whole screen).
          setTypingMap((prev) => (prev[peerId] === isTyping ? prev : { ...prev, [peerId]: isTyping }))
        }
      })
    })
    // Keyed on the conversation (the chat OBJECT changes on every new message / list refresh and re-subscribed this each time).
  }, [activeChat?.id, activeChat?.kind, user?.uid])

  // 14. Group Members Loader
  // Was gated on `showGroupInfo` only - now also loads as soon as a group chat is opened
  // (not just when the Group Info screen is), so the "who reacted" list on a message can
  // resolve member names/avatars without waiting for the person to open Group Info first.
  useEffect(() => {
    if (!activeChat || activeChat.kind !== 'group') return
    let active = true
    setGroupMemberProfilesLoading(true)
    void (async () => {
      const members = Array.from(new Set(activeChat.members || []))
      const profiles = await Promise.all(members.map(async (uid) => {
        const snap = await getDoc(doc(firestore, 'users', uid))
        if (!snap.exists()) return null
        const d = snap.data()
        return {
          uid,
          name: String(d.name || 'Operative'),
          username: String(d.username || ''),
          usernameLower: String(d.usernameLower || '').toLowerCase(),
          email: '',
          avatar: String(d.avatar || fallbackAvatarSvg(d.name || 'Operative')),
          bio: '',
        } as Profile
      }))
      if (active) {
        setGroupMemberProfiles(profiles.filter(Boolean) as Profile[])
        setGroupMemberProfilesLoading(false)
      }
    })()
    return () => { active = false }
    // Keyed on id + member list: the chat object changes with every new message, which re-downloaded EVERY member profile each time.
  }, [activeChat?.id, activeChat?.kind, (activeChat?.members || []).join(',')])

  // Push Notification Helper
  const sendAppNotification = async (toUserId: string, type: 'like' | 'comment' | 'friend_request' | 'gift' | 'mention' | 'admin' | 'group_request', messageText: string, targetId?: string, extra?: { giftLabel?: string }) => {
    if (!user || toUserId === user.uid) return
    try {
      await addDoc(collection(firestore, 'notifications', toUserId, 'items'), {
        fromId: user.uid,
        // Moderation notices come from Animora itself - never expose the admin's own name/photo to the user.
        fromName: type === 'admin' ? 'Animora' : (profile?.name || 'Someone'),
        fromAvatar: type === 'admin' ? '/icon-192.png' : (profile?.avatar || ''),
        type,
        message: messageText,
        targetId: targetId || '',
        read: false,
        ...(extra?.giftLabel ? { giftLabel: extra.giftLabel } : {}),
        createdAt: serverTimestamp(),
      })
    } catch { /* ignore */ }
  }

  // @mentions: scans free-typed text for @handles and resolves each to a real uid via the
  // usernames/{lower} doc - the same lookup already used for username-uniqueness at signup,
  // so no separate mentions index is needed. Used by group chat messages and post/story
  // comments; callers are responsible for deciding who's actually eligible to be notified
  // (e.g. only group members). Self-mentions are dropped here since they'd never need a
  // notification either way.
  const MENTION_RE = /@([a-zA-Z0-9_.]{2,30})/g
  const resolveMentions = async (text: string): Promise<string[]> => {
    const handles = [...new Set((text.match(MENTION_RE) || []).map((m) => m.slice(1).toLowerCase()))].slice(0, 20)
    if (!handles.length || !user) return []
    const uids = await Promise.all(handles.map(async (h) => {
      try {
        const snap = await getDoc(doc(firestore, 'usernames', h))
        return snap.exists() ? String(snap.data()?.uid || '') : ''
      } catch { return '' }
    }))
    return [...new Set(uids.filter((uid) => uid && uid !== user.uid))]
  }

  // ==========================================================================
  // BROWSER NOTIFICATIONS (Animora logo + name, Reply on chats, Accept on friend requests)
  // These show while the site is open in a browser tab / installed app. The page itself does the
  // work when a button is tapped (public/sw.js just hands the tap over to it).
  // ==========================================================================
  const findChatForNotification = (id: string, kind?: string): Chat | undefined =>
    kind === 'group' ? chats.find((c) => c.kind === 'group' && c.id === id) : allFriends.find((c) => c.id === id)

  // Same writes as a normal typed message (see sendMessage), used for replies typed into a notification.
  const sendQuickReply = async (chat: Chat, rawText: string) => {
    const text = rawText.trim().slice(0, 2000)
    if (!user || !text) return
    if (chat.kind !== 'group' && bannedUids.has(chat.id)) { notify('This account is unavailable.'); return }
    const chatId = chat.kind === 'group' ? chat.id : [user.uid, chat.id].sort().join('_')
    try {
      const qrRef = doc(collection(firestore, 'chats', chatId, 'messages'))
      await sendWithRetry(() => setDoc(qrRef, {
        text,
        userId: user.uid,
        senderName: profile?.name || 'Friend',
        senderAvatar: profile?.avatar || '',
        read: false,
        createdAt: serverTimestamp(),
      }), async () => (await getDoc(qrRef)).exists())
      updateDoc(doc(firestore, 'users', user.uid), {
        messagesSent: increment(1),
        ...(chat.kind === 'group' ? { groupMessagesSent: increment(1) } : {}),
      }).catch(() => undefined)
      if (chat.kind === 'group') {
        await updateDoc(doc(firestore, 'groupChats', chat.id), { lastMessage: text, lastMessageAt: serverTimestamp() })
      } else {
        await Promise.all([
          setDoc(doc(firestore, 'friends', user.uid, 'list', chat.id), { lastMessage: text, lastMessageAt: serverTimestamp() }, { merge: true }),
          setDoc(doc(firestore, 'friends', chat.id, 'list', user.uid), { lastMessage: text, lastMessageAt: serverTimestamp() }, { merge: true }),
        ])
      }
      notify('Reply sent.')
    } catch { notify('Message send failed.') }
  }

  // ---- Connect / Accept: ONE server round trip each, UI answers instantly ---------------------------------
  // Before: Connect = delete (wait) -> create (wait) -> notification (3rd call); Accept = batch (wait) -> delete (wait),
  // and the button only changed after all of that. Now each is a single commit, the button flips at once (optimistic)
  // and rolls back only if the server refuses. If the other person already asked ME, Connect simply accepts - nobody
  // has to send a second request.
  const connectBusyRef = useRef<Set<string>>(new Set())
  const acceptFriendFast = async (fromId: string, reqId?: string, name?: string): Promise<boolean> => {
    if (!user || !fromId || connectBusyRef.current.has(fromId)) return false
    connectBusyRef.current.add(fromId)
    const rid = reqId || `${fromId}_${user.uid}`
    const before = rawRequests
    setRequests((c) => c.filter((r) => r.id !== rid)) // the row leaves the list right away
    try {
      const wb = writeBatch(firestore)
      wb.set(doc(firestore, 'friends', user.uid, 'list', fromId), { createdAt: serverTimestamp() })
      wb.set(doc(firestore, 'friends', fromId, 'list', user.uid), { createdAt: serverTimestamp() })
      wb.delete(doc(firestore, 'friendRequests', rid))
      // both sides had asked each other: drop my own pending request too, so nothing is left dangling
      if (sentRequests.has(fromId)) wb.delete(doc(firestore, 'friendRequests', `${user.uid}_${fromId}`))
      await wb.commit()
      setSentRequests((c) => { if (!c.has(fromId)) return c; const n = new Set(c); n.delete(fromId); return n })
      notify(name ? `You and ${name} are now connected.` : 'Connected.')
      return true
    } catch {
      setRequests(before)
      notify('Action failed.')
      return false
    } finally {
      connectBusyRef.current.delete(fromId)
    }
  }
  const connectWith = async (target: { uid: string; name?: string }, okMsg?: string): Promise<boolean> => {
    if (!user || !target.uid || target.uid === user.uid || connectBusyRef.current.has(target.uid)) return false
    const to = target.uid
    const incoming = rawRequests.find((r) => r.from === to && r.to === user.uid)
    if (incoming) return acceptFriendFast(to, incoming.id, target.name || incoming.fromName)
    connectBusyRef.current.add(to)
    setSentRequests((c) => new Set(c).add(to)) // "Pending" shows instantly
    const reqId = `${user.uid}_${to}`
    const send = async () => {
      const wb = writeBatch(firestore)
      wb.set(doc(firestore, 'friendRequests', reqId), { from: user.uid, to, status: 'pending', createdAt: serverTimestamp() })
      // the "wants to connect" alert rides in the same commit (it used to be a separate call afterwards)
      wb.set(doc(collection(firestore, 'notifications', to, 'items')), {
        fromId: user.uid, fromName: profile?.name || 'Someone', fromAvatar: profile?.avatar || '',
        type: 'friend_request', message: 'dispatched an alliance request.', targetId: '', read: false, createdAt: serverTimestamp(),
      })
      await wb.commit()
    }
    try {
      try { await send() } catch (e: any) {
        if (e?.code !== 'permission-denied') throw e
        // an old accepted / rejected doc with this id blocks a re-create: clear it once, then try again
        await deleteDoc(doc(firestore, 'friendRequests', reqId)).catch(() => undefined)
        await send()
      }
      notify(okMsg || `Request sent to ${target.name || 'them'}.`)
      return true
    } catch {
      setSentRequests((c) => { const n = new Set(c); n.delete(to); return n })
      notify('Request failed.')
      return false
    } finally {
      connectBusyRef.current.delete(to)
    }
  }
  // Both people asked each other at about the same moment -> connect them automatically.
  useEffect(() => {
    if (!user) return
    for (const r of rawRequests) {
      if (r.to === user.uid && sentRequests.has(r.from)) void acceptFriendFast(r.from, r.id, r.fromName)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawRequests, sentRequests, user])

  // Same as the Accept button inside the notification drawer (opened from a push, so the request may not be loaded yet).
  const acceptFriendFromNotification = async (fromId: string, fromName: string) => {
    if (!user) return
    const reqId = `${fromId}_${user.uid}`
    try {
      if (!rawRequests.some((r) => r.id === reqId)) {
        const reqSnap = await getDoc(doc(firestore, 'friendRequests', reqId))
        if (!reqSnap.exists() || reqSnap.data().status !== 'pending') { notify('This friend request is no longer available.'); return }
      }
      const ok = await acceptFriendFast(fromId, reqId, fromName)
      if (ok) {
        void showBrandedNotification({
          body: `You and ${fromName || 'your new friend'} are now friends`,
          tag: `alert_fr_${fromId}`,
          data: { kind: 'friend_request', fromId, fromName, fromAvatar: pfpFor(fromId) },
        })
      }
    } catch { notify('Action failed.') }
  }

  // A friend / group doc says "newer message" -> read that message and notify if someone else sent it.
  const notifyLatestChatMessage = async (kind: 'direct' | 'group', id: string) => {
    if (!user) return
    try {
      const chatDocId = kind === 'group' ? id : [user.uid, id].sort().join('_')
      const snap = await getDocs(query(collection(firestore, 'chats', chatDocId, 'messages'), orderBy('createdAt', 'desc'), limit(1)))
      const m = snap.docs[0]?.data()
      const lastMsgId = snap.docs[0]?.id || ''
      if (!m || m.userId === user.uid || m.deleted) return
      if ((m.deletedFor || []).includes(user.uid) || bannedUids.has(String(m.userId || ''))) return
      // Already looking at this very chat -> the message is on screen, no pop-up needed.
      const open = activeChatRef.current
      if (document.visibilityState === 'visible' && open && open.id === id && (open.kind === 'group') === (kind === 'group')) return

      const sender = String(m.senderName || 'Friend')
      const preview = m.sticker ? 'Sticker'
        : m.viewOnce ? 'View once photo'
        : m.audioUrl ? 'Voice note'
        : String(m.text || '').replace(/\s+/g, ' ').trim().slice(0, 140) || (m.sharedImageUrl ? 'Photo' : m.sharedPostId ? 'Shared a post' : 'New message')
      const groupName = kind === 'group' ? (chats.find((c) => c.kind === 'group' && c.id === id)?.name || 'Group') : ''
      void showBrandedNotification({
        body: kind === 'group' ? `${sender} in ${groupName}: ${preview}` : `${sender}: ${preview}`,
        tag: `chat_${kind}_${id}`, // newer messages from the same chat replace the older pop-up instead of stacking
        renotify: true,
        data: { kind: 'message', chatId: id, chatKind: kind, fromName: sender, fromAvatar: pfpFor(String(m.userId || '')), msgId: lastMsgId },
        actions: [{ action: 'reply', type: 'text', title: 'Reply', placeholder: 'Type a reply…' }, { action: 'like', title: 'Like' }],
      })
    } catch { /* a missed pop-up is never worth an error */ }
  }

  // Country flag: once per session look up the country of the current IP and save it on the profile,
  // so it also follows the person when they travel / switch network.
  const geoCheckedFor = useRef<string | null>(null)
  useEffect(() => {
    if (!user || !profile || geoCheckedFor.current === user.uid) return
    geoCheckedFor.current = user.uid
    void (async () => {
      try {
        const res = await fetch('/api/geo', { cache: 'no-store' })
        if (!res.ok) return
        const country = normalizeCountry((await res.json())?.country)
        if (!country || country === profile.country) return
        await updateDoc(doc(firestore, 'users', user.uid), { country })
      } catch { /* offline / blocked: keep the old flag */ }
    })()
  }, [user, profile])

  // Keep the handlers the listeners call up to date with the latest state.
  useEffect(() => {
    pushRef.current = {
      chatActivity: (kind, id, ts, pending) => {
        const seen = chatMsgSeenRef.current
        const key = `${kind}:${id}`
        const prev = seen.get(key)
        if (prev === undefined) { seen.set(key, ts); return } // first time this session: just remember it
        if (pending || !ts || ts <= prev) return
        seen.set(key, ts)
        if (mutedChatIds.has(`${kind}:${id}`)) return
        if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return
        void notifyLatestChatMessage(kind, id)
      },
      alert: (n) => {
        if (bannedUids.has(n.fromId)) return
        const who = n.fromName || 'Someone'
        const canNotify = typeof Notification !== 'undefined' && Notification.permission === 'granted'
        if (n.type === 'gift') {
          // In-app celebration always plays; the OS notification is a bonus if permission was granted.
          setIncomingGift({ fromName: who, label: n.giftLabel || 'Gift' })
        }
        if (!canNotify) return
        if (n.type === 'friend_request') {
          void showBrandedNotification({
            body: `${who} sent you a friend request`,
            tag: `alert_${n.id}`,
            data: { kind: 'friend_request', fromId: n.fromId, fromName: who, fromAvatar: pfpFor(n.fromId) },
            actions: [{ action: 'accept', title: 'Accept' }],
          })
        } else if (n.type === 'gift') {
          void showBrandedNotification({
            body: n.giftLabel ? `You received a ${n.giftLabel} gift from ${who}` : `${who} ${n.message}`,
            tag: `alert_${n.id}`,
            data: { kind: 'gift', fromId: n.fromId, fromName: who, fromAvatar: pfpFor(n.fromId) },
          })
        } else if (n.type === 'group_request') {
          void showBrandedNotification({
            body: `${who} ${n.message}`,
            tag: `alert_${n.id}`,
            data: { kind: 'group_request', fromId: n.fromId, fromName: who, fromAvatar: pfpFor(n.fromId) },
          })
        } else {
          void showBrandedNotification({
            body: `${who} ${n.message}`,
            tag: `alert_${n.id}`,
            data: { kind: n.type === 'comment' ? 'comment' : 'like', fromId: n.fromId, fromName: who, fromAvatar: pfpFor(n.fromId) },
          })
        }
      },
      action: async (m) => {
        if (!user) return
        const d = (m && m.data) || {}
        if (m?.action === 'accept' && d.kind === 'friend_request' && typeof d.fromId === 'string') {
          await acceptFriendFromNotification(d.fromId, String(d.fromName || ''))
          return
        }
        if (d.kind === 'call') {
          // Answer / Decline buttons on the notification itself (app open in the background, or
          // reached cold via the FCM push - see public/sw.js). A plain tap just brings the app
          // forward; the ring screen is already showing (or, cold-start, the ?na= flow below
          // handles it once the matching call doc shows up).
          const callId = typeof d.callId === 'string' ? d.callId : ''
          if (m?.action === 'answer') {
            if (incomingCallId && (!callId || incomingCallId === callId)) void answerIncoming()
            else if (incomingGroupCallId && (!callId || incomingGroupCallId === callId)) answerIncomingGroup()
            return
          }
          if (m?.action === 'decline') {
            if (incomingCallId && (!callId || incomingCallId === callId)) declineIncoming()
            else if (incomingGroupCallId && (!callId || incomingGroupCallId === callId)) declineIncomingGroup()
            return
          }
          return
        }
        if (d.kind === 'message' && typeof d.chatId === 'string') {
          const chat = findChatForNotification(d.chatId, d.chatKind)
          if (!chat) { setShowChats(true); return }
          if (m?.action === 'reply' && typeof m.text === 'string' && m.text.trim()) { await sendQuickReply(chat, m.text); return }
          if (m?.action === 'like') {
            // ❤️ straight from the notification: react to that message without opening the chat.
            const cid = chat.kind === 'group' ? chat.id : [user.uid, chat.id].sort().join('_')
            const mid = typeof d.msgId === 'string' ? d.msgId : ''
            if (mid) updateDoc(doc(firestore, 'chats', cid, 'messages', mid), { 'reactions.❤️': arrayUnion(user.uid) }).then(() => notify('Liked')).catch(() => undefined)
            return
          }
          setShowChats(true)
          setActiveChat(chat)
          return
        }
        setShowNotifications(true)
      },
    }
  })

  useEffect(() => {
    activeChatRef.current = activeChat
    openDirectChatRef.current = Boolean(activeChat && activeChat.kind !== 'group')
    if (activeChat) lastChatActivityRef.current = Date.now()
  }, [activeChat])

  // Ask for notification permission right after the person has logged in (not on the login screen).
  // Inside the Android app this also registers for FCM and hands us back a device token to save.
  useEffect(() => {
    if (!user) return
    let stopped = false
    // pushMode tells the server how to build this phone's pushes: 'native-actions' = the Android app has the Animora
    // notification service (buttons, call ringtone) -> data-only pushes; anything else = normal pushes.
    // The native plugin can answer a moment late right after launch, so ask a few times before settling on 'standard'
    // (one early "no" used to leave this phone on plain notifications - no Reply / Like buttons - until the next launch).
    const resolveMode = async (): Promise<'native-actions' | 'standard'> => {
      if (!isNativeApp()) return 'standard'
      for (let i = 0; i < 4 && !stopped; i++) {
        if (await detectNativeActions()) return 'native-actions'
        await new Promise((r) => setTimeout(r, 700 * (i + 1)))
      }
      return 'standard'
    }
    let savedMode = ''
    const saveMode = async (extra: Record<string, unknown> = {}) => {
      const pushMode = await resolveMode()
      if (stopped) return
      if (!Object.keys(extra).length && pushMode === savedMode) return
      savedMode = pushMode
      void updateDoc(doc(firestore, 'users', user.uid), { ...extra, pushMode, updatedAt: serverTimestamp() }).catch(() => { savedMode = '' })
    }
    const stopAsk = askForNotificationPermission((fcmToken) => { void saveMode({ fcmToken }) })
    // Also record the mode on its own (not only when a token arrives) and again whenever the app comes back to the front.
    if (isNativeApp()) void saveMode()
    const onShow = () => { if (!document.hidden && isNativeApp()) void saveMode() }
    document.addEventListener('visibilitychange', onShow)
    return () => { stopped = true; stopAsk(); document.removeEventListener('visibilitychange', onShow) }
  }, [user?.uid])

  // Web Push (browser / installed app): lets Reply / Like / Answer / Decline buttons show on notifications even with the app closed.
  useEffect(() => {
    if (!user) return
    return watchWebPush(VAPID_PUBLIC_KEY, (webPush) => {
      void updateDoc(doc(firestore, 'users', user.uid), { webPush, updatedAt: serverTimestamp() }).catch(() => undefined)
    })
  }, [user?.uid])

  // Reply / Accept / tap on a notification, handed over by public/sw.js.
  useEffect(() => {
    if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return
    const onMessage = (e: MessageEvent) => {
      if (e.data && e.data.type === 'ANIMORA_NOTIFICATION_ACTION') void pushRef.current.action(e.data)
    }
    navigator.serviceWorker.addEventListener('message', onMessage)
    return () => navigator.serviceWorker.removeEventListener('message', onMessage)
  }, [])

  // ---- Android app: buttons on the native notification (Answer / Decline, Reply / Like, Accept) ----
  // Decline / Like / Reply are completed by the notification itself (see native/android + /api/notif-action) and never get here.
  // Answer, Accept and a plain tap open the app; the native side hands the pressed action over through the AnimoraNotify plugin:
  // as a stored "pending" action when the app was closed (cold start), or live as a 'notificationAction' event when it was running.
  const nativeActRef = useRef<any>(null)
  useEffect(() => {
    const plugin = animoraNotifyPlugin()
    if (!plugin) return
    const toNav = (a: any) => {
      if (a?.kind === 'call' && typeof a.callId === 'string') pendingNavRef.current = { k: 'call', c: a.callId, d: a.action === 'decline' ? 'decline' : 'answer', at: Date.now() }
      else if (a?.kind === 'message' && typeof a.chatId === 'string') pendingNavRef.current = { k: 'message', c: a.chatId, ck: a.chatKind === 'group' ? 'group' : 'direct', d: '', at: Date.now() }
    }
    const take = async () => {
      try {
        const a = (await plugin.getPendingAction?.())?.action
        if (a && a.kind) { toNav(a); nativeActRef.current = a }
      } catch { /* plugin not ready */ }
    }
    void take()
    let handle: any = null
    try {
      handle = plugin.addListener?.('notificationAction', (a: any) => {
        if (!a || !a.kind) return
        toNav(a)
        nativeActRef.current = a
        void take()
        void pushRef.current.action({ action: a.action || 'open', text: '', data: a })
      })
    } catch { /* ignore */ }
    const onResume = () => { void take() }
    document.addEventListener('visibilitychange', onResume)
    return () => { document.removeEventListener('visibilitychange', onResume); try { handle?.remove?.() } catch { /* ignore */ } }
  }, [])
  // Accept (friend request) / other notification taps from a cold start: run once the person is logged in and loaded.
  useEffect(() => {
    const a = nativeActRef.current
    if (!a || !user || !profile) return
    if (a.kind === 'call' || a.kind === 'message') { nativeActRef.current = null; return } // handled by the pendingNavRef effects
    nativeActRef.current = null
    void pushRef.current.action({ action: a.action || 'open', text: '', data: a })
  }, [user, profile])

  // Muted chats also live on the profile, so the SERVER stops sending their pushes (the local list only hid the in-app pop-up).
  useEffect(() => {
    if (!user || !profile) return
    const mine = Array.from(mutedChatIds).sort().slice(0, 500)
    const saved = Array.isArray((profile as any).mutedChats) ? [...(profile as any).mutedChats].sort() : []
    if (JSON.stringify(mine) === JSON.stringify(saved)) return
    const t = setTimeout(() => { void updateDoc(doc(firestore, 'users', user.uid), { mutedChats: mine }).catch(() => undefined) }, 800)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mutedChatIds, user?.uid, profile?.uid])

  // A notification was tapped while no Animora tab was open: the worker reopens the app with ?na=...
  // It only opens the right screen (a typed reply becomes a draft) - nothing is sent from a link.
  useEffect(() => {
    try {
      const url = new URL(window.location.href)
      const raw = url.searchParams.get('na')
      if (!raw) return
      url.searchParams.delete('na')
      window.history.replaceState(null, '', url.pathname + url.search + url.hash)
      const nav = JSON.parse(raw)
      if (nav && (nav.k === 'alerts' || (nav.k === 'message' && typeof nav.c === 'string') || (nav.k === 'call' && typeof nav.c === 'string'))) {
        pendingNavRef.current = { k: nav.k, c: nav.c, ck: nav.ck === 'group' ? 'group' : 'direct', d: typeof nav.d === 'string' ? nav.d.slice(0, 500) : '', at: Date.now() }
      }
    } catch { /* ignore a malformed link */ }
  }, [])

  useEffect(() => {
    const p = pendingNavRef.current
    if (!p || !user || !profile) return
    if (Date.now() - p.at > 60000) { pendingNavRef.current = null; return }
    if (p.k === 'alerts') { pendingNavRef.current = null; setShowNotifications(true); return }
    if (p.k === 'call') return // handled by the dedicated effect below, once the ring listener catches up
    const chat = findChatForNotification(p.c || '', p.ck)
    if (!chat) return // chats are still loading - this effect runs again when they arrive
    pendingNavRef.current = null
    setShowChats(true)
    setActiveChat(chat)
    if (p.d) setMessage(p.d)
  }, [user, profile, chats, allFriends])

  // Answer / Decline tapped on a call notification while the app was fully closed (public/sw.js
  // reopens it with ?na=...). The app has to boot, log in and let useCalls' own Firestore listener
  // pick the still-ringing call doc back up before answer()/decline() has anything to act on - so
  // this waits for incomingCallId / incomingGroupCallId to match, rather than acting immediately.
  useEffect(() => {
    const p = pendingNavRef.current
    if (!p || p.k !== 'call' || !user) return
    if (Date.now() - p.at > 60000) { pendingNavRef.current = null; return } // call long gone by now
    const wantsDecline = p.d === 'decline'
    if (incomingCallId && incomingCallId === p.c) {
      pendingNavRef.current = null
      if (wantsDecline) declineIncoming(); else void answerIncoming()
    } else if (incomingGroupCallId && incomingGroupCallId === p.c) {
      pendingNavRef.current = null
      if (wantsDecline) declineIncomingGroup(); else answerIncomingGroup()
    }
  }, [user, incomingCallId, incomingGroupCallId])

  // Which friend requests were already looked at on this device (a request stays pending until accepted,
  // but its red dot should still go away once the person has seen it).
  useEffect(() => {
    if (!user) return
    try {
      const raw = window.localStorage.getItem(`animora-seen-requests:${user.uid}`)
      setSeenRequestIds(new Set(raw ? (JSON.parse(raw) as string[]) : []))
    } catch { /* ignore */ }
  }, [user])

  // Opening the notification drawer = alerts seen: mark them read (red dot goes away, they stay in the list).
  useEffect(() => {
    if (!user || !showNotifications) return
    const unread = rawNotifications.filter((n) => !n.read)
    if (unread.length) {
      const batch = writeBatch(firestore)
      unread.forEach((n) => batch.update(doc(firestore, 'notifications', user.uid, 'items', n.id), { read: true }))
      batch.commit().catch(() => undefined)
    }
    const unseen = requests.filter((r) => !seenRequestIds.has(r.id))
    if (unseen.length) {
      const next = new Set(seenRequestIds)
      unseen.forEach((r) => next.add(r.id))
      setSeenRequestIds(next)
      try { window.localStorage.setItem(`animora-seen-requests:${user.uid}`, JSON.stringify(Array.from(next).slice(-200))) } catch { /* ignore */ }
    }
  }, [showNotifications, rawNotifications, requests, user, seenRequestIds])

  // How long before the SAME viewer's next watch counts as a fresh view again - like YouTube,
  // not a one-time-ever tally. `post.viewers` is a map of uid -> the server timestamp of their
  // last counted view (not a plain "seen it once" list), so we can tell how long ago that was.
  const VIEW_RECOUNT_COOLDOWN_MS = 10 * 60 * 1000

  const recordPostView = async (post: Post) => {
    if (!user) return
    const viewers = (post.viewers && typeof post.viewers === 'object' && !Array.isArray(post.viewers))
      ? (post.viewers as Record<string, any>)
      : {}
    const last = viewers[user.uid]
    const lastMs = typeof last?.toMillis === 'function' ? last.toMillis() : (typeof last === 'number' ? last : null)
    // Own posts now count too, and a repeat visit after the cooldown counts again - only a
    // watch inside the cooldown window is skipped, so refreshing/replaying doesn't spam it.
    if (lastMs !== null && Date.now() - lastMs < VIEW_RECOUNT_COOLDOWN_MS) return
    try {
      await updateDoc(doc(firestore, 'posts', post.id), {
        [`viewers.${user.uid}`]: serverTimestamp(),
        views: increment(1)
      })
    } catch { /* rejected (e.g. account under 24h old) - ignore, not worth surfacing to the viewer */ }
  }

  // ---- Personalized reels: learn from what the user likes / watches ----
  const flushReelInterests = () => {
    if (reelInterestTimer.current) { clearTimeout(reelInterestTimer.current); reelInterestTimer.current = null }
    if (!user) return
    updateDoc(doc(firestore, 'users', user.uid), { reelInterests: reelInterestRef.current }).catch(() => undefined)
  }

  // weight: like +3 (unlike -3), comments/share +2, watch time up to +2, quick skip -0.4.
  // Ads and the user's own reels are never used to learn taste.
  const recordReelSignal = (post: Post, weight: number) => {
    if (!user || !post || !weight || post.promoted || post.authorId === user.uid) return
    addReelSignal(reelInterestRef.current, post, weight)
    if (!reelInterestTimer.current) reelInterestTimer.current = setTimeout(flushReelInterests, 10000)
  }

  // Called when the Reels tab opens: runs the 24-hour taste check (if it is due) and returns
  // the frozen profile the feed is ranked with for this visit.
  const getReelInterests = (): ReelInterestSnapshot => {
    if (evaluateReelInterests(reelInterestRef.current, Date.now())) flushReelInterests()
    return snapshotReelInterests(reelInterestRef.current)
  }

  // Save pending signals if the user leaves / backgrounds the app before the 10s timer fires.
  useEffect(() => {
    if (!user) return
    const onHide = () => { if (document.hidden && reelInterestTimer.current) flushReelInterests() }
    document.addEventListener('visibilitychange', onHide)
    return () => document.removeEventListener('visibilitychange', onHide)
  }, [user])

  // Like with ❤️ Heart Emoji Notification
  const likeBusyRef = useRef<Set<string>>(new Set())
  const togglePostLike = async (stalePost: Post) => {
    if (!user) return
    // A double tap / fast taps must not fire two writes for the same reel at once.
    if (likeBusyRef.current.has(stalePost.id)) return
    // Use the newest copy of the post: the caller may hold an older object (e.g. a list row
    // captured before the last like), which would flip the heart the wrong way.
    const post = rawPostsRef.current.find((p) => p.id === stalePost.id) ?? stalePost
    const isLiked = (post.likes || []).includes(user.uid)
    likeBusyRef.current.add(post.id)
    if (post.mediaType === 'video') recordReelSignal(post, isLiked ? -3 : 3)
    // Optimistic: flip the heart locally right away. Previously this waited on the write's
    // round trip AND the next poll tick before the UI changed at all - on a slow tick that
    // reads as "the like didn't register" even when the write itself was fine. Reverted below
    // if the write is actually rejected.
    const applyLocal = (liked: boolean) => {
      const patch = (p: Post) => p.id !== post.id ? p : {
        ...p,
        likes: liked ? [...(p.likes || []), user.uid] : (p.likes || []).filter((u) => u !== user.uid),
      }
      setPosts((prev) => prev.map(patch))
      setMyPosts((prev) => prev.map(patch))
      setViewingProfilePosts((prev) => prev.map(patch))
    }
    applyLocal(!isLiked)
    try {
      await updateDoc(doc(firestore, 'posts', post.id), {
        likes: isLiked ? arrayRemove(user.uid) : arrayUnion(user.uid),
      })
      if (!isLiked) {
        void sendAppNotification(post.authorId, 'like', 'liked your post', post.id)
      }
    } catch {
      applyLocal(isLiked) // revert
      notify('Could not update like.')
    } finally {
      likeBusyRef.current.delete(post.id)
    }
  }

  // ---- Coins: in-app credit only (no cash value, never cashed out). Earned from ads, achievements, streaks, referrals and gifts, or bought in the Coin Store (Google Play) ----
  const coinsEarned = profile?.giftsReceivedRupees || 0
  const walletTopUpTotal = profile?.walletTopUpRupees || 0
  const coinsSpentTotal = profile?.coinsSpent || 0
  const referralCoinsTotal = profile?.referralCoins || 0
  const rewardCoinsTotal = profile?.rewardCoins || 0
  const coinBalance = Math.max(0, coinsEarned + walletTopUpTotal + referralCoinsTotal + rewardCoinsTotal - coinsSpentTotal)
  // Takes coins atomically via the server (Admin SDK) - never a direct Firestore write,
  // since coinsSpent/giftsReceivedRupees are denied to clients entirely (firestore.rules).
  // See app/api/wallet/transfer/route.ts. `toUid` credits that user the same amount in
  // the same atomic transaction (used for gifts/subscriptions - see handleSendGift /
  // handleCreatorSubscribe). Returns a txnId; pass it to refundCoins if granting the
  // purchase fails afterwards.
  const spendCoins = async (
    amount: number, toUid?: string, kind = 'spend',
    subscription?: { creatorName?: string; planId: string; durationDays: number },
    promotion?: { kind: 'post' | 'story'; id: string; durationHours: number; goal?: 'profile' | 'website' },
    selfGrant?: { premiumDays?: number; chatThemeIds?: string[]; groupMemberSlots?: number },
    giftMeta?: { postId?: string; count?: number },
  ): Promise<string> => {
    if (!user) throw new Error('Please sign in again.')
    if (!(amount > 0)) throw new Error('Invalid amount.')
    const idToken = await user.getIdToken()
    const res = await fetch('/api/wallet/transfer', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
      body: JSON.stringify({ amountRupees: amount, toUid, kind, subscription, promotion, selfGrant, giftMeta }),
    })
    const data = await res.json().catch(() => ({}))
    if (!res.ok || !data.ok) {
      if (data.error === 'not-enough-coins') throw new Error('Not enough coins.')
      if (data.error === 'boost-coming-soon') throw new Error('Boost is coming soon.')
      if (data.error === 'boost-limit-active') throw new Error(`You can have at most ${BOOST_LIMITS.maxActive} active boosts at a time. Wait for one to end.`)
      if (data.error === 'boost-limit-daily') throw new Error(`Daily boost limit reached (${BOOST_LIMITS.maxPerDay} per day). Try again tomorrow.`)
      if (data.error === 'already-boosted') throw new Error('This post is already boosted. You can boost it again after it ends.')
      if (data.error === 'boost-cooldown') throw new Error('Please wait a few minutes before starting another boost.')
      if (data.error === 'age-restricted') throw new Error('Gifts and subscriptions are only available to accounts confirmed as 18 or older.')
      throw new Error(data.error ? `Could not use your coins right now (${data.error}). Please try again.` : 'Could not use your coins right now. Please try again.')
    }
    return data.txnId as string
  }

  // Renaming atomically (free / coin-priced handles only - premium 1-4 character handles are paid with REAL MONEY via Google Play (buyUsername)): the coin spend,
  // the usernames/{name} reservation swap and the users/{uid} username fields all move
  // together on the server (see the `usernameChange` branch of /api/wallet/transfer).
  // Replaces the old pattern of a client-side runTransaction() against usernames/{name}
  // (no length/price check at all) followed by a separate updateDoc() on username fields
  // that were, until now, freely owner-writable - see lib/d1-rules.ts.
  const changeUsername = async (newUsername: string, priceCoins: number, confirmLoseHandle = false): Promise<void> => {
    if (!user) throw new Error('Please sign in again.')
    const idToken = await user.getIdToken()
    const res = await fetch('/api/wallet/transfer', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
      body: JSON.stringify({ amountRupees: priceCoins, usernameChange: { newUsername, confirmLoseHandle } }),
    })
    const data = await res.json().catch(() => ({}))
    if (!res.ok || !data.ok) {
      if (data.error === 'username-taken') throw new Error('That username is already taken. Try another one.')
      if (data.error === 'not-enough-coins') throw new Error('Not enough coins for this username.')
      if (data.error === 'username-change-locked') throw new Error(usernameLockText(Number(data.until || 0)))
      if (data.error === 'confirm-lose-handle') throw new Error('Please confirm that you give up your short username to change it.')
      if (data.error === 'premium-requires-play') throw new Error('Usernames with 1-4 characters can only be bought with Google Play in the Animora Android app.')
      if (data.error === 'trademark' || data.error === 'reserved' || data.error === 'format') {
        throw new Error(data.message || 'That username is not allowed.')
      }
      throw new Error('Could not save your username right now. Please try again.')
    }
  }

  // Reverses one specific spend (by txnId) - never a raw "give me back N coins" call,
  // which would let anyone claw back coins from an already-granted purchase for free.
  const refundCoins = async (txnId: string) => {
    if (!user || !txnId) return
    try {
      const idToken = await user.getIdToken()
      await fetch('/api/wallet/reverse', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
        body: JSON.stringify({ txnId }),
      })
    } catch { /* balance is re-synced from Firestore either way */ }
  }

  const coinsApi: CoinsApi = { balance: coinBalance, spend: spendCoins, refund: refundCoins }

  // Buys one +GROUP_MEMBER_BONUS_INCREMENT block of extra guild member capacity for
  // GROUP_MEMBER_BONUS_PRICE_COINS coins. Server-verified & atomic with the coin spend
  // (see selfGrant.groupMemberSlots in /api/wallet/transfer) - the actual bump to
  // profile.groupMemberBonus arrives back through the live users/{uid} listener, this
  // just updates local state right away so the button doesn't feel stuck.
  const [buyingGroupSlot, setBuyingGroupSlot] = useState(false)
  const buyGroupMemberSlot = async () => {
    if (!user || buyingGroupSlot) return
    setBuyingGroupSlot(true)
    try {
      await spendCoins(GROUP_MEMBER_BONUS_PRICE_COINS, undefined, 'group_slots', undefined, undefined, { groupMemberSlots: 1 })
      setProfile((c) => c ? { ...c, groupMemberBonus: (c.groupMemberBonus || 0) + 1 } : c)
      notify(`+${GROUP_MEMBER_BONUS_INCREMENT} member slots added to your guilds.`)
    } catch (err) {
      notify(friendlyError(err, 'Could not buy more slots.'))
    } finally { setBuyingGroupSlot(false) }
  }

  // (Wallet top-ups / coin packs were removed: coins are earned free (achievements, streaks, snaps, referrals) or from gifts.
  //  Boost, Green Tick and premium 1-4 character usernames are the only real-money purchases - see BoostModal / buyUsername.)

  // Payment History (Wallet page): loaded fresh each time the Wallet is opened, from the
  // 3 places money actually moves for this account - coins spent (coinTxns where uid ==
  // me), coins received via gifts/subscriptions (coinTxns where toUid == me), and real-money
  // top-ups (processedPayments where uid == me). Merged newest-first, capped at 40 rows.
  useEffect(() => {
    if (!showWallet || !user) return
    let cancelled = false
    setWalletTxnsLoading(true)
    ;(async () => {
      try {
        const [spentSnap, receivedSnap, topupSnap] = await Promise.all([
          getDocs(query(collection(firestore, 'coinTxns'), where('uid', '==', user.uid), orderBy('createdAt', 'desc'), limit(25))),
          getDocs(query(collection(firestore, 'coinTxns'), where('toUid', '==', user.uid), orderBy('createdAt', 'desc'), limit(25))),
          getDocs(query(collection(firestore, 'processedPayments'), where('uid', '==', user.uid), orderBy('createdAt', 'desc'), limit(25))),
        ])
        const toMs = (d: any) => Number(d?.createdAt?.toMillis?.() ?? 0)
        const rows: WalletTxn[] = [
          ...spentSnap.docs.map((d) => {
            const data = d.data()
            return { id: d.id, amountRupees: Number(data.amountRupees || 0), createdAtMs: toMs(data), direction: 'out' as const, kind: String(data.kind || 'spend') }
          }),
          ...receivedSnap.docs.map((d) => {
            const data = d.data()
            return { id: d.id, amountRupees: Number(data.amountRupees || 0), createdAtMs: toMs(data), direction: 'in' as const, kind: String(data.kind || 'gift') }
          }),
          // Boost payments (real money, category 'boost') are NOT coins - only old wallet top-ups show here.
          ...topupSnap.docs.filter((d) => d.data()?.category !== 'boost').map((d) => {
            const data = d.data()
            return { id: d.id, amountRupees: Number(data.amountRupees || 0), createdAtMs: toMs(data), direction: 'in' as const, kind: 'topup' }
          }),
        ].sort((a, b) => b.createdAtMs - a.createdAtMs).slice(0, 40)
        if (!cancelled) setWalletTxns(rows)
      } catch {
        if (!cancelled) setWalletTxns([])
      } finally {
        if (!cancelled) setWalletTxnsLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [showWallet, user])

  // ---- Direct purchases (there is no store): each handler saves exactly what was bought. ----
  // purchaseWith() has already taken the coins before these run and gives them back if they return false.

  // Green Tick: 3,000 coins = 30 days. Renewing while it is still active stacks another 30
  // days on top. isPremium/premiumUntil are now set atomically server-side together with
  // the coin spend (see /api/wallet/transfer's `selfGrant.premiumDays`, called from
  // GreenTickSheet) - a plain client updateDoc() here used to be legal too (it's the
  // owner editing their own doc), completely disconnected from whether it was paid for.
  // This just re-syncs local state so the UI updates immediately.
  const grantGreenTick = async (): Promise<boolean> => {
    if (!user || !profile) return false
    const now = Date.now()
    const base = isPremiumActive && profile.premiumUntil && profile.premiumUntil > now ? profile.premiumUntil : now
    setProfile((c) => c ? { ...c, isPremium: true, premiumUntil: base + GREEN_TICK_DAYS * 24 * 60 * 60 * 1000 } : c)
    notify('Green Tick activated! Premium themes, more stickers & bigger guilds unlocked')
    return true
  }

  // Google Play Billing (real money: Green Tick subscription + Boost). Starts once the person is signed in, inside the Android app only.
  // The server (/api/billing/verify) is the one that grants; these callbacks only refresh what the screen shows.
  // Owned purchases that were never confirmed (app closed mid-payment) and Green Tick renewals arrive here as well.
  useEffect(() => {
    if (!user || !isNativeApp()) return
    void startBilling({
      uid: user.uid,
      getIdToken: () => user.getIdToken(),
      onTickGranted: (premiumUntil: number) => {
        setProfile((c) => c ? { ...c, isPremium: true, premiumUntil: Math.max(Number(c.premiumUntil || 0), premiumUntil) } : c)
        notify('Green Tick activated! Premium themes, more stickers & bigger guilds unlocked')
      },
      onUsernameGranted: ({ username }: { username: string }) => {
        setProfile((c) => c ? { ...c, username, usernameLower: username.toLowerCase() } : c)
      },
      onCoinsGranted: ({ coins, again }: { coins: number; again?: boolean }) => {
        // Take the REAL balance from the server (the server is the only one that adds coins). Never add locally: a retry after a bad
        // connection / an unconsumed purchase on restart is answered with again=true and must not be counted twice on screen.
        void (async () => {
          try {
            const snap = await getDoc(doc(firestore, 'users', user.uid))
            const d: any = snap.data()
            if (d) setProfile((c) => c ? { ...c, walletTopUpRupees: Number(d.walletTopUpRupees || 0), giftsReceivedRupees: Math.max(Number(d.giftsReceivedRupees || 0), c.giftsReceivedRupees || 0), coinsSpent: Number(d.coinsSpent || 0), referralCoins: Number(d.referralCoins || 0), rewardCoins: Number(d.rewardCoins || 0) } : c)
          } catch { /* the live profile listener refreshes it anyway */ }
        })()
        if (!again) notify(`+${coins.toLocaleString('en-IN')} coins added to your wallet`)
      },
      onBoostGranted: ({ hours }: { promotedUntil: number; hours: number }) => {
        notify(`Your ${hours >= 24 ? `${Math.round(hours / 24)}-day` : `${hours}-hour`} boost is live`)
        setBoostTarget(null)
      },
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.uid])

  // Chat themes: unlocks exactly the theme(s) that were bought. Same reasoning as Green
  // Tick above - unlockedChatThemes is now set atomically server-side with the spend
  // (see selfGrant.chatThemeIds), this just re-syncs local state.
  const grantChatThemes = async (themeIds: string[]): Promise<boolean> => {
    if (!user || !profile) return false
    const valid = themeIds.filter((id) => (PREMIUM_CHAT_THEME_IDS as readonly string[]).includes(id))
    if (!valid.length) return false
    setProfile((c) => c ? { ...c, unlockedChatThemes: Array.from(new Set([...(c.unlockedChatThemes || []), ...valid])) } : c)
    notify(valid.length > 1 ? 'All premium chat themes unlocked' : 'Premium chat theme unlocked')
    return true
  }

  // Called after paying to Subscribe to (or send a Gift/Tip to) another creator.
  // Writes a subscription doc under subscriptions/{subscriberId}_{creatorId} and, for
  // gifts, a log entry under gifts/ plus a running total on the creator's own profile.
  const handleCreatorSubscribe = async (creatorId: string, planId: string, paidWith: PayMethod = 'coins', creatorName: string = ''): Promise<boolean> => {
    if (!user) return false
    if (creatorId === user.uid) {
      notify("You can't subscribe to yourself.")
      return false
    }
    // The subscriptions/{uid}_{creatorId} doc is created/extended server-side, in the
    // SAME atomic transaction as the coin spend - see /api/wallet/transfer's
    // `subscription` param (called from purchaseWith, before this function even runs).
    // This function only handles the notification; if it were still writing the
    // subscription doc itself here, that write would be entirely disconnected from
    // whether payment actually happened - which was exactly the old bug.
    try {
      const plan = SUBSCRIPTION_PLANS.find((p) => p.id === planId)
      void sendAppNotification(creatorId, 'gift', `subscribed to support you (${plan?.label || 'Subscription'})`, creatorId, { giftLabel: plan?.label })
      notify('Subscribed! Thanks for supporting this creator')
      return true
    } catch {
      notify('Could not subscribe - your coins were returned.')
      return false
    }
  }

  // ---- Subscriptions: live list, monthly auto-renewal, unsubscribe ----
  const [mySubs, setMySubs] = useState<MySubscription[]>([])
  const [subNames, setSubNames] = useState<Record<string, string>>({})
  const mySubsRef = useRef<MySubscription[]>([])
  const renewingRef = useRef<Set<string>>(new Set())

  useEffect(() => {
    if (!user) { setMySubs([]); return }
    const q = query(collection(firestore, 'subscriptions'), where('subscriberId', '==', user.uid))
    return onSnapshot(q, (snap) => {
      setMySubs(snap.docs.map((d) => {
        const x = d.data() as any
        return {
          id: d.id,
          creatorId: String(x.creatorId || ''),
          creatorName: String(x.creatorName || ''),
          tier: String(x.tier || ''),
          expiresAt: Number(x.expiresAt || 0),
          autoRenew: x.autoRenew === true, // older one-time subscriptions have no flag and never renew
        }
      }))
    }, () => { /* ignore - list simply stays empty */ })
  }, [user?.uid])

  // Older subscriptions were saved without the creator's name - look it up once.
  useEffect(() => {
    const missing = mySubs.filter((x) => !x.creatorName && x.creatorId && !subNames[x.creatorId])
    if (!missing.length) return
    let cancelled = false
    void Promise.all(missing.map(async (x) => {
      try {
        const snap = await getDoc(doc(firestore, 'users', x.creatorId))
        return [x.creatorId, String((snap.data() as any)?.name || 'creator')] as const
      } catch { return [x.creatorId, 'creator'] as const }
    })).then((pairs) => { if (!cancelled) setSubNames((c) => ({ ...c, ...Object.fromEntries(pairs) })) })
    return () => { cancelled = true }
  }, [mySubs])

  const mySubsNamed: MySubscription[] = useMemo(
    () => mySubs.map((x) => ({ ...x, creatorName: x.creatorName || subNames[x.creatorId] || 'creator' })),
    [mySubs, subNames],
  )
  mySubsRef.current = mySubsNamed

  const handleUnsubscribe = async (subId: string): Promise<boolean> => {
    if (!user) return false
    try {
      await updateDoc(doc(firestore, 'subscriptions', subId), { autoRenew: false, cancelledAt: Date.now() })
      notify('Unsubscribed. No more coins will be taken.')
      return true
    } catch {
      notify('Could not unsubscribe. Please try again.')
      return false
    }
  }

  // Takes ONE month (never several at once) when a subscription's 30 days are over. The coins, the
  // new expiry date and the balance check happen in one transaction, so it can't double-charge.
  // If the wallet is too low, the subscription simply ends (autoRenew off) instead of going negative.
  const renewSubscription = async (sub: MySubscription) => {
    if (!user || renewingRef.current.has(sub.id)) return
    const plan = SUBSCRIPTION_PLANS.find((p) => p.id === sub.tier)
    const subRef = doc(firestore, 'subscriptions', sub.id)
    const userRef = doc(firestore, 'users', user.uid)
    renewingRef.current.add(sub.id)
    try {
      if (!plan) { await updateDoc(subRef, { autoRenew: false, cancelledAt: Date.now() }); return }
      let outcome = 'skip' as 'renewed' | 'no-coins' | 'skip'
      let alreadyDue = false
      await runTransaction(firestore, async (tx) => {
        outcome = 'skip'
        const subSnap = await tx.get(subRef)
        const subData = subSnap.data() as any
        if (!subSnap.exists() || subData?.autoRenew !== true || Number(subData.expiresAt || 0) > Date.now()) return
        alreadyDue = true
      })
      if (alreadyDue && plan) {
        // Spend + credit the creator atomically via the server (Admin SDK) - coinsSpent /
        // giftsReceivedRupees can no longer be written directly by the client (see
        // firestore.rules + /api/wallet/transfer).
        try {
          // expiresAt/renewedAt are extended server-side in the SAME transaction as the
          // spend now (see the `subscription` param) - no separate updateDoc needed, and
          // none would be allowed anyway (firestore.rules locks those fields to the
          // server/Admin SDK only, same reasoning as the wallet money fields).
          await spendCoins(plan.amountRupees, sub.creatorId, 'subscription_renewal', { creatorName: sub.creatorName, planId: plan.id, durationDays: plan.durationDays || 30 })
          outcome = 'renewed'
        } catch (err: any) {
          if (err?.message === 'Not enough coins.') {
            await updateDoc(subRef, { autoRenew: false, cancelledAt: Date.now(), endedReason: 'not-enough-coins' })
            outcome = 'no-coins'
          } else {
            throw err
          }
        }
      }
      if (outcome === 'renewed') {
        void sendAppNotification(sub.creatorId, 'gift', `renewed their subscription (${plan.label})`, sub.creatorId, { giftLabel: plan.label })
        notify(`Subscription renewed: ${plan.amountRupees} coins sent to @${sub.creatorName || 'creator'}.`)
      } else if (outcome === 'no-coins') {
        notify(`Not enough coins to renew your subscription to @${sub.creatorName || 'creator'}. It has ended.`)
      }
    } catch (err) {
      console.warn('subscription renewal failed', err)
    } finally {
      renewingRef.current.delete(sub.id)
    }
  }

  // Check when the app opens / the list changes, and every 5 minutes while it stays open.
  const profileReady = Boolean(profile)
  useEffect(() => {
    if (!user || !profileReady) return
    const checkDue = () => mySubsRef.current.filter((x) => x.autoRenew && x.expiresAt > 0 && x.expiresAt <= Date.now()).forEach((x) => { void renewSubscription(x) })
    checkDue()
    const t = window.setInterval(checkDue, 5 * 60 * 1000)
    return () => window.clearInterval(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.uid, profileReady, mySubs])

  const handleSendGift = async (creatorId: string, planId: string, paidWith: PayMethod = 'coins', postId?: string, quantity = 1): Promise<boolean> => {
    if (!user) return false
    if (creatorId === user.uid) {
      notify("You can't send a gift to yourself.")
      return false
    }
    const gift = GIFT_CATALOG.find((g) => g.id === planId)
    const count = Math.max(1, Math.min(99, Math.floor(quantity) || 1))
    const totalRupees = (gift?.amountRupees || 0) * count
    // The gifts/{id} log rows themselves are now written server-side, atomically with the
    // coin spend, by /api/wallet/transfer (see CoinsApi.spend's `giftMeta` param) - a
    // separate client-side write here had no server check that a matching payment ever
    // happened, so anyone could forge a "gift" log for free. This function only handles
    // the best-effort extras: a comment on the post it was sent from, and a notification.
    try {
      if (postId && gift) {
        try {
          await addDoc(collection(firestore, 'posts', postId, 'comments'), {
            authorId: user.uid,
            authorName: profile?.name || 'Traveler',
            authorAvatar: profile?.avatar || '',
            text: count > 1 ? `sent ${count}× ${gift.label}` : `sent ${gift.label}`,
            giftLabel: gift.label,
            giftAmount: totalRupees,
            likes: [],
            createdAt: serverTimestamp(),
          })
          await updateDoc(doc(firestore, 'posts', postId), { commentCount: increment(1) })
        } catch (err) { console.warn('gift comment failed', err) }
      }
      void sendAppNotification(creatorId, 'gift', count > 1 ? `sent you ${count}× ${gift?.label || 'gifts'}` : `sent you a gift ${gift?.label || ''}`.trim(), creatorId, { giftLabel: gift?.label })
      notify(count > 1 ? `${count} gifts sent - ${gift?.label || 'thank you'}!` : `Gift sent - ${gift?.label || 'thank you'}!`)
      void refreshAchievementStats()
      return true
    } catch (err) {
      // Surface the real reason (e.g. a permissions error) instead of a generic message,
      // so a genuine failure here is actually possible to diagnose.
      console.warn('handleSendGift failed', err)
      throw err instanceof Error ? err : new Error('Could not send the gift - your coins were returned.')
    }
  }

  // NOTE: applyPromotion() used to live here as a plain client updateDoc({promoted:true,
  // ...}) on the author's own post/story - removed because it was disconnected from
  // payment (rules allow an author to freely edit their own post). Boost now writes
  // these fields server-side after the Google Play purchase is verified - see /api/billing/verify.

  // Lets a post owner add a website link, a custom "Learn More" button label, and a
  // custom banner image right from the Boost sheet, instead of having to leave and
  // re-create the post. Returns the normalized URL so the modal can update its own
  // state immediately. Throws if the URL doesn't look like a real website, so junk
  // links can never be saved as an ad destination.
  const saveBoostWebsite = async (postId: string, rawUrl: string, col: 'posts' | 'stories' = 'posts') => {
    if (!isValidWebsiteUrl(rawUrl)) throw new Error('Enter a valid website address, like yourstore.com')
    const normalized = normalizeUrl(rawUrl.trim())
    await updateDoc(doc(firestore, col, postId), { websiteUrl: normalized })
    return normalized
  }

  const saveBoostCtaText = async (postId: string, ctaText: string, col: 'posts' | 'stories' = 'posts') => {
    const trimmed = ctaText.trim().slice(0, 24)
    await updateDoc(doc(firestore, col, postId), { ctaText: trimmed || null })
    return trimmed
  }

  // A solid brand color behind the ad's CTA instead of an uploaded banner image - no
  // file, no upload, nothing that can fail. Empty string clears it back to the default.
  const saveBoostBannerColor = async (postId: string, color: string, col: 'posts' | 'stories' = 'posts') => {
    await updateDoc(doc(firestore, col, postId), { bannerColor: color || null })
    return color
  }

  const recordStoryView = async (story: Story) => {
    if (!user) return
    // Your own story never gets a server-side "viewer" entry (you can't be a viewer of
    // your own story), but the ring still needs to dim once you've watched it - track
    // that locally instead.
    setViewedStoryIds((prev) => prev.has(story.id) ? prev : new Set(prev).add(story.id)) // saved on the phone -> ring stays grey
    if (story.authorId === user.uid) return
    if ((story.viewers || []).includes(user.uid)) return
    try {
      await updateDoc(doc(firestore, 'stories', story.id), { viewers: arrayUnion(user.uid) })
    } catch { /* ignore */ }
  }

  // Optimistic like/unlike, same pattern as togglePostLike: flip the heart locally first so
  // it never waits on the write round-trip + next poll tick (up to ~700-1500ms), and revert
  // only if the write is actually rejected.
  const toggleStoryLike = async (staleStory: Story) => {
    if (!user) return
    // Fast double taps must not fire two writes at once, and the caller may hold an older copy
    // of the story - always flip from the newest one (same pattern as togglePostLike).
    if (storyLikeBusyRef.current.has(staleStory.id)) return
    const story = rawStoriesRef.current.find((s) => s.id === staleStory.id) ?? staleStory
    const isLiked = (story.likes || []).includes(user.uid)
    storyLikeBusyRef.current.add(story.id)
    const applyLocal = (liked: boolean) => {
      setStories((prev) => prev.map((s) => s.id !== story.id ? s : {
        ...s, likes: liked ? [...(s.likes || []).filter((u) => u !== user.uid), user.uid] : (s.likes || []).filter((u) => u !== user.uid),
      }))
    }
    applyLocal(!isLiked)
    try {
      await updateDoc(doc(firestore, 'stories', story.id), {
        likes: isLiked ? arrayRemove(user.uid) : arrayUnion(user.uid),
      })
      if (!isLiked) void sendAppNotification(story.authorId, 'like', 'liked your story', story.id)
    } catch {
      applyLocal(isLiked) // revert
      notify('Could not update like.')
    } finally {
      storyLikeBusyRef.current.delete(story.id)
    }
  }

  const toggleCommentLike = async (post: Post, comment: PostComment) => {
    if (!user) return
    const isLiked = (comment.likes || []).includes(user.uid)
    const applyLocal = (liked: boolean) => {
      setComments((prev) => prev.map((c) => c.id !== comment.id ? c : {
        ...c, likes: liked ? [...(c.likes || []), user.uid] : (c.likes || []).filter((u) => u !== user.uid),
      }))
    }
    applyLocal(!isLiked)
    try {
      await updateDoc(doc(firestore, 'posts', post.id, 'comments', comment.id), {
        likes: isLiked ? arrayRemove(user.uid) : arrayUnion(user.uid),
      })
      if (!isLiked) void sendAppNotification(comment.authorId, 'like', 'liked your comment', post.id)
    } catch {
      applyLocal(isLiked) // revert
      notify('Could not like comment.')
    }
  }

  // Only the post's author can pin/unpin a comment (Firestore rules enforce this
  // server-side too - see the comments subcollection rule in firestore.rules).
  const togglePinComment = async (post: Post, comment: PostComment) => {
    if (!user || post.authorId !== user.uid) return
    try {
      await updateDoc(doc(firestore, 'posts', post.id, 'comments', comment.id), { pinned: !comment.pinned })
    } catch { notify('Could not pin comment.') }
  }

  const addComment = async (post: Post, text: string, reply?: CommentReplyMeta) => {
    if (!user || !post || !text.trim()) return
    const trimmed = text.trim()
    // Optimistic: show the comment (and bump the visible count) right away instead of waiting
    // on the write round trip + next poll tick - same reasoning as togglePostLike below. The
    // temp row is simply replaced once the real one comes back from the next poll.
    const tempId = `local-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
    const optimisticComment: PostComment = {
      id: tempId,
      authorId: user.uid,
      authorName: profile?.name || 'Traveler',
      authorAvatar: profile?.avatar || '',
      text: trimmed,
      time: 'now',
      likes: [],
      ...(reply ? { threadId: reply.threadId, replyToName: reply.name } : {}),
    }
    setComments((prev) => [...prev, optimisticComment])
    const bumpCommentCount = (delta: number) => {
      const patch = (p: Post) => p.id !== post.id ? p : { ...p, commentCount: Math.max(0, (p.commentCount || 0) + delta) }
      setPosts((prev) => prev.map(patch))
      setMyPosts((prev) => prev.map(patch))
      setViewingProfilePosts((prev) => prev.map(patch))
    }
    bumpCommentCount(1)
    try {
      // ONE commit: the comment and the post's comment counter are written together. Before this was two commits in a
      // row (addDoc, then updateDoc) = two full server round trips, and the counter on everyone else's screen lagged
      // behind the comment itself.
      const commentBatch = writeBatch(firestore)
      commentBatch.set(doc(collection(firestore, 'posts', post.id, 'comments')), {
        authorId: user.uid,
        authorName: profile?.name || 'Traveler',
        authorAvatar: profile?.avatar || '',
        text: trimmed,
        likes: [],
        ...(reply ? { threadId: reply.threadId, replyToName: reply.name, replyToAuthorId: reply.authorId } : {}),
        createdAt: serverTimestamp(),
      })
      commentBatch.update(doc(firestore, 'posts', post.id), { commentCount: increment(1) })
      await commentBatch.commit()
    } catch (err) {
      console.error('addComment failed:', err)
      setComments((prev) => prev.filter((c) => c.id !== tempId))
      bumpCommentCount(-1)
      const code = String((err as any)?.code || (err as any)?.message || '')
      notify(/permission|denied/i.test(code) ? "Your comment can't be posted here - it may contain words the post owner has filtered." : friendlyError(err, 'Comment failed.'))
      return
    }
    // Notifications are best-effort and run in the background: the comment is already saved above.
    if (reply) {
      void sendAppNotification(reply.authorId, 'comment', `replied to your comment: "${text.trim().slice(0, 35)}"`, post.id)
      if (post.authorId !== reply.authorId) void sendAppNotification(post.authorId, 'comment', `commented: "${text.trim().slice(0, 35)}"`, post.id)
    } else {
      void sendAppNotification(post.authorId, 'comment', `commented: "${text.trim().slice(0, 35)}"`, post.id)
    }
    // @mentions in the comment text - notify anyone tagged (skip whoever was already
    // notified above for the same comment, to avoid a double alert for the same action).
    resolveMentions(text).then((uids) => {
      for (const uid of uids) {
        if (uid === post.authorId || uid === reply?.authorId) continue
        void sendAppNotification(uid, 'mention', `mentioned you in a comment: "${text.trim().slice(0, 35)}"`, post.id)
      }
    })
  }

  const editComment = async (post: Post, comment: PostComment, newText: string) => {
    if (!user || comment.authorId !== user.uid || !newText.trim()) return
    try {
      await updateDoc(doc(firestore, 'posts', post.id, 'comments', comment.id), {
        text: newText.trim(),
        edited: true
      })
      notify('Comment updated.')
    } catch { notify('Failed to edit comment.') }
  }

  const deleteComment = (post: Post, comment: PostComment) => {
    if (!user || comment.authorId !== user.uid) return
    askConfirm({
      title: 'Delete this comment?',
      confirmLabel: 'Delete',
      action: async () => {
        try {
          await deleteDoc(doc(firestore, 'posts', post.id, 'comments', comment.id))
          await updateDoc(doc(firestore, 'posts', post.id), { commentCount: increment(-1) })
          notify('Comment deleted.')
        } catch { notify('Failed to delete comment.') }
      },
    })
  }

  const toggleStoryCommentLike = async (story: Story, comment: PostComment) => {
    if (!user) return
    const isLiked = (comment.likes || []).includes(user.uid)
    const applyLocal = (liked: boolean) => {
      setStoryComments((prev) => prev.map((c) => c.id !== comment.id ? c : {
        ...c, likes: liked ? [...(c.likes || []), user.uid] : (c.likes || []).filter((u) => u !== user.uid),
      }))
    }
    applyLocal(!isLiked)
    try {
      await updateDoc(doc(firestore, 'stories', story.id, 'comments', comment.id), {
        likes: isLiked ? arrayRemove(user.uid) : arrayUnion(user.uid),
      })
      if (!isLiked) void sendAppNotification(comment.authorId, 'like', 'liked your comment', story.id)
    } catch {
      applyLocal(isLiked) // revert
      notify('Could not like comment.')
    }
  }

  const togglePinStoryComment = async (story: Story, comment: PostComment) => {
    if (!user || story.authorId !== user.uid) return
    try {
      await updateDoc(doc(firestore, 'stories', story.id, 'comments', comment.id), { pinned: !comment.pinned })
    } catch { notify('Could not pin comment.') }
  }

  const addStoryComment = async (story: Story, text: string, reply?: CommentReplyMeta) => {
    if (!user || !story || !text.trim()) return
    try {
      // ONE commit (comment + counter), same as addComment.
      const commentBatch = writeBatch(firestore)
      commentBatch.set(doc(collection(firestore, 'stories', story.id, 'comments')), {
        authorId: user.uid,
        authorName: profile?.name || 'Traveler',
        authorAvatar: profile?.avatar || '',
        text: text.trim(),
        likes: [],
        ...(reply ? { threadId: reply.threadId, replyToName: reply.name, replyToAuthorId: reply.authorId } : {}),
        createdAt: serverTimestamp(),
      })
      commentBatch.update(doc(firestore, 'stories', story.id), { commentCount: increment(1) })
      await commentBatch.commit()
    } catch (err) {
      console.error('addStoryComment failed:', err)
      notify('Comment failed.')
      return
    }
    if (reply) {
      void sendAppNotification(reply.authorId, 'comment', `replied to your comment: "${text.trim().slice(0, 35)}"`, story.id)
      if (story.authorId !== reply.authorId) void sendAppNotification(story.authorId, 'comment', `commented on your story: "${text.trim().slice(0, 35)}"`, story.id)
    } else {
      void sendAppNotification(story.authorId, 'comment', `commented on your story: "${text.trim().slice(0, 35)}"`, story.id)
    }
    resolveMentions(text).then((uids) => {
      for (const uid of uids) {
        if (uid === story.authorId || uid === reply?.authorId) continue
        void sendAppNotification(uid, 'mention', `mentioned you in a comment: "${text.trim().slice(0, 35)}"`, story.id)
      }
    })
  }

  const editStoryComment = async (story: Story, comment: PostComment, newText: string) => {
    if (!user || comment.authorId !== user.uid || !newText.trim()) return
    try {
      await updateDoc(doc(firestore, 'stories', story.id, 'comments', comment.id), {
        text: newText.trim(),
        edited: true
      })
      notify('Comment updated.')
    } catch { notify('Failed to edit comment.') }
  }

  const deleteStoryComment = (story: Story, comment: PostComment) => {
    if (!user || comment.authorId !== user.uid) return
    askConfirm({
      title: 'Delete this comment?',
      confirmLabel: 'Delete',
      action: async () => {
        try {
          await deleteDoc(doc(firestore, 'stories', story.id, 'comments', comment.id))
          await updateDoc(doc(firestore, 'stories', story.id), { commentCount: increment(-1) })
          notify('Comment deleted.')
        } catch { notify('Failed to delete comment.') }
      },
    })
  }

  const updateChatBackground = async (bgId: string) => {
    if (!user) return
    setProfile((c) => c ? { ...c, chatBackground: bgId } : c)
    try {
      await updateDoc(doc(firestore, 'users', user.uid), { chatBackground: bgId })
    } catch { notify('Could not save chat background.') }
  }

  // Header bar + chat input box theme. 'auto' (default) means "keep following
  // the chat wallpaper colors", same as before this feature existed.
  const updateHeaderTheme = async (themeId: string) => {
    if (!user) return
    setProfile((c) => c ? { ...c, headerTheme: themeId } : c)
    try {
      await updateDoc(doc(firestore, 'users', user.uid), { headerTheme: themeId })
    } catch { notify('Could not save header theme.') }
  }


  // Same wallpapers as the chat picker, but applied behind the whole app (Radar, Comms, Orbit...).
  const updateAppBackground = async (bgId: string) => {
    if (!user) return
    setProfile((c) => c ? { ...c, appBackground: bgId } : c)
    try {
      await updateDoc(doc(firestore, 'users', user.uid), { appBackground: bgId })
    } catch { notify('Could not save app theme.') }
  }

  // Theme of the Games list (components/games.tsx). Same catalogue as the home themes; '' = the classic black look.
  const updateGamesTheme = async (themeId: string) => {
    if (!user) return
    setProfile((c) => c ? { ...c, gamesTheme: themeId } : c)
    try {
      await updateDoc(doc(firestore, 'users', user.uid), { gamesTheme: themeId })
    } catch { notify('Could not save games theme.') }
  }

  // Block / restrict / hide (and undo). Goes through /api/block because a block has to remove
  // follow + friend docs the caller does not own. Returns true on success.
  const [blockBusy, setBlockBusy] = useState(false)
  const blockAction = async (target: { uid: string; name?: string; username?: string; avatar?: string }, action: BlockAction): Promise<boolean> => {
    if (!user || blockBusy) return false
    setBlockBusy(true)
    try {
      const idToken = await user.getIdToken()
      const res = await fetch('/api/block', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
        body: JSON.stringify({ targetUid: target.uid, action }),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok || !data.ok) {
        notify(data.error === 'unblock-first' ? 'Unblock this account first.' : typeof data.error === 'string' && data.error.includes('Too many') ? data.error : 'Could not update this right now. Please try again.')
        return false
      }
      // Optimistic local update - the live listener above confirms it a moment later.
      const mode: BlockMode = action === 'block' || action === 'unblock' ? 'block' : action === 'restrict' || action === 'unrestrict' ? 'restrict' : 'hide'
      const removing = action === 'unblock' || action === 'unrestrict' || action === 'unhide'
      setBlockRecords((cur) => {
        const next = { ...cur }
        if (removing) { if (next[target.uid]?.mode === mode) delete next[target.uid] } else {
          next[target.uid] = { uid: target.uid, mode, name: target.name || cur[target.uid]?.name || '', username: target.username || cur[target.uid]?.username || '', avatar: target.avatar || cur[target.uid]?.avatar || '' }
        }
        return next
      })
      if (action === 'block') {
        setFollowing((c) => { const n = new Set(c); n.delete(target.uid); return n })
        setSentRequests((c) => { const n = new Set(c); n.delete(target.uid); return n })
        setViewingProfilePosts([])
      }
      notify({
        block: `Blocked ${target.name || 'account'}.`, unblock: `Unblocked ${target.name || 'account'}.`,
        restrict: `Restricted ${target.name || 'account'}.`, unrestrict: `Removed restriction on ${target.name || 'account'}.`,
        hide: `Hiding posts from ${target.name || 'account'}.`, unhide: `Showing posts from ${target.name || 'account'} again.`,
      }[action])
      return true
    } catch {
      notify('Could not update this right now. Please try again.')
      return false
    } finally { setBlockBusy(false) }
  }

  const toggleFollow = async (targetId: string, targetName: string) => {
    if (!user || user.uid === targetId) return
    const isFol = following.has(targetId)
    setFollowBusy(targetId)
    try {
      const rel = doc(firestore, 'following', user.uid, 'list', targetId)
      const fol = doc(firestore, 'followers', targetId, 'list', user.uid)
      if (isFol) {
        const b = writeBatch(firestore)
        b.set(rel, { status: 'removed' }, { merge: true })
        b.set(fol, { status: 'removed' }, { merge: true })
        b.update(doc(firestore, 'users', targetId), { followersCount: increment(-1) })
        b.update(doc(firestore, 'users', user.uid), { followingCount: increment(-1) })
        await b.commit()
        setFollowing((c) => { const n = new Set(c); n.delete(targetId); return n })
        notify(`Unlinked ${targetName}.`)
      } else {
        const b = writeBatch(firestore)
        b.set(rel, { uid: targetId, status: 'active', createdAt: serverTimestamp() })
        b.set(fol, { uid: user.uid, status: 'active', createdAt: serverTimestamp() })
        b.update(doc(firestore, 'users', targetId), { followersCount: increment(1) })
        b.update(doc(firestore, 'users', user.uid), { followingCount: increment(1) })
        await b.commit()
        setFollowing((c) => new Set(c).add(targetId))
        notify(`Linked to ${targetName}.`)
      }
    } catch { notify('Link action failed.') } finally { setFollowBusy(null) }
  }

  // Posts/stories store a COPY of the author's name/avatar at the time they were
  // created (so the feed doesn't need to fetch every author's live profile just to
  // render a card). That means changing your profile picture or name previously only
  // updated the Profile screen - every post you'd already shared kept showing the old
  // one. This backfills every existing post + story you authored right after a
  // successful profile save, so they all pick up the new avatar/name immediately.
  const syncAuthorInfoAcrossContent = async (uid: string, patch: { authorName?: string; authorAvatar?: string }) => {
    if (!patch.authorName && !patch.authorAvatar) return
    try {
      const [postsSnap, storiesSnap] = await Promise.all([
        getDocs(query(collection(firestore, 'posts'), where('authorId', '==', uid))),
        getDocs(query(collection(firestore, 'stories'), where('authorId', '==', uid))),
      ])
      const refs = [...postsSnap.docs.map((d) => doc(firestore, 'posts', d.id)), ...storiesSnap.docs.map((d) => doc(firestore, 'stories', d.id))]
      // Firestore batches cap out at 500 writes - chunk just in case a very active
      // account has more posts + stories than that combined.
      for (let i = 0; i < refs.length; i += 450) {
        const b = writeBatch(firestore)
        for (const ref of refs.slice(i, i + 450)) b.update(ref, patch)
        await b.commit()
      }
    } catch (err) {
      console.warn('syncAuthorInfoAcrossContent failed', err)
    }
  }

  // Add friend: exact @username search (shared by the Add friend page)
  const searchPeople = async (e?: FormEvent) => {
    e?.preventDefault()
    if (!user) return
    const term = peopleSearch.trim().toLowerCase().replace(/^@/, '')
    if (term.length < 2) { notify('Type at least 2 characters.'); return }
    setBusy(true)
    try {
      const snap = await getDocs(query(collection(firestore, 'users'), where('usernameLower', '>=', term), where('usernameLower', '<=', `${term}\uf8ff`), limit(20)))
      // Same fix as Admin Console search: attach d.id as uid, or every result's
      // uid is undefined and Add/Follow silently breaks (writes to "..._undefined").
      const res = snap.docs.map((d) => ({ ...(d.data() as Profile), uid: d.id })).filter((p) => p.uid !== user.uid)
      setContacts(res)
      notify(`${res.length} operative${res.length === 1 ? '' : 's'} localized.`)
    } catch { notify('Search failed.') } finally { setBusy(false) }
  }

  // Profile cache: a profile you opened a moment ago re-opens INSTANTLY (and quietly refreshes); the user document and the posts load
  // at the same time instead of one after the other.
  const profileCacheRef = useRef<Map<string, { at: number; profile: any; posts: Post[] }>>(new Map())
  const openProfileByUid = async (uid: string) => {
    const cached = profileCacheRef.current.get(uid)
    if (cached) { setViewingProfile(cached.profile); setViewingProfilePosts(cached.posts); setViewingProfileLoading(false) }
    else { setViewingProfile(null); setViewingProfileLoading(true) }
    try {
      const userP = getDoc(doc(firestore, 'users', uid))
      const postsP = getDocs(query(collection(firestore, 'posts'), where('authorId', '==', uid), limit(30))).catch(() => null)
      const snap = await userP
      if (snap.exists()) {
        const d = snap.data()
        const deletionScheduledAt = typeof d.deletionScheduledAt === 'number' ? d.deletionScheduledAt : d.deletionScheduledAt?.toMillis?.()
        const isUnavailable = Boolean(d.deactivated) || Boolean(deletionScheduledAt) || isBanActive(d)
        if (!isUnavailable) rememberLiveAvatar(uid, d.avatar, false, d.equipped ?? null)
        const prof = profileFromUserDoc(uid, d, isUnavailable)
        setViewingProfile(prof)
        setViewingProfileLoading(false)
        if (isUnavailable) { setViewingProfilePosts([]); return }
        const postsSnap = await postsP
        const list = postsSnap ? postsSnap.docs.map((x: any) => ({ id: x.id, ...x.data() } as Post)) : (cached?.posts || [])
        setViewingProfilePosts(list)
        const m = profileCacheRef.current
        m.set(uid, { at: Date.now(), profile: prof, posts: list })
        if (m.size > 30) m.delete(m.keys().next().value as string)
      }
    } catch { if (!cached) notify('Could not load profile.') } finally { setViewingProfileLoading(false) }
  }

  // Tap on an @mention (chat bubble): look the handle up and open that person's profile (own handle -> my profile tab).
  const openProfileByHandle = async (handle: string) => {
    try {
      const snap = await getDoc(doc(firestore, 'usernames', handle.toLowerCase()))
      const uid = snap.exists() ? String(snap.data()?.uid || '') : ''
      if (!uid) { notify('User not found.'); return }
      if (user && uid === user.uid) { setActiveChat(null); setActiveTab('profile'); return }
      void openProfileByUid(uid)
    } catch { notify('Could not open profile.') }
  }

  // Opened from a shared link: /reel/<id> plays that reel, /@name opens that profile (the same real screens, no separate page).
  const deepLinkDoneRef = useRef(false)
  useEffect(() => {
    if (!user || !profile || deepLinkDoneRef.current) return
    deepLinkDoneRef.current = true
    const deep = parseDeepLink()
    if (deep.reel) {
      void (async () => {
        try {
          const snap = await getDoc(doc(firestore, 'posts', deep.reel!))
          if (snap.exists()) {
            const m = mapPostDoc(snap.id, snap.data())
            deepPostRef.current = m
            setPosts((cur) => (cur.some((p) => p.id === m.id) ? cur : [m, ...cur]))
          }
        } catch { /* private / removed: the normal feed opens */ }
        setFocusedReelId(deep.reel!)
        setActiveTab('reels')
      })()
    } else if (deep.user) {
      void (async () => {
        try {
          const snap = await getDoc(doc(firestore, 'usernames', deep.user!))
          const uid = snap.exists() ? String(snap.data()?.uid || '') : ''
          if (uid === user.uid) setActiveTab('profile')
          else if (uid) void openProfileByUid(uid)
        } catch { /* unknown username */ }
      })()
    }
  }, [user, profile])

  const openFollowList = async (uid: string, name: string, kind: 'followers' | 'following') => {
    setFollowListing({ kind, uid, name, items: [] })
    setFollowListingLoading(true)
    try {
      const snap = await getDocs(query(collection(firestore, kind, uid, 'list'), where('status', '==', 'active')))
      const list = await Promise.all(snap.docs.map(async (i) => {
        const u = await getDoc(doc(firestore, 'users', i.id))
        if (!u.exists()) return null
        const d = u.data()
        if (isBanActive(d)) return null
        return {
          uid: u.id,
          name: String(d.name || 'Traveler'),
          username: String(d.username || ''),
          usernameLower: '',
          email: '',
          avatar: String(d.avatar || fallbackAvatarSvg(d.name || 'Traveler')),
          bio: '',
        } as Profile
      }))
      setFollowListing({ kind, uid, name, items: list.filter(Boolean) as Profile[] })
    } catch { notify('Could not load list.') } finally { setFollowListingLoading(false) }
  }

  // Chat Actions
  const reactToMessage = async (chat: Chat, msg: Message, emoji: string, blast?: boolean) => {
    if (!user) return
    const chatId = chat.kind === 'group' ? chat.id : [user.uid, chat.id].sort().join('_')
    const ref = doc(firestore, 'chats', chatId, 'messages', msg.id)
    const reactions = msg.reactions || {}
    // A 3-second hold (blast) always ADDS the reaction - and tells the other person's chat to play the same shower.
    const isAlready = !blast && reactions[emoji]?.includes(user.uid)

    // Optimistic: flip the reaction locally right away, same reasoning as togglePostLike
    // above - without this the emoji only shows up once the write round-trips AND the next
    // chat poll tick lands (up to ~700ms), which reads as "the reaction is stuck/slow" even
    // though the write itself is fine. Reverted below if the write is actually rejected.
    const nextReactions = (r: Record<string, string[]> | undefined): Record<string, string[]> => {
      const out: Record<string, string[]> = {}
      Object.entries(r || {}).forEach(([k, v]) => { out[k] = (v || []).filter((u) => u !== user.uid) })
      if (!isAlready) out[emoji] = [...(out[emoji] || []), user.uid]
      Object.keys(out).forEach((k) => { if (out[k].length === 0) delete out[k] })
      return out
    }
    const applyLocal = (r: Record<string, string[]> | undefined, blastPatch?: Message['blast']) => {
      setMessages((prev) => prev.map((m) => m.id !== msg.id ? m : { ...m, reactions: r, ...(blastPatch !== undefined ? { blast: blastPatch } : {}) }))
    }
    const prevReactions = reactions
    const prevBlast = msg.blast
    applyLocal(nextReactions(reactions), blast ? { emoji, by: user.uid, at: Date.now() } : undefined)

    const updates: Record<string, unknown> = {}
    if (blast) updates.blast = { emoji, by: user.uid, at: Date.now() }
    Object.keys(reactions).forEach((k) => {
      if (k !== emoji && reactions[k]?.includes(user.uid)) updates[`reactions.${k}`] = arrayRemove(user.uid)
    })
    updates[`reactions.${emoji}`] = isAlready ? arrayRemove(user.uid) : arrayUnion(user.uid)
    try {
      await updateDoc(ref, updates)
    } catch {
      applyLocal(prevReactions, prevBlast) // revert
    }
  }

  const deleteMessageForMe = async (chat: Chat, msg: Message) => {
    if (!user) return
    const chatId = chat.kind === 'group' ? chat.id : [user.uid, chat.id].sort().join('_')
    // Optimistic: hide it locally right away instead of waiting on the write + next poll.
    setMessages((prev) => prev.filter((m) => m.id !== msg.id))
    try {
      await updateDoc(doc(firestore, 'chats', chatId, 'messages', msg.id), {
        deletedFor: arrayUnion(user.uid),
      })
      notify('Message removed.')
    } catch {
      setMessages((prev) => prev.some((m) => m.id === msg.id) ? prev : [...prev, msg].sort((a, b) => (a.time || '').localeCompare(b.time || '')))
      notify('Could not delete message.')
    }
  }

  const unsendMessage = async (chat: Chat, msg: Message) => {
    if (!user || !msg.own) return
    const chatId = chat.kind === 'group' ? chat.id : [user.uid, chat.id].sort().join('_')
    const prev = { text: msg.text, sharedImageUrl: msg.sharedImageUrl, audioUrl: msg.audioUrl, deleted: msg.deleted }
    // Optimistic: show it as unsent right away instead of waiting on the write + next poll.
    setMessages((p) => p.map((m) => m.id !== msg.id ? m : { ...m, deleted: true, text: '', sharedImageUrl: undefined, audioUrl: undefined }))
    try {
      await updateDoc(doc(firestore, 'chats', chatId, 'messages', msg.id), {
        deleted: true,
        text: '',
        sharedImageUrl: null,
        audioUrl: null
      })
      notify('Message unsent.')
    } catch {
      setMessages((p) => p.map((m) => m.id !== msg.id ? m : { ...m, ...prev }))
      notify('Could not unsend message.')
    }
  }

  const typingWriteRef = useRef<{ lastSentTyping: boolean; lastSentAt: number; timer: ReturnType<typeof setTimeout> | null }>({ lastSentTyping: false, lastSentAt: 0, timer: null })
  const handleTypingChange = (val: string) => {
    setMessage(val)
    if (!user || !activeChat) return
    const chatId = activeChat.kind === 'group' ? activeChat.id : [user.uid, activeChat.id].sort().join('_')
    const isTyping = val.trim().length > 0
    const writeNow = () => {
      const st = typingWriteRef.current
      st.lastSentTyping = isTyping
      st.lastSentAt = Date.now()
      void setDoc(doc(firestore, 'typing', chatId, 'users', user.uid), {
        typing: isTyping,
        name: profile?.name || 'Someone',
        updatedAt: serverTimestamp()
      }, { merge: true }).catch(() => undefined)
    }
    const st = typingWriteRef.current
    if (st.timer) { clearTimeout(st.timer); st.timer = null }
    // The "typing" state flipping (started / stopped typing) matters most - send that instantly.
    // While it stays true across a burst of keystrokes, only refresh it every ~1.2s instead of per key.
    if (isTyping !== st.lastSentTyping || Date.now() - st.lastSentAt > 1200) {
      writeNow()
    } else {
      st.timer = setTimeout(writeNow, 400)
    }
  }

  const sendMessage = async (
    e?: FormEvent,
    extra?: { textOverride?: string; sharedImageUrl?: string; sharedPostId?: string; audioUrl?: string; audioDuration?: number; viewOnce?: boolean; sticker?: boolean }
  ) => {
    e?.preventDefault()
    const chatBanMsg = chatBanMessage()
    if (chatBanMsg) { notify(chatBanMsg); return }
    let text = extra?.textOverride !== undefined ? extra.textOverride : draftRef.current.trim()

    if (editingMsg) {
      if (!user || !activeChat || !text) return
      const chatId = activeChat.kind === 'group' ? activeChat.id : [user.uid, activeChat.id].sort().join('_')
      // Only plain text messages can be edited, and each message can be edited at most MAX_MSG_EDITS (2) times.
      const used = editingMsg.editCount ?? (editingMsg.edited ? 1 : 0)
      if (editingMsg.sharedImageUrl || editingMsg.audioUrl || editingMsg.sticker || editingMsg.viewOnce || editingMsg.deleted) { notify('Only text messages can be edited.'); setMessage(''); setEditingMsg(null); return }
      if (used >= MAX_MSG_EDITS) { notify('Edit limit reached - a message can be edited only 2 times.'); setMessage(''); setEditingMsg(null); return }
      if (editingMsg.id.startsWith('pending-') || editingMsg.id.startsWith('local-')) { notify('Message is still sending - try editing again in a moment.'); return }
      if (text === editingMsg.text) { setMessage(''); setEditingMsg(null); return }
      const editedId = editingMsg.id
      try {
        await updateDoc(doc(firestore, 'chats', chatId, 'messages', editedId), {
          text,
          edited: true,
          editCount: used + 1
        })
        setMessages((prev) => prev.map((m) => m.id === editedId ? { ...m, text, edited: true, editCount: used + 1 } : m))
        setMessage('')
        draftRef.current = ''
        setEditingMsg(null)
      } catch (err) {
        const code = (err as { code?: string })?.code
        notify(code === 'permission-denied' ? 'You cannot edit this message (edit limit reached or not your message).' : code === 'not-found' ? 'Message not found - it may have been deleted.' : 'Failed to edit message. Check your connection and try again.')
      }
      return
    }

    if (!user || !activeChat || (!text && !extra?.sharedImageUrl && !extra?.audioUrl)) return
    if (activeChat.kind !== 'group' && bannedUids.has(activeChat.id)) { notify('This account is unavailable.'); return }
    // Message requests: a non-friend gets ONE message until they accept; a pending request I received must be accepted before I can reply.
    const dmOther = activeChat.kind !== 'group' ? activeChat.id : ''
    const isFriendChat = !!dmOther && allFriends.some((f) => f.id === dmOther)
    const outReq = dmOther ? dmOut.find((r) => r.otherId === dmOther) : undefined
    const inReq = dmOther ? dmIn.find((r) => r.otherId === dmOther) : undefined
    if (dmOther && !isFriendChat && inReq?.status !== 'accepted' && outReq?.status !== 'accepted') {
      if (inReq?.status === 'pending') { notify('Accept this request first to reply.'); return }
      if (outReq) { notify(`You can message again once ${activeChat.name} accepts your request.`); return }
    }
    if (!extra?.sticker) { setMessage(''); handleTypingChange('') } // a sticker leaves the typed draft alone
    playSendSound() // soft chime the instant Send is tapped, not once the write round-trips
    const replying = replyTarget
    const replyPrev = replying ? replyPreviewOf(replying) : null
    setReplyTarget(null)

    const chatId = activeChat.kind === 'group' ? activeChat.id : [user.uid, activeChat.id].sort().join('_')

    // Optimistic local echo: without this, the bubble only appears once the write round-trips
    // to the server AND the active-chat poll (up to ~700ms) picks it up - which reads as "slow"
    // even on a fast connection, since it's two network hops instead of an instant local update.
    // The real message replaces this the instant the next poll tick lands (which the write
    // below triggers right away via pokeListeners), so this is only visible for a moment.
    const tempId = `pending-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
    // Record this optimistic send so the listener above can recognize the real Firestore
    // doc that replaces it and skip replaying the pop-in animation for it.
    const pendingSendEntry = { text, at: Date.now(), matched: false }
    pendingOwnSendsRef.current.push(pendingSendEntry)
    setMessages((prev) => [...prev, {
      id: tempId,
      text,
      own: true,
      read: false,
      senderId: user.uid,
      senderName: profile?.name || 'Friend',
      senderAvatar: profile?.avatar || '',
      sharedImageUrl: extra?.sharedImageUrl,
      sharedPostId: extra?.sharedPostId,
      sharedMediaType: extra?.sharedImageUrl ? 'image' : undefined,
      audioUrl: extra?.audioUrl,
      audioDuration: extra?.audioDuration,
      time: formatTime(),
      at: Date.now(),
      replyToText: replyPrev ? replyPrev.text : undefined,
      replyToSender: replying ? (replying.senderName || 'Friend') : undefined,
      replyToImage: replyPrev?.image,
      replyToId: replying && !replying.id.startsWith('pending-') ? replying.id : undefined,
      viewOnce: Boolean(extra?.viewOnce),
      sticker: Boolean(extra?.sticker),
    }])

    const previewText = extra?.sticker ? '✨ Sticker' : extra?.viewOnce ? '👁️ View Once Photo' : extra?.audioUrl ? '🎵 Voice note' : extra?.sharedImageUrl ? '📷 Photo' : text
    // Writes the message. For a non-friend with no accepted request yet, the message and its dmRequests doc are
    // committed together in one batch (the server rules only allow that ONE first message).
    const deliverMessage = async (payload: Record<string, any>): Promise<{ id: string }> => {
      const msgRef = doc(collection(firestore, 'chats', chatId, 'messages'))
      const needsRequestDoc = !!dmOther && !isFriendChat && inReq?.status !== 'accepted' && outReq?.status !== 'accepted'
      const landed = async () => (await getDoc(msgRef)).exists()
      if (!needsRequestDoc) { await sendWithRetry(() => setDoc(msgRef, payload), landed); return msgRef }
      const reqId = `${user.uid}_${dmOther}`
      const commitReq = async (status: 'pending' | 'accepted') => {
        const b = writeBatch(firestore)
        b.set(doc(firestore, 'dmRequests', reqId), { from: user.uid, to: dmOther, status, lastMessage: previewText.slice(0, 500), lastMessageAt: serverTimestamp(), createdAt: serverTimestamp() })
        b.set(msgRef, payload)
        await b.commit()
        setDmOut((cur) => [...cur.filter((r) => r.id !== reqId), {
          id: reqId, from: user.uid, to: dmOther, otherId: dmOther, status, lastMessage: previewText.slice(0, 500),
          sortAt: Date.now(), time: formatTime(), name: activeChat.name, handle: activeChat.handle, avatar: activeChat.avatar,
        }])
      }
      // Non-friend first message ALWAYS lands in the recipient's Requests folder (following someone is not a "contact").
      await sendWithRetry(() => commitReq('pending'), landed)
      notify('Message request sent. They will see this chat once they accept.')
      return msgRef
    }
    let sentMsgRef: { id: string } | null = null
    try {
      // Only the actual message write can fail the send. Everything after this point
      // (chat-list preview, achievement counters, @mention pings) is best-effort follow-up:
      // it used to be sequentially awaited INSIDE this try block, so a hiccup on any one of
      // those unrelated writes would hit the catch below and roll back a message that had
      // already sent successfully - the bubble vanished, the text came back, and resending
      // created a duplicate on the server. None of that follow-up work should block the
      // send from resolving, or be able to make an already-sent message look "failed".
      sentMsgRef = await deliverMessage({
        text,
        userId: user.uid,
        senderName: profile?.name || 'Friend',
        senderAvatar: profile?.avatar || '',
        read: false,
        createdAt: serverTimestamp(),
        ...(extra?.sharedImageUrl ? { sharedImageUrl: extra.sharedImageUrl } : {}),
        ...(extra?.sharedPostId ? { sharedPostId: extra.sharedPostId } : {}),
        ...(extra?.audioUrl ? { audioUrl: extra.audioUrl, audioDuration: extra.audioDuration } : {}),
        ...(extra?.viewOnce ? { viewOnce: true, viewedBy: [] } : {}),
        ...(extra?.sticker ? { sticker: true } : {}),
        ...(replying && replyPrev ? { replyToText: replyPrev.text, replyToSender: replying.senderName || 'Friend', ...(replyPrev.image ? { replyToImage: replyPrev.image } : {}), ...(!replying.id.startsWith('pending-') ? { replyToId: replying.id } : {}) } : {}),
      })
    } catch (err) {
      // Roll back the optimistic bubble and give the text back so the person can retry.
      setMessages((prev) => prev.filter((m) => m.id !== tempId))
      pendingOwnSendsRef.current = pendingOwnSendsRef.current.filter((p) => p !== pendingSendEntry)
      setMessage(text)
      // Say WHY when the server gave a real reason (rate limit, restriction); otherwise the connection was the problem.
      const why = String((err as any)?.message || '')
      notify(/too fast|slow down|restricted|banned|unavailable/i.test(why) && !/network/i.test(why) ? why : isTransientSendError(err) ? 'Message not sent - check your connection and try again.' : 'Message send failed.')
      return
    }

    // Achievement counters (best-effort - never blocks or fails a send).
    updateDoc(doc(firestore, 'users', user.uid), {
      messagesSent: increment(1),
      ...(activeChat.kind === 'group' ? { groupMessagesSent: increment(1) } : {}),
    }).catch(() => undefined)
    // Snap rewards removed: sending a snap no longer gives coins.
    const preview = previewText
    if (activeChat.kind === 'group') {
      updateDoc(doc(firestore, 'groupChats', activeChat.id), { lastMessage: preview, lastMessageAt: serverTimestamp() }).catch(() => undefined)
      // @mentions in a group message - only notify uids who are actually members of
      // THIS group (tagging someone not in the group shouldn't page them).
      if (text) {
        const members = new Set(activeChat.members || [])
        resolveMentions(text).then((uids) => {
          for (const uid of uids) {
            if (!members.has(uid)) continue
            void sendAppNotification(uid, 'mention', `mentioned you in ${activeChat.name}`, activeChat.id)
          }
        })
      }
    } else if (isFriendChat) {
      setDoc(doc(firestore, 'friends', user.uid, 'list', activeChat.id), { lastMessage: preview, lastMessageAt: serverTimestamp() }, { merge: true }).catch(() => undefined)
      setDoc(doc(firestore, 'friends', activeChat.id, 'list', user.uid), { lastMessage: preview, lastMessageAt: serverTimestamp() }, { merge: true }).catch(() => undefined)
    } else {
      // Non-friend chat (accepted request): keep the chat-list preview on the request doc. A brand-new request doc already carries it.
      const acceptedId = outReq?.status === 'accepted' ? outReq.id : inReq?.status === 'accepted' ? inReq.id : ''
      if (acceptedId) updateDoc(doc(firestore, 'dmRequests', acceptedId), { lastMessage: preview.slice(0, 500), lastMessageAt: serverTimestamp() }).catch(() => undefined)
    }
  }

  // ---- Ludo (components/ludo.tsx, app/api/ludo/route.ts) ----
  // After a friend / group room is created the hub calls this: an invite bubble (with a Join button) is dropped into the
  // 1:1 chat of every invited friend, or into the group chat when playing with a guild.
  const postLudoInvite = (targets: LudoFriend[], info: LudoInviteInfo, chatId?: string) => {
    if (!user) return
    const base = { text: '🎲 Ludo invite', userId: user.uid, senderName: profile?.name || 'Friend', senderAvatar: profile?.avatar || '', read: false, createdAt: serverTimestamp(), ludo: { roomId: info.roomId, code: info.code, stake: info.stake, size: info.size } }
    const preview = info.stake > 0 ? `🎲 Ludo invite · ${info.stake} coins` : '🎲 Ludo invite'
    if (chatId) {
      void setDoc(doc(collection(firestore, 'chats', chatId, 'messages')), base).catch(() => notify('Could not post the invite in the group.'))
      void updateDoc(doc(firestore, 'groupChats', chatId), { lastMessage: preview, lastMessageAt: serverTimestamp() }).catch(() => undefined)
    }
    for (const f of targets) {
      const cid = [user.uid, f.id].sort().join('_')
      void setDoc(doc(collection(firestore, 'chats', cid, 'messages')), base).catch(() => notify(`Could not send the invite to ${f.name}.`))
      void setDoc(doc(firestore, 'friends', user.uid, 'list', f.id), { lastMessage: preview, lastMessageAt: serverTimestamp() }, { merge: true }).catch(() => undefined)
      void setDoc(doc(firestore, 'friends', f.id, 'list', user.uid), { lastMessage: preview, lastMessageAt: serverTimestamp() }, { merge: true }).catch(() => undefined)
    }
    if (targets.length || chatId) notify('Invite sent!')
  }
  // Chess (components/chess.tsx, app/api/chess/route.ts): a challenge bubble (with a Join button) goes into the friend's 1:1 chat.
  const postChessInvite = (friend: ChessFriend, info: ChessInviteInfo) => {
    if (!user) return
    const base = { text: '♟️ Chess challenge', userId: user.uid, senderName: profile?.name || 'Friend', senderAvatar: profile?.avatar || '', read: false, createdAt: serverTimestamp(), chess: { roomId: info.roomId, code: info.code, stake: info.stake, tc: info.tc } }
    const preview = info.stake > 0 ? `♟️ Chess challenge · ${info.stake} coins` : '♟️ Chess challenge'
    const cid = [user.uid, friend.id].sort().join('_')
    void setDoc(doc(collection(firestore, 'chats', cid, 'messages')), base).catch(() => notify(`Could not send the challenge to ${friend.name}.`))
    void setDoc(doc(firestore, 'friends', user.uid, 'list', friend.id), { lastMessage: preview, lastMessageAt: serverTimestamp() }, { merge: true }).catch(() => undefined)
    void setDoc(doc(firestore, 'friends', friend.id, 'list', user.uid), { lastMessage: preview, lastMessageAt: serverTimestamp() }, { merge: true }).catch(() => undefined)
    notify('Challenge sent!')
  }
  // Chat poll: a normal message with a `poll` object (rules validate 2-6 short options). Votes are separate docs (see PollBubble).
  const sendPoll = (question: string, options: string[]) => {
    if (!user || !activeChat) return
    const isGroup = activeChat.kind === 'group'
    const cid = isGroup ? activeChat.id : [user.uid, activeChat.id].sort().join('_')
    const preview = `📊 ${question}`.slice(0, 120)
    const base = { text: preview, userId: user.uid, senderName: profile?.name || 'Friend', senderAvatar: profile?.avatar || '', read: false, createdAt: serverTimestamp(), poll: { question, options } }
    void setDoc(doc(collection(firestore, 'chats', cid, 'messages')), base).catch(() => notify('Could not send the poll.'))
    if (isGroup) {
      updateDoc(doc(firestore, 'groupChats', activeChat.id), { lastMessage: preview, lastMessageAt: serverTimestamp() }).catch(() => undefined)
    } else {
      void setDoc(doc(firestore, 'friends', user.uid, 'list', activeChat.id), { lastMessage: preview, lastMessageAt: serverTimestamp() }, { merge: true }).catch(() => undefined)
      void setDoc(doc(firestore, 'friends', activeChat.id, 'list', user.uid), { lastMessage: preview, lastMessageAt: serverTimestamp() }, { merge: true }).catch(() => undefined)
    }
  }
  // Carrom (components/carrom.tsx, app/api/carrom/route.ts): same challenge bubble as Chess.
  const postCarromInvite = (friend: CarromFriend, info: CarromInviteInfo) => {
    if (!user) return
    const base = { text: '🎯 Carrom challenge', userId: user.uid, senderName: profile?.name || 'Friend', senderAvatar: profile?.avatar || '', read: false, createdAt: serverTimestamp(), carrom: { roomId: info.roomId, code: info.code, stake: info.stake, size: info.size } }
    const preview = info.stake > 0 ? `🎯 Carrom challenge · ${info.stake} coins` : '🎯 Carrom challenge'
    const cid = [user.uid, friend.id].sort().join('_')
    void setDoc(doc(collection(firestore, 'chats', cid, 'messages')), base).catch(() => notify(`Could not send the challenge to ${friend.name}.`))
    void setDoc(doc(firestore, 'friends', user.uid, 'list', friend.id), { lastMessage: preview, lastMessageAt: serverTimestamp() }, { merge: true }).catch(() => undefined)
    void setDoc(doc(firestore, 'friends', friend.id, 'list', user.uid), { lastMessage: preview, lastMessageAt: serverTimestamp() }, { merge: true }).catch(() => undefined)
    notify('Challenge sent!')
  }
  // "Add friend" button on players you met in a Ludo game - same request as Discover -> Add.
  const ludoAddFriend = async (targetUid: string, targetName: string) => {
    if (!user) return
    const ok = await connectWith({ uid: targetUid, name: targetName }, `Friend request sent to ${targetName}.`)
    if (!ok) throw new Error('request-failed')
  }

  // ---- Stickers: "My stickers" are stored on the person's own user doc (users/{uid}.savedStickers) ----
  // Free accounts can keep FREE_STICKER_LIMIT stickers; an active Green Tick raises it.
  const stickerLimit = isPremiumActive ? PREMIUM_STICKER_LIMIT : FREE_STICKER_LIMIT
  // Guild size works the same way: FREE_GUILD_MEMBER_LIMIT, or PREMIUM_GUILD_MEMBER_LIMIT with the
  // tick, PLUS any +GROUP_MEMBER_BONUS_INCREMENT blocks bought (profile.groupMemberBonus - a
  // permanent account entitlement, see lib/premium-plans.ts and lib/d1-rules.ts).
  const groupMemberBonus = profile?.groupMemberBonus || 0
  const guildMemberLimit = (isPremiumActive ? PREMIUM_GUILD_MEMBER_LIMIT : FREE_GUILD_MEMBER_LIMIT) + groupMemberBonus * GROUP_MEMBER_BONUS_INCREMENT
  const guildLimitReached = () => {
    notify(`A guild can have up to ${guildMemberLimit} members. Buy +${GROUP_MEMBER_BONUS_INCREMENT} more from the group's info screen.`)
  }
  const stickerLimitReached = () => {
    if (isPremiumActive) {
      notify(`Sticker limit reached (${stickerLimit}). Remove one first.`)
    } else {
      notify(`Free limit is ${FREE_STICKER_LIMIT} stickers - get the Green Tick for up to ${PREMIUM_STICKER_LIMIT}.`)
      setShowTickSheet(true)
    }
  }
  const saveStickerUrl = async (url: string) => {
    if (!user || !url) return
    const mine = profile?.savedStickers || []
    if (mine.includes(url)) { notify('Already in your stickers.'); return }
    if (mine.length >= stickerLimit) { stickerLimitReached(); return }
    try {
      await updateDoc(doc(firestore, 'users', user.uid), { savedStickers: arrayUnion(url) })
      notify('Sticker saved!')
    } catch { notify('Could not save sticker.') }
  }

  const removeSavedSticker = async (url: string) => {
    if (!user) return
    try {
      await updateDoc(doc(firestore, 'users', user.uid), { savedStickers: arrayRemove(url) })
    } catch { notify('Could not remove sticker.') }
  }

  const addOwnSticker = async (file: File) => {
    if (!user) return
    if (!file.type.startsWith('image/')) { notify('Please pick an image for your sticker.'); return }
    if (file.size > 5 * 1024 * 1024) { notify('Sticker image must be under 5 MB.'); return }
    if ((profile?.savedStickers || []).length >= stickerLimit) { stickerLimitReached(); return }
    try {
      const url = await uploadWithProgress(file, 'chat', user.uid)
      await updateDoc(doc(firestore, 'users', user.uid), { savedStickers: arrayUnion(url) })
      notify('Sticker added!')
    } catch { notify('Could not add sticker.') }
  }

  // Relay Post to Chat
  // Forward a live story into a chat, same relay pattern as sharePostToChat but reading the
  // story's own fields (mediaUrl, not imageUrl) and counting the share on the `stories` doc.
  const shareStoryToChat = async (targetChat: Chat, storyToShare: Story) => {
    if (!user) return
    const chatId = targetChat.kind === 'group' ? targetChat.id : [user.uid, targetChat.id].sort().join('_')
    try {
      await addDoc(collection(firestore, 'chats', chatId, 'messages'), {
        text: storyToShare.caption ? `Shared a story: ${storyToShare.caption}` : 'Shared a story',
        sharedImageUrl: storyToShare.mediaUrl,
        sharedMediaType: storyToShare.mediaType === 'video' ? 'video' : 'image',
        sharedStoryId: storyToShare.id,
        sharedAuthorId: storyToShare.authorId,
        sharedAuthorName: storyToShare.authorName || '',
        sharedAuthorAvatar: storyToShare.authorAvatar || '',
        userId: user.uid,
        senderName: profile?.name || 'Friend',
        senderAvatar: profile?.avatar || '',
        read: false,
        createdAt: serverTimestamp(),
      })
      if (storyToShare.id) {
        try { await updateDoc(doc(firestore, 'stories', storyToShare.id), { shareCount: increment(1) }) } catch { /* best-effort */ }
      }
      notify(`Shared to ${targetChat.name}!`)
      setSharingStory(null)
    } catch {
      notify('Failed to share story.')
    }
  }

  const sharePostToChat = async (targetChat: Chat, postToShare: Post) => {
    if (!user) return
    const chatId = targetChat.kind === 'group' ? targetChat.id : [user.uid, targetChat.id].sort().join('_')
    try {
      await addDoc(collection(firestore, 'chats', chatId, 'messages'), {
        text: postToShare.caption
          ? `Shared ${postToShare.mediaType === 'video' ? 'reel' : 'post'}: ${postToShare.caption}`
          : `Shared a ${postToShare.mediaType === 'video' ? 'reel' : 'post'} transmission`,
        sharedImageUrl: postToShare.imageUrl,
        // Real cover frame (same one the feed/reel player already uses) so the chat bubble
        // shows the actual reel frame instead of a grey placeholder while the <video> loads.
        ...(postToShare.thumbnailUrl ? { sharedThumbnailUrl: postToShare.thumbnailUrl } : {}),
        sharedPostId: postToShare.id,
        sharedMediaType: postToShare.mediaType === 'video' ? 'video' : 'image',
        // Who made the post: the chat card shows their profile pic + @username on the thumbnail.
        sharedAuthorId: postToShare.authorId,
        sharedAuthorName: postToShare.authorName || '',
        sharedAuthorAvatar: postToShare.authorAvatar || '',
        userId: user.uid,
        senderName: profile?.name || 'Friend',
        senderAvatar: profile?.avatar || '',
        read: false,
        createdAt: serverTimestamp(),
      })
      void bumpShareCount(postToShare)
      notify(`Shared to ${targetChat.name}!`)
      setSharingPost(null)
    } catch {
      notify('Failed to share post.')
    }
  }

  // Runs only inside the Android app (built with Capacitor). Google blocks OAuth
  // sign-in inside embedded WebViews entirely (disallowed_useragent), so popup/
  // redirect can never work there - a real native Google account chooser is
  // required. We call the native FirebaseAuthentication plugin through the
  // window.Capacitor bridge, which Capacitor injects automatically into this
  // page at runtime (no npm import needed on the web side). skipNativeAuth is
  // set to true in capacitor.config.json, so the plugin only performs the
  // native Google sign-in and hands us a Google ID token - we then finish the
  // sign-in ourselves on the JS Firebase SDK, same as the web popup path.
  const nativeGoogleLogin = async (): Promise<string> => {
    const cap = (window as any).Capacitor
    // The bridge can take a moment to expose the plugin right after launch (slow phones): wait up to ~4s instead of failing at once.
    let plugin = cap?.Plugins?.FirebaseAuthentication
    for (let i = 0; !plugin && i < 16; i++) { await new Promise((r) => setTimeout(r, 250)); plugin = cap?.Plugins?.FirebaseAuthentication }
    if (!plugin) throw new Error('Google sign-in is not available in this app build. Please update the app, or sign up with email below.')

    const isCancel = (e: any) => {
      const t = `${e?.code || ''} ${e?.message || ''}`.toLowerCase()
      return /cancel|12501|dismiss|closed by user|user_cancel/.test(t)
    }
    // Never leave the button spinning forever: some phones never answer the native call.
    const withTimeout = <T,>(pr: Promise<T>, ms: number) =>
      Promise.race([pr, new Promise<T>((_, rej) => setTimeout(() => rej(new Error('timeout')), ms))])

    // Attempt 1: Android Credential Manager (newer, default). Attempt 2: the classic Google Sign-In dialog,
    // which still works on phones where Credential Manager / Google Play services are old, partial or "not supported".
    let lastErr: any = null
    for (const useCredentialManager of [true, false]) {
      try {
        const result: any = await withTimeout<any>(plugin.signInWithGoogle({ useCredentialManager }), 40000)
        const idToken = result?.credential?.idToken
        if (idToken) return idToken
        lastErr = new Error('no-token')
      } catch (e: any) {
        if (isCancel(e)) throw new Error('Sign-in was cancelled. Tap "Continue with Google" to try again.')
        lastErr = e
      }
    }
    const raw = `${lastErr?.code || ''} ${lastErr?.message || ''}`.toLowerCase()
    if (/timeout/.test(raw)) throw new Error('Google did not respond. Check your internet, then try again - or sign up with email below.')
    if (/network|connect/.test(raw)) throw new Error('Network problem while contacting Google. Check your internet and try again.')
    if (/play services|play-services|unavailable|not supported|unsupported|credential|no credential|manager|10:|developer_error|12500/.test(raw))
      throw new Error('Google sign-in is not working on this phone. Open Settings > Google > make sure a Google account is added and Google Play services is updated (or update it in the Play Store), then try again. You can also sign up with email below.')
    throw new Error('Google sign-in failed on this phone. Please try again, or sign up with email below.')
  }

  const loginWithGoogle = async () => {
    if (loginBusy) return
    setLoginBusy(true)
    setLoginError('')
    const host = typeof window !== 'undefined' ? window.location.hostname : ''
    const isNativeApp = typeof window !== 'undefined' && !!(window as any).Capacitor?.isNativePlatform?.()
    // Safety net: if a mobile browser never reports the pop-up closing, unlock the button.
    const unlockTimer = setTimeout(() => setLoginBusy(false), 45000)
    // Persistence is best-effort - if the browser refuses it, still try to sign in.
    try { await setPersistence(firebaseAuth, browserLocalPersistence) } catch { /* ignore */ }

    if (isNativeApp) {
      try {
        const idToken = await nativeGoogleLogin()
        const credential = GoogleAuthProvider.credential(idToken)
        await signInWithCredential(firebaseAuth, credential)
        try { localStorage.removeItem('animora_google_fails') } catch { /* ignore */ }
      } catch (err: any) {
        const msg = friendlyError(err, 'Google sign-in failed.')
        setLoginError(msg)
        notify(msg)
      } finally {
        clearTimeout(unlockTimer)
        setLoginBusy(false)
      }
      return
    }

    const provider = new GoogleAuthProvider()
    provider.setCustomParameters({ prompt: 'select_account' })
    try {
      await signInWithPopup(firebaseAuth, provider)
      try { localStorage.removeItem('animora_google_fails') } catch { /* ignore */ }
    } catch (err: any) {
      const code: string = err?.code || ''
      // Note: signInWithRedirect is intentionally NOT used as a fallback here.
      // Inside Android WebViews (and other storage-partitioned browser contexts),
      // the redirect flow loses sessionStorage across the navigation, which
      // triggers "missing initial state" errors on return. Popup-only avoids this.
      if (code === 'auth/popup-blocked' || code === 'auth/operation-not-supported-in-this-environment') {
        const msg = 'Your browser blocked the Google sign-in popup. Please allow pop-ups for this site and try again.'
        setLoginError(msg)
        notify(msg)
      } else {
        const msg = describeAuthError(err, host)
        setLoginError(msg)
        if (code !== 'auth/popup-closed-by-user' && code !== 'auth/cancelled-popup-request') notify('Google sign-in failed.')
      }
    } finally {
      clearTimeout(unlockTimer)
      setLoginBusy(false)
    }
  }

  // Sends the date of birth to the server (the only place the age band is decided/stored).
  const submitDob = async (dob: string): Promise<{ ok: true } | { ok: false; error: string }> => {
    if (!user) return { ok: false, error: 'Session expired. Please sign in again.' }
    try {
      let deviceHash = ''
      try { deviceHash = (await getDeviceHash()) || '' } catch { /* fingerprint optional */ }
      const res = await fetch('/api/account/age', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${await user.getIdToken()}` },
        body: JSON.stringify({ dob, deviceHash }),
      })
      const d = await res.json().catch(() => ({}))
      if (res.ok) { setAgeStatus('ok'); return { ok: true } }
      if (d?.error === 'under-age') setAgeStatus('under-age')
      return { ok: false, error: d?.message || 'Could not verify your age. Please try again.' }
    } catch {
      return { ok: false, error: 'Could not verify your age. Please check your connection and try again.' }
    }
  }

  // Finish account creation for a brand-new Google sign-in (no Firestore profile yet).
  // Returns { ok:false, error } instead of throwing so the setup screen can show a real reason.
  const createAccount = async (data: { name: string; username: string; bio: string; gender: 'male' | 'female'; avatar: string; dob: string; refCode?: string }) => {
    if (!user) return { ok: false, error: 'Session expired. Please sign in again.' }
    setBusy(true)
    try {
      const usernameLower = data.username.trim().toLowerCase()
      const policy = checkUsername(data.username)
      if (!policy.ok) return { ok: false, error: policy.message }
      if (data.username.trim().length <= PREMIUM_USERNAME_MAX_LENGTH) return { ok: false, error: `Usernames with 1-${PREMIUM_USERNAME_MAX_LENGTH} characters are premium. Pick a username with ${PREMIUM_USERNAME_MAX_LENGTH + 1} or more characters now - you can get a short one later from Edit profile.` }

      // Belt-and-braces copy of the check UsernameSetupModal already gates its form on -
      // an email/password account must have a verified email before a profile is created.
      const isPasswordAccount = !!user.providerData?.some((p: any) => p?.providerId === 'password')
      if (isPasswordAccount && !user.emailVerified) {
        return { ok: false, error: 'Please verify your email first, then try again.' }
      }

      // Make sure the ID token carries email_verified=true (a token minted before the user verified
      // their email is cached for up to an hour and makes the profile write fail with permission-denied).
      if (isPasswordAccount) {
        try { await user.reload(); await user.getIdToken(true) } catch { /* fall through */ }
      }

      // Age gate: the server checks the date of birth BEFORE any profile is created. Under 13 = no account.
      const ageRes = await submitDob(data.dob)
      if (!ageRes.ok) return { ok: false, error: ageRes.error }

      // Device lock: block this device from originating a second live account. Runs before
      // the profile is created so a blocked device never gets even a half-created account.
      // See app/api/account/device/route.ts.
      try {
        const deviceHash = await getDeviceHash()
        if (deviceHash) {
          const idToken = await user.getIdToken()
          const res = await fetch('/api/account/device', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
            body: JSON.stringify({ deviceHash }),
          })
          if (!res.ok) {
            const d = await res.json().catch(() => ({}))
            // Only a real per-install limit blocks. The shared-network (IP) limit never blocks a real person: carriers put thousands of phones behind one IP.
            if ((d?.error === 'device-locked' || d?.error === 'device-limit-reached') && d?.reason !== 'ip') {
              return { ok: false, error: d.message || 'This device already has an Animora account. Please delete that account first.' }
            }
            // Any other failure (network hiccup, etc.) - don't hard-block signup over it.
          }
        }
      } catch { /* fingerprinting unsupported/blocked - don't hard-block signup over it */ }

      const newProfileData = {
        name: data.name.trim(),
        username: data.username.trim(),
        usernameLower,
        avatar: data.avatar,
        bio: data.bio.trim(),
        gender: data.gender,
        followersCount: 0,
        followingCount: 0,
        chatBackground: 'none',
        private: false,
        isAdmin: isAdminAccount,
        createdAt: serverTimestamp(),
      }
      // Never overwrite an account that already exists (e.g. a double tap, or a failed load
      // that sent a returning user here) - just load it.
      let alreadyExists = false
      try { alreadyExists = (await getDoc(doc(firestore, 'users', user.uid))).exists() } catch { /* fall through to create */ }
      if (alreadyExists) {
        window.location.reload()
        return { ok: true }
      }
      // Claiming the username and creating the profile happen in ONE transaction: if
      // someone else's signup/rename claims this exact usernameLower in the same instant,
      // Firestore's transaction retry sees the now-existing reservation and this whole
      // attempt fails cleanly - it can't ever silently create a duplicate.
      const usernameRef = doc(firestore, 'usernames', usernameLower)
      try {
        await runTransaction(firestore, async (tx) => {
          const existing = await tx.get(usernameRef)
          if (existing.exists()) throw new Error('username-taken')
          tx.set(usernameRef, { uid: user.uid, createdAt: serverTimestamp() })
          tx.set(doc(firestore, 'users', user.uid), newProfileData)
        })
      } catch (err) {
        if (err instanceof Error && err.message === 'username-taken') {
          return { ok: false, error: 'That username is already taken. Try another one.' }
        }
        throw err
      }

      // Referral: attach the invite code (server decides whether it counts). Never blocks or fails sign-up.
      try {
        const refCode = normalizeReferralCode(data.refCode) || normalizeReferralCode(localStorage.getItem('animora_ref'))
        if (refCode) {
          let deviceHash = ''
          try { deviceHash = (await getDeviceHash()) || '' } catch { /* optional */ }
          await fetch('/api/referral', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${await user.getIdToken()}` },
            body: JSON.stringify({ action: 'claim', code: refCode, deviceHash }),
          })
        }
        localStorage.removeItem('animora_ref')
      } catch { /* ignore */ }

      setProfile({
        uid: user.uid,
        email: user.email || '',
        name: newProfileData.name,
        username: newProfileData.username,
        usernameLower: newProfileData.usernameLower,
        avatar: newProfileData.avatar,
        bio: newProfileData.bio,
        gender: newProfileData.gender,
        followersCount: 0,
        followingCount: 0,
        chatBackground: 'none',
        private: false,
        isAdmin: newProfileData.isAdmin,
      })
      setShowUsernameSetup(false)
      return { ok: true }
    } catch (err: any) {
      return { ok: false, error: friendlyError(err, 'Could not create your account. Please check your connection and try again.') }
    } finally {
      setBusy(false)
    }
  }

  // ===== Back gesture / back button: close ONLY the top layer, never the whole app =====
  // MUST stay above the early returns below (authBusy / !user / !profile ...): hooks have to run in the same
  // order on every render, otherwise React crashes with "Rendered more hooks than during the previous render".
  // Each layer pushes one history entry while open (see lib/back-stack.ts). Opening order = closing
  // order, so back always peels off whatever was opened last: sheet -> chat/profile -> tab -> home.
  // At home with nothing open, the first back shows "Press back again to exit".
  const signedIn = Boolean(user && profile)
  useBackGuard(signedIn, () => notify('Press back again to exit'))
  useBackLayer(signedIn && activeTab !== 'home', () => setActiveTab('home'))
  useBackLayer(showChats, () => setShowChats(false))
  useBackLayer(Boolean(activeChat), () => { setActiveChat(null); setEditingMsg(null); setReplyTarget(null) })
  useBackLayer(Boolean(viewingProfile), () => setViewingProfile(null))
  useBackLayer(Boolean(viewingStory), () => setViewingStory(null))
  useBackLayer(Boolean(commentsFor), () => setCommentsFor(null))
  useBackLayer(Boolean(commentsForStory), () => setCommentsForStory(null))
  useBackLayer(Boolean(previewPost), () => setPreviewPost(null))
  useBackLayer(Boolean(feedTarget), () => setFeedTarget(null))
  useBackLayer(Boolean(followListing), () => setFollowListing(null))
  useBackLayer(Boolean(reelDraft) || showNewPost, () => { setReelDraft(null); setShowNewPost(false); setRemixSound(null) })
  useBackLayer(showCreateStory, () => setShowCreateStory(false))
  useBackLayer(showMsgRequests, () => setShowMsgRequests(false))
  useBackLayer(showNotifications, () => setShowNotifications(false))
  useBackLayer(showWallet, () => setShowWallet(false))
  useBackLayer(showGames, () => setShowGames(false))
  useBackLayer(Boolean(ludoLaunch), () => setLudoLaunch(null))
  useBackLayer(Boolean(chessLaunch), () => setChessLaunch(null))
  useBackLayer(Boolean(carromLaunch), () => setCarromLaunch(null))
  // Download the lazy screens (Games, gifts, wallet, game lobbies) quietly in the background: the first tap then opens instantly.
  useEffect(() => { enableTapFeedback() }, [])
  useEffect(() => { if (user) warmScreens() }, [user])
  useBackLayer(showGroup, () => setShowGroup(false))
  useBackLayer(showGroupInfo, () => setShowGroupInfo(false))
  useBackLayer(showPublicGuilds, () => setShowPublicGuilds(false))
  useBackLayer(showDiscoverPeople, () => setShowDiscoverPeople(false))
  useBackLayer(showProfileEdit, () => setShowProfileEdit(false))
  useBackLayer(showFriendsList, () => setShowFriendsList(false))
  useBackLayer(showAdminPanel, () => setShowAdminPanel(false))
  useBackLayer(showBlockedList, () => setShowBlockedList(false))
  useBackLayer(showSaved, () => setShowSaved(false))
  useBackLayer(showNotifSettings, () => setShowNotifSettings(false))
  useBackLayer(Boolean(hashtagView), () => setHashtagView(null))
  useBackLayer(showCommentFilter, () => setShowCommentFilter(false))
  useBackLayer(showReferralCode, () => setShowReferralCode(false))
  useBackLayer(showAppealPanel, () => setShowAppealPanel(false))
  useBackLayer(showHomeThemes, () => setShowHomeThemes(false))
  useBackLayer(showTickSheet, () => setShowTickSheet(false))
  useBackLayer(Boolean(supportCreator), () => setSupportCreator(null))
  useBackLayer(Boolean(shareActionPost), () => setShareActionPost(null))
  useBackLayer(Boolean(sharingPost), () => setSharingPost(null))
  useBackLayer(Boolean(sharingStory), () => setSharingStory(null))
  useBackLayer(Boolean(boostTarget), () => setBoostTarget(null))
  useBackLayer(Boolean(editTarget), () => setEditTarget(null))
  useBackLayer(Boolean(forwardTarget), () => setForwardTarget(null))
  useBackLayer(Boolean(reportTarget), () => setReportTarget(null))

  // STARTUP (no black / blank screen): app/layout.tsx paints a pink boot screen (html.native-boot) from the very first frame of the
  // native app. This tells it the real screen is ready, so it is removed only when something visible is underneath: the home screen
  // (user + profile restored) or the login screen (no saved session). A returning user's login page is hidden by "has-session",
  // so that case keeps the boot screen up until the session is back - no empty frame in between. (A 8 s failsafe lives in layout.tsx.)
  useEffect(() => {
    const d = document.documentElement
    if (d.classList.contains('app-ready')) return
    const iv = setInterval(() => {
      const ready = Boolean(user && profile) || (!user && !d.classList.contains('has-session'))
      if (!ready) return
      clearInterval(iv)
      requestAnimationFrame(() => requestAnimationFrame(() => d.classList.add('app-ready')))
    }, 120)
    return () => clearInterval(iv)
  }, [user, profile])

  // Signed out on a shared reel / profile link: remember it (sessionStorage) so the sign-in flow brings them back to it.
  useEffect(() => { if (!authBusy && !user) rememberReturnTo() }, [authBusy, user])

  if (authBusy) {
    // Plain empty screen (no icon / animation) - only shown for a few ms while Firebase restores the saved session.
    return <main className="app-shell" aria-busy="true" />
  }

  // Signed-out visitors go straight to the sign-in screen on EVERY address (no public preview any more).
  // A shared /reel/<id> or /@name link is remembered first, so after signing in they land on that reel / profile.
  if (!user) {
    return <div className="guest-gate"><AuthScreen key={pathname === '/sign-up' ? 'signup' : 'login'} initialMode={pathname === '/sign-up' ? 'signup' : 'login'} onGoogle={loginWithGoogle} googleBusy={loginBusy} googleError={loginError} /></div>
  }

  // Signed in, but the account could not be loaded (rules / network). Offer a retry instead of
  // the sign-up screen, which is only for genuinely new accounts.
  if (!profile && profileLoadError) {
    return (
      <main className="app-shell" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
        <div className="modal-card" style={{ textAlign: 'center', padding: '2rem 1.5rem' }}>
          <h1 style={{ fontSize: '1.2rem', margin: '0 0 0.5rem', fontWeight: 700, color: 'var(--ink)' }}>Couldn&apos;t load your account</h1>
          <p style={{ fontSize: '.82rem', color: 'var(--muted)', lineHeight: 1.6, margin: 0 }}>{profileLoadError}</p>
          <button className="primary-button" style={{ width: '100%', marginTop: '1.25rem' }} onClick={() => window.location.reload()}>
            Try again
          </button>
          <button className="text-button danger" style={{ justifyContent: 'center', width: '100%', marginTop: '0.5rem' }} onClick={directSignOut}>
            Sign out
          </button>
        </div>
      </main>
    )
  }

  // Signed in with Google but no Firestore profile yet = brand-new account.
  // (This used to silently dead-end here with no UI, so new accounts could never finish signing up.)
  if (!profile) {
    return (
      <main className="app-shell" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
        <UsernameSetupModal
          googleUser={user}
          busy={busy}
          onUploadAvatar={async (file: File) => uploadWithProgress(file, 'avatar', user.uid)}
          onCreate={createAccount}
          onSignOut={directSignOut}
        />
      </main>
    )
  }

  // Existing account that has not given a date of birth yet (or was found to be under 13): blocking screen.
  if (ageStatus === 'needs' || ageStatus === 'under-age') {
    return (
      <main className="app-shell" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
        <AgeGateModal underAge={ageStatus === 'under-age'} onSubmit={submitDob} onSignOut={directSignOut} />
      </main>
    )
  }

  // Banned accounts get a blocking screen instead of the app. A temporary ban
  // lifts itself once bannedUntil passes (checked purely on the client); a
  // permanent ban never does. Only an admin can clear the underlying flags.
  const isCurrentlyBanned = isBanActive(profile, nowTick)
  if (isCurrentlyBanned) {
    return (
      <main className="app-shell" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
        <div className="modal-card" style={{ textAlign: 'center', padding: '2rem 1.5rem' }}>
          <Ban size={40} color="var(--danger)" style={{ marginBottom: '1rem' }} />
          <h1 style={{ fontSize: '1.25rem', margin: '0 0 0.5rem', fontWeight: 700, color: 'var(--ink)' }}>
            {profile.banType === 'permanent' ? 'Account Permanently Banned' : 'Account Temporarily Banned'}
          </h1>
          <p style={{ fontSize: '.84rem', color: 'var(--muted)', lineHeight: 1.6, margin: 0 }}>
            {profile.banReason || 'Your account has been suspended for violating community guidelines.'}
          </p>
          {profile.banType === 'temporary' && profile.bannedUntil && (
            <p style={{ fontSize: '.8rem', color: 'var(--ink-2)', marginTop: '1rem' }}>
              Access restores on {new Date(profile.bannedUntil).toLocaleString()}.
            </p>
          )}
          <button className="primary-button" style={{ width: '100%', marginTop: '1.5rem' }} onClick={() => setShowAppealPanel((v) => !v)}>
            <LifeBuoy size={16} /> {showAppealPanel ? 'Hide appeal' : 'Appeal this ban'}
          </button>
          {showAppealPanel && <BanAppealPanel profile={profile} />}
          <button className="text-button" style={{ justifyContent: 'center', width: '100%', marginTop: '0.5rem' }} onClick={() => setContactPreset('Ban appeal / Unban request')}>
            Contact support instead
          </button>
          <Link href="/privacy" target="_blank" rel="noopener noreferrer" className="text-button" style={{ justifyContent: 'center', width: '100%', marginTop: '0.5rem', textDecoration: 'none' }}>
            Privacy Policy
          </Link>
          <button className="text-button danger" style={{ justifyContent: 'center', width: '100%', marginTop: '0.5rem' }} onClick={directSignOut}>
            Sign Out
          </button>
        </div>
        {contactPreset && (
          <ContactModal
            preset={contactPreset}
            profile={profile}
            extra={{
              ban_type: profile.banType,
              ban_until: profile.bannedUntil ? new Date(profile.bannedUntil).toISOString() : undefined,
              ban_reason: profile.banReason,
            }}
            onClose={() => setContactPreset(null)}
          />
        )}
      </main>
    )
  }

  // App-wide theme (header toggle button): just Light (paper white) or Dark (paper black).
  // Only affects Radar / Comms / Discover / Orbit chrome - a chat's own wallpaper (set from
  // inside that chat) is unrelated and stays exactly as it is.
  const isDarkMode = darkTheme

  // Message-request gate for the open 1:1 chat: 'incoming' = they wrote first (Accept / Delete / Block bar),
  // 'waiting' = I wrote first and they have not accepted yet (I get one message until they do).
  const dmGate: 'incoming' | 'waiting' | null = (() => {
    if (!activeChat || activeChat.kind === 'group') return null
    const oid = activeChat.id
    if (allFriends.some((f) => f.id === oid) || dmIn.some((r) => r.otherId === oid && r.status === 'accepted')) return null
    if (dmIn.some((r) => r.otherId === oid && r.status === 'pending')) return 'incoming'
    const out = dmOut.find((r) => r.otherId === oid)
    return out && out.status !== 'accepted' ? 'waiting' : null
  })()
  const respondDmRequest = async (req: DmRequest, action: 'accept' | 'delete' | 'block') => {
    if (!user || dmBusy) return
    setDmBusy(true)
    try {
      if (action === 'block') {
        const ok = await blockAction({ uid: req.otherId, name: req.name, username: req.handle.replace(/^@/, ''), avatar: req.avatar }, 'block')
        if (ok) {
          setDmIn((cur) => cur.filter((r) => r.id !== req.id))
          if (activeChat && activeChat.kind !== 'group' && activeChat.id === req.otherId) setActiveChat(null)
        }
        return
      }
      const status = action === 'accept' ? 'accepted' : 'declined'
      await updateDoc(doc(firestore, 'dmRequests', req.id), { status })
      setDmIn((cur) => cur.map((r) => r.id === req.id ? { ...r, status } : r))
      if (action === 'accept') {
        notify(`Accepted. ${req.name} is now in your chats.`)
        // Re-open the chat so the messages they sent are now marked as read.
        setActiveChat((c) => c && c.kind !== 'group' && c.id === req.otherId ? { ...c, dm: 'accepted' } : c)
      } else {
        notify('Request deleted.')
        if (activeChat && activeChat.kind !== 'group' && activeChat.id === req.otherId) setActiveChat(null)
      }
    } catch {
      notify('Could not update this request. Please try again.')
    } finally { setDmBusy(false) }
  }
  // Profile -> Chat button. Friends open their normal chat; everyone else opens a chat whose first message becomes a request.
  const openChatWithProfile = (p: { uid: string; name: string; username?: string; avatar?: string }) => {
    const friend = allFriends.find((f) => f.id === p.uid)
    const dmEntry = dmChatEntries.find((c) => c.id === p.uid)
    const incoming = dmIn.find((r) => r.otherId === p.uid && r.status === 'pending')
    const base: Chat = friend ?? dmEntry ?? {
      id: p.uid, name: p.name, handle: `@${(p.username || '').replace(/^@/, '')}`,
      avatar: p.avatar || fallbackAvatarSvg(p.name || 'User'), preview: '', time: '', kind: 'direct',
    }
    setViewingProfile(null)
    setShowChats(true)
    unhideDm(p.uid)
    setActiveChat({ ...base, kind: 'direct', hidden: false, ...(incoming ? { dm: undefined } : {}) })
  }


  // Bottom-nav Comms badge: ONLY chats that are really listed in Comms and not muted.
  // (The old total also counted hidden chats, chats of removed friends and anything else with an
  // unread flag that never shows in the list, so the number stayed on the tab with nothing to open.)
  // A chat marked "unread" by hand counts as 1, same as WhatsApp.
  const navUnread = sortedChats.reduce((sum, c) => {
    if (c.kind === 'group' || c.hidden) return sum
    if (mutedChatIds.has(`direct:${c.id}`)) return sum
    const n = unreadMap[c.id] || 0
    return sum + (n > 0 ? n : (manualUnread.has(c.id) ? 1 : 0))
  }, 0)

  return (
    <GameThemeContext.Provider value={activeHomeTheme ?? null}>
    <CoinAdContext.Provider value={{ user, onEarned: (n: number) => setProfile((c) => c ? { ...c, rewardCoins: (c.rewardCoins || 0) + n } : c) }}>
    <AuthorInfoContext.Provider value={authorInfoAll}>
    <StoryRingContext.Provider value={storyRingApi}>
    <main className="app-shell">
      {/* No banner ad any more (removed on purpose, v29): only coin / rewarded ads remain, each started by the person's own tap. */}
      {/* No App Open ad any more (removed on purpose: the app already has rewarded ads, and an ad on every start made it feel slow). */}
      {/* 
        COCKPIT FRAME:
        Fixed at 100dvh, Topbar & Bottom Nav DO NOT SCROLL, ONLY Content-Area scrolls!
      */}
      <section
        className={`phone-frame ${isDarkMode ? 'theme-dark' : ''} ${activeHomeTheme ? 'home-theme-glass' : ''}`}
        style={{
          height: '100dvh', maxHeight: '100dvh', overflow: 'hidden', display: 'flex', flexDirection: 'column',
          ...(activeHomeTheme ? ({
            ...homeThemeVars(activeHomeTheme),
            backgroundImage: 'var(--home-pattern)',
            backgroundSize: 'var(--home-pattern-size)',
            backgroundRepeat: 'var(--home-pattern-repeat)',
            backgroundPosition: 'var(--home-pattern-position)',
            backgroundColor: activeHomeTheme.bg,
          } as React.CSSProperties) : {}),
        }}
      >
        {/* Pinned Topbar (the full-screen reels section draws its own header) */}
        {activeTab !== 'reels' && (
        <>
        {/* One slim header: wordmark left, actions right. Search now lives inside Comms. */}
        <header className="topbar app-head">
          <BrandMark />
          <div className="topbar-actions">
            <button className="coin-chip" onClick={() => setShowWallet(true)} aria-label="Coin wallet">
              <CoinIcon size={18} />
              <span>{coinBalance.toLocaleString('en-IN')}</span>
            </button>
            <button className="icon-button hd-round" onClick={() => setShowGames(true)} aria-label="Games" title="Games">
              <Gamepad2 size={17} />
            </button>
            <button className="icon-button hd-round" onClick={() => setShowNotifications(true)} aria-label="Alerts">
              <Bell size={17} />
              {hasUnreadAlerts && <span className="notif-dot" />}
            </button>
          </div>
        </header>
        </>
        )}

        {/* Scrollable Inner Content Area Only (reels mode = full-bleed, no padding/scroll) */}
        <div
          ref={(el) => fadeOnTabChange(el, activeTab)}
          className={`content-area ${activeTab === 'reels' ? 'reels-mode' : ''}`}
          style={activeTab === 'reels' ? { flex: '1 1 0%', minHeight: 0 } : { flex: '1 1 0%', minHeight: 0, overflowY: 'auto', overflowX: 'hidden' }}
        >
          {activeTab === 'reels' && (
            <ReelFeedTab
              posts={[...(extraReel && !posts.some((x) => x.id === extraReel.id) ? [extraReel] : []), ...posts.filter((p) => isReelFeedPost(p))]}
              adFree={false}
              userId={user.uid}
              following={following}
              followBusy={followBusy}
              focusedReelId={focusedReelId}
              onLike={togglePostLike}
              onOpenComments={(p: Post) => setCommentsFor(p)}
              onOpenShareOptions={(p: Post) => setShareActionPost(p)}
              onOpenGift={(p: Post) => setSupportCreator({ uid: p.authorId, name: p.authorName, postId: p.id })}
              onFollow={(id: string, name: string) => toggleFollow(id, name)}
              onDelete={deletePost}
              onReport={(p: Post) => setReportTarget({ targetUid: p.authorId, targetUsername: p.authorName, targetName: p.authorName, contentType: 'post', contentId: p.id, contentText: p.caption || p.title || '' })}
              onRecordView={recordPostView}
              onReelSignal={recordReelSignal}
              getReelInterests={getReelInterests}
              suspend={uploadOpen || Boolean(viewingStory) || Boolean(viewingProfile)}
              onViewProfile={openProfileByUid}
              onOpenBoost={(p: Post) => setBoostTarget({ kind: 'post', item: p })}
              onEdit={(p: Post) => setEditTarget(p)}
              onPickVideo={(f: File, remix?: SoundInfo | null) => { setRemixSound(remix || null); setReelDraft(f) }}
              onOpenComposer={() => { setRemixSound(null); setShowNewPost(true) }}
              onOpenAlerts={() => setShowNotifications(true)}
              hasAlerts={hasUnreadAlerts}
              suggestUsers={reelSuggest}
              suggestLoading={reelSuggestLoading}
              onRefreshSuggest={fetchReelSuggest}
              friendIds={new Set(chats.filter((c) => c.kind !== 'group').map((c) => c.id))}
              sentRequests={sentRequests}
              onAddFriend={(target: Profile) => { void connectWith(target, `Friend request sent to ${target.name}.`) }}
              onCancelFriend={async (target: Profile) => {
                try {
                  await deleteDoc(doc(firestore, 'friendRequests', `${user.uid}_${target.uid}`))
                  setSentRequests((c) => { const n = new Set(c); n.delete(target.uid); return n })
                } catch { notify('Could not cancel request.') }
              }}
            />
          )}

          {activeTab === 'home' && (
            <HomeFeed
              posts={posts}
              stories={stories}
              userId={user.uid}
              myAvatar={profile.avatar}
              myName={profile.name}
              following={following}
              followBusy={followBusy}
              playingPostAudioId={playingPostAudioId}
              suspendVideo={uploadOpen || Boolean(viewingStory) || Boolean(viewingProfile) || showChats || Boolean(activeChat)}
              onToggleAudio={togglePlayPostAudio}
              onAddStory={() => setShowCreateStory(true)}
              onViewStory={(st: Story) => storyRingApi.open(st.authorId)}
              onLike={togglePostLike}
              onOpenComments={(p: Post) => setCommentsFor(p)}
              onOpenShareOptions={(p: Post) => setShareActionPost(p)}
              onOpenGift={(p: Post) => setSupportCreator({ uid: p.authorId, name: p.authorName, postId: p.id })}
              onFollow={(id: string, name: string) => toggleFollow(id, name)}
              onDelete={deletePost}
              onReport={(p: Post) => setReportTarget({ targetUid: p.authorId, targetUsername: p.authorName, targetName: p.authorName, contentType: 'post', contentId: p.id, contentText: p.caption || p.title || '' })}
              onOpenBoost={(p: Post) => setBoostTarget({ kind: 'post', item: p })}
              onEdit={(p: Post) => setEditTarget(p)}
              onViewProfile={openProfileByUid}
              onRecordView={recordPostView}
              onOpenComposer={() => { setRemixSound(null); setShowNewPost(true) }}
            />
          )}

          {activeTab === 'contacts' && (
            <ContactsTab
              search={peopleSearch}
              setSearch={setPeopleSearch}
              results={contacts}
              requests={requests}
              following={following}
              sentRequests={sentRequests}
              friendIds={new Set(chats.filter((c) => c.kind !== 'group').map((c) => c.id))}
              followBusy={followBusy}
              discoverSubTab={discoverSubTab}
              setDiscoverSubTab={setDiscoverSubTab}
              randomUsers={randomUsers}
              randomLoading={randomLoading}
              onRefreshRandom={fetchRandomUsers}
              leaderboardUsers={leaderboardUsers}
              coinLeaders={coinLeaders}
              boardsLoaded={boardsLoaded}
              onSearch={searchPeople}
              onAdd={(target: Profile) => { void connectWith(target, `Connection requested to ${target.name}.`) }}
              onCancel={async (target: Profile) => {
                try {
                  await deleteDoc(doc(firestore, 'friendRequests', `${user.uid}_${target.uid}`))
                  setSentRequests((c) => { const n = new Set(c); n.delete(target.uid); return n })
                  notify('Request cancelled.')
                } catch { notify('Could not cancel request.') }
              }}
              onFollow={(target: Profile) => toggleFollow(target.uid, target.name)}
              onRespond={async (req: FriendRequest, accept: boolean) => {
                if (accept) { void acceptFriendFast(req.from, req.id, req.fromName); return }
                setRequests((c) => c.filter((r) => r.id !== req.id)) // gone from the list at once
                try {
                  await deleteDoc(doc(firestore, 'friendRequests', req.id))
                  notify('Request rejected.')
                } catch { notify('Action failed.') }
              }}
              onViewProfile={openProfileByUid}
              busy={busy}
              posts={posts}
              onOpenPostPreview={(p: Post) => setPreviewPost(p)}
              onOpenReel={(p: Post) => {
                setFocusedReelId(p.id)
                setActiveTab('reels')
              }}
            />
          )}

          {activeTab === 'profile' && (
            <ProfileTab
              profile={profile}
              posts={myPosts}
              onEdit={() => setShowProfileEdit(true)}
              onOpenDiscover={() => setShowDiscoverPeople(true)}
              // A reel opens right inside the Reels feed, at that exact reel; posts open that
              // person's feed, scrolled to the tapped post (see UserFeedModal below).
              onOpenPostPreview={(p: Post) => openPostInReelFeed(p)}
              onOpenFollowList={(uid: string, name: string, kind: 'followers' | 'following') => openFollowList(uid, name, kind)}
              isPremium={isPremiumActive}
              coinBalance={coinBalance}
              onOpenSettings={() => setActiveTab('settings')}
            />
          )}

          {activeTab === 'settings' && (
            <div className="settings-list" style={{ display: 'grid', gap: '0.5rem', padding: '0.5rem 0 calc(96px + env(safe-area-inset-bottom, 0px))' }}>
              <div className="comms-head settings-head" style={{ justifyContent: 'flex-start', gap: '0.6rem' }}>
                <button type="button" className="icon-button hd-round" onClick={() => setActiveTab('profile')} aria-label="Back to Orbit"><ArrowLeft size={17} /></button>
                <div className="comms-title">
                  <h2>HUD</h2>
                  <span>Account &amp; settings</span>
                </div>
              </div>
              <button type="button" className="settings-me" onClick={() => setActiveTab('profile')}>
                <Avatar src={profile.avatar} name={profile.name} size="md" showStatus={false} />
                <span className="settings-me-text">
                  <strong>{profile.name}</strong>
                  <small>@{profile.username}</small>
                </span>
                <span className="settings-me-coins"><CoinIcon size={16} /> {coinBalance.toLocaleString('en-IN')}</span>
                <ChevronRight size={16} />
              </button>
              <p className="eyebrow" style={{ textAlign: 'left', margin: '0.5rem 0 0' }}>Account</p>
              <div className="settings-group">
                <button type="button" className="text-button settings-row" onClick={() => setShowProfileEdit(true)}>
                  <span className="settings-row-main"><CircleUserRound size={18} color="var(--cyan)" /> Account Profile &amp; HUD</span>
                  <ChevronRight size={16} />
                </button>
                <button type="button" className="text-button settings-row" onClick={() => setShowTickSheet(true)}>
                  <span className="settings-row-main"><GreenTick size={19} /> Green Tick</span>
                  <span className="settings-row-end settings-row-accent">
                    {isPremiumActive ? 'Active' : 'Get'}
                    <ChevronRight size={16} />
                  </span>
                </button>
              </div>

              <SubscriptionsList subs={mySubsNamed} onUnsubscribe={(sub) => handleUnsubscribe(sub.id)} />

              <p className="eyebrow" style={{ textAlign: 'left', margin: '0.5rem 0 0' }}>More</p>
              <div className="settings-group">
                <HudAnalytics posts={myPosts} coinsEarned={coinsEarned} />
                <button type="button" className="text-button settings-row" onClick={() => setShowReferralCode(true)}>
                  <span className="settings-row-main"><Gift size={18} color="var(--cyan)" /> Enter referral code</span>
                  <ChevronRight size={16} />
                </button>
                <button type="button" className="text-button settings-row" onClick={() => setShowCommentFilter(true)}>
                  <span className="settings-row-main"><ShieldCheck size={18} color="var(--cyan)" /> Comment filter</span>
                  <ChevronRight size={16} />
                </button>
                <button type="button" className="text-button settings-row" onClick={() => setShowNotifSettings(true)}>
                  <span className="settings-row-main"><Bell size={18} color="var(--cyan)" /> Notifications</span>
                  <ChevronRight size={16} />
                </button>
                <button type="button" className="text-button settings-row" onClick={() => setShowSaved(true)}>
                  <span className="settings-row-main"><Bookmark size={18} color="var(--cyan)" /> Saved reels &amp; posts</span>
                  <ChevronRight size={16} />
                </button>
                <button type="button" className="text-button settings-row" onClick={() => setShowBlockedList(true)}>
                  <span className="settings-row-main"><UserX size={18} color="var(--cyan)" /> Blocked &amp; restricted accounts</span>
                  <span className="settings-row-end">
                    {Object.keys(blockRecords).length || ''}
                    <ChevronRight size={16} />
                  </span>
                </button>
              </div>

              <p className="eyebrow" style={{ textAlign: 'left', margin: '0.5rem 0 0' }}>Help &amp; legal</p>
              <div className="settings-group">
                <button type="button" className="text-button settings-row" onClick={() => setContactPreset('General question')}>
                  <span className="settings-row-main"><LifeBuoy size={18} color="var(--cyan)" /> Contact &amp; Support</span>
                  <ChevronRight size={16} />
                </button>
                <Link href="/about" target="_blank" rel="noopener noreferrer" className="text-button settings-row">
                  <span className="settings-row-main"><Info size={18} color="var(--cyan)" /> About Animora</span>
                  <ChevronRight size={16} />
                </Link>
                <Link href="/privacy" target="_blank" rel="noopener noreferrer" className="text-button settings-row">
                  <span className="settings-row-main"><ShieldCheck size={18} color="var(--cyan)" /> Privacy Policy</span>
                  <ChevronRight size={16} />
                </Link>
                <Link href="/terms" target="_blank" rel="noopener noreferrer" className="text-button settings-row">
                  <span className="settings-row-main"><ShieldCheck size={18} color="var(--cyan)" /> Terms &amp; Conditions</span>
                  <ChevronRight size={16} />
                </Link>
                {isAdminAccount && (
                  <button type="button" className="text-button settings-row" onClick={() => setShowAdminPanel(true)}>
                    <span className="settings-row-main"><ShieldAlert size={18} color="var(--cyan)" /> Admin Console</span>
                    <ChevronRight size={16} />
                  </button>
                )}
              </div>

              <div className="danger-card">
                <button type="button" className="text-button danger danger-card-btn" onClick={confirmSignOut}>
                  Disengage Session
                </button>
                <button type="button" className="text-button danger danger-card-btn" onClick={() => setShowDeleteAccount(true)}>
                  <Trash2 size={16} /> Delete Account
                </button>
              </div>
            </div>
          )}
        </div>

        {showDeleteAccount && (
          <DeleteAccountModal
            busy={deleteAccountBusy}
            onClose={() => setShowDeleteAccount(false)}
            onTemporaryDelete={async () => {
              setDeleteAccountBusy(true)
              try {
                await updateDoc(doc(firestore, 'users', user.uid), { deactivated: true, deletionScheduledAt: null })
                setShowDeleteAccount(false)
                await signOut(firebaseAuth)
              } catch { notify('Could not deactivate your account. Please try again.') } finally { setDeleteAccountBusy(false) }
            }}
            onPermanentDelete={async () => {
              setDeleteAccountBusy(true)
              try {
                await updateDoc(doc(firestore, 'users', user.uid), { deletionScheduledAt: Date.now(), deactivated: false })
                setShowDeleteAccount(false)
                await signOut(firebaseAuth)
              } catch { notify('Could not schedule deletion. Please try again.') } finally { setDeleteAccountBusy(false) }
            }}
          />
        )}

        {reportTarget && (
          <ReportModal
            target={reportTarget}
            busy={reportBusy}
            onClose={() => setReportTarget(null)}
            onSubmit={(reason: string) => submitReport({ ...reportTarget, reason })}
          />
        )}

        {/* Green Tick purchase sheet (renders itself into <body>, so it always centres on the real viewport) */}
        {showTickSheet && (
          <GreenTickSheet
            active={isPremiumActive}
            until={profile?.premiumUntil}
            onClose={() => setShowTickSheet(false)}
          />
        )}

        {typeof document !== 'undefined' && createPortal(callUI, document.body)}
        {typeof document !== 'undefined' && createPortal(groupCallUI, document.body)}

        {showGames && !ludoLaunch && !chessLaunch && !carromLaunch && typeof document !== 'undefined' && createPortal(
          <GamesPage
            coinBalance={coinBalance}
            onClose={() => setShowGames(false)}
            onOpenWallet={() => setShowWallet(true)}
            theme={profile?.gamesTheme || ''}
            premiumUnlocked={themesUnlocked}
            onGetPremium={() => { setShowGames(false); setShowTickSheet(true) }}
            streak={Number(profile?.activityStreak || 0)}
            friends={allFriends.map((f) => ({ id: f.id, name: f.name, avatar: f.avatar }))}
            onViewProfile={(uid: string) => { setShowGames(false); void openProfileByUid(uid) }}
            onCoins={(n: number, label: string) => showCoinReward(n, label)}
            onOpen={(id: string) => { if (id === 'ludo') { setShowGames(false); setLudoLaunch({ type: 'hub' }) } else if (id === 'chess') { setShowGames(false); setChessLaunch({ type: 'hub' }) } else if (id === 'carrom') { setShowGames(false); setCarromLaunch({ type: 'hub' }) } }}
          />,
          document.body
        )}
        {ludoLaunch && user && typeof document !== 'undefined' && createPortal(
          <LudoHub
            key={JSON.stringify(ludoLaunch)}
            user={{ uid: user.uid, name: profile?.name || 'Player', avatar: profile?.avatar || '', country: profile?.country }}
            coinBalance={coinBalance}
            friends={allFriends.map((f) => ({ id: f.id, name: f.name, avatar: f.avatar, online: Boolean(presenceMap[f.id] && nowTick - presenceMap[f.id] < PRESENCE_ONLINE_MS) }))}
            friendIds={new Set(allFriends.map((f) => f.id))}
            sentRequests={sentRequests}
            launch={ludoLaunch}
            onClose={() => setLudoLaunch(null)}
            onOpenWallet={() => setShowWallet(true)}
            onAddFriend={ludoAddFriend}
            onInvite={postLudoInvite}
            notify={notify}
          />,
          document.body
        )}
        {chessLaunch && user && typeof document !== 'undefined' && createPortal(
          <ChessHub
            key={JSON.stringify(chessLaunch)}
            user={{ uid: user.uid, name: profile?.name || 'Player', avatar: profile?.avatar || '', country: profile?.country }}
            coinBalance={coinBalance}
            friends={allFriends.map((f) => ({ id: f.id, name: f.name, avatar: f.avatar, online: Boolean(presenceMap[f.id] && nowTick - presenceMap[f.id] < PRESENCE_ONLINE_MS) }))}
            friendIds={new Set(allFriends.map((f) => f.id))}
            sentRequests={sentRequests}
            launch={chessLaunch}
            onClose={() => setChessLaunch(null)}
            onOpenWallet={() => setShowWallet(true)}
            onAddFriend={ludoAddFriend}
            onInvite={postChessInvite}
            notify={notify}
          />,
          document.body
        )}
        {carromLaunch && user && typeof document !== 'undefined' && createPortal(
          <CarromHub
            key={JSON.stringify(carromLaunch)}
            user={{ uid: user.uid, name: profile?.name || 'Player', avatar: profile?.avatar || '', country: profile?.country }}
            coinBalance={coinBalance}
            friends={allFriends.map((f) => ({ id: f.id, name: f.name, avatar: f.avatar, online: Boolean(presenceMap[f.id] && nowTick - presenceMap[f.id] < PRESENCE_ONLINE_MS) }))}
            friendIds={new Set(allFriends.map((f) => f.id))}
            sentRequests={sentRequests}
            launch={carromLaunch}
            onClose={() => setCarromLaunch(null)}
            onOpenWallet={() => setShowWallet(true)}
            onAddFriend={ludoAddFriend}
            onInvite={postCarromInvite}
            notify={notify}
          />,
          document.body
        )}

        {showHomeThemes && typeof document !== 'undefined' && createPortal(
          <HomeThemePicker
            current={profile.appBackground || 'none'}
            isPremium={themesUnlocked}
            onPick={(id: string) => { updateAppBackground(id); setShowHomeThemes(false) }}
            onGetPremium={() => { setShowHomeThemes(false); setShowTickSheet(true) }}
            onClose={() => setShowHomeThemes(false)}
          />,
          document.body
        )}
        {showWallet && typeof document !== 'undefined' && createPortal(
          <WalletPage
            balance={coinBalance}
            earned={coinsEarned}
            spent={coinsSpentTotal}
            onClose={() => setShowWallet(false)}
            onOpenTick={() => { setShowWallet(false); setShowTickSheet(true) }}
            dark={isDarkMode}
            user={user}
            transactions={walletTxns}
            transactionsLoading={walletTxnsLoading}
            followersCount={profile.followersCount || 0}
            reelViews={achServerStats?.reelViews || 0}
            homeTheme={activeHomeTheme ?? null}
            locked={false}
            onCoinsEarned={(n: number) => setProfile((c) => c ? { ...c, rewardCoins: (c.rewardCoins || 0) + n } : c)}
          />,
          document.body
        )}

        {supportCreator && typeof document !== 'undefined' && createPortal(
          <CreatorSupportModal
            creatorId={supportCreator.uid}
            creatorName={supportCreator.name}
            profile={profile}
            onClose={() => setSupportCreator(null)}
            onSubscribed={(planId, paidWith) => handleCreatorSubscribe(supportCreator.uid, planId, paidWith, supportCreator.name)}
            subscription={mySubsNamed.find((x) => x.creatorId === supportCreator.uid) ?? null}
            onGifted={(planId, paidWith, quantity) => handleSendGift(supportCreator.uid, planId, paidWith, supportCreator.postId, quantity)}
            fromPost={Boolean(supportCreator.postId)}
            postId={supportCreator.postId}
            coins={coinsApi}
          />,
          document.body
        )}

        {incomingGift && typeof document !== 'undefined' && createPortal(
          <div className="modal-backdrop" style={{ zIndex: 200 }} onClick={() => setIncomingGift(null)}>
            <div className="modal-card gift-received-toast" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 300, textAlign: 'center' }}>
              <div className="ps-sent-burst">
                {giftVisualFor(incomingGift.label).image
                  ? <span className="gift-tile ps-sent-emoji" style={{ width: 84, height: 84, borderRadius: 22, fontSize: 0 }}><img decoding="async" src={giftVisualFor(incomingGift.label).image} alt="" /></span>
                  : <span className="ps-sent-emoji"><PosterIcon name="gift" size={72} /></span>}
              </div>
              <h3 style={{ margin: '1rem 0 0.25rem', fontSize: '1.05rem', fontWeight: 700, color: 'var(--ink)' }}>You received a gift!</h3>
              <p style={{ margin: 0, fontSize: '.8rem', color: 'var(--muted)' }}>
                <strong>{incomingGift.fromName}</strong> sent you {incomingGift.label}
              </p>
              <button type="button" className="text-button primary" style={{ width: '100%', marginTop: '1rem' }} onClick={() => setIncomingGift(null)}>
                Nice!
              </button>
            </div>
          </div>,
          document.body
        )}

        {isAdminAccount && typeof document !== 'undefined' && (restrictSheet || adminRemoveTarget || profileBanTarget) && createPortal(
          <div style={{ position: 'relative', zIndex: 200 }}>
            {restrictSheet && (
              <RestrictUserSheet
                target={restrictSheet}
                busy={adminActionBusy}
                onClose={() => setRestrictSheet(null)}
                onConfirm={async (days: number | 'forever', reason: string) => {
                  if (await adminSetRestriction(restrictSheet, restrictSheet.kind, days, reason)) setRestrictSheet(null)
                }}
              />
            )}
            {adminRemoveTarget && (
              <RemovePostSheet
                post={adminRemoveTarget}
                busy={adminActionBusy}
                onClose={() => setAdminRemoveTarget(null)}
                onConfirm={(reason: string) => adminRemovePost(adminRemoveTarget, reason)}
              />
            )}
            {profileBanTarget && (
              <BanUserSheet
                target={profileBanTarget}
                busy={adminActionBusy}
                onClose={() => setProfileBanTarget(null)}
                onConfirm={async (mode: 'temporary' | 'permanent', durationMs?: number, reason?: string) => {
                  if ((await adminBanUser(profileBanTarget, mode, durationMs, reason)) !== false) setProfileBanTarget(null)
                }}
              />
            )}
          </div>,
          document.body
        )}

        {showAdminPanel && isAdminAccount && (
          <AdminPanelModal
            onRestrict={(u: Profile, kind: 'chat' | 'upload') => setRestrictSheet({ uid: u.uid, username: u.username, kind, until: restrictLeft(kind === 'chat' ? u.chatBanUntil : u.uploadHoldUntil), reason: (kind === 'chat' ? u.chatBanReason : u.uploadHoldReason) || '' })}
            adminName={profile?.username || 'admin'}
            results={adminSearchResults}
            searchBusy={adminSearchBusy}
            actionBusy={adminActionBusy}
            onSearch={adminSearchUsers}
            onClose={() => { setShowAdminPanel(false); setAdminSearchResults([]) }}
            onBan={adminBanUser}
            onUnban={adminUnbanUser}
            wipeBusy={adminWipeBusy}
            wipeError={adminWipeError}
            onWipeDatabase={adminWipeDatabase}
            onResetWipeFlow={() => { setAdminWipeError('') }}
            boostEnabled={boostEnabled}
            boostLockBusy={adminBoostLockBusy}
            onToggleBoostLock={adminToggleBoostLock}
          />
        )}

        {/* Pinned Bottom Navigation Dock */}
        <nav className={`bottom-nav ${activeTab === 'reels' ? 'reels-nav' : ''}`}>
          {/* one orange bar that glides under the active tab (replaces the per-item marker) */}
          <span className="nav-slider" aria-hidden="true" style={{ transform: `translateX(${({ home: 0, reels: 2, contacts: 4, profile: 5, settings: 5 } as Record<string, number>)[activeTab] ?? 0}00%)` }} />
          {[
            ['home', Home, 'Home'],
            ['chat', MessageCircle, 'Chat'],
            ['reels', Clapperboard, REELS_BRAND],
            ['add', Plus, 'Create'],
            ['contacts', Users, 'Discover'],
            ['profile', CircleUserRound, 'Orbit'],
          ].map(([t, Icon, label]: any) => t === 'chat' ? (
            <button
              key="chat"
              type="button"
              className="nav-item nav-chat"
              aria-label={navUnread > 0 ? `Messages, ${navUnread} unread` : 'Messages'}
              onClick={() => setShowChats(true)}
            >
              <span className="nav-icon">
                <MessageCircle size={19} />
                {navUnread > 0 && <span className="nav-count" key={navUnread}>{navUnread > 99 ? '99+' : navUnread}</span>}
              </span>
              <span>{label}</span>
            </button>
          ) : t === 'add' ? (
            <button
              key="add"
              type="button"
              className="nav-item nav-add"
              aria-label="Upload a post or reel"
              onClick={() => {
                // The New post page opens at once from any tab (no tab switch, no waiting).
                setRemixSound(null)
                setShowNewPost(true)
              }}
            >
              <span className="nav-icon nav-add-icon"><Plus size={22} strokeWidth={2.8} /></span>
              <span className="nav-add-label">Create</span>
            </button>
          ) : (
            <button
              key={t}
              className={`nav-item ${activeTab === t ? 'active' : ''}`}
              onClick={() => {
                // Opening Shonen from the nav always starts from the top, not from a
                // reel that was focused earlier via a tap in Radar/Discover.
                if (t === 'reels') setFocusedReelId(null)
                setActiveTab(t)
              }}
            >
              {t === 'profile' ? (
                <span className="nav-icon nav-me">
                  <img
                    className="nav-me-img"
                    src={profile.avatar || fallbackAvatarSvg(profile.name || 'Fan')}
                    alt=""
                    decoding="async"
                    onError={(e) => { e.currentTarget.src = fallbackAvatarSvg(profile.name || 'Fan') }}
                  />
                </span>
              ) : (
                <span className="nav-icon">
                  <Icon size={19} />
                </span>
              )}
              <span>{label}</span>
            </button>
          ))}
        </nav>

        {/* Full-screen Comms: opened from the message icon in the header. The chat itself (ChatView) opens on top of it. */}
        {showChats && (
          <div className="chats-screen" role="dialog" aria-label="Comms">
            <ChatsTab
              onBack={() => setShowChats(false)}
                coinBalance={coinBalance}
                onOpenWallet={() => { setShowChats(false); setShowWallet(true) }}
                onOpenGames={() => { setShowChats(false); setShowGames(true) }}
                onOpenAlerts={() => { setShowChats(false); setShowNotifications(true) }}
                hasUnreadAlerts={hasUnreadAlerts}
                myNote={myNote}
                friendNotes={friendNotes}
                onSaveNote={saveMyNote}
                onDeleteNote={deleteMyNote}
                stories={stories}
                userId={user.uid}
                myAvatar={profile.avatar}
                myName={profile.name}
                onAddStory={() => setShowCreateStory(true)}
                onFindPeople={() => { setPeopleSearch(''); setContacts([]); setShowAddFriend(true) }}
                onDiscoverFriends={() => { setPeopleSearch(''); setContacts([]); setShowAddFriend(true) }}
                onViewStory={(st: Story) => storyRingApi.open(st.authorId)}
                chats={filteredChats}
                query={search}
                setQuery={setSearch}
                pinnedChatIds={pinnedChatIds}
                mutedChatIds={mutedChatIds}
                onSelect={(c: Chat) => { setManualUnreadFlag(c.id, false); setActiveChat(c) }}
                onTogglePin={(c: Chat) => togglePinChat(c.id)}
                onToggleMute={(c: Chat) => toggleMuteChat(c)}
                onMarkRead={markChatRead}
                onMarkUnread={(c: Chat) => setManualUnreadFlag(c.id, true)}
                manualUnread={manualUnread}
                onNewGroup={() => setShowGroup(true)}
                onOpenPublicGuilds={() => setShowPublicGuilds(true)}
                onManageFriends={() => setShowFriendsList(true)}
                requestCount={dmPendingIn.length}
                onOpenRequests={() => setShowMsgRequests(true)}
                onLongPress={(c: Chat) => setChatLongPressTarget(c)}
                presenceMap={presenceMap}
                typingMap={typingMap}
                lastMsgMeta={lastMsgMeta}
                unreadMap={unreadMap}
                nowTick={nowTick}
            />
          </div>
        )}

        {/* Fixed Chat View (Swipe to reply, Green seen, Tap shared post) */}
        {activeChat && (
          <ChatView
            notify={notify}
            noAnimateMsgIdsRef={noAnimateMsgIdsRef}
            groupMemberProfiles={groupMemberProfiles}
            onMention={openProfileByHandle}
            onSendPoll={sendPoll}
            mentionCandidates={(activeChat.kind === 'group'
              ? groupMemberProfiles.map((m) => ({ uid: m.uid, name: m.name, username: m.username, avatar: m.avatar }))
              : [{ uid: activeChat.id, name: activeChat.name, username: (activeChat.handle || '').replace(/^@/, ''), avatar: activeChat.avatar },
                 ...allFriends.map((f) => ({ uid: f.id, name: f.name, username: (f.handle || '').replace(/^@/, ''), avatar: f.avatar }))]
            ).filter((c, i, a) => c.uid !== user?.uid && c.username && a.findIndex((x) => x.uid === c.uid) === i)}
            chat={activeChat}
            messages={messages}
            loading={messagesLoading}
            hasOlder={hasOlderMsgs}
            onLoadOlder={() => setMsgWindow((w) => w + MSG_WINDOW_STEP)}
            draftStore={draftStore}
            setMessage={handleTypingChange}
            onBack={() => { setActiveChat(null); setEditingMsg(null); setReplyTarget(null) }}
            onSend={sendMessage}
            onSendImage={async (file: File, opts?: { viewOnce?: boolean }): Promise<boolean> => {
              try {
                const url = await uploadWithProgress(file, 'chat', user.uid)
                await sendMessage(undefined, { sharedImageUrl: url, ...(opts?.viewOnce ? { viewOnce: true } : {}) })
                return true
              } catch { notify('Could not send photo.'); return false }
            }}
            // Receiver opened a view-once photo: remember it, so it can never be opened again.
            onMarkViewed={async (m: Message) => {
              if (!activeChat) return
              const chatId = activeChat.kind === 'group' ? activeChat.id : [user.uid, activeChat.id].sort().join('_')
              try {
                await updateDoc(doc(firestore, 'chats', chatId, 'messages', m.id), { viewedBy: arrayUnion(user.uid) })
              } catch { return } // not saved as viewed -> keep the file so it can be opened again
              // 1-to-1 chat: the only receiver has seen it, so the file itself can go too (in a group the others still need it).
              if (activeChat.kind !== 'group' && m.sharedImageUrl) void deleteUploadedFile(m.sharedImageUrl)
            }}
            onSendSticker={(url: string) => sendMessage(undefined, { sharedImageUrl: url, textOverride: '', sticker: true })}
            onSendVoice={async (blob: Blob, durationSecs: number): Promise<boolean> => {
              try {
                const url = await uploadWithProgress(blob, 'audio', user.uid)
                await sendMessage(undefined, { audioUrl: url, audioDuration: Math.round(durationSecs), textOverride: '' })
                return true
              } catch { notify('Could not send voice note.'); return false }
            }}
            savedStickers={profile.savedStickers || []}
            stickerLimit={stickerLimit}
            onStickerLimit={stickerLimitReached}
            onAddSticker={addOwnSticker}
            onSaveSticker={saveStickerUrl}
            onRemoveSticker={removeSavedSticker}
            isTypingPartner={Boolean(typingMap[activeChat.id])}
            onStartLudo={() => {
              if (activeChat.kind === 'group') setLudoLaunch({ type: 'invite', friends: [], chatId: activeChat.id, chatName: activeChat.name })
              else setLudoLaunch({ type: 'invite', friends: [{ id: activeChat.id, name: activeChat.name, avatar: activeChat.avatar }], chatName: activeChat.name })
            }}
            onJoinLudo={(code: string) => setLudoLaunch({ type: 'join', code })}
            onStartChess={activeChat.kind === 'group' ? undefined : () => setChessLaunch({ type: 'hub' })}
            onJoinChess={(code: string) => setChessLaunch({ type: 'join', code })}
            onStartCarrom={activeChat.kind === 'group' ? undefined : () => setCarromLaunch({ type: 'hub' })}
            onJoinCarrom={(code: string) => setCarromLaunch({ type: 'join', code })}
            onStartCall={activeChat.kind === 'group'
              ? (type: 'voice' | 'video') => void startGroupCall({ id: activeChat.id, name: activeChat.name, avatar: activeChat.avatar, members: activeChat.members || [] }, type)
              : (type: 'voice' | 'video') => void startCall({ id: activeChat.id, name: activeChat.name, avatar: activeChat.avatar, username: activeChat.handle }, type)}
            isOnlinePartner={activeChat.kind !== 'group' ? Boolean(presenceMap[activeChat.id] && nowTick - presenceMap[activeChat.id] < PRESENCE_ONLINE_MS) : (activeChat.members || []).some((m: string) => m !== user.uid && Boolean(presenceMap[m] && nowTick - presenceMap[m] < PRESENCE_ONLINE_MS))}
            lastSeenText={activeChat.kind !== 'group' && presenceMap[activeChat.id] ? formatTime(new Date(presenceMap[activeChat.id])) : undefined}
            groupOnlineCount={activeChat.kind === 'group' ? (activeChat.members || []).filter((m: string) => m === user.uid || Boolean(presenceMap[m] && nowTick - presenceMap[m] < PRESENCE_ONLINE_MS)).length : 0}
            currentUserId={user.uid}
            chatBackground={profile.chatBackground || 'none'}
            onChangeBackground={updateChatBackground}
            isPremium={themesUnlocked}
            unlockedChatThemes={profile.unlockedChatThemes || []}
            themeShop={{ coins: coinsApi, unlock: grantChatThemes, addCoins: () => setShowWallet(true), openTick: () => setShowTickSheet(true) }}
            replyTarget={replyTarget}
            onCancelReply={() => setReplyTarget(null)}
            onReply={(m: Message) => setReplyTarget(m)}
            onForward={(m: Message) => setForwardTarget(m)}
            onReact={(m: Message, emoji: string, blast?: boolean) => reactToMessage(activeChat, m, emoji, blast)}
            onDeleteForMe={(m: Message) => deleteMessageForMe(activeChat, m)}
            onUnsend={(m: Message) => unsendMessage(activeChat, m)}
            onReport={(m: Message) => setReportTarget({
              targetUid: m.senderId || activeChat.id,
              targetUsername: activeChat.kind === 'group' ? (m.senderName || 'Unknown') : ((activeChat.handle || '').replace(/^@/, '') || activeChat.name || 'Unknown'),
              targetName: activeChat.kind === 'group' ? (m.senderName || 'Unknown') : (activeChat.name || 'Unknown'),
              contentType: 'message',
              contentId: m.id,
              contentText: m.text,
            })}
            onEditMsg={(m: Message) => { setEditingMsg(m); setMessage(m.text) }}
            editingMsg={editingMsg}
            onCancelEdit={() => { setEditingMsg(null); setMessage('') }}
            onViewProfile={activeChat.kind !== 'group' ? () => openProfileByUid(activeChat.id) : undefined}
            onGroupInfo={activeChat.kind === 'group' ? () => setShowGroupInfo(true) : undefined}
            dmGate={dmGate}
            dmBusy={dmBusy}
            onDmAccept={() => { const r = dmIn.find((x) => x.otherId === activeChat.id && x.status === 'pending'); if (r) void respondDmRequest(r, 'accept') }}
            onDmDelete={() => { const r = dmIn.find((x) => x.otherId === activeChat.id && x.status === 'pending'); if (r) void respondDmRequest(r, 'delete') }}
            onDmBlock={() => { const r = dmIn.find((x) => x.otherId === activeChat.id && x.status === 'pending'); if (r) askConfirm({ title: `Block ${r.name}?`, message: 'They will not be able to message you or see your activity. You can unblock them from Settings.', confirmLabel: 'Block', action: async () => { await respondDmRequest(r, 'block') } }) }}
            onOpenSharedPost={(imageUrl: string, postId?: string, mediaType?: 'image' | 'video') => {
              const target = posts.find(p => (postId && p.id === postId) || p.imageUrl === imageUrl)
              if (target) {
                // Close the chat overlay and hand off to the real place that content
                // lives: a shared reel jumps into the actual Shonen reel section
                // (focused on that reel), a shared post opens inside the poster's
                // real post feed - same behavior as tapping either from Radar/Discover.
                setActiveChat(null)
                if (isReelPost(target)) {
                  setFocusedReelId(target.id)
                  setActiveTab('reels')
                } else {
                  setFeedTarget({ authorId: target.authorId, postId: target.id })
                }
                return
              }
              // Original post/reel is gone or out of range (deleted, private, older than
              // the feed window) - fall back to a lightweight preview of just the media.
              setPreviewPost({
                id: postId || 'shared',
                authorId: '',
                authorName: 'Transmission Post',
                authorAvatar: '',
                imageUrl,
                mediaType: mediaType || 'image',
                caption: '',
                likes: [],
                commentCount: 0,
                time: 'now',
                privacy: 'public',
                viewers: {},
                views: 1,
                // No postId = a sticker / plain chat photo, not a real post: preview only.
                previewOnly: !postId
              })
            }}
          />
        )}

        {/* FULL POST PREVIEW MODAL (With Safe Fallbacks & High Z-Index) */}
        {previewPost && (
          <PostPreviewModal
            post={previewPost}
            currentUserId={user.uid}
            isPlayingAudio={playingPostAudioId === previewPost.id}
            onToggleAudio={() => togglePlayPostAudio(previewPost.id, previewPost.audioUrl)}
            onClose={() => setPreviewPost(null)}
            onLike={togglePostLike}
            onDelete={async (p: Post) => {
              await deletePost(p)
              setPreviewPost(null)
            }}
            onOpenComments={() => {
              const target = previewPost
              setPreviewPost(null)
              setCommentsFor(target)
            }}
            onOpenBoost={(p: Post) => { setPreviewPost(null); setBoostTarget({ kind: 'post', item: p }) }}
            onEdit={(p: Post) => { setPreviewPost(null); setEditTarget(p) }}
            onViewProfile={(uid: string) => { setPreviewPost(null); openProfileByUid(uid) }}
          />
        )}

        {/* Tapping a post in a profile's post grid opens here: that person's feed, scrolled
            to the tapped post - full post cards (like/comment/share/follow/report), not the
            old stripped-down popup. */}
        {feedTarget && (
          <UserFeedModal
            posts={posts}
            authorId={feedTarget.authorId}
            focusPostId={feedTarget.postId}
            userId={user.uid}
            following={following}
            followBusy={followBusy}
            playingPostAudioId={playingPostAudioId}
            onToggleAudio={togglePlayPostAudio}
            suspendVideo={Boolean(viewingStory) || uploadOpen}
            onClose={() => setFeedTarget(null)}
            onLike={togglePostLike}
            onOpenComments={(p: Post) => setCommentsFor(p)}
            onOpenShareOptions={(p: Post) => setShareActionPost(p)}
            onOpenGift={(p: Post) => setSupportCreator({ uid: p.authorId, name: p.authorName, postId: p.id })}
            onFollow={(id: string, name: string) => toggleFollow(id, name)}
            onDelete={deletePost}
            onReport={(p: Post) => setReportTarget({ targetUid: p.authorId, targetUsername: p.authorName, targetName: p.authorName, contentType: 'post', contentId: p.id, contentText: p.caption || p.title || '' })}
            onOpenBoost={(p: Post) => setBoostTarget({ kind: 'post', item: p })}
            onEdit={(p: Post) => setEditTarget(p)}
            onViewProfile={(uid: string) => { setFeedTarget(null); openProfileByUid(uid) }}
          />
        )}

        {editTarget && (
          <EditPostModal
            post={editTarget}
            onClose={() => setEditTarget(null)}
            onSave={(updates: { title: string; caption?: string; tags: string[]; thumbnailFile?: File | null }) => saveEditedPost(editTarget, updates)}
          />
        )}

        {/* Share Action Sheet */}
        {shareActionPost && (
          <div className="action-sheet-backdrop" style={{ zIndex: 125 }} onClick={() => setShareActionPost(null)}>
            <div className="action-sheet" onClick={(e) => e.stopPropagation()}>
              <div style={{ textAlign: 'center', marginBottom: '0.75rem' }}>
                <strong style={{ fontSize: '1rem', color: 'var(--ink)' }}>Share Transmission</strong>
                <p style={{ margin: '0.25rem 0 0', color: 'var(--cyan)', fontSize: '.74rem' }}>Select destination</p>
              </div>

              <button
                className="action-sheet-item"
                onClick={() => void addPostToStory(shareActionPost)}
              >
                <Sparkles size={18} color="var(--cyan)" /> Add to My Story (with Audio/Song)
              </button>

              <button
                className="action-sheet-item"
                onClick={() => {
                  const p = shareActionPost
                  setShareActionPost(null)
                  setSharingPost(p)
                }}
              >
                <Forward size={18} color="var(--ink)" /> Share to Friends / Guild Comms
              </button>

              <button className="action-sheet-item" onClick={() => setShareActionPost(null)}>
                <X size={16} /> Cancel
              </button>
            </div>
          </div>
        )}

        {/* Contact & Support (suggestions, bug reports, privacy/copyright requests) */}
        {contactPreset && (
          <ContactModal preset={contactPreset} profile={profile} onClose={() => setContactPreset(null)} />
        )}

        {/* Comments Modal */}
        {commentsFor && (
          <CommentsModal
            post={commentsFor}
            comments={comments}
            currentUserId={user.uid}
            onClose={() => setCommentsFor(null)}
            onSend={(txt: string, reply?: CommentReplyMeta) => addComment(commentsFor, txt, reply)}
            onToggleLikeComment={(c: PostComment) => toggleCommentLike(commentsFor, c)}
            onEdit={editComment}
            onDelete={deleteComment}
            onTogglePin={(c: PostComment) => togglePinComment(commentsFor, c)}
            onViewProfile={(uid: string) => { setCommentsFor(null); openProfileByUid(uid) }}
          />
        )}

        {/* Boost / Promote Modal (posts & stories) */}
        {boostTarget && BOOST_COMING_SOON && (
          <BoostComingSoon kind={boostTarget.kind} onClose={() => setBoostTarget(null)} />
        )}
        {boostTarget && !BOOST_COMING_SOON && (
          <BoostModal
            kind={boostTarget.kind}
            item={boostTarget.item}
            profile={profile}
            uid={user!.uid}
            getIdToken={() => user!.getIdToken()}
            coinBalance={coinBalance}
            followers={profile?.followersCount || 0}
            reelViews={achServerStats?.reelViews || 0}
            isAdmin={isAdminAccount}
            spendCoins={spendCoins}
            user={user}
            onCoinsEarned={(n: number) => setProfile((c) => c ? { ...c, rewardCoins: (c.rewardCoins || 0) + n } : c)}
            onClose={() => setBoostTarget(null)}
            onSaveWebsite={(url: string) => saveBoostWebsite(boostTarget.item.id, url, boostTarget.kind === 'story' ? 'stories' : 'posts')}
            onSaveCtaText={(text: string) => saveBoostCtaText(boostTarget.item.id, text, boostTarget.kind === 'story' ? 'stories' : 'posts')}
            onSaveColor={(color: string) => saveBoostBannerColor(boostTarget.item.id, color, boostTarget.kind === 'story' ? 'stories' : 'posts')}
            onSuccess={async (durationHours: number, goal?: 'profile' | 'website') => {
              // Paid with real money (Google Play); promoted/promotedUntil are saved on the server by /api/billing/verify. No coins are spent.
              notify(`Your ${durationHours >= 24 ? `${Math.round(durationHours / 24)}-day` : `${durationHours}-hour`} boost is live`)
              setBoostTarget(null)
            }}
          />
        )}

        {/* Chat Long-Press Sheet */}
        {chatLongPressTarget && (
          <div className="action-sheet-backdrop" onClick={() => setChatLongPressTarget(null)}>
            <div className="action-sheet" onClick={(e) => e.stopPropagation()}>
              <div style={{ textAlign: 'center', marginBottom: '0.75rem' }}>
                <strong style={{ fontSize: '.95rem', color: 'var(--ink)' }}>{chatLongPressTarget.name}</strong>
                <p style={{ margin: '0.25rem 0 0', color: 'var(--cyan)', fontSize: '.74rem' }}>Channel Options</p>
              </div>

              <button
                className="action-sheet-item"
                onClick={() => togglePinChat(chatLongPressTarget.id)}
              >
                <Pin size={16} color="var(--cyan)" />
                {pinnedChatIds.has(chatLongPressTarget.id) ? 'Unpin Channel' : 'Pin Channel to Top'}
              </button>

              <button
                className="action-sheet-item"
                onClick={() => toggleMuteChat(chatLongPressTarget)}
              >
                {mutedChatIds.has(`${chatLongPressTarget.kind === 'group' ? 'group' : 'direct'}:${chatLongPressTarget.id}`)
                  ? <><Bell size={16} color="var(--cyan)" /> Unmute Notifications</>
                  : <><BellOff size={16} color="var(--cyan)" /> Mute Notifications</>}
              </button>

              <button
                className="action-sheet-item danger"
                onClick={() => void hideChatFromList(chatLongPressTarget)}
              >
                <X size={16} /> Remove / Delete Chat from List
              </button>

              <button
                className="action-sheet-item danger"
                onClick={() => {
                  const target = chatLongPressTarget
                  setChatLongPressTarget(null)
                  askConfirm({
                    title: `Clear chat with ${target.name}?`,
                    message: 'This wipes the messages on your side only. It can\'t be undone.',
                    confirmLabel: 'Clear',
                    action: async () => {
                      try {
                        const chatId = target.kind === 'group' ? target.id : [user.uid, target.id].sort().join('_')
                        const snap = await getDocs(collection(firestore, 'chats', chatId, 'messages'))
                        const mine = snap.docs.filter((d) => !((d.data().deletedFor || []) as string[]).includes(user.uid))
                        // Batches of 100: one request per batch instead of one per message.
                        for (let i = 0; i < mine.length; i += 100) {
                          const wb = writeBatch(firestore)
                          mine.slice(i, i + 100).forEach((d) => wb.update(d.ref, { deletedFor: arrayUnion(user.uid) }))
                          await wb.commit()
                        }
                        setChats((curr) => curr.map((c) => (c.kind === target.kind && c.id === target.id ? { ...c, preview: 'Tap to chat' } : c)))
                        setAllFriends((curr) => curr.map((f) => (f.id === target.id ? { ...f, preview: 'Tap to chat' } : f)))
                        notify(mine.length ? 'Chat cleared.' : 'This chat is already empty.')
                      } catch (err) {
                        notify(friendlyError(err, 'Could not clear this chat. Please try again.'))
                      }
                    },
                  })
                }}
              >
                <Trash2 size={16} /> Wipe Messages (for me)
              </button>

              <button className="action-sheet-item" onClick={() => setChatLongPressTarget(null)}>
                <X size={16} /> Cancel
              </button>
            </div>
          </div>
        )}

        {/* Post Share Relay Picker Modal */}
        {sharingPost && (
          <ForwardPickerModal
            chats={chats}
            busy={busy}
            onClose={() => setSharingPost(null)}
            onForward={(targetChat: Chat) => void sharePostToChat(targetChat, sharingPost)}
          />
        )}

        {/* Story Share Relay Picker Modal */}
        {sharingStory && (
          <ForwardPickerModal
            chats={chats}
            busy={busy}
            onClose={() => setSharingStory(null)}
            onForward={(targetChat: Chat) => void shareStoryToChat(targetChat, sharingStory)}
          />
        )}

        {/* Notifications Drawer */}
        {showNotifications && (
          <NotificationDrawerModal
            notifications={notifications}
            requests={requests}
            posts={posts}
            stories={stories}
            onClose={() => setShowNotifications(false)}
            onOpenTarget={(post?: Post, story?: Story) => {
              setShowNotifications(false)
              if (post) {
                if (isReelPost(post)) { setFocusedReelId(post.id); setActiveTab('reels') } else setPreviewPost(post)
              } else if (story) {
                const group = stories.filter((item) => item.authorId === story.authorId).reverse()
                const at = Math.max(0, group.findIndex((item) => item.id === story.id))
                setStoryGroups(group)
                setStoryIndex(at)
                setViewingStory(group[at] || story)
              }
            }}
            onDismiss={async (id: string) => { await deleteDoc(doc(firestore, 'notifications', user.uid, 'items', id)) }}
            onClearAll={async () => {
              const snap = await getDocs(collection(firestore, 'notifications', user.uid, 'items'))
              await Promise.all(snap.docs.map((d) => deleteDoc(d.ref)))
              notify('Cleared notifications.')
            }}
            onAcceptRequest={(req: FriendRequest) => { void acceptFriendFast(req.from, req.id, req.fromName) }}
          />
        )}

        {/* Picker used by "Remix this sound" on a story (the story viewer closes first, so the input lives here) */}
        <input ref={storyRemixPickRef} type="file" accept="video/*,image/*" className="pc-file-input" onChange={handleStoryRemixPick} />

        {/* Story Viewer (Sound On/Off Button, Attached Music Support) */}
        {viewingStory && (
          <StoryViewerModal
            stories={storyGroups}
            index={storyIndex}
            currentUserId={user.uid}
            onClose={() => setViewingStory(null)}
            onLike={toggleStoryLike}
            onRecordView={recordStoryView}
            // also pauses the story while its Boost sheet or Share picker is open
            commentsOpen={Boolean(commentsForStory) || boostTarget?.kind === 'story' || Boolean(sharingStory)}
            onOpenComments={(s: Story) => setCommentsForStory(s)}
            onOpenBoost={(s: Story) => setBoostTarget({ kind: 'story', item: s })}
            onShare={(s: Story) => setSharingStory(s)}
            onRemixSound={(sp: any) => {
              // Same "Remix this sound" flow as a reel: pick a photo / video, then the composer opens with this sound attached.
              const heldMsg = uploadHoldMessage()
              if (heldMsg) { alert(heldMsg); return }
              const info = soundInfoOf(sp)
              if (!info.key) return
              storyRemixRef.current = info
              setViewingStory(null)
              storyRemixPickRef.current?.click()
            }}
            onOpenSoundReel={(p: Post) => {
              setViewingStory(null)
              if (isReelPost(p)) { setFocusedReelId(p.id); setActiveTab('reels') } else setFeedTarget({ authorId: p.authorId, postId: p.id })
            }}
            onViewProfile={(uid: string) => { setViewingStory(null); openProfileByUid(uid) }}
            onNext={() => {
              if (storyIndex < storyGroups.length - 1) setStoryIndex((i) => i + 1)
              else setViewingStory(null)
            }}
            onPrev={() => setStoryIndex((i) => Math.max(0, i - 1))}
            onDelete={(s: Story) => {
              askConfirm({
                title: 'Erase this story?',
                confirmLabel: 'Erase',
                action: async () => {
                  await deleteDoc(doc(firestore, 'stories', s.id))
                  // A story made from a post shares that post's file - only erase the file
                  // if no post owns it (i.e. it was uploaded directly as a story).
                  if (!(await storyMediaBelongsToPost(s))) void deleteUploadedFile(s.mediaUrl)
                  setViewingStory(null)
                  notify('Story removed.')
                },
              })
            }}
          />
        )}

        {commentsForStory && (
          <CommentsModal
            post={commentsForStory}
            comments={storyComments}
            currentUserId={user.uid}
            zIndex={110}
            onClose={() => setCommentsForStory(null)}
            onSend={(txt: string, reply?: CommentReplyMeta) => addStoryComment(commentsForStory, txt, reply)}
            onToggleLikeComment={(c: PostComment) => toggleStoryCommentLike(commentsForStory, c)}
            onEdit={(story: Story, c: PostComment, txt: string) => editStoryComment(story, c, txt)}
            onDelete={(story: Story, c: PostComment) => deleteStoryComment(story, c)}
            onTogglePin={(c: PostComment) => togglePinStoryComment(commentsForStory, c)}
            onViewProfile={(uid: string) => { setCommentsForStory(null); setViewingStory(null); openProfileByUid(uid) }}
          />
        )}

        {/* Upload Story Modal (60s Video + Photo, Resumable Chunked Upload, Live MB Tracker, Privacy) */}
        {showCreateStory && (
          <CreateStoryModal
            userId={user.uid}
            authorName={profile.name}
            authorAvatar={profile.avatar}
            onClose={() => setShowCreateStory(false)}
            onSuccess={() => {
              setShowCreateStory(false)
              notify('Story synchronized!')
              void refreshAchievementStats()
            }}
          />
        )}

        {/* Upload: the "+" in Shonen opens the gallery straight away; once a video or photo is picked,
            the clean composer drops down for title / tags / caption. */}
        {(reelDraft || showNewPost) && (
          <div className="pc-sheet-backdrop">
            <div className="pc-sheet">
              <PostComposer
                variant="sheet"
                videoOnly={Boolean(reelDraft) && !(reelDraft as File).type.startsWith('image/')}
                initialFile={reelDraft}
                remixSound={remixSound}
                userId={user.uid}
                authorName={profile.name}
                authorAvatar={profile.avatar}
                coins={coinsApi}
                onAddCoins={() => setShowWallet(true)}
                onCancel={() => { setReelDraft(null); setShowNewPost(false); setRemixSound(null) }}
                onPosted={() => {
                  setReelDraft(null)
                  setShowNewPost(false)
                  setRemixSound(null)
                  notify('Uploaded!')
                  void refreshAchievementStats()
                }}
                onRequestBoost={(p: { id: string }) => setBoostTarget({ kind: 'post', item: { id: p.id } as Post })}
              />
            </div>
          </div>
        )}

        {/* Profile Edit Modal */}
        {showProfileEdit && (
          <ProfileModal
            profile={profile}
            busy={busy}
            isPremium={isPremiumActive}
            onRequirePremium={() => setShowTickSheet(true)}
            coinBalance={coinBalance}
            onAddCoins={() => setShowWallet(true)}
            isAdmin={isAdminAccount}
            askConfirm={askConfirm}
            onClose={() => setShowProfileEdit(false)}
            onSave={async (next: any): Promise<string | void> => {
              setBusy(true)
              let chargedCoins = 0
              let chargedRupees = 0 // > 0 when a short username was just bought with Google Play
              let usernamePending = false
              try {
                // ---- Username: rules, availability and payment are all settled atomically on the
                // server (see changeUsername / the `usernameChange` branch of /api/wallet/transfer).
                // 1-4 characters = premium handles, bought once with Google Play (real money).
                // 5+ characters: first change free, then a small coin fee. Names that use someone else's brand / trademark are refused (policy violation).
                const wanted = String(next.username || '').trim().replace(/^@/, '').toLowerCase()
                let finalUsername = profile.username
                if (wanted && wanted !== String(profile.username).toLowerCase()) {
                  const check = checkUsername(wanted)
                  if (!check.ok) return check.message
                  // Rename limit (5 renames -> 15 days locked). The server enforces it too; this just answers faster.
                  const lockedUntil = Number(profile.usernameChangeLockUntil || 0)
                  if (!isAdminAccount && lockedUntil > Date.now()) return usernameLockText(lockedUntil)
                  const cost = usernameChangeCost(String(profile.username), wanted, {
                    changeUsed: Boolean(profile.usernameChangeUsed), credits: profile.customUsernameCredits || 0, isAdmin: isAdminAccount,
                  })
                  if (cost.kind === 'premium') {
                    // 1-4 character handle: one-time REAL-MONEY purchase through Google Play. The server verifies the payment,
                    // reserves the handle and renames the account in one step (see /api/billing/verify).
                    if (!billingAvailable()) return 'Usernames with 1-4 characters can only be bought in the Animora Android app (Google Play).'
                    const bought = await buyUsername(wanted)
                    if (!bought.ok) {
                      if (bought.cancelled) return 'Cancelled - your username was not changed.'
                      if (bought.error === 'username-change-locked') return usernameLockText(Number(bought.until || 0))
                      return billingErrorText(bought.error)
                    }
                    chargedRupees = cost.rupees
                    finalUsername = wanted
                  } else {
                    if (cost.coins > 0 && cost.coins > coinBalance) return `You need ${cost.coins - coinBalance} more coins for this username.`
                    // Atomic on the server: the coin spend, the usernames/{name} reservation swap
                    // and the users/{uid} username fields all move together, or none of them do.
                    await changeUsername(wanted, cost.coins, next.loseHandleConfirmed === true)
                    chargedCoins = cost.coins
                    finalUsername = wanted
                  }
                }
                // Keep usernameLower in sync so search still finds this account after a rename -
                // this is only the LOCAL mirror of what the server already committed above; the
                // username/usernameLower fields themselves are no longer directly client-writable.
                const { username: _typed, loseHandleConfirmed: _lose, ...rest } = next
                const payload = { ...rest }
                await updateDoc(doc(firestore, 'users', user.uid), { ...payload, updatedAt: serverTimestamp() })
                setProfile((c) => c ? { ...c, ...payload, username: finalUsername, usernameLower: String(finalUsername || '').trim().toLowerCase() } : c)
                // Keep every post/story this user has already shared in sync with their
                // latest avatar/name, instead of leaving old posts stuck on the old one.
                const contentPatch: { authorName?: string; authorAvatar?: string } = {}
                if (next.avatar && next.avatar !== profile.avatar) contentPatch.authorAvatar = next.avatar
                if (next.name && next.name !== profile.name) contentPatch.authorName = next.name
                void syncAuthorInfoAcrossContent(user.uid, contentPatch)
                setShowProfileEdit(false)
                notify(usernamePending ? 'Done! Your new username will appear in a minute - please reopen the app if it does not.'
                  : chargedRupees > 0 ? `@${finalUsername} is yours!`
                  : chargedCoins > 0 ? `@${finalUsername} is yours! ${chargedCoins} coins used.` : 'Profile updated.')
              } catch (err) {
                if (err instanceof Error && err.message === '__cancelled__') return 'Cancelled - your username was not changed.'
                if (err instanceof Error) return friendlyError(err, 'Could not change your username. Please try again.')
                notify('Failed to save.')
              } finally { setBusy(false) }
            }}
            onUploadAvatar={async (file: File) => uploadWithProgress(file, 'avatar', user.uid)}
            onUploadCover={async (file: File) => uploadWithProgress(file, 'cover', user.uid)}
          />
        )}

        {/* Group Creation Modal */}
        {showGroup && (
          <GroupModal
            friends={chats.filter((c) => c.kind === 'direct')}
            busy={busy}
            onClose={() => setShowGroup(false)}
            onCreate={async (name: string, selected: string[], privacy: 'public' | 'private', joinMode: 'auto' | 'manual') => {
              // A private guild needs at least one friend to start with; a public one can start empty (people find + join it).
              if (!name.trim() || (privacy === 'private' && !selected.length)) return
              const members = Array.from(new Set([user.uid, ...selected]))
              if (members.length > guildMemberLimit) { setShowGroup(false); guildLimitReached(); return }
              setBusy(true)
              try {
                await addDoc(collection(firestore, 'groupChats'), {
                  name: name.trim(),
                  nameLower: name.trim().toLowerCase(),
                  ownerId: user.uid,
                  members,
                  admins: [user.uid],
                  avatar: fallbackAvatarSvg(name.trim()),
                  privacy,
                  ...(privacy === 'public' ? { joinMode } : {}),
                  createdAt: serverTimestamp(),
                })
                setShowGroup(false)
                notify(privacy === 'public' ? 'Public guild commissioned - people can now find it.' : 'Group channel commissioned.')
              } catch { notify('Commission failed.') } finally { setBusy(false) }
            }}
          />
        )}

        {/* Discover people: 10 random people (Shuffle = 10 new ones). Opened from the icon on EVERY profile (yours and other people's). */}
        {showDiscoverPeople && (
          <DiscoverPeopleModal
            userId={user.uid}
            following={following}
            sentRequests={sentRequests}
            friendIds={new Set(chats.filter((c) => c.kind !== 'group').map((c) => c.id))}
            requests={requests}
            followBusy={followBusy}
            hiddenIds={bannedUids}
            onAdd={(target: Profile) => { void connectWith(target, `Connection requested to ${target.name}.`) }}
            onCancel={async (target: Profile) => {
              try {
                await deleteDoc(doc(firestore, 'friendRequests', `${user.uid}_${target.uid}`))
                setSentRequests((c) => { const n = new Set(c); n.delete(target.uid); return n })
                notify('Request cancelled.')
              } catch { notify('Could not cancel request.') }
            }}
            onFollow={(target: Profile) => toggleFollow(target.uid, target.name)}
            onViewProfile={(uid: string) => { setShowDiscoverPeople(false); void openProfileByUid(uid) }}
            onClose={() => setShowDiscoverPeople(false)}
          />
        )}

        {/* Public Guilds: discover (random picks + search), join instantly or send a request */}
        {showPublicGuilds && (
          <PublicGuildsModal
            userId={user.uid}
            me={{ name: profile?.name || '', username: profile?.username || '', avatar: profile?.avatar || '' }}
            myGroupIds={chats.filter((c) => c.kind === 'group').map((c) => c.id)}
            onClose={() => setShowPublicGuilds(false)}
            onJoined={(g: { name: string }) => { setShowPublicGuilds(false); setShowChats(true); notify(`You joined ${g.name}.`) }}
            onRequested={(g: { id: string; name: string; admins: string[] }) => {
              g.admins.slice(0, 5).forEach((a) => { void sendAppNotification(a, 'group_request', `wants to join "${g.name}".`, g.id) })
              notify('Request sent. You will be added once an admin accepts.')
            }}
          />
        )}

        {/* Friends Management List */}
        {showFriendsList && (
          <FriendsListModal
            friends={allFriends}
            busy={busy}
            onClose={() => setShowFriendsList(false)}
            onOpenChat={async (f: Chat) => {
              unhideDm(f.id)
              try { await setDoc(doc(firestore, 'friends', user.uid, 'list', f.id), { hidden: false }, { merge: true }) } catch { notify('Could not open this chat. Please try again.'); return }
              setShowFriendsList(false)
              setShowChats(true)
              setActiveChat({ ...f, hidden: false })
            }}
            onRemoveFriend={(f: Chat) => {
              askConfirm({
                title: `Unlink ${f.name}?`,
                message: 'You\'ll no longer be connected, and your chat history stays but no new messages can be sent until you reconnect.',
                confirmLabel: 'Unlink',
                action: async () => {
                  await setDoc(doc(firestore, 'friends', user.uid, 'list', f.id), { status: 'removed' }, { merge: true })
                  await setDoc(doc(firestore, 'friends', f.id, 'list', user.uid), { status: 'removed' }, { merge: true })
                  setChats((c) => c.filter((x) => x.id !== f.id))
                  notify('Operative unlinked.')
                },
              })
            }}
          />
        )}

        {/* Add friend: its own full-screen page (was a tab inside Discover, next to the leaderboards) */}
        {showAddFriend && (
          <AddFriendPage
            search={peopleSearch}
            setSearch={setPeopleSearch}
            results={contacts}
            randomUsers={randomUsers}
            randomLoading={randomLoading}
            onShuffle={fetchRandomUsers}
            requests={requests}
            following={following}
            sentRequests={sentRequests}
            friendIds={new Set(chats.filter((c) => c.kind !== 'group').map((c) => c.id))}
            followBusy={followBusy}
            busy={busy}
            onSearch={searchPeople}
            onAdd={(target: Profile) => { void connectWith(target, `Connection requested to ${target.name}.`) }}
            onCancel={async (target: Profile) => {
              try {
                await deleteDoc(doc(firestore, 'friendRequests', `${user.uid}_${target.uid}`))
                setSentRequests((c) => { const n = new Set(c); n.delete(target.uid); return n })
                notify('Request cancelled.')
              } catch { notify('Could not cancel request.') }
            }}
            onFollow={(target: Profile) => toggleFollow(target.uid, target.name)}
            onViewProfile={openProfileByUid}
            onClose={() => setShowAddFriend(false)}
          />
        )}

        {/* Profile Card View Modal */}
        {viewingProfile && (
          <ProfileViewModal
            profile={viewingProfile}
            loading={viewingProfileLoading}
            isOwnProfile={viewingProfile.uid === user.uid}
            isFollowing={following.has(viewingProfile.uid)}
            isFriend={allFriends.some((c) => c.id === viewingProfile.uid)}
            isBestFriend={(() => {
              // "Best Friends" = one of your 3 most recently active Allies (messaged in the last 14 days).
              const cutoff = Date.now() - 14 * 24 * 60 * 60 * 1000
              return [...allFriends].filter((c) => (c.sortAt || 0) > cutoff).sort((x, y) => (y.sortAt || 0) - (x.sortAt || 0)).slice(0, 3).some((c) => c.id === viewingProfile.uid)
            })()}
            requested={sentRequests.has(viewingProfile.uid)}
            posts={viewingProfilePosts}
            busy={busy}
            followBusy={followBusy}
            onClose={() => setViewingProfile(null)}
            onFollow={() => toggleFollow(viewingProfile.uid, viewingProfile.name)}
            onOpenDiscover={() => setShowDiscoverPeople(true)}
            onRequest={() => { void connectWith(viewingProfile, 'Alliance request dispatched.') }}
            onCancelRequest={async () => {
              try {
                await deleteDoc(doc(firestore, 'friendRequests', `${user.uid}_${viewingProfile.uid}`))
                setSentRequests((c) => { const n = new Set(c); n.delete(viewingProfile.uid); return n })
                notify('Request cancelled.')
              } catch { notify('Could not cancel request.') }
            }}
            onOpenFollowList={(uid: string, name: string, kind: 'followers' | 'following') => openFollowList(uid, name, kind)}
            onOpenPostPreview={(p: Post) => {
              setViewingProfile(null)
              openPostInReelFeed(p)
            }}
            onOpenSupport={(uid: string, name: string) => setSupportCreator({ uid, name })}
            onChat={() => openChatWithProfile({ uid: viewingProfile.uid, name: viewingProfile.name, username: viewingProfile.username, avatar: viewingProfile.avatar })}
            subscription={mySubsNamed.find((x) => x.creatorId === viewingProfile.uid) ?? null}
            onUnsubscribe={() => handleUnsubscribe(`${user.uid}_${viewingProfile.uid}`)}
            blockMode={blockRecords[viewingProfile.uid]?.mode ?? null}
            blockBusy={blockBusy}
            onBlockAction={(action: BlockAction) => blockAction({ uid: viewingProfile.uid, name: viewingProfile.name, username: viewingProfile.username, avatar: viewingProfile.avatar }, action)}
            isAdmin={isAdminAccount}
            onAdminRestrict={(kind: 'chat' | 'upload') => setRestrictSheet({ uid: viewingProfile.uid, username: viewingProfile.username, kind, until: restrictLeft(kind === 'chat' ? viewingProfile.chatBanUntil : viewingProfile.uploadHoldUntil), reason: (kind === 'chat' ? viewingProfile.chatBanReason : viewingProfile.uploadHoldReason) || '' })}
            onAdminBan={() => setProfileBanTarget(viewingProfile)}
            onReportAccount={() => setReportTarget({ targetUid: viewingProfile.uid, targetUsername: viewingProfile.username, targetName: viewingProfile.name, resolved: true, contentType: 'account', contentId: viewingProfile.uid, contentText: viewingProfile.bio || '' })}
          />
        )}

        {/* Blocked / restricted / hidden accounts (Settings) */}
        {showCommentFilter && user && <CommentFilterModal uid={user.uid} onClose={() => setShowCommentFilter(false)} notify={notify} />}
        {showReferralCode && user && <ReferralCodeModal user={user} onClose={() => setShowReferralCode(false)} />}
        {refCongrats !== null && <ReferralCongratsModal coins={refCongrats} onClose={() => setRefCongrats(null)} />}

        {showMsgRequests && (
          <MessageRequestsModal
            requests={dmPendingIn}
            busy={dmBusy}
            onClose={() => setShowMsgRequests(false)}
            onOpen={(r: DmRequest) => {
              setShowMsgRequests(false)
              setActiveChat({ id: r.otherId, name: r.name, handle: r.handle, avatar: r.avatar, preview: r.lastMessage, time: r.time, kind: 'direct' })
            }}
            onAccept={(r: DmRequest) => void respondDmRequest(r, 'accept')}
            onDelete={(r: DmRequest) => void respondDmRequest(r, 'delete')}
          />
        )}

        {showNotifSettings && user && (
          <NotificationSettingsModal uid={user.uid} off={Array.isArray((profile as any)?.notifOff) ? (profile as any).notifOff : []} onClose={() => setShowNotifSettings(false)} />
        )}

        {hashtagView && (
          <HashtagModal tag={hashtagView} posts={posts} onClose={() => setHashtagView(null)} />
        )}

        {showSaved && user && (
          <SavedPostsModal uid={user.uid} onClose={() => setShowSaved(false)} />
        )}

        {showBlockedList && (
          <BlockedAccountsModal
            entries={Object.values(blockRecords).sort((a, b) => a.username.localeCompare(b.username))}
            busy={blockBusy}
            onClose={() => setShowBlockedList(false)}
            onUndo={(e: BlockEntry) => blockAction(e, e.mode === 'block' ? 'unblock' : e.mode === 'restrict' ? 'unrestrict' : 'unhide')}
            onOpenProfile={(uid: string) => { setShowBlockedList(false); void openProfileByUid(uid) }}
          />
        )}

        {/* Followers / Following Listing */}
        {followListing && (
          <FollowListModal
            kind={followListing.kind}
            name={followListing.name}
            items={followListing.items}
            loading={followListingLoading}
            onClose={() => setFollowListing(null)}
            onViewProfile={openProfileByUid}
          />
        )}

        {/* Forward Picker Modal */}
        {forwardTarget && (
          <ForwardPickerModal
            chats={chats.filter((c) => c.id !== activeChat?.id)}
            busy={busy}
            onClose={() => setForwardTarget(null)}
            onForward={async (c: Chat) => {
              const chatId = c.kind === 'group' ? c.id : [user.uid, c.id].sort().join('_')
              await addDoc(collection(firestore, 'chats', chatId, 'messages'), {
                text: forwardTarget.text,
                ...(isStickerMsg(forwardTarget) ? { sharedImageUrl: forwardTarget.sharedImageUrl, sticker: true } : {}),
                userId: user.uid,
                senderName: profile.name,
                senderAvatar: profile.avatar,
                read: false,
                forwarded: true,
                createdAt: serverTimestamp(),
              })
              setForwardTarget(null)
              notify(`Relayed to ${c.name}.`)
            }}
          />
        )}

        {/* Group Info Modal */}
        {showGroupInfo && activeChat && activeChat.kind === 'group' && (
          <GroupInfoModal
            chat={activeChat}
            currentUserId={user.uid}
            memberProfiles={groupMemberProfiles}
            presenceMap={presenceMap}
            nowTick={nowTick}
            friendsNotInGroup={chats.filter((c) => c.kind === 'direct' && !(activeChat.members || []).includes(c.id))}
            loading={groupMemberProfilesLoading}
            busy={busy}
            memberLimit={guildMemberLimit}
            slotPriceCoins={GROUP_MEMBER_BONUS_PRICE_COINS}
            slotShortCoins={Math.max(0, GROUP_MEMBER_BONUS_PRICE_COINS - coinBalance)}
            slotIncrement={GROUP_MEMBER_BONUS_INCREMENT}
            buyingSlot={buyingGroupSlot}
            onBuySlot={buyGroupMemberSlot}
            onClose={() => setShowGroupInfo(false)}
            joinRequests={groupJoinRequests}
            onSetPrivacy={async (privacy: 'public' | 'private') => {
              try {
                const patch = privacy === 'public'
                  ? { privacy, joinMode: activeChat.joinMode === 'auto' ? 'auto' : 'manual', nameLower: String(activeChat.name || '').trim().toLowerCase() }
                  : { privacy }
                await updateDoc(doc(firestore, 'groupChats', activeChat.id), patch)
                setActiveChat((c) => c ? { ...c, ...patch } as Chat : c)
                notify(privacy === 'public' ? 'Guild is now public - people can find it in Public Guilds.' : 'Guild is now private.')
              } catch { notify('Could not change the guild type.') }
            }}
            onSetJoinMode={async (joinMode: 'auto' | 'manual') => {
              try {
                await updateDoc(doc(firestore, 'groupChats', activeChat.id), { joinMode })
                setActiveChat((c) => c ? { ...c, joinMode } : c)
                notify(joinMode === 'auto' ? 'Auto approval on - anyone can join instantly.' : 'Manual approval on - you accept each request.')
              } catch { notify('Could not change approval mode.') }
            }}
            onApproveRequest={async (req: GroupJoinRequest) => {
              if ((activeChat.members || []).length >= guildMemberLimit) { setShowGroupInfo(false); guildLimitReached(); return }
              try {
                const b = writeBatch(firestore)
                b.update(doc(firestore, 'groupChats', activeChat.id), { members: arrayUnion(req.uid) })
                b.delete(doc(firestore, 'groupJoinRequests', req.id))
                await b.commit()
                setActiveChat((c) => c ? { ...c, members: [...(c.members || []).filter((x) => x !== req.uid), req.uid] } : c)
                void sendAppNotification(req.uid, 'group_request', `accepted your request to join "${activeChat.name}".`, activeChat.id)
                notify(`${req.name || 'Member'} enlisted.`)
              } catch { notify('Could not accept the request.') }
            }}
            onDeclineRequest={async (req: GroupJoinRequest) => {
              try {
                await updateDoc(doc(firestore, 'groupJoinRequests', req.id), { status: 'declined' })
                notify('Request declined.')
              } catch { notify('Could not decline the request.') }
            }}
            onRename={async (name: string) => {
              await updateDoc(doc(firestore, 'groupChats', activeChat.id), { name, nameLower: name.trim().toLowerCase() })
              setActiveChat((c) => c ? { ...c, name } : c)
              notify('Guild renamed.')
            }}
            onChangePhoto={async (file: File) => {
              const url = await uploadWithProgress(file, 'group', activeChat.id)
              await updateDoc(doc(firestore, 'groupChats', activeChat.id), { avatar: url })
              setActiveChat((c) => c ? { ...c, avatar: url } : c)
              notify('Guild insignia updated.')
            }}
            onPromote={(uid: string) => {
              const m = groupMemberProfiles.find((p) => p.uid === uid)
              askConfirm({
                title: `Make ${m?.name || 'this member'} an admin?`,
                message: 'They\'ll be able to add or remove members, promote other admins, and edit the group.',
                confirmLabel: 'Make Admin',
                danger: false,
                action: async () => {
                  await updateDoc(doc(firestore, 'groupChats', activeChat.id), { admins: arrayUnion(uid) })
                  setActiveChat((c) => c ? { ...c, admins: [...(c.admins || []), uid] } : c)
                  notify('Commander role assigned.')
                },
              })
            }}
            onKick={(uid: string) => {
              const m = groupMemberProfiles.find((p) => p.uid === uid)
              askConfirm({
                title: `Remove ${m?.name || 'this member'} from the group?`,
                message: 'They\'ll be removed immediately and won\'t see new messages unless added back.',
                confirmLabel: 'Remove',
                action: async () => {
                  await updateDoc(doc(firestore, 'groupChats', activeChat.id), { members: arrayRemove(uid), admins: arrayRemove(uid) })
                  setActiveChat((c) => c ? { ...c, members: (c.members || []).filter((x) => x !== uid) } : c)
                  notify('Member removed.')
                },
              })
            }}
            onAddMember={async (uid: string) => {
              if ((activeChat.members || []).length >= guildMemberLimit) { setShowGroupInfo(false); guildLimitReached(); return }
              await updateDoc(doc(firestore, 'groupChats', activeChat.id), { members: arrayUnion(uid) })
              setActiveChat((c) => c ? { ...c, members: [...(c.members || []), uid] } : c)
              notify('Member enlisted.')
            }}
            onLeaveGroup={() => {
              askConfirm({
                title: `Leave "${activeChat.name}"?`,
                message: activeChat.privacy === 'public' ? 'You can find it again in Public Guilds.' : 'You can only rejoin if a member adds you back.',
                confirmLabel: 'Leave',
                action: async () => {
                  try {
                    await updateDoc(doc(firestore, 'groupChats', activeChat.id), {
                      members: arrayRemove(user.uid),
                      admins: arrayRemove(user.uid)
                    })
                  } catch { /* proceed optimistically */ }
                  setChats((prev) => prev.filter((c) => c.id !== activeChat.id))
                  setShowGroupInfo(false)
                  setActiveChat(null)
                  notify('Left the group channel.')
                },
              })
            }}
          />
        )}
      </section>

      {status && <div className="toast" role="status">{status}</div>}
      {coinReward && (
        <div className="coin-reward" key={coinReward.key} role="status" aria-live="polite">
          <span className="coin-reward-coin"><CoinIcon size={30} /></span>
          <span className="coin-reward-text">
            <b>+{coinReward.amount} {coinReward.amount === 1 ? 'Coin' : 'Coins'}</b>
            <small>{coinReward.label}</small>
          </span>
        </div>
      )}
      <ConfirmDialog options={confirmOptions} busy={confirmBusy} onCancel={closeConfirm} onConfirm={runConfirm} />
    </main>
    </StoryRingContext.Provider>
    </AuthorInfoContext.Provider>
    </CoinAdContext.Provider>
    </GameThemeContext.Provider>
  )
}

/* =========================================================================
   COMPACT HOME FEED
   ========================================================================= */
const NOTE_MAX_LEN = 60
const NOTE_TTL_MS = 24 * 60 * 60 * 1000

// Brand wordmark used in the app headers (same markup the home header always had: text only, no logo / tagline).
function BrandMark() {
  return <div className="app-head-brand"><strong>Ani<span>mora</span></strong></div>
}

// Notes strip at the top of Comms: your own note first, then Allies who have one. Friends only.
function NotesStrip({ myNote, myAvatar, myName, friendNotes, onEditMine, onOpenFriend }: any) {
  const pic = myAvatar || fallbackAvatarSvg(myName || 'You')
  return (
    <div className="notes-wrap">
      <div className="notes-label"><Lock size={11} strokeWidth={2.4} /> Notes · friends only</div>
      <div className="notes-row" role="list" aria-label="Notes">
        <button type="button" role="listitem" className="note-item is-mine" onClick={onEditMine} aria-label={myNote ? 'Edit your note' : 'Share a note'}>
          <span className={`note-bubble${myNote ? '' : ' is-empty'}`}>{myNote ? myNote.text : 'Share a note'}</span>
          <span className={`note-av ${myNote ? 'has-note' : 'add'}`}><Avatar src={pic} name={myName || 'You'} size="md" showStatus={false} /></span>
          <small>Your note</small>
        </button>
        {friendNotes.map((f: any) => (
          <button key={f.id} type="button" role="listitem" className="note-item" onClick={() => onOpenFriend(f)} aria-label={`${f.name}'s note: ${f.text}`}>
            <span className="note-bubble">{f.text}</span>
            <span className="note-av has-note"><Avatar src={f.avatar} name={f.name} size="md" showStatus={false} uid={f.id} /></span>
            <small>{f.name}</small>
          </button>
        ))}
      </div>
    </div>
  )
}

function NoteEditor({ initial, hasNote, onSave, onDelete, onClose }: { initial: string; hasNote: boolean; onSave: (t: string) => Promise<boolean>; onDelete: () => Promise<void>; onClose: () => void }) {
  const [text, setText] = useState(initial)
  const [busy, setBusy] = useState(false)
  const submit = async () => {
    if (busy || !text.trim()) return
    setBusy(true)
    const ok = await onSave(text)
    setBusy(false)
    if (ok) onClose()
  }
  const remove = async () => {
    if (busy) return
    setBusy(true)
    await onDelete()
    setBusy(false)
    onClose()
  }
  if (typeof document === 'undefined') return null
  return createPortal(
    <div className="modal-backdrop" style={{ zIndex: 200 }} onClick={busy ? undefined : onClose}>
      <div className="modal-card note-editor" role="dialog" aria-label="Your note" onClick={(e) => e.stopPropagation()}>
        <h3>Your note</h3>
        <p className="note-editor-hint"><Lock size={12} /> Only your friends can see this. It disappears after 24 hours.</p>
        <textarea
          autoFocus rows={3} maxLength={NOTE_MAX_LEN} value={text}
          onChange={(e) => setText(e.target.value.replace(/\n/g, ' '))}
          placeholder="What's on your mind?"
        />
        <div className="note-editor-count">{NOTE_MAX_LEN - text.length}</div>
        <div className="note-editor-actions">
          {hasNote && <button type="button" className="text-button" onClick={remove} disabled={busy}>Delete</button>}
          <button type="button" className="text-button primary" onClick={submit} disabled={busy || !text.trim()}>Share</button>
        </div>
      </div>
    </div>,
    document.body
  )
}

// Comms story strip. Your own bubble always comes first and shows YOUR profile picture (never a blank placeholder).
// No live story yet: plain picture + a "+" badge, tap = add a story. Live story: bright ring, tap = watch it.
function CommsMyStory({ myStory, myAvatar, myName, onAddStory, onViewStory }: { myStory: any; myAvatar?: string; myName?: string; onAddStory: () => void; onViewStory: (s: Story) => void }) {
  const storyApi = useContext(StoryRingContext)
  const status = myStory ? storyApi.statusOf(myStory.authorId) : null
  const pic = myAvatar || myStory?.authorAvatar || fallbackAvatarSvg(myName || 'You')
  // The "+" badge is ALWAYS visible, even after you already posted a story, so you can add as many stories as you like.
  // Avatar tap = watch your stories (or add one if you have none); "+" tap = always add a new story.
  return (
    <div role="listitem" className="cs-item cs-item-mine">
      <span className={`cs-ring ${myStory ? (status === 'seen' ? 'seen' : 'has-story') : 'add'}`}>
        <button type="button" className="cs-avatar-btn" onClick={myStory ? () => onViewStory(myStory) : onAddStory} aria-label={myStory ? 'View your story' : 'Add a story'}>
          <Avatar src={pic} name={myName || 'You'} size="md" showStatus={false} />
        </button>
        <button type="button" className="cs-plus" onClick={onAddStory} aria-label="Add a new story"><Plus size={13} strokeWidth={3} /></button>
      </span>
      <small>{myStory ? 'Your Story' : 'Add Story'}</small>
    </div>
  )
}

const CommsStoryItem = memoLatest(CommsStoryItemImpl)
function CommsStoryItemImpl({ story }: { story: Story }) {
  const storyApi = useContext(StoryRingContext)
  const status = storyApi.statusOf(story.authorId)
  return (
    <button type="button" role="listitem" className="cs-item" onClick={() => storyApi.open(story.authorId)}>
      <span className={`cs-ring ${status === 'seen' ? 'seen' : 'has-story'}${story.promoted && status !== 'seen' ? ' promo' : ''}`}>
        <Avatar src={story.authorAvatar} name={story.authorName} size="md" uid={story.authorId} showStatus={false} />
      </span>
      <small>{story.authorName.split(' ')[0]}</small>
    </button>
  )
}

// Your own bubble in the story tray: bright ring until you've watched it yourself, then it dims,
// same as everyone else's.
const MyStoryTrayItem = memoLatest(MyStoryTrayItemImpl)
function MyStoryTrayItemImpl({ myStory, onAddStory, onViewStory }: { myStory: any; onAddStory: () => void; onViewStory: (s: Story) => void }) {
  const storyApi = useContext(StoryRingContext)
  const status = myStory ? storyApi.statusOf(myStory.authorId) : null
  return (
    <button className="story-circle" onClick={myStory ? () => onViewStory(myStory) : onAddStory}>
      <span className={`story-ring ${myStory ? (status === 'seen' ? 'seen' : 'has-story') : ''}`}>
        <Avatar src={myStory?.authorAvatar || ''} name="You" size="md" showStatus={false} />
        <span className="story-plus" onClick={(e) => { e.stopPropagation(); onAddStory() }}><Plus size={14} strokeWidth={3} /></span>
      </span>
      <small>{myStory ? 'Your Story' : 'Add Story'}</small>
    </button>
  )
}

// One other-person bubble in the story tray: bright ring until you've watched all of their stories,
// then it dims.
const StoryTrayItem = memoLatest(StoryTrayItemImpl)
function StoryTrayItemImpl({ story, onViewStory }: { story: Story; onViewStory: (s: Story) => void }) {
  const storyApi = useContext(StoryRingContext)
  const status = storyApi.statusOf(story.authorId)
  // Opens the person's whole set of stories (not just one), so every story gets watched and the ring can dim.
  return (
    <button className="story-circle" onClick={() => storyApi.open(story.authorId)}>
      <span
        className={`story-ring ${status === 'seen' ? 'seen' : 'has-story'}`}
        style={story.promoted && status !== 'seen' ? { background: 'var(--gold)' } : undefined}
      >
        <Avatar src={story.authorAvatar} name={story.authorName} size="md" uid={story.authorId} showStatus={false} />
      </span>
      <small>{story.authorName.split(' ')[0]}</small>
    </button>
  )
}

/* =========================================================================
   USER FEED MODAL - opened by tapping a post inside a profile's post grid
   (Vault Grid). Shows that person's own posts as full feed cards (same
   AdPostCard used in Radar - likes/comments/share/follow/report all work),
   scrolled to the exact post that was tapped, instead of the old stripped
   single-post popup.
   ========================================================================= */
function UserFeedModal({ posts, authorId, focusPostId, userId, following, followBusy, playingPostAudioId, onToggleAudio, suspendVideo, onClose, onLike, onOpenComments, onOpenShareOptions, onOpenGift, onFollow, onDelete, onReport, onOpenBoost, onEdit, onViewProfile }: any) {
  const authorPosts = posts.filter((p: Post) => p.authorId === authorId && p.postKind !== 'reel')
  const [doubleTapId, setDoubleTapId] = useState<string | null>(null)
  const lastTapRef = useRef<number>(0)
  const postRefs = useRef<Record<string, HTMLDivElement | null>>({})
  const jumpedRef = useRef(false)

  useEffect(() => {
    if (jumpedRef.current || !focusPostId) return
    const el = postRefs.current[focusPostId]
    if (!el) return
    jumpedRef.current = true
    el.scrollIntoView({ block: 'start' })
  }, [focusPostId, authorPosts.length])

  // Same double-tap-to-like as the Radar feed; there's no reel hand-off here since
  // reels never enter this list (they keep their own dedicated feed).
  const handleImageTap = (post: Post) => {
    const now = Date.now()
    if (now - lastTapRef.current < 280) {
      if (!post.likes.includes(userId)) onLike(post)
      setDoubleTapId(post.id)
      setTimeout(() => setDoubleTapId(null), 800)
    }
    lastTapRef.current = now
  }

  const headerName = authorPosts[0]?.authorName || 'Feed'

  return (
    <div className="modal-backdrop user-feed-backdrop" style={{ zIndex: 118 }} onClick={onClose}>
      <div className="user-feed-modal" onClick={(e) => e.stopPropagation()}>
        <header className="user-feed-header">
          <button type="button" className="icon-button" onClick={onClose} aria-label="Back"><ArrowLeft size={18} /></button>
          <strong>{headerName}</strong>
          <span style={{ width: 28 }} />
        </header>
        <div className="user-feed-scroll">
          {authorPosts.length === 0 ? (
            <EmptyState icon={<Grid3x3 size={20} />} title="No posts yet" hint="Posts will show up here." compact />
          ) : authorPosts.map((post: Post) => (
            <div key={post.id} ref={(el) => { postRefs.current[post.id] = el }}>
              <AdPostCard
                post={post}
                userId={userId}
                following={following}
                followBusy={followBusy}
                doubleTapId={doubleTapId}
                playingPostAudioId={playingPostAudioId}
                suspendVideo={suspendVideo}
                onImageTap={handleImageTap}
                onToggleAudio={onToggleAudio}
                onLike={onLike}
                onOpenComments={onOpenComments}
                onOpenShareOptions={onOpenShareOptions}
                onOpenGift={onOpenGift}
                onFollow={onFollow}
                onDelete={onDelete}
                onReport={onReport}
                onOpenBoost={onOpenBoost}
                onEdit={onEdit}
                onViewProfile={onViewProfile}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

/* =========================================================================
   ADVERTISEMENT LOOK - edge-to-edge sponsored card for boosted posts (Radar Feed),
   plus the same identity header + floating glass CTA bar reused on boosted reels.
   Styles: globals.css -> "15. ADVERTISEMENT CARDS".
   ========================================================================= */
const adWebsiteUrl = (post: Post) => (post.boostGoal === 'website' ? safeHttpUrl(post.websiteUrl) : '') // only plain http(s) ever becomes a link
const adHostname = (url: string) => { try { return new URL(url).hostname.replace('www.', '') } catch { return url } }

// Gradient avatar ring + name + verified tick + "Sponsored Profile" / "Official Website" badge.
// showBadge controls the tick + badge - only real boosted/promoted posts pass true; plain
// video posts reusing this edge-to-edge layout get just the avatar ring + name.
function AdIdentity({ post, showBadge = true, onClick }: { post: Post; showBadge?: boolean; onClick?: () => void }) {
  const { label: authorLabel, tick: authorTick } = usePostAuthor(post)
  return (
    <span
      className="ad-user-profile"
      // When onClick is given (Radar feed cards) tapping the avatar/name opens that user's profile.
      {...(onClick ? {
        role: 'button',
        tabIndex: 0,
        style: { cursor: 'pointer' },
        onClick: (e: React.MouseEvent) => { e.stopPropagation(); onClick() },
        onKeyDown: (e: React.KeyboardEvent) => { if (e.key === 'Enter') { e.stopPropagation(); onClick() } },
      } : {})}
    >
      <span className="ad-avatar-ring">
        <Avatar src={post.authorAvatar || ''} name={post.authorName || 'Fan'} size="md" storyUid={post.authorId} uid={post.authorId} showStatus={false} />
      </span>
      <span className="ad-user-info">
        <span className="ad-username-row">
          <span className="ad-username">{authorLabel}</span>
          {authorTick && <GreenTick size={17} />}
        </span>
        {showBadge ? (
          <span className="sp-pill sp-pill-sm">Sponsored</span>
        ) : (
          post.time && <span className="ad-timestamp">{post.time}</span>
        )}
      </span>
    </span>
  )
}

// ---- Sponsored format (same look everywhere an ad shows up: Radar feed, Reels, profile /
// explore previews). Sponsor row + "Sponsored" tag + headline + description + media, then a
// "Promoted Content" footer with one button (Learn More for websites, View Profile for profiles).
const sponsoredCtaLabel = (post: Post) => (adWebsiteUrl(post) ? (post.ctaText || 'Learn More') : 'View Profile')

const ExternalLinkIcon = ({ size = 16 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M15 3h6v6" />
    <path d="M10 14 21 3" />
    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
  </svg>
)

// Favicon for an ad's advertiser domain. Tries the advertiser's OWN site first (their real
// favicon, straight from their server) - only falls back to Google's favicon cache if that
// fails (some sites don't serve /favicon.ico at the conventional path), and only after that
// falls back to hiding the image entirely. Google's cache alone was showing a generic globe
// placeholder for domains it hasn't crawled a favicon for yet, even though the real site
// does have one - going to the domain directly fixes that for any live site.
function AdFavicon({ domain, className }: { domain: string; className: string }) {
  if (!domain) return null
  return (
    <img decoding="async"
      src={`https://${domain}/favicon.ico`}
      alt=""
      className={className}
      onError={(e) => {
        const img = e.currentTarget as HTMLImageElement
        if (img.dataset.stage !== 'google') {
          img.dataset.stage = 'google'
          img.src = `https://www.google.com/s2/favicons?sz=64&domain=${domain}`
        } else {
          img.style.display = 'none'
        }
      }}
    />
  )
}

function SponsoredCta({ post, onViewProfile, className = '', showIcon = false }: { post: Post; onViewProfile?: (uid: string) => void; className?: string; showIcon?: boolean }) {
  const url = adWebsiteUrl(post)
  const label = sponsoredCtaLabel(post)
  const content = (
    <>
      <span className="sp-cta-shine" />
      <span className="sp-cta-text">{label}</span>
      {showIcon && <ExternalLinkIcon size={14} />}
    </>
  )
  if (url) {
    return (
      <a href={url} target="_blank" rel="noopener noreferrer sponsored" className={`sp-cta ${className}`} onClick={(e) => e.stopPropagation()}>
        {content}
      </a>
    )
  }
  return (
    <button type="button" className={`sp-cta ${className}`} onClick={(e) => { e.stopPropagation(); onViewProfile?.(post.authorId) }}>
      {content}
    </button>
  )
}

// "Promoted Content" label on the left, CTA button on the right. Always sits on the
// card's own theme background (no per-ad brand color/banner-image fill) so it never
// clashes with the card around it - a raw advertiser color composited at low opacity
// was showing up as a mismatched tinted patch against the app's light/dark theme.
function SponsoredFooter({ post, onViewProfile, className = '', hideLabel = false }: { post: Post; onViewProfile?: (uid: string) => void; className?: string; hideLabel?: boolean }) {
  const url = adWebsiteUrl(post)
  const domain = url ? adHostname(url) : ''
  return (
    <div className={`sp-footer ${className}`}>
      {!hideLabel && (
        <span className="sp-footer-label">
          {domain ? <AdFavicon domain={domain} className="sp-footer-favicon" /> : <ExternalLinkIcon size={16} />}
          Promoted Content{domain ? ` · ${domain}` : ''}
        </span>
      )}
      <SponsoredCta post={post} onViewProfile={onViewProfile} showIcon />
    </div>
  )
}

// Reel version: a compact "Promoted Content" card floating just above the username row -
// label + external-link icon on top, headline/description with a small preview thumbnail,
// then the CTA button - matches a premium native ad card look while staying short enough
// that it never covers the reel itself (see .sp-reel-card in globals.css).
function AdCtaBar({ post, onViewProfile, className = '' }: { post: Post; onViewProfile: (uid: string) => void; className?: string }) {
  const url = adWebsiteUrl(post)
  const domain = url ? adHostname(url) : ''
  const headline = post.title || (url ? domain : post.authorName || 'Sponsored')
  const desc = post.caption || ''
  // Small preview image next to the copy: prefers the advertiser's own banner image, then
  // falls back to the reel's own cover frame / photo so the card never looks empty.
  const thumb = post.bannerImageUrl || post.thumbnailUrl || (post.mediaType !== 'video' ? post.imageUrl : '')
  return (
    <div className={`sp-reel-card ${className}`}>
      <div className="sp-reel-card-top">
        <span className="sp-reel-card-label"><Megaphone size={14} /> Promoted Content</span>
        <ExternalLinkIcon size={13} />
      </div>
      {domain && (
        <div className="sp-reel-card-site">
          <AdFavicon domain={domain} className="sp-reel-card-favicon" />
          <span>{domain}</span>
        </div>
      )}
      <div className="sp-reel-card-body">
        <div className="sp-reel-card-copy">
          <p className="sp-reel-card-title">{headline}</p>
          {desc && <p className="sp-reel-card-desc">{desc}</p>}
        </div>
        {thumb && <img decoding="async" className="sp-reel-card-thumb" src={thumb} alt="" loading="lazy" />}
      </div>
      <SponsoredCta post={post} onViewProfile={onViewProfile} className="sp-reel-card-cta" />
    </div>
  )
}

// Sponsor row: avatar + name on the left, "Sponsored" tag + 3-dot menu on the right.
function SponsoredHeader({ post, onViewProfile, menu }: { post: Post; onViewProfile?: (uid: string) => void; menu?: React.ReactNode }) {
  return (
    <div className="sp-header">
      <button type="button" className="sp-brand" onClick={() => onViewProfile?.(post.authorId)}>
        <Avatar src={post.authorAvatar || ''} name={post.authorName || 'Fan'} size="md" storyUid={post.authorId} uid={post.authorId} showStatus={false} />
        <span className="sp-name"><PostAuthorName post={post} /></span>
      </button>
      <div className="sp-header-right">
        <span className="sp-pill">Sponsored</span>
        {menu}
      </div>
    </div>
  )
}

// 3-dot menu on a sponsored card: owner gets Edit + Delete, everyone else gets Link + Report.
function SponsoredMenu({ post, userId, following, followBusy, onFollow, onDelete, onEdit, onReport }: any) {
  const [open, setOpen] = useState(false)
  const isOwner = post.authorId === userId
  const close = () => setOpen(false)
  return (
    <>
      <button type="button" className="sp-menu-btn" aria-label="More options" aria-expanded={open} onClick={(e) => { e.stopPropagation(); setOpen((o) => !o) }}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <circle cx="12" cy="5" r="1.9" /><circle cx="12" cy="12" r="1.9" /><circle cx="12" cy="19" r="1.9" />
        </svg>
      </button>
      {open && (
        <>
          <button type="button" className="sp-menu-backdrop" aria-label="Close menu" onClick={close} />
          <div className="sp-menu" role="menu">
            {isOwner ? (
              <>
                <button type="button" role="menuitem" className="sp-menu-item" onClick={() => { close(); onEdit?.(post) }}>
                  <Pencil size={16} /> Edit post
                </button>
                <button type="button" role="menuitem" className="sp-menu-item danger" onClick={() => { close(); onDelete?.(post) }}>
                  <Trash2 size={16} /> Delete post
                </button>
              </>
            ) : (
              <>
                <button type="button" role="menuitem" className="sp-menu-item" disabled={followBusy === post.authorId} onClick={() => { close(); onFollow?.(post.authorId, post.authorName) }}>
                  <UserPlus size={16} /> {following?.has(post.authorId) ? 'Linked' : `Link ${post.authorName}`}
                </button>
                <button type="button" role="menuitem" className="sp-menu-item danger" onClick={() => { close(); onReport?.(post) }}>
                  <Flag size={16} /> Report ad
                </button>
              </>
            )}
          </div>
        </>
      )}
    </>
  )
}

// Post media (image / video / audio button) shared by the normal and sponsored feed cards.
// Swipeable multi-photo viewer (native scroll-snap: smooth, no gesture code to fight the page scroll).
// `fit` = 'cover' fills the feed's square card; 'contain' shows every photo whole (preview modal).
function PostImageCarousel({ urls, fit = 'cover', firstSlide }: { urls: string[]; fit?: 'cover' | 'contain'; firstSlide?: React.ReactNode }) {
  const total = urls.length + (firstSlide ? 1 : 0)
  const [idx, setIdx] = useState(0)
  const trackRef = useRef<HTMLDivElement | null>(null)
  const goTo = (i: number) => {
    const el = trackRef.current
    if (!el) return
    el.scrollTo({ left: i * el.clientWidth, behavior: 'smooth' })
  }
  return (
    <div className={`post-carousel fit-${fit}`}>
      <div
        ref={trackRef}
        className="post-carousel-track"
        onScroll={(e) => {
          const el = e.currentTarget
          const i = Math.round(el.scrollLeft / Math.max(1, el.clientWidth))
          if (i !== idx) setIdx(i)
        }}
      >
        {firstSlide && <div className="post-carousel-slide is-video-slide">{firstSlide}</div>}
        {urls.map((u, i) => (
          <div className="post-carousel-slide" key={`${u}-${i}`}>
            <img decoding="async"
              src={u}
              alt=""
              draggable={false}
              loading={i === 0 && !firstSlide ? 'eager' : 'lazy'}
              onError={(e) => {
                const img = e.currentTarget
                if (!img.dataset.retried) {
                  img.dataset.retried = '1'
                  setTimeout(() => { img.src = `${u}${u.includes('?') ? '&' : '?'}retry=${Date.now()}` }, 1200)
                }
              }}
            />
          </div>
        ))}
      </div>
      <span className="post-carousel-count">{idx + 1}/{total}</span>
      {idx > 0 && (
        <button type="button" className="post-carousel-nav prev" aria-label="Previous photo" onClick={(e) => { e.stopPropagation(); goTo(idx - 1) }}>
          <ChevronLeft size={18} />
        </button>
      )}
      {idx < total - 1 && (
        <button type="button" className="post-carousel-nav next" aria-label="Next photo" onClick={(e) => { e.stopPropagation(); goTo(idx + 1) }}>
          <ChevronRight size={18} />
        </button>
      )}
      <div className="post-carousel-dots" aria-hidden="true">
        {Array.from({ length: total }).map((_, i) => <span key={i} className={i === idx ? 'on' : ''} />)}
      </div>
    </div>
  )
}

function AdMediaBlock({ post, doubleTapId, playingPostAudioId, suspendVideo, onImageTap, onToggleAudio, homeFormat = false }: any) {
  // New videos know their own ratio: the card takes that shape (clamped 3:4 .. 1.91:1) and shows the
  // whole video, so nothing is cropped. Older videos don't have a stored ratio - read it from the
  // file itself so they still fit properly instead of being force-cropped into a square.
  const [detectedRatio, setDetectedRatio] = useState<number>(0)
  const hasStoredRatio = post.mediaType === 'video' && typeof post.mediaRatio === 'number' && post.mediaRatio > 0
  useEffect(() => {
    if (post.mediaType !== 'video' || hasStoredRatio || detectedRatio) return
    const v = document.createElement('video')
    v.preload = 'metadata'
    v.muted = true
    v.onloadedmetadata = () => {
      if (v.videoWidth && v.videoHeight) setDetectedRatio(v.videoWidth / v.videoHeight)
      v.onloadedmetadata = null
      v.removeAttribute('src')
      v.load()
    }
    v.src = resolveMediaUrl(post.imageUrl) as string
    return () => { v.onloadedmetadata = null; v.removeAttribute('src') }
  }, [post.mediaType, post.imageUrl, hasStoredRatio, detectedRatio])
  const rawRatio = hasStoredRatio ? post.mediaRatio : detectedRatio
  const multi = post.mediaType !== 'video' && Array.isArray(post.imageUrls) && post.imageUrls.length > 1
  // Photos: card takes the picture's own shape (so nothing is cropped) and the image is shown with object-fit: contain.
  const [imgRatio, setImgRatio] = useState<number>(0)
  const firstPhotoUrl: string = multi ? post.imageUrls[0] : post.imageUrl
  useEffect(() => {
    if (post.mediaType === 'video' || !multi || !firstPhotoUrl) return
    let alive = true
    const im = new Image()
    im.onload = () => { if (alive && im.naturalWidth && im.naturalHeight) setImgRatio(im.naturalWidth / im.naturalHeight) }
    im.src = resolveMediaUrl(firstPhotoUrl) as string
    return () => { alive = false; im.onload = null }
  }, [post.mediaType, multi, firstPhotoUrl])
  const ratio = homeFormat && !post.promoted
    ? snapFeedRatio(post.mediaType === 'video' ? rawRatio : imgRatio) // Home feed: always square or 4:5
    : post.mediaType === 'video'
      ? (rawRatio ? Math.min(1.91, Math.max(0.75, rawRatio)) : 0)
      : (imgRatio ? Math.min(1.91, Math.max(0.5, imgRatio)) : 0)
  // Scrolled completely past this post while its song plays -> song off (it should not follow you down the feed).
  const mediaRef = useRef<HTMLDivElement | null>(null)
  const songPlayingHere = Boolean(post.audioUrl) && playingPostAudioId === post.id
  useEffect(() => {
    const el = mediaRef.current
    if (!songPlayingHere || !el || typeof IntersectionObserver === 'undefined') return
    const io = new IntersectionObserver(([e]) => { if (!e.isIntersecting) stopFeedPostAudio() }, { threshold: 0 })
    io.observe(el)
    return () => io.disconnect()
  }, [songPlayingHere])
  return (
    <div
      ref={mediaRef}
      className={`ad-media ${post.mediaType === 'video' ? 'is-video' : 'is-photo'} ${ratio ? 'has-ratio' : ''}`}
      style={ratio && !post.promoted ? { aspectRatio: String(ratio) } : undefined}
      onClick={() => onImageTap(post)}
    >
      {post.mediaType === 'video' && post.extraImageUrls && post.extraImageUrls.length > 0 ? (
        <PostImageCarousel urls={post.extraImageUrls} fit={homeFormat ? 'cover' : 'contain'} firstSlide={<PostVideoPlayer src={post.imageUrl} poster={post.thumbnailUrl} suspend={suspendVideo} openOnTap={isPostVideo(post)} />} />
      ) : post.mediaType === 'video' ? (
        <PostVideoPlayer src={post.imageUrl} poster={post.thumbnailUrl} suspend={suspendVideo} openOnTap={isPostVideo(post)} />
      ) : multi ? (
        <PostImageCarousel urls={post.imageUrls} fit={homeFormat ? 'cover' : 'contain'} />
      ) : (
        <img decoding="async"
          className="ad-media-img"
          src={post.imageUrl}
          alt=""
          loading="lazy"
          onLoad={(e) => {
            const im = e.currentTarget
            if (im.naturalWidth && im.naturalHeight) setImgRatio(im.naturalWidth / im.naturalHeight)
          }}
          onError={(e) => {
            // Drive's public-permission grant can lag a moment right after upload,
            // or briefly fail for a viewer's very first request - one retry a
            // second later covers that instead of leaving the image blank forever.
            const img = e.currentTarget
            if (!img.dataset.retried) {
              img.dataset.retried = '1'
              setTimeout(() => { img.src = `${post.imageUrl}${post.imageUrl.includes('?') ? '&' : '?'}retry=${Date.now()}` }, 1200)
            }
          }}
        />
      )}
      {doubleTapId === post.id && (
        <div className="double-tap-heart">
          <Heart size={64} fill="#ef4444" color="#ef4444" />
        </div>
      )}

      {/* Song Player Button */}
      {post.audioUrl && (
        <button
          type="button"
          className="icon-button"
          style={{
            position: 'absolute',
            bottom: 10,
            right: 10,
            background: 'rgba(0,0,0,0.65)',
            border: '1px solid var(--line-strong)',
            width: 32,
            height: 32,
            borderRadius: '50%',
            zIndex: 10
          }}
          onClick={(e) => {
            e.stopPropagation()
            onToggleAudio(post.id, post.audioUrl)
          }}
        >
          {playingPostAudioId === post.id ? (
            <Volume2 size={16} color="var(--cyan)" />
          ) : (
            <VolumeX size={16} color="#fff" />
          )}
        </button>
      )}
    </div>
  )
}

// Boosted post in the Radar feed. Layout:
//   [avatar] Name                         [Sponsored] [⋮]
//   Headline
//   Description
//   [ media ]
//   ♥ 💬 share                                 👁 views
//   ──────────────────────────────────────────────────
//   ↗ Promoted Content                       [Learn More]
// Fires onRecordView(post) exactly once, the first time this card is at least ~55% inside its
// scroll container - not the moment the post is fetched. Used by both feed card variants below.
function usePostViewObserver(post: any, onRecordView: ((p: any) => void) | undefined) {
  const ref = useRef<HTMLElement | null>(null)
  const firedForRef = useRef<string | null>(null)
  useEffect(() => {
    const el = ref.current
    if (!el || !onRecordView || !post?.id) return
    if (firedForRef.current === post.id) return
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting && e.intersectionRatio >= 0.55) {
            firedForRef.current = post.id
            onRecordView(post)
            io.disconnect()
          }
        }
      },
      { threshold: [0.55] },
    )
    io.observe(el)
    return () => io.disconnect()
  }, [post?.id, onRecordView])
  return ref
}

/* =========================================================================
   FRIEND SUGGESTION SLIDE (Reels feed): 4 random people in a 2x2 grid
   (2 on top, 2 below) so a new person can make friends without leaving Reels.
   It behaves like any other slide (slideRef + data-reel-id) but has no video.
   ========================================================================= */
function FriendSuggestSlide({ slotKey, slot, users, loading, friendIds, sentRequests, slideRef, onAdd, onCancel, onViewProfile, onRefresh }: any) {
  const pool: Profile[] = users || []
  // Each slide shows its own 4 (slot 0 -> users 0-3, slot 1 -> 4-7 ...), wrapping around when the pool is small.
  const picked: Profile[] = []
  if (pool.length) {
    const start = (slot * 4) % pool.length
    for (let i = 0; i < Math.min(4, pool.length); i++) picked.push(pool[(start + i) % pool.length])
  }
  return (
    <div ref={slideRef} data-reel-id={slotKey} className="reel-slide friend-suggest-slide">
      <div className="fs-wrap">
        <div className="fs-head">
          <h3>Make new friends</h3>
          <p>People you may know. Tap Connect to send a request.</p>
        </div>
        <div className="fs-grid">
          {picked.map((target) => {
            const isFriend = friendIds?.has(target.uid)
            const isSent = sentRequests?.has(target.uid)
            return (
              <div key={target.uid} className="fs-card">
                <button type="button" className="fs-profile" onClick={() => onViewProfile?.(target.uid)} aria-label={`Open ${target.name}'s profile`}>
                  <Avatar src={target.avatar} name={target.name} size="lg" uid={target.uid} showStatus={false} />
                  <span className="fs-name">{target.name}</span>
                  <span className="fs-handle">@{target.username}</span>
                </button>
                {isFriend ? (
                  <span className="fs-btn is-done"><Check size={14} strokeWidth={3} aria-hidden="true" /> Friends</span>
                ) : isSent ? (
                  <button type="button" className="fs-btn is-pending" onClick={() => onCancel?.(target)}><Check size={14} strokeWidth={3} aria-hidden="true" /> Requested</button>
                ) : (
                  <button type="button" className="fs-btn" onClick={() => { try { navigator.vibrate?.(12) } catch { /* ignore */ } onAdd?.(target) }}><UserPlus size={14} strokeWidth={2.6} aria-hidden="true" /> Connect</button>
                )}
              </div>
            )
          })}
        </div>
        <button type="button" className="fs-refresh" disabled={loading} onClick={() => onRefresh?.()}>
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> <span>Show different people</span>
        </button>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------------------------------------------------------
// PERF: feed rows (post cards, people rows) used to re-render every time ANY state in the big App component changed (a typing
// indicator, a presence beat, a clock tick...). memoLatest() skips the re-render unless one of the row's DATA props changed.
// Handlers (functions) are recreated by the parent on every render, so they are ignored in the comparison and each row gets
// stable wrappers that always call the LATEST handler - nothing can go stale, a tap always runs the newest code.
// ---------------------------------------------------------------------------------------------------------------------------
function memoLatest<P extends Record<string, any>>(Inner: (props: P) => any): (props: P) => any {
  const Wrapped = function MemoLatest(props: P) {
    const latest = useRef(props)
    latest.current = props
    const proxies = useRef<Record<string, (...a: any[]) => any>>({})
    const out: Record<string, any> = {}
    for (const k of Object.keys(props)) {
      const v = (props as any)[k]
      if (typeof v === 'function') {
        if (!proxies.current[k]) proxies.current[k] = (...a: any[]) => (latest.current as any)[k]?.(...a)
        out[k] = proxies.current[k]
      } else out[k] = v
    }
    return Inner(out as P)
  }
  return memo(Wrapped, (a: any, b: any) => {
    const ka = Object.keys(a), kb = Object.keys(b)
    if (ka.length !== kb.length) return false
    for (const k of ka) {
      const x = a[k], y = b[k]
      if (typeof x === 'function' && typeof y === 'function') continue
      if (!Object.is(x, y)) return false
    }
    return true
  }) as any
}

function SponsoredPostCardImpl({ post, userId, following, followBusy, doubleTapId, playingPostAudioId, suspendVideo, onImageTap, onToggleAudio, onLike, onOpenComments, onOpenShareOptions, onOpenGift, onFollow, onDelete, onReport, onEdit, onViewProfile, onRecordView }: any) {
  const liked = (post.likes || []).includes(userId)
  // Headline shows the advertiser's own custom title only - never falls back to the
  // bare domain, since that just repeats what's already in the "Promoted Content ·
  // domain" footer and reads like a stray link sitting above the post.
  const headline = post.title
  const cardRef = usePostViewObserver(post, onRecordView)

  return (
    <article className="ad-card sp-card" ref={cardRef}>
      <SponsoredHeader
        post={post}
        onViewProfile={onViewProfile}
        menu={<SponsoredMenu post={post} userId={userId} following={following} followBusy={followBusy} onFollow={onFollow} onDelete={onDelete} onEdit={onEdit} onReport={onReport} />}
      />

      {headline && <h3 className="sp-title">{headline}</h3>}
      {post.caption && <p className="sp-desc">{post.caption}</p>}

      <AdMediaBlock post={post} doubleTapId={doubleTapId} playingPostAudioId={playingPostAudioId} suspendVideo={suspendVideo} onImageTap={onImageTap} onToggleAudio={onToggleAudio} />

      <div className="ad-footer">
        <div className="ad-footer-left">
          <button type="button" className={`ad-icon-action ${liked ? 'liked' : ''}`} onClick={() => onLike(post)}>
            <Heart size={16} fill={liked ? 'currentColor' : 'none'} />
            <span>{formatCount((post.likes || []).length)}</span>
          </button>
          <button type="button" className="ad-icon-action" onClick={() => onOpenComments(post)}>
            <MessageCircle size={16} />
            <span>{formatCount(post.commentCount || 0)}</span>
          </button>
          <button type="button" className="ad-icon-action" onClick={() => onOpenShareOptions(post)} aria-label="Share">
            <Share2 size={16} />
            <span>{formatCount(post.shareCount || 0)}</span>
          </button>
          {post.authorId !== userId && onOpenGift && (
            <button type="button" className="ad-icon-action" onClick={() => onOpenGift(post)} aria-label="Send a gift" title="Send a gift">
              <Gift size={16} />
            </button>
          )}
        </div>
        <div className="ad-views">
          <Eye size={16} />
          <span>{formatCount(post.views || 0)} views</span>
        </div>
      </div>

      <SponsoredFooter post={post} onViewProfile={onViewProfile} />
    </article>
  )
}

function AdPostCardImpl({ post, userId, following, followBusy, doubleTapId, playingPostAudioId, suspendVideo, onImageTap, onToggleAudio, onLike, onOpenComments, onOpenShareOptions, onOpenGift, onFollow, onDelete, onReport, onOpenBoost, onEdit, onViewProfile, onRecordView, homeFormat = false }: any) {
  const [captionOpen, setCaptionOpen] = useState(false)
  const cardRef = usePostViewObserver(post, onRecordView)
  // Boosted posts always use the Sponsored format.
  if (post.promoted) {
    return (
      <SponsoredPostCard
        post={post} userId={userId} following={following} followBusy={followBusy} doubleTapId={doubleTapId}
        playingPostAudioId={playingPostAudioId} suspendVideo={suspendVideo} onImageTap={onImageTap} onToggleAudio={onToggleAudio}
        onLike={onLike} onOpenComments={onOpenComments} onOpenShareOptions={onOpenShareOptions} onOpenGift={onOpenGift} onFollow={onFollow}
        onDelete={onDelete} onReport={onReport} onEdit={onEdit} onViewProfile={onViewProfile} onRecordView={onRecordView}
      />
    )
  }

  const liked = (post.likes || []).includes(userId)
  const hasCopy = Boolean(post.caption || post.title || (post.tags && post.tags.length > 0))

  return (
    <article className="ad-card" ref={cardRef}>
      <div className="ad-header">
        <AdIdentity post={post} showBadge={post.promoted} onClick={() => onViewProfile?.(post.authorId)} />
        {post.authorId === userId ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            {!post.promoted && (
              <button className="icon-button" style={{ width: 28, height: 28, border: 0 }} title="Boost this post" onClick={() => onOpenBoost(post)}>
                <Rocket size={16} color="var(--cyan)" />
              </button>
            )}
            <button className="icon-button" style={{ width: 28, height: 28, border: 0 }} title="Edit" onClick={() => onEdit?.(post)}>
              <Pencil size={16} color="var(--muted)" />
            </button>
            <button className="icon-button" style={{ width: 28, height: 28, border: 0 }} onClick={() => onDelete(post)}>
              <Trash2 size={16} color="var(--muted)" />
            </button>
          </div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <button className="text-button" disabled={followBusy === post.authorId} onClick={() => onFollow(post.authorId, post.authorName)}>
              {following.has(post.authorId) ? 'Linked' : '+ Link'}
            </button>
            <button className="icon-button" style={{ width: 28, height: 28, border: 0 }} title="Report post" onClick={() => onReport(post)}>
              <Flag size={16} color="var(--muted)" />
            </button>
            {adminModeFlag && (
              <button className="icon-button" style={{ width: 28, height: 28, border: 0 }} title="Remove post (admin)" onClick={() => onDelete(post)}>
                <Trash2 size={16} color="var(--danger)" />
              </button>
            )}
          </div>
        )}
      </div>

      {/* Title / caption / tags sit right under the name and ABOVE the media. */}
      {hasCopy && (
        <div className="ad-copy">
          {post.title && <h3 className="ad-copy-title">{post.title}</h3>}
          {post.caption && (
            <p className={`ad-copy-caption ${captionOpen ? 'open' : ''}`}>
              {post.caption}
              {post.caption.length > 90 && (
                <button type="button" className="ad-caption-more" onClick={() => setCaptionOpen((o) => !o)}>
                  {captionOpen ? ' Show less' : ' Show more'}
                </button>
              )}
            </p>
          )}
          {post.tags && post.tags.length > 0 && (
            <p className="ad-copy-tags">
              {post.tags.map((tag: string) => (
                <button type="button" key={tag} className="ad-tag-chip" onClick={() => openHashtag(tag)}>{tag.startsWith('#') ? tag : `#${tag}`}</button>
              ))}
            </p>
          )}
        </div>
      )}

      <AdMediaBlock post={post} doubleTapId={doubleTapId} playingPostAudioId={playingPostAudioId} suspendVideo={suspendVideo} onImageTap={onImageTap} onToggleAudio={onToggleAudio} homeFormat={homeFormat} />

      <div className="ad-footer">
        <div className="ad-footer-left">
          <button type="button" className={`ad-icon-action ${liked ? 'liked' : ''}`} onClick={() => onLike(post)}>
            <Heart size={16} fill={liked ? 'currentColor' : 'none'} />
            <span>{formatCount((post.likes || []).length)}</span>
          </button>
          <button type="button" className="ad-icon-action" onClick={() => onOpenComments(post)}>
            <MessageCircle size={16} />
            <span>{formatCount(post.commentCount || 0)}</span>
          </button>
          <button type="button" className="ad-icon-action" onClick={() => onOpenShareOptions(post)} aria-label="Share">
            <Share2 size={16} />
            <span>{formatCount(post.shareCount || 0)}</span>
          </button>
          {post.authorId !== userId && onOpenGift && (
            <button type="button" className="ad-icon-action" onClick={() => onOpenGift(post)} aria-label="Send a gift" title="Send a gift">
              <Gift size={16} />
            </button>
          )}
        </div>
        <div className="ad-views">
          <Eye size={16} />
          <span>{formatCount(post.views || 0)} views</span>
        </div>
      </div>
    </article>
  )
}

/* =========================================================================
   REEL FEED - full-screen, Instagram-Reels-style vertical video section
   (the "Shonen" tab). One reel per screen, swipe up/down to move between
   them. Only the reel in view plays; its neighbours just preload.
   Opened from the bottom nav, or by tapping a video inside Radar/Discover.
   ========================================================================= */
const SponsoredPostCard = memoLatest(SponsoredPostCardImpl)
const AdPostCard = memoLatest(AdPostCardImpl)
const normTag = (t: string) => String(t || '').trim().replace(/^#+/, '').toLowerCase()
const openHashtag = (tag: string) => {
  const t = normTag(tag)
  if (t && typeof window !== 'undefined') window.dispatchEvent(new CustomEvent('animora:hashtag', { detail: t }))
}

const HIDDEN_REELS_KEY = 'animora_hidden_reels'
const readHiddenReels = (): Set<string> => {
  try { return new Set<string>(JSON.parse(localStorage.getItem(HIDDEN_REELS_KEY) || '[]')) } catch { return new Set<string>() }
}
const writeHiddenReels = (set: Set<string>) => {
  try { localStorage.setItem(HIDDEN_REELS_KEY, JSON.stringify([...set].slice(-500))) } catch { /* storage full / blocked */ }
}

function ReelFeedTab({ posts, adFree, userId, following, followBusy, focusedReelId, onLike: onLikeProp, onOpenComments, onOpenShareOptions, onOpenGift, onFollow, onDelete, onReport, onRecordView, onReelSignal: onReelSignalProp, getReelInterests, suspend, onViewProfile, onOpenBoost, onEdit, onPickVideo, onOpenComposer, onOpenAlerts, hasAlerts, suggestUsers = [], suggestLoading = false, onRefreshSuggest, friendIds, sentRequests, onAddFriend, onCancelFriend, onGuestGate }: any) {
  // "Not interested": reels the viewer hid stay out of the feed (kept on this device).
  const [hiddenReelIds, setHiddenReelIds] = useState<Set<string>>(() => readHiddenReels())
  const hideReel = (id: string) => {
    setHiddenReelIds((prev) => { const n = new Set(prev); n.add(id); writeHiddenReels(n); return n })
  }
  const videoPosts: Post[] = posts.filter((p: Post) => isReelFeedPost(p) && !hiddenReelIds.has(p.id))
  const idsKey = videoPosts.map((p: Post) => p.id).sort().join('|')

  // Session mood (see reorderReelsByMood): every reaction is also remembered here, in memory only.
  const moodEventsRef = useRef<ReelMoodEvent[]>([])
  const [moodTick, setMoodTick] = useState(0)
  const addMoodEvent = (post: Post, w: number) => {
    if (!post || !w || post.promoted || post.authorId === userId) return
    const keys = reelMoodKeys(post)
    if (!keys.length) return
    moodEventsRef.current = [...moodEventsRef.current, { keys, w }].slice(-REEL_MOOD_EVENTS)
    setMoodTick((n) => n + 1)
  }
  const onReelSignal = (post: Post, w: number) => { addMoodEvent(post, w); onReelSignalProp?.(post, w) }
  // The parent records the like itself (+3 / -3); here it only feeds the session mood.
  const onLike = (post: Post) => {
    addMoodEvent(post, (post.likes || []).includes(userId) ? -3 : 3)
    return onLikeProp?.(post)
  }

  // The user's taste profile is read ONCE when the Reels tab opens (this also runs the
  // 24-hour taste check if it is due). Likes / watching during this visit are collected for
  // the next check. Only the far-ahead reels are re-ordered by the session mood (see reorderReelsByMood).
  // The reels on / next to the screen never move under the viewer's thumb.
  const [interests] = useState<ReelInterestSnapshot | null>(() => getReelInterests?.() ?? null)

  // Rank once per set of videos. Re-ranking on every like/view update used to shuffle
  // the list under the viewer's thumb (a like changes the score => reel jumps away).
  // Fresh random seed every time the Reels tab mounts (page reload / tab re-open) -> new random order.
  // `seen` is read once here, so watching reels now never re-orders the list under the viewer.
  const [shuffle] = useState<ReelShuffle>(() => ({ seen: readSeenReels() }))
  const orderedIds = useMemo(() => buildRankedFeed(videoPosts, adFree, interests, userId, shuffle).map((p) => p.id), [idsKey, adFree, interests, userId, shuffle])
  const byId = new Map<string, Post>(videoPosts.map((p) => [p.id, p]))
  const baseReels = orderedIds.map((id) => byId.get(id)).filter(Boolean) as Post[]
  // Mood re-order of the far-ahead reels; only valid for the exact base order it was made from.
  const baseKey = orderedIds.join('|')
  const [moodOrder, setMoodOrder] = useState<{ base: string; ids: string[] } | null>(null)
  const reels = moodOrder && moodOrder.base === baseKey && moodOrder.ids.length === baseReels.length && moodOrder.ids.every((id) => byId.has(id))
    ? (moodOrder.ids.map((id) => byId.get(id)) as Post[])
    : baseReels
    const showSuggest = (suggestUsers?.length || 0) >= 1
  const reelRows = useMemo(() => weaveFriendRows(buildReelRows(reels), showSuggest), [idsKey, adFree, interests, userId, showSuggest, moodOrder])
  // Every non-reel slide (friend suggestions) counts as a valid "current" slide for the scroll observer.
  const adRowKeys = useMemo(() => new Set(reelRows.filter((r) => r.kind !== 'reel').map((r: any) => r.key as string)), [reelRows])

  const scrollRef = useRef<HTMLDivElement | null>(null)
  const slideRefs = useRef<Record<string, HTMLDivElement | null>>({})
  const jumpedForRef = useRef<string | null>(null)
  const [activeId, setActiveId] = useState<string | null>(null)
  const [muted, setMuted] = useState(false)

  const currentId = activeId && (byId.has(activeId) || adRowKeys.has(activeId)) ? activeId : (reels[0]?.id ?? null)
  const lastRealIndexRef = useRef(0)
  const foundIndex = reels.findIndex((r) => r.id === currentId)
  if (foundIndex >= 0) lastRealIndexRef.current = foundIndex
  // On an ad / friends slide keep the neighbouring reels loaded (instead of dropping every video past #2).
  const currentIndex = foundIndex >= 0 ? foundIndex : (currentId ? lastRealIndexRef.current : -1)

  // After every swipe / reaction: re-order only the reels that are still far ahead of the viewer.
  useEffect(() => {
    if (currentIndex < 0) return
    const ids = reorderReelsByMood(reels, currentIndex + REEL_MOOD_FROZEN_AHEAD, moodEventsRef.current)
    if (ids) setMoodOrder({ base: baseKey, ids })
  }, [currentId, moodTick])

  // Jump straight to the reel that was tapped in Radar/Discover.
  useEffect(() => {
    if (!focusedReelId || jumpedForRef.current === focusedReelId) return
    const el = slideRefs.current[focusedReelId]
    if (!el) return
    jumpedForRef.current = focusedReelId
    scrollRef.current?.scrollTo({ top: el.offsetTop })
    setActiveId(focusedReelId)
  }, [focusedReelId, idsKey])

  // Whichever slide is >= 60% on screen becomes the active (playing) reel.
  useEffect(() => {
    const root = scrollRef.current
    if (!root) return
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting && e.intersectionRatio >= 0.6) setActiveId((e.target as HTMLElement).dataset.reelId || null)
        }
      },
      { root, threshold: [0.6] },
    )
    Object.values(slideRefs.current).forEach((el) => { if (el) io.observe(el) })
    return () => io.disconnect()
  }, [idsKey, showSuggest])

  // Laptop / desktop: mouse wheel, trackpad, arrow keys and the up/down buttons move exactly one reel.
  // (Touch phones keep using native swipe + scroll-snap.)
  const wheelLockRef = useRef({ until: 0, last: 0 })
  const rowCountRef = useRef(reelRows.length)
  rowCountRef.current = reelRows.length
  const goReel = useCallback((dir: 1 | -1) => {
    const root = scrollRef.current
    if (!root || root.clientHeight <= 0) return
    const h = root.clientHeight
    const idx = Math.round(root.scrollTop / h)
    const next = Math.max(0, Math.min(rowCountRef.current - 1, idx + dir))
    root.scrollTo({ top: next * h, behavior: 'smooth' })
  }, [])
  useEffect(() => {
    const root = scrollRef.current
    if (!root) return
    const onWheel = (e: WheelEvent) => {
      if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) return // sideways = photo strip, leave it alone
      if (Math.abs(e.deltaY) < 4) return
      e.preventDefault()
      const lock = wheelLockRef.current
      const now = Date.now()
      if (now < lock.until) { // still animating, or a trackpad inertia tail: swallow it
        if (now - lock.last < 100) lock.until = now + 150
        lock.last = now
        return
      }
      lock.last = now
      lock.until = now + 550
      goReel(e.deltaY > 0 ? 1 : -1)
    }
    const onKey = (e: KeyboardEvent) => {
      if (suspendRef.current || e.metaKey || e.ctrlKey || e.altKey) return
      const t = e.target as HTMLElement | null
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return
      if (e.key === 'ArrowDown' || e.key === 'PageDown' || e.key === 'j') { e.preventDefault(); goReel(1) }
      else if (e.key === 'ArrowUp' || e.key === 'PageUp' || e.key === 'k') { e.preventDefault(); goReel(-1) }
    }
    root.addEventListener('wheel', onWheel, { passive: false })
    window.addEventListener('keydown', onKey)
    return () => { root.removeEventListener('wheel', onWheel); window.removeEventListener('keydown', onKey) }
  }, [goReel, idsKey])

  // Count a view only for the reel actually being watched (not every reel in the list).
  useEffect(() => {
    const p = currentId ? byId.get(currentId) : null
    if (p) { onRecordView(p); markReelSeen(p.id) }
  }, [currentId])

  // Watch-time signal for the taste profile: how long each reel stays on screen while the
  // app is visible. Long watch = interest, instant scroll-past = mild dislike.
  const byIdRef = useRef(byId)
  byIdRef.current = byId
  const signalRef = useRef(onReelSignal)
  signalRef.current = onReelSignal
  const currentIdRef = useRef<string | null>(null)
  currentIdRef.current = currentId
  const watchRef = useRef<{ id: string; startedAt: number } | null>(null)
  const commitWatch = () => {
    const w = watchRef.current
    watchRef.current = null
    if (!w) return
    const post = byIdRef.current.get(w.id)
    const secs = (Date.now() - w.startedAt) / 1000
    if (!post || secs < 0.4) return
    signalRef.current?.(post, secs < 1.5 ? -0.4 : Math.min(secs, 20) / 10)
  }
  const suspendRef = useRef(Boolean(suspend))
  suspendRef.current = Boolean(suspend)
  useEffect(() => {
    if (!currentId || document.hidden || suspendRef.current) return
    watchRef.current = { id: currentId, startedAt: Date.now() }
    return () => commitWatch()
  }, [currentId])
  // Upload sheet open = the reel is paused, so stop the watch clock; restart it on close.
  useEffect(() => {
    if (suspend) commitWatch()
    else if (currentIdRef.current && !document.hidden && !watchRef.current) watchRef.current = { id: currentIdRef.current, startedAt: Date.now() }
  }, [suspend])
  useEffect(() => {
    const onVis = () => {
      if (document.hidden) commitWatch()
      else if (currentIdRef.current && !suspendRef.current) watchRef.current = { id: currentIdRef.current, startedAt: Date.now() }
    }
    document.addEventListener('visibilitychange', onVis)
    return () => document.removeEventListener('visibilitychange', onVis)
  }, [])

  // "+" goes straight to the gallery (videos only). After a pick, the composer sheet opens.
  const pickRef = useRef<HTMLInputElement | null>(null)
  // Set when the picker was opened by "Remix this sound": the picked photo / video then gets that sound.
  const remixRef = useRef<SoundInfo | null>(null)
  // plain "+" = a normal upload: the New post page opens straight away (Photo / Video buttons live there); guests go to sign-up
  const openPicker = () => { if (onGuestGate) { onGuestGate(); return } remixRef.current = null; if (onOpenComposer) onOpenComposer(); else pickRef.current?.click() }
  // The centre "+" in the bottom bar asks this screen to open the gallery picker.
  const openPickerRef = useRef(openPicker)
  openPickerRef.current = openPicker
  useEffect(() => {
    const onOpen = () => openPickerRef.current()
    window.addEventListener('animora:open-upload', onOpen)
    return () => window.removeEventListener('animora:open-upload', onOpen)
  }, [])
  const startRemix = (post: Post) => {
    if (onGuestGate) { onGuestGate(); return }
    const heldMsg = uploadHoldMessage()
    if (heldMsg) { alert(heldMsg); return }
    const info = soundInfoOf(post)
    if (!info.key) return
    remixRef.current = info
    pickRef.current?.click()
  }
  // A sound-list row was tapped: jump to that reel if it is loaded here, otherwise open it on its own page.
  const openSoundReel = (p: Post) => {
    const el = slideRefs.current[p.id]
    if (el) { scrollRef.current?.scrollTo({ top: el.offsetTop }); setActiveId(p.id) }
    else window.location.href = `/reel/${p.id}`
  }
  const handlePick = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0]
    e.target.value = ''
    const remix = remixRef.current
    remixRef.current = null
    if (!f) return
    const heldMsg = uploadHoldMessage()
    if (heldMsg) { alert(heldMsg); return }
    const isImg = f.type.startsWith('image/')
    if (isImg) { onPickVideo?.(f, remix); return } // photo post: the composer sheet opens as a normal Post
    if (!(f.type.startsWith('video/') || /\.(mp4|mov|webm|m4v)$/i.test(f.name))) {
      alert('Please choose a photo or a video for Shonen.')
      return
    }
    const duration = await readVideoDuration(f)
    if (duration > 65) {
      alert(`Video length is ${Math.round(duration)}s. Reels support up to 60 seconds. Please select a video up to 60 seconds.`)
      return
    }
    onPickVideo?.(f, remix)
  }

  const header = (
    <div className="reel-header">
      <input ref={pickRef} type="file" accept="video/*,image/*" className="pc-file-input" onChange={handlePick} />
      <span className="reel-header-spacer" aria-hidden="true" />
      <div className="reel-brand">
        <Clapperboard size={18} />
        <span>{REELS_BRAND}</span>
      </div>
      {/* Mute / unmute sits in the top-right corner of the header row (it used to float below it, leaving that corner empty). */}
      {reels.length > 0 ? (
        <button
          type="button"
          className="reel-round-btn"
          onClick={() => setMuted((m) => !m)}
          aria-label={muted ? 'Unmute' : 'Mute'}
        >
          {muted ? <VolumeX size={18} /> : <Volume2 size={18} color="var(--cyan)" />}
        </button>
      ) : (
        <span className="reel-header-spacer" aria-hidden="true" />
      )}
    </div>
  )

  if (reels.length === 0) {
    return (
      <div className="reel-root">
        {header}
        <div className="reel-empty">
          <EmptyState
            icon={<Clapperboard size={20} />}
            title="Nothing to watch yet"
            hint="Posts and reels from you and your allies will show up here."
            action={<button type="button" className="text-button primary" onClick={openPicker}><Plus size={16} /> Upload</button>}
          />
        </div>
      </div>
    )
  }

  return (
    <div className="reel-stage">
      <div className="reel-nav-btns">
        <button type="button" className="reel-nav-btn" onClick={() => goReel(-1)} aria-label="Previous reel"><ChevronUp size={20} /></button>
        <button type="button" className="reel-nav-btn" onClick={() => goReel(1)} aria-label="Next reel"><ChevronDown size={20} /></button>
      </div>
    <div className="reel-root">
      {header}

      <div ref={scrollRef} className="reel-feed">
        {reelRows.map((row) =>
          row.kind === 'friends' ? (
            <FriendSuggestSlide
              key={row.key}
              slotKey={row.key}
              slot={row.slot}
              users={suggestUsers}
              loading={suggestLoading}
              friendIds={friendIds}
              sentRequests={sentRequests}
              slideRef={(el: HTMLDivElement | null) => { slideRefs.current[row.key] = el }}
              onAdd={onAddFriend}
              onCancel={onCancelFriend}
              onViewProfile={onViewProfile}
              onRefresh={onRefreshSuggest}
            />
          ) : (
            <ReelSlide
              key={row.post.id}
              // reelRows is memoised on the ID list only (so the order never reshuffles under the
              // thumb), which froze `row.post` at its first snapshot: the heart, like count, comment
              // count and share count never updated after a tap. Always hand the slide the LIVE post.
              post={byId.get(row.post.id) ?? row.post}
              isActive={row.post.id === currentId}
              isNear={currentIndex < 0 ? row.realIndex < 3 : (row.realIndex - currentIndex >= -1 && row.realIndex - currentIndex <= 2)}
              isNext={currentIndex < 0 ? (row.realIndex === 1 || row.realIndex === 2) : (row.realIndex === currentIndex + 1 || row.realIndex === currentIndex + 2)}
              muted={muted || Boolean((byId.get(row.post.id) ?? row.post).muteOriginal)}
              suspend={Boolean(suspend)}
              setMuted={setMuted}
              userId={userId}
              following={following}
              followBusy={followBusy}
              slideRef={(el: HTMLDivElement | null) => { slideRefs.current[row.post.id] = el }}
              onLike={onLike}
              onOpenComments={(p: Post) => { onReelSignal?.(p, 2); onOpenComments(p) }}
              onOpenShareOptions={(p: Post) => { onReelSignal?.(p, 2); onOpenShareOptions(p) }}
              onOpenGift={onOpenGift}
              onFollow={onFollow}
              onDelete={onDelete}
              onReport={onReport}
              onRemixSound={startRemix}
              onOpenSoundReel={openSoundReel}
              onViewProfile={onViewProfile}
              onOpenBoost={onOpenBoost}
              onEdit={onEdit}
              onNotInterested={(p: Post) => { onReelSignal?.(p, -2); hideReel(p.id) }}
            />
          )
        )}
      </div>
    </div>
    </div>
  )
}

// "Original audio • username" line under every reel, with the creator's DP as a small rounded-square cover
// (like Instagram). If the creator attached a song, its title is shown instead of "Original audio".
function ReelAudioLine({ post, onOpen }: { post: any; onOpen?: () => void }) {
  const { label } = usePostAuthor(post)
  const title = post.audioUrl ? (post.audioTitle || 'Attached Audio Track') : 'Original audio'
  const who = post.soundOwnerName || label
  return (
    <button type="button" className="reel-audio-line reel-audio-btn" onClick={(e) => { e.stopPropagation(); onOpen?.() }} aria-label="See every reel that uses this sound">
      <Music size={14} className="reel-audio-note" />
      <span className="reel-audio-text">{title} • {who}</span>
      <ChevronRight size={14} className="reel-audio-note" />
    </button>
  )
}

// Bottom sheet opened from the music line: every reel / post that uses this sound + a "Remix" button.
function SoundSheet({ post, myUid, onClose, onRemix, onOpenReel }: { post: any; myUid: string; onClose: () => void; onRemix: () => void; onOpenReel: (p: Post) => void }) {
  const info = soundInfoOf(post)
  const [items, setItems] = useState<Post[] | null>(null)
  const [failed, setFailed] = useState(false)
  useEffect(() => {
    let dead = false
    ;(async () => {
      try {
        const [users, source] = await Promise.all([
          getDocs(query(collection(firestore, 'posts'), where('audioUrl', '==', info.key), limit(60))),
          getDocs(query(collection(firestore, 'posts'), where('imageUrl', '==', info.key), limit(5))),
        ])
        const map = new Map<string, Post & { _createdAtMs: number }>()
        for (const snap of [source, users]) snap.docs.forEach((d) => map.set(d.id, mapPostDoc(d.id, d.data())))
        const list = [...map.values()]
          .filter((x) => x.privacy === 'public' || x.authorId === myUid)
          .sort((x, y) => {
            const xo = x.imageUrl === info.key ? 1 : 0
            const yo = y.imageUrl === info.key ? 1 : 0
            return yo - xo || y._createdAtMs - x._createdAtMs // original first, then newest
          })
        if (!dead) setItems(list)
      } catch { if (!dead) setFailed(true) }
    })()
    return () => { dead = true }
  }, [info.key, myUid])

  const count = items?.length ?? 0
  return (
    <div className="reel-more-backdrop" onClick={(e) => { e.stopPropagation(); onClose() }}>
      <div className="reel-more-sheet" role="dialog" aria-label="Sound" onClick={(e) => e.stopPropagation()} style={{ maxHeight: '78vh', display: 'flex', flexDirection: 'column' }}>
        <span className="reel-more-grab" aria-hidden="true" />
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '4px 4px 12px' }}>
          <div style={{ width: 48, height: 48, borderRadius: 12, display: 'grid', placeItems: 'center', background: 'linear-gradient(135deg,#ff2d75,#7c3aed)', color: '#fff', flexShrink: 0 }}><Music size={22} /></div>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ fontWeight: 750, fontSize: '1rem', color: 'var(--ink)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{info.title}</div>
            <div style={{ fontSize: '.76rem', color: 'var(--muted)' }}>
              {info.owner ? `by ${info.owner}` : ''}{items ? `${info.owner ? ' · ' : ''}${count} ${count === 1 ? 'reel uses' : 'reels use'} this sound` : ''}
            </div>
          </div>
        </div>
        <button type="button" className="primary-button" onClick={onRemix} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginBottom: 10 }}>
          <Sparkles size={16} /> Remix this sound
        </button>
        <div style={{ overflowY: 'auto', minHeight: 80, flex: 1 }}>
          {!items && !failed && <SkeletonRows count={3} />}
          {failed && <div style={{ padding: '14px 4px', fontSize: '.82rem', color: 'var(--muted)' }}>Could not load the list. Check your connection and try again.</div>}
          {items && items.length === 0 && <div style={{ padding: '14px 4px', fontSize: '.82rem', color: 'var(--muted)' }}>No other reels use this sound yet - be the first to remix it!</div>}
          {items?.map((x) => {
            const thumb = x.thumbnailUrl || (x.mediaType !== 'video' ? x.imageUrl : '')
            const isOriginal = x.imageUrl === info.key || (!x.remixOf && !x.audioShared)
            return (
              <button key={x.id} type="button" onClick={() => onOpenReel(x)} style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%', textAlign: 'left', padding: '8px 4px', background: 'transparent', border: 0, cursor: 'pointer', color: 'var(--ink)' }}>
                <div style={{ width: 52, height: 68, borderRadius: 10, overflow: 'hidden', background: '#111', flexShrink: 0, display: 'grid', placeItems: 'center' }}>
                  {thumb ? <img src={thumb} alt="" loading="lazy" decoding="async" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : (x.mediaType === 'video' && x.imageUrl ? <VideoPoster src={x.imageUrl} /> : <Play size={20} color="#fff" />)}
                </div>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ fontWeight: 650, fontSize: '.88rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{x.title || x.caption || 'Untitled reel'}</div>
                  <div style={{ fontSize: '.74rem', color: 'var(--muted)', marginTop: 2 }}>@{x.authorName} · {x.time}</div>
                  <div style={{ fontSize: '.72rem', marginTop: 3, display: 'flex', gap: 10, alignItems: 'center' }}>
                    <span style={{ color: '#ff2d75', fontWeight: 650, display: 'inline-flex', alignItems: 'center', gap: 3 }}><PosterIcon name="heart" size={14} /> {(x.likes || []).length}</span>
                    <span style={{ padding: '1px 7px', borderRadius: 999, fontWeight: 650, background: isOriginal ? 'rgba(255,161,26,.18)' : 'rgba(124,58,237,.2)', color: isOriginal ? '#ffa11a' : '#a78bfa' }}>{isOriginal ? 'Original' : 'Remix'}</span>
                  </div>
                </div>
                <ChevronRight size={16} color="var(--muted)" />
              </button>
            )
          })}
        </div>
        <button type="button" className="reel-more-item cancel" onClick={onClose}>Close</button>
      </div>
    </div>
  )
}

const ReelSlide = memoLatest(ReelSlideImpl)
function ReelSlideImpl({ post, isActive, isNear, isNext, muted, suspend, setMuted, userId, following, followBusy, slideRef, onLike, onOpenComments, onOpenShareOptions, onOpenGift, onFollow, onDelete, onReport, onViewProfile, onOpenBoost, onEdit, onRemixSound, onOpenSoundReel, onNotInterested }: any) {
  const BOOST_COMING_SOON = !useBoostEnabled()
  const liked = (post.likes || []).includes(userId)

  const videoRef = useRef<HTMLVideoElement | null>(null)
  const progressRef = useRef<HTMLDivElement | null>(null)
  const lastTapRef = useRef(0)
  const tapTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const userPausedRef = useRef(false)
  const suspendRef = useRef(Boolean(suspend))
  suspendRef.current = Boolean(suspend)

  const [paused, setPaused] = useState(false)
  // Photo-slide posts (no video) and video + extra photos: swipe sideways inside the reel.
  const photoOnly = post.mediaType !== 'video'
  const stripPhotos: string[] = photoOnly ? (Array.isArray(post.imageUrls) && post.imageUrls.length > 1 ? post.imageUrls : [post.imageUrl]) : (post.extraImageUrls || [])
  const hasStrip = stripPhotos.length > 0
  const totalMedia = stripPhotos.length + (photoOnly ? 0 : 1)
  const trackRef = useRef<HTMLDivElement | null>(null)
  const [mediaIdx, setMediaIdx] = useState(0)
  const mediaIdxRef = useRef(0)
  mediaIdxRef.current = mediaIdx
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>(photoOnly ? 'ready' : 'loading')
  const [buffering, setBuffering] = useState(false)
  const [errorDetail, setErrorDetail] = useState('')
  const [attempt, setAttempt] = useState(0)
  // Same rule as Instagram Reels: the WHOLE video is always visible (object-fit: contain, centred,
  // black bars where the shape differs). Only a video whose shape is within ~5% of the screen's
  // fills it edge-to-edge (cover) - the crop is then too small to cut any text or picture.
  // Cropping every clip to fill the screen made landscape videos blurry (zoomed in) and cut off
  // their text.
  const [fit, setFit] = useState<'cover' | 'contain'>('contain')
  const wrapRef = useRef<HTMLDivElement | null>(null)
  // The cover image sits on top of the video (same fit) until playback really starts.
  const [hasPlayed, setHasPlayed] = useState(false)
  useEffect(() => { if (!isActive) setHasPlayed(false) }, [isActive])
  const [hearts, setHearts] = useState<{ id: number; x: number; y: number; rot: number }[]>([])
  const heartSeq = useRef(0)
  const chainUntilRef = useRef(0) // while a fast like-chain is running every extra tap adds a heart at once (Instagram style)
  const [captionOpen, setCaptionOpen] = useState(false)
  const [moreOpen, setMoreOpen] = useState(false)
  const [soundOpen, setSoundOpen] = useState(false)
  // Playback speed (menu: 0.5x / 1x / 1.5x / 2x) and "saved" state of this reel (users/{me}/savedPosts/{id}).
  const [speed, setSpeed] = useState(1)
  const [saved, setSaved] = useState(false)
  useEffect(() => {
    const v = videoRef.current
    if (v) { try { v.playbackRate = speed } catch { /* ignore */ } }
  }, [speed, isActive, attempt, status, hasPlayed])
  useEffect(() => {
    if (!moreOpen || !userId) return
    let alive = true
    getDoc(doc(firestore, 'users', userId, 'savedPosts', post.id)).then((d: any) => { if (alive) setSaved(Boolean(d.exists())) }).catch(() => {})
    return () => { alive = false }
  }, [moreOpen, userId, post.id])
  const toggleSaved = async () => {
    if (!userId) return
    const ref = doc(firestore, 'users', userId, 'savedPosts', post.id)
    const next = !saved
    setSaved(next)
    try {
      if (next) await setDoc(ref, { postId: post.id, authorId: post.authorId || '', authorName: post.authorName || '', caption: String(post.text || post.caption || '').slice(0, 140), thumb: post.thumbnailUrl || (photoOnly ? post.imageUrl : '') || '', kind: photoOnly ? 'photo' : 'reel', savedAt: serverTimestamp() })
      else await deleteDoc(ref)
    } catch { setSaved(!next) }
  }
  useEffect(() => { if (!isActive) { setMoreOpen(false); setSoundOpen(false) } }, [isActive])
  // Ad banner reveal: stays hidden while the reel loads, then slides up into view
  // a beat after playback actually starts (and hides again once you scroll away,
  // so it replays the same way next time this reel becomes active).
  const [adVisible, setAdVisible] = useState(false)
  useEffect(() => {
    if (!post.promoted || !isActive) { setAdVisible(false); return }
    const t = setTimeout(() => setAdVisible(true), 1200)
    return () => clearTimeout(t)
  }, [isActive, post.promoted])

  const baseSrc = resolveMediaUrl(post.imageUrl) as string
  // "#t=0.001" makes iOS/Safari paint the first frame instead of staying black.
  const src = `${baseSrc}${attempt > 0 ? `${baseSrc.includes('?') ? '&' : '?'}r=${attempt}` : ''}#t=0.001`

  // Play only the reel in view; everything else pauses and rewinds.
  useEffect(() => {
    const v = videoRef.current
    if (!v) return
    v.muted = muted || Boolean(post.muteOriginal) // remix: the video's own sound is replaced by the remixed one
    if (!isActive) {
      v.pause()
      userPausedRef.current = false
      setPaused(false)
      try { v.currentTime = 0 } catch { /* not loaded yet */ }
      return
    }
    // Upload sheet open: silence the reel right away. It resumes from the same spot
    // (with the user's own mute setting) as soon as the sheet closes.
    if (suspend) { v.pause(); return }
    if (userPausedRef.current) return
    if (mediaIdx !== 0) { v.pause(); return } // swiped to a photo: video waits on slide 1
    const p = v.play()
    if (p) {
      p.catch((err: any) => {
        // Browser refused sound-on autoplay - fall back to muted so the reel still moves
        // (the mute button at the top right shows the state; tap it for sound).
        if (err?.name === 'NotAllowedError' && !v.muted) {
          v.muted = true
          setMuted(true)
          v.play().catch(() => { userPausedRef.current = true; setPaused(true) })
        }
      })
    }
  }, [isActive, muted, isNear, attempt, status, suspend, mediaIdx])

  // Music of the reel: a song attached to a photo reel, or the remixed sound of a remix video (its own audio is muted).
  // Created only for the reel in view, so scrolling never keeps several audio players alive.
  const bgAudioUrl: string = post.audioUrl && (photoOnly || post.muteOriginal) ? post.audioUrl : ''
  const bgAudioRef = useRef<HTMLAudioElement | null>(null)
  useEffect(() => {
    if (!bgAudioUrl || !isActive) return
    const a = new Audio(bgAudioUrl)
    a.loop = true
    bgAudioRef.current = a
    const v = videoRef.current
    // keep the sound in step with the video (it loops / seeks)
    const sync = () => {
      if (!v || !a.duration || !isFinite(a.duration)) return
      if (Math.abs(a.currentTime - v.currentTime) > 0.6) { try { a.currentTime = v.currentTime % a.duration } catch { /* not ready */ } }
    }
    const onHide = () => { if (document.hidden) a.pause() }
    v?.addEventListener('timeupdate', sync)
    document.addEventListener('visibilitychange', onHide)
    return () => {
      v?.removeEventListener('timeupdate', sync)
      document.removeEventListener('visibilitychange', onHide)
      a.pause()
      bgAudioRef.current = null
    }
  }, [bgAudioUrl, isActive])
  useEffect(() => {
    const a = bgAudioRef.current
    if (!a) return
    if (isActive && !suspend && !muted && !paused && status !== 'error') a.play().catch(() => { /* needs a tap first - the next tap / unmute starts it */ })
    else a.pause()
  }, [bgAudioUrl, isActive, suspend, muted, paused, status, mediaIdx])

  // Leaving this reel: the photo strip goes back to its first slide for next time.
  useEffect(() => {
    if (isActive) return
    setMediaIdx(0)
    trackRef.current?.scrollTo({ left: 0 })
  }, [isActive])

  // Pause when the app/tab goes to the background, resume when it comes back.
  useEffect(() => {
    if (!isActive) return
    const onVisibility = () => {
      const v = videoRef.current
      if (!v) return
      if (document.hidden) v.pause()
      else if (!userPausedRef.current && !suspendRef.current && mediaIdxRef.current === 0) v.play().catch(() => {})
    }
    document.addEventListener('visibilitychange', onVisibility)
    return () => document.removeEventListener('visibilitychange', onVisibility)
  }, [isActive])

  useEffect(() => () => { if (tapTimerRef.current) clearTimeout(tapTimerRef.current) }, [])

  const togglePlay = () => {
    const v = videoRef.current
    if (!v) return
    if (v.paused) {
      userPausedRef.current = false
      setPaused(false)
      v.play().catch(() => {})
    } else {
      userPausedRef.current = true
      setPaused(true)
      v.pause()
    }
  }

  // Single tap = play/pause, double tap = like. After a double tap, every further quick tap adds another heart
  // instantly at the finger (no waiting), like Instagram. Hearts appear right where you tapped.
  const spawnHeart = (e?: React.MouseEvent<HTMLElement>) => {
    let x = 50; let y = 50
    if (e) {
      const r = (e.currentTarget as HTMLElement).getBoundingClientRect()
      if (r.width > 0 && r.height > 0) { x = ((e.clientX - r.left) / r.width) * 100; y = ((e.clientY - r.top) / r.height) * 100 }
    }
    const id = ++heartSeq.current
    setHearts((h) => [...h.slice(-7), { id, x, y, rot: Math.round(Math.random() * 30 - 15) }])
    window.setTimeout(() => setHearts((h) => h.filter((q) => q.id !== id)), 650)
  }
  const handleTap = (e?: React.MouseEvent<HTMLElement>) => {
    const now = Date.now()
    if (now < chainUntilRef.current) { // like-chain running: more hearts, no play/pause
      chainUntilRef.current = now + 600
      lastTapRef.current = 0
      spawnHeart(e)
      return
    }
    if (now - lastTapRef.current < 260) {
      if (tapTimerRef.current) clearTimeout(tapTimerRef.current)
      lastTapRef.current = 0
      chainUntilRef.current = now + 600
      togglePlay() // undo the instant play/pause from the first tap - a double tap is a like, not a pause
      if (!liked) onLike(post)
      spawnHeart(e)
      return
    }
    lastTapRef.current = now
    togglePlay() // respond on the first tap, no 260 ms wait
  }

  const handleError = () => {
    const v = videoRef.current
    // One silent retry (Drive/Vercel can hiccup on a cold start) before showing an error.
    if (attempt < 1) {
      setTimeout(() => setAttempt((a) => a + 1), 1200)
      return
    }
    setStatus('error')
    setBuffering(false)
    const code = v?.error?.code
    // Ask the server what it actually said, so the error explains itself instead of a black screen.
    fetch(baseSrc, { headers: { Range: 'bytes=0-1' } })
      .then(async (res) => {
        if (res.ok) {
          setErrorDetail(code === 3 || code === 4 ? "This browser can't play this video format." : 'Playback failed.')
          return
        }
        const j = await res.json().catch(() => null)
        setErrorDetail(`Server ${res.status}${j?.error ? ` · ${j.error}` : ''}`)
      })
      .catch(() => setErrorDetail('Network error - check your connection.'))
  }

  const retry = () => {
    setStatus('loading')
    setErrorDetail('')
    setAttempt((a) => a + 1)
  }

  const videoNode = isNear ? (
          <video
            ref={videoRef}
            className={`reel-video fit-${fit}`}
            src={src}
            playsInline
            loop
            muted={muted || Boolean(post.muteOriginal)}
            preload={isActive || isNext ? 'auto' : 'metadata'}
            onLoadedMetadata={(e) => {
              const v = e.currentTarget
              const box = wrapRef.current
              if (v.videoWidth && v.videoHeight && box && box.clientWidth && box.clientHeight) {
                const videoRatio = v.videoWidth / v.videoHeight
                const screenRatio = box.clientWidth / box.clientHeight
                setFit(pickReelFit(videoRatio, screenRatio))
              } else setFit('contain')
              setStatus('ready')
            }}
            onPlaying={() => { setStatus('ready'); setBuffering(false); setPaused(false); setHasPlayed(true) }}
            onPlay={() => setPaused(false)}
            onWaiting={() => setBuffering(true)}
            onCanPlay={() => setBuffering(false)}
            onError={handleError}
            onTimeUpdate={(e) => {
              const v = e.currentTarget
              if (progressRef.current && v.duration) progressRef.current.style.width = `${(v.currentTime / v.duration) * 100}%`
            }}
          />
  ) : null

  return (
    <div ref={slideRef} data-reel-id={post.id} className={`reel-slide ${post.promoted ? 'is-ad' : ''}`}>
      {/* Like Instagram Reels: the full video is shown centred (contain); it only fills the screen
          edge-to-edge when its shape already matches the screen (see `fit` above). */}
      <div ref={wrapRef} className="reel-video-wrap">
        {hasStrip ? (
          // Same swipe as the feed card: video (if any) first, then the photos. Sits UNDER the scrim (which
          // ignores touches), so sideways swipes reach it while up/down swipes still change the reel.
          <div
            ref={trackRef}
            className="reel-media-track"
            onClick={handleTap}
            onScroll={(e) => {
              const el = e.currentTarget
              const i = Math.round(el.scrollLeft / Math.max(1, el.clientWidth))
              if (i !== mediaIdxRef.current) setMediaIdx(i)
            }}
          >
            {!photoOnly && <div className="reel-media-slide">{videoNode}</div>}
            {stripPhotos.map((u: string, i: number) => (
              <div className="reel-media-slide" key={`${u}-${i}`}>
                {isNear && (
                  <img decoding="async"
                    className="reel-video fit-contain"
                    src={u}
                    alt=""
                    draggable={false}
                    loading={i === 0 ? 'eager' : 'lazy'}
                    onLoad={(e) => {
                      // Same rule as videos: whole picture visible; fill edge-to-edge only if the shape already matches.
                      const im = e.currentTarget
                      const box = wrapRef.current
                      if (im.naturalWidth && im.naturalHeight && box && box.clientWidth && box.clientHeight) {
                        im.style.objectFit = pickReelFit(im.naturalWidth / im.naturalHeight, box.clientWidth / box.clientHeight)
                      }
                    }}
                    onError={(e) => {
                      const im = e.currentTarget
                      if (!im.dataset.retried) {
                        im.dataset.retried = '1'
                        setTimeout(() => { im.src = `${u}${u.includes('?') ? '&' : '?'}retry=${Date.now()}` }, 1200)
                      }
                    }}
                  />
                )}
              </div>
            ))}
          </div>
        ) : videoNode}
        {post.thumbnailUrl && !hasPlayed && (!hasStrip || mediaIdx === 0) && (
          // Was hardcoded to fit-contain regardless of `fit`, so the cover image sat
          // letterboxed (black bars top/bottom) the whole time the video was loading/
          // buffering, even though the video underneath fills edge-to-edge - this is
          // exactly the black band the ad's cover image sat in before playback started.
          // Matching the video's own fit keeps the transition from thumbnail -> video seamless.
          <img className={`reel-video fit-${fit} reel-thumb`} src={post.thumbnailUrl} alt="" draggable={false} loading="lazy" decoding="async" />
        )}
      </div>

      <div className="reel-scrim" />
      {!hasStrip && <div className="reel-tap-layer" onClick={handleTap} />}

      {hasStrip && totalMedia > 1 && (
        <>
          <span className="reel-media-count">{mediaIdx + 1}/{totalMedia}</span>
          <div className="reel-media-dots" aria-hidden="true">
            {Array.from({ length: totalMedia }).map((_, i) => <span key={i} className={i === mediaIdx ? 'on' : ''} />)}
          </div>
        </>
      )}

      {hearts.length > 0 && (
        <div className="dt-hearts" aria-hidden="true">
          {hearts.map((h) => (
            <span key={h.id} className="dt-heart" style={{ left: `${h.x}%`, top: `${h.y}%`, ['--dt-rot' as any]: `${h.rot}deg` }}>
              <Heart size={78} fill="#ff2d75" color="#ff2d75" />
            </span>
          ))}
        </div>
      )}

      {isActive && paused && status !== 'error' && (
        <div className="reel-center-badge"><Play size={30} fill="#fff" color="#fff" /></div>
      )}
      {isActive && status !== 'error' && (status === 'loading' || buffering) && <div className="reel-spinner" />}

      {status === 'error' && (
        <div className="reel-error">
          <EmptyState
            tone="error"
            icon={<Clapperboard size={20} />}
            title="Couldn't load this reel"
            hint={errorDetail || 'Check your connection and try again.'}
            action={<button type="button" className="text-button primary" onClick={retry}><RefreshCw size={14} /> Retry</button>}
          />
        </div>
      )}

      <div className="reel-overlay-bottom">
        {post.promoted && <AdCtaBar post={post} onViewProfile={onViewProfile} className={`in-reel${adVisible ? ' ad-in' : ''}`} />}
        <div className="reel-author-row">
          <button type="button" className="reel-author" onClick={() => onViewProfile(post.authorId)}>
            {post.promoted ? (
              <AdIdentity post={post} />
            ) : (
              <>
                <Avatar src={post.authorAvatar} name={post.authorName} size="sm" storyUid={post.authorId} uid={post.authorId} showStatus={false} />
                <span className="reel-author-text">
                  <strong><PostAuthorName post={post} /></strong>
                  {post.time && <small className="reel-timestamp">{post.time}</small>}
                </span>
              </>
            )}
          </button>
          {post.authorId !== userId && (
            <button type="button" className="reel-follow-btn" disabled={followBusy === post.authorId} onClick={() => onFollow(post.authorId, post.authorName)}>
              {following.has(post.authorId) ? 'Linked' : '+ Link'}
            </button>
          )}
        </div>
        {/* Promoted reels already show the headline/description once, inside the
            "Promoted Content" card (AdCtaBar) above this row - repeating post.title /
            post.caption / post.tags here duplicated that same copy a second time,
            stretching this bottom-anchored overlay tall enough to shove the ad card
            up near the top of the screen and leave a big gap under it. Non-ad reels
            are unaffected. */}
        {!post.promoted && post.title && <p className="reel-title">{post.title}</p>}
        {!post.promoted && post.caption && (
          <p className={`reel-caption ${captionOpen ? 'open' : ''}`}>
            {post.caption}
            {post.caption.length > 60 && (
              <button type="button" className="reel-caption-more" onClick={(e) => { e.stopPropagation(); setCaptionOpen((o) => !o) }}>
                {captionOpen ? ' Show less' : ' Show more'}
              </button>
            )}
          </p>
        )}
        {!post.promoted && post.tags && post.tags.length > 0 && <p className="reel-tags">{post.tags.map((tag: string) => (
          <button type="button" key={tag} className="reel-tag-btn" onClick={(e) => { e.stopPropagation(); openHashtag(tag) }}>{tag.startsWith('#') ? tag : `#${tag}`}</button>
        ))}</p>}
        {!post.promoted && <ReelAudioLine post={post} onOpen={() => setSoundOpen(true)} />}
      </div>

      <div className="reel-actions-rail">
        <button type="button" className={`reel-action act-like ${liked ? 'liked' : ''}`} onClick={() => onLike(post)} aria-label="Like">
          <Heart size={26} strokeWidth={2.2} fill={liked ? 'currentColor' : 'none'} />
          <small>{(post.likes || []).length}</small>
        </button>
        <button type="button" className="reel-action act-comment" onClick={() => onOpenComments(post)} aria-label="Comments">
          <MessageCircle size={26} strokeWidth={2.2} />
          <small>{post.commentCount || 0}</small>
        </button>
        {post.authorId !== userId && onOpenGift && (
          <button type="button" className="reel-action act-gift" onClick={() => onOpenGift(post)} aria-label="Send a gift">
            <Gift size={26} strokeWidth={2.2} />
            <small>Gift</small>
          </button>
        )}
        <button type="button" className="reel-action act-share" onClick={() => onOpenShareOptions(post)} aria-label="Share">
          <Share2 size={26} strokeWidth={2.2} />
          <small>{formatCount(post.shareCount || 0)}</small>
        </button>
        <button type="button" className="reel-action" onClick={() => setMoreOpen(true)} aria-label="More options">
          <MoreHorizontal size={26} strokeWidth={2.2} />
        </button>
        {/* Small "Ad" disclosure at the foot of the rail, same spot every viewer sees it. */}
        {post.promoted && <span className="reel-ad-tag">Ad</span>}
      </div>

      {soundOpen && (
        <SoundSheet
          post={post}
          myUid={userId}
          onClose={() => setSoundOpen(false)}
          onRemix={() => { setSoundOpen(false); onRemixSound?.(post) }}
          onOpenReel={(x: Post) => { setSoundOpen(false); onOpenSoundReel?.(x) }}
        />
      )}

      {moreOpen && (
        <div className="reel-more-backdrop" onClick={(e) => { e.stopPropagation(); setMoreOpen(false) }}>
          <div className="reel-more-sheet" role="menu" onClick={(e) => e.stopPropagation()}>
            <span className="reel-more-grab" aria-hidden="true" />
            <button type="button" role="menuitem" className="reel-more-item" onClick={() => { void toggleSaved() }}>
              {saved ? <BookmarkCheck size={18} strokeWidth={1.75} /> : <Bookmark size={18} strokeWidth={1.75} />} {saved ? 'Saved - tap to remove' : 'Save'}
            </button>
            {!photoOnly && (
              <button type="button" role="menuitem" className="reel-more-item" onClick={() => setSpeed((s: number) => (s === 1 ? 1.5 : s === 1.5 ? 2 : s === 2 ? 0.5 : 1))}>
                <Gauge size={18} strokeWidth={1.75} /> Speed: {speed}x
              </button>
            )}
            {!post.promoted && onRemixSound && (soundInfoOf(post).key) && (
              <button type="button" role="menuitem" className="reel-more-item" onClick={() => { setMoreOpen(false); onRemixSound(post) }}>
                <Sparkles size={18} strokeWidth={1.75} /> Remix this sound
              </button>
            )}
            {post.authorId === userId ? (
              <>
                <button
                  type="button"
                  role="menuitem"
                  className="reel-more-item"
                  disabled={!!post.promoted}
                  onClick={() => { setMoreOpen(false); if (!post.promoted) onOpenBoost?.(post) }}
                >
                  <Rocket size={18} strokeWidth={1.75} /> {post.promoted ? 'Boosted' : BOOST_COMING_SOON ? 'Boost (soon)' : 'Boost'}
                </button>
                <button type="button" role="menuitem" className="reel-more-item" onClick={() => { setMoreOpen(false); onEdit?.(post) }}>
                  <Pencil size={18} strokeWidth={1.75} /> Edit
                </button>
                <button type="button" role="menuitem" className="reel-more-item danger" onClick={() => { setMoreOpen(false); onDelete(post) }}>
                  <Trash2 size={18} strokeWidth={1.75} /> Delete
                </button>
              </>
            ) : (
              <>
                <button type="button" role="menuitem" className="reel-more-item" onClick={() => { setMoreOpen(false); onNotInterested?.(post) }}>
                  <ThumbsDown size={18} strokeWidth={1.75} /> Not interested
                </button>
                <button type="button" role="menuitem" className="reel-more-item" onClick={() => { setMoreOpen(false); onReport(post) }}>
                  <Flag size={18} strokeWidth={1.75} /> Report
                </button>
                {adminModeFlag && (
                  <button type="button" role="menuitem" className="reel-more-item danger" onClick={() => { setMoreOpen(false); onDelete(post) }}>
                    <Trash2 size={18} strokeWidth={1.75} /> Remove (admin)
                  </button>
                )}
              </>
            )}
            <button type="button" className="reel-more-item cancel" onClick={() => setMoreOpen(false)}>Cancel</button>
          </div>
        </div>
      )}

      {!photoOnly && <div className="reel-progress"><div ref={progressRef} className="reel-progress-fill" /></div>}
    </div>
  )
}

/* =========================================================================
   CHATS TAB
   ========================================================================= */
/** Swipe a row sideways to reveal actions: right swipe -> `left` actions, left swipe -> `right` actions (touch only; long-press menu still works). */
function SwipeRow({ children, left, right, leftW = 84, rightW = 136, isOpen, onOpen, onClose }: any) {
  const [x, setX] = useState(0)
  const [drag, setDrag] = useState(false)
  const xRef = useRef(0)
  const g = useRef({ x0: 0, y0: 0, base: 0, mode: '' as '' | 'h' | 'v', moved: false })
  const move = (v: number) => { xRef.current = v; setX(v) }
  useEffect(() => { if (!isOpen && xRef.current !== 0) move(0) }, [isOpen])
  const clamp = (v: number) => Math.max(-(right ? rightW + 14 : 0), Math.min(left ? leftW + 14 : 0, v))
  const onTouchStart = (e: React.TouchEvent) => {
    const t = e.touches[0]
    g.current = { x0: t.clientX, y0: t.clientY, base: xRef.current, mode: '', moved: false }
  }
  const onTouchMove = (e: React.TouchEvent) => {
    const o = g.current; const t = e.touches[0]
    const dx = t.clientX - o.x0, dy = t.clientY - o.y0
    if (!o.mode) {
      if (Math.abs(dx) > 9 && Math.abs(dx) > Math.abs(dy) * 1.2) { o.mode = 'h'; setDrag(true) }
      else if (Math.abs(dy) > 9) o.mode = 'v'
    }
    if (o.mode === 'h') { o.moved = true; move(clamp(o.base + dx)) }
  }
  const onTouchEnd = () => {
    const o = g.current
    if (o.mode !== 'h') return
    setDrag(false)
    const v = xRef.current
    const target = v > leftW * 0.45 && left ? leftW : v < -rightW * 0.45 && right ? -rightW : 0
    move(target)
    if (target !== 0) { try { navigator.vibrate?.(8) } catch { /* ignore */ } onOpen() } else onClose()
  }
  const swallowClick = (e: React.MouseEvent) => {
    if (g.current.moved || xRef.current !== 0) {
      e.preventDefault(); e.stopPropagation()
      if (xRef.current !== 0 && !g.current.moved) { move(0); onClose() }
      g.current.moved = false
    }
  }
  return (
    <div className="swipe-row" onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd} onTouchCancel={onTouchEnd}>
      {left && <div className="swipe-actions is-left" style={{ width: Math.max(0, x) }}><div style={{ width: leftW }}>{left}</div></div>}
      {right && <div className="swipe-actions is-right" style={{ width: Math.max(0, -x) }}><div style={{ width: rightW }}>{right}</div></div>}
      <div className={`swipe-content${drag ? ' dragging' : ''}`} style={{ transform: x ? `translateX(${x}px)` : undefined }} onClickCapture={swallowClick}>{children}</div>
    </div>
  )
}

/* =========================================================================
   HOME FEED - the Home tab. Stories strip on top, then every photo / video post running
   edge to edge across the screen, Instagram style: each post is either SQUARE (1:1, 1080x1080)
   or TALL (4:5, 1080x1350). Chats moved to their own full-screen page (message icon in the header).
   Cards are the same AdPostCard used everywhere else (like / comment / share / gift / follow / report).
   Styles: globals.css -> "HOME FEED (edge to edge)".
   ========================================================================= */
const HOME_FEED_PAGE = 8
function HomeFeed({ posts, stories, userId, myAvatar, myName, following, followBusy, playingPostAudioId, suspendVideo, onToggleAudio, onAddStory, onViewStory, onLike, onOpenComments, onOpenShareOptions, onOpenGift, onFollow, onDelete, onReport, onOpenBoost, onEdit, onViewProfile, onRecordView, onOpenComposer }: any) {
  const [doubleTapId, setDoubleTapId] = useState<string | null>(null)
  const lastTapRef = useRef<{ id: string; at: number }>({ id: '', at: 0 })
  const [shown, setShown] = useState(HOME_FEED_PAGE)
  const sentinelRef = useRef<HTMLDivElement | null>(null)

  // Reels-only uploads stay in Shonen; previews (stickers / chat photos) never belong in a feed.
  const feed: Post[] = useMemo(() => (posts as Post[]).filter((p) => !p.previewOnly && p.postKind !== 'reel'), [posts])
  const myStory = (stories || []).find((st: any) => st.authorId === userId)
  const otherStories = useMemo(
    () => [...new Map((stories || []).filter((st: any) => st.authorId !== userId).map((st: any) => [st.authorId, st])).values()]
      .sort((x: any, y: any) => (y.promoted ? 1 : 0) - (x.promoted ? 1 : 0)),
    [stories, userId],
  )

  // Endless scroll: render a page at a time, add the next one just before the bottom is reached.
  useEffect(() => {
    const el = sentinelRef.current
    if (!el || shown >= feed.length || typeof IntersectionObserver === 'undefined') return
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) setShown((n) => n + HOME_FEED_PAGE)
    }, { rootMargin: '900px 0px' })
    io.observe(el)
    return () => io.disconnect()
  }, [shown, feed.length])

  // Double-tap the picture = like (same feel as Instagram).
  const handleImageTap = (post: Post) => {
    const now = Date.now()
    const last = lastTapRef.current
    if (last.id === post.id && now - last.at < 280) {
      if (!(post.likes || []).includes(userId)) onLike(post)
      setDoubleTapId(post.id)
      setTimeout(() => setDoubleTapId((c) => (c === post.id ? null : c)), 800)
      lastTapRef.current = { id: '', at: 0 }
      return
    }
    lastTapRef.current = { id: post.id, at: now }
  }

  return (
    <div className="home-feed">
      <div className="home-stories cs-strip" role="list" aria-label="Stories">
        <CommsMyStory myStory={myStory} myAvatar={myAvatar} myName={myName} onAddStory={onAddStory} onViewStory={onViewStory} />
        {otherStories.map((story: any) => (
          <CommsStoryItem key={story.id} story={story} />
        ))}
      </div>

      {feed.length === 0 ? (
        <EmptyState
          icon={<Grid3x3 size={20} />}
          title="No posts yet"
          hint="Posts from people on Animora will show up here."
          action={<button type="button" className="text-button primary" onClick={onOpenComposer}><Plus size={16} /> Create a post</button>}
        />
      ) : (
        <>
          {feed.slice(0, shown).map((post: Post) => (
            <AdPostCard
              key={post.id}
              homeFormat
              post={post}
              userId={userId}
              following={following}
              followBusy={followBusy}
              doubleTapId={doubleTapId}
              playingPostAudioId={playingPostAudioId}
              suspendVideo={suspendVideo}
              onImageTap={handleImageTap}
              onToggleAudio={onToggleAudio}
              onLike={onLike}
              onOpenComments={onOpenComments}
              onOpenShareOptions={onOpenShareOptions}
              onOpenGift={onOpenGift}
              onFollow={onFollow}
              onDelete={onDelete}
              onReport={onReport}
              onOpenBoost={onOpenBoost}
              onEdit={onEdit}
              onViewProfile={onViewProfile}
              onRecordView={onRecordView}
            />
          ))}
          {shown < feed.length && <div ref={sentinelRef} className="home-feed-more" aria-hidden="true" />}
        </>
      )}
    </div>
  )
}

// One chat-list row. Split out of ChatsTab so a typing dot / presence beat / clock tick re-renders only the row it concerns.
function ChatListRowImpl({ chat, isLiveOnline, isTyping, isPinned, isMuted, unread, flaggedUnread, seenFlag, isOpen, setOpenSwipe, onSelect, onLongPress, onMarkRead, onMarkUnread, onToggleMute, onTogglePin }: any) {
  const act = (fn: () => void) => () => { setOpenSwipe(null); fn() }
  return (
        <SwipeRow
          key={chat.id}
          isOpen={isOpen}
          onOpen={() => setOpenSwipe(chat.id)}
          onClose={() => setOpenSwipe((c: string | null) => (c === chat.id ? null : c))}
          leftW={chat.kind === 'group' ? 0 : 84}
          rightW={136}
          left={chat.kind === 'group' ? null : (unread > 0 || flaggedUnread
            ? <button type="button" className="swipe-act is-read" onClick={act(() => onMarkRead(chat))}><CheckCheck size={18} /><span>Read</span></button>
            : <button type="button" className="swipe-act is-read" onClick={act(() => onMarkUnread(chat))}><MessageCircle size={18} /><span>Unread</span></button>)}
          right={<>
            <button type="button" className="swipe-act is-mute" onClick={act(() => onToggleMute(chat))}>{isMuted ? <Bell size={18} /> : <BellOff size={18} />}<span>{isMuted ? 'Unmute' : 'Mute'}</span></button>
            <button type="button" className="swipe-act is-pin" onClick={act(() => onTogglePin(chat))}>{isPinned ? <PinOff size={18} /> : <Pin size={18} />}<span>{isPinned ? 'Unpin' : 'Pin'}</span></button>
          </>}
        >
        <button
          className={`chat-row ${unread > 0 || flaggedUnread ? 'is-unread' : ''} ${isPinned ? 'is-pinned' : ''}`}
          onClick={() => onSelect(chat)}
          onContextMenu={(e) => { e.preventDefault(); onLongPress(chat) }}
        >
          {/* frame intentionally omitted here so every row in the chat list keeps the
              same plain, even look - a custom frame made some avatars look "thicker"
              than others and threw off the list's rhythm. */}
          <Avatar src={chat.avatar} name={chat.name} size="md" uid={chat.kind === 'group' ? undefined : chat.id} online={isLiveOnline} showStatus={true} />

          <div className="chat-copy">
            <div className="chat-row-top">
              <strong className="chat-row-name" style={{ fontSize: '.92rem', color: 'var(--ink)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {chat.name}
                {isPinned && <span className="pinned-badge"><Pin size={14} /></span>}
                {isMuted && <span className="pinned-badge"><BellOff size={14} /></span>}
              </strong>
            </div>

            <div className="chat-row-bottom">
              {isTyping ? (
                <span style={{ fontSize: '.76rem', color: 'var(--cyan)', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 4 }}>
                  typing<span className="typing-dots" aria-hidden="true"><i /><i /><i /></span>
                </span>
              ) : (
                <p style={{ margin: 0, fontSize: '.76rem', color: 'var(--muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0, display: 'flex', alignItems: 'center', gap: 0 }}>
                  {chat.dm === 'request-sent' && <span className="chat-seen-tag">Request sent</span>}
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}><IconText text={chat.preview} /></span>
                  {/* Instagram-style single line: "message · 9:06 AM". When my last message was opened it reads "message · Seen". */}
                  {(() => {
                    const seen = seenFlag
                    const tail = seen ? 'Seen' : chat.time
                    if (!tail) return null
                    return (
                      <span className={`chat-meta-tail ${seen ? 'is-seen' : ''}`} style={{ flexShrink: 0, whiteSpace: 'nowrap', fontWeight: unread && !seen ? 700 : undefined, color: unread && !seen ? 'var(--online-green)' : undefined }}>
                        {'\u00A0\u00B7\u00A0'}{tail}
                      </span>
                    )
                  })()}
                </p>
              )}

              {unread > 0 ? (
                <span className={`chat-unread-badge ${isMuted ? 'is-muted' : ''}`} aria-label={`${unread} unread messages`}>
                  {unread > 99 ? '99+' : unread}
                </span>
              ) : flaggedUnread ? (
                <span className="chat-unread-badge chat-unread-dot" aria-label="Marked unread" />
              ) : (
                <span style={{ fontSize: '.66rem', fontWeight: 700, flexShrink: 0, marginLeft: 8, color: isLiveOnline ? 'var(--online-green)' : 'var(--muted)' }}>
                  {isLiveOnline ? 'Online' : ''}
                </span>
              )}
            </div>
          </div>
        </button>
        </SwipeRow>
  )
}
const ChatListRow = memoLatest(ChatListRowImpl)

function ChatsTab({ stories, userId, myAvatar, myName, onAddStory, onViewStory, onFindPeople, onDiscoverFriends, chats, query, setQuery, pinnedChatIds, mutedChatIds, onSelect, onNewGroup, onOpenPublicGuilds, onManageFriends, requestCount = 0, onOpenRequests, onLongPress, presenceMap, typingMap, lastMsgMeta, unreadMap, nowTick, onTogglePin, onToggleMute, onMarkRead, onMarkUnread, manualUnread, onBack, coinBalance = 0, onOpenWallet, onOpenGames, onOpenAlerts, hasUnreadAlerts, myNote, friendNotes = [], onSaveNote, onDeleteNote, }: any) {
  const [openSwipe, setOpenSwipe] = useState<string | null>(null)
  const [noteOpen, setNoteOpen] = useState(false)
  const myStory = (stories || []).find((st: any) => st.authorId === userId)
  const otherStories = [...new Map((stories || []).filter((st: any) => st.authorId !== userId).map((st: any) => [st.authorId, st])).values()]
    .sort((x: any, y: any) => (y.promoted ? 1 : 0) - (x.promoted ? 1 : 0))
  const hasStoryTray = Boolean(myStory) || otherStories.length > 0
  return (
    <div className="comms-tab">
      {/* Pinned top: stories, title row, search. Only .comms-scroll below scrolls. */}
      <div className="comms-top">
      {/* App header inside Comms (this page covers the main header): wordmark left, coins / games / alerts right. */}
      {onBack && (
        <header className="topbar app-head comms-appbar">
          <BrandMark />
          <div className="topbar-actions">
            <button className="coin-chip" onClick={onOpenWallet} aria-label="Coin wallet">
              <CoinIcon size={18} />
              <span>{Number(coinBalance || 0).toLocaleString('en-IN')}</span>
            </button>
            <button className="icon-button hd-round" onClick={onOpenGames} aria-label="Games" title="Games"><Gamepad2 size={17} /></button>
            <button className="icon-button hd-round" onClick={onOpenAlerts} aria-label="Alerts">
              <Bell size={17} />
              {hasUnreadAlerts && <span className="notif-dot" />}
            </button>
          </div>
        </header>
      )}
      {/* Notes: friends-only, 24 h. */}
      <NotesStrip
        myNote={myNote} myAvatar={myAvatar} myName={myName} friendNotes={friendNotes}
        onEditMine={() => setNoteOpen(true)}
        onOpenFriend={(f: any) => { const c = chats.find((x: any) => x.kind !== 'group' && x.id === f.id); if (c) onSelect(c) }}
      />
      {noteOpen && <NoteEditor initial={myNote?.text || ''} hasNote={Boolean(myNote)} onSave={onSaveNote} onDelete={onDeleteNote} onClose={() => setNoteOpen(false)} />}
      {/* Stories: always on top. First bubble is YOU (your own profile picture + a + badge), then friends with live stories. */}
      {!onBack && (
      <div className="cs-strip" role="list" aria-label="Stories">
        <CommsMyStory myStory={myStory} myAvatar={myAvatar} myName={myName} onAddStory={onAddStory} onViewStory={onViewStory} />
        {otherStories.map((story: any) => (
          <CommsStoryItem key={story.id} story={story} />
        ))}
      </div>
      )}
      <div className="comms-head">
        {onBack && (
          <button type="button" className="comms-back" onClick={onBack} aria-label="Back"><ArrowLeft size={22} /></button>
        )}
        <div className="comms-title">
          <div className="comms-title-row">
            <h2>Comms</h2>
          </div>
          <span>{chats.length} {chats.length === 1 ? 'channel' : 'channels'}</span>
        </div>
        <div className="comms-actions">
          <button type="button" className="comms-act" onClick={onOpenRequests} aria-label={`Message requests${requestCount ? `, ${requestCount} pending` : ''}`} title="Requests">
            <MessageCircle size={18} />
            {requestCount > 0 && <span className="comms-act-badge">{requestCount > 99 ? '99+' : requestCount}</span>}
          </button>
          <button type="button" className="comms-act" onClick={onManageFriends} aria-label="Allies" title="Allies"><Users size={18} /></button>
          <button type="button" className="comms-act" onClick={onOpenPublicGuilds} aria-label="Public Guilds" title="Public Guilds"><Globe size={18} /></button>
          <button type="button" className="comms-act is-primary" onClick={onNewGroup} aria-label="New Guild" title="New Guild"><Plus size={18} /></button>
        </div>
      </div>

      <div className="search-box">
        <Search size={16} />
        <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search chats" />
        {query ? (
          <button type="button" className="search-clear" onClick={() => setQuery('')} aria-label="Clear search"><X size={14} /></button>
        ) : (
          <button type="button" className="search-add-friend" onClick={onFindPeople} aria-label="Find people and send a friend request">
            <UserPlus size={14} strokeWidth={2.4} /> <span>Add friend</span>
          </button>
        )}
      </div>
      </div>

      <div className="comms-scroll">
      {requestCount > 0 && (
        <button type="button" className="chat-row comms-requests" onClick={onOpenRequests}>
          <span style={{ width: 44, height: 44, borderRadius: '50%', display: 'grid', placeItems: 'center', background: 'var(--accent-tint)', color: 'var(--cyan)', flexShrink: 0 }}><MessageCircle size={20} /></span>
          <div className="chat-copy">
            <strong style={{ fontSize: '.92rem', color: 'var(--ink)' }}>Message requests</strong>
            <p style={{ margin: 0, fontSize: '.76rem', color: 'var(--muted)' }}>{requestCount} {requestCount === 1 ? 'person wants' : 'people want'} to message you</p>
          </div>
          <span className="chat-unread-badge">{requestCount > 99 ? '99+' : requestCount}</span>
        </button>
      )}

      {chats.length === 0 && (
        query.trim() ? (
          <EmptyState icon={<Search size={20} />} title="No chats found" hint="Try a different name." compact />
        ) : (
          <EmptyState
            icon={<Users size={20} />}
            title="No friends yet"
            hint="Tap below to discover people and make friends. Chats will show up here."
            action={<button type="button" className="text-button primary" onClick={onDiscoverFriends || onFindPeople}><Users size={16} /> Make friends</button>}
          />
        )
      )}

      <div className="chat-list">
        {chats.map((chat: Chat) => {
          const isLiveOnline = chat.kind !== 'group'
            ? Boolean(presenceMap[chat.id] && nowTick - presenceMap[chat.id] < PRESENCE_ONLINE_MS)
            : (chat.members || []).some((m: string) => m !== userId && Boolean(presenceMap[m] && nowTick - presenceMap[m] < PRESENCE_ONLINE_MS))
          const unread = chat.kind === 'group' ? 0 : (unreadMap?.[chat.id] || 0)
          return (
            <ChatListRow
              key={chat.id}
              chat={chat}
              isLiveOnline={isLiveOnline}
              isTyping={Boolean(typingMap[chat.id])}
              isPinned={pinnedChatIds.has(chat.id)}
              isMuted={mutedChatIds.has(`${chat.kind === 'group' ? 'group' : 'direct'}:${chat.id}`)}
              unread={unread}
              flaggedUnread={chat.kind !== 'group' && unread === 0 && Boolean(manualUnread?.has(chat.id))}
              seenFlag={chat.kind !== 'group' && chat.dm !== 'request-sent' && !!lastMsgMeta?.[chat.id]?.own && !!lastMsgMeta[chat.id].read}
              isOpen={openSwipe === chat.id}
              setOpenSwipe={setOpenSwipe}
              onSelect={onSelect}
              onLongPress={onLongPress}
              onMarkRead={onMarkRead}
              onMarkUnread={onMarkUnread}
              onToggleMute={onToggleMute}
              onTogglePin={onTogglePin}
            />
          )
        })}
      </div>
      </div>
    </div>
  )
}

/* =========================================================================
   STICKER KEYBOARD - two tabs:
   * "My stickers": stickers you saved from chats + ones you added yourself (+ tile).
   * "GIPHY": search (falls back to trending when the box is empty).
   Tapping a sticker sends it as a small sticker message (no card, no preview).
   ========================================================================= */
function StickerPickerSheet({ savedStickers = [], stickerLimit, onStickerLimit, onPick, onAddSticker, onRemoveSticker, onClose }: {
  savedStickers?: string[]
  stickerLimit?: number
  onStickerLimit?: () => void
  onPick: (url: string) => void
  onAddSticker?: (file: File) => Promise<void> | void
  onRemoveSticker?: (url: string) => void
  onClose: () => void
}) {
  const [tab, setTab] = useState<'mine' | 'giphy'>(savedStickers.length ? 'mine' : 'giphy')
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<{ id: string; url: string; preview: string }[]>([])
  const [loading, setLoading] = useState(true)
  const [adding, setAdding] = useState(false)
  const debounceRef = useRef<any>(null)
  const fileRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (tab !== 'giphy') return
    const run = async () => {
      setLoading(true)
      try {
        const endpoint = query.trim()
          ? `https://api.giphy.com/v1/stickers/search?api_key=${GIPHY_API_KEY}&q=${encodeURIComponent(query.trim())}&limit=24&rating=g`
          : `https://api.giphy.com/v1/stickers/trending?api_key=${GIPHY_API_KEY}&limit=24&rating=g`
        const res = await fetch(endpoint)
        const data = await res.json()
        const items = Array.isArray(data?.data) ? data.data : []
        setResults(items.map((g: any) => ({
          id: g.id,
          url: g.images?.original?.url || g.images?.fixed_height?.url,
          preview: g.images?.fixed_width_small?.url || g.images?.fixed_height_small?.url || g.images?.fixed_height?.url,
        })).filter((s: any) => s.url && s.preview))
      } catch {
        setResults([])
      } finally {
        setLoading(false)
      }
    }
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(run, query.trim() ? 350 : 0)
    return () => clearTimeout(debounceRef.current)
  }, [query, tab])

  const tileStyle: React.CSSProperties = { borderRadius: 12, overflow: 'hidden', aspectRatio: '1 / 1', background: 'var(--soft)', border: '1px solid var(--line)', padding: 4, width: '100%' }
  const imgStyle: React.CSSProperties = { width: '100%', height: '100%', objectFit: 'contain', display: 'block' }
  const gridStyle: React.CSSProperties = { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0.5rem', maxHeight: '46vh', overflowY: 'auto' }

  return (
    <div className="action-sheet-backdrop" onClick={onClose}>
      <div className="action-sheet sticker-sheet" onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
          <strong style={{ color: 'var(--ink)', fontSize: '.92rem', display: 'flex', alignItems: 'center', gap: 8 }}>
            <Sticker size={16} color="var(--cyan)" /> Stickers
          </strong>
          <button className="icon-button" style={{ width: 28, height: 28 }} onClick={onClose}><X size={16} /></button>
        </div>

        <div className="theme-filter-row">
          <button type="button" className={`theme-filter ${tab === 'mine' ? 'active' : ''}`} onClick={() => setTab('mine')}>My stickers</button>
          <button type="button" className={`theme-filter ${tab === 'giphy' ? 'active' : ''}`} onClick={() => setTab('giphy')}>GIPHY</button>
        </div>

        {tab === 'mine' ? (
          <>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={async (e) => {
                const file = e.target.files?.[0]
                e.target.value = ''
                if (!file || !onAddSticker) return
                setAdding(true)
                try { await onAddSticker(file) } finally { setAdding(false) }
              }}
            />
            <div style={gridStyle}>
              <button
                type="button"
                disabled={adding}
                aria-label="Add your own sticker"
                title="Add your own sticker"
                onClick={() => {
                  // At the sticker limit: don't even open the file picker. This sheet sits above
                  // modals, so close it first, then let the page explain / open the Green Tick store.
                  if (stickerLimit && savedStickers.length >= stickerLimit) { onClose(); onStickerLimit?.(); return }
                  fileRef.current?.click()
                }}
                style={{ ...tileStyle, border: '1.5px dashed var(--line-strong)', color: 'var(--ink-2)', display: 'grid', placeItems: 'center' }}
              >
                {adding ? <RefreshCw size={18} className="spin-icon" /> : <Plus size={20} />}
              </button>
              {savedStickers.map((url) => (
                <div key={url} style={{ position: 'relative', aspectRatio: '1 / 1' }}>
                  <button type="button" onClick={() => { onPick(url); onClose() }} style={tileStyle}>
                    <img decoding="async" src={url} alt="" loading="lazy" style={imgStyle} />
                  </button>
                  <button
                    type="button"
                    aria-label="Remove sticker"
                    onClick={(e) => { e.stopPropagation(); onRemoveSticker?.(url) }}
                    style={{ position: 'absolute', top: 3, right: 3, width: 18, height: 18, borderRadius: '50%', display: 'grid', placeItems: 'center', padding: 0, background: 'rgba(15,17,26,.8)', color: '#fff', border: '1px solid rgba(255,255,255,.35)' }}
                  >
                    <X size={14} />
                  </button>
                </div>
              ))}
            </div>
            {!savedStickers.length && (
              <small style={{ color: 'var(--muted)', display: 'block', textAlign: 'center', padding: '0.75rem 0.5rem 0', lineHeight: 1.5 }}>
                Tap + to add your own sticker. Long-press a sticker in a chat to save it here.
              </small>
            )}
          </>
        ) : (
          <>
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search stickers…"
              style={{
                width: '100%', padding: '0.5rem 0.75rem', borderRadius: 12, border: '1px solid var(--line)',
                background: 'var(--soft)', color: 'var(--ink)', fontSize: '.85rem', marginBottom: '0.75rem',
              }}
            />
            <div style={gridStyle}>
              {loading && <div style={{ gridColumn: '1 / -1' }}><SkeletonRows count={3} /></div>}
              {!loading && !results.length && <small style={{ color: 'var(--muted)', gridColumn: '1 / -1', textAlign: 'center', padding: '1rem 0' }}>No stickers found.</small>}
              {results.map((s) => (
                <button key={s.id} type="button" onClick={() => { onPick(s.url); onClose() }} style={tileStyle}>
                  <img decoding="async" src={s.preview} alt="" loading="lazy" style={imgStyle} />
                </button>
              ))}
            </div>
            <small style={{ color: 'var(--muted)', fontSize: '.62rem', display: 'block', textAlign: 'center', marginTop: '0.5rem' }}>Powered by GIPHY</small>
          </>
        )}
      </div>
    </div>
  )
}

/* =========================================================================
   THEME PICKER MODAL - shared by the chat (wallpaper + bubble color) and by
   the palette button in the top header (app-wide wallpaper).
   ========================================================================= */
function ThemePickerModal({
  title, hint, activeId, onSelect, onClose, isPremium, unlockedChatThemes, themeShop,
}: any) {
  const [filter, setFilter] = useState<'all' | 'dark' | 'light' | 'free' | 'premium'>('all')
  // Tapping a locked premium theme opens a direct "unlock this theme" purchase sheet (no store).
  const [buyingTheme, setBuyingTheme] = useState<{ id: string; label: string; url?: string } | null>(null)
  const lockedThemeCount = isPremium ? 0 : PREMIUM_CHAT_THEME_IDS.filter((id) => !(unlockedChatThemes || []).includes(id)).length
  const FILTERS: { id: typeof filter; label: string }[] = [
    { id: 'all', label: 'All' },
    { id: 'dark', label: 'Black' },
    { id: 'light', label: 'White' },
    { id: 'free', label: 'Free' },
    { id: 'premium', label: 'Premium' },
  ]
  const visible = CHAT_BACKGROUNDS.filter((bg) => {
    if (bg.id === 'none') return filter === 'all' || filter === 'free'
    if (filter === 'dark') return bg.group === 'dark'
    if (filter === 'light') return bg.group === 'light'
    if (filter === 'free') return !isPremiumChatTheme(bg.id)
    if (filter === 'premium') return isPremiumChatTheme(bg.id)
    return true
  })
  return (
    <>
    <div className="modal-backdrop fp-backdrop">
      <div className="modal-card fp-card">
        <header className="fp-header">
          <button type="button" className="icon-button fp-back" onClick={onClose} aria-label="Back"><ArrowLeft size={20} /></button>
          <h2 className="fp-title">{title}</h2>
        </header>
        {hint && <p style={{ margin: '-.3rem 0 0.75rem', fontSize: '.74rem', color: 'var(--muted)' }}>{hint}</p>}

        <div className="theme-filter-row" role="tablist" aria-label="Theme filter">
          {FILTERS.map((ft) => (
            <button key={ft.id} type="button" role="tab" aria-selected={filter === ft.id} className={`theme-filter ${filter === ft.id ? 'active' : ''}`} onClick={() => setFilter(ft.id)}>
              {ft.label}
            </button>
          ))}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
          {visible.map((bg) => {
            const premiumTheme = isPremiumChatTheme(bg.id)
            const isLockedTheme = premiumTheme && !isPremium && !(unlockedChatThemes || []).includes(bg.id)
            return (
            <button
              type="button"
              key={bg.id}
              onClick={() => isLockedTheme ? setBuyingTheme({ id: bg.id, label: bg.label, url: bg.url }) : (() => { onSelect?.(bg.id); onClose?.() })()}
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '0.5rem',
                padding: '0.5rem',
                borderRadius: 14,
                background: 'var(--paper)',
                border: activeId === bg.id ? '2px solid var(--cyan)' : '1px solid var(--line)',
                position: 'relative',
              }}
            >
              <div
                style={{
                  position: 'relative',
                  width: '100%',
                  height: 118,
                  borderRadius: 10,
                  overflow: 'hidden',
                  background: bg.url ? undefined : 'var(--paper)',
                  backgroundImage: bg.url ? `url(${bg.url})` : undefined,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  border: '1px solid var(--line)',
                  filter: isLockedTheme ? 'saturate(.75)' : undefined,
                }}
              >
                {bg.id !== 'none' && (
                  <span className={`theme-tag ${premiumTheme ? 'pro' : 'free'}`}>
                    {premiumTheme ? (isLockedTheme ? <Lock size={14} /> : <Crown size={14} />) : null}
                    {premiumTheme ? 'Premium' : 'Free'}
                  </span>
                )}
                {activeId === bg.id && <span className="theme-check"><Check size={14} strokeWidth={3} /></span>}
              </div>
              <span style={{ fontSize: '.76rem', color: 'var(--ink)', fontWeight: 700, textAlign: 'left', padding: '0 0.15rem 0.1rem' }}>{bg.label}</span>
            </button>
            )
          })}
        </div>

      </div>
    </div>

    {/* Locked theme tapped: buy exactly that theme right here (a sibling of the picker, so closing it never closes the picker) */}
    {buyingTheme && themeShop && (
      <ThemePurchaseSheet
        theme={buyingTheme}
        lockedCount={lockedThemeCount}
        coins={themeShop.coins}
        onUnlock={themeShop.unlock}
        onDone={(themeId: string) => { onSelect?.(themeId); setBuyingTheme(null); onClose?.() }}
        onClose={() => setBuyingTheme(null)}
        onAddCoins={themeShop.addCoins}
        onOpenGreenTick={() => { setBuyingTheme(null); themeShop.openTick() }}
      />
    )}
    </>
  )
}

/* =========================================================================
   KEYBOARD-PINNED CHAT VIEW (Tap on Shared Post -> Opens Post Preview)
   ========================================================================= */
// Small chip that stands in for a view-once photo inside the chat bubble.
function ViewOnceChip({ item, currentUserId, onOpen }: { item: Message; currentUserId: string; onOpen: () => void }) {
  const viewedBy = item.viewedBy || []
  const others = viewedBy.filter((u) => u !== currentUserId)
  const opened = item.own ? others.length > 0 : viewedBy.includes(currentUserId)
  const canOpen = !item.own && !opened
  const sub = canOpen
    ? 'Tap to view · plays once'
    : item.own
      ? (opened ? (others.length > 1 ? `Opened by ${others.length}` : 'Opened') : 'Sent · view once')
      : 'Opened · no longer available'
  return (
    <button type="button" className={`vo-chip ${canOpen ? 'can-open' : 'is-done'}`} aria-disabled={!canOpen} onClick={() => { if (canOpen) onOpen() }}>
      <span className="vo-icon">{canOpen || (item.own && !opened) ? '1' : opened && item.own ? <Eye size={16} /> : <EyeOff size={16} />}</span>
      <span className="vo-label"><b>Photo</b><small>{sub}</small></span>
    </button>
  )
}

function VoiceMessageBubble({ url, duration }: { url: string; duration?: number }) {
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const [playing, setPlaying] = useState(false)
  const [progress, setProgress] = useState(0)
  const [elapsed, setElapsed] = useState(0)
  const [totalDuration, setTotalDuration] = useState(duration || 0)

  useEffect(() => {
    const audio = new Audio(url)
    audioRef.current = audio
    const onTime = () => {
      setElapsed(audio.currentTime)
      if (isFinite(audio.duration) && audio.duration > 0) setProgress(audio.currentTime / audio.duration)
    }
    const onMeta = () => { if (isFinite(audio.duration) && audio.duration > 0) setTotalDuration(audio.duration) }
    const onEnd = () => { setPlaying(false); setProgress(0); setElapsed(0) }
    audio.addEventListener('timeupdate', onTime)
    audio.addEventListener('loadedmetadata', onMeta)
    audio.addEventListener('ended', onEnd)
    return () => {
      audio.pause()
      audio.removeEventListener('timeupdate', onTime)
      audio.removeEventListener('loadedmetadata', onMeta)
      audio.removeEventListener('ended', onEnd)
    }
  }, [url])

  const toggle = () => {
    const audio = audioRef.current
    if (!audio) return
    if (playing) { audio.pause(); setPlaying(false) } else { void audio.play().catch(() => undefined); setPlaying(true) }
  }

  const seek = (e: React.MouseEvent<HTMLDivElement>) => {
    const audio = audioRef.current
    if (!audio || !totalDuration) return
    const rect = e.currentTarget.getBoundingClientRect()
    const ratio = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width))
    audio.currentTime = ratio * totalDuration
    setProgress(ratio)
  }

  const fmt = (s: number) => `${Math.floor(s / 60)}:${Math.floor(s % 60).toString().padStart(2, '0')}`

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 190, padding: '0.15rem 0.1rem' }}>
      <button
        type="button"
        onClick={toggle}
        style={{ width: 34, height: 34, borderRadius: '50%', border: 0, display: 'grid', placeItems: 'center', background: 'var(--cyan)', color: '#fff', flexShrink: 0, cursor: 'pointer' }}
        aria-label={playing ? 'Pause voice note' : 'Play voice note'}
      >
        {playing ? <Pause size={16} fill="currentColor" /> : <Play size={16} fill="currentColor" style={{ marginLeft: 1 }} />}
      </button>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div onClick={seek} style={{ height: 5, borderRadius: 999, background: 'rgba(127,127,127,0.3)', cursor: 'pointer', position: 'relative' }}>
          <div style={{ position: 'absolute', inset: 0, width: `${Math.min(100, progress * 100)}%`, borderRadius: 999, background: 'var(--cyan)' }} />
        </div>
        <small style={{ fontSize: '.65rem', opacity: 0.75, display: 'block', marginTop: 4 }}>
          {fmt(playing || elapsed > 0 ? elapsed : (totalDuration || duration || 0))}
        </small>
      </div>
    </div>
  )
}

// Reactions on a message, WhatsApp style: ONE small pill that sits on the bottom edge of the bubble (bottom-right on
// your own messages, bottom-left on received ones) - never inside the card. It shows the emoji (up to 3 different
// ones, most used first) and a number ONLY when more than one person reacted: a single reaction is just the emoji.
const reactionEntries = (m: Message) =>
  Object.entries(m.reactions || {}).filter(([, uids]) => uids.length).sort((a, b) => b[1].length - a[1].length)
const hasReactions = (m: Message) => !m.deleted && reactionEntries(m).length > 0

function ReactionPill({ item, onOpen }: { item: Message; onOpen?: () => void }) {
  if (item.deleted) return null
  const list = reactionEntries(item)
  if (!list.length) return null
  const total = list.reduce((n, [, uids]) => n + uids.length, 0)
  return (
    <button
      type="button"
      className="msg-reaction"
      onClick={onOpen}
      aria-label={`${list.map(([emoji]) => emoji).join(' ')}${total > 1 ? ` ${total} reactions - tap to see who` : ' reaction - tap to see who'}`}
    >
      {list.slice(0, 3).map(([emoji]) => <span key={emoji} className="msg-reaction-emoji"><Emoji3D char={emoji} /></span>)}
      {total > 1 && <span className="msg-reaction-count">{total}</span>}
    </button>
  )
}

// Who-reacted list: grouped by emoji (most-used first, same order as the pill), each with the
// reactor's name/avatar - resolved from the group's member profiles for a group chat, or from
// the two people in a direct chat (only ever the current user or the other party).
function ReactionListModal({ msg, chat, currentUserId, groupMemberProfiles, onClose }: {
  msg: Message; chat: Chat; currentUserId: string; groupMemberProfiles: Profile[]; onClose: () => void
}) {
  const list = reactionEntries(msg)
  const resolve = (uid: string): { name: string; avatar: string } => {
    if (uid === currentUserId) return { name: 'You', avatar: '' }
    if (chat.kind === 'group') {
      const m = groupMemberProfiles.find((p) => p.uid === uid)
      return { name: m?.name || 'Operative', avatar: m?.avatar || '' }
    }
    // Direct chat: the only other possible reactor is the chat partner.
    return { name: chat.name, avatar: chat.avatar }
  }
  // Back gesture / back button closes just this sheet (the chat underneath stays open).
  useBackLayer(true, onClose)
  const total = list.reduce((n, [, uids]) => n + uids.length, 0)
  return (
    <div className="action-sheet-backdrop" onClick={onClose}>
      <div className="rx-sheet" onClick={(e) => e.stopPropagation()} role="dialog" aria-label="Reactions">
        <div className="comments-sheet-grab" />
        <div className="rx-head">
          <strong>Reactions</strong>
          <span className="rx-chips">
            {list.map(([emoji, uids]) => (
              <span key={emoji} className="rx-chip"><Emoji3D char={emoji} /> <b>{uids.length}</b></span>
            ))}
          </span>
          <span className="rx-total">{total}</span>
        </div>
        <div className="rx-list">
          {/* One flat list: who reacted, with THEIR emoji on the right (grouped by emoji, most-used first). */}
          {list.flatMap(([emoji, uids]) => uids.map((uid) => {
            const { name, avatar } = resolve(uid)
            return (
              <div key={`${emoji}:${uid}`} className="rx-row">
                <Avatar src={avatar} name={name} size="sm" uid={uid} showStatus={false} />
                <span className="rx-name" style={{ fontWeight: uid === currentUserId ? 700 : 500 }}>{name}</span>
                <span className="rx-emoji"><Emoji3D char={emoji} size="1.7rem" /></span>
              </div>
            )
          }))}
        </div>
      </div>
    </div>
  )
}

// One chat message. Memoized: typing in the composer / swiping one bubble no longer re-renders every message.
// All handlers go through `api` (a stable ref holding the latest closures), so memo never sees a stale one.
type ChatRowApi = {
  handleTouchStart: (m: Message, e: React.TouchEvent) => void
  handleTouchMove: (m: Message, e: React.TouchEvent) => void
  handleTouchEnd: (m: Message) => void
  handleBubbleTap: (m: Message) => void
  setSelectedMsg: (m: Message | null) => void
  setViewer: (v: { url: string; once?: Message } | null) => void
  setReactionListMsg: (m: Message | null) => void
  onOpenSharedPost?: (url: string, postId?: string, mediaType?: string) => void
  onJoinLudo?: (code: string) => void
  onJoinChess?: (code: string) => void
  onJoinCarrom?: (code: string) => void
  jumpToMessage?: (id?: string) => void
  onMention?: (handle: string) => void
  chatId?: string
}
// Plain "Seen 18h ago" under the last message you sent. Re-checks every minute so "just now" turns into "5m ago" on its own.
// Older messages that were read before the time was saved just show "Seen".
function seenAgoText(at?: number, now = Date.now()): string {
  if (!at || at > now + 60_000) return 'Seen'
  const sec = Math.max(0, Math.floor((now - at) / 1000))
  if (sec < 60) return 'Seen just now'
  const min = Math.floor(sec / 60)
  if (min < 60) return `Seen ${min}m ago`
  const hr = Math.floor(min / 60)
  if (hr < 24) return `Seen ${hr}h ago`
  return `Seen ${Math.floor(hr / 24)}d ago`
}
function SeenAgo({ at, className }: { at?: number; className?: string }) {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    if (!at) return
    const t = setInterval(() => setNow(Date.now()), 60_000)
    return () => clearInterval(t)
  }, [at])
  return <div className={className}>{seenAgoText(at, now)}</div>
}

const ChatMessageRow = memo(function ChatMessageRow({ item, chat, currentUserId, dragDx, heartPop, isNew, showSeen, api, replyOrig, groupStart, showAvatar }: {
  item: Message; chat: any; currentUserId: string; dragDx: number | null; heartPop: boolean; isNew: boolean; showSeen: boolean; api: { current: ChatRowApi }; replyOrig?: Message
  groupStart: boolean; showAvatar: boolean
}) {
  // What the reply quote shows. Built from the ORIGINAL message when both people have it (so a sticker / photo /
  // voice note looks identical on both phones and never depends on what was copied into the reply), and falls
  // back to the fields stored on the reply itself (older replies, or the original is scrolled out / deleted).
  const rp = replyOrig && !replyOrig.deleted ? replyPreviewOf(replyOrig) : null
  const quoteText = rp?.text || item.replyToText || ''
  const quoteImage = rp?.image || item.replyToImage
  const quoteSender = replyOrig ? (replyOrig.own ? 'You' : (replyOrig.senderName || 'Friend')) : (item.replyToSender || 'Reply')
  const bubbleEq = useLiveEquipped(item.own ? currentUserId : (item.senderId || (chat.kind === 'group' ? undefined : chat.id)))
  return (
    <>
    <div data-mid={item.id} className={`message ${item.own ? 'own' : ''}${hasReactions(item) ? ' has-reactions' : ''}${groupStart ? ' group-start' : ''}`}>
      {!item.own && !showAvatar && <span className="msg-avatar-spacer" aria-hidden="true" />}
      {!item.own && showAvatar && <Avatar src={item.senderAvatar || chat.avatar} name={item.senderName || chat.name} size="xs" uid={chat.kind === 'group' ? item.senderId : (item.senderId || chat.id)} showStatus={false} />}

      {dragDx != null && dragDx > 10 && (
        <span
          className="swipe-reply-hint"
          style={{
            opacity: Math.min(1, (dragDx ?? 0) / 40),
            transform: `scale(${Math.min(1, (dragDx ?? 0) / 40)})`
          }}
        >
          <Reply size={16} />
        </span>
      )}

      <div
        className={`bubble${isStickerMsg(item) || (!item.deleted && !item.viewOnce && !item.audioUrl && !item.sharedImageUrl && !item.ludo && !item.chess && !item.carrom && !item.call && !isSharedPostMsg(item) && isJumboText(item.text)) ? ' sticker-bubble' : ''}${isPlainPhotoMsg(item) ? ' photo-bubble' : ''}${isSharedPostMsg(item) ? ' share-bubble' : ''}${item.viewOnce && !item.deleted ? ' vo-bubble' : ''}${isNew ? ' bubble-pop-in' : ''}${bubbleEq.bubble && !isStickerMsg(item) && !item.deleted ? ` pz-bb pz-bb-${bubbleEq.bubble}` : ''}`}
        style={{
          touchAction: 'pan-y',
          position: 'relative',
          transform: dragDx != null ? `translateX(${dragDx}px)` : undefined,
          transition: dragDx != null ? 'none' : 'transform 0.2s ease'
        }}
        onTouchStart={(e) => api.current.handleTouchStart(item, e)}
        onTouchMove={(e) => api.current.handleTouchMove(item, e)}
        onTouchEnd={() => api.current.handleTouchEnd(item)}
        onClick={() => api.current.handleBubbleTap(item)}
        onContextMenu={(e) => { e.preventDefault(); api.current.setSelectedMsg(item) }}
      >
        {heartPop && <span className="heart-pop" aria-hidden="true">❤️</span>}
        {item.forwarded && !item.deleted && <div className="fwd-tag" style={{ fontSize: '.68rem', opacity: .75, display: 'flex', alignItems: 'center', gap: 4 }}><Forward size={14} /> Forwarded</div>}
        {(quoteText || quoteImage) && !item.deleted && (
          <div
            className={`reply-quote${quoteImage ? ' has-thumb' : ''}${item.replyToId ? ' is-jump' : ''}`}
            onClick={item.replyToId ? (e) => { e.stopPropagation(); api.current.jumpToMessage?.(item.replyToId) } : undefined}
          >
            <div className="reply-quote-body">
              <b className="reply-quote-name">{quoteSender}</b>
              <span className="reply-quote-text"><IconText text={quoteText || '📷 Photo'} /></span>
            </div>
            {quoteImage && <img className="reply-quote-thumb" src={quoteImage} alt="" loading="lazy" decoding="async" draggable={false} />}
          </div>
        )}

        {/* Tapping a Shared Post/Reel opens the full Post Preview Modal - video gets
            a real <video> thumbnail + play badge instead of a broken <img decoding="async"> */}
        {item.sharedImageUrl && isStickerMsg(item) && (
          <img decoding="async" className="chat-sticker" src={item.sharedImageUrl} alt="Sticker" draggable={false} loading="lazy" />
        )}
        {/* Normal photo: just the picture in the bubble (no card, no "tap to view post") */}
        {isPlainPhotoMsg(item) && (
          <div className="chat-photo-wrap" onClick={() => api.current.setViewer({ url: item.sharedImageUrl! })}>
            <img decoding="async" className="chat-photo" src={item.sharedImageUrl} alt="Photo" draggable={false} loading="lazy" />
          </div>
        )}
        {/* View-once photo: only a small chip - the picture itself is never drawn in the chat */}
        {item.viewOnce && !item.deleted && item.sharedImageUrl && (
          <ViewOnceChip item={item} currentUserId={currentUserId} onOpen={() => api.current.setViewer({ url: item.sharedImageUrl!, once: item })} />
        )}
        {isSharedPostMsg(item) && (
          <SharedPostCard item={item} onOpen={() => api.current.onOpenSharedPost?.(item.sharedImageUrl!, item.sharedPostId, item.sharedMediaType)} />
        )}

        {item.audioUrl && !item.deleted && (
          <VoiceMessageBubble url={item.audioUrl} duration={item.audioDuration} />
        )}

        {item.ludo && !item.deleted && (
          <LudoInviteBubble invite={item.ludo} own={item.own} onJoin={() => api.current.onJoinLudo?.(item.ludo!.code)} />
        )}
        {item.chess && !item.deleted && (
          <ChessInviteBubble invite={item.chess} own={item.own} onJoin={() => api.current.onJoinChess?.(item.chess!.code)} />
        )}

        {item.carrom && !item.deleted && (
          <CarromInviteBubble invite={item.carrom} own={item.own} onJoin={() => api.current.onJoinCarrom?.(item.carrom!.code)} />
        )}

        {item.poll && !item.deleted && <PollBubble chatId={api.current.chatId || ''} messageId={item.id} poll={item.poll} meUid={currentUserId} />}
        {item.call && !item.deleted && <CallLogBubble call={item.call} currentUserId={currentUserId} />}

        {(item.deleted || (item.text && !item.ludo && !item.chess && !item.carrom && !item.call && !item.poll && !isSharedPostMsg(item))) && (
          <div className="bubble-text">
            {item.deleted ? <i style={{ opacity: 0.6 }}>Message deleted</i> : <RichEmojiText text={item.text} onMention={(h) => api.current.onMention?.(h)} />}
          </div>
        )}

        <div className="bubble-meta">
          <span>{item.edited && !item.deleted ? `edited · ${item.time}` : item.time}</span>
        </div>

        {/* Reaction pill: hangs on the bubble's bottom edge (see .msg-reaction in globals.css), not inside the card.
            Tapping it opens the "who reacted" list below. */}
        <ReactionPill item={item} onOpen={() => api.current.setReactionListMsg(item)} />
      </div>
    </div>
    {/* "Seen" sits BELOW the bubble (not inside the card), bright green pill for contrast on any wallpaper */}
    {item.own && item.read && showSeen && (
      <SeenAgo at={item.readAt} className={`seen-plain${hasReactions(item) ? ' has-reactions' : ''}`} />
    )}
    </>
  )
})

// The message box. Owns the typed text in its OWN tiny state so a keystroke re-renders only this <input>
// (not the whole chat screen). It stays in sync with the Page-owned draft store, so clearing after send,
// editing a message, or a shared-in draft still show up here.
function ComposerTextInput({ inputRef, draftStore, onTextChange, bodyRef, extraProps, color }: {
  inputRef: React.RefObject<HTMLInputElement | null>
  draftStore: { ref: { current: string }; subs: Set<(v: string) => void> }
  onTextChange: (v: string) => void
  bodyRef: React.RefObject<HTMLDivElement | null>
  extraProps: Record<string, any>
  color?: string
}) {
  const [value, setValue] = useState<string>(() => draftStore.ref.current)
  useEffect(() => {
    const f = (v: string) => setValue((cur) => (cur === v ? cur : v))
    draftStore.subs.add(f); f(draftStore.ref.current)
    return () => { draftStore.subs.delete(f) }
  }, [draftStore])
  return (
    <input
      ref={inputRef}
      {...extraProps}
      className="composer-text"
      value={value}
      onChange={(e) => {
        const v = e.target.value
        setValue(v) // show the character immediately
        onTextChange(v)
      }}
      onFocus={() => {
        // Keyboard is opening: its resize handler already keeps the list pinned to the bottom. One instant
        // (non-smooth) nudge afterwards - a smooth scroll here fights the keyboard animation and janks typing.
        setTimeout(() => {
          const b = bodyRef.current
          if (b) b.scrollTop = b.scrollHeight
        }, 220)
      }}
      placeholder="Message…"
      maxLength={1000}
      style={color ? { color } : undefined}
    />
  )
}

function ChatView({
  chat, messages, draftStore, setMessage, onBack, onSend, onSendImage, onSendVoice, onMarkViewed, onSendSticker, noAnimateMsgIdsRef,
  savedStickers, stickerLimit, onStickerLimit, onAddSticker, onSaveSticker, onRemoveSticker,
  isTypingPartner, isOnlinePartner, lastSeenText, groupOnlineCount = 0, currentUserId, groupMemberProfiles, onMention, mentionCandidates = [], onSendPoll,
  chatBackground, onChangeBackground, isPremium, unlockedChatThemes, themeShop,
  replyTarget, onCancelReply, onReply, onForward, onReact, onDeleteForMe, onUnsend, onReport,
  onEditMsg, editingMsg, onCancelEdit, onViewProfile, onGroupInfo, onOpenSharedPost, onStartCall, onStartLudo, onJoinLudo, onStartChess, onJoinChess, onStartCarrom, onJoinCarrom, notify,
  dmGate, dmBusy, onDmAccept, onDmDelete, onDmBlock, loading = false, hasOlder = false, onLoadOlder
}: any) {
  // Draft text: subscribe to the store owned by Page (Page itself does not re-render while typing).
  // PERF: ChatView must NOT hold the typed text in state - that re-rendered the whole chat (header, every message row
  // mapping, sheets) on every keystroke and made typing lag. The text lives in <ComposerTextInput/> (tiny component);
  // ChatView only tracks a boolean (has text / no text) which flips rarely, and React skips the render when unchanged.
  const [hasText, setHasText] = useState<boolean>(() => draftStore.ref.current.trim().length > 0)
  useEffect(() => {
    const f = (v: string) => setHasText(v.trim().length > 0)
    draftStore.subs.add(f); f(draftStore.ref.current)
    return () => { draftStore.subs.delete(f) }
  }, [draftStore])
  // @mention suggestions: while the draft ends in "@abc", offer matching people; tapping one completes the handle.
  const [mentionQ, setMentionQ] = useState<string | null>(null)
  useEffect(() => {
    const f = (v: string) => {
      const m = /(?:^|\s)@([a-zA-Z0-9_.]{0,30})$/.exec(v)
      setMentionQ(m ? m[1].toLowerCase() : null)
    }
    draftStore.subs.add(f); f(draftStore.ref.current)
    return () => { draftStore.subs.delete(f) }
  }, [draftStore])
  const mentionMatches = useMemo(() => {
    if (mentionQ === null) return [] as any[]
    return (mentionCandidates as any[]).filter((c) => c.username.toLowerCase().startsWith(mentionQ) || String(c.name || '').toLowerCase().startsWith(mentionQ)).slice(0, 6)
  }, [mentionQ, mentionCandidates])
  const pickMention = (username: string) => {
    const cur = draftStore.ref.current
    const next = cur.replace(/@([a-zA-Z0-9_.]{0,30})$/, `@${username} `)
    setMessage(next)
    setTimeout(() => inputRef.current?.focus(), 0)
  }
  const [headerMenu, setHeaderMenu] = useState(false)
  const bodyRef = useRef<HTMLDivElement>(null)
  const chatContainerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  // Which message ids have already been rendered once in THIS chat - only ids that show up
  // for the first time after that get the "pop in" entrance animation, so a message you just
  // sent (or just received) visibly emerges near the input bar instead of snapping into place,
  // while scrolling through history or reopening a chat never replays it for old bubbles.
  const seenMsgIdsRef = useRef<Set<string>>(new Set())
  const newMsgIdsRef = useRef<Set<string>>(new Set())
  const prevChatIdRef = useRef<string | null | undefined>(undefined)
  const loadedForRef = useRef<string | null>(null)
  const maxSeenAtRef = useRef(0) // newest message time already shown (tells a NEW message from older history added on top)
  const olderAnchorRef = useRef<{ h: number; t: number; at: number } | null>(null)
  if (chat?.id !== prevChatIdRef.current) {
    // First render of this chat (mount, or switched conversations): treat everything
    // already here as "old" so opening a chat doesn't animate its whole history in.
    seenMsgIdsRef.current = new Set(messages.map((m: Message) => m.id))
    maxSeenAtRef.current = (messages as Message[]).reduce((mx, m) => (m.at && m.at > mx ? m.at : mx), 0)
    newMsgIdsRef.current = new Set()
    prevChatIdRef.current = chat?.id ?? null
    loadedForRef.current = loading ? null : (chat?.id ?? null)
  } else if (!loading && loadedForRef.current !== (chat?.id ?? null)) {
    // The history of this chat just finished loading: it is "old" too (no pop-in for the whole list).
    seenMsgIdsRef.current = new Set(messages.map((m: Message) => m.id))
    maxSeenAtRef.current = (messages as Message[]).reduce((mx, m) => (m.at && m.at > mx ? m.at : mx), 0)
    newMsgIdsRef.current = new Set()
    loadedForRef.current = chat?.id ?? null
  } else {
    const fresh = new Set<string>()
    const prevMax = maxSeenAtRef.current
    let nextMax = prevMax
    for (const m of messages as Message[]) {
      if (m.at && m.at > nextMax) nextMax = m.at
      if (!seenMsgIdsRef.current.has(m.id)) {
        seenMsgIdsRef.current.add(m.id)
        // The optimistic "pending-..." bubble for this same message already played the
        // pop-in animation - this is just its real id taking over, so don't replay it.
        if (noAnimateMsgIdsRef?.current?.has(m.id)) {
          noAnimateMsgIdsRef.current.delete(m.id)
        } else if (!m.at || !prevMax || m.at >= prevMax - 1500) {
          fresh.add(m.id)
        } // else: OLDER history that "Earlier messages" just added above - no pop-in for it
      }
    }
    maxSeenAtRef.current = nextMax
    newMsgIdsRef.current = fresh
  }
  // Was the message box focused (keyboard open) the moment Send was pressed?
  const keyboardWasOpenRef = useRef(false)
  // Sending must not close the keyboard (like other chat apps) - it only closes when the user hides it themselves.
  const submitKeepingKeyboard = (e: FormEvent) => {
    const keepKeyboard = keyboardWasOpenRef.current || document.activeElement === inputRef.current
    keyboardWasOpenRef.current = false
    onSend(e)
    if (keepKeyboard) inputRef.current?.focus({ preventScroll: true })
  }
  // Chrome on Android adds a passwords / cards / addresses shortcut bar above the keyboard for normal text boxes,
  // but not for search-type ones. So on Android the message box is search-type (same look, keyboard stays clean).
  // The other props keep the keyboard behaving as before (arrow key, auto-capital).
  const androidInputProps = typeof navigator !== 'undefined' && /Android/i.test(navigator.userAgent)
    ? ({ type: 'search', autoComplete: 'off', enterKeyHint: 'go', autoCapitalize: 'sentences' } as const)
    : {}
  const galleryInputRef = useRef<HTMLInputElement>(null)
  const cameraInputRef = useRef<HTMLInputElement>(null)
  const [selectedMsg, setSelectedMsg] = useState<Message | null>(null)
  // Tapping a message's reaction pill opens a "who reacted" list, grouped by emoji.
  const [reactionListMsg, setReactionListMsg] = useState<Message | null>(null)

  // Customizable quick-reactions: any of the default emojis can be swapped for one the person picks from
  // their own keyboard's emoji picker. The choice is saved so it's remembered next time.
  const [quickEmojis, setQuickEmojis] = useState<string[]>(() => {
    try {
      const saved = typeof window !== 'undefined' ? window.localStorage.getItem('quickReactionEmojis') : null
      if (saved) {
        const parsed = JSON.parse(saved)
        if (Array.isArray(parsed) && parsed.length === QUICK_EMOJIS.length && parsed.every((e) => typeof e === 'string' && e)) {
          return parsed
        }
      }
    } catch {}
    return QUICK_EMOJIS
  })
  const [customizingReactions, setCustomizingReactions] = useState(false)
  const [customizeSlot, setCustomizeSlot] = useState<number | null>(null)
  const emojiPickerInputRef = useRef<HTMLInputElement>(null)
  const openEmojiPickerForSlot = (idx: number) => {
    setCustomizeSlot(idx)
    // A plain text input with no visible caret UI - focusing it is what pops the phone's own emoji keyboard.
    requestAnimationFrame(() => emojiPickerInputRef.current?.focus())
  }
  useEffect(() => { if (!selectedMsg) { setCustomizingReactions(false); setCustomizeSlot(null) } }, [selectedMsg])
  const applyPickedEmoji = (raw: string) => {
    const picked = Array.from(raw).find((ch) => /\p{Extended_Pictographic}/u.test(ch))
    if (picked && customizeSlot !== null) {
      setQuickEmojis((prev) => {
        const next = [...prev]
        next[customizeSlot] = picked
        try { window.localStorage.setItem('quickReactionEmojis', JSON.stringify(next)) } catch {}
        return next
      })
    }
    setCustomizeSlot(null)
    if (emojiPickerInputRef.current) { emojiPickerInputRef.current.value = ''; emojiPickerInputRef.current.blur() }
  }

  const [showBgPicker, setShowBgPicker] = useState(false)
  const [pollOpen, setPollOpen] = useState(false)
  const [voiceOpen, setVoiceOpen] = useState(false)
  const [showStickerSheet, setShowStickerSheet] = useState(false)
  const [sendingImage, setSendingImage] = useState(false)
  // Photo picked from the gallery, waiting in the preview sheet ("View once" switch + Send).
  const [pendingPhoto, setPendingPhoto] = useState<{ file: File; url: string } | null>(null)
  const [photoViewOnce, setPhotoViewOnce] = useState(false)
  // Full-screen photo. `once` is set for a view-once photo: it is marked as viewed when it has loaded.
  const [viewer, setViewer] = useState<{ url: string; once?: Message } | null>(null)
  const viewedOnceRef = useRef<Set<string>>(new Set())
  const consumeViewOnce = () => {
    const m = viewer?.once
    if (!m || viewedOnceRef.current.has(m.id)) return
    viewedOnceRef.current.add(m.id)
    void onMarkViewed?.(m)
  }
  useEffect(() => {
    const url = pendingPhoto?.url
    return () => { if (url) URL.revokeObjectURL(url) }
  }, [pendingPhoto])
  const pressTimer = useRef<any>(null)
  const activeBg = CHAT_BACKGROUNDS.find((b) => b.id === chatBackground) || CHAT_BACKGROUNDS[0]

  // Hold-to-record voice notes: hold the mic button to record, slide left to cancel,
  // release anywhere else to send. Recorded as small Opus/WebM (same encoder used
  // elsewhere in the app) and uploaded through the normal chat media pipeline.
  const VOICE_CANCEL_PX = 90
  const [recording, setRecording] = useState(false)
  const [recordSecs, setRecordSecs] = useState(0)
  const [cancelHint, setCancelHint] = useState(false)
  const [sendingVoice, setSendingVoice] = useState(false)
  const voiceRecorderRef = useRef<MediaRecorder | null>(null)
  const voiceStreamRef = useRef<MediaStream | null>(null)
  const voiceChunksRef = useRef<BlobPart[]>([])
  const voiceTimerRef = useRef<any>(null)
  const voiceStartXRef = useRef(0)
  const voiceCancelledRef = useRef(false)

  const startVoiceRecording = async (clientX: number) => {
    if (recording || sendingVoice) return
    if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
      notify('Voice notes need microphone access, which is not available here.')
      return
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      const mimeType = typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : '')
      const recorder = mimeType ? new MediaRecorder(stream, { mimeType, audioBitsPerSecond: 64000 }) : new MediaRecorder(stream)
      voiceChunksRef.current = []
      voiceCancelledRef.current = false
      recorder.ondataavailable = (e) => { if (e.data && e.data.size > 0) voiceChunksRef.current.push(e.data) }
      recorder.start()
      voiceRecorderRef.current = recorder
      voiceStreamRef.current = stream
      voiceStartXRef.current = clientX
      setCancelHint(false)
      setRecordSecs(0)
      setRecording(true)
      voiceTimerRef.current = setInterval(() => setRecordSecs((s: number) => s + 1), 1000)
    } catch {
      notify('Could not access the microphone. Check app permissions and try again.')
    }
  }

  const finishVoiceRecording = async (send: boolean) => {
    const recorder = voiceRecorderRef.current
    if (!recorder) return
    if (voiceTimerRef.current) clearInterval(voiceTimerRef.current)
    voiceTimerRef.current = null
    const secs = recordSecs
    const cancelled = !send || voiceCancelledRef.current
    await new Promise<void>((resolve) => {
      recorder.onstop = () => resolve()
      try { recorder.stop() } catch { resolve() }
    })
    voiceStreamRef.current?.getTracks().forEach((t) => t.stop())
    voiceStreamRef.current = null
    voiceRecorderRef.current = null
    setRecording(false)
    setCancelHint(false)
    const chunks = voiceChunksRef.current
    voiceChunksRef.current = []
    if (!cancelled && secs >= 1 && chunks.length && onSendVoice) {
      const blob = new Blob(chunks, { type: 'audio/webm' })
      setSendingVoice(true)
      try { await onSendVoice(blob, secs) } finally { setSendingVoice(false) }
    }
  }

  const handleVoicePointerMove = (clientX: number) => {
    if (!recording) return
    const dx = clientX - voiceStartXRef.current
    if (dx < -VOICE_CANCEL_PX) {
      voiceCancelledRef.current = true
      void finishVoiceRecording(false)
    } else {
      setCancelHint(dx < -30)
    }
  }

  // Safety net: stop the mic if the chat is closed mid-recording.
  useEffect(() => () => {
    if (voiceTimerRef.current) clearInterval(voiceTimerRef.current)
    voiceStreamRef.current?.getTracks().forEach((t) => t.stop())
  }, [])

  // Touch Swipe State
  const [dragState, setDragState] = useState<{ id: string; dx: number } | null>(null)
  const touchStartX = useRef<number>(0)
  const touchStartY = useRef<number>(0)
  const touchMsgId = useRef<string | null>(null)

  useEffect(() => {
    const vv = window.visualViewport
    if (!vv) return

    // PERF: the keyboard animation fires many resize/scroll events. Doing layout writes + a forced scroll in each one
    // competes with the typing. So: at most one update per frame, skip writes when nothing changed, and only
    // jump to the newest message when the height actually changed (keyboard opened / closed).
    let raf = 0
    let lastH = -1
    let lastTop = -1
    const apply = () => {
      raf = 0
      const el = chatContainerRef.current
      const h = Math.round(vv.height)
      const top = Math.round(vv.offsetTop)
      if (el) {
        if (h !== lastH) el.style.height = `${h}px`
        if (top !== lastTop) el.style.top = `${top}px`
      }
      if (h !== lastH) {
        const b = bodyRef.current
        if (b) b.scrollTop = b.scrollHeight
      }
      lastH = h
      lastTop = top
    }
    const handleResize = () => { if (!raf) raf = requestAnimationFrame(apply) }

    apply()
    vv.addEventListener('resize', handleResize)
    vv.addEventListener('scroll', handleResize)

    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    return () => {
      if (raf) cancelAnimationFrame(raf)
      vv.removeEventListener('resize', handleResize)
      vv.removeEventListener('scroll', handleResize)
      document.body.style.overflow = prevOverflow
    }
  }, [])

  // OPEN AT THE END: a chat always opens (or re-opens after going back) directly at the newest message. Photos, stickers
  // and replies load a moment AFTER the first paint and push the content down, which used to leave the chat parked near the top.
  // So for the first ~2.5 s the list keeps itself pinned to the bottom whenever its content grows - until the person touches /
  // scrolls it themselves, then it never fights them.
  useLayoutEffect(() => {
    const el = bodyRef.current
    if (!el) return
    let pinned = true
    const toEnd = () => { if (pinned) el.scrollTop = el.scrollHeight }
    const release = () => { pinned = false }
    toEnd()
    const raf1 = requestAnimationFrame(() => { toEnd(); requestAnimationFrame(toEnd) })
    const mo = new MutationObserver(toEnd)
    mo.observe(el, { childList: true, subtree: true })
    el.addEventListener('load', toEnd, true) // <img> load events do not bubble - capture them
    el.addEventListener('touchstart', release, { passive: true })
    el.addEventListener('wheel', release, { passive: true })
    el.addEventListener('pointerdown', release, { passive: true })
    const stop = window.setTimeout(release, 2500)
    return () => {
      cancelAnimationFrame(raf1)
      window.clearTimeout(stop)
      mo.disconnect()
      el.removeEventListener('load', toEnd, true)
      el.removeEventListener('touchstart', release)
      el.removeEventListener('wheel', release)
      el.removeEventListener('pointerdown', release)
    }
  }, [chat?.id])

  // Stay glued to the newest message only if the reader is already at the bottom (or sent it themselves).
  // Otherwise (reading older messages) don't yank them down: show a "latest" button with a new-message count.
  const nearBottomRef = useRef(true)
  const prevLenRef = useRef(0)
  const lastIdRef = useRef('')
  const [jump, setJump] = useState({ show: false, count: 0 })
  useEffect(() => {
    const len = messages.length
    const last = messages[len - 1]
    const prevLen = prevLenRef.current
    const sameTail = !!last && lastIdRef.current === last.id
    prevLenRef.current = len
    lastIdRef.current = last?.id || ''
    const el = bodyRef.current
    // "Earlier messages" was just tapped and older ones were added on top: keep the reader exactly where they were.
    const anchor = olderAnchorRef.current
    if (anchor && el && sameTail && len > prevLen && Date.now() - anchor.at < 15000) {
      olderAnchorRef.current = null
      el.scrollTop = el.scrollHeight - anchor.h + anchor.t
      return
    }
    if (prevLen === 0 || sameTail || !last || last.own || nearBottomRef.current) {
      // A message that just ARRIVED glides into view; opening a chat / loading history still jumps instantly.
      const arrived = prevLen > 0 && !sameTail && !!last
      const calm = typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
      el?.scrollTo({ top: el.scrollHeight, behavior: arrived && !calm ? 'smooth' : 'auto' })
      setJump((j) => (j.show || j.count ? { show: false, count: 0 } : j))
    } else {
      const added = Math.max(1, len - prevLen)
      setJump((j) => ({ show: true, count: j.count + added }))
    }
  }, [messages.length])
  const onBodyScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget
    const dist = el.scrollHeight - el.scrollTop - el.clientHeight
    nearBottomRef.current = dist < 140
    if (dist < 140) setJump((j) => (j.show || j.count ? { show: false, count: 0 } : j))
    else if (dist > 520) setJump((j) => (j.show ? j : { ...j, show: true }))
  }
  const jumpToLatest = () => {
    const el = bodyRef.current
    el?.scrollTo({ top: el.scrollHeight, behavior: 'smooth' })
    setJump({ show: false, count: 0 })
  }

  const startHold = (msg: Message) => {
    pressTimer.current = setTimeout(() => { setSelectedMsg(msg) }, 450)
  }
  const cancelHold = () => {
    if (pressTimer.current) clearTimeout(pressTimer.current)
  }

  // Double-tap a message = quick ❤️ reaction (only adds it; long-press menu still toggles/changes).
  const lastMsgTap = useRef<{ id: string; t: number }>({ id: '', t: 0 })
  const [heartPopId, setHeartPopId] = useState<string | null>(null)
  const handleBubbleTap = (msg: Message) => {
    if (msg.deleted) return
    const now = Date.now()
    const last = lastMsgTap.current
    if (last.id === msg.id && now - last.t < 300) {
      lastMsgTap.current = { id: '', t: 0 }
      if (!(msg.reactions?.['❤️'] || []).includes(currentUserId)) onReact(msg, '❤️')
      setHeartPopId(msg.id)
      window.setTimeout(() => setHeartPopId((c) => (c === msg.id ? null : c)), 700)
      if (typeof navigator !== 'undefined' && navigator.vibrate) { try { navigator.vibrate(15) } catch {} }
    } else {
      lastMsgTap.current = { id: msg.id, t: now }
    }
  }

  const handleTouchStart = (msg: Message, e: React.TouchEvent) => {
    startHold(msg)
    touchStartX.current = e.touches[0].clientX
    touchStartY.current = e.touches[0].clientY
    touchMsgId.current = msg.id
  }

  const handleTouchMove = (msg: Message, e: React.TouchEvent) => {
    if (touchMsgId.current !== msg.id) return
    const dx = e.touches[0].clientX - touchStartX.current
    const dy = e.touches[0].clientY - touchStartY.current
    if (dx > 8 && Math.abs(dx) > Math.abs(dy)) {
      cancelHold()
      setDragState({ id: msg.id, dx: Math.min(dx, 75) })
    }
  }

  const handleTouchEnd = (msg: Message) => {
    cancelHold()
    if (dragState?.id === msg.id && dragState.dx > 40 && !msg.deleted) {
      onReply(msg)
      if (typeof navigator !== 'undefined' && navigator.vibrate) {
        try { navigator.vibrate(20) } catch {}
      }
    }
    setDragState(null)
    touchMsgId.current = null
  }

  const messageById = useMemo(() => new Map<string, Message>(messages.map((m: Message) => [m.id, m])), [messages])
  const lastOwnId = useMemo(() => { for (let i = messages.length - 1; i >= 0; i--) if (messages[i].own) return messages[i].id; return '' }, [messages])
  // GUILD "seen by" (Instagram style): every member's small profile picture sits under the NEWEST message they have seen.
  const groupSeenByMsg = useMemo(() => {
    const out = new Map<string, { uid: string; name: string; avatar: string }[]>()
    if (chat.kind !== 'group') return out
    const memberSet = new Set<string>(chat.members || [])
    const lastIdx = new Map<string, number>()
    messages.forEach((m: Message, i: number) => {
      for (const u of m.seenBy || []) if (u !== currentUserId && u !== m.senderId && memberSet.has(u)) lastIdx.set(u, i)
    })
    lastIdx.forEach((i, u) => {
      const id = messages[i].id
      const prof = (groupMemberProfiles || []).find((p: Profile) => p.uid === u)
      const list = out.get(id) || []
      list.push({ uid: u, name: prof?.name || 'Member', avatar: prof?.avatar || '' })
      out.set(id, list)
    })
    return out
  }, [messages, chat.kind, chat.members, currentUserId, groupMemberProfiles])
  const rowApi = useRef<ChatRowApi>(null as unknown as ChatRowApi)
  // Tap a reply quote -> scroll to the original message and flash it (like WhatsApp / Instagram).
  const jumpToMessage = (id?: string) => {
    if (!id) return
    const root = bodyRef.current
    const el = root?.querySelector<HTMLElement>(`[data-mid="${id.replace(/"/g, '')}"]`)
    if (!el) { notify('Original message is not loaded.'); return }
    el.scrollIntoView({ block: 'center', behavior: 'smooth' })
    el.classList.remove('msg-flash'); void el.offsetWidth; el.classList.add('msg-flash')
    window.setTimeout(() => el.classList.remove('msg-flash'), 1700)
    if (typeof navigator !== 'undefined' && navigator.vibrate) { try { navigator.vibrate(10) } catch {} }
  }
  rowApi.current = { handleTouchStart, handleTouchMove, handleTouchEnd, handleBubbleTap, setSelectedMsg, setViewer, setReactionListMsg, onOpenSharedPost, onJoinLudo, onJoinChess, onJoinCarrom, jumpToMessage, onMention, chatId: chat.kind === 'group' ? chat.id : [currentUserId, chat.id].sort().join('_') }

  // Header bar + composer (input box) follow the active chat wallpaper. Every wallpaper has
  // a matching strip cut from the SAME design - /chat-bgs/header/<id>.svg and
  // /chat-bgs/composer/<id>.svg (same scale, so the pattern lines up with the chat area) - so
  // the top bar, the messages and the message box read as one surface. Text/icon colors
  // switch by wallpaper group (dark/light) so they always stay readable.
  const [autoA, autoB] = BG_THEME_GLOW[activeBg.id] || BG_THEME_GLOW.none
  const headerThemed = activeBg.id !== 'none' && Boolean(activeBg.url)
  const chromeDark = activeBg.group === 'dark'
  const chromeVars = (chromeDark
    ? { '--ink': '#f5f7ff', '--ink-2': '#dde2f0', '--muted': '#a9b1c6', '--line': 'rgba(255,255,255,.16)', '--soft': 'rgba(255,255,255,.10)', '--soft-2': 'rgba(255,255,255,.16)', '--paper': '#0b0d16', '--glass-sheet': '#161926', '--ink-3': '#a9b1c6', '--line-strong': 'rgba(255,255,255,.28)' }
    : { '--ink': '#14161f', '--ink-2': '#2b2f3d', '--muted': '#5d6475', '--line': 'rgba(0,0,0,.10)', '--soft': 'rgba(255,255,255,.70)', '--soft-2': 'rgba(255,255,255,.88)', '--paper': '#ffffff', '--glass-sheet': '#ffffff', '--ink-3': '#5d6475', '--line-strong': 'rgba(0,0,0,.18)' }) as React.CSSProperties
  const chromeEdge = chromeDark ? 'rgba(255,255,255,.14)' : 'rgba(0,0,0,.08)'
  const chromeBase = chromeDark ? '#0b0d16' : '#f4f6fb'
  const chromeGlass = chromeDark ? 'rgba(255,255,255,.12)' : 'rgba(255,255,255,.72)'
  const chromeImage = (folder: 'header' | 'composer') => ({
    // Semi-transparent base (was a flat opaque color) so the header/composer read as
    // glass panels sitting over the wallpaper - matching the same glassy treatment
    // used on the home screens (topbar, cards, nav) - instead of a solid opaque bar.
    backgroundColor: 'transparent',
    backgroundImage: `url(${activeBg.url.replace('/chat-bgs/', `/chat-bgs/${folder}/`)})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center bottom',
    backgroundRepeat: 'no-repeat',
  } as React.CSSProperties)
  // The header strip is cut from the wallpaper, so its pattern used to show through. A solid scrim is layered ON TOP of
  // the strip (first background layer) + a blur, so it reads clean on EVERY wallpaper, dark or light.
  const headerScrim = chromeDark ? 'rgba(11,13,22,.92)' : 'rgba(244,246,251,.92)'
  const headerThemeStyle: React.CSSProperties = headerThemed ? {
    ...chromeImage('header'),
    backgroundImage: `linear-gradient(${headerScrim}, ${headerScrim}), ${chromeImage('header').backgroundImage}`,
    backgroundSize: 'auto, cover',
    backgroundPosition: 'center, center bottom',
    backgroundRepeat: 'no-repeat, no-repeat',
    WebkitBackdropFilter: 'blur(16px)',
    backdropFilter: 'blur(16px)',
    borderBottom: "0",
    boxShadow: "none",
    ...chromeVars,
  } : {}
  const headerBtnStyle: React.CSSProperties = headerThemed ? {
    border: `1px solid ${chromeEdge}`,
    background: chromeGlass,
    color: chromeDark ? '#f5f7ff' : '#14161f',
  } : {}
  // No bar/card behind the composer any more: the form is transparent and only the slim pill
  // inside it (see .composer-pill in globals.css) has a background.
  // Phone status bar (clock, battery, signal): dark icons on a light wallpaper, light icons on a dark one.
  // Without this they stay white and vanish on light chat themes. Restored when the chat closes.
  useEffect(() => {
    if (typeof document === 'undefined') return
    const meta = document.querySelector('meta[name="theme-color"]') as HTMLMetaElement | null
    const prevColor = meta?.getAttribute('content') || null
    const appIsDark = () => !!document.querySelector('.phone-frame.theme-dark, body.theme-dark')
    const setBar = (dark: boolean) => {
      try { (window as any).Capacitor?.Plugins?.StatusBar?.setStyle?.({ style: dark ? 'DARK' : 'LIGHT' }) } catch { /* plain browser */ }
    }
    if (headerThemed) {
      meta?.setAttribute('content', chromeBase)
      setBar(chromeDark)
    }
    return () => {
      if (prevColor && meta) meta.setAttribute('content', prevColor)
      setBar(appIsDark())
    }
  }, [headerThemed, chromeDark, chromeBase])
  const composerThemeStyle: React.CSSProperties = headerThemed ? {
    ...chromeVars,
  } : {}
  const composerInputStyle: React.CSSProperties = headerThemed ? {
    background: chromeGlass,
    borderColor: chromeEdge,
    color: chromeDark ? '#f5f7ff' : '#14161f',
  } : {}
  const composerSendStyle: React.CSSProperties = headerThemed ? {
    background: `linear-gradient(135deg, ${autoA}, ${autoB})`,
    color: '#fff',
  } : {}

  // Received (other person's) bubble color: automatically tinted to match whichever
  // wallpaper is active - no more flat black card on every dark theme.
  const otherBubbleAuto: React.CSSProperties = headerThemed ? {
    '--other-bubble-bg': chromeDark
      ? `linear-gradient(135deg, ${autoA}40, ${autoB}26), rgba(17,19,27,0.88)`
      : `linear-gradient(135deg, ${autoA}24, ${autoB}18), rgba(255,255,255,0.92)`,
    '--other-bubble-text': chromeDark ? '#f3f5f9' : '#14161f',
    '--other-bubble-border': chromeDark ? `${autoA}55` : `${autoA}3d`,
  } as React.CSSProperties : {}
  // Message colors are no longer user-pickable: they always follow the active chat theme, and the text color
  // is computed for readable contrast (WCAG) against the actual bubble colors.
  const ownBubbleAuto: React.CSSProperties = (() => {
    let [a, b] = headerThemed ? [autoA, autoB] : ['#ff4d6d', '#b84dff']
    let text = readableTextOnBoth(a, b)
    // Light (white) wallpapers use pastel accents: the contrast helper then picked BLACK text on the sent bubbles.
    // Deepen the pastel gradient a little and keep the text white so sent messages look the same as on the other themes.
    if (headerThemed && !chromeDark && /^#[0-9a-fA-F]{6}$/.test(a) && /^#[0-9a-fA-F]{6}$/.test(b)) {
      const deepen = (hex: string) => {
        const n = parseInt(hex.slice(1), 16)
        const ch = (v: number) => Math.round(v * 0.58).toString(16).padStart(2, '0')
        return `#${ch((n >> 16) & 255)}${ch((n >> 8) & 255)}${ch(n & 255)}`
      }
      a = deepen(a); b = deepen(b); text = '#ffffff'
    }
    return {
      '--own-bubble-bg': `linear-gradient(135deg, ${a} 0%, ${b} 100%)`,
      '--own-bubble-text': text,
    } as React.CSSProperties
  })()
  // "Seen" ticks/badges follow the active theme's accent color too (falls back to the
  // default saffron when no wallpaper is set).
  const themeAccentVars: React.CSSProperties = headerThemed ? { '--saffron': autoA } as React.CSSProperties : {}

  // Guild header: always says how many people are in it and how many of them are online right now.
  const guildMemberCount = chat.kind === 'group' ? (chat.members || []).length : 0
  const guildSubtitle = `${guildMemberCount} ${guildMemberCount === 1 ? 'member' : 'members'}${groupOnlineCount > 0 ? ` · ${groupOnlineCount} online` : ''}`
  const subtitle = isTypingPartner 
    ? 'typing...' 
    : chat.kind === 'group'
    ? guildSubtitle
    : isOnlinePartner 
    ? '● Online' 
    : lastSeenText 
    ? `Last seen ${lastSeenText}` 
    // A friend who hid their online status writes no presence at all - saying "Offline" would be wrong (they may be
    // chatting with you right now), so a direct chat just shows nothing there.
    : chat.kind === 'group' ? 'Offline' : ''

  return (
    <div
      ref={chatContainerRef}
      className="chat-view"
      style={{
        position: 'fixed',
        left: 0,
        right: 0,
        top: 0,
        width: '100%',
        maxWidth: 520,
        margin: '0 auto',
        height: '100dvh',
        zIndex: 80,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        overscrollBehavior: 'none',
        background: 'var(--bg)',
        // The wallpaper now sits behind everything (header, messages, composer) so there is no
        // black strip / card around the input box. Colour tokens flip with the wallpaper so every
        // child (reaction chips, reply bar, request bars, Seen, composer) stays readable.
        ...(headerThemed ? {
          ...chromeVars,
          backgroundColor: chromeBase,
          backgroundImage: `url(${activeBg.url})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
        } : {}),
      }}
    >
      {/* 1. Header */}
      <header className="chat-topbar chat-topbar-tight" style={{ position: 'relative', zIndex: 10, transition: 'background .3s ease, border-color .3s ease', ...headerThemeStyle }}>
        <button className="icon-button" style={headerBtnStyle} onClick={onBack} aria-label="Back"><ArrowLeft size={18} /></button>
        <button
          className="chat-title-button"
          onClick={chat.kind === 'group' ? onGroupInfo : onViewProfile}
        >
          <Avatar src={chat.avatar} name={chat.name} size="sm" uid={chat.kind === 'group' ? undefined : chat.id} online={isOnlinePartner} showStatus={true} />
          <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
            <strong style={{ color: 'var(--ink)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{chat.name}</strong>
            {/* nowrap: "Last seen 11:09 AM" must stay on ONE line ("AM" used to drop under the time) */}
            <small className="chat-sub" style={{ ['--sub-color' as any]: isTypingPartner ? (headerThemed ? (chromeDark ? '#f5f7ff' : '#14161f') : 'var(--cyan)') : isOnlinePartner ? (headerThemed ? (chromeDark ? '#34d399' : '#047857') : 'var(--online-green)') : 'var(--muted)', fontWeight: 700 }}>
              {isTypingPartner ? <>typing<span className="typing-dots" aria-hidden="true"><i /><i /><i /></span></> : subtitle}
            </small>
          </div>
        </button>
        {onStartCall && (
          <>
            <button className="icon-button" style={{ marginLeft: 'auto', ...headerBtnStyle, minWidth: 44, minHeight: 44 }} onClick={() => onStartCall('voice')} aria-label="Voice call" title="Voice call">
              <Phone size={24} />
            </button>
            <button className="icon-button" style={{ ...headerBtnStyle, minWidth: 44, minHeight: 44 }} onClick={() => onStartCall('video')} aria-label="Video call" title="Video call">
              <Video size={26} />
            </button>
          </>
        )}
        <div style={{ position: 'relative', marginLeft: onStartCall ? 0 : 'auto' }}>
          <button className="icon-button" style={headerBtnStyle} onClick={() => setHeaderMenu((v: boolean) => !v)} aria-label="More" aria-expanded={headerMenu} title="More">
            <MoreHorizontal size={18} />
          </button>
          {headerMenu && (
            <>
              {/* Transparent tap-catcher: the old dark dim lived inside the header's stacking context, so it greyed/blackened the header. */}
              <div onClick={() => setHeaderMenu(false)} style={{ position: 'fixed', inset: 0, zIndex: 40, background: 'transparent' }} />
              <div className="chat-more-menu" role="menu">
                {onStartLudo && (
                  <button type="button" role="menuitem" onClick={() => { setHeaderMenu(false); onStartLudo() }}><Dices size={18} /> Play Ludo</button>
                )}
                {onStartChess && (
                  <button type="button" role="menuitem" onClick={() => { setHeaderMenu(false); onStartChess() }}><Crown size={18} /> Play Chess</button>
                )}
                {onStartCarrom && (
                  <button type="button" role="menuitem" onClick={() => { setHeaderMenu(false); onStartCarrom() }}><CircleDot size={18} /> Play Carrom</button>
                )}
                {onSendPoll && !dmGate && (
                  <button type="button" role="menuitem" onClick={() => { setHeaderMenu(false); setPollOpen(true) }}><BarChart3 size={18} /> Create poll</button>
                )}
                {chat.kind === 'group' && (
                  <button type="button" role="menuitem" onClick={() => { setHeaderMenu(false); setVoiceOpen(true) }}><Mic size={18} /> Voice room</button>
                )}
                <button type="button" role="menuitem" onClick={() => { setHeaderMenu(false); setShowBgPicker(true) }}><Palette size={18} /> Chat theme</button>
              </div>
            </>
          )}
        </div>
      </header>

      {/* 2. Messages List */}
      <div
        className={`message-body ${activeBg.group === 'dark' ? 'wp-dark' : ''}`}
        ref={bodyRef}
        onScroll={onBodyScroll}
        style={{
          flex: '1 1 0%',
          minHeight: 0,
          overflowY: 'auto',
          overflowX: 'hidden',
          overscrollBehavior: 'contain',
          WebkitOverflowScrolling: 'touch',
          ...ownBubbleAuto,
          ...otherBubbleAuto,
          ...themeAccentVars,
          background: 'transparent',
        }}
      >
        {loading && messages.length === 0 && (
          <div aria-busy="true" aria-label="Loading messages" style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '16px 12px' }}>
            <style>{'@keyframes cvSk{0%,100%{opacity:.35}50%{opacity:.7}}'}</style>
            {[['62%', 'flex-start'], ['48%', 'flex-end'], ['70%', 'flex-start'], ['38%', 'flex-end']].map(([w, a], i) => (
              <span key={i} style={{ width: w, height: 36, borderRadius: 16, alignSelf: a as any, background: 'rgba(128,128,128,.28)', animation: `cvSk 1.2s ease-in-out ${i * 0.12}s infinite` }} />
            ))}
          </div>
        )}
        {hasOlder && messages.length > 0 && (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '8px 0 4px' }}>
            <button
              type="button"
              className="text-button"
              onClick={() => {
                const el = bodyRef.current
                if (el) olderAnchorRef.current = { h: el.scrollHeight, t: el.scrollTop, at: Date.now() }
                onLoadOlder?.()
              }}
              style={{ fontSize: '.78rem', padding: '6px 14px', borderRadius: 999 }}
            >
              Earlier messages
            </button>
          </div>
        )}
        {messages.map((item: Message, i: number) => {
          const prev = i > 0 ? messages[i - 1] : undefined
          const next = i < messages.length - 1 ? messages[i + 1] : undefined
          const sameSender = (a?: Message) => !!a && a.own === item.own && (item.own || (a.senderId || '') === (item.senderId || ''))
          const dayChanged = !!item.at && (!prev?.at || dayLabel(prev.at) !== dayLabel(item.at))
          return (
            <Fragment key={item.id}>
              {dayChanged && <div className="chat-date-sep"><span>{dayLabel(item.at!)}</span></div>}
              <ChatMessageRow
                replyOrig={item.replyToId ? messageById.get(item.replyToId) : undefined}
                item={item}
                chat={chat}
                currentUserId={currentUserId}
                dragDx={dragState?.id === item.id ? dragState.dx : null}
                heartPop={heartPopId === item.id}
                isNew={newMsgIdsRef.current.has(item.id)}
                showSeen={item.id === lastOwnId && chat.kind !== 'group'}
                api={rowApi}
                groupStart={!sameSender(prev) || dayChanged}
                showAvatar={!sameSender(next) || (!!next?.at && !!item.at && dayLabel(next.at) !== dayLabel(item.at))}
              />
              {groupSeenByMsg.has(item.id) && (
                <div className="seen-avatars" aria-label={`Seen by ${groupSeenByMsg.get(item.id)!.map((x) => x.name).join(', ')}`}>
                  {groupSeenByMsg.get(item.id)!.slice(0, 6).map((x) => (
                    <img key={x.uid} src={x.avatar || fallbackAvatarSvg(x.name)} alt="" title={x.name} loading="lazy" decoding="async" draggable={false}
                      onError={(e) => { e.currentTarget.src = fallbackAvatarSvg(x.name) }} />
                  ))}
                  {groupSeenByMsg.get(item.id)!.length > 6 && <span className="seen-avatars-more">+{groupSeenByMsg.get(item.id)!.length - 6}</span>}
                </div>
              )}
            </Fragment>
          )
        })}
        {jump.show && (
          <div className="chat-jump-wrap">
            <button type="button" className="chat-jump" onClick={jumpToLatest} aria-label={jump.count ? `${jump.count} new messages, jump to latest` : 'Jump to latest message'}>
              <ChevronDown size={18} strokeWidth={2.6} />
              {jump.count > 0 && <span className="chat-jump-count">{jump.count > 99 ? '99+' : jump.count}</span>}
            </button>
          </div>
        )}
      </div>

      {/* Message request bar: recipient decides; sender waits */}
      {dmGate === 'incoming' && (
        <div style={{ flex: '0 0 auto', padding: '0.75rem 1rem', background: 'var(--soft)', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)', display: 'grid', gap: '0.5rem', position: 'relative', zIndex: 10 }}>
          <p style={{ margin: 0, fontSize: '.8rem', color: 'var(--ink-2)', textAlign: 'center' }}>
            <strong style={{ color: 'var(--ink)' }}>{chat?.name}</strong> wants to message you. They will not see that you read this until you accept.
          </p>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button type="button" className="text-button primary" disabled={dmBusy} onClick={onDmAccept} style={{ flex: 1, justifyContent: 'center' }}>Accept</button>
            <button type="button" className="text-button" disabled={dmBusy} onClick={onDmDelete} style={{ flex: 1, justifyContent: 'center' }}>Delete</button>
            <button type="button" className="text-button" disabled={dmBusy} onClick={onDmBlock} style={{ flex: 1, justifyContent: 'center', color: '#ff6b81' }}>Block</button>
          </div>
        </div>
      )}
      {dmGate === 'waiting' && (
        <div style={{ flex: '0 0 auto', padding: '0.75rem 1rem', background: 'var(--soft)', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)', position: 'relative', zIndex: 10 }}>
          <p style={{ margin: 0, fontSize: '.8rem', color: 'var(--muted)', textAlign: 'center' }}>
            Message request sent. You can keep chatting once {chat?.name} accepts.
          </p>
        </div>
      )}

      {/* Replying / Editing Preview Bar */}
      {(replyTarget || editingMsg) && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.5rem 1rem', background: 'var(--soft)', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)', }}>
          <div style={{ minWidth: 0, flex: 1 }}>
            <small style={{ color: 'var(--cyan)', fontWeight: 700 }}>{editingMsg ? 'Editing message' : `Replying to ${replyTarget?.own ? 'Yourself' : replyTarget?.senderName}`}</small>
            <span style={{ fontSize: '.78rem', color: 'var(--ink)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>
              {editingMsg ? editingMsg.text : (replyTarget ? <IconText text={replyPreviewOf(replyTarget).text} /> : '')}
            </span>
          </div>
          {!editingMsg && replyTarget && replyPreviewOf(replyTarget).image && (
            <img src={replyPreviewOf(replyTarget).image} alt="" draggable={false} style={{ width: 38, height: 38, objectFit: 'contain', borderRadius: 8, marginRight: 8, flexShrink: 0 }} />
          )}
          <button className="icon-button" style={{ width: 26, height: 26, border: 0 }} onClick={editingMsg ? onCancelEdit : onCancelReply}><X size={16} /></button>
        </div>
      )}

      {/* 3. Composer: one slim pill - camera | message | mic/send | photo | sticker */}
      <form className={`composer${headerThemed ? (chromeDark ? ' composer-on-dark' : ' composer-on-light') : ''}`} style={{ flex: '0 0 auto', position: 'relative', zIndex: 10, ...composerThemeStyle, ...(dmGate ? { display: 'none' } : {}) }} onSubmit={submitKeepingKeyboard}>
        {mentionMatches.length > 0 && !dmGate && (
          <div className="mention-pop">
            {mentionMatches.map((c: any) => (
              <button key={c.uid} type="button" className="mention-item" onMouseDown={(e) => e.preventDefault()} onClick={() => pickMention(c.username)}>
                {c.avatar ? <img decoding="async" src={c.avatar} alt="" draggable={false} /> : <span style={{ width: 30, height: 30 }} />}
                <span><b>{c.name}</b><small>@{c.username}</small></span>
              </button>
            ))}
          </div>
        )}
        <div className="composer-pill" style={headerThemed ? { background: chromeGlass, borderColor: chromeEdge } : undefined}>
          {!recording && (
            <>
              <input
                ref={galleryInputRef}
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={(e) => {
                  const file = e.target.files?.[0]
                  e.target.value = ''
                  if (!file || !onSendImage) return
                  setPhotoViewOnce(false)
                  setPendingPhoto({ file, url: URL.createObjectURL(file) })
                }}
              />
              <input
                ref={cameraInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                style={{ display: 'none' }}
                onChange={(e) => {
                  const file = e.target.files?.[0]
                  e.target.value = ''
                  if (!file || !onSendImage) return
                  setPhotoViewOnce(false)
                  setPendingPhoto({ file, url: URL.createObjectURL(file) })
                }}
              />
              {!hasText && (
                <button
                  type="button"
                  className="composer-camera-btn"
                  aria-label="Take a photo"
                  title="Take a photo"
                  disabled={sendingImage}
                  onClick={() => cameraInputRef.current?.click()}
                >
                  <CameraIcon size={18} />
                </button>
              )}
              <ComposerTextInput
                inputRef={inputRef}
                draftStore={draftStore}
                onTextChange={setMessage}
                bodyRef={bodyRef}
                extraProps={androidInputProps}
                color={headerThemed ? (chromeDark ? '#f5f7ff' : '#14161f') : undefined}
              />
            </>
          )}

          {recording && (
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 12, padding: '0 0.25rem 0 0.5rem', minWidth: 0, userSelect: 'none' }}>
              <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#e0455a', flexShrink: 0, animation: 'voice-rec-pulse 1s ease-in-out infinite' }} />
              <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700, fontSize: '.85rem', flexShrink: 0, minWidth: 36 }}>
                {`${Math.floor(recordSecs / 60)}:${(recordSecs % 60).toString().padStart(2, '0')}`}
              </span>
              <span style={{ flex: 1, textAlign: 'center', fontSize: '.76rem', color: cancelHint ? '#e0455a' : 'var(--muted)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, transition: 'color .15s ease', whiteSpace: 'nowrap' }}>
                <ChevronLeft size={16} /> Slide to cancel
              </span>
            </div>
          )}

          {hasText && !recording ? (
            <button
              type="submit"
              className="composer-send-btn"
              aria-label="Send"
              onPointerDown={() => { keyboardWasOpenRef.current = document.activeElement === inputRef.current }}
              onMouseDown={(e) => e.preventDefault()} // keep focus in the message box so the keyboard stays open
            >
              <Send size={16} />
            </button>
          ) : (
            <button
              type="button"
              className={`composer-mic-btn${recording ? ' is-recording' : ''}`}
              style={{ touchAction: 'none' }}
              aria-label={recording ? 'Release to send, slide left to cancel' : 'Hold to record a voice note'}
              title={recording ? undefined : 'Hold to record a voice note'}
              disabled={sendingVoice}
              onPointerDown={(e) => { e.preventDefault(); try { e.currentTarget.setPointerCapture(e.pointerId) } catch {}; void startVoiceRecording(e.clientX) }}
              onPointerMove={(e) => handleVoicePointerMove(e.clientX)}
              onPointerUp={() => { void finishVoiceRecording(true) }}
              onPointerCancel={() => { void finishVoiceRecording(false) }}
            >
              {sendingVoice ? <RefreshCw size={20} className="spin-icon" /> : <Mic size={20} />}
            </button>
          )}

          {!recording && !hasText && (
            <>
              <button
                type="button"
                className="composer-tool-btn"
                aria-label="Send a photo"
                title="Send a photo"
                disabled={sendingImage}
                onClick={() => galleryInputRef.current?.click()}
              >
                {sendingImage ? <RefreshCw size={20} className="spin-icon" /> : <ImageIcon size={20} />}
              </button>
              <button
                type="button"
                className="composer-tool-btn"
                aria-label="Stickers & GIFs"
                title="Stickers & GIFs"
                onClick={() => setShowStickerSheet(true)}
              >
                <Sticker size={20} />
              </button>
            </>
          )}
        </div>
      </form>

      {showStickerSheet && (
        <StickerPickerSheet
          savedStickers={savedStickers || []}
          stickerLimit={stickerLimit}
          onStickerLimit={onStickerLimit}
          onPick={(url) => onSendSticker?.(url)}
          onAddSticker={onAddSticker}
          onRemoveSticker={onRemoveSticker}
          onClose={() => setShowStickerSheet(false)}
        />
      )}

      {/* Photo preview: choose "View once" or a normal photo, then send */}
      {pendingPhoto && (
        <div className="action-sheet-backdrop" onClick={() => { if (!sendingImage) setPendingPhoto(null) }}>
          <div className="action-sheet vo-sheet" onClick={(e) => e.stopPropagation()}>
            <img decoding="async" className="vo-preview" src={pendingPhoto.url} alt="Selected photo" draggable={false} />
            <button type="button" className={`vo-toggle ${photoViewOnce ? 'on' : ''}`} aria-pressed={photoViewOnce} onClick={() => setPhotoViewOnce((v) => !v)}>
              <span className="vo-icon">1</span>
              <span className="vo-toggle-text">
                <b>View once</b>
                <small>{photoViewOnce ? 'They can open it one time, then it disappears' : 'Off - sent as a normal photo'}</small>
              </span>
              <span className="vo-switch" />
            </button>
            <div className="vo-actions">
              <button type="button" className="text-button" disabled={sendingImage} onClick={() => setPendingPhoto(null)}>Cancel</button>
              <button
                type="button"
                className="text-button primary"
                disabled={sendingImage}
                onClick={async () => {
                  const pending = pendingPhoto
                  setSendingImage(true)
                  try {
                    const ok = await onSendImage(pending.file, { viewOnce: photoViewOnce })
                    if (ok !== false) setPendingPhoto(null)
                  } finally { setSendingImage(false) }
                }}
              >
                {sendingImage ? 'Sending…' : photoViewOnce ? 'Send · view once' : 'Send'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Full-screen photo (a view-once photo is used up as soon as it has loaded here) */}
      {viewer && (
        <div className="vo-viewer" onClick={() => setViewer(null)} onContextMenu={(e) => e.preventDefault()}>
          <div className="vo-viewer-top" onClick={(e) => e.stopPropagation()}>
            <span className="vo-viewer-badge">{viewer.once ? <><span className="vo-icon">1</span> View once</> : 'Photo'}</span>
            <button type="button" className="vo-viewer-close" aria-label="Close" onClick={() => setViewer(null)}><X size={20} /></button>
          </div>
          <img decoding="async"
            className="vo-viewer-img"
            src={viewer.url}
            alt=""
            draggable={false}
            ref={(el) => { if (el && el.complete && el.naturalWidth > 0) consumeViewOnce() }}
            onLoad={consumeViewOnce}
          />
        </div>
      )}

      {/* Long-press Action Sheet */}
      {selectedMsg && (
        <div className="action-sheet-backdrop" onClick={() => setSelectedMsg(null)}>
          <div className="action-sheet" onClick={(e) => e.stopPropagation()}>
            {!selectedMsg.deleted && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-around', paddingBottom: '1rem', marginBottom: '0.75rem' }}>
                {quickEmojis.map((emoji, idx) => (
                  <button
                    type="button"
                    key={idx}
                    className={`react-tray-btn${customizingReactions ? ' is-customizable' : ''}`}
                    onClick={() => {
                      if (customizingReactions) { openEmojiPickerForSlot(idx); return }
                      onReact(selectedMsg, emoji)
                      setSelectedMsg(null)
                    }}
                  >
                    <Emoji3D char={emoji} size="1.9rem" />
                  </button>
                ))}
                <button
                  type="button"
                  className={`react-tray-customize-btn${customizingReactions ? ' is-active' : ''}`}
                  aria-label={customizingReactions ? 'Done customizing reactions' : 'Customize reactions'}
                  onClick={() => setCustomizingReactions((v) => !v)}
                >
                  {customizingReactions ? <Check size={16} /> : <Plus size={16} />}
                </button>
                {/* Invisible text input - focusing it is what opens the phone's own emoji keyboard, so any
                    emoji the person picks there can replace a quick-reaction. Nothing else about it is used. */}
                <input
                  ref={emojiPickerInputRef}
                  type="text"
                  inputMode="text"
                  autoComplete="off"
                  autoCorrect="off"
                  aria-hidden="true"
                  className="emoji-picker-input"
                  onChange={(e) => applyPickedEmoji(e.target.value)}
                  onBlur={() => setCustomizeSlot(null)}
                />
              </div>
            )}
            {customizingReactions && (
              <p style={{ margin: '-.6rem 0 0.75rem', fontSize: '.72rem', color: 'var(--muted)', textAlign: 'center' }}>
                Tap an emoji to replace it with one from your keyboard
              </p>
            )}
            {/* While picking a replacement emoji, hide the rest of the sheet (Reply/Edit/Forward/Copy/Delete/
                Unsend). Keeping them mounted made the sheet tall enough that opening the keyboard pushed the
                emoji row itself off-screen, leaving only these unrelated buttons visible above the keyboard -
                so the person could no longer see or finish the emoji swap they'd started. */}
            {!customizingReactions && (
            <>
            {isStickerMsg(selectedMsg) && <button className="action-sheet-item" onClick={() => { onSaveSticker?.(selectedMsg.sharedImageUrl!); setSelectedMsg(null) }}><Sticker size={16} /> Save sticker</button>}
            {!selectedMsg.deleted && <button className="action-sheet-item" onClick={() => { onReply(selectedMsg); setSelectedMsg(null) }}><Reply size={16} /> Reply</button>}
            {selectedMsg.own && !selectedMsg.deleted && !isStickerMsg(selectedMsg) && !selectedMsg.viewOnce && !selectedMsg.sharedImageUrl && !selectedMsg.audioUrl && !selectedMsg.id.startsWith('pending-') && (selectedMsg.editCount ?? (selectedMsg.edited ? 1 : 0)) < MAX_MSG_EDITS && <button className="action-sheet-item" onClick={() => { onEditMsg(selectedMsg); setSelectedMsg(null) }}><Edit3 size={16} /> Edit Message ({MAX_MSG_EDITS - (selectedMsg.editCount ?? (selectedMsg.edited ? 1 : 0))} left)</button>}
            {!selectedMsg.deleted && !selectedMsg.viewOnce && <button className="action-sheet-item" onClick={() => { onForward(selectedMsg); setSelectedMsg(null) }}><Forward size={16} /> Forward</button>}
            {!selectedMsg.deleted && !isStickerMsg(selectedMsg) && !selectedMsg.viewOnce && <button className="action-sheet-item" onClick={() => { navigator.clipboard?.writeText(selectedMsg.text); setSelectedMsg(null) }}><Copy size={16} /> Copy Text</button>}
            <button className="action-sheet-item danger" onClick={() => { onDeleteForMe(selectedMsg); setSelectedMsg(null) }}><Trash2 size={16} /> Delete for me</button>
            {selectedMsg.own && !selectedMsg.deleted && <button className="action-sheet-item danger" onClick={() => { onUnsend(selectedMsg); setSelectedMsg(null) }}><X size={16} /> Unsend for everyone</button>}
            {!selectedMsg.own && !selectedMsg.deleted && <button className="action-sheet-item danger" onClick={() => { onReport(selectedMsg); setSelectedMsg(null) }}><Flag size={16} /> Report</button>}
            </>
            )}
          </div>
        </div>
      )}

      {pollOpen && onSendPoll && typeof document !== 'undefined' && createPortal(
        <PollComposer onClose={() => setPollOpen(false)} onSend={onSendPoll} />, document.body)}
      {voiceOpen && chat.kind === 'group' && typeof document !== 'undefined' && createPortal(
        <GroupVoiceRoom groupId={chat.id} meUid={currentUserId} members={(groupMemberProfiles || []).map((m: any) => ({ uid: m.uid, name: m.name, avatar: m.avatar }))} onClose={() => setVoiceOpen(false)} />, document.body)}

      {/* Chat Background Picker */}
      {/* Portalled to <body>: inside the chat window it inherited the wallpaper's text/card colours (chromeVars), so on a
          light wallpaper the picker showed dark title text on the dark app background and white cards. */}
      {showBgPicker && typeof document !== 'undefined' && createPortal(
        <ThemePickerModal
          title="Chat Background"
          activeId={activeBg.id}
          onSelect={(id: string) => onChangeBackground?.(id)}
          onClose={() => setShowBgPicker(false)}
          isPremium={isPremium}
          unlockedChatThemes={unlockedChatThemes}
          themeShop={themeShop}
        />,
        document.body
      )}

      {/* "Who reacted" list - opened by tapping a message's reaction pill */}
      {reactionListMsg && (
        <ReactionListModal
          msg={reactionListMsg}
          chat={chat}
          currentUserId={currentUserId}
          groupMemberProfiles={groupMemberProfiles || []}
          onClose={() => setReactionListMsg(null)}
        />
      )}
    </div>
  )
}

/* =========================================================================
   FULL POST PREVIEW MODAL (With Safe Fallbacks & High Z-Index)
   ========================================================================= */
function PostPreviewModal({ post, currentUserId, isPlayingAudio, onToggleAudio, onClose, onLike, onOpenComments, onDelete, onOpenBoost, onEdit, onViewProfile }: any) {

  const isLiked = (post.likes || []).includes(currentUserId)
  const isOwner = post.authorId === currentUserId

  // Stickers / chat photos: just the picture with a close button - no author,
  // no "Transmission Post" title, no likes / comments / views.
  if (post.previewOnly) {
    return (
      <div className="modal-backdrop" style={{ zIndex: 120 }} onClick={onClose}>
        <div className="post-preview-card" onClick={(e) => e.stopPropagation()}>
          <header style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '0.5rem' }}>
            <button className="icon-button" onClick={onClose} aria-label="Close"><X size={16} /></button>
          </header>
          <div style={{ width: '100%', borderRadius: 14, overflow: 'hidden', background: 'var(--soft-2)' }}>
            {post.mediaType === 'video' ? (
              <video preload="metadata" src={post.imageUrl} controls controlsList="nodownload noremoteplayback" disablePictureInPicture onContextMenu={(e) => e.preventDefault()} playsInline loop style={{ width: '100%', maxHeight: 380, display: 'block' }} />
            ) : (
              <img src={post.imageUrl} alt="" style={{ width: '100%', maxHeight: 380, objectFit: 'contain', display: 'block' }} loading="lazy" decoding="async" />
            )}
          </div>
        </div>
      </div>
    )
  }

  const url = post.promoted ? adWebsiteUrl(post) : ''
  const views = post.views || 0

  const media = (
    <div className="pv-media">
      {post.mediaType === 'video' && post.extraImageUrls && post.extraImageUrls.length > 0 ? (
        <PostImageCarousel urls={post.extraImageUrls} fit="contain" firstSlide={<PostPreviewVideo post={post} />} />
      ) : post.mediaType === 'video' ? (
        <PostPreviewVideo post={post} />
      ) : post.imageUrls && post.imageUrls.length > 1 ? (
        <PostImageCarousel urls={post.imageUrls} fit="contain" />
      ) : (
        <img src={post.imageUrl} alt="" loading="lazy" decoding="async" />
      )}
      {post.audioUrl && (
        <button type="button" className="pv-audio-btn" onClick={onToggleAudio} aria-label={isPlayingAudio ? 'Mute song' : 'Play song'}>
          {isPlayingAudio ? <Volume2 size={18} color="var(--cyan)" /> : <VolumeX size={18} color="#fff" />}
        </button>
      )}
    </div>
  )

  // The song's name is intentionally not shown on photo posts (only the speaker button).
  const audioLine = null

  const actions = (
    <div className="pv-actions">
      <button type="button" className={`pv-action ${isLiked ? 'liked' : ''}`} onClick={() => onLike(post)}>
        <Heart size={20} fill={isLiked ? 'currentColor' : 'none'} />
        <span>{formatCount((post.likes || []).length)}</span>
      </button>
      <button type="button" className="pv-action" onClick={onOpenComments}>
        <MessageCircle size={20} />
        <span>{formatCount(post.commentCount || 0)}</span>
      </button>
      <span className="pv-views"><Eye size={16} /> {formatCount(views)} views</span>
    </div>
  )

  // ---- Boosted post: same Sponsored format as the Radar feed ----
  if (post.promoted) {
    // Same as the Radar feed card: only a custom advertiser title shows here, never the
    // bare domain (that's already in the "Promoted Content · domain" footer below).
    const headline = post.title
    return (
      <div className="modal-backdrop pv-backdrop" style={{ zIndex: 120 }} onClick={onClose}>
        <div className="post-preview-card pv-card sp-card" onClick={(e) => e.stopPropagation()}>
          <div className="pv-scroll">
            <SponsoredHeader
              post={post}
              onViewProfile={onViewProfile}
              menu={
                <>
                  {isOwner && (
                    <button type="button" className="sp-menu-btn" onClick={() => onEdit?.(post)} title="Edit post" aria-label="Edit post">
                      <Pencil size={16} color="var(--cyan)" />
                    </button>
                  )}
                  {isOwner && (
                    <button type="button" className="sp-menu-btn" onClick={() => onDelete?.(post)} title="Delete post" aria-label="Delete post">
                      <Trash2 size={16} color="var(--danger)" />
                    </button>
                  )}
                  <button type="button" className="sp-menu-btn" onClick={onClose} aria-label="Close"><X size={18} /></button>
                </>
              }
            />
            <h3 className="sp-title">{headline}</h3>
            {post.caption && <p className="sp-desc sp-desc-full">{post.caption}</p>}
            <div className="sp-media-wrap">{media}</div>
            {audioLine}
            {actions}
            <SponsoredFooter post={post} onViewProfile={onViewProfile} />
          </div>
        </div>
      </div>
    )
  }

  // ---- Normal post ----
  return (
    <div className="modal-backdrop pv-backdrop" style={{ zIndex: 120 }} onClick={onClose}>
      <div className="post-preview-card pv-card" onClick={(e) => e.stopPropagation()}>
        <header className="pv-header">
          <button type="button" className="pv-author" onClick={() => onViewProfile?.(post.authorId)}>
            <Avatar src={post.authorAvatar || ''} name={post.authorName || 'Traveler'} size="sm" storyUid={post.authorId} uid={post.authorId} showStatus={false} />
            <span className="pv-author-text">
              <strong><PostAuthorName post={{ authorId: post.authorId, authorName: post.authorName || 'Operative' }} /></strong>
              <small>{post.time || 'now'}</small>
            </span>
          </button>
          <div className="pv-header-actions">
            {isOwner && (
              <button className="icon-button" onClick={() => onOpenBoost?.(post)} title="Boost this post" aria-label="Boost this post">
                <Rocket size={16} color="var(--cyan)" />
              </button>
            )}
            {isOwner && (
              <button className="icon-button" onClick={() => onEdit?.(post)} title="Edit post" aria-label="Edit post">
                <Pencil size={16} color="var(--cyan)" />
              </button>
            )}
            {isOwner && (
              <button className="icon-button" onClick={() => onDelete?.(post)} title="Delete post" aria-label="Delete post">
                <Trash2 size={16} color="var(--danger)" />
              </button>
            )}
            <button className="icon-button" onClick={onClose} aria-label="Close"><X size={16} /></button>
          </div>
        </header>

        <div className="pv-scroll">
          {(post.title || post.caption || (post.tags && post.tags.length > 0)) && (
            <div className="pv-copy">
              {post.title && <p className="pv-title">{post.title}</p>}
              {post.caption && <p className="pv-caption">{post.caption}</p>}
              {post.tags && post.tags.length > 0 && (
                <p className="pv-tags">
                  {post.tags.map((tag: string) => (
                    <span key={tag} className="ad-tag-chip">{tag.startsWith('#') ? tag : `#${tag}`}</span>
                  ))}
                </p>
              )}
            </div>
          )}
          {media}
          {audioLine}
          {actions}
        </div>
      </div>
    </div>
  )
}

/* =========================================================================
   EDIT POST / REEL - rename, re-tag, and (for video) swap the cover after a
   post is already live. Photo posts only expose Title + Tags (there's no
   separate cover image for a photo - it IS the photo). Video posts and
   reels also get Caption + a Thumbnail picker.
   ========================================================================= */
function EditPostModal({ post, onClose, onSave }: { post: Post; onClose: () => void; onSave: (updates: { title: string; caption?: string; tags: string[]; thumbnailFile?: File | null }) => Promise<void> }) {
  const isVideo = post.mediaType === 'video'
  const [title, setTitle] = useState(post.title || '')
  const [caption, setCaption] = useState(post.caption || '')
  const [tags, setTags] = useState<string[]>(post.tags || [])
  const [tagDraft, setTagDraft] = useState('')
  const [thumbPreview, setThumbPreview] = useState(post.thumbnailUrl || '')
  const [thumbFile, setThumbFile] = useState<File | null>(null)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const addTagsFromDraft = () => {
    if (!tagDraft.trim()) return
    setTags(mergeTags(tags, tagDraft))
    setTagDraft('')
  }
  const onTagKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',' || e.key === ' ') { e.preventDefault(); addTagsFromDraft() }
    else if (e.key === 'Backspace' && !tagDraft && tags.length) setTags(tags.slice(0, -1))
  }
  const pickThumb = (file: File) => {
    if (!file.type.startsWith('image/')) { setError('Please choose an image file.'); return }
    setError('')
    setThumbFile(file)
    setThumbPreview(URL.createObjectURL(file))
  }

  const save = async () => {
    setSaving(true)
    setError('')
    try {
      await onSave({
        title,
        tags: mergeTags(tags, tagDraft),
        ...(isVideo ? { caption, thumbnailFile: thumbFile } : {}),
      })
    } catch (err) {
      setError(friendlyError(err, 'Could not save your changes. Try again.'))
      setSaving(false)
    }
  }

  return (
    <div className="modal-backdrop fp-backdrop">
      <div className="modal-card fp-card">
        <header className="fp-header">
          <button type="button" className="icon-button fp-back" onClick={onClose} disabled={saving} aria-label="Back"><ArrowLeft size={20} /></button>
          <h2 className="fp-title"><Pencil size={18} color="var(--cyan)" /> Edit {post.postKind === 'reel' ? 'reel' : isVideo ? 'video' : 'post'}</h2>
        </header>

        <div style={{ display: 'grid', gap: '1rem' }}>
          {isVideo && (
            <div>
              <small style={{ display: 'block', fontSize: '.72rem', fontWeight: 700, color: 'var(--muted)', marginBottom: '0.25rem', textTransform: 'uppercase', letterSpacing: '.03em' }}>
                Cover / Thumbnail
              </small>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                {thumbPreview && (
                  <img decoding="async" src={thumbPreview} alt="Cover preview" style={{ width: 56, height: 56, borderRadius: 10, objectFit: 'cover', border: '1px solid var(--line)' }} />
                )}
                <label className="text-button" style={{ cursor: 'pointer' }}>
                  {thumbPreview ? 'Change cover' : '+ Add a cover'}
                  <input
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    disabled={saving}
                    onChange={(e) => { const f = e.target.files?.[0]; if (f) pickThumb(f); e.target.value = '' }}
                  />
                </label>
              </div>
            </div>
          )}

          <div>
            <small style={{ display: 'block', fontSize: '.72rem', fontWeight: 700, color: 'var(--muted)', marginBottom: '0.25rem', textTransform: 'uppercase', letterSpacing: '.03em' }}>
              Title
            </small>
            <input
              type="text"
              maxLength={100}
              placeholder="Give it a title"
              value={title}
              disabled={saving}
              onChange={(e) => setTitle(e.target.value)}
              style={{ width: '100%', border: '1px solid var(--line)', borderRadius: 10, padding: '0.5rem 0.75rem', background: 'var(--soft)', color: 'var(--ink)', fontSize: '.85rem', outline: 0 }}
            />
          </div>

          {isVideo && (
            <div>
              <small style={{ display: 'block', fontSize: '.72rem', fontWeight: 700, color: 'var(--muted)', marginBottom: '0.25rem', textTransform: 'uppercase', letterSpacing: '.03em' }}>
                Caption
              </small>
              <textarea
                maxLength={2200}
                placeholder="Write a caption…"
                value={caption}
                disabled={saving}
                onChange={(e) => setCaption(e.target.value)}
                rows={3}
                style={{ width: '100%', border: '1px solid var(--line)', borderRadius: 10, padding: '0.5rem 0.75rem', background: 'var(--soft)', color: 'var(--ink)', fontSize: '.85rem', outline: 0, resize: 'vertical', fontFamily: 'inherit' }}
              />
            </div>
          )}

          <div>
            <small style={{ display: 'block', fontSize: '.72rem', fontWeight: 700, color: 'var(--muted)', marginBottom: '0.25rem', textTransform: 'uppercase', letterSpacing: '.03em' }}>
              Tags
            </small>
            <div className="pc-tags" style={{ border: '1px solid var(--line)', borderRadius: 10, padding: '0.5rem 0.5rem', background: 'var(--soft)', display: 'flex', flexWrap: 'wrap', gap: '0.25rem', alignItems: 'center' }} onClick={(e) => (e.currentTarget.querySelector('input') as HTMLInputElement | null)?.focus()}>
              {tags.map((t) => (
                <span key={t} className="ad-tag-chip" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  #{t}
                  <button type="button" aria-label={`Remove ${t}`} disabled={saving} onClick={(e) => { e.stopPropagation(); setTags(tags.filter((x) => x !== t)) }} style={{ background: 'transparent', border: 0, padding: 0, display: 'flex' }}>
                    <X size={14} />
                  </button>
                </span>
              ))}
              <input
                type="text"
                value={tagDraft}
                disabled={saving}
                placeholder={tags.length ? '' : 'anime, cosplay…'}
                onChange={(e) => setTagDraft(e.target.value)}
                onKeyDown={onTagKeyDown}
                onBlur={addTagsFromDraft}
                style={{ flex: 1, minWidth: 80, border: 0, background: 'transparent', color: 'var(--ink)', fontSize: '.8rem', outline: 0 }}
              />
            </div>
          </div>

          {error && <small style={{ color: 'var(--danger)', fontSize: '.75rem' }}>{error}</small>}

          <button type="button" className="primary-button" disabled={saving} onClick={save} style={{ width: '100%' }}>
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </div>
    </div>
  )
}

function ContactModal({ preset, profile, extra, onClose }: { preset: ContactCategory; profile?: Profile | null; extra?: Record<string, string | undefined>; onClose: () => void }) {
  return (
    <div className="modal-backdrop fp-backdrop">
      <div className="modal-card fp-card">
        <header className="fp-header">
          <button type="button" className="icon-button fp-back" onClick={onClose} aria-label="Back"><ArrowLeft size={20} /></button>
          <h2 className="fp-title"><LifeBuoy size={20} color="var(--cyan)" /> Contact &amp; Support</h2>
        </header>
        <p style={{ margin: '0 0 1rem', fontSize: '.78rem', color: 'var(--muted)', lineHeight: 1.6 }}>
          Appeal a ban, share a suggestion, report a bug, or make a privacy / copyright request.
        </p>
        <ContactForm
          initialCategory={preset}
          prefill={{ name: profile?.name, username: profile?.username, email: profile?.email, uid: profile?.uid }}
          extra={extra}
        />
      </div>
    </div>
  )
}

/* =========================================================================
   DISCOVER & LEADERBOARD TAB
   ========================================================================= */
// Shared leaderboard page: used by both Hall of Fame (Allies) and Coin Kings (Coins).
function LeaderboardView({ variant, users, onViewProfile, metricOf, title, subtitle, loading = false }: any) {
  const isCoins = variant === 'coins'
  const unit = isCoins ? 'Coins' : 'Allies'
  const unitOf = (n: number) => (n === 1 ? (isCoins ? 'Coin' : 'Ally') : unit)
  const top: Profile | undefined = users[0]
  return (
    <div className={`lbv lbv-${variant}`}>
      {users.length === 0 && loading && (
        <div className="lbv-skel" aria-busy="true" aria-label="Loading leaderboard">{[0, 1, 2, 3, 4].map((i) => <div key={i} className="lbv-skel-row"><i /><span><b /><b /></span></div>)}</div>
      )}
      {users.length === 0 && !loading && <EmptyState icon={<Users size={20} />} title="No one on the board yet" compact />}

      {users.length > 0 && (
        <div className="hof-podium lbv-podium">
          <span className="hof-rays" aria-hidden="true" />
          <i className="hof-spark hs1" aria-hidden="true" /><i className="hof-spark hs2" aria-hidden="true" /><i className="hof-spark hs3" aria-hidden="true" />
          <i className="hof-spark hs4" aria-hidden="true" /><i className="hof-spark hs5" aria-hidden="true" /><i className="hof-spark hs6" aria-hidden="true" />
          {[1, 0, 2].map((i) => {
            const target: Profile | undefined = users[i]
            if (!target) return <div key={i} className="hof-col hof-col-empty" />
            const isRank1 = i === 0
            const isQueen = isRank1 && target.gender === 'female'
            const tone = i === 0 ? 'gold' : i === 1 ? 'silver' : 'bronze'
            return (
              <button key={target.uid} type="button" className={`hof-col hof-${tone}`} style={{ animationDelay: `${i === 0 ? 0.25 : i === 1 ? 0.05 : 0.15}s` }} onClick={() => onViewProfile(target.uid)}>
                <div className="hof-avatar-wrap">
                  {isRank1 && (
                    <span className="hof-crown" aria-hidden="true">
                      <svg viewBox="0 0 24 24" width="24" height="24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M3.5 17.5L2 8l5 3.2L12 5l5 6.2 5-3.2-1.5 9.5H3.5Z" fill="url(#hofCrownGold)" stroke="#a5670c" strokeWidth="0.9" strokeLinejoin="round" />
                        <rect x="3.3" y="17.5" width="17.4" height="2.4" rx="1" fill="url(#hofCrownGold)" stroke="#a5670c" strokeWidth="0.9" />
                        <circle cx="12" cy="9.2" r="1.15" fill="#fff4d6" />
                        <circle cx="6.4" cy="11.3" r="0.9" fill="#fff4d6" />
                        <circle cx="17.6" cy="11.3" r="0.9" fill="#fff4d6" />
                        <defs>
                          <linearGradient id="hofCrownGold" x1="2" y1="5" x2="20" y2="20" gradientUnits="userSpaceOnUse">
                            <stop stopColor="#ffe08a" />
                            <stop offset="0.55" stopColor="#f6c343" />
                            <stop offset="1" stopColor="#d99a1f" />
                          </linearGradient>
                        </defs>
                      </svg>
                    </span>
                  )}
                  <div className="hof-avatar-ring lbv-ring">
                    <Avatar src={target.avatar} name={target.name} size="lg" uid={target.uid} showStatus={false} />
                  </div>
                  <span className="hof-medal">{i + 1}</span>
                </div>
                <div className="hof-name">{target.name}</div>
                {isRank1 && <span className={isQueen ? 'lb-badge-queen' : 'lb-badge-king'}>{isQueen ? 'QUEEN' : isCoins ? 'RICHEST' : 'KING'}</span>}
                <div className="hof-handle has-tick">
                  <CountryFlag code={target.country} height={11} />
                  <span className="hof-handle-text">@{target.username}</span>
                  {hasGreenTick(target) && <GreenTick size={15} />}
                </div>
                <div className="hof-block">
                  {isCoins && <CoinIcon size={16} />}
                  <strong>{formatCount(metricOf(target))}</strong>
                  <span>{unitOf(Number(metricOf(target)) || 0)}</span>
                </div>
              </button>
            )
          })}
        </div>
      )}

      <div className="lb-list lbv-list">
        {users.slice(3).map((target: Profile, k: number) => {
          const idx = k + 3
          return (
            <div key={target.uid} role="button" tabIndex={0} className={`hof-row lbv-row ${idx < 5 ? 'hof-row-top' : ''}`} style={{ animationDelay: `${Math.min(k, 8) * 0.05 + 0.3}s` }} onClick={() => onViewProfile(target.uid)} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onViewProfile(target.uid) } }}>
              <div className="hof-rank">{idx + 1}</div>
              <div className="lb-avatar-box">
                <Avatar src={target.avatar} name={target.name} size="md" uid={target.uid} showStatus={false} />
              </div>
              <div className="lb-details" style={{ flex: 1 }}>
                <span className="lb-username">{target.name}</span>
                <span className="lb-user-handle has-tick">
                  <CountryFlag code={target.country} height={12} />
                  <span className="lb-handle-text">@{target.username}</span>
                  {hasGreenTick(target) && <GreenTick size={16} />}
                </span>
              </div>
              <div className="hof-pill lbv-pill">
                {isCoins && <CoinIcon size={14} />}
                <strong>{metricOf(target).toLocaleString('en-IN')}</strong> {unitOf(Number(metricOf(target)) || 0)}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

/** Pull down at the top of a scrolling page to refresh it (touch). Finds the nearest `.content-area` scroller itself. */
function PullToRefresh({ onRefresh, busy, children }: { onRefresh?: () => void; busy?: boolean; children: React.ReactNode }) {
  const wrapRef = useRef<HTMLDivElement>(null)
  const [pull, setPull] = useState(0)
  const st = useRef({ y0: 0, active: false, pull: 0 })
  const refreshRef = useRef(onRefresh); refreshRef.current = onRefresh
  const busyRef = useRef(busy); busyRef.current = busy
  useEffect(() => {
    const scroller = wrapRef.current?.closest('.content-area') as HTMLElement | null
    if (!scroller) return
    const o = st.current
    const start = (e: TouchEvent) => {
      if (e.touches.length === 1 && scroller.scrollTop <= 0 && !busyRef.current) { o.y0 = e.touches[0].clientY; o.active = true } else o.active = false
    }
    const move = (e: TouchEvent) => {
      if (!o.active) return
      const dy = e.touches[0].clientY - o.y0
      if (dy <= 0 || scroller.scrollTop > 0) { if (o.pull) { o.pull = 0; setPull(0) } return }
      o.pull = Math.min(84, dy * 0.5); setPull(o.pull)
    }
    const end = () => {
      if (!o.active) return
      o.active = false
      const p = o.pull; o.pull = 0; setPull(0)
      if (p >= 56) { try { navigator.vibrate?.(12) } catch { /* ignore */ } refreshRef.current?.() }
    }
    scroller.addEventListener('touchstart', start, { passive: true })
    scroller.addEventListener('touchmove', move, { passive: true })
    scroller.addEventListener('touchend', end, { passive: true })
    scroller.addEventListener('touchcancel', end, { passive: true })
    return () => {
      scroller.removeEventListener('touchstart', start); scroller.removeEventListener('touchmove', move)
      scroller.removeEventListener('touchend', end); scroller.removeEventListener('touchcancel', end)
    }
  }, [])
  const h = busy ? 44 : pull
  return (
    <div ref={wrapRef}>
      <div className={`ptr-indicator${pull === 0 ? ' settle' : ''}`} style={{ height: h, opacity: Math.min(1, h / 40) }} aria-hidden="true">
        <RefreshCw size={18} className={busy ? 'animate-spin' : ''} style={busy ? undefined : { transform: `rotate(${pull * 4}deg)` }} />
      </div>
      {children}
    </div>
  )
}

function ContactsTab({
  search, setSearch, results, requests, following, sentRequests, friendIds, followBusy,
  discoverSubTab, setDiscoverSubTab, randomUsers, randomLoading, onRefreshRandom,
  leaderboardUsers, coinLeaders, boardsLoaded, onSearch, onAdd, onCancel, onFollow, onRespond, onViewProfile, busy,
  posts, onOpenPostPreview, onOpenReel
}: any) {
  // people who already asked ME: their button says "Accept" and one tap connects (no second request needed)
  const incomingFrom = useMemo(() => new Set<string>((requests || []).map((r: any) => String(r.from))), [requests])
  return (
    <div>
      {/* Discover = Hall of Fame only (ranked by coin balance) */}
      {discoverSubTab === 'coins' && (
        <LeaderboardView
          variant="coins"
          users={coinLeaders}
          loading={!boardsLoaded?.coins}
          onViewProfile={onViewProfile}
          metricOf={(u: Profile) => u.publicCoins || 0}
          title="Hall of Fame"
          subtitle="Biggest coin balances"
        />
      )}
    </div>
  )
}

// Add friend: a full-screen page of its own. Type an exact @username, tap Search, then Connect / Link from the result.
function AddFriendPage({ search, setSearch, results, randomUsers = [], randomLoading = false, onShuffle, requests, following, sentRequests, friendIds, followBusy, busy, onSearch, onAdd, onCancel, onFollow, onViewProfile, onClose }: any) {
  const incomingFrom = useMemo(() => new Set<string>((requests || []).map((r: any) => String(r.from))), [requests])
  // z-index 79: above the header + bottom bar, but UNDER the profile view (80) so tapping a result opens that profile on top, and Back returns here
  return (
    <div className="modal-backdrop fp-backdrop" style={{ zIndex: 79 }}>
      <div className="modal-card fp-card">
        <header className="fp-header">
          <button type="button" className="icon-button fp-back" onClick={onClose} aria-label="Back"><ArrowLeft size={20} /></button>
          <h2 className="fp-title">Add friend</h2>
        </header>

        <div className="find-friend-head">
          <span className="find-friend-icon"><UserPlus size={18} strokeWidth={2.4} /></span>
          <div>
            <strong>Find your friend</strong>
            <p>Search their exact @username, then tap Connect to send a friend request.</p>
          </div>
        </div>
        <form className="search-box find-friend-box" onSubmit={onSearch}>
          <Search size={16} />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="@username" autoCapitalize="none" autoCorrect="off" spellCheck={false} enterKeyHint="search" autoFocus />
          <button type="submit" disabled={busy} className="find-friend-go">{busy ? 'Searching…' : 'Search'}</button>
        </form>

        <div className="discover-user-list">
          {results.length === 0 && (
            <div className="discover-hint">
              <UserPlus size={22} />
              <strong>Find someone</strong>
              <span>Type an exact @username and tap Search. When they show up, tap Connect to send a friend request.</span>
            </div>
          )}
          {results.map((target: Profile) => (
            <OperativeRow
              key={target.uid}
              target={target}
              following={following}
              friendIds={friendIds}
              sentRequests={sentRequests}
              incomingFrom={incomingFrom}
              followBusy={followBusy}
              onFollow={onFollow}
              onAdd={onAdd}
              onCancel={onCancel}
              onViewProfile={onViewProfile}
            />
          ))}
        </div>

        {/* Discover people: 50 random members, Shuffle re-rolls the whole list */}
        <div className="section-header">
          <div className="section-heading">
            <span className="section-title">Discover people</span>
            <span className="section-sub">Connect to send a friend request</span>
          </div>
          <button type="button" className="btn-shuffle" disabled={randomLoading} onClick={onShuffle}>
            <RefreshCw size={14} className={randomLoading ? 'animate-spin' : ''} /> <span>Shuffle</span>
          </button>
        </div>
        <div className="discover-user-list">
          {randomUsers.map((target: Profile) => (
            <OperativeRow
              key={target.uid}
              target={target}
              following={following}
              friendIds={friendIds}
              sentRequests={sentRequests}
              incomingFrom={incomingFrom}
              followBusy={followBusy}
              onFollow={onFollow}
              onAdd={onAdd}
              onCancel={onCancel}
              onViewProfile={onViewProfile}
            />
          ))}
          {!randomUsers.length && randomLoading && Array.from({ length: 6 }).map((_, i) => (
            <div key={`sk-${i}`} className="user-card sk-row" aria-hidden="true">
              <span className="sk sk-avatar" />
              <span className="sk-lines"><span className="sk sk-line" style={{ width: `${52 + (i % 3) * 14}%` }} /><span className="sk sk-line sm" /></span>
              <span className="sk sk-pill" />
            </div>
          ))}
          {!randomUsers.length && !randomLoading && (
            <p style={{ textAlign: 'center', color: 'var(--muted)', fontSize: '.8rem', margin: '1.5rem 0' }}>
              Tap Shuffle to find people.
            </p>
          )}
        </div>
      </div>
    </div>
  )
}

const OperativeRow = memoLatest(OperativeRowImpl)
function OperativeRowImpl({ target, following, friendIds, sentRequests, incomingFrom, followBusy, onFollow, onAdd, onCancel, onViewProfile }: any) {
  return (
    <div className="user-card">
      <div className="user-info" onClick={() => onViewProfile(target.uid)}>
        <div className="user-avatar-box">
          <Avatar src={target.avatar} name={target.name} size="md" uid={target.uid} showStatus={false} />
        </div>
        <div className="user-details">
          <span className="user-name">{target.name}</span>
          <span className="user-handle">@{target.username}</span>
        </div>
      </div>

      <div className="user-actions">
        <button
          type="button"
          className="badge-btn badge-dark"
          disabled={followBusy === target.uid}
          onClick={() => onFollow(target)}
        >
          {following.has(target.uid) ? 'Linked' : '+ Link'}
        </button>

        {friendIds.has(target.uid) ? (
          <span className="badge-btn badge-allied"><Check size={13} strokeWidth={3} aria-hidden="true" /> Allied</span>
        ) : sentRequests.has(target.uid) ? (
          <>
            <span className="badge-btn badge-pending">Pending</span>
            <button type="button" className="badge-btn badge-cancel" aria-label="Cancel request" onClick={() => onCancel?.(target)}><X size={14} /></button>
          </>
        ) : (
          <button type="button" className="badge-btn badge-connect" onClick={() => { try { navigator.vibrate?.(10) } catch { /* ignore */ } onAdd(target) }}>
            <UserPlus size={13} strokeWidth={2.6} aria-hidden="true" /> {incomingFrom?.has(target.uid) ? 'Accept' : 'Connect'}
          </button>
        )}
      </div>
    </div>
  )
}

/* =========================================================================
   PROFILE TAB
   ========================================================================= */
// Profile "Transmission Vault" grid - used on your own profile and when viewing someone else's.
function VaultGrid({ posts, onOpenPostPreview }: { posts: Post[]; onOpenPostPreview: (p: Post) => void }) {
  if (posts.length === 0) {
    return (
      <EmptyState icon={<Grid3x3 size={20} />} title="No posts yet" hint="Photos and videos will show up here." compact />
    )
  }
  return (
    <div className="vault-grid">
      {posts.map((p) => (
        <button key={p.id} type="button" className="vault-item" onClick={() => onOpenPostPreview(p)} aria-label="Open post">
          {p.mediaType === 'video' ? (
            <>
              {p.thumbnailUrl ? <img decoding="async" src={p.thumbnailUrl} alt="" loading="lazy" /> : <VideoPoster src={p.imageUrl} />}
              <div className="play-badge"><Play size={14} color="#fff" fill="#fff" /></div>
            </>
          ) : (
            <>
              <img decoding="async" src={p.imageUrl} alt="" loading="lazy" />
              {p.imageUrls && p.imageUrls.length > 1 && <div className="play-badge"><Layers size={14} color="#fff" /></div>}
            </>
          )}
          {p.promoted && <span className="vault-sponsored">Sponsored</span>}
          <span className="vault-stats">
            <span><Heart size={14} fill="currentColor" /> {formatCount((p.likes || []).length)}</span>
            <span><Eye size={14} /> {formatCount(p.views || 0)}</span>
          </span>
        </button>
      ))}
    </div>
  )
}

// Analytics lives in the HUD (settings) tab now - the Orbit/profile screen stays clean.
function HudAnalytics({ posts, coinsEarned = 0 }: { posts: Post[]; coinsEarned?: number }) {
  const [open, setOpen] = useState(false)
  const totalLikes = posts.reduce((sum: number, p: Post) => sum + (p.likes?.length || 0), 0)
  const totalComments = posts.reduce((sum: number, p: Post) => sum + (p.commentCount || 0), 0)
  const totalViews = posts.reduce((sum: number, p: Post) => sum + (p.views || 0), 0)
  const topPost = [...posts].sort((a: Post, b: Post) => (b.views || 0) - (a.views || 0))[0]
  return (
    <>
      <button type="button" className="text-button settings-row" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
        <span className="settings-row-main"><BarChart3 size={18} color="var(--cyan)" /> Analytics</span>
        <span className="settings-row-end">
          {formatCount(totalViews)} views
          <ChevronRight size={16} style={{ transform: open ? 'rotate(90deg)' : undefined, transition: 'transform .15s ease' }} />
        </span>
      </button>
      {open && (
        <div className="pf-analytics">
          <div className="stat-box"><span className="stat-number">{formatCount(totalViews)}</span><span className="stat-label">Total Views</span></div>
          <div className="stat-box"><span className="stat-number">{formatCount(totalLikes)}</span><span className="stat-label">Total Likes</span></div>
          <div className="stat-box"><span className="stat-number">{formatCount(totalComments)}</span><span className="stat-label">Total Comments</span></div>
          <div className="stat-box"><span className="stat-number" style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}><CoinIcon size={16} />{formatCount(coinsEarned)}</span><span className="stat-label">Coins from Gifts</span></div>
          {topPost && (
            <div className="pf-analytics-top">
              Top post: {formatCount(topPost.views || 0)} views · {formatCount(topPost.likes?.length || 0)} likes
            </div>
          )}
        </div>
      )}
    </>
  )
}

// "Transmission Vault" heading - identical on your own profile and on someone else's.
function VaultHeading({ count }: { count: number }) {
  return (
    <div className="vault-header">
      <svg className="pigeon-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#ff4d6d" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 6c-2 0-4 1-5 2.5C15.5 6.5 13.5 5 11 5c-4.4 0-8 3.6-8 8 0 1.5.4 2.9 1.1 4.1L2 20l3.2-1.2C6.3 19.5 7.6 20 9 20c4.4 0 8-3.6 8-8 0-1.5-.4-2.9-1.1-4.1L22 6z" />
        <circle cx="16.5" cy="8.5" r="1" fill="#ff4d6d" />
      </svg>
      <span>Transmission Vault</span>
      <span className="vault-count">{count}</span>
    </div>
  )
}

// Cover for a video that has no saved thumbnail: shows the video's own first frame (never a blank / grey tile).
// "#t=0.1" + preload="auto" makes the browser decode and paint a real frame without playing it.
function VideoPoster({ src, style }: { src: string; style?: React.CSSProperties }) {
  if (!src) return null
  const withFrame = src.includes('#') ? src : `${src}#t=0.1`
  return <video src={withFrame} muted playsInline preload="auto" disablePictureInPicture tabIndex={-1} aria-hidden="true" style={{ pointerEvents: 'none', objectFit: 'cover', width: '100%', height: '100%', display: 'block', background: '#000', ...style }} />
}

// Profile picture on a profile page. The colourful ring shows ONLY while that person has a story you have not watched yet.
// Once you have watched all of it (or there is no story) the ring is gone - but tapping the picture still replays the story while it is live.
function ProfileAvatarRing({ profile }: { profile: any }) {
  const storyApi = useContext(StoryRingContext)
  const ringOn = storyApi.statusOf(profile.uid) === 'unseen'
  return (
    <div className={`pfx-av${ringOn ? ' has-ring' : ''}${profile.equipped?.aura ? ` pz-aura pz-au-${profile.equipped.aura}` : ''}`}>
      <Avatar src={profile.avatar} name={profile.name} size="lg" storyUid={profile.uid} showStatus={false} />
    </div>
  )
}

function ProfileTab({ profile, posts, onEdit, onOpenDiscover, onOpenPostPreview, onOpenFollowList, isPremium, coinBalance = 0, onOpenSettings }: any) {
  // Clean edge-to-edge profile: full-bleed cover, circle avatar, left-aligned identity, no divider lines.
  return (
    <div className="pfx">
      <div className={`pfx-cover${profile.coverUrl || profile.equipped?.banner ? ' has-img' : ''}`} aria-hidden="true">
        {profile.equipped?.banner
          ? <PrizeBanner id={profile.equipped.banner} />
          : profile.coverUrl && <img className="pfx-cover-img" src={profile.coverUrl} alt="" decoding="async" />}
      </div>

      <div className="pfx-id">
        <ProfileAvatarRing profile={profile} />
        <h2 className="pfx-name"><NameFx id={profile.equipped?.namefx}>{profile.name}</NameFx></h2>
        <div className="pfx-handle">
          <CountryFlag code={profile.country} height={14} />
          <span>@{profile.username}</span>
          {isPremium && <GreenTick size={20} />}
          {profile.equipped?.badge && <PrizeBadge id={profile.equipped.badge} />}
        </div>

        <ProfileChips profile={{ ...profile, publicCoins: coinBalance }} />

        {profile.bio ? (
          <p className="pfx-bio">{profile.bio}</p>
        ) : (
          <button type="button" className="pfx-bio-add" onClick={onEdit}>+ Add a bio so people know you</button>
        )}

        <div className="pfx-stats">
          <div className="pfx-stat"><b>{formatCount(posts.length)}</b><span>Posts</span></div>
          <button type="button" className="pfx-stat" onClick={() => onOpenFollowList(profile.uid, profile.name, 'followers')}>
            <b>{formatCount(Math.max(0, profile.followersCount || 0))}</b><span>Allies</span>
          </button>
          <button type="button" className="pfx-stat" onClick={() => onOpenFollowList(profile.uid, profile.name, 'following')}>
            <b>{formatCount(Math.max(0, profile.followingCount || 0))}</b><span>Linked</span>
          </button>
        </div>
      </div>

      <div className="pfx-row">
        <button type="button" className="pfx-btn pri" onClick={onEdit}><Edit3 size={20} /> <span>Edit HUD</span></button>
        {onOpenDiscover && (
          <button type="button" className="pfx-ico" onClick={onOpenDiscover} aria-label="Discover people" title="Discover people">
            <UserPlus size={22} />
          </button>
        )}
        {onOpenSettings && (
          <button type="button" className="pfx-ico" onClick={onOpenSettings} aria-label="HUD - Account & settings" title="HUD">
            <Settings size={22} />
          </button>
        )}
      </div>

      {/* Your call state at a glance (only shown when something is limiting calls); tap to change it. */}
      {(profile.callsEnabled === false || profile.callDnd?.on) && (
        <div className="pfx-pad">
          <button type="button" className="pfx-callchip" onClick={onEdit}>
            <Phone size={13} />
            {profile.callsEnabled === false ? 'Calls are off' : 'Call Do Not Disturb on'}
          </button>
        </div>
      )}

      <div className="pfx-vault">
        <VaultHeading count={posts.length} />
        <VaultGrid posts={posts} onOpenPostPreview={onOpenPostPreview} />
      </div>
    </div>
  )
}

/* =========================================================================
   COMMENTS MODAL
   ========================================================================= */
/* Shown instead of the Boost sheet while BOOST_COMING_SOON is true. */
function BoostComingSoon({ kind, onClose }: { kind: 'post' | 'story'; onClose: () => void }) {
  return (
    <div className="modal-backdrop" style={{ zIndex: 130 }} onClick={onClose}>
      <div className="modal-card" onClick={(e) => e.stopPropagation()} style={{ textAlign: 'center' }}>
        <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
          <h2 style={{ fontSize: '1.1rem', fontWeight: 700, color: 'var(--ink)', display: 'flex', alignItems: 'center', gap: 8 }}>
            <Rocket size={18} color="var(--cyan)" /> Boost this {kind}
          </h2>
          <button className="icon-button" onClick={onClose} aria-label="Close"><X size={16} /></button>
        </header>
        <div style={{ display: 'grid', placeItems: 'center', gap: '0.5rem', padding: '1rem 0.5rem 1.25rem' }}>
          <Lock size={34} color="var(--muted)" />
          <strong style={{ fontSize: '1.05rem', color: 'var(--ink)' }}>Coming soon</strong>
          <p style={{ margin: 0, fontSize: '.82rem', color: 'var(--muted)' }}>
            Boost is locked for now. We are working on it and it will be available in a future update.
          </p>
        </div>
        <button type="button" className="primary-button" style={{ width: '100%' }} onClick={onClose}>Got it</button>
      </div>
    </div>
  )
}

/* =========================================================================
   BOOST / PROMOTE MODAL (paid post & story promotion, real money via Google Play Billing)
   ========================================================================= */
// Clean, brand-safe swatches for the ad banner - solid colors instead of an uploaded
// image, so there's nothing that can fail to upload or look broken/stretched.
const AD_BANNER_COLORS = [
  { id: 'cyan', value: '#0891b2' },
  { id: 'indigo', value: '#4f46e5' },
  { id: 'violet', value: '#7c3aed' },
  { id: 'rose', value: '#e11d48' },
  { id: 'orange', value: '#ea580c' },
  { id: 'amber', value: '#d97706' },
  { id: 'emerald', value: '#059669' },
  { id: 'slate', value: '#1e293b' },
]

// Localized Google Play price of one Boost option ("₹49.00"), read from the store.
function BoostPlanPrice({ productId }: { productId: string }) {
  const price = usePlayPrice(productId)
  return <strong style={{ flexShrink: 0, fontSize: '1.05rem', color: 'var(--ink)' }}>{price || '…'}</strong>
}

function BoostModal({ kind, item, onClose, onSuccess, onSaveWebsite, onSaveCtaText, onSaveColor, profile, getIdToken, uid, coinBalance = 0, spendCoins, user, onCoinsEarned, followers = 0, reelViews = 0, isAdmin = false }: any) {
  const [siteUrl, setSiteUrl] = useState(item?.websiteUrl || '')
  const [addingLink, setAddingLink] = useState(false)
  const [linkInput, setLinkInput] = useState('')
  const [savingLink, setSavingLink] = useState(false)
  const hasWebsite = Boolean(siteUrl)
  const [selectedPlan, setSelectedPlan] = useState<typeof BOOST_PLANS[number]['id']>(BOOST_PLANS[1].id)
  const [goal, setGoal] = useState<'profile' | 'website'>(hasWebsite ? 'website' : 'profile')
  const [status, setStatus] = useState<'idle' | 'processing' | 'error'>('idle')
  const [errorMsg, setErrorMsg] = useState('')
  // Boost is paid with real money through Google Play Billing (price shown comes from the store).

  // Custom CTA button text (default "Learn More") and a brand color shown behind the
  // ad's button on the promoted card - both optional, only meaningful once a website
  // link exists.
  const [ctaText, setCtaText] = useState(item?.ctaText || '')
  const [savingCta, setSavingCta] = useState(false)
  // Brief "Saved ✓" confirmation on the button after a successful save - saveCta() was
  // resolving fine, but the button just snapped straight back to its normal "Save" label
  // with nothing to show for it, so a save that actually worked looked like it did nothing.
  const [ctaJustSaved, setCtaJustSaved] = useState(false)
  const [bannerColor, setBannerColor] = useState(item?.bannerColor || '')
  const [savingColor, setSavingColor] = useState(false)

  const saveLink = async () => {
    if (!linkInput.trim()) return
    setSavingLink(true)
    setErrorMsg('')
    try {
      const saved = await onSaveWebsite?.(linkInput.trim())
      setSiteUrl(saved)
      setGoal('website')
      setAddingLink(false)
    } catch (err) {
      setErrorMsg(friendlyError(err, 'Could not save the link. Try again.'))
    } finally {
      setSavingLink(false)
    }
  }

  const saveCta = async () => {
    setSavingCta(true)
    try {
      const saved = await onSaveCtaText?.(ctaText)
      setCtaText(saved)
      setCtaJustSaved(true)
      setTimeout(() => setCtaJustSaved(false), 1600)
    } catch {
      setErrorMsg('Could not save the button text. Try again.')
    } finally {
      setSavingCta(false)
    }
  }

  const pickColor = async (color: string) => {
    const next = bannerColor === color ? '' : color // tap again to clear
    setBannerColor(next)
    setSavingColor(true)
    try {
      await onSaveColor?.(next)
    } catch {
      setErrorMsg('Could not save the banner color. Try again.')
    } finally {
      setSavingColor(false)
    }
  }

  // Boost = REAL MONEY through Google Play Billing (lib/play-billing.ts). Flow: anti-spam precheck on our server -> Google Play
  // checkout -> /api/billing/verify checks the purchase with Google and switches the boost on (promoted/promotedUntil) in one
  // transaction. Nothing is granted from the phone. When the server says ok, the page closes this sheet (see startBilling in Page()).
  const startPayment = async () => {
    const product = playProductForBoostPlan(selectedPlan)
    if (!product) { setStatus('error'); setErrorMsg('This boost is not available right now.'); return }
    if (!billingAvailable()) { setStatus('error'); setErrorMsg(billingErrorText('billing-unavailable')); return }
    setStatus('processing')
    setErrorMsg('')
    try {
      const r = await buyBoost(product.productId, { kind, id: item.id, goal: hasWebsite ? goal : 'profile' })
      if (!r.ok) {
        if (r.cancelled) { setStatus('idle'); return }
        setStatus('error'); setErrorMsg(billingErrorText(r.error)); return
      }
      // Paid. The boost switches on the moment our server confirms with Google; if that is slow, stop the spinner after a while.
      window.setTimeout(() => {
        setStatus((s) => (s === 'processing' ? 'error' : s))
        setErrorMsg((m) => m || 'Your payment is processing. Your boost starts automatically in a moment.')
      }, 30000)
    } catch (err) {
      setStatus('error'); setErrorMsg(friendlyError(err, 'Could not start the payment. Try again.'))
    }
  }

  return (
    // z-index 130: above the story viewer (100), post preview (120) and share sheet (125). At the
    // default 80 the Boost sheet opened BEHIND a story and only showed after closing the story.
    <div className="modal-backdrop fp-backdrop">
      <div className="modal-card fp-card">
        <header className="fp-header">
          <button type="button" className="icon-button fp-back" onClick={onClose} disabled={status === 'processing'} aria-label="Back"><ArrowLeft size={20} /></button>
          <h2 className="fp-title"><Rocket size={18} color="var(--cyan)" /> Boost this {kind}</h2>
        </header>

        <>
        <p style={{ margin: '0 0 1rem', fontSize: '.82rem', color: 'var(--muted)' }}>
          Promoted {kind === 'post' ? 'posts' : 'stories'} get pinned to the top of everyone's Shonen feed for the duration you pick - more eyes, more views, more profile visits.
        </p>

        {(kind === 'post' || kind === 'story') && (
          <div style={{ marginBottom: '1rem' }}>
            <small style={{ display: 'block', fontSize: '.72rem', fontWeight: 700, color: 'var(--muted)', marginBottom: '0.5rem', textTransform: 'uppercase', letterSpacing: '.03em' }}>
              Campaign goal
            </small>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
              <button
                type="button"
                onClick={() => setGoal('profile')}
                style={{
                  display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, padding: '0.75rem 0.5rem', borderRadius: 12,
                  border: goal === 'profile' ? '1.5px solid var(--cyan)' : '1px solid var(--line)',
                  background: goal === 'profile' ? 'var(--accent-tint)' : 'var(--soft)',
                }}
              >
                <CircleUserRound size={16} color={goal === 'profile' ? 'var(--cyan)' : 'var(--muted)'} />
                <strong style={{ fontSize: '.76rem', color: 'var(--ink)' }}>Profile Views</strong>
              </button>
              <button
                type="button"
                onClick={() => {
                  // Select it right away regardless of whether a link is saved yet - it used
                  // to only look selected once hasWebsite was true, so a first-time user could
                  // tap this, watch nothing visibly happen, and assume the button was broken.
                  setGoal('website')
                  if (!hasWebsite) setAddingLink(true)
                }}
                style={{
                  display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, padding: '0.75rem 0.5rem', borderRadius: 12,
                  border: goal === 'website' ? '1.5px solid var(--cyan)' : '1px solid var(--line)',
                  background: goal === 'website' ? 'var(--accent-tint)' : 'var(--soft)',
                }}
              >
                <Forward size={16} color={goal === 'website' ? 'var(--cyan)' : 'var(--muted)'} />
                <strong style={{ fontSize: '.76rem', color: 'var(--ink)' }}>Website Clicks</strong>
              </button>
            </div>
            {!hasWebsite && !addingLink && (
              <button type="button" onClick={() => setAddingLink(true)} style={{ background: 'transparent', display: 'block', marginTop: '0.5rem', fontSize: '.72rem', color: 'var(--cyan)', fontWeight: 700 }}>
                + Add a website link to unlock this goal
              </button>
            )}
            {hasWebsite && !addingLink && (
              <div style={{ marginTop: '0.5rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem' }}>
                <small style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '.72rem', color: 'var(--muted)' }}>{siteUrl}</small>
                <button type="button" onClick={() => { setLinkInput(siteUrl); setAddingLink(true) }} style={{ background: 'transparent', flexShrink: 0, fontSize: '.72rem', color: 'var(--cyan)', fontWeight: 700 }}>
                  Change link
                </button>
              </div>
            )}
            {addingLink && (
              <div style={{ marginTop: '0.5rem', display: 'flex', gap: '0.5rem' }}>
                <input
                  autoFocus
                  type="text"
                  placeholder="yourstore.com"
                  value={linkInput}
                  onChange={(e) => setLinkInput(e.target.value)}
                  style={{ flex: 1, border: '1px solid var(--line)', borderRadius: 10, padding: '0.5rem 0.75rem', background: 'var(--soft)', color: 'var(--ink)', fontSize: '.8rem', outline: 0 }}
                />
                <button type="button" className="text-button primary" disabled={savingLink || !linkInput.trim()} onClick={saveLink} style={{ whiteSpace: 'nowrap' }}>
                  {savingLink ? 'Saving…' : 'Save'}
                </button>
                <button
                  type="button"
                  className="text-button"
                  disabled={savingLink}
                  onClick={() => {
                    setAddingLink(false)
                    setLinkInput('')
                    // Back out of the "website" selection too if no link ever got saved,
                    // so the button doesn't sit there looking selected but not actually usable.
                    if (!hasWebsite) setGoal('profile')
                  }}
                  style={{ whiteSpace: 'nowrap' }}
                >
                  Cancel
                </button>
              </div>
            )}

            {hasWebsite && (
              <div style={{ marginTop: '1rem', display: 'grid', gap: '0.5rem' }}>
                <div>
                  <small style={{ display: 'block', fontSize: '.72rem', fontWeight: 700, color: 'var(--muted)', marginBottom: '0.25rem', textTransform: 'uppercase', letterSpacing: '.03em' }}>
                    Button text
                  </small>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <input
                      type="text"
                      maxLength={24}
                      placeholder="Learn More"
                      value={ctaText}
                      onChange={(e) => { setCtaText(e.target.value); setCtaJustSaved(false) }}
                      style={{ flex: 1, border: '1px solid var(--line)', borderRadius: 10, padding: '0.5rem 0.75rem', background: 'var(--soft)', color: 'var(--ink)', fontSize: '.8rem', outline: 0 }}
                    />
                    <button
                      type="button"
                      className="text-button primary"
                      disabled={savingCta}
                      onClick={saveCta}
                      style={{ whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: 4 }}
                    >
                      {savingCta ? 'Saving…' : ctaJustSaved ? (<><Check size={14} /> Saved</>) : 'Save'}
                    </button>
                  </div>
                  <small style={{ display: 'block', fontSize: '.68rem', color: 'var(--muted)', marginTop: '0.25rem' }}>
                    Shown on the button instead of "Learn More", e.g. "Sign Up", "Shop Now".
                  </small>
                </div>

                <div>
                  <small style={{ display: 'block', fontSize: '.72rem', fontWeight: 700, color: 'var(--muted)', marginBottom: '0.25rem', textTransform: 'uppercase', letterSpacing: '.03em' }}>
                    Banner color
                  </small>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                    {AD_BANNER_COLORS.map((c) => (
                      <button
                        key={c.id}
                        type="button"
                        disabled={savingColor}
                        onClick={() => pickColor(c.value)}
                        aria-label={c.id}
                        style={{
                          width: 30, height: 30, borderRadius: '50%', background: c.value, cursor: savingColor ? 'default' : 'pointer',
                          border: bannerColor === c.value ? '2.5px solid var(--ink)' : '2px solid var(--line)',
                          boxShadow: bannerColor === c.value ? '0 0 0 2px var(--cyan)' : 'none',
                          padding: 0,
                        }}
                      />
                    ))}
                  </div>
                  <small style={{ display: 'block', fontSize: '.68rem', color: 'var(--muted)', marginTop: '0.5rem' }}>
                    Optional - a clean brand color shown behind your promoted card's button, instead of an image. Tap a color again to remove it.
                  </small>
                </div>
              </div>
            )}
          </div>
        )}

        <div style={{ display: 'grid', gap: '0.5rem', marginBottom: '1rem' }}>
          {BOOST_PLANS.map((plan, i) => {
            // Small visual cue on the two upsell tiers, matching the badge pattern already
            // used for the coin packs in the Wallet - the blurb said "Most popular" /
            // "Best value" before but had nothing to catch the eye at a glance.
            const badge = i === 1 ? 'Popular' : i === 2 ? 'Best Value' : null
            const PlanIcon = i === 0 ? Clock : i === 1 ? Zap : Sparkles
            const selected = selectedPlan === plan.id
            return (
              <button
                key={plan.id}
                type="button"
                onClick={() => setSelectedPlan(plan.id)}
                style={{
                  position: 'relative', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.75rem',
                  padding: '0.75rem 1rem', borderRadius: 14,
                  border: selected ? '1.5px solid var(--cyan)' : '1px solid var(--line)',
                  background: selected ? 'var(--accent-tint)' : 'var(--soft)',
                  textAlign: 'left', boxShadow: selected ? '0 4px 14px var(--accent-tint)' : 'none',
                }}
              >
                {badge && (
                  <span style={{
                    position: 'absolute', top: -9, left: 12, padding: '2px 8px', borderRadius: 999,
                    background: badge === 'Popular' ? 'var(--cyan)' : 'var(--accent)',
                    color: '#fff', fontSize: '.6rem', fontWeight: 700, letterSpacing: '.03em', textTransform: 'uppercase',
                  }}>{badge}</span>
                )}
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', minWidth: 0 }}>
                  <span style={{
                    flexShrink: 0, width: 34, height: 34, borderRadius: 10, display: 'grid', placeItems: 'center',
                    background: selected ? 'var(--cyan)' : 'var(--soft-2)', color: selected ? '#fff' : 'var(--muted)',
                  }}>
                    <PlanIcon size={16} />
                  </span>
                  <div style={{ minWidth: 0 }}>
                    <strong style={{ display: 'block', fontSize: '.9rem', color: 'var(--ink)' }}>{plan.label}</strong>
                    <small style={{ fontSize: '.72rem', color: 'var(--muted)' }}>{plan.blurb}</small>
                  </div>
                </div>
                <BoostPlanPrice productId={playProductForBoostPlan(plan.id)?.productId || plan.id} />
              </button>
            )
          })}
        </div>

        {errorMsg && <p style={{ color: 'var(--danger)', fontSize: '.78rem', marginBottom: '0.75rem' }}>{errorMsg}</p>}

        <button
          className="primary-button"
          style={{ width: '100%', minHeight: 44, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
          disabled={status === 'processing'}
          onClick={startPayment}
        >
          {status === 'processing' ? 'Processing…' : 'Boost now'}
        </button>
        <p style={{ margin: '0.75rem 0 0', fontSize: '.68rem', color: 'var(--muted)', textAlign: 'center' }}>
          Paid securely with Google Play. Your boost starts right after payment.
          {' '}Fair-use limits: {BOOST_LIMITS.maxActive} active boosts at once, {BOOST_LIMITS.maxPerDay} per day.
        </p>
        </>
      </div>
    </div>
  )
}

function CommentsModal({ post, comments, currentUserId, onClose, onSend, onToggleLikeComment, onEdit, onDelete, onTogglePin, onViewProfile, zIndex }: any) {
  const [text, setText] = useState('')
  const [editingId, setEditingId] = useState<string | null>(null)
  const [replyTo, setReplyTo] = useState<CommentReplyMeta | null>(null)
  const [openThreads, setOpenThreads] = useState<Set<string>>(new Set())
  const inputRef = useRef<HTMLInputElement | null>(null)
  const isPostOwner = post?.authorId === currentUserId

  // Double-tap-to-like on any comment/reply row (mirrors the post/reel double-tap-to-like
  // pattern). Each comment id tracks its own last-tap time since several rows sit in one list.
  const [doubleTapCommentId, setDoubleTapCommentId] = useState<string | null>(null)
  const commentTapRef = useRef<Record<string, number>>({})
  const handleCommentTap = (c: PostComment) => {
    const now = Date.now()
    const last = commentTapRef.current[c.id] || 0
    if (now - last < 280) {
      if (!(c.likes || []).includes(currentUserId)) onToggleLikeComment(c)
      setDoubleTapCommentId(c.id)
      setTimeout(() => setDoubleTapCommentId(null), 600)
    }
    commentTapRef.current[c.id] = now
  }

  // Threads: a reply carries the id of its top-level comment (threadId). Replies whose parent
  // was deleted simply show up as normal comments.
  const ids = new Set<string>(comments.map((c: PostComment) => c.id))
  const isReply = (c: PostComment) => Boolean(c.threadId && ids.has(c.threadId))
  const topLevel = comments
    .filter((c: PostComment) => !isReply(c))
    .sort((a: PostComment, b: PostComment) => (b.pinned ? 1 : 0) - (a.pinned ? 1 : 0))
  // Pinned replies float to the top of their own thread too - pinning isn't limited to
  // top-level comments, the post/story owner can pin any comment in the discussion.
  const repliesOf = (id: string) => comments
    .filter((c: PostComment) => c.threadId === id)
    .sort((a: PostComment, b: PostComment) => (b.pinned ? 1 : 0) - (a.pinned ? 1 : 0))

  const toggleThread = (id: string) => setOpenThreads((prev) => {
    const next = new Set(prev)
    if (next.has(id)) next.delete(id); else next.add(id)
    return next
  })

  const startReply = (c: PostComment) => {
    setEditingId(null)
    setText('')
    setReplyTo({ threadId: isReply(c) ? (c.threadId as string) : c.id, name: c.authorName, authorId: c.authorId })
    setTimeout(() => inputRef.current?.focus(), 30)
  }

  const renderComment = (c: PostComment, nested: boolean) => {
    // A gift sent from this post: shows who sent it and which gift, so the sender gets credit.
    if (c.giftLabel) {
      const vis = giftVisualFor(c.giftLabel)
      return (
        <div key={c.id} style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', padding: '0.5rem 0.5rem', borderRadius: 12, background: 'rgba(255,107,53,.08)', border: c.pinned ? '1.5px solid var(--cyan)' : '1px solid rgba(255,200,61,.38)' }}>
          <div onClick={(e) => { e.stopPropagation(); onViewProfile?.(c.authorId) }} style={{ cursor: 'pointer' }}>
            <Avatar src={c.authorAvatar} name={c.authorName} size="xs" uid={c.authorId} showStatus={false} />
          </div>
          <div style={{ flex: 1, minWidth: 0, position: 'relative' }} onClick={() => handleCommentTap(c)}>
            {doubleTapCommentId === c.id && (
              <div className="double-tap-heart" style={{ width: 40, height: 40 }}>
                <Heart size={30} fill="#ef4444" color="#ef4444" />
              </div>
            )}
            {c.pinned && (
              <small style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '.66rem', color: 'var(--cyan)', fontWeight: 700, marginBottom: 2 }}>
                <Pin size={14} /> Pinned by creator
              </small>
            )}
            <p style={{ margin: 0, fontSize: '.8rem', color: 'var(--ink-2)', wordBreak: 'break-word' }}>
              <strong style={{ color: 'var(--ink)' }}>{c.authorName}</strong> sent <b style={{ color: 'var(--gold)' }}>{vis.name}</b>
            </p>
            <small style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '.7rem', color: 'var(--muted)', marginTop: 2 }}>
              {c.time}{typeof c.giftAmount === 'number' && <> · <CoinIcon size={12} /> {c.giftAmount.toLocaleString('en-IN')}</>}
              {/* Only the post's creator can pin - any comment/reply in the discussion, not just top-level */}
              {isPostOwner && (
                <button type="button" style={{ background: 'transparent', color: c.pinned ? 'var(--cyan)' : 'var(--muted)', fontWeight: 700, marginLeft: 8 }} onClick={(e) => { e.stopPropagation(); onTogglePin(c) }}>
                  {c.pinned ? 'Unpin' : 'Pin'}
                </button>
              )}
            </small>
          </div>
          <span className="gift-tile" style={{ width: 46, height: 46, borderRadius: 12, fontSize: 0, flexShrink: 0 }}>
            {vis.image ? <img decoding="async" src={vis.image} alt="" draggable={false} /> : <PosterIcon name="gift" size={30} />}
          </span>
          {/* Like the gift - the likes go to the person who sent it */}
          {(() => {
            const giftLiked = (c.likes || []).includes(currentUserId)
            const giftLikes = (c.likes || []).length
            return (
              <button
                type="button"
                aria-label={giftLiked ? 'Unlike gift' : 'Like gift'}
                style={{ background: 'transparent', color: giftLiked ? 'var(--danger)' : 'var(--muted)', display: 'grid', justifyItems: 'center', gap: 1, flexShrink: 0, minWidth: 28 }}
                onClick={() => onToggleLikeComment(c)}
              >
                <Heart size={18} fill={giftLiked ? '#ef4444' : 'none'} />
                {giftLikes > 0 && <span style={{ fontSize: '.66rem', fontWeight: 700 }}>{giftLikes}</span>}
              </button>
            )
          })()}
        </div>
      )
    }
    const isLiked = (c.likes || []).includes(currentUserId)
    const byCreator = Boolean(post?.authorId) && c.authorId === post.authorId
    // Creator comments get a soft brand-tinted card + a "Creator" pill so they stand out.
    const cardStyle: React.CSSProperties = c.pinned
      ? { background: 'var(--accent-tint)', border: '1px solid rgba(255, 107, 53,0.25)', borderRadius: 12, padding: '0.5rem 0.5rem' }
      : byCreator
        ? { background: 'rgba(255,107,53,.08)', border: '1px solid rgba(255,107,53,.28)', borderRadius: 12, padding: '0.5rem 0.5rem' }
        : {}
    return (
      <div key={c.id} style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start', ...cardStyle }}>
        <div onClick={(e) => { e.stopPropagation(); onViewProfile?.(c.authorId) }} style={{ cursor: 'pointer' }}>
          <Avatar src={c.authorAvatar} name={c.authorName} size="xs" uid={c.authorId} showStatus={false} />
        </div>
        <div style={{ flex: 1, minWidth: 0, position: 'relative' }} onClick={() => handleCommentTap(c)}>
          {doubleTapCommentId === c.id && (
            <div className="double-tap-heart" style={{ width: 40, height: 40 }}>
              <Heart size={30} fill="#ef4444" color="#ef4444" />
            </div>
          )}
          {c.pinned && (
            <small style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '.66rem', color: 'var(--cyan)', fontWeight: 700, marginBottom: 2 }}>
              <Pin size={14} /> Pinned by creator
            </small>
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <strong style={{ fontSize: '.84rem', color: 'var(--ink)' }}>{c.authorName}</strong>
            {byCreator && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '1px 8px', borderRadius: 999, fontSize: '.62rem', fontWeight: 700, color: '#fff', background: '#ff4d6d' }}>
                <Crown size={14} /> Creator
              </span>
            )}
          </div>
          <p style={{ margin: 0, fontSize: '.8rem', color: 'var(--ink-2)', wordBreak: 'break-word' }}>
            {nested && c.replyToName && <span style={{ color: 'var(--cyan)', fontWeight: 700 }}>@{c.replyToName} </span>}
            <RichEmojiText text={c.text} />
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', fontSize: '.7rem', color: 'var(--muted)', marginTop: 2 }}>
            <span>{c.edited ? `${c.time} · edited` : c.time}</span>
            <button type="button" style={{ background: 'transparent', color: 'var(--muted)', fontWeight: 700 }} onClick={(e) => { e.stopPropagation(); startReply(c) }}>Reply</button>
            {/* Any comment or reply can be pinned by the post/story owner, not just top-level ones */}
            {isPostOwner && (
              <button type="button" style={{ background: 'transparent', color: c.pinned ? 'var(--cyan)' : 'var(--muted)' }} onClick={(e) => { e.stopPropagation(); onTogglePin(c) }}>
                {c.pinned ? 'Unpin' : 'Pin'}
              </button>
            )}
            {c.authorId === currentUserId && (
              <>
                <button type="button" style={{ background: 'transparent', color: 'var(--cyan)' }} onClick={(e) => { e.stopPropagation(); setReplyTo(null); setEditingId(c.id); setText(c.text) }}>Edit</button>
                <button type="button" style={{ background: 'transparent', color: 'var(--danger)' }} onClick={(e) => { e.stopPropagation(); onDelete(post, c) }}>Delete</button>
              </>
            )}
          </div>
        </div>
        <button
          type="button"
          style={{ background: 'transparent', color: isLiked ? 'var(--danger)' : 'var(--muted)', display: 'grid', justifyItems: 'center', gap: 1, minWidth: 28 }}
          onClick={() => onToggleLikeComment(c)}
        >
          <Heart size={16} fill={isLiked ? '#ef4444' : 'none'} />
          {(c.likes || []).length > 0 && <span style={{ fontSize: '.66rem', fontWeight: 700 }}>{(c.likes || []).length}</span>}
        </button>
      </div>
    )
  }

  return (
    <div className="action-sheet-backdrop" style={zIndex ? { zIndex } : undefined} onClick={onClose}>
      <div className="comments-sheet" onClick={(e) => e.stopPropagation()}>
        <div className="comments-sheet-grab" />
        <header className="comments-sheet-head">
          <h2 style={{ fontSize: '1.05rem', fontWeight: 700, color: 'var(--ink)', display: 'flex', alignItems: 'baseline', gap: 8 }}>
            Discussion <small style={{ fontSize: '.78rem', fontWeight: 700, color: 'var(--muted)' }}>{comments.length}</small>
          </h2>
          <button className="icon-button" onClick={onClose} aria-label="Close"><X size={16} /></button>
        </header>

        <div className="comments-sheet-list">
          {topLevel.map((c: PostComment) => {
            const replies = repliesOf(c.id)
            const open = openThreads.has(c.id)
            return (
              <div key={c.id} style={{ display: 'grid', gap: '0.5rem' }}>
                {renderComment(c, false)}
                {replies.length > 0 && (
                  <div style={{ marginLeft: 32, display: 'grid', gap: '0.5rem' }}>
                    <button
                      type="button"
                      onClick={() => toggleThread(c.id)}
                      style={{ background: 'transparent', color: 'var(--muted)', fontSize: '.72rem', fontWeight: 700, textAlign: 'left', padding: 0 }}
                    >
                      {open ? 'Hide replies' : `View ${replies.length} ${replies.length === 1 ? 'reply' : 'replies'}`}
                    </button>
                    {open && (
                      <div style={{ display: 'grid', gap: '0.5rem', borderLeft: '2px solid var(--line)', paddingLeft: '0.5rem' }}>
                        {replies.map((r: PostComment) => renderComment(r, true))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )
          })}
          {!comments.length && (
            <div style={{ margin: 'auto' }}>
              <EmptyState icon={<MessageCircle size={20} />} title="No comments yet" hint="Be the first to say something." compact />
            </div>
          )}
        </div>

        <div className="comments-sheet-foot">
        {replyTo && !editingId && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '0.25rem 0.5rem', marginBottom: '0.5rem', borderRadius: 10, background: 'var(--accent-tint)', fontSize: '.74rem', color: 'var(--ink-2)' }}>
            <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              Replying to <b style={{ color: 'var(--cyan)' }}>@{replyTo.name}</b>
            </span>
            <button type="button" aria-label="Cancel reply" onClick={() => setReplyTo(null)} style={{ background: 'transparent', color: 'var(--muted)', display: 'grid', placeItems: 'center' }}>
              <X size={14} />
            </button>
          </div>
        )}

        <form
          style={{ display: 'flex', gap: '0.5rem' }}
          onSubmit={(e) => {
            e.preventDefault()
            if (text.trim()) {
              if (editingId) {
                const target = comments.find((x: PostComment) => x.id === editingId)
                if (target) onEdit(post, target, text.trim())
                setEditingId(null)
              } else {
                onSend(text.trim(), replyTo || undefined)
                if (replyTo) {
                  const tid = replyTo.threadId
                  setOpenThreads((prev) => new Set(prev).add(tid))
                  setReplyTo(null)
                }
              }
              setText('')
            }
          }}
        >
          <input
            ref={inputRef}
            style={{ flex: 1, border: '1px solid var(--line)', borderRadius: 12, padding: '0.75rem 0.75rem', background: 'var(--soft)', color: 'var(--ink)', fontSize: '.82rem', outline: 0 }}
            placeholder={editingId ? 'Edit comment…' : replyTo ? `Reply to ${replyTo.name}…` : 'Add a comment…'}
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
          <button className="primary-button" style={{ minHeight: 38, padding: '0 1rem' }}>
            {editingId ? <Check size={16} /> : <Send size={16} />}
          </button>
        </form>
        </div>
      </div>
    </div>
  )
}

/* =========================================================================
   AVATAR COMPONENT WITH showStatus PROP
   ========================================================================= */
function Avatar({
  src,
  name,
  size = 'md',
  online = false,
  showStatus = true,
  storyUid,
  uid
}: {
  src: string
  name: string
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl'
  online?: boolean
  showStatus?: boolean
  storyUid?: string // pass a user id to show that person's story ring (and open their story on tap)
  uid?: string // pass a user id to always show that person's CURRENT profile picture (instead of the copy stored on a post / comment / message)
}) {
  const storyApi = useContext(StoryRingContext)
  const storyStatus = storyUid ? storyApi.statusOf(storyUid) : null
  const liveSrc = useLiveAvatar(uid)
  const prizeEq = useLiveEquipped(uid || storyUid)
  const prizeFrame = prizeEq.frame
  const [liveFailed, setLiveFailed] = useState(false)
  useEffect(() => { setLiveFailed(false) }, [liveSrc])
  const finalSrc = (!liveFailed && liveSrc) || src || fallbackAvatarSvg(name || 'Fan')

  return (
    <span
      className={`avatar-frame avatar-frame-${size} ${!showStatus ? 'no-dot' : ''} ${storyStatus ? `has-story-ring story-${storyStatus}` : ''}`}
      // Only when a live story exists does a tap open it; otherwise the tap falls through to the parent (e.g. open profile).
      {...(storyStatus && storyUid ? {
        role: 'button',
        'aria-label': 'View story',
        onClick: (e: React.MouseEvent) => { e.stopPropagation(); storyApi.open(storyUid) },
      } : {})}
    >
      {storyStatus && <span className={`story-halo${prizeEq.ring ? ` pz-sr-${prizeEq.ring}` : ''}`} aria-hidden="true" />}
      {prizeFrame && size !== 'xs' && <PrizeFrameLayers id={prizeFrame} />}
      <span className={`avatar avatar-${size}`}>
        <img src={finalSrc} alt="" loading="lazy" decoding="async" draggable={false} onContextMenu={(e) => e.preventDefault()} onError={(e) => {
          // the live picture failed to load: fall back to the stored copy first, then to the letter avatar
          if (liveSrc && !liveFailed && src && src !== liveSrc) { setLiveFailed(true); return }
          e.currentTarget.src = fallbackAvatarSvg(name)
        }} />
      </span>
      {showStatus && (online ? <span className="online-dot radar-dot" /> : <span className="offline-dot" />)}
    </span>
  )
}

/* =========================================================================
   CUSTOM STYLED VIDEO PLAYER FOR POST CARDS (replaces the plain native
   scrubber with a themed tap-to-play button + a small mute toggle)
   ========================================================================= */
// Video inside the post preview (opened from a profile grid / Discover): looks exactly like a feed post -
// the same player (tap to play/pause, one mute button, no native timeline) in a box that has the video's own
// shape, so the whole video is shown fitted.
function PostPreviewVideo({ post }: { post: Post }) {
  const [ratio, setRatio] = useState<number>(typeof post.mediaRatio === 'number' && post.mediaRatio > 0 ? post.mediaRatio : 0)
  useEffect(() => {
    if (ratio) return
    // Older videos don't store their shape - read it from the file.
    const v = document.createElement('video')
    v.preload = 'metadata'
    v.muted = true
    v.onloadedmetadata = () => {
      if (v.videoWidth && v.videoHeight) setRatio(v.videoWidth / v.videoHeight)
      v.onloadedmetadata = null
      v.removeAttribute('src')
      v.load()
    }
    v.src = resolveMediaUrl(post.imageUrl) as string
    return () => { v.onloadedmetadata = null; v.removeAttribute('src') }
  }, [post.imageUrl])
  return (
    <div className="ad-media is-video has-ratio pv-video" style={{ aspectRatio: String(clampPostRatio(ratio || 1)) }} onClick={(e) => e.stopPropagation()}>
      <PostVideoPlayer src={post.imageUrl} poster={post.thumbnailUrl} />
    </div>
  )
}

function PostVideoPlayer({ src, poster, suspend, openOnTap }: { src: string; poster?: string; suspend?: boolean; openOnTap?: boolean }) {
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const [isPlaying, setIsPlaying] = useState(false)
  // The cover image stays on screen until the video actually starts playing.
  const [started, setStarted] = useState(false)
  const [isMuted, setIsMuted] = useState(true)
  const [attempt, setAttempt] = useState(0)
  const [failed, setFailed] = useState(false)
  const baseSrc = resolveMediaUrl(src) as string
  // "#t=0.001" makes iOS/Safari paint the first frame instead of staying black.
  const videoSrc = `${baseSrc}${attempt > 0 ? `${baseSrc.includes('?') ? '&' : '?'}r=${attempt}` : ''}#t=0.001`

  const entryRef = useRef<PostVideoEntry | null>(null)
  const suspendRef = useRef(Boolean(suspend))
  suspendRef.current = Boolean(suspend)

  // One-video-at-a-time: this player only registers itself. reconcileVideos()
  // (module level) decides which single clip may play - the one the user just
  // tapped, otherwise the most-visible clip that is >= 60% on screen - and
  // pauses/mutes everything else. Sound follows scroll position: the winning
  // clip plays with sound, and it is switched off as soon as it loses.
  useEffect(() => {
    const el = videoRef.current
    if (!el) return
    const entry: PostVideoEntry = {
      el,
      ratio: 0,
      suspended: suspendRef.current,
      userPaused: false,
      play: () => {
        if (!el.paused) return
        // Feed posts always start muted, unlike Reels - sound only turns on
        // when the user manually taps the speaker icon on this post.
        el.play().catch(() => {})
      },
      stop: () => {
        if (!el.paused || !el.muted) {
          el.muted = true
          setIsMuted(true)
          el.pause()
        }
      },
    }
    entryRef.current = entry
    postVideoEntries.add(entry)

    const observer = new IntersectionObserver(
      ([e]) => {
        entry.ratio = e.isIntersecting ? e.intersectionRatio : 0
        // Once a clip leaves the screen, a manual pause no longer sticks.
        if (entry.ratio < 0.6) entry.userPaused = false
        reconcileVideos()
      },
      { threshold: [0, 0.6] }
    )
    observer.observe(el)
    return () => {
      observer.disconnect()
      postVideoEntries.delete(entry)
      if (manualPostVideo === entry) manualPostVideo = null
      entryRef.current = null
      reconcileVideos()
    }
  }, [])

  // Re-evaluate immediately whenever something covers/uncovers the screen
  // (e.g. opening or closing a story) instead of waiting for the next scroll.
  useEffect(() => {
    if (entryRef.current) entryRef.current.suspended = Boolean(suspend)
    reconcileVideos()
  }, [suspend])

  const togglePlay = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (suspend) return
    const v = videoRef.current
    const entry = entryRef.current
    if (!v || !entry) return
    if (v.paused) {
      // The user picked this clip: it becomes the one that plays, all others stop.
      entry.userPaused = false
      manualPostVideo = entry
      reconcileVideos()
    } else {
      entry.userPaused = true
      if (manualPostVideo === entry) manualPostVideo = null
      v.pause()
    }
  }

  const toggleMute = (e: React.MouseEvent) => {
    e.stopPropagation()
    const v = videoRef.current
    if (!v) return
    v.muted = !v.muted
    setIsMuted(v.muted)
  }

  return (
    <>
      <video
        ref={videoRef}
        className="post-video"
        src={videoSrc}
        playsInline
        loop
        preload="metadata"
        muted={isMuted}
        // openOnTap (post-videos in the feed): a tap on the video bubbles up and opens it in the Reels feed;
        // the round play/pause button still controls playback here.
        onClick={openOnTap ? undefined : togglePlay}
        onPlaying={() => setStarted(true)}
        onPlay={() => {
          setIsPlaying(true)
          // Safety net: whatever started this clip, no other feed clip may keep playing.
          postVideoEntries.forEach((other) => { if (other !== entryRef.current && !other.el.paused) other.stop() })
          // A video starting to play switches the photo post's song off (they would clash).
          stopFeedPostAudio()
        }}
        onPause={() => setIsPlaying(false)}
        onLoadedData={() => setFailed(false)}
        onError={() => {
          // One silent retry (Drive/Vercel can hiccup on a cold start), then show a Retry button.
          if (attempt < 1) setTimeout(() => setAttempt((a) => a + 1), 1200)
          else setFailed(true)
        }}
      />
      {poster && !started && <img className="post-video-thumb" src={poster} alt="" draggable={false} loading="lazy" decoding="async" />}
      {failed && (
        <button
          type="button"
          className="text-button primary"
          style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 7 }}
          onClick={(e) => { e.stopPropagation(); setFailed(false); setAttempt((a) => a + 1) }}
        >
          <RefreshCw size={14} /> Video didn't load - Retry
        </button>
      )}
      <button
        type="button"
        className={`video-toggle-btn video-play-btn ${isPlaying ? 'is-hidden' : ''}`}
        onClick={togglePlay}
        aria-label={isPlaying ? 'Pause video' : 'Play video'}
      >
        <Play size={20} color="#fff" fill="#fff" />
      </button>
      <button
        type="button"
        className="video-toggle-btn video-mute-btn"
        onClick={toggleMute}
        aria-label={isMuted ? 'Unmute video' : 'Mute video'}
      >
        {isMuted ? <VolumeX size={16} color="#fff" /> : <Volume2 size={16} color="var(--cyan)" />}
      </button>
    </>
  )
}

/* =========================================================================
   NOTIFICATION DRAWER MODAL
   ========================================================================= */
function NotificationDrawerModal({ notifications, requests, posts, stories, onClose, onDismiss, onClearAll, onAcceptRequest, onOpenTarget }: any) {
  // Full-screen page (was a small popup card): back arrow + title, friend requests first, then alerts grouped by age.
  const now = Date.now()
  const DAY = 24 * 60 * 60 * 1000
  const groups: { heading: string; items: AppNotification[] }[] = [
    { heading: 'Today', items: [] }, { heading: 'This week', items: [] }, { heading: 'Earlier', items: [] },
  ]
  for (const n of notifications as AppNotification[]) {
    const age = n.createdAtMs ? now - n.createdAtMs : Infinity
    groups[age < DAY ? 0 : age < 7 * DAY ? 1 : 2].items.push(n)
  }
  return (
    <div className="fs-page" role="dialog" aria-modal="true" aria-label="Notifications">
      <header className="fs-top">
        <button className="icon-button" onClick={onClose} aria-label="Back"><ArrowLeft size={18} /></button>
        <h2>Notifications</h2>
        {notifications.length > 0 && <button className="text-button danger" onClick={onClearAll}>Clear all</button>}
      </header>
      <div className="fs-body">
        {requests.length > 0 && (
          <section>
            <div className="fs-sec">Friend requests · {requests.length}</div>
            {requests.map((req: FriendRequest) => (
              <div key={req.id} className="fs-row">
                <Avatar src={req.fromAvatar || ''} name={req.fromName || 'Friend'} size="md" uid={req.from} showStatus={false} />
                <div className="fs-row-main">
                  <strong>{req.fromName}</strong>
                  <small>wants to connect</small>
                </div>
                <button className="text-button primary" onClick={() => onAcceptRequest(req)}>Accept</button>
              </div>
            ))}
          </section>
        )}

        {groups.filter((g) => g.items.length).map((g) => (
          <section key={g.heading}>
            <div className="fs-sec">{g.heading}</div>
            {g.items.map((n: AppNotification) => {
              // Which post/reel/story this alert is about, so a small preview can be tapped to jump straight to it
              // (like / comment / mention alerts only).
              const isContentAlert = n.type === 'like' || n.type === 'comment' || n.type === 'mention'
              const targetPost: Post | undefined = isContentAlert ? posts?.find((p: Post) => p.id === n.targetId) : undefined
              const targetStory: Story | undefined = !targetPost && isContentAlert ? stories?.find((st: Story) => st.id === n.targetId) : undefined
              const thumbSrc = targetPost ? (targetPost.thumbnailUrl || targetPost.imageUrl) : targetStory ? targetStory.mediaUrl : null
              const thumbIsVideo = targetPost ? targetPost.mediaType === 'video' && !targetPost.thumbnailUrl : targetStory ? targetStory.mediaType === 'video' : false
              return (
                <div key={n.id} className={`fs-row ${n.read ? '' : 'is-unread'}`}>
                  <Avatar src={n.fromAvatar} name={n.fromName} size="md" uid={n.type === 'admin' ? undefined : n.fromId} showStatus={false} />
                  <div className="fs-row-main">
                    <p><strong>{n.fromName}</strong> {n.message}</p>
                    <small>{n.time}</small>
                  </div>
                  {thumbSrc && (
                    <button type="button" className="fs-thumb" onClick={() => onOpenTarget?.(targetPost, targetStory)} aria-label="Open post">
                      {thumbIsVideo ? (
                        <VideoPoster src={thumbSrc} />
                      ) : (
                        <img src={thumbSrc} alt="" loading="lazy" decoding="async" />
                      )}
                    </button>
                  )}
                  <button type="button" className="fs-x" onClick={() => onDismiss(n.id)} aria-label="Dismiss"><X size={15} /></button>
                </div>
              )
            })}
          </section>
        ))}

        {!notifications.length && !requests.length && (
          <div className="fs-empty"><Bell size={30} /><p>No new alerts.</p></div>
        )}
      </div>
    </div>
  )
}

/* =========================================================================
   STORY VIEWER (Sound On/Off Button, Music & Video Playback)
   ========================================================================= */
function StoryViewerModal({ stories, index, currentUserId, onClose, onNext, onPrev, onDelete, onLike, onRecordView, onOpenComments, commentsOpen, onOpenBoost, onViewProfile, onShare, onRemixSound, onOpenSoundReel }: any) {
  const story = stories[index]
  const [showSound, setShowSound] = useState(false)

  const [progress, setProgress] = useState(0)
  const [isPaused, setIsPaused] = useState(false)
  const [isMuted, setIsMuted] = useState(false)
  const [showViewers, setShowViewers] = useState(false)
  const [viewerProfiles, setViewerProfiles] = useState<{ uid: string; name: string; avatar: string }[]>([])
  const [loadingViewers, setLoadingViewers] = useState(false)
  // How long this story stays on screen. Plain images get a fixed 5s. Videos and
  // song-attached images use their real media length (capped at 60s) - see below.
  const [durationMs, setDurationMs] = useState(5000)
  // Progress must not start counting until the actual image/video has finished loading -
  // otherwise (slow connection) the bar finishes and skips to the next story while the
  // user is still staring at a blank/loading screen, so they never see it at all.
  const [mediaReady, setMediaReady] = useState(false)
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const storyAudioRef = useRef<HTMLAudioElement | null>(null)
  const elapsedRef = useRef(0)
  // width / height of the current story's media - decides the reel-style layout (see the card below).
  const [mediaRatio, setMediaRatio] = useState(0)
  const isVideoStory = story?.mediaType === 'video'

  useEffect(() => {
    setProgress(0)
    elapsedRef.current = 0
    setShowViewers(false)
    setShowSound(false)
    setMediaReady(false)
    setMediaRatio(0)
    // Until we know the real video/audio length, use a generous placeholder so we
    // never auto-advance before metadata has a chance to load (it corrects itself below).
    setDurationMs(story?.mediaType === 'video' || story?.audioUrl ? 60000 : 5000)

    // Mark this story as viewed by the current viewer (skipped for the author's own story).
    if (story) void onRecordView?.(story)

    // If story has attached song from a shared post, play it - and size the story's
    // on-screen duration to match the song's actual length (max 60s).
    if (story?.audioUrl) {
      if (storyAudioRef.current) storyAudioRef.current.pause()
      const audio = new Audio(story.audioUrl)
      audio.muted = isMuted
      audio.onloadedmetadata = () => {
        if (isFinite(audio.duration) && audio.duration > 0) {
          setDurationMs(Math.min(audio.duration, 60) * 1000)
        }
      }
      audio.onended = () => { setProgress(100); onNext() }
      storyAudioRef.current = audio
      audio.play().catch(() => {})
    } else {
      if (storyAudioRef.current) storyAudioRef.current.pause()
    }

    return () => {
      if (storyAudioRef.current) storyAudioRef.current.pause()
    }
  }, [index, story?.id])

  // Sync mute state with video and audio
  useEffect(() => {
    if (videoRef.current) videoRef.current.muted = isMuted
    if (storyAudioRef.current) storyAudioRef.current.muted = isMuted
  }, [isMuted])

  // Resume playback once the comments drawer (rendered by the parent) is closed.
  useEffect(() => {
    if (!commentsOpen) setIsPaused(false)
  }, [commentsOpen])

  // Holding the story / opening comments / viewers pauses the VIDEO (and song) too - the bar stops with it,
  // exactly like a reel. Letting go resumes both from the same spot.
  useEffect(() => {
    const stopped = isPaused || showViewers || commentsOpen || showSound
    const v = videoRef.current
    const a = storyAudioRef.current
    if (stopped) { v?.pause(); a?.pause() }
    else {
      if (v && v.paused && !v.ended && mediaReady) v.play().catch(() => {})
      if (a && a.paused && !a.ended) a.play().catch(() => {})
    }
  }, [isPaused, showViewers, commentsOpen, showSound, mediaReady])

  // Once the video's real length is known, size the story duration to match (max 60s)
  // instead of cutting every video off at a fixed 5 seconds.
  const handleVideoMeta = () => {
    const d = videoRef.current?.duration
    if (d && isFinite(d) && d > 0) setDurationMs(Math.min(d, 60) * 1000)
  }

  useEffect(() => {
    // A video story's bar follows the video itself (onTimeUpdate below), so it can never run on after the video stops.
    if (isVideoStory) return
    if (isPaused || showViewers || !mediaReady || commentsOpen || showSound) return
    const tickStart = Date.now()
    const baseElapsed = elapsedRef.current
    let advanced = false
    const interval = setInterval(() => {
      const elapsed = baseElapsed + (Date.now() - tickStart)
      elapsedRef.current = elapsed
      const pct = Math.min(100, (elapsed / durationMs) * 100)
      setProgress(pct)
      if (pct >= 100 && !advanced) {
        advanced = true
        onNext()
      }
    }, 100)
    return () => clearInterval(interval)
  }, [isPaused, index, onNext, durationMs, showViewers, mediaReady, commentsOpen, showSound, isVideoStory])

  const openViewers = async () => {
    if (story.authorId !== currentUserId) return
    setIsPaused(true)
    setShowViewers(true)
    const uids: string[] = story.viewers || []
    if (!uids.length) { setViewerProfiles([]); return }
    setLoadingViewers(true)
    try {
      const docs = await Promise.all(uids.map((uid) => getDoc(doc(firestore, 'users', uid))))
      setViewerProfiles(docs.filter((d) => d.exists()).map((d) => {
        const data = d.data() as any
        return { uid: d.id, name: data.name || 'Traveler', avatar: data.avatar || '' }
      }))
    } catch { /* ignore */ }
    setLoadingViewers(false)
  }

  if (!story) return null
  const isLiked = (story.likes || []).includes(currentUserId)
  const likeCount = (story.likes || []).length
  const viewCount = (story.viewers || []).length
  // A story has a real sound when it carries a song file, or it is a video made from a post (its own audio is the "Original audio").
  // Plain video stories are NOT sounds: their file is erased after 24 h, so a reel could not reuse it.
  const soundPost: any = (story.audioUrl || (story.mediaType === 'video' && story.sourcePostId))
    ? { id: story.sourcePostId || story.id, audioUrl: story.audioUrl || undefined, audioTitle: story.audioTitle, imageUrl: story.mediaUrl, mediaType: story.mediaType, soundOwnerName: story.soundOwnerName, authorName: story.authorName, remixOf: story.remixOf }
    : null

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 100, background: '#000', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
      {showSound && soundPost && (
        <div style={{ position: 'absolute', inset: 0, zIndex: 90 }} onPointerDown={(e) => e.stopPropagation()} onPointerUp={(e) => e.stopPropagation()}>
          <SoundSheet
            post={soundPost}
            myUid={currentUserId}
            onClose={() => setShowSound(false)}
            onRemix={() => { setShowSound(false); onRemixSound?.(soundPost) }}
            onOpenReel={(x: Post) => { setShowSound(false); onOpenSoundReel?.(x) }}
          />
        </div>
      )}

      <div onPointerDown={(e) => e.stopPropagation()} onPointerUp={(e) => e.stopPropagation()} style={{ position: 'absolute', top: 'calc(env(safe-area-inset-top, 0px) + 12px)', left: 10, right: 10, display: 'flex', flexDirection: 'column', gap: 8, zIndex: 60, color: '#fff' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', width: '100%', minWidth: 0 }}>
        <div style={{ flexShrink: 0 }}><Avatar src={story.authorAvatar} name={story.authorName} size="sm" uid={story.authorId} showStatus={false} /></div>
        <div style={{ flex: '1 1 0', minWidth: 0 }}>
          <strong style={{ fontSize: '.88rem', display: 'flex', alignItems: 'center', gap: 4, minWidth: 0 }}>
            {story.authorName}
            {story.promoted && (
              <span className="sp-pill sp-pill-sm sp-pill-dark">Sponsored</span>
            )}
          </strong>
          {soundPost && (
            // Instagram style "Artist · Title" line under the name. Tap = every reel made with this sound + Remix.
            <button type="button" className="story-sound-line" onClick={() => setShowSound(true)} aria-label="See reels that use this sound">
              <span className="story-sound-bars" aria-hidden="true"><i /><i /><i /></span>
              <span className="story-sound-text">{soundPost.audioUrl ? `${soundPost.soundOwnerName ? `${soundPost.soundOwnerName} · ` : ''}${soundPost.audioTitle || 'Attached Audio Track'}` : `Original audio · ${soundPost.soundOwnerName || story.authorName}`}</span>
            </button>
          )}
          {!soundPost && story.mediaType === 'video' && (
            // A video uploaded straight to the story (not shared from a post): its own sound is the "Original audio", so the
            // banner shows for everyone watching. Not tappable - the file is erased after 24 h, so a reel could not reuse it.
            <span className="story-sound-line" style={{ cursor: 'default' }}>
              <span className="story-sound-bars" aria-hidden="true"><i /><i /><i /></span>
              <span className="story-sound-text">{`Original audio · ${story.authorName}`}</span>
            </span>
          )}
        </div>

        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '0.4rem', flexShrink: 0 }}>
          {/* Sound On/Off Button */}
          <button
            className="icon-button"
            style={{ border: 0, width: 34, height: 34 }}
            onClick={() => setIsMuted(!isMuted)}
            title={isMuted ? 'Unmute' : 'Mute'}
          >
            {isMuted ? <VolumeX size={18} color="var(--danger)" /> : <Volume2 size={18} color="var(--cyan)" />}
          </button>

          {story.authorId === currentUserId && !story.promoted && (
            <button className="icon-button" style={{ border: 0 }} title="Boost this story" onClick={() => { setIsPaused(true); onOpenBoost?.(story) }}><Rocket size={16} color="var(--cyan)" /></button>
          )}
          {story.authorId === currentUserId && (
            <button className="icon-button" style={{ border: 0 }} onClick={() => onDelete(story)}><Trash2 size={16} /></button>
          )}
          <button className="icon-button" style={{ border: 0 }} onClick={onClose}><X size={16} /></button>
        </div>
      </div>
      {/* Progress bar: sits just BELOW the profile row (not above it). */}
      <div style={{ display: 'flex', gap: 4, width: '100%' }}>
        {stories.map((_: any, i: number) => (
          <div key={i} style={{ flex: 1, height: 2.5, borderRadius: 999, background: 'rgba(255,255,255,0.25)', overflow: 'hidden' }}>
            <div style={{ height: '100%', background: '#fff', width: i < index ? '100%' : i === index ? `${progress}%` : '0%', transition: i === index && isVideoStory ? 'width .3s linear' : undefined }} />
          </div>
        ))}
      </div>
      </div>

      <div style={{ position: 'absolute', inset: 0 }} onPointerDown={() => setIsPaused(true)} onPointerUp={() => setIsPaused(false)}>
        {/* Reel-style fit: tall (9:16-ish) media fills the whole screen; wider media (landscape / square) sits in the
            free area between the top bar and the buttons - nothing is cropped. */}
        <div style={{ position: 'absolute', left: 0, right: 0, top: mediaRatio > 0.7 ? 92 : 0, bottom: mediaRatio > 0.7 ? 96 : 0, overflow: 'hidden', background: '#000' }}>
          {story.mediaType === 'video' ? (
            <video
              ref={videoRef}
              src={story.mediaUrl}
              autoPlay
              playsInline
              muted={isMuted}
              controls={false}
              onLoadedMetadata={(e) => { handleVideoMeta(); const v = e.currentTarget; if (v.videoWidth && v.videoHeight) setMediaRatio(v.videoWidth / v.videoHeight) }}
              onTimeUpdate={(e) => { const v = e.currentTarget; if (v.duration > 0 && isFinite(v.duration)) setProgress(Math.min(100, (v.currentTime / v.duration) * 100)) }}
              onLoadedData={() => setMediaReady(true)}
              onError={() => setMediaReady(true)}
              onEnded={() => { setProgress(100); onNext() }}
              style={{ width: '100%', height: '100%', objectFit: 'contain', background: '#000' }}
            />
          ) : (
            <img decoding="async"
              src={story.mediaUrl}
              alt=""
              onLoad={(e) => { const im = e.currentTarget; if (im.naturalWidth && im.naturalHeight) setMediaRatio(im.naturalWidth / im.naturalHeight); setMediaReady(true) }}
              onError={() => setMediaReady(true)}
              style={{ width: '100%', height: '100%', objectFit: 'contain', background: '#000' }}
            />
          )}
          <div style={{ position: 'fixed', inset: 0, zIndex: 6, pointerEvents: 'none', background: 'linear-gradient(to bottom, rgba(0,0,0,.5) 0, transparent 18%), linear-gradient(to top, rgba(0,0,0,.55) 0, transparent 16%)' }} />
          {story.caption ? (
            <div style={{ position: 'fixed', left: 0, right: 0, bottom: 'calc(104px + env(safe-area-inset-bottom, 0px))', zIndex: 7, pointerEvents: 'none', display: 'flex', justifyContent: 'center', padding: '0 16px' }}>
              <span style={{ maxWidth: '100%', background: 'rgba(0,0,0,.55)', color: '#fff', fontSize: '.92rem', lineHeight: 1.35, padding: '8px 14px', borderRadius: 14, textAlign: 'center', wordBreak: 'break-word', textShadow: '0 1px 2px rgba(0,0,0,.6)' }}>{story.caption}</span>
            </div>
          ) : null}
          {!mediaReady && (
            <div style={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', zIndex: 5 }}>
              <div style={{ width: 34, height: 34, borderRadius: '50%', border: '3px solid rgba(255,255,255,0.2)', borderTopColor: '#fff', animation: 'spin 0.8s linear infinite' }} />
            </div>
          )}
        </div>
        <div style={{ position: 'absolute', inset: 0, display: 'flex', zIndex: 10 }}>
          <div style={{ width: '35%', height: '100%' }} onClick={onPrev} />
          <div style={{ width: '65%', height: '100%' }} onClick={onNext} />
        </div>
      </div>

      {/* Sponsored stories: same "Promoted Content" footer as every other ad, with the
          same optional brand color (or legacy banner image) behind it as posts/reels get. */}
      {story.promoted && story.authorId !== currentUserId && (
        <div
          className="sp-footer sp-footer-float sp-footer-story"
          style={story.bannerColor ? {
            background: `linear-gradient(135deg, ${story.bannerColor}, ${story.bannerColor}cc)`,
          } : story.bannerImageUrl ? {
            backgroundImage: `linear-gradient(90deg, rgba(15,17,21,.72), rgba(15,17,21,.4)), url(${JSON.stringify(safeMediaUrl(story.bannerImageUrl))})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          } : undefined}
        >
          <span className="sp-footer-label"><ExternalLinkIcon size={16} /> Promoted Content</span>
          {story.boostGoal === 'website' && safeHttpUrl(story.websiteUrl) ? (
            <a
              href={safeHttpUrl(story.websiteUrl)}
              target="_blank"
              rel="noopener noreferrer sponsored"
              className="sp-cta"
              onClick={(e) => { e.stopPropagation(); setIsPaused(true) }}
            >
              {story.ctaText || 'Learn More'}
            </a>
          ) : (
            <button
              type="button"
              className="sp-cta"
              onClick={(e) => { e.stopPropagation(); setIsPaused(true); onViewProfile?.(story.authorId) }}
            >
              View Profile
            </button>
          )}
        </div>
      )}

      {/* Like + Views Bar */}
      <div onPointerDown={(e) => e.stopPropagation()} onPointerUp={(e) => e.stopPropagation()} style={{ position: 'absolute', bottom: 'calc(env(safe-area-inset-bottom, 0px) + 28px)', left: 14, right: 14, display: 'flex', alignItems: 'center', justifyContent: 'space-between', zIndex: 60, touchAction: 'manipulation' }}>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); void onLike?.(story) }}
          style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'rgba(0,0,0,.45)', border: '1px solid rgba(255,255,255,.16)', borderRadius: 999, padding: '0.75rem 1rem', minHeight: 44, color: '#fff', cursor: 'pointer', touchAction: 'manipulation' }}
        >
          <Heart size={16} fill={isLiked ? 'var(--danger, #ff4d6d)' : 'none'} color={isLiked ? 'var(--danger, #ff4d6d)' : '#fff'} />
          <span style={{ fontSize: '.78rem', fontWeight: 700 }}>{likeCount}</span>
        </button>

        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); setIsPaused(true); onOpenComments?.(story) }}
          style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'rgba(0,0,0,.45)', border: '1px solid rgba(255,255,255,.16)', borderRadius: 999, padding: '0.75rem 1rem', minHeight: 44, color: '#fff', cursor: 'pointer', touchAction: 'manipulation' }}
        >
          <MessageCircle size={16} />
          <span style={{ fontSize: '.78rem', fontWeight: 700 }}>{story.commentCount || 0}</span>
        </button>

        {story.authorId === currentUserId && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); void openViewers() }}
            style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'rgba(0,0,0,.45)', border: '1px solid rgba(255,255,255,.16)', borderRadius: 999, padding: '0.75rem 1rem', minHeight: 44, color: '#fff', cursor: 'pointer', touchAction: 'manipulation' }}
          >
            <Eye size={16} color="var(--cyan)" />
            <span style={{ fontSize: '.78rem', fontWeight: 700 }}>{viewCount}</span>
          </button>
        )}

        {!story.promoted && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setIsPaused(true); onShare?.(story) }}
            aria-label="Share this story"
            style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'rgba(0,0,0,.45)', border: '1px solid rgba(255,255,255,.16)', borderRadius: 999, padding: '0.75rem 1rem', minHeight: 44, color: '#fff', cursor: 'pointer', touchAction: 'manipulation' }}
          >
            <Share2 size={16} color="var(--cyan)" />
          </button>
        )}
      </div>

      {/* Viewers Drawer */}
      {showViewers && (
        <div
          style={{ position: 'absolute', inset: 0, zIndex: 40, background: 'rgba(0,0,0,.6)', display: 'flex', alignItems: 'flex-end' }}
          onClick={() => { setShowViewers(false); setIsPaused(false) }}
        >
          <div
            style={{ width: '100%', maxHeight: '55%', overflow: 'auto', background: '#111319', borderTopLeftRadius: 22, borderTopRightRadius: 22, border: '1px solid rgba(255,255,255,.1)', padding: '1rem' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
              <strong style={{ color: '#fff', fontSize: '.92rem' }}>Seen by {viewCount}</strong>
              <button className="icon-button" style={{ border: 0 }} onClick={() => { setShowViewers(false); setIsPaused(false) }}><X size={16} /></button>
            </div>
            {loadingViewers ? (
              <SkeletonRows count={3} />
            ) : viewerProfiles.length ? (
              <div style={{ display: 'grid', gap: '0.5rem' }}>
                {viewerProfiles.map((v) => (
                  <div key={v.uid} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <Avatar src={v.avatar} name={v.name} size="sm" uid={v.uid} showStatus={false} />
                    <span style={{ color: '#fff', fontSize: '.84rem' }}>{v.name}</span>
                  </div>
                ))}
              </div>
            ) : (
              <EmptyState icon={<Eye size={20} />} title="No views yet" compact />
            )}
          </div>
        </div>
      )}
    </div>
  )
}

/* =========================================================================
   CREATE STORY MODAL (Photo + Video, Cloudflare R2 upload, Live MB Tracker, Privacy) - stories auto-delete after 24h
   ========================================================================= */
function CreateStoryModal({ userId, authorName, authorAvatar, onClose, onSuccess }: any) {
  const STORY_MAX_SECONDS = 30
  const CAPTION_MAX = 120
  const [file, setFile] = useState<File | null>(null)
  const [preview, setPreview] = useState('')
  const [isVideo, setIsVideo] = useState(false)
  const [durationSec, setDurationSec] = useState(0)
  const [caption, setCaption] = useState('')
  const [privacy, setPrivacy] = useState<'public' | 'private'>('public')
  const [uploading, setUploading] = useState(false)
  const [compressing, setCompressing] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [prepPct, setPrepPct] = useState(0)
  const [uploadProgress, setUploadProgress] = useState({ loadedMB: '0', totalMB: '0', percent: 0 })
  const photoInputRef = useRef<HTMLInputElement | null>(null)
  const videoInputRef = useRef<HTMLInputElement | null>(null)
  const previewRef = useRef('')

  const busy = uploading || compressing || saving

  // Free the preview's memory when the picture changes / the screen closes.
  useEffect(() => () => { if (previewRef.current) URL.revokeObjectURL(previewRef.current) }, [])
  const showPreview = (f: File, video: boolean, secs: number) => {
    if (previewRef.current) URL.revokeObjectURL(previewRef.current)
    const url = URL.createObjectURL(f)
    previewRef.current = url
    setFile(f)
    setPreview(url)
    setIsVideo(video)
    setDurationSec(secs)
    setError('')
  }
  const clearFile = () => {
    if (busy) return
    if (previewRef.current) URL.revokeObjectURL(previewRef.current)
    previewRef.current = ''
    setFile(null); setPreview(''); setIsVideo(false); setDurationSec(0); setError('')
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0]
    e.target.value = '' // lets the same file be picked again after removing it
    if (!f) return

    if (f.type.startsWith('video/') || /\.(mp4|mov|webm|m4v|mkv|3gp|3g2|avi|mpg|mpeg|wmv|flv|mts|m2ts)$/i.test(f.name)) {
      const v = document.createElement('video')
      v.preload = 'metadata'
      const probe = URL.createObjectURL(f)
      v.src = probe
      v.onloadedmetadata = () => {
        URL.revokeObjectURL(probe)
        if (v.duration > STORY_MAX_SECONDS + 3) {
          setError(`That video is ${Math.round(v.duration)}s long. Stories can be up to ${STORY_MAX_SECONDS} seconds - pick a shorter one.`)
          return
        }
        showPreview(f, true, Math.round(isFinite(v.duration) ? v.duration : 0))
      }
      // Some phones cannot read the length up front - still let the video through.
      v.onerror = () => { URL.revokeObjectURL(probe); showPreview(f, true, 0) }
      return
    }
    if (!f.type.startsWith('image/') && !/\.(jpe?g|png|webp|gif|avif|heic|heif)$/i.test(f.name)) {
      setError('Please choose a photo or a video.')
      return
    }
    showPreview(f, false, 0)
  }

  const handleUpload = async () => {
    if (!file || busy) return
    const heldMsg = uploadHoldMessage()
    if (heldMsg) { setError(heldMsg); return }
    setError('')
    setUploadProgress({ loadedMB: '0', totalMB: '0', percent: 0 })
    setUploading(true)
    let uploadedUrl = ''
    try {
      // Compress video before upload so it's small and fast on slow connections.
      let fileToUpload: File = file
      if (isVideo) {
        setPrepPct(0)
        setCompressing(true)
        try {
          fileToUpload = await compressVideoIfNeeded(file, (p) => setPrepPct(Math.round(p * 100)))
        } finally {
          setCompressing(false)
        }
      }

      // Cloudflare R2 (same pipeline as Reels / Posts), into the stories/ folder.
      // The hourly cleanup job (app/api/cron/stories) erases file + story 24 hours later.
      uploadedUrl = await uploadWithProgress(fileToUpload, 'story', userId, (loadedMB, totalMB, percent) => {
        setUploadProgress({ loadedMB, totalMB, percent })
      })

      setSaving(true)
      const cleanCaption = caption.trim().slice(0, CAPTION_MAX)
      await addDoc(collection(firestore, 'stories'), {
        authorId: userId,
        authorName,
        authorAvatar,
        mediaUrl: uploadedUrl,
        mediaType: isVideo ? 'video' : 'image',
        ...(cleanCaption ? { caption: cleanCaption } : {}),
        likes: [],
        privacy,
        viewers: [],
        createdAt: serverTimestamp(),
      })

      onSuccess()
    } catch (err) {
      if (uploadedUrl) void deleteUploadedFile(uploadedUrl) // story record failed - don't leave an orphan file in R2
      const raw = err instanceof Error ? err.message : ''
      console.error('story upload failed', err)
      setError(
        raw === 'network-error' || /Failed to fetch|NetworkError/i.test(raw)
          ? 'Upload failed. Check your network connection and try again.'
          : `Upload failed${raw ? `: ${raw.slice(0, 160)}` : '.'}`
      )
      setUploading(false)
      setCompressing(false)
      setSaving(false)
    }
  }

  const sizeMB = file ? (file.size / 1048576).toFixed(1) : '0'
  const stage = compressing
    ? `Getting your video ready…${prepPct > 0 ? ` ${prepPct}%` : ''}`
    : saving ? 'Publishing your story…' : 'Uploading to Cloudflare…'
  const stagePct = compressing ? prepPct : saving ? 100 : uploadProgress.percent

  const privacyOptions: { id: 'public' | 'private'; icon: React.ReactNode; title: string; hint: string }[] = [
    { id: 'public', icon: <Globe size={18} />, title: 'Galactic', hint: 'Public - everyone can see it' },
    { id: 'private', icon: <Lock size={18} />, title: 'Allies only', hint: 'Only people you are connected with' },
  ]

  return (
    <div className="modal-backdrop fp-backdrop">
      <div className="modal-card fp-card">
        <header className="fp-header">
          <button type="button" className="icon-button fp-back" onClick={onClose} disabled={busy} aria-label="Back"><ArrowLeft size={20} /></button>
          <h2 className="fp-title">Deploy to Story</h2>
        </header>

        <input ref={photoInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleFileChange} />
        <input ref={videoInputRef} type="file" accept="video/*" style={{ display: 'none' }} onChange={handleFileChange} />

        <div style={{ display: 'grid', gap: '0.9rem', paddingBottom: 8 }}>
          {!file ? (
            <div style={{ border: '1.5px dashed var(--line)', borderRadius: 20, padding: '1.6rem 1rem', background: 'var(--accent-tint)', textAlign: 'center', display: 'grid', gap: 12, justifyItems: 'center' }}>
              <div style={{ width: 56, height: 56, borderRadius: '50%', display: 'grid', placeItems: 'center', background: 'var(--soft-2)', color: 'var(--cyan)' }}>
                <UploadCloud size={28} />
              </div>
              <div>
                <div style={{ fontWeight: 750, fontSize: '1.02rem', color: 'var(--ink)' }}>Add to your story</div>
                <div style={{ fontSize: '.78rem', color: 'var(--muted)', marginTop: 4 }}>Photo or video up to {STORY_MAX_SECONDS} seconds</div>
              </div>
              <div style={{ display: 'flex', gap: 10, width: '100%' }}>
                <button type="button" className="primary-button" style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }} onClick={() => photoInputRef.current?.click()}>
                  <ImageIcon size={18} /> Photo
                </button>
                <button type="button" className="primary-button" style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }} onClick={() => videoInputRef.current?.click()}>
                  <Video size={18} /> Video
                </button>
              </div>
            </div>
          ) : (
            <div style={{ display: 'grid', gap: 8, justifyItems: 'center' }}>
              <div style={{ position: 'relative', width: '100%', maxWidth: 260, aspectRatio: '9 / 16', maxHeight: 400, background: '#000', borderRadius: 20, overflow: 'hidden', border: '1px solid var(--line)' }}>
                {isVideo ? (
                  <video preload="metadata" src={preview} controls controlsList="nodownload noremoteplayback" disablePictureInPicture onContextMenu={(e) => e.preventDefault()} playsInline style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                ) : (
                  <img decoding="async" src={preview} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                )}
                <div style={{ position: 'absolute', top: 8, left: 8, display: 'flex', gap: 6, pointerEvents: 'none' }}>
                  <span style={{ background: 'rgba(0,0,0,.6)', color: '#fff', fontSize: '.68rem', fontWeight: 650, padding: '3px 8px', borderRadius: 999 }}>
                    {isVideo ? `Video${durationSec ? ` · ${durationSec}s` : ''}` : 'Photo'} · {sizeMB} MB
                  </span>
                </div>
                {!busy && (
                  <button type="button" aria-label="Remove" onClick={clearFile} style={{ position: 'absolute', top: 8, right: 8, width: 30, height: 30, borderRadius: '50%', border: 0, display: 'grid', placeItems: 'center', background: 'rgba(0,0,0,.6)', color: '#fff', cursor: 'pointer' }}>
                    <X size={16} />
                  </button>
                )}
              </div>
              {!busy && (
                <button type="button" className="text-button" onClick={() => (isVideo ? videoInputRef : photoInputRef).current?.click()} style={{ fontSize: '.8rem' }}>
                  Change {isVideo ? 'video' : 'photo'}
                </button>
              )}
            </div>
          )}

          {/* Caption */}
          <div>
            <label style={{ fontSize: '.76rem', color: 'var(--muted)', display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
              <span>Caption (optional)</span>
              <span>{caption.length}/{CAPTION_MAX}</span>
            </label>
            <input
              type="text"
              value={caption}
              maxLength={CAPTION_MAX}
              disabled={busy}
              onChange={(e) => setCaption(e.target.value)}
              placeholder="Say something about this story…"
              style={{ width: '100%', boxSizing: 'border-box', border: '1px solid var(--line)', borderRadius: 12, padding: '0.75rem', background: 'var(--paper)', color: 'var(--ink)', outline: 0 }}
            />
          </div>

          {/* Privacy */}
          <div>
            <label style={{ fontSize: '.76rem', color: 'var(--muted)', display: 'block', marginBottom: 6 }}>Clearance (Privacy)</label>
            <div style={{ display: 'grid', gap: 8 }}>
              {privacyOptions.map((o) => {
                const on = privacy === o.id
                return (
                  <button
                    key={o.id}
                    type="button"
                    disabled={busy}
                    onClick={() => setPrivacy(o.id)}
                    aria-pressed={on}
                    style={{ display: 'flex', alignItems: 'center', gap: 12, textAlign: 'left', padding: '0.7rem 0.85rem', borderRadius: 14, cursor: 'pointer', color: 'var(--ink)', background: on ? 'var(--accent-tint)' : 'var(--paper)', border: `1.5px solid ${on ? 'var(--cyan)' : 'var(--line)'}` }}
                  >
                    <span style={{ color: on ? 'var(--cyan)' : 'var(--muted)', display: 'grid' }}>{o.icon}</span>
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <span style={{ display: 'block', fontWeight: 700, fontSize: '.9rem' }}>{o.title}</span>
                      <span style={{ display: 'block', fontSize: '.74rem', color: 'var(--muted)' }}>{o.hint}</span>
                    </span>
                    <span style={{ width: 18, height: 18, borderRadius: '50%', border: `2px solid ${on ? 'var(--cyan)' : 'var(--line)'}`, display: 'grid', placeItems: 'center' }}>
                      {on && <span style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--cyan)' }} />}
                    </span>
                  </button>
                )
              })}
            </div>
          </div>

          {/* 24h note */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '.76rem', color: 'var(--muted)' }}>
            <Clock size={14} /> Your story disappears automatically after 24 hours.
          </div>

          {/* Progress */}
          {busy && (
            <div style={{ padding: '0.75rem', borderRadius: 12, background: 'var(--accent-tint)', border: '1px solid var(--line)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: '.74rem', marginBottom: 6 }}>
                <span style={{ color: 'var(--cyan)' }}>{stage}</span>
                {uploading && !compressing && !saving && (
                  <strong>{uploadProgress.loadedMB} / {uploadProgress.totalMB} MB ({uploadProgress.percent}%)</strong>
                )}
              </div>
              <div style={{ width: '100%', height: 6, borderRadius: 999, background: 'var(--soft-2)', overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${Math.max(3, stagePct)}%`, background: 'var(--cyan)', borderRadius: 999, transition: 'width .25s ease' }} />
              </div>
            </div>
          )}

          {/* Error */}
          {error && (
            <div role="alert" style={{ display: 'flex', gap: 8, alignItems: 'flex-start', padding: '0.7rem 0.8rem', borderRadius: 12, background: 'rgba(239,68,68,.12)', border: '1px solid rgba(239,68,68,.45)', color: 'var(--ink)', fontSize: '.8rem' }}>
              <AlertTriangle size={16} color="#ef4444" style={{ flexShrink: 0, marginTop: 1 }} />
              <span style={{ flex: 1 }}>{error}</span>
            </div>
          )}

          <button
            className="primary-button"
            disabled={!file || busy}
            onClick={handleUpload}
            style={{ position: 'sticky', bottom: 0, zIndex: 4, width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, boxShadow: '0 -14px 16px -6px var(--glass-sheet, var(--paper))' }}
          >
            {busy ? 'Broadcasting Story…' : error && file ? <><RefreshCw size={16} /> Try again</> : <><Rocket size={16} /> Share to Story</>}
          </button>
        </div>
      </div>
    </div>
  )
}

/* =========================================================================
   POST COMPOSER - ONE clean card for both sections:
     - Radar (posts): (inline variant is no longer used - uploads start from the "+" in Shonen)
     - Shonen (reels): drops down after "+" picks a video (variant="sheet")
   Photo / Video buttons open the gallery directly. Title, caption and tags have
   hard limits so a post can never turn into a wall of text.
   Styles: globals.css -> "POST COMPOSER".
   ========================================================================= */
const cleanTag = (raw: string) =>
  raw.replace(/[#\s,;.!?"'`()\[\]{}<>\\/|@$%^&*+=~:]/g, '').slice(0, POST_TAG_MAX_LEN)

// Adds the tags found in `raw` to `existing` - no duplicates, never more than POST_TAGS_MAX.
const mergeTags = (existing: string[], raw: string) => {
  const next = [...existing]
  for (const t of raw.split(/[\s,]+/).map(cleanTag).filter(Boolean)) {
    if (next.length >= POST_TAGS_MAX) break
    if (!next.some((x) => x.toLowerCase() === t.toLowerCase())) next.push(t)
  }
  return next
}

type PostComposerProps = {
  userId: string
  authorName: string
  authorAvatar: string
  variant?: 'inline' | 'sheet'
  videoOnly?: boolean
  initialFile?: File | null
  remixSound?: SoundInfo | null // "Remix this sound": the post reuses this sound instead of its own audio
  coins: CoinsApi
  onAddCoins?: () => void // opens the Wallet (used from the "Not enough coins" state)
  onPosted: () => void
  // Fires right after a successful post if the person turned on "Boost after posting" -
  // opens the same BoostModal used everywhere else, aimed at the post that just got created.
  onRequestBoost?: (post: { id: string }) => void
  onCancel?: () => void
  onActiveChange?: (active: boolean) => void
}

function PostComposer({ userId, authorName, authorAvatar, variant = 'inline', videoOnly = false, initialFile = null, remixSound = null, coins, onAddCoins, onPosted, onRequestBoost, onCancel, onActiveChange }: PostComposerProps) {
  const BOOST_COMING_SOON = !useBoostEnabled()
  const [open, setOpen] = useState(variant === 'sheet' || Boolean(initialFile))
  const [file, setFile] = useState<File | null>(null)
  const [preview, setPreview] = useState('')
  const [isVideo, setIsVideo] = useState(false)
  const [title, setTitle] = useState('')
  const [caption, setCaption] = useState('')
  const [tags, setTags] = useState<string[]>([])
  const [tagDraft, setTagDraft] = useState('')
  const [privacy, setPrivacy] = useState<'public' | 'private'>('public')
  // Boost right after posting: opens the same Boost sheet used everywhere else (see onRequestBoost),
  // aimed at the post the moment it's created - no separate paid "unlock" step in the composer itself.
  const [boostAfterPost, setBoostAfterPost] = useState(false)
  const [moreOpen, setMoreOpen] = useState(false)
  const [error, setError] = useState('')

  // Photos: a post carries up to MAX_POST_PHOTOS. `file` / `preview` is always the FIRST photo,
  // the rest live in extraPhotos. Video: an optional cover image (auto frame unless one is picked).
  const [extraPhotos, setExtraPhotos] = useState<{ file: File; url: string }[]>([])
  const extraPhotosRef = useRef<{ file: File; url: string }[]>([])
  extraPhotosRef.current = extraPhotos
  const [videoRatio, setVideoRatio] = useState(9 / 16)
  // Where this upload goes: a Post (any shape, photos + video) or a Reel (9:16 video) - both play in the Shonen feed.
  const [kind, setKind] = useState<'post' | 'reel'>(videoOnly ? 'reel' : 'post')
  const ratioFor = (k: 'post' | 'reel', vr: number) => (k === 'reel' ? REEL_RATIO : clampPostRatio(vr))
  const coverRatio = ratioFor(kind, videoRatio)
  const coverSrcRef = useRef<File | null>(null) // the picture chosen as cover, so it can be re-cropped if Post/Reel is switched
  const [coverBlob, setCoverBlob] = useState<Blob | null>(null)
  const [coverPreview, setCoverPreview] = useState('')
  const [coverCustom, setCoverCustom] = useState(false)
  const [coverBusy, setCoverBusy] = useState(false)
  const coverUrlRef = useRef('')
  const coverTokenRef = useRef(0)
  const [uploadStep, setUploadStep] = useState('')

  const [compressing, setCompressing] = useState(false)
  const [prepPct, setPrepPct] = useState(0)
  const [uploading, setUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState({ loadedMB: '0', totalMB: '0', percent: 0 })
  const busy = uploading || compressing

  // Optional 60s song (photos only - videos keep their own sound)
  const [audioFile, setAudioFile] = useState<File | null>(null)
  const [audioTotalDuration, setAudioTotalDuration] = useState(0)
  const [audioStartTime, setAudioStartTime] = useState(0)
  const [isPlayingAudioPreview, setIsPlayingAudioPreview] = useState(false)
  const previewAudioRef = useRef<HTMLAudioElement | null>(null)
  const previewTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const captionRef = useRef<HTMLTextAreaElement | null>(null)
  const titleRef = useRef<HTMLInputElement | null>(null)
  const tagInputRef = useRef<HTMLInputElement | null>(null)
  const previewUrlRef = useRef('')

  // While a file is picked, the app silences background reels / feed videos / songs.
  useEffect(() => { onActiveChange?.(Boolean(file)) }, [file])
  useEffect(() => () => {
    onActiveChange?.(false)
    previewAudioRef.current?.pause()
    if (previewTimerRef.current) clearTimeout(previewTimerRef.current)
    if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current)
    extraPhotosRef.current.forEach((x) => URL.revokeObjectURL(x.url))
    if (coverUrlRef.current) URL.revokeObjectURL(coverUrlRef.current)
  }, [])

  const stopAudioPreview = () => {
    previewAudioRef.current?.pause()
    if (previewTimerRef.current) clearTimeout(previewTimerRef.current)
    setIsPlayingAudioPreview(false)
  }

  // ----- multi-photo + video cover helpers -----
  const clearExtraPhotos = () => {
    extraPhotosRef.current.forEach((x) => URL.revokeObjectURL(x.url))
    setExtraPhotos([])
  }
  const setCover = (blob: Blob | null, custom: boolean) => {
    if (coverUrlRef.current) URL.revokeObjectURL(coverUrlRef.current)
    coverUrlRef.current = blob ? URL.createObjectURL(blob) : ''
    setCoverBlob(blob)
    setCoverPreview(coverUrlRef.current)
    setCoverCustom(custom)
  }
  const autoCover = async (f: File, ratio: number = coverRatio) => {
    const token = ++coverTokenRef.current
    coverSrcRef.current = null
    setCoverBusy(true)
    const blob = await captureVideoFrame(f, ratio)
    if (token !== coverTokenRef.current) return
    setCoverBusy(false)
    setCover(blob, false)
  }
  const handleCoverInput = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0]
    e.target.value = ''
    if (!f || !isVideo) return
    if (!f.type.startsWith('image/')) { setError('Pick an image for the cover.'); return }
    const token = ++coverTokenRef.current
    setCoverBusy(true)
    const blob = await cropImageToRatio(f, coverRatio)
    if (token !== coverTokenRef.current) return
    setCoverBusy(false)
    if (!blob) { setError("Couldn't use that image. Try another one."); return }
    setError('')
    coverSrcRef.current = f
    setCover(blob, true)
  }
  const switchKind = async (next: 'post' | 'reel') => {
    if (next === kind || busy) return
    setError('')
    setKind(next)
    if (next === 'reel') {
      // Reels are video only - photos can't go in a reel.
      if (file && !isVideo) removeMedia()
      else if (file && isVideo) clearExtraPhotos()
    }
    if (file && isVideo) {
      const r = ratioFor(next, videoRatio)
      if (coverCustom && coverSrcRef.current) {
        const src = coverSrcRef.current
        const token = ++coverTokenRef.current
        setCoverBusy(true)
        const blob = await cropImageToRatio(src, r)
        if (token !== coverTokenRef.current) return
        setCoverBusy(false)
        if (blob) setCover(blob, true)
      } else {
        void autoCover(file, r)
      }
    }
  }
  const removeExtraAt = (i: number) => {
    const target = extraPhotos[i]
    if (target) URL.revokeObjectURL(target.url)
    setExtraPhotos(extraPhotos.filter((_, k) => k !== i))
  }
  const acceptPhotos = (list: File[]) => {
    setError('')
    if (kind === 'reel') { setError('Reels only support videos. Switch to Post to add photos.'); return }
    const imgs = list.filter((f) => f.type.startsWith('image/') || /\.(jpe?g|png|webp|gif|heic|heif|avif)$/i.test(f.name))
    if (!imgs.length) return
    // Photos can ride along with a video post (swipe: video first, then the photos).
    const hasVideo = Boolean(file) && isVideo
    const startFresh = !file
    const room = MAX_POST_PHOTOS - (hasVideo ? extraPhotos.length : startFresh ? 0 : 1 + extraPhotos.length)
    if (room <= 0) { setError(`You can add up to ${MAX_POST_PHOTOS} photos in one post.`); return }
    const take = imgs.slice(0, room)
    if (startFresh) void acceptFile(take[0])
    const extras = take.slice(startFresh ? 1 : 0).map((f) => ({ file: f, url: URL.createObjectURL(f) }))
    setExtraPhotos((prev) => (startFresh ? extras : [...prev, ...extras]))
    setOpen(true)
    if (imgs.length > room) setError(`Only ${MAX_POST_PHOTOS} photos fit in one post - the extra ones were skipped.`)
  }
  const handlePhotoInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const list = Array.from(e.target.files || [])
    e.target.value = ''
    if (list.length) acceptPhotos(list)
  }
  const removePhotoAt = (i: number) => {
    if (!file) return
    const list = [{ file, url: preview }, ...extraPhotos]
    const next = list.filter((_, k) => k !== i)
    if (!next.length) { removeMedia(); return }
    if (i === 0) {
      // First photo removed: the next one becomes the main preview.
      if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current)
      previewUrlRef.current = next[0].url
      setFile(next[0].file)
      setPreview(next[0].url)
    } else {
      URL.revokeObjectURL(list[i].url)
    }
    setExtraPhotos(next.slice(1))
  }

  const acceptFile = async (f: File) => {
    setError('')
    const looksVideo = f.type.startsWith('video/') || /\.(mp4|mov|webm|m4v|mkv|3gp|3g2|avi|mpg|mpeg|wmv|flv|mts|m2ts)$/i.test(f.name)
    if (kind === 'reel' && !looksVideo) { setError('Reels only support videos. Switch to Post to add photos.'); return }
    let ratio = 9 / 16
    if (looksVideo) {
      const info = await readVideoInfo(f)
      if (info.duration > 65) {
        setError(`That video is ${Math.round(info.duration)}s long. The limit is 60 seconds.`)
        return
      }
      if (info.width > 0 && info.height > 0) ratio = info.width / info.height
    }
    // Picking a video for a POST keeps any photos already chosen - they become the swipe-along photos.
    let carry: { file: File; url: string }[] = []
    if (looksVideo && kind === 'post') {
      carry = isVideo ? extraPhotos : (file ? [{ file, url: URL.createObjectURL(file) }, ...extraPhotos] : [])
    }
    if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current)
    const url = URL.createObjectURL(f)
    previewUrlRef.current = url
    setFile(f)
    setPreview(url)
    setIsVideo(looksVideo)
    setOpen(true)
    if (carry.length) {
      if (!isVideo) extraPhotosRef.current.forEach((x) => URL.revokeObjectURL(x.url))
      setExtraPhotos(carry)
    } else clearExtraPhotos()
    if (looksVideo) { setVideoRatio(ratio); void autoCover(f, ratioFor(kind, ratio)) }
    else { coverTokenRef.current++; setCoverBusy(false); setCover(null, false) }
    // A song only goes with a photo - drop it if the person switched to a video.
    if (looksVideo && audioFile) { stopAudioPreview(); setAudioFile(null); setAudioTotalDuration(0); setAudioStartTime(0) }
  }

  useEffect(() => { if (initialFile) void acceptFile(initialFile) }, [])

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0]
    e.target.value = ''
    if (f) void acceptFile(f)
  }

  const removeMedia = () => {
    stopAudioPreview()
    if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current)
    previewUrlRef.current = ''
    setFile(null)
    setPreview('')
    setIsVideo(false)
    clearExtraPhotos()
    coverTokenRef.current++
    setCoverBusy(false)
    setCover(null, false)
    setAudioFile(null)
    setAudioTotalDuration(0)
    setAudioStartTime(0)
  }

  const resetAll = () => {
    removeMedia()
    setTitle(''); setCaption(''); setTags([]); setTagDraft('')
    setPrivacy('public'); setBoostAfterPost(false); setMoreOpen(false); setError('')
    setOpen(variant === 'sheet')
    if (captionRef.current) captionRef.current.style.height = ''
  }

  const close = () => {
    if (busy) return
    if (variant === 'sheet') { onCancel?.(); return }
    resetAll()
  }

  // ----- tags: chips, max POST_TAGS_MAX -----
  const onTagChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value
    if (!/[\s,]/.test(v)) { setTagDraft(cleanTag(v)); return }
    // A space / comma (typed or pasted) finishes the tag(s) before it.
    const parts = v.split(/[\s,]+/)
    const rest = /[\s,]$/.test(v) ? '' : (parts.pop() ?? '')
    const next = mergeTags(tags, parts.join(' '))
    setTags(next)
    setTagDraft(next.length >= POST_TAGS_MAX ? '' : cleanTag(rest))
  }
  const commitTagDraft = () => {
    if (!tagDraft) return
    setTags(mergeTags(tags, tagDraft))
    setTagDraft('')
  }
  const onTagKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') { e.preventDefault(); commitTagDraft() }
    else if (e.key === 'Backspace' && !tagDraft && tags.length) setTags(tags.slice(0, -1))
  }

  const growCaption = (el: HTMLTextAreaElement) => {
    el.style.height = 'auto'
    el.style.height = `${Math.min(el.scrollHeight, 150)}px`
  }

  // ----- audio -----
  const handleAudioSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0]
    e.target.value = ''
    if (!f) return
    setAudioFile(f)
    const audioEl = new Audio(URL.createObjectURL(f))
    audioEl.onloadedmetadata = () => {
      setAudioTotalDuration(audioEl.duration || 60)
      setAudioStartTime(0)
    }
  }
  const toggleAudioPreview = () => {
    if (!audioFile) return
    if (isPlayingAudioPreview) { stopAudioPreview(); return }
    previewAudioRef.current?.pause()
    const audioEl = new Audio(URL.createObjectURL(audioFile))
    previewAudioRef.current = audioEl
    audioEl.currentTime = audioStartTime
    audioEl.play().catch(() => {})
    setIsPlayingAudioPreview(true)
    if (previewTimerRef.current) clearTimeout(previewTimerRef.current)
    previewTimerRef.current = setTimeout(() => { audioEl.pause(); setIsPlayingAudioPreview(false) }, 60000)
    audioEl.onended = () => setIsPlayingAudioPreview(false)
  }
  const formatSeconds = (sec: number) => {
    const m = Math.floor(sec / 60)
    const s = Math.floor(sec % 60)
    return `${m}:${s.toString().padStart(2, '0')}`
  }

  // ----- submit -----
  const submit = async () => {
    if (busy) return
    setError('')
    const heldMsg = uploadHoldMessage()
    if (heldMsg) { setError(heldMsg); return }
    if (!file) { setOpen(true); setError('Pick a photo or video to post.'); return }
    const cleanTitle = title.trim()
    const finalTags = mergeTags(tags, tagDraft)
    setTags(finalTags)
    setTagDraft('')
    stopAudioPreview()
    setUploading(true)

    try {
      // 1. Compress + upload. Photos go one after another (up to MAX_POST_PHOTOS); a video is
      //    uploaded once, followed by its cover image.
      let imageUrl = ''
      let imageUrls: string[] | undefined
      let thumbnailUrl: string | undefined
      let extraImageUrls: string[] | undefined
      // The ratio saved with the post must match the file that actually gets uploaded and
      // played back - compression can re-encode the video, so re-measure the FINAL file
      // instead of trusting the ratio detected from the original pick. Without this, a
      // mismatched saved ratio permanently boxes the video in the wrong shape (and blocks
      // the feed card's own on-device re-detection, since a stored ratio skips it).
      let finalVideoRatio = videoRatio
      if (isVideo) {
        let optimizedMedia: File
        setPrepPct(0)
        setCompressing(true)
        try { optimizedMedia = await compressVideoIfNeeded(file, (p) => setPrepPct(Math.round(p * 100))) } finally { setCompressing(false) }
        try {
          const finalInfo = await readVideoInfo(optimizedMedia)
          if (finalInfo.width > 0 && finalInfo.height > 0) finalVideoRatio = finalInfo.width / finalInfo.height
        } catch { /* keep the pre-compression ratio as a fallback */ }
        imageUrl = await uploadWithProgress(optimizedMedia, 'post', userId, (loadedMB, totalMB, percent) => {
          setUploadProgress({ loadedMB, totalMB, percent })
        }, cleanTitle)
        if (kind === 'post' && extraPhotos.length) {
          const ex: string[] = []
          try {
            for (let i = 0; i < extraPhotos.length; i++) {
              setUploadStep(`Uploading photo ${i + 1} of ${extraPhotos.length}…`)
              const optimized = await compressMediaFile(extraPhotos[i].file)
              ex.push(await uploadWithProgress(optimized, 'post', userId))
            }
          } catch (err) {
            ex.forEach((u) => void deleteUploadedFile(u))
            void deleteUploadedFile(imageUrl)
            throw err
          }
          extraImageUrls = ex
        }
        // No cover picked (or the automatic one failed earlier): grab one from the video now so a post / reel never has a blank thumbnail.
        let finalCover: Blob | null = coverBlob
        if (!finalCover) { try { finalCover = await captureVideoFrame(file, coverRatio) } catch { finalCover = null } }
        if (finalCover) {
          setUploadStep('Uploading cover…')
          try {
            thumbnailUrl = await uploadWithProgress(new File([finalCover], `cover_${Date.now()}.jpg`, { type: 'image/jpeg' }), 'post', userId)
          } catch { /* the video still posts fine without a cover */ }
        }
      } else {
        const photos: File[] = [file, ...extraPhotos.map((x) => x.file)]
        const urls: string[] = []
        try {
          for (let i = 0; i < photos.length; i++) {
            if (photos.length > 1) setUploadStep(`Uploading photo ${i + 1} of ${photos.length}…`)
            const optimized = await compressMediaFile(photos[i])
            const u = await uploadWithProgress(optimized, 'post', userId, (loadedMB, totalMB, percent) => {
              setUploadProgress({ loadedMB, totalMB, percent: Math.round(((i + percent / 100) / photos.length) * 100) })
            })
            urls.push(u)
          }
        } catch (err) {
          urls.forEach((u) => void deleteUploadedFile(u)) // don't leave half a post's photos behind
          throw err
        }
        imageUrl = urls[0]
        if (urls.length > 1) imageUrls = urls
      }

      // 2. Optional 60s trimmed song (photos only)
      let audioUrl: string | undefined
      let audioTitle: string | undefined
      if (audioFile && !isVideo && !remixSound) {
        const trimmedAudioBlob = await trimAudioTo60s(audioFile, audioStartTime, 60)
        audioUrl = await uploadWithProgress(trimmedAudioBlob, 'audio', userId)
        audioTitle = audioFile.name.replace(/\.[^/.]+$/, '').slice(0, 35)
      }

      // 3. Save to Firestore
      const docRef = await addDoc(collection(firestore, 'posts'), {
        authorId: userId,
        authorName,
        authorAvatar,
        imageUrl,
        ...(imageUrls ? { imageUrls } : {}),
        ...(thumbnailUrl ? { thumbnailUrl } : {}),
        ...(extraImageUrls ? { extraImageUrls } : {}),
        // Always the video's own true width/height (clamped to the feed card's supported
        // range) - never the cover-crop ratio, which for a Reel is a fixed 9:16 regardless
        // of the video's real shape and would badly letterbox a landscape/square clip.
        ...(isVideo && finalVideoRatio > 0 ? { mediaRatio: Math.round(clampPostRatio(finalVideoRatio) * 10000) / 10000 } : {}),
        postKind: isVideo && kind === 'reel' ? 'reel' : 'post',
        mediaType: isVideo ? 'video' : 'image',
        caption: caption.trim().slice(0, POST_CAPTION_MAX),
        title: cleanTitle.slice(0, POST_TITLE_MAX),
        tags: finalTags.map((t) => `#${t}`),
        likes: [],
        commentCount: 0,
        privacy,
        viewers: {},
        views: 0,
        ...(remixSound
          // Remix: reuse the original sound (same file url = same sound). A remix VIDEO mutes its own audio.
          ? { audioUrl: remixSound.playUrl, audioTitle: remixSound.title, soundOwnerName: remixSound.owner, remixOf: remixSound.sourceId, audioShared: true, ...(isVideo ? { muteOriginal: true } : {}) }
          : audioUrl ? { audioUrl, audioTitle } : {}),
        createdAt: serverTimestamp(),
      })

      setUploading(false)
      setUploadStep('')
      setUploadProgress({ loadedMB: '0', totalMB: '0', percent: 0 })
      const shouldBoost = boostAfterPost && !BOOST_COMING_SOON
      if (variant === 'inline') resetAll()
      onPosted()
      if (shouldBoost) onRequestBoost?.({ id: docRef.id })
    } catch (postErr) {
      // Show the real reason (server error / R2 status / "Failed to fetch" for a CORS
      // or network block) so a failing upload can actually be diagnosed.
      const reason = postErr instanceof Error && postErr.message ? ` (${postErr.message.slice(0, 140)})` : ''
      setError(`Upload failed${reason}. Check your connection and try again.`)
      setUploading(false)
      setUploadStep('')
      setCompressing(false)
    }
  }

  const showPhoto = kind !== 'reel'
  const audioAllowed = Boolean(file) && !isVideo && !remixSound
  // True when Post will use coins (link unlocked AND something typed in it) - shown on the Post button.

  return (
    <div className={`card pc-card ${variant === 'sheet' ? 'is-sheet' : ''} ${open ? 'is-open' : ''}`}>
      {variant === 'sheet' && (
        <header className="fp-header">
          <button type="button" className="icon-button fp-back" onClick={close} disabled={busy} aria-label="Back"><ArrowLeft size={20} /></button>
          <h2 className="fp-title">{kind === 'reel' ? 'New reel' : 'New post'}</h2>
        </header>
      )}
      <div className="pc-top">
        <Avatar src={authorAvatar || ''} name={authorName || 'You'} size="md" showStatus={false} />
        <div className="pc-fields">
          {open && !videoOnly && (
            <div className="pc-kind" role="group" aria-label="Upload type">
              <button type="button" className={kind === 'post' ? 'on' : ''} disabled={busy} onClick={() => void switchKind('post')}>
                <ImageIcon size={16} /> Post
              </button>
              <button type="button" className={kind === 'reel' ? 'on' : ''} disabled={busy} onClick={() => void switchKind('reel')}>
                <Video size={16} /> Reel
              </button>
            </div>
          )}
          {open && (
            <div className="pc-row">
              <input
                ref={titleRef}
                className="pc-title"
                type="text"
                value={title}
                maxLength={POST_TITLE_MAX}
                disabled={busy}
                placeholder="Title (optional)"
                onChange={(e) => setTitle(e.target.value)}
              />
              <span className={`pc-count ${title.length >= POST_TITLE_MAX ? 'is-max' : ''}`}>{title.length}/{POST_TITLE_MAX}</span>
            </div>
          )}
          <textarea
            ref={captionRef}
            className="pc-caption"
            rows={open ? 1 : 2}
            value={caption}
            maxLength={POST_CAPTION_MAX}
            disabled={busy}
            placeholder={open ? 'Add a caption (optional)…' : "What's on your mind?"}
            onFocus={() => setOpen(true)}
            onChange={(e) => { setCaption(e.target.value); growCaption(e.target) }}
          />
          {open && (
            <span className={`pc-count pc-count-end ${caption.length >= POST_CAPTION_MAX ? 'is-max' : ''}`}>{caption.length}/{POST_CAPTION_MAX}</span>
          )}
        </div>
        {(open && variant === 'inline') && (
          <button type="button" className="pc-close" onClick={close} disabled={busy} aria-label="Discard draft">
            <X size={16} />
          </button>
        )}
      </div>

      {open && (
        <>
          <div className="pc-tags" onClick={() => tagInputRef.current?.focus()}>
            {tags.map((t) => (
              <span key={t} className="pc-chip">
                #{t}
                <button type="button" aria-label={`Remove ${t}`} disabled={busy} onClick={(e) => { e.stopPropagation(); setTags(tags.filter((x) => x !== t)) }}>
                  <X size={14} />
                </button>
              </span>
            ))}
            {tags.length < POST_TAGS_MAX && (
              <span className="pc-tag-input">
                <span aria-hidden="true">#</span>
                <input
                  ref={tagInputRef}
                  type="text"
                  value={tagDraft}
                  maxLength={POST_TAG_MAX_LEN + 1}
                  disabled={busy}
                  autoCapitalize="none"
                  autoCorrect="off"
                  placeholder={tags.length === 0 ? 'Add up to 4 tags (optional)' : 'Add tag'}
                  onChange={onTagChange}
                  onKeyDown={onTagKeyDown}
                  onBlur={commitTagDraft}
                />
              </span>
            )}
            <span className={`pc-count pc-tags-count ${tags.length >= POST_TAGS_MAX ? 'is-max' : ''}`}>{tags.length}/{POST_TAGS_MAX}</span>
          </div>

          {variant === 'sheet' && (
            <div className="pc-pick">
              {showPhoto && (
                <label className={`pc-pick-btn ${busy ? 'is-disabled' : ''}`}>
                  <ImageIcon size={18} color="var(--blue)" /> <span>Photo</span>
                  <input type="file" accept="image/*" multiple className="pc-file-input" disabled={busy} onChange={handlePhotoInput} />
                </label>
              )}
              <label className={`pc-pick-btn ${busy ? 'is-disabled' : ''}`}>
                <Video size={18} color="var(--green)" /> <span>{file && isVideo ? 'Change video' : 'Video'}</span>
                <input type="file" accept="video/*" className="pc-file-input" disabled={busy} onChange={handleFileInput} />
              </label>
            </div>
          )}

          {preview && (
            <div className={`pc-media${isVideo ? (kind === 'reel' ? ' is-reel' : ' is-post') : ''}`} style={isVideo && kind === 'post' ? { aspectRatio: String(coverRatio) } : undefined}>
              {isVideo ? (
                <video preload="metadata" src={preview} controls controlsList="nodownload noremoteplayback" disablePictureInPicture onContextMenu={(e) => e.preventDefault()} playsInline />
              ) : (
                <img decoding="async" src={preview} alt="" />
              )}
              {!busy && (
                <button type="button" className="pc-media-x" onClick={removeMedia} aria-label="Remove media">
                  <X size={16} />
                </button>
              )}
            </div>
          )}

          {preview && !isVideo && (
            <div className="pc-photos" aria-label="Photos in this post">
              {[{ file: file as File, url: preview }, ...extraPhotos].map((ph, i) => (
                <div key={ph.url} className="pc-photo">
                  <img decoding="async" src={ph.url} alt="" />
                  {!busy && (
                    <button type="button" className="pc-photo-x" onClick={() => removePhotoAt(i)} aria-label={`Remove photo ${i + 1}`}>
                      <X size={14} />
                    </button>
                  )}
                </div>
              ))}
              {1 + extraPhotos.length < MAX_POST_PHOTOS && !busy && (
                <label className="pc-photo pc-photo-add">
                  <Plus size={18} />
                  <span>{1 + extraPhotos.length}/{MAX_POST_PHOTOS}</span>
                  <input type="file" accept="image/*" multiple className="pc-file-input" onChange={handlePhotoInput} />
                </label>
              )}
            </div>
          )}

          {preview && isVideo && kind === 'post' && (
            <div className="pc-photos" aria-label="Photos added with this video">
              {extraPhotos.map((ph, i) => (
                <div key={ph.url} className="pc-photo">
                  <img decoding="async" src={ph.url} alt="" />
                  {!busy && (
                    <button type="button" className="pc-photo-x" onClick={() => removeExtraAt(i)} aria-label={`Remove photo ${i + 1}`}>
                      <X size={14} />
                    </button>
                  )}
                </div>
              ))}
              {extraPhotos.length < MAX_POST_PHOTOS && !busy && (
                <label className="pc-photo pc-photo-add">
                  <Plus size={18} />
                  <span>Photos</span>
                  <input type="file" accept="image/*" multiple className="pc-file-input" onChange={handlePhotoInput} />
                </label>
              )}
            </div>
          )}

          {preview && isVideo && (
            <div className="pc-cover">
              <div className={`pc-cover-thumb${kind === 'reel' ? ' is-reel' : ''}`} style={{ aspectRatio: String(coverRatio) }}>
                {coverPreview ? <img decoding="async" src={coverPreview} alt="Video cover" /> : <span>{coverBusy ? 'Making cover…' : 'No cover'}</span>}
              </div>
              <div className="pc-cover-info">
                <strong>Cover image</strong>
                <small>{kind === 'reel' ? 'Reel cover (9:16) - shown in Reels and your profile before the video plays.' : 'Post cover - same shape as your video, shown in Shonen and Discover before it plays.'}</small>
                {!busy && (
                  <div className="pc-cover-actions">
                    <label className="text-button primary">
                      <ImageIcon size={14} /> {coverCustom ? 'Change cover' : 'Choose cover'}
                      <input type="file" accept="image/*" className="pc-file-input" onChange={(e) => void handleCoverInput(e)} />
                    </label>
                    {coverCustom && (
                      <button type="button" className="text-button" onClick={() => file && void autoCover(file)}>Use video frame</button>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          <button type="button" className={`pc-more-toggle ${moreOpen ? 'is-open' : ''}`} onClick={() => setMoreOpen((o) => !o)} aria-expanded={moreOpen}>
            <ChevronRight size={16} /> More options
          </button>

          {moreOpen && (
            <div className="pc-options">
              <div className="pc-opt">
                <span className="pc-opt-label">Who can see this</span>
                <div className="pc-seg" role="group" aria-label="Privacy">
                  <button type="button" className={privacy === 'public' ? 'on' : ''} disabled={busy} onClick={() => setPrivacy('public')}>Everyone</button>
                  <button type="button" className={privacy === 'private' ? 'on' : ''} disabled={busy} onClick={() => setPrivacy('private')}>Allies only</button>
                </div>
              </div>

              {/* Boost after posting: opens the same paid Boost sheet used everywhere else,
                  aimed at this post the moment it's created (see onRequestBoost in submit()). */}
              <div className="pc-opt">
                <span className="pc-opt-label pc-opt-label-row">
                  <span><Rocket size={14} /> Boost after posting</span>
                </span>
                <button
                  type="button"
                  className={`pc-locked${boostAfterPost && !BOOST_COMING_SOON ? ' is-on' : ''}`}
                  disabled={busy || BOOST_COMING_SOON}
                  onClick={() => { if (!BOOST_COMING_SOON) setBoostAfterPost((v) => !v) }}
                  aria-pressed={boostAfterPost && !BOOST_COMING_SOON}
                >
                  {BOOST_COMING_SOON ? <Lock size={16} /> : <Rocket size={16} />}
                  <span>{BOOST_COMING_SOON ? 'Coming soon' : boostAfterPost ? 'Boost sheet opens right after you post' : 'Turn on to boost this right after posting'}</span>
                  <i className="pc-switch" aria-hidden="true" />
                </button>
              </div>

              {remixSound && (
                <div className="pc-opt">
                  <span className="pc-opt-label pc-opt-label-row">
                    <span><Sparkles size={14} /> Remixing a sound</span>
                  </span>
                  <div className="pc-audio-name" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Music size={14} />
                    <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{remixSound.title}{remixSound.owner ? ` • ${remixSound.owner}` : ''}</span>
                  </div>
                  <small style={{ color: 'var(--muted)', fontSize: '.72rem' }}>{isVideo ? 'Your video\'s own sound is replaced by this sound.' : 'This sound plays on your photo.'}</small>
                </div>
              )}

              {audioAllowed && (
                <div className="pc-opt">
                  <span className="pc-opt-label pc-opt-label-row">
                    <span><Music size={14} /> Song (optional, 60s)</span>
                    {audioFile && (
                      <button type="button" className="pc-link-danger" onClick={() => { stopAudioPreview(); setAudioFile(null); setAudioTotalDuration(0) }}>Remove</button>
                    )}
                  </span>
                  {!audioFile ? (
                    <label className="btn-choose-audio">
                      <Music size={14} /> Choose song from files
                      <input type="file" accept="audio/*" className="pc-file-input" onChange={handleAudioSelect} />
                    </label>
                  ) : (
                    <div className="pc-audio">
                      <div className="pc-audio-name">
                        <span>{audioFile.name}</span>
                        <button type="button" className="text-button primary" style={{ padding: '2px 8px', fontSize: '.68rem' }} onClick={toggleAudioPreview}>
                          {isPlayingAudioPreview ? <Pause size={14} /> : <Play size={14} />}
                          {isPlayingAudioPreview ? 'Pause' : 'Test'}
                        </button>
                      </div>
                      <div className="pc-audio-times">
                        <span>Start {formatSeconds(audioStartTime)}</span>
                        <span>to {formatSeconds(Math.min(audioStartTime + 60, audioTotalDuration))}</span>
                      </div>
                      <input
                        type="range"
                        min={0}
                        max={Math.max(0, audioTotalDuration - 10)}
                        step={1}
                        value={audioStartTime}
                        onChange={(e) => {
                          const val = Number(e.target.value)
                          setAudioStartTime(val)
                          if (previewAudioRef.current) previewAudioRef.current.currentTime = val
                        }}
                        style={{ width: '100%', accentColor: 'var(--accent)', cursor: 'pointer' }}
                      />
                    </div>
                  )}
                  <p className="pc-music-note">Use your own music or copyright-free / commercial-use music only. Copyrighted songs can get your post removed and your account restricted.</p>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {compressing && <div className="pc-status">Getting your video ready…{prepPct > 0 ? ` ${prepPct}%` : ''}</div>}
      {uploading && !compressing && (
        <div className="pc-status">
          <div className="pc-status-row">
            <span>{uploadStep || 'Uploading…'}</span>
            <strong>{uploadProgress.loadedMB} / {uploadProgress.totalMB} MB ({uploadProgress.percent}%)</strong>
          </div>
          <div className="pc-progress"><div style={{ width: `${uploadProgress.percent}%` }} /></div>
        </div>
      )}
      {error && <p className="pc-error" role="alert">{error}</p>}

      <div className="composer-card-bar">
        {variant !== 'sheet' && showPhoto && (
          <label className={`composer-card-tool ${busy ? 'is-disabled' : ''}`}>
            <ImageIcon size={18} color="var(--blue)" /> <span className="composer-card-tool-label">Photo</span>
            <input type="file" accept="image/*" multiple className="pc-file-input" disabled={busy} onChange={handlePhotoInput} />
          </label>
        )}
        {variant !== 'sheet' && (
          <label className={`composer-card-tool ${busy ? 'is-disabled' : ''}`}>
            <Video size={18} color="var(--green)" /> <span className="composer-card-tool-label">{videoOnly && file ? 'Change video' : 'Video'}</span>
            <input type="file" accept="video/*" className="pc-file-input" disabled={busy} onChange={handleFileInput} />
          </label>
        )}
        <button type="button" className="composer-card-post" disabled={busy} onClick={() => void submit()}>
          {(compressing || uploading) ? 'Posting…' : boostAfterPost && !BOOST_COMING_SOON ? (
            <span className="pc-post-cost"><Rocket size={14} /> Post & Boost</span>
          ) : 'Post'}
        </button>
      </div>
    </div>
  )
}

/* =========================================================================
   ACCOUNT SETUP MODAL (first Google sign-in → create your Animora profile)
   ========================================================================= */
function UsernameSetupModal({ googleUser, busy, onCreate, onUploadAvatar, onSignOut }: any) {
  const [name, setName] = useState(googleUser?.displayName || '')
  const [username, setUsername] = useState('')
  const [bio, setBio] = useState('Connected to the anime multiverse.')
  const [gender, setGender] = useState<'male' | 'female'>('male')
  const [avatarFile, setAvatarFile] = useState<File | null>(null)
  const [avatarPreview, setAvatarPreview] = useState(
    googleUser?.photoURL || presetForSeed(googleUser?.displayName || googleUser?.email || 'Traveler')
  )
  const [dob, setDob] = useState('')
  const [refCode, setRefCode] = useState(() => { try { return normalizeReferralCode(localStorage.getItem('animora_ref')) } catch { return '' } })
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [refAuto, setRefAuto] = useState(false)

  // No code from the link/storage (typical after a Play Store install)? Ask the server whether an invite link was
  // opened recently from this network and auto-fill it. The person can still edit or clear it.
  useEffect(() => {
    if (refCode || !googleUser?.getIdToken) return
    let cancelled = false
    ;(async () => {
      try {
        const res = await fetch('/api/referral', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${await googleUser.getIdToken()}` },
          body: JSON.stringify({ action: 'hint' }),
        })
        const d = await res.json().catch(() => ({}))
        const c = normalizeReferralCode(d?.code)
        if (!cancelled && c) { setRefCode((cur: string) => cur || c); setRefAuto(true) }
      } catch { /* optional */ }
    })()
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [googleUser?.uid])
  useEffect(() => { if (refCode && !refAuto) { try { if (normalizeReferralCode(localStorage.getItem('animora_ref')) === refCode) setRefAuto(true) } catch { /* ignore */ } } }, [refCode, refAuto])

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    setError('')

    const dobParts = parseDob(dob)
    if (!dobParts) {
      setError('Please enter your date of birth.')
      return
    }
    if (ageFromDob(dobParts) < MIN_AGE) {
      setError(`Animora is only for people aged ${MIN_AGE} or older, so you can't create an account.`)
      return
    }

    const cleanName = name.trim()
    const cleanUsername = username.trim().replace(/^@/, '')

    if (cleanName.length < 2) {
      setError('Please enter a display name (2+ characters).')
      return
    }
    const usernameRule = checkUsername(cleanUsername)
    if (!usernameRule.ok) {
      setError(usernameRule.message)
      return
    }
    if (cleanUsername.length <= PREMIUM_USERNAME_MAX_LENGTH) {
      setError(`Usernames with 1-${PREMIUM_USERNAME_MAX_LENGTH} characters are premium - you can get one after signing up (Edit Identity HUD). For now pick a username with ${PREMIUM_USERNAME_MAX_LENGTH + 1} or more characters.`)
      return
    }

    setSaving(true)
    try {
      let finalAvatar = avatarPreview
      if (avatarFile) {
        finalAvatar = await onUploadAvatar(avatarFile)
      }
      const result = await onCreate({ name: cleanName, username: cleanUsername, bio, gender, avatar: finalAvatar, dob, refCode })
      if (result && result.ok === false) {
        setError(result.error || 'Could not create your account. Please try again.')
      }
    } catch (err: any) {
      setError(friendlyError(err, 'Could not create your account. Please check your connection and try again.'))
    } finally {
      setSaving(false)
    }
  }

  const isBusy = saving || busy

  // Email/password accounts must verify their email before they can finish setting up their
  // profile - Google/other OAuth sign-ins arrive already verified by the provider, so this
  // only ever gates the email+password signup path. Fixes accounts being fully usable while
  // registered with a fake/never-checked inbox.
  const isPasswordAccount = !!googleUser?.providerData?.some((p: any) => p?.providerId === 'password')
  const [verified, setVerified] = useState(!!googleUser?.emailVerified)
  const [resendBusy, setResendBusy] = useState(false)
  const [resendMsg, setResendMsg] = useState('')
  const [checkingVerified, setCheckingVerified] = useState(false)
  const [resendUntil, setResendUntil] = useState(0)
  const [resendNow, setResendNow] = useState(() => Date.now())
  const resendLeft = Math.max(0, Math.ceil((resendUntil - resendNow) / 1000))

  // Detect the verification by itself: when the person comes back from their mail app (tab/app becomes visible) and every 4s while waiting,
  // so they no longer have to press Refresh (many never did and thought sign-up was stuck).
  useEffect(() => {
    if (!isPasswordAccount || verified || !googleUser) return
    let stop = false
    const check = async () => {
      try {
        await googleUser.reload()
        if (!stop && googleUser.emailVerified) {
          try { await googleUser.getIdToken(true) } catch { /* retried at signup */ }
          setVerified(true)
        }
      } catch { /* offline: try again on the next tick */ }
    }
    const iv = window.setInterval(() => { if (document.visibilityState === 'visible') void check() }, 4000)
    const onVis = () => { if (document.visibilityState === 'visible') void check() }
    document.addEventListener('visibilitychange', onVis)
    window.addEventListener('focus', onVis)
    return () => { stop = true; window.clearInterval(iv); document.removeEventListener('visibilitychange', onVis); window.removeEventListener('focus', onVis) }
  }, [isPasswordAccount, verified, googleUser])
  useEffect(() => {
    if (resendUntil <= Date.now()) return
    const t = window.setInterval(() => setResendNow(Date.now()), 1000)
    return () => window.clearInterval(t)
  }, [resendUntil])

  if (isPasswordAccount && !verified) {
    return (
      <div className="modal-card" style={{ maxWidth: 420, width: '100%', padding: '2rem 1.5rem' }}>
        <div className="brand-mark" style={{ justifyContent: 'center', marginBottom: '1rem' }}>
          <span style={{ background: 'transparent', boxShadow: 'none', padding: 0, width: 40, height: 40 }}>
            <img decoding="async" src="/brand-logo.webp" alt="" style={{ width: 40, height: 40, borderRadius: '10px', objectFit: 'cover' }} />
          </span>
          <strong>Ani<span>mora</span> <em className="brand-social-tag">Social</em></strong>
        </div>
        <p className="eyebrow" style={{ textAlign: 'center' }}>VERIFY YOUR EMAIL</p>
        <h1 style={{ fontSize: '1.3rem', margin: '0.25rem 0 1rem', textAlign: 'center', fontWeight: 700 }}>
          Confirm it's really you
        </h1>
        <p style={{ fontSize: '.85rem', color: 'var(--muted)', lineHeight: 1.6, textAlign: 'center', margin: '0 0 1.25rem' }}>
          We sent a verification link to <strong>{googleUser?.email}</strong>. Open it (check Spam / Promotions too) and come back here - this screen continues by itself. Wrong email? Sign out and sign up again.
        </p>
        {resendMsg && <p style={{ fontSize: '.8rem', color: 'var(--cyan)', textAlign: 'center', marginBottom: '0.75rem' }}>{resendMsg}</p>}
        <div style={{ display: 'grid', gap: '0.5rem' }}>
          <button
            className="primary-button"
            style={{ width: '100%' }}
            disabled={checkingVerified}
            onClick={async () => {
              setCheckingVerified(true)
              try {
                await googleUser.reload()
                // reload() updates user.emailVerified, but the cached ID token still carries the old
                // email_verified=false claim; the server rules read that claim, so force a fresh token.
                if (googleUser.emailVerified) { try { await googleUser.getIdToken(true) } catch { /* retried at signup */ } }
                if (googleUser.emailVerified) setVerified(true)
                else setResendMsg("Still not verified - check your inbox (and spam folder).")
              } catch {
                setResendMsg('Could not check right now. Please try again.')
              } finally {
                setCheckingVerified(false)
              }
            }}
          >
            {checkingVerified ? 'Checking…' : "I've verified - Refresh"}
          </button>
          <button
            className="text-button"
            style={{ justifyContent: 'center' }}
            disabled={resendBusy || resendLeft > 0}
            onClick={async () => {
              setResendBusy(true)
              setResendMsg('')
              try {
                await sendEmailVerification(googleUser)
                setResendMsg('Verification email sent. Check your inbox and spam folder.')
                setResendUntil(Date.now() + 60_000); setResendNow(Date.now())
              } catch (e: any) {
                if (e?.code === 'auth/too-many-requests') { setResendMsg('Too many emails requested. Please wait a few minutes, then try again.'); setResendUntil(Date.now() + 120_000); setResendNow(Date.now()) }
                else setResendMsg('Could not send the email right now. Please try again shortly.')
              } finally {
                setResendBusy(false)
              }
            }}
          >
            {resendBusy ? 'Sending…' : resendLeft > 0 ? `Resend in ${resendLeft}s` : 'Resend Verification Email'}
          </button>
          <button className="text-button danger" style={{ justifyContent: 'center' }} onClick={onSignOut}>
            Sign out
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="modal-card" style={{ maxWidth: 420, width: '100%', padding: '2rem 1.5rem' }}>
      <div className="brand-mark" style={{ justifyContent: 'center', marginBottom: '1rem' }}>
        <span style={{ background: 'transparent', boxShadow: 'none', padding: 0, width: 40, height: 40 }}>
          <img decoding="async" src="/brand-logo.webp" alt="" style={{ width: 40, height: 40, borderRadius: '10px', objectFit: 'cover' }} />
        </span>
        <strong>Ani<span>mora</span> <em className="brand-social-tag">Social</em></strong>
      </div>
      <p className="eyebrow" style={{ textAlign: 'center' }}>ONE LAST STEP</p>
      <h1 style={{ fontSize: '1.4rem', margin: '0.25rem 0 1.25rem', textAlign: 'center', fontWeight: 700 }}>
        Set up your account
      </h1>

      <form onSubmit={submit} style={{ display: 'grid', gap: '0.75rem', textAlign: 'left' }}>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '0.25rem' }}>
          <div style={{ position: 'relative' }}>
            <Avatar src={avatarPreview} name={name || 'Traveler'} size="lg" showStatus={false} />
            <label style={{ position: 'absolute', bottom: -2, right: -2, background: 'var(--ink)', borderRadius: '50%', width: 26, height: 26, display: 'grid', placeItems: 'center', cursor: 'pointer' }}>
              <Edit3 size={14} color="#fff" />
              <input
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={(e) => {
                  const f = e.target.files?.[0]
                  if (f) { setAvatarFile(f); setAvatarPreview(URL.createObjectURL(f)) }
                }}
              />
            </label>
          </div>
        </div>

        <AvatarPicker value={avatarPreview} onSelect={(url) => { setAvatarFile(null); setAvatarPreview(url) }} />

        <label style={{ fontSize: '.76rem', color: 'var(--muted)' }}>
          Display Name
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={40}
            placeholder="Kaito Shin"
            style={{ width: '100%', border: '1px solid var(--line)', borderRadius: 12, padding: '0.75rem 0.75rem', background: 'var(--soft)', color: 'var(--ink)', marginTop: 4 }}
          />
        </label>

        <label style={{ fontSize: '.76rem', color: 'var(--muted)' }}>
          Choose a Username
          <input
            value={username}
            onChange={(e) => setUsername(e.target.value.replace(/\s/g, '').toLowerCase())}
            maxLength={20}
            placeholder="kaito_shin"
            autoCapitalize="none"
            autoCorrect="off"
            style={{ width: '100%', border: '1px solid var(--line)', borderRadius: 12, padding: '0.75rem 0.75rem', background: 'var(--soft)', color: 'var(--ink)', marginTop: 4 }}
          />
          <small style={{ display: 'block', marginTop: 4, fontSize: '.68rem' }}>
            5-20 characters (letters, numbers, "_" or "."). Names of 1-4 characters are premium: you can buy one once, after signing up, with Google Play.
          </small>
        </label>

        <label style={{ fontSize: '.76rem', color: 'var(--muted)' }}>
          Bio (optional)
          <textarea
            value={bio}
            onChange={(e) => setBio(e.target.value)}
            maxLength={160}
            rows={2}
            style={{ width: '100%', border: '1px solid var(--line)', borderRadius: 12, padding: '0.75rem 0.75rem', background: 'var(--soft)', color: 'var(--ink)', marginTop: 4, resize: 'none', fontFamily: 'inherit' }}
          />
        </label>

        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button type="button" className={`text-button ${gender === 'male' ? 'primary' : ''}`} style={{ flex: 1, justifyContent: 'center' }} onClick={() => setGender('male')}>
            Male
          </button>
          <button type="button" className={`text-button ${gender === 'female' ? 'primary' : ''}`} style={{ flex: 1, justifyContent: 'center' }} onClick={() => setGender('female')}>
            Female
          </button>
        </div>

        <label style={{ fontSize: '.76rem', color: 'var(--muted)' }}>
          Date of Birth
          <input
            type="date"
            value={dob}
            onChange={(e) => setDob(e.target.value)}
            max={new Date().toISOString().slice(0, 10)}
            min="1900-01-01"
            required
            style={{ width: '100%', border: '1px solid var(--line)', borderRadius: 12, padding: '0.75rem 0.75rem', background: 'var(--soft)', color: 'var(--ink)', marginTop: 4 }}
          />
          <small style={{ display: 'block', marginTop: 4, fontSize: '.68rem' }}>
            You must be {MIN_AGE} or older. It is used only to check your age, is never shown on your profile, and can&apos;t be changed later. See our <a href="/privacy" target="_blank" rel="noreferrer">Privacy Policy</a>.
          </small>
        </label>

        <label style={{ fontSize: '.76rem', color: 'var(--muted)' }}>
          Referral code (optional)
          <input
            value={refCode}
            onChange={(e) => setRefCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 8))}
            maxLength={8}
            placeholder="ABCD2345"
            autoCapitalize="characters"
            autoCorrect="off"
            style={{ width: '100%', border: '1px solid var(--line)', borderRadius: 12, padding: '0.75rem 0.75rem', background: 'var(--soft)', color: 'var(--ink)', marginTop: 4, letterSpacing: '.12em' }}
          />
          {refAuto && refCode && <small style={{ display: 'block', marginTop: 4, fontSize: '.68rem', color: 'var(--cyan)' }}>Referral code filled in automatically - you&apos;ll get a welcome coin!</small>}
        </label>

        {error && <p style={{ color: 'var(--danger)', fontSize: '.76rem', margin: 0, textAlign: 'center' }}>{error}</p>}

        <button className="primary-button" style={{ width: '100%' }} disabled={isBusy}>
          {isBusy ? 'Creating your account…' : 'Create Account'}
        </button>

        <button type="button" className="text-button" style={{ width: '100%', justifyContent: 'center' }} onClick={onSignOut} disabled={isBusy}>
          Not you? Sign out
        </button>
      </form>
    </div>
  )
}

/* =========================================================================
   AGE GATE for accounts that existed before the date-of-birth check was added.
   Blocking: no DOB = no app. Under 13 = account is closed (the server also bans the profile).
   ========================================================================= */
function AgeGateModal({ underAge, onSubmit, onSignOut }: { underAge: boolean; onSubmit: (dob: string) => Promise<{ ok: true } | { ok: false; error: string }>; onSignOut: () => void }) {
  const [dob, setDob] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const go = async (e: FormEvent) => {
    e.preventDefault()
    setError('')
    const parts = parseDob(dob)
    if (!parts) { setError('Please enter a valid date of birth.'); return }
    setBusy(true)
    const r = await onSubmit(dob)
    setBusy(false)
    if (!r.ok) setError(r.error)
  }
  return (
    <div className="modal-card" style={{ maxWidth: 420, width: '100%', padding: '2rem 1.5rem', textAlign: 'center' }}>
      <p className="eyebrow">{underAge ? 'AGE REQUIREMENT' : 'ONE QUICK STEP'}</p>
      <h1 style={{ fontSize: '1.3rem', margin: '0.25rem 0 1rem', fontWeight: 700 }}>{underAge ? `You must be ${MIN_AGE} or older` : 'Confirm your date of birth'}</h1>
      {underAge ? (
        <p style={{ fontSize: '.85rem', color: 'var(--muted)', lineHeight: 1.6, margin: '0 0 1.25rem' }}>
          Animora is only for people aged {MIN_AGE} or older, so this account can&apos;t be used. If you entered your date of birth by mistake, contact us from the Contact page.
        </p>
      ) : (
        <form onSubmit={go} style={{ display: 'grid', gap: '0.75rem', textAlign: 'left' }}>
          <p style={{ fontSize: '.85rem', color: 'var(--muted)', lineHeight: 1.6, margin: 0 }}>
            To keep Animora safe we now ask everyone for their date of birth. You must be {MIN_AGE} or older. It isn&apos;t shown on your profile and can&apos;t be changed later.
          </p>
          <input type="date" value={dob} onChange={(e) => setDob(e.target.value)} max={new Date().toISOString().slice(0, 10)} min="1900-01-01" required
            style={{ width: '100%', border: '1px solid var(--line)', borderRadius: 12, padding: '0.75rem 0.75rem', background: 'var(--soft)', color: 'var(--ink)' }} />
          {error && <p style={{ color: 'var(--danger)', fontSize: '.76rem', margin: 0, textAlign: 'center' }}>{error}</p>}
          <button className="primary-button" style={{ width: '100%' }} disabled={busy}>{busy ? 'Checking…' : 'Continue'}</button>
        </form>
      )}
      <button className="text-button danger" style={{ justifyContent: 'center', width: '100%', marginTop: '0.5rem' }} onClick={onSignOut}>Sign out</button>
    </div>
  )
}

/* =========================================================================
   PROFILE EDIT MODAL (With Gender Selection)
   ========================================================================= */
/* =========================================================================
   DELETE ACCOUNT MODAL - two paths:
   - Temporary: just deactivates the account (nothing is erased); logging
     back in at any point automatically reactivates it.
   - Permanent: schedules real deletion; logging back in within 3 days
     cancels it, otherwise everything (posts + profile) is erased for good
     the next time the account is touched after the window closes.
   ========================================================================= */
function ReportModal({ target, busy, onClose, onSubmit }: any) {
  const REASONS = ['Spam', 'Harassment or bullying', 'Hate speech', 'Nudity or sexual content', 'Violence or dangerous content', 'Misinformation', 'Other']
  const [reason, setReason] = useState(REASONS[0])
  const [note, setNote] = useState('')

  return (
    // zIndex forced above .chat-view (80): reporting a message is opened from inside the
    // full-screen chat, and without this the modal rendered behind it (invisible until the
    // chat itself closed) since both shared the same stacking level and chat-view came later in the DOM.
    <div className="modal-backdrop fp-backdrop">
      <div className="modal-card fp-card">
        <header className="fp-header">
          <button type="button" className="icon-button fp-back" onClick={onClose} disabled={busy} aria-label="Back"><ArrowLeft size={20} /></button>
          <h2 className="fp-title">Report {target.contentType === 'post' ? 'Post' : target.contentType === 'account' ? 'Account' : 'Message'}</h2>
        </header>

        <p style={{ fontSize: '.8rem', color: 'var(--muted)', marginBottom: '1rem' }}>
          Reporting {target.contentType === 'account' ? 'the account of' : 'content from'} <strong style={{ color: 'var(--ink)' }}>{target.resolved ? `@${String(target.targetUsername).replace(/^@/, '')}` : (target.targetName || '…')}</strong>. Our admins will review this.
        </p>

        <div style={{ display: 'grid', gap: '0.5rem', marginBottom: '1rem', maxHeight: '38vh', overflowY: 'auto' }}>
          {REASONS.map((r) => (
            <button
              key={r}
              type="button"
              onClick={() => setReason(r)}
              style={{
                display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.75rem 1rem',
                borderRadius: 12, border: reason === r ? '1px solid var(--cyan)' : '1px solid var(--line)',
                background: reason === r ? 'var(--accent-tint)' : 'var(--soft)', textAlign: 'left'
              }}
            >
              <Flag size={16} color={reason === r ? 'var(--cyan)' : 'var(--muted)'} />
              <span style={{ fontSize: '.82rem', color: 'var(--ink)' }}>{r}</span>
            </button>
          ))}
        </div>

        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Add more detail (optional)…"
          rows={3}
          style={{ width: '100%', resize: 'none', background: 'var(--soft)', border: '1px solid var(--line)', borderRadius: 12, padding: '0.75rem 1rem', color: 'var(--ink)', fontSize: '.82rem', marginBottom: '1rem', fontFamily: 'inherit' }}
        />

        <button
          className="text-button danger"
          style={{ justifyContent: 'center', width: '100%' }}
          disabled={busy}
          onClick={() => onSubmit(note.trim() ? `${reason} — ${note.trim()}` : reason)}
        >
          {busy ? 'Submitting…' : 'Submit Report'}
        </button>
      </div>
    </div>
  )
}

// =========================================================================
// MODERATION: report queue, ban appeals, audit log (admin) + in-app appeal (banned user)
// =========================================================================
type ModLogEntry = { at: number; by: string; action: string; note?: string }
type ReportRow = {
  id: string; status: 'pending' | 'reviewing' | 'actioned' | 'dismissed'; reason: string
  contentType: string; contentId: string; contentText: string
  targetUid: string; targetUsername: string; targetName: string
  reportedByUsername: string; createdAt: number; actionLog: ModLogEntry[]
}
type AppealRow = {
  uid: string; username: string; name: string; message: string; status: 'pending' | 'approved' | 'rejected'
  banType?: string; banReason?: string; bannedUntil?: number | null
  createdAt: number; decidedBy?: string; decidedAt?: number; decisionNote?: string
}
const msOf = (v: any): number => (typeof v === 'number' ? v : typeof v?.toMillis === 'function' ? v.toMillis() : typeof v?.__ts === 'number' ? v.__ts : 0)
const fmtWhen = (ms: number) => (ms ? new Date(ms).toLocaleString() : '')
const ACTION_LABEL: Record<string, string> = {
  review_started: 'Review started', dismissed: 'Dismissed', post_deleted: 'Post deleted', reopened: 'Reopened',
  ban_permanent: 'Permanent ban', ban_temporary: 'Temporary ban', unban: 'Unbanned',
  appeal_approved: 'Appeal approved (unbanned)', appeal_rejected: 'Appeal rejected',
  post_removed: 'Post removed', chat_ban: 'Chat ban', chat_ban_lifted: 'Chat ban lifted', upload_hold: 'Upload hold', upload_hold_lifted: 'Upload hold lifted',
}
const actionLabel = (a: string) => ACTION_LABEL[a] || a

/** Append-only admin audit trail (modLog collection, admin-only in lib/d1-rules.ts). Best effort. */
async function writeModLog(entry: { by: string; action: string; targetUid?: string; targetUsername?: string; reportId?: string; note?: string }) {
  try {
    const data: Record<string, unknown> = { by: entry.by, action: entry.action, at: Date.now() }
    if (entry.targetUid) data.targetUid = entry.targetUid
    if (entry.targetUsername) data.targetUsername = entry.targetUsername
    if (entry.reportId) data.reportId = entry.reportId
    if (entry.note) data.note = entry.note
    await addDoc(collection(firestore, 'modLog'), data)
  } catch { /* an audit-log hiccup must never block the moderation action itself */ }
}

const chipStyle = (on: boolean): React.CSSProperties => ({
  padding: '0.25rem 0.75rem', borderRadius: 999, fontSize: '.72rem', fontWeight: 700,
  border: on ? '1px solid var(--cyan)' : '1px solid var(--line)',
  background: on ? 'var(--accent-tint)' : 'var(--soft)', color: on ? 'var(--cyan)' : 'var(--muted)',
})
const cardStyle: React.CSSProperties = { display: 'grid', gap: '0.5rem', padding: '0.75rem 0.75rem', borderRadius: 14, border: '1px solid var(--line)', background: 'var(--soft)' }

function LogLines({ log }: { log: ModLogEntry[] }) {
  if (!log.length) return null
  return (
    <div style={{ display: 'grid', gap: 2, paddingTop: '0.25rem' }}>
      {log.slice(-6).map((l, i) => (
        <small key={i} style={{ fontSize: '.68rem', color: 'var(--muted)' }}>
          {fmtWhen(l.at)} · @{l.by} · <b style={{ color: 'var(--ink-2)' }}>{actionLabel(l.action)}</b>{l.note ? ` - ${l.note}` : ''}
        </small>
      ))}
    </div>
  )
}

function AdminReportsQueue({ adminName, onRequestBan }: any) {
  const [rows, setRows] = useState<ReportRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [filter, setFilter] = useState<'pending' | 'reviewing' | 'resolved'>('pending')
  const [busyId, setBusyId] = useState<string | null>(null)
  const [dismissFor, setDismissFor] = useState<string | null>(null)
  const [note, setNote] = useState('')

  const load = async () => {
    setLoading(true); setError('')
    try {
      const snap = await getDocs(query(collection(firestore, 'reports'), orderBy('createdAt', 'desc'), limit(150)))
      setRows(snap.docs.map((d) => {
        const x = d.data() as any
        return {
          id: d.id,
          status: (['pending', 'reviewing', 'actioned', 'dismissed'].includes(x.status) ? x.status : 'pending') as ReportRow['status'],
          reason: String(x.reason || ''), contentType: String(x.contentType || ''), contentId: String(x.contentId || ''),
          contentText: String(x.contentText || ''), targetUid: String(x.targetUid || ''), targetUsername: String(x.targetUsername || ''),
          targetName: String(x.targetName || ''), reportedByUsername: String(x.reportedByUsername || ''),
          createdAt: msOf(x.createdAt), actionLog: Array.isArray(x.actionLog) ? x.actionLog : [],
        }
      }))
    } catch { setError('Could not load reports.') } finally { setLoading(false) }
  }
  useEffect(() => { void load() }, [])

  const openAgainst = useMemo(() => {
    const m: Record<string, number> = {}
    rows.forEach((r) => { if (r.status === 'pending' || r.status === 'reviewing') m[r.targetUid] = (m[r.targetUid] || 0) + 1 })
    return m
  }, [rows])

  const apply = async (
    row: ReportRow, status: ReportRow['status'], action: string,
    opts: { note?: string; before?: () => Promise<void>; skipLog?: boolean } = {},
  ) => {
    setBusyId(row.id); setError('')
    try {
      if (opts.before) await opts.before()
      const entry: ModLogEntry = { at: Date.now(), by: adminName || 'admin', action, ...(opts.note ? { note: opts.note } : {}) }
      await updateDoc(doc(firestore, 'reports', row.id), {
        status,
        actionLog: arrayUnion(entry),
        resolvedAt: status === 'actioned' || status === 'dismissed' ? Date.now() : null,
      })
      if (!opts.skipLog) void writeModLog({ by: entry.by, action, targetUid: row.targetUid, targetUsername: row.targetUsername, reportId: row.id, note: opts.note })
      setRows((list) => list.map((r) => (r.id === row.id ? { ...r, status, actionLog: [...r.actionLog, entry] } : r)))
      setDismissFor(null); setNote('')
    } catch { setError('That action failed. Please try again.') } finally { setBusyId(null) }
  }

  const banFromReport = async (row: ReportRow) => {
    setError('')
    try {
      const snap = await getDoc(doc(firestore, 'users', row.targetUid))
      if (!snap.exists()) { setError('This account no longer exists.'); return }
      const target = { ...(snap.data() as Profile), uid: row.targetUid }
      onRequestBan(target, async (ok: boolean, detail: string) => {
        if (ok) await apply(row, 'actioned', detail.startsWith('banned permanently') ? 'ban_permanent' : 'ban_temporary', { note: detail, skipLog: true })
      })
    } catch { setError('Could not load that account.') }
  }

  const visible = rows.filter((r) => (filter === 'resolved' ? r.status === 'actioned' || r.status === 'dismissed' : r.status === filter))
  const count = (f: typeof filter) => rows.filter((r) => (f === 'resolved' ? r.status === 'actioned' || r.status === 'dismissed' : r.status === f)).length

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem', flexWrap: 'wrap', alignItems: 'center' }}>
        {(['pending', 'reviewing', 'resolved'] as const).map((f) => (
          <button key={f} type="button" style={chipStyle(filter === f)} onClick={() => setFilter(f)}>
            {f === 'pending' ? 'Pending' : f === 'reviewing' ? 'In review' : 'Resolved'} ({count(f)})
          </button>
        ))}
        <button type="button" className="icon-button" style={{ marginLeft: 'auto', width: 32, height: 32 }} onClick={load} disabled={loading} aria-label="Refresh"><RefreshCw size={14} /></button>
      </div>
      {error && <p role="alert" style={{ fontSize: '.78rem', color: 'var(--danger)', margin: '0 0 0.5rem' }}>{error}</p>}
      <div style={{ overflowY: 'auto', flex: 1, display: 'grid', gap: '0.5rem', alignContent: 'start' }}>
        {loading && <SkeletonRows count={3} avatar />}
        {!loading && !visible.length && <EmptyState icon={<Grid3x3 size={20} />} title="Nothing here" compact />}
        {visible.map((r) => {
          const busy = busyId === r.id
          const resolved = r.status === 'actioned' || r.status === 'dismissed'
          return (
            <div key={r.id} style={cardStyle}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                <strong style={{ fontSize: '.86rem', color: 'var(--ink)' }}>@{r.targetUsername || r.targetName || 'unknown'}</strong>
                <small style={{ fontSize: '.66rem', fontWeight: 700, textTransform: 'uppercase', color: 'var(--cyan)' }}>{r.contentType}</small>
                <small style={{ fontSize: '.66rem', fontWeight: 700, textTransform: 'uppercase', color: r.status === 'actioned' ? 'var(--online-green)' : r.status === 'dismissed' ? 'var(--muted)' : 'var(--gold)' }}>{r.status}</small>
                {(openAgainst[r.targetUid] || 0) > 1 && !resolved && (
                  <small style={{ fontSize: '.66rem', fontWeight: 700, color: 'var(--danger)' }}>{openAgainst[r.targetUid]} open reports on this account</small>
                )}
              </div>
              <span style={{ fontSize: '.82rem', color: 'var(--ink)' }}>{r.reason}</span>
              {r.contentText && <p style={{ margin: 0, fontSize: '.76rem', color: 'var(--ink-2)', background: 'var(--soft-2)', borderRadius: 10, padding: '0.5rem 0.5rem', overflowWrap: 'anywhere' }}>{r.contentText}</p>}
              <small style={{ fontSize: '.68rem', color: 'var(--muted)' }}>Reported by @{r.reportedByUsername || 'unknown'} · {fmtWhen(r.createdAt)}</small>
              <LogLines log={r.actionLog} />

              {dismissFor === r.id ? (
                <div style={{ display: 'grid', gap: '0.5rem' }}>
                  <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Reason for dismissing (optional)…" maxLength={200}
                    style={{ background: 'var(--soft-2)', border: '1px solid var(--line)', borderRadius: 10, padding: '0.5rem 0.75rem', color: 'var(--ink)', fontSize: '.8rem' }} />
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button className="text-button" style={{ flex: 1, justifyContent: 'center' }} disabled={busy} onClick={() => { setDismissFor(null); setNote('') }}>Cancel</button>
                    <button className="text-button primary" style={{ flex: 1, justifyContent: 'center' }} disabled={busy} onClick={() => apply(r, 'dismissed', 'dismissed', { note: note.trim() || undefined })}>{busy ? '…' : 'Dismiss report'}</button>
                  </div>
                </div>
              ) : (
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                  {r.status === 'pending' && <button className="text-button" disabled={busy} onClick={() => apply(r, 'reviewing', 'review_started')}>Start review</button>}
                  {!resolved && r.contentType === 'post' && (
                    <button className="text-button danger" disabled={busy}
                      onClick={() => { if (window.confirm('Delete this post permanently?')) void apply(r, 'actioned', 'post_deleted', { before: async () => { await deleteDoc(doc(firestore, 'posts', r.contentId)) } }) }}>
                      <Trash2 size={14} /> Delete post
                    </button>
                  )}
                  {!resolved && r.targetUid && <button className="text-button danger" disabled={busy} onClick={() => banFromReport(r)}><Ban size={14} /> Ban…</button>}
                  {!resolved && <button className="text-button" disabled={busy} onClick={() => { setDismissFor(r.id); setNote('') }}>Dismiss</button>}
                  {resolved && <button className="text-button" disabled={busy} onClick={() => apply(r, 'reviewing', 'reopened')}>Reopen</button>}
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

function AdminAppealsQueue({ adminName }: any) {
  const [rows, setRows] = useState<AppealRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [filter, setFilter] = useState<'pending' | 'decided'>('pending')
  const [busyId, setBusyId] = useState<string | null>(null)
  const [notes, setNotes] = useState<Record<string, string>>({})

  const load = async () => {
    setLoading(true); setError('')
    try {
      const snap = await getDocs(query(collection(firestore, 'appeals'), limit(150)))
      setRows(snap.docs.map((d) => {
        const x = d.data() as any
        return {
          uid: d.id, username: String(x.username || ''), name: String(x.name || ''), message: String(x.message || ''),
          status: (['approved', 'rejected'].includes(x.status) ? x.status : 'pending') as AppealRow['status'],
          banType: x.banType, banReason: x.banReason, bannedUntil: typeof x.bannedUntil === 'number' ? x.bannedUntil : null,
          createdAt: msOf(x.createdAt), decidedBy: x.decidedBy, decidedAt: typeof x.decidedAt === 'number' ? x.decidedAt : undefined,
          decisionNote: x.decisionNote,
        } as AppealRow
      }).sort((a, b) => b.createdAt - a.createdAt))
    } catch { setError('Could not load appeals.') } finally { setLoading(false) }
  }
  useEffect(() => { void load() }, [])

  const decide = async (row: AppealRow, approve: boolean) => {
    setBusyId(row.uid); setError('')
    const decisionNote = (notes[row.uid] || '').trim()
    try {
      // Approve = lift the ban first; only then mark the appeal decided.
      if (approve) await updateDoc(doc(firestore, 'users', row.uid), { banned: false, banType: null, bannedUntil: null, banReason: '' })
      await updateDoc(doc(firestore, 'appeals', row.uid), {
        status: approve ? 'approved' : 'rejected', decidedBy: adminName || 'admin', decidedAt: Date.now(), decisionNote,
      })
      void writeModLog({ by: adminName || 'admin', action: approve ? 'appeal_approved' : 'appeal_rejected', targetUid: row.uid, targetUsername: row.username, note: decisionNote || undefined })
      setRows((list) => list.map((r) => (r.uid === row.uid ? { ...r, status: approve ? 'approved' : 'rejected', decidedBy: adminName, decidedAt: Date.now(), decisionNote } : r)))
    } catch { setError('That action failed. Please try again.') } finally { setBusyId(null) }
  }

  const visible = rows.filter((r) => (filter === 'pending' ? r.status === 'pending' : r.status !== 'pending'))
  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem', alignItems: 'center' }}>
        <button type="button" style={chipStyle(filter === 'pending')} onClick={() => setFilter('pending')}>Pending ({rows.filter((r) => r.status === 'pending').length})</button>
        <button type="button" style={chipStyle(filter === 'decided')} onClick={() => setFilter('decided')}>Decided ({rows.filter((r) => r.status !== 'pending').length})</button>
        <button type="button" className="icon-button" style={{ marginLeft: 'auto', width: 32, height: 32 }} onClick={load} disabled={loading} aria-label="Refresh"><RefreshCw size={14} /></button>
      </div>
      {error && <p role="alert" style={{ fontSize: '.78rem', color: 'var(--danger)', margin: '0 0 0.5rem' }}>{error}</p>}
      <div style={{ overflowY: 'auto', flex: 1, display: 'grid', gap: '0.5rem', alignContent: 'start' }}>
        {loading && <SkeletonRows count={3} avatar />}
        {!loading && !visible.length && <EmptyState icon={<Grid3x3 size={20} />} title="Nothing here" compact />}
        {visible.map((r) => {
          const busy = busyId === r.uid
          return (
            <div key={r.uid} style={cardStyle}>
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                <strong style={{ fontSize: '.86rem', color: 'var(--ink)' }}>@{r.username || r.name}</strong>
                <small style={{ fontSize: '.66rem', fontWeight: 700, textTransform: 'uppercase', color: r.status === 'approved' ? 'var(--online-green)' : r.status === 'rejected' ? 'var(--danger)' : 'var(--gold)' }}>{r.status}</small>
              </div>
              <small style={{ fontSize: '.7rem', color: 'var(--muted)' }}>
                {r.banType === 'permanent' ? 'Permanent ban' : r.banType === 'temporary' ? 'Temporary ban' : 'Ban'}{r.banReason ? ` · “${r.banReason}”` : ''} · appealed {fmtWhen(r.createdAt)}
              </small>
              <p style={{ margin: 0, fontSize: '.8rem', color: 'var(--ink)', background: 'var(--soft-2)', borderRadius: 10, padding: '0.5rem 0.75rem', overflowWrap: 'anywhere' }}>{r.message}</p>
              {r.status === 'pending' ? (
                <>
                  <input value={notes[r.uid] || ''} onChange={(e) => setNotes((n) => ({ ...n, [r.uid]: e.target.value }))} placeholder="Note shown to the user (optional)…" maxLength={300}
                    style={{ background: 'var(--soft-2)', border: '1px solid var(--line)', borderRadius: 10, padding: '0.5rem 0.75rem', color: 'var(--ink)', fontSize: '.8rem' }} />
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button className="text-button danger" style={{ flex: 1, justifyContent: 'center' }} disabled={busy} onClick={() => decide(r, false)}>Reject</button>
                    <button className="text-button primary" style={{ flex: 1, justifyContent: 'center' }} disabled={busy}
                      onClick={() => { if (window.confirm(`Approve and unban @${r.username}?`)) void decide(r, true) }}>{busy ? '…' : 'Approve & unban'}</button>
                  </div>
                </>
              ) : (
                <small style={{ fontSize: '.68rem', color: 'var(--muted)' }}>Decided by @{r.decidedBy || 'admin'} · {fmtWhen(r.decidedAt || 0)}{r.decisionNote ? ` - ${r.decisionNote}` : ''}</small>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

function AdminModLog() {
  const [rows, setRows] = useState<Array<ModLogEntry & { id: string; targetUsername?: string }>>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const load = async () => {
    setLoading(true); setError('')
    try {
      const snap = await getDocs(query(collection(firestore, 'modLog'), orderBy('at', 'desc'), limit(100)))
      setRows(snap.docs.map((d) => { const x = d.data() as any; return { id: d.id, at: Number(x.at) || 0, by: String(x.by || ''), action: String(x.action || ''), note: x.note ? String(x.note) : undefined, targetUsername: x.targetUsername ? String(x.targetUsername) : undefined } }))
    } catch { setError('Could not load the log.') } finally { setLoading(false) }
  }
  useEffect(() => { void load() }, [])
  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      <div style={{ display: 'flex', marginBottom: '0.75rem' }}>
        <small style={{ color: 'var(--muted)', fontSize: '.72rem', alignSelf: 'center' }}>Latest 100 moderation actions</small>
        <button type="button" className="icon-button" style={{ marginLeft: 'auto', width: 32, height: 32 }} onClick={load} disabled={loading} aria-label="Refresh"><RefreshCw size={14} /></button>
      </div>
      {error && <p role="alert" style={{ fontSize: '.78rem', color: 'var(--danger)', margin: '0 0 0.5rem' }}>{error}</p>}
      <div style={{ overflowY: 'auto', flex: 1, display: 'grid', gap: '0.5rem', alignContent: 'start' }}>
        {loading && <SkeletonRows count={3} avatar />}
        {!loading && !rows.length && <EmptyState icon={<Grid3x3 size={20} />} title="No actions yet" compact />}
        {rows.map((r) => (
          <div key={r.id} style={{ ...cardStyle, gap: 2, padding: '0.5rem 0.75rem' }}>
            <span style={{ fontSize: '.8rem', color: 'var(--ink)' }}><b>{actionLabel(r.action)}</b>{r.targetUsername ? ` · @${r.targetUsername}` : ''}</span>
            <small style={{ fontSize: '.68rem', color: 'var(--muted)' }}>{fmtWhen(r.at)} · by @{r.by}{r.note ? ` - ${r.note}` : ''}</small>
          </div>
        ))}
      </div>
    </div>
  )
}

/** Shown on the ban screen: file (or re-file) an appeal inside the app and see its outcome. */
function BanAppealPanel({ profile }: any) {
  const [appeal, setAppeal] = useState<any | null>(null)
  const [loading, setLoading] = useState(true)
  const [message, setMessage] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    let live = true
    getDoc(doc(firestore, 'appeals', profile.uid))
      .then((snap) => { if (live) setAppeal(snap.exists() ? snap.data() : null) })
      .catch(() => undefined)
      .finally(() => { if (live) setLoading(false) })
    return () => { live = false }
  }, [profile.uid])

  const submit = async () => {
    const text = message.trim()
    if (text.length < 10) { setError('Please explain in at least 10 characters.'); return }
    setBusy(true); setError('')
    try {
      const payload = {
        uid: profile.uid, username: profile.username, name: profile.name, message: text.slice(0, 1000),
        banType: profile.banType || 'permanent', banReason: profile.banReason || '', bannedUntil: profile.bannedUntil ?? null,
        status: 'pending', createdAt: serverTimestamp(),
      }
      await setDoc(doc(firestore, 'appeals', profile.uid), payload)
      setAppeal({ ...payload, createdAt: Date.now() })
      setMessage('')
    } catch { setError('Could not send your appeal. Please try again.') } finally { setBusy(false) }
  }

  if (loading) return <div style={{ marginTop: '1rem' }}><SkeletonRows count={3} /></div>
  const pending = appeal?.status === 'pending'
  return (
    <div style={{ textAlign: 'left', marginTop: '1rem', display: 'grid', gap: '0.5rem' }}>
      {appeal && (
        <div style={{ ...cardStyle, gap: '0.25rem' }}>
          <strong style={{ fontSize: '.8rem', color: 'var(--ink)' }}>
            {pending ? 'Appeal sent - waiting for review' : appeal.status === 'rejected' ? 'Your last appeal was not approved' : 'Your last appeal was approved'}
          </strong>
          <small style={{ fontSize: '.72rem', color: 'var(--muted)', overflowWrap: 'anywhere' }}>“{String(appeal.message || '')}”</small>
          {appeal.decisionNote && <small style={{ fontSize: '.72rem', color: 'var(--ink-2)' }}>Admin note: {String(appeal.decisionNote)}</small>}
        </div>
      )}
      {!pending && (
        <>
          <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={4} maxLength={1000}
            placeholder="Tell us why this ban should be lifted…"
            style={{ width: '100%', resize: 'none', background: 'var(--soft)', border: '1px solid var(--line)', borderRadius: 12, padding: '0.75rem 1rem', color: 'var(--ink)', fontSize: '.82rem', fontFamily: 'inherit', boxSizing: 'border-box' }} />
          {error && <p role="alert" style={{ fontSize: '.76rem', color: 'var(--danger)', margin: 0 }}>{error}</p>}
          <button className="text-button primary" style={{ justifyContent: 'center' }} disabled={busy} onClick={submit}>{busy ? 'Sending…' : appeal ? 'Send a new appeal' : 'Send appeal'}</button>
        </>
      )}
    </div>
  )
}

function AdminPanelModal({
  adminName, results, searchBusy, actionBusy, onSearch, onClose, onBan, onUnban,
  wipeBusy, wipeError, onWipeDatabase, onResetWipeFlow,
  boostEnabled, boostLockBusy, onToggleBoostLock, onRestrict,
}: any) {
  const [term, setTerm] = useState('')
  const [tab, setTab] = useState<'users' | 'reports' | 'appeals' | 'log'>('users')
  const [banTarget, setBanTarget] = useState<Profile | null>(null)
  // Set when a ban is started from a report card: runs once the ban succeeds/fails so the report can be marked actioned.
  const banAfterRef = useRef<((ok: boolean, detail: string) => void | Promise<void>) | null>(null)
  const [showWipeSheet, setShowWipeSheet] = useState(false)

  return (
    <div className="modal-backdrop fp-backdrop">
      <div className="modal-card fp-card">
        <header className="fp-header">
          <button type="button" className="icon-button fp-back" onClick={onClose} disabled={actionBusy} aria-label="Back"><ArrowLeft size={20} /></button>
          <h2 className="fp-title"><ShieldAlert size={18} color="var(--gold)" /> Admin Console</h2>
        </header>

        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem', flexShrink: 0, flexWrap: 'wrap' }}>
          {([['users', 'Users'], ['reports', 'Reports'], ['appeals', 'Appeals'], ['log', 'Log']] as const).map(([id, label]) => (
            <button key={id} type="button" style={chipStyle(tab === id)} onClick={() => setTab(id)}>{label}</button>
          ))}
        </div>

        {tab === 'reports' && <AdminReportsQueue adminName={adminName} onRequestBan={(u: Profile, cb?: (ok: boolean, detail: string) => void | Promise<void>) => { banAfterRef.current = cb || null; setBanTarget(u) }} />}
        {tab === 'appeals' && <AdminAppealsQueue adminName={adminName} />}
        {tab === 'log' && <AdminModLog />}

        {tab === 'users' && (<>
        <form
          onSubmit={(e) => { e.preventDefault(); onSearch(term) }}
          style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem', flexShrink: 0 }}
        >
          <input
            value={term}
            onChange={(e) => { setTerm(e.target.value); onSearch(e.target.value) }}
            placeholder="Search username…"
            style={{ flex: 1, background: 'var(--soft)', border: '1px solid var(--line)', borderRadius: 12, padding: '0.75rem 1rem', color: 'var(--ink)', fontSize: '.85rem' }}
          />
          <button type="submit" className="icon-button" style={{ width: 42, height: 42 }}><Search size={16} /></button>
        </form>

        <div style={{ overflowY: 'auto', flex: 1, display: 'grid', gap: '0.5rem' }}>
          {searchBusy && <p style={{ fontSize: '.8rem', color: 'var(--muted)', textAlign: 'center' }}>Searching…</p>}
          {!searchBusy && term.trim().length >= 2 && results.length === 0 && (
            <p style={{ fontSize: '.8rem', color: 'var(--muted)', textAlign: 'center' }}>No users found.</p>
          )}
          {results.map((u: Profile) => {
            const activeBan = u.banned && (u.banType === 'permanent' || (u.banType === 'temporary' && u.bannedUntil && Date.now() < u.bannedUntil))
            return (
              <div key={u.uid} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.75rem 0.75rem', borderRadius: 14, border: '1px solid var(--line)', background: 'var(--soft)' }}>
                <Avatar src={u.avatar} name={u.name} size="sm" uid={u.uid} showStatus={false} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <strong style={{ display: 'block', fontSize: '.86rem', color: 'var(--ink)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.name}</strong>
                  <small style={{ fontSize: '.72rem', color: 'var(--cyan)' }}>@{u.username}</small>
                  {activeBan && (
                    <small style={{ display: 'block', fontSize: '.68rem', color: 'var(--danger)', fontWeight: 700, marginTop: 2 }}>
                      {u.banType === 'permanent' ? 'PERMANENTLY BANNED' : `BANNED until ${new Date(u.bannedUntil as number).toLocaleString()}`}
                    </small>
                  )}
                  {restrictLeft(u.chatBanUntil) > 0 && <small style={{ display: 'block', fontSize: '.66rem', color: 'var(--danger)', fontWeight: 700, marginTop: 2 }}>Chat banned {restrictWhen(restrictLeft(u.chatBanUntil))}</small>}
                  {restrictLeft(u.uploadHoldUntil) > 0 && <small style={{ display: 'block', fontSize: '.66rem', color: 'var(--danger)', fontWeight: 700, marginTop: 2 }}>Uploads held {restrictWhen(restrictLeft(u.uploadHoldUntil))}</small>}
                  <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
                    <button type="button" className="text-button" style={{ fontSize: '.68rem', padding: '0.25rem 0.5rem' }} disabled={actionBusy} onClick={() => onRestrict?.(u, 'chat')}>Chat ban</button>
                    <button type="button" className="text-button" style={{ fontSize: '.68rem', padding: '0.25rem 0.5rem' }} disabled={actionBusy} onClick={() => onRestrict?.(u, 'upload')}>Upload hold</button>
                  </div>
                </div>
                {activeBan ? (
                  <button className="text-button" disabled={actionBusy} onClick={() => onUnban(u)}>Unban</button>
                ) : (
                  <button className="text-button danger" disabled={actionBusy} onClick={() => setBanTarget(u)}>
                    <Ban size={14} /> Ban
                  </button>
                )}
              </div>
            )
          })}
        </div>
        </>)}

        {banTarget && (
          <BanUserSheet
            target={banTarget}
            busy={actionBusy}
            onClose={() => { banAfterRef.current = null; setBanTarget(null) }}
            onConfirm={async (mode: 'temporary' | 'permanent', durationMs?: number, reason?: string) => {
              const ok = (await onBan(banTarget, mode, durationMs, reason)) !== false
              const detail = mode === 'permanent' ? 'banned permanently' : `banned for ${Math.max(1, Math.round((durationMs || 0) / 3_600_000))}h`
              const after = banAfterRef.current
              banAfterRef.current = null
              setBanTarget(null)
              if (after) await after(ok, reason ? `${detail} - ${reason}` : detail)
            }}
          />
        )}

        {tab === 'users' && (<>
        <div style={{ marginTop: '1rem', paddingTop: '0.75rem', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
            <Lock size={16} color={!boostEnabled ? 'var(--danger)' : 'var(--muted)'} />
            <div style={{ minWidth: 0 }}>
              <strong style={{ display: 'block', fontSize: '.84rem', color: 'var(--ink)' }}>Boost lock</strong>
              <small style={{ fontSize: '.7rem', color: 'var(--muted)' }}>
                {!boostEnabled ? 'Locked - Boost shows "Coming soon" to every user.' : 'Unlocked - Boost works normally for everyone.'}
              </small>
            </div>
          </div>
          <button
            type="button"
            className={!boostEnabled ? 'text-button danger' : 'text-button'}
            disabled={boostLockBusy}
            onClick={() => onToggleBoostLock?.(!boostEnabled)}
            style={{ flexShrink: 0 }}
          >
            {boostLockBusy ? '…' : !boostEnabled ? 'Unlock' : 'Lock'}
          </button>
        </div>

        <div style={{ marginTop: '1rem', paddingTop: '0.75rem', flexShrink: 0 }}>
          <button
            type="button"
            className="text-button danger"
            style={{ justifyContent: 'center', width: '100%' }}
            onClick={() => { onResetWipeFlow?.(); setShowWipeSheet(true) }}
          >
            <Trash2 size={16} /> Clear Entire Database
          </button>
        </div>

        </>)}

        {showWipeSheet && (
          <WipeDatabaseSheet
            busy={wipeBusy}
            error={wipeError}
            onClose={() => { if (!wipeBusy) setShowWipeSheet(false) }}
            onConfirm={onWipeDatabase}
          />
        )}
      </div>
    </div>
  )
}

// Admin-only "Danger Zone" flow: no Firebase step at all - type the exact confirmation
// phrase below and tap Delete to wipe the database. See app/api/admin/wipe/route.ts,
// which re-checks the same phrase server-side before it does anything.
const WIPE_CONFIRM_PHRASE = 'DELETE DATABASE'

function WipeDatabaseSheet({ busy, error, onClose, onConfirm }: any) {
  const [typed, setTyped] = useState('')
  const matches = typed === WIPE_CONFIRM_PHRASE

  return (
    <div className="action-sheet-backdrop" onClick={busy ? () => {} : onClose}>
      <div className="action-sheet" onClick={(e) => e.stopPropagation()}>
        <h3 style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--danger)', margin: '0 0 0.5rem', display: 'flex', alignItems: 'center', gap: 8 }}>
          <ShieldAlert size={16} /> Clear Entire Database
        </h3>

        <p style={{ fontSize: '.82rem', color: 'var(--ink-2)', lineHeight: 1.6, margin: '0 0 1rem' }}>
          This permanently deletes <b>every</b> user, post, reel, chat, wallet and ban in the app -
          everything resets to a brand-new install. Usernames will need to be created again and every
          ban is lifted, because nobody is left banned. This cannot be undone.
        </p>

        <p style={{ fontSize: '.8rem', color: 'var(--ink-2)', lineHeight: 1.6, margin: '0 0 0.5rem' }}>
          Type <b style={{ color: 'var(--danger)', background: 'rgba(220,50,50,.12)', padding: '0.05rem 0.25rem', borderRadius: 6 }}>{WIPE_CONFIRM_PHRASE}</b> below to confirm.
        </p>

        <input
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          placeholder={WIPE_CONFIRM_PHRASE}
          autoCapitalize="characters"
          autoCorrect="off"
          spellCheck={false}
          disabled={busy}
          style={{
            width: '100%',
            background: 'var(--soft)',
            border: `1px solid ${matches ? 'var(--danger)' : 'var(--line)'}`,
            borderRadius: 12,
            padding: '0.75rem 1rem',
            color: 'var(--ink)',
            fontSize: '.85rem',
            marginBottom: '1rem',
            boxSizing: 'border-box',
          }}
        />

        {error && <p role="alert" style={{ fontSize: '.78rem', color: 'var(--danger)', margin: '0 0 0.75rem' }}>{error}</p>}

        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button className="text-button" style={{ flex: 1, justifyContent: 'center' }} onClick={onClose} disabled={busy}>Cancel</button>
          <button
            className="text-button danger"
            style={{ flex: 1, justifyContent: 'center' }}
            disabled={busy || !matches}
            onClick={() => onConfirm(typed)}
          >
            {busy ? 'Wiping…' : 'Delete Database'}
          </button>
        </div>
      </div>
    </div>
  )
}

// Admin: chat ban / upload hold for N days (or permanent, or lift). Written to users/{uid}; enforced server-side.
function RestrictUserSheet({ target, busy, onClose, onConfirm }: any) {
  const isChat = target.kind === 'chat'
  const activeUntil = restrictLeft(target.until)
  const [days, setDays] = useState<number>(7)
  const [forever, setForever] = useState(false)
  const [reason, setReason] = useState<string>(target.reason || '')
  const presets = [1, 3, 7, 15, 30, 90, 365]
  return (
    <div className="action-sheet-backdrop" onClick={busy ? () => {} : onClose}>
      <div className="action-sheet" onClick={(e) => e.stopPropagation()}>
        <h3 style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--ink)', margin: '0 0 0.25rem' }}>
          {isChat ? 'Chat ban' : 'Upload hold'} · @{target.username}
        </h3>
        <p style={{ margin: '0 0 0.75rem', fontSize: '.76rem', color: 'var(--muted)', lineHeight: 1.5 }}>
          {isChat
            ? 'They cannot send any message (DMs, requests, guilds) while this is active.'
            : 'They cannot upload posts, reels, stories or music while this is active. Chat photos and profile pictures still work.'}
          {activeUntil ? <b style={{ display: 'block', color: 'var(--danger)', marginTop: 4 }}>Active {restrictWhen(activeUntil)}</b> : null}
        </p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '0.75rem' }}>
          {presets.map((n) => (
            <button key={n} type="button" style={chipStyle(!forever && days === n)} onClick={() => { setForever(false); setDays(n) }}>{n}d</button>
          ))}
          <button type="button" style={chipStyle(forever)} onClick={() => setForever(true)}>Permanent</button>
        </div>
        {!forever && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
            <input
              type="number"
              min={1}
              max={3650}
              value={days}
              onChange={(e) => setDays(Math.min(3650, Math.max(1, Math.round(Number(e.target.value)) || 1)))}
              style={{ width: 90, background: 'var(--soft)', border: '1px solid var(--line)', borderRadius: 12, padding: '0.5rem 0.75rem', color: 'var(--ink)', fontSize: '.85rem' }}
            />
            <span style={{ fontSize: '.8rem', color: 'var(--muted)' }}>days (custom)</span>
          </div>
        )}
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value.slice(0, 200))}
          placeholder="Reason (shown to the user, optional)…"
          rows={2}
          style={{ width: '100%', resize: 'none', background: 'var(--soft)', border: '1px solid var(--line)', borderRadius: 12, padding: '0.75rem 1rem', color: 'var(--ink)', fontSize: '.82rem', marginBottom: '0.75rem', fontFamily: 'inherit' }}
        />
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button type="button" className="text-button" style={{ flex: 1, justifyContent: 'center' }} disabled={busy} onClick={onClose}>Cancel</button>
          {activeUntil > 0 && (
            <button type="button" className="text-button" style={{ flex: 1, justifyContent: 'center' }} disabled={busy} onClick={() => onConfirm(0, '')}>Lift</button>
          )}
          <button type="button" className="text-button danger" style={{ flex: 1, justifyContent: 'center' }} disabled={busy} onClick={() => onConfirm(forever ? 'forever' : days, reason.trim())}>
            {busy ? '…' : 'Apply'}
          </button>
        </div>
      </div>
    </div>
  )
}

// Admin: remove someone else's post with a reason (author gets an alert, action goes to the Log tab).
function RemovePostSheet({ post, busy, onClose, onConfirm }: any) {
  const [reason, setReason] = useState('')
  return (
    <div className="action-sheet-backdrop" onClick={busy ? () => {} : onClose}>
      <div className="action-sheet" onClick={(e) => e.stopPropagation()}>
        <h3 style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--danger)', margin: '0 0 0.25rem' }}>Remove this post?</h3>
        <p style={{ margin: '0 0 0.75rem', fontSize: '.78rem', color: 'var(--muted)', lineHeight: 1.5 }}>
          By @{post.authorName}. It is deleted for everyone and cannot be restored. The author is notified.
        </p>
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value.slice(0, 200))}
          placeholder="Reason (shown to the author, optional)…"
          rows={2}
          style={{ width: '100%', resize: 'none', background: 'var(--soft)', border: '1px solid var(--line)', borderRadius: 12, padding: '0.75rem 1rem', color: 'var(--ink)', fontSize: '.82rem', marginBottom: '0.75rem', fontFamily: 'inherit' }}
        />
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button type="button" className="text-button" style={{ flex: 1, justifyContent: 'center' }} disabled={busy} onClick={onClose}>Cancel</button>
          <button type="button" className="text-button danger" style={{ flex: 1, justifyContent: 'center' }} disabled={busy} onClick={() => onConfirm(reason.trim())}>
            <Trash2 size={16} /> {busy ? 'Removing…' : 'Remove'}
          </button>
        </div>
      </div>
    </div>
  )
}

function BanUserSheet({ target, busy, onClose, onConfirm }: any) {
  const [mode, setMode] = useState<'temporary' | 'permanent'>('temporary')
  const [amount, setAmount] = useState(24)
  const [unit, setUnit] = useState<'hours' | 'days'>('hours')
  const [reason, setReason] = useState('')

  const durationMs = unit === 'hours' ? amount * 60 * 60 * 1000 : amount * 24 * 60 * 60 * 1000

  return (
    <div className="action-sheet-backdrop" onClick={busy ? () => {} : onClose}>
      <div className="action-sheet" onClick={(e) => e.stopPropagation()}>
        <h3 style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--ink)', margin: '0 0 0.75rem' }}>
          Ban @{target.username}
        </h3>

        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
          <button
            type="button"
            onClick={() => setMode('temporary')}
            className={mode === 'temporary' ? 'text-button primary' : 'text-button'}
            style={{ flex: 1, justifyContent: 'center' }}
          >
            <Clock size={16} /> Temporary
          </button>
          <button
            type="button"
            onClick={() => setMode('permanent')}
            className={mode === 'permanent' ? 'text-button danger' : 'text-button'}
            style={{ flex: 1, justifyContent: 'center' }}
          >
            <Ban size={16} /> Permanent
          </button>
        </div>

        {mode === 'temporary' && (
          <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
            <input
              type="number"
              min={1}
              value={amount}
              onChange={(e) => setAmount(Math.max(1, Number(e.target.value) || 1))}
              style={{ width: 80, background: 'var(--soft)', border: '1px solid var(--line)', borderRadius: 12, padding: '0.5rem 0.75rem', color: 'var(--ink)', fontSize: '.85rem' }}
            />
            <select
              value={unit}
              onChange={(e) => setUnit(e.target.value as 'hours' | 'days')}
              style={{ flex: 1, background: 'var(--soft)', border: '1px solid var(--line)', borderRadius: 12, padding: '0.5rem 0.75rem', color: 'var(--ink)', fontSize: '.85rem' }}
            >
              <option value="hours">Hours</option>
              <option value="days">Days</option>
            </select>
          </div>
        )}

        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Reason (shown to the user, optional)…"
          rows={2}
          style={{ width: '100%', resize: 'none', background: 'var(--soft)', border: '1px solid var(--line)', borderRadius: 12, padding: '0.75rem 1rem', color: 'var(--ink)', fontSize: '.82rem', marginBottom: '1rem', fontFamily: 'inherit' }}
        />

        <button
          className="text-button danger"
          style={{ justifyContent: 'center', width: '100%' }}
          disabled={busy}
          onClick={() => onConfirm(mode, mode === 'temporary' ? durationMs : undefined, reason.trim())}
        >
          {busy ? 'Applying…' : mode === 'permanent' ? 'Confirm Permanent Ban' : `Confirm Ban for ${amount} ${unit}`}
        </button>
      </div>
    </div>
  )
}

function DeleteAccountModal({ busy, onClose, onTemporaryDelete, onPermanentDelete }: any) {
  const [mode, setMode] = useState<'choose' | 'confirm-temp' | 'confirm-perm'>('choose')

  return (
    <div className="modal-backdrop fp-backdrop">
      <div className="modal-card fp-card">
        <header className="fp-header">
          <button type="button" className="icon-button fp-back" onClick={onClose} disabled={busy} aria-label="Back"><ArrowLeft size={20} /></button>
          <h2 className="fp-title">Delete Account</h2>
        </header>

        {mode === 'choose' && (
          <div style={{ display: 'grid', gap: '0.75rem' }}>
            <button
              type="button"
              onClick={() => setMode('confirm-temp')}
              style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', padding: '1rem', borderRadius: 14, border: '1px solid var(--line)', background: 'var(--soft)', textAlign: 'left' }}
            >
              <EyeOff size={18} color="var(--cyan)" style={{ marginTop: 2, flexShrink: 0 }} />
              <span>
                <strong style={{ display: 'block', fontSize: '.88rem', color: 'var(--ink)' }}>Temporarily Deactivate</strong>
                <small style={{ fontSize: '.76rem', color: 'var(--muted)' }}>
                  Nothing is deleted. Your account just hides itself until you log back in - then it's exactly as you left it.
                </small>
              </span>
            </button>

            <button
              type="button"
              onClick={() => setMode('confirm-perm')}
              style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', padding: '1rem', borderRadius: 14, border: '1px solid rgba(255,42,95,0.35)', background: 'rgba(255,42,95,0.06)', textAlign: 'left' }}
            >
              <Trash2 size={18} color="var(--danger)" style={{ marginTop: 2, flexShrink: 0 }} />
              <span>
                <strong style={{ display: 'block', fontSize: '.88rem', color: 'var(--ink)' }}>Delete Permanently</strong>
                <small style={{ fontSize: '.76rem', color: 'var(--muted)' }}>
                  Your account and posts are permanently erased in 3 days. Log back in before then to cancel it.
                </small>
              </span>
            </button>
          </div>
        )}

        {mode === 'confirm-temp' && (
          <div style={{ display: 'grid', gap: '1rem' }}>
            <p style={{ fontSize: '.84rem', color: 'var(--ink-2)', margin: 0 }}>
              Your account will be hidden and you'll be signed out. Nothing is deleted - simply log back in anytime to have it restored exactly as it is now.
            </p>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button className="text-button" style={{ flex: 1, justifyContent: 'center' }} onClick={() => setMode('choose')} disabled={busy}>Back</button>
              <button className="text-button primary" style={{ flex: 1, justifyContent: 'center' }} onClick={onTemporaryDelete} disabled={busy}>
                {busy ? 'Deactivating…' : 'Yes, Deactivate'}
              </button>
            </div>
          </div>
        )}

        {mode === 'confirm-perm' && (
          <div style={{ display: 'grid', gap: '1rem' }}>
            <p style={{ fontSize: '.84rem', color: 'var(--ink-2)', margin: 0 }}>
              You'll be signed out now. If you don't log back in within <b>3 days</b>, your profile and every post you've made will be permanently erased - this can't be undone after that window closes. Logging back in any time before then cancels the deletion.
            </p>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button className="text-button" style={{ flex: 1, justifyContent: 'center' }} onClick={() => setMode('choose')} disabled={busy}>Back</button>
              <button className="text-button danger" style={{ flex: 1, justifyContent: 'center' }} onClick={onPermanentDelete} disabled={busy}>
                {busy ? 'Scheduling…' : 'Yes, Delete in 3 Days'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

// Centre-crops any image to the profile banner size (1500 x 500, ratio 3:1) and returns a WebP/JPEG file.
const BANNER_W = 1500
const BANNER_H = 500
async function cropToBanner(file: File): Promise<File> {
  const url = URL.createObjectURL(file)
  try {
    const img: HTMLImageElement = await new Promise((resolve, reject) => {
      const i = new Image(); i.onload = () => resolve(i); i.onerror = () => reject(new Error('bad-image')); i.src = url
    })
    const target = BANNER_W / BANNER_H
    let sw = img.naturalWidth, sh = img.naturalHeight
    if (sw / sh > target) sw = Math.round(sh * target); else sh = Math.round(sw / target)
    const sx = Math.round((img.naturalWidth - sw) / 2), sy = Math.round((img.naturalHeight - sh) / 2)
    const canvas = document.createElement('canvas')
    canvas.width = BANNER_W; canvas.height = BANNER_H
    const ctx = canvas.getContext('2d')
    if (!ctx) throw new Error('no-canvas')
    ctx.drawImage(img, sx, sy, sw, sh, 0, 0, BANNER_W, BANNER_H)
    const blob: Blob = await new Promise((resolve, reject) => canvas.toBlob((b) => b ? resolve(b) : reject(new Error('no-blob')), 'image/webp', 0.85))
    return new File([blob], `banner_${Date.now()}.webp`, { type: 'image/webp' })
  } finally { URL.revokeObjectURL(url) }
}

// Section title + one reusable switch row for Edit Profile, so every toggle looks and behaves the same
// (switch on = feature on, keyboard/screen-reader friendly via role="switch"). Optional children (like the
// Do-Not-Disturb time pickers) only show while the switch is on.
function SettingsHeading({ children }: { children: React.ReactNode }) {
  return <div style={{ margin: '0.4rem 0 -0.2rem', fontSize: '.68rem', fontWeight: 800, letterSpacing: '.08em', textTransform: 'uppercase', color: 'var(--muted)' }}>{children}</div>
}

function SettingToggle({ icon, title, desc, on, onChange, children }: { icon?: React.ReactNode; title: string; desc: string; on: boolean; onChange: (v: boolean) => void; children?: React.ReactNode }) {
  return (
    <div style={{ padding: '0.75rem 1rem', borderRadius: 14, border: on ? '1px solid var(--accent-line)' : '1px solid var(--line)', background: on ? 'var(--accent-tint)' : 'var(--soft)', display: 'grid', gap: '0.6rem', transition: 'background .2s ease, border-color .2s ease' }}>
      <button type="button" role="switch" aria-checked={on} onClick={() => onChange(!on)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem', textAlign: 'left', background: 'none', border: 0, padding: 0, width: '100%', cursor: 'pointer' }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: '0.55rem', minWidth: 0 }}>
          {icon && <span style={{ display: 'grid', color: on ? 'var(--cyan)' : 'var(--muted)', flexShrink: 0 }}>{icon}</span>}
          <span style={{ minWidth: 0 }}>
            <strong style={{ display: 'block', fontSize: '.86rem', color: 'var(--ink)' }}>{title}</strong>
            <small style={{ display: 'block', fontSize: '.72rem', color: 'var(--muted)', lineHeight: 1.35 }}>{desc}</small>
          </span>
        </span>
        <span aria-hidden style={{ width: 40, height: 22, borderRadius: 999, flexShrink: 0, position: 'relative', background: on ? 'var(--cyan)' : 'var(--line-strong)', transition: 'background .2s ease' }}>
          <span style={{ position: 'absolute', top: 2, left: on ? 20 : 2, width: 18, height: 18, borderRadius: '50%', background: '#fff', transition: 'left .2s ease', boxShadow: '0 1px 2px rgba(15,23,42,0.25)' }} />
        </span>
      </button>
      {on && children}
    </div>
  )
}

function ProfileModal({ profile, busy, onClose, onSave, onUploadAvatar, onUploadCover, isPremium, onRequirePremium, coinBalance = 0, onAddCoins, isAdmin = false, askConfirm }: any) {
  const [name, setName] = useState(profile.name)
  const [username, setUsername] = useState(profile.username)
  const [bio, setBio] = useState(profile.bio)
  const [gender, setGender] = useState<'male' | 'female' | 'other'>(profile.gender || 'male')
  const [avatarPreview, setAvatarPreview] = useState(profile.avatar)
  const [avatarFile, setAvatarFile] = useState<File | null>(null)
  const [isPrivate, setIsPrivate] = useState(Boolean(profile.private))
  const [callsEnabled, setCallsEnabled] = useState(profile.callsEnabled !== false)
  const [hideLastSeen, setHideLastSeen] = useState(Boolean(profile.hideLastSeen))
  const [dndOn, setDndOn] = useState(Boolean(profile.callDnd?.on))
  const [dndStart, setDndStart] = useState<number>(typeof profile.callDnd?.start === 'number' ? profile.callDnd.start : 23 * 60)
  const [dndEnd, setDndEnd] = useState<number>(typeof profile.callDnd?.end === 'number' ? profile.callDnd.end : 7 * 60)
  const [uploading, setUploading] = useState(false)
  const [saveError, setSaveError] = useState('')
  // Profile banner (1500x500, 3:1). `coverPreview` is what the person sees; `coverFile` is a new picked image to upload on save.
  const [coverPreview, setCoverPreview] = useState<string>(profile.coverUrl || '')
  const [coverFile, setCoverFile] = useState<File | null>(null)

  // Username rules + price, worked out live from what is typed (same functions the save uses).
  const cleanUsername = username.trim().replace(/^@/, '')
  const handleEdited = cleanUsername.toLowerCase() !== String(profile.username || '').toLowerCase()
  const usernameCheck = checkUsername(cleanUsername)
  const usernameCost = usernameChangeCost(String(profile.username || ''), cleanUsername, {
    changeUsed: Boolean(profile.usernameChangeUsed), credits: profile.customUsernameCredits || 0, isAdmin,
  })
  const usernameBlocked = handleEdited && !usernameCheck.ok
  const usernameShort = handleEdited && usernameCheck.ok && usernameCost.coins > coinBalance
  const usernameCharge = handleEdited && usernameCheck.ok ? usernameCost.coins : 0
  // Rename limit + paid-handle warning + Google Play price of a short handle
  const usernameLockedUntil = !isAdmin && Number(profile.usernameChangeLockUntil || 0) > Date.now() ? Number(profile.usernameChangeLockUntil) : 0
  const leavingShortHandle = !isAdmin && handleEdited && String(profile.username || '').trim().length > 0 && String(profile.username || '').trim().length <= PREMIUM_USERNAME_MAX_LENGTH
  const premiumProduct = playProductForUsernameLength(cleanUsername.length)
  const premiumPlayPrice = usePlayPrice(premiumProduct?.productId || 'username_4')
  const changesLeft = Math.max(0, USERNAME_CHANGE_LIMIT.maxChanges - Number(profile.usernameChangeCount || 0))
  const usernameRupees = handleEdited && usernameCheck.ok ? usernameCost.rupees : 0

  const submit = async (e: FormEvent, loseHandleConfirmed = false) => {
    e.preventDefault()
    setSaveError('')
    if (handleEdited && usernameLockedUntil) { setSaveError(usernameLockText(usernameLockedUntil)); return }
    // Giving up a short (1-4 character) handle: warn first - it is released and has to be bought again to get it back.
    if (handleEdited && usernameCheck.ok && leavingShortHandle && !loseHandleConfirmed && typeof askConfirm === 'function') {
      askConfirm({
        title: `Give up @${profile.username}?`,
        message: `@${profile.username} is a short paid username. If you change it, you lose it and anyone can take it. Getting a short username again means buying it again.`,
        confirmLabel: 'Change anyway',
        cancelLabel: 'Keep it',
        danger: true,
        action: () => { void submit({ preventDefault() {} } as unknown as FormEvent, true) },
      })
      return
    }
    let avatarUrl = avatarPreview // a picked preset, or the unchanged current avatar
    if (avatarFile) {
      setUploading(true)
      try { avatarUrl = await onUploadAvatar(avatarFile) }
      catch { setUploading(false); return }
      setUploading(false)
    }
    // onSave returns a message when it could not save (name taken, not enough coins ...)
    let coverUrl = coverPreview
    if (coverFile) {
      setUploading(true)
      try { coverUrl = await onUploadCover(coverFile) }
      catch { setUploading(false); setSaveError('Banner upload failed. Try again.'); return }
      setUploading(false)
    }
    const problem = await onSave({ name, username: cleanUsername, bio, gender, avatar: avatarUrl, private: isPrivate, callsEnabled, hideLastSeen, callDnd: { on: dndOn, start: dndStart, end: dndEnd, tz: -new Date().getTimezoneOffset() }, coverUrl, loseHandleConfirmed })
    if (typeof problem === 'string' && problem) setSaveError(problem)
  }

  return (
    <div className="modal-backdrop fp-backdrop">
      <div className="modal-card fp-card">
        <header className="fp-header">
          <button type="button" className="icon-button fp-back" onClick={onClose} aria-label="Back"><ArrowLeft size={20} /></button>
          <h2 className="fp-title">Edit Identity HUD</h2>
        </header>
        <form onSubmit={submit} style={{ display: 'grid', gap: '1rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <Avatar src={avatarPreview} name={name} size="lg" showStatus={false} />
            <label className="text-button primary" style={{ cursor: 'pointer' }}>
              Change Photo
              <input type="file" accept="image/*" style={{ display: 'none' }} onChange={(e) => { const f = e.target.files?.[0]; if (f) { setAvatarFile(f); setAvatarPreview(URL.createObjectURL(f)) } }} />
            </label>
          </div>

          <AvatarPicker value={avatarPreview} onSelect={(url) => { setAvatarFile(null); setAvatarPreview(url) }} />

          {/* Custom profile banner: 1500 x 500 (3:1). Any image is auto-cropped to that size. */}
          <div>
            <label style={{ fontSize: '.76rem', color: 'var(--muted)', display: 'block', marginBottom: 4 }}>Profile banner (1500 × 500)</label>
            <div style={{ position: 'relative', width: '100%', aspectRatio: '3 / 1', borderRadius: 12, overflow: 'hidden', border: '1px solid var(--line)', background: 'linear-gradient(120deg, #2a1208 0%, #7c2d12 40%, #db2777 100%)' }}>
              {coverPreview && <img src={coverPreview} alt="" decoding="async" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />}
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
              <label className="text-button primary" style={{ cursor: 'pointer', flex: 1, justifyContent: 'center' }}>
                {coverPreview ? 'Change banner' : 'Add banner'}
                <input type="file" accept="image/png,image/jpeg,image/webp" style={{ display: 'none' }} onChange={async (e) => {
                  const f = e.target.files?.[0]; e.target.value = ''
                  if (!f) return
                  try {
                    const cropped = await cropToBanner(f)
                    setCoverFile(cropped); setCoverPreview(URL.createObjectURL(cropped)); setSaveError('')
                  } catch { setSaveError('Could not read that image. Try another one.') }
                }} />
              </label>
              {coverPreview && <button type="button" className="text-button" onClick={() => { setCoverFile(null); setCoverPreview('') }}>Remove</button>}
            </div>
            <small style={{ display: 'block', fontSize: '.7rem', color: 'var(--muted)', marginTop: '0.25rem' }}>Use a 1500 × 500 image for the best result - other sizes are centre-cropped to fit.</small>
          </div>

          {/* Gender Selector */}
          <div>
            <label style={{ fontSize: '.76rem', color: 'var(--muted)', display: 'block', marginBottom: 4 }}>Operative Title (Gender)</label>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button
                type="button"
                className={`text-button ${gender === 'male' ? 'primary' : ''}`}
                style={{ flex: 1, justifyContent: 'center' }}
                onClick={() => setGender('male')}
              >
                <Crown size={14} style={{ marginRight: 6 }} />Male (King)
              </button>
              <button
                type="button"
                className={`text-button ${gender === 'female' ? 'primary' : ''}`}
                style={{ flex: 1, justifyContent: 'center' }}
                onClick={() => setGender('female')}
              >
                <Crown size={14} style={{ marginRight: 6 }} />Female (Queen)
              </button>
            </div>
          </div>

          <label style={{ fontSize: '.76rem', color: 'var(--muted)' }}>Display Name<input value={name} onChange={(e) => setName(e.target.value)} maxLength={40} style={{ width: '100%', border: '1px solid var(--line)', borderRadius: 12, padding: '0.75rem', background: 'var(--soft)', color: 'var(--ink)', marginTop: 4 }} /></label>
          <label style={{ fontSize: '.76rem', color: 'var(--muted)' }}>
            Handle (@username)
            <input value={username} onChange={(e) => { setUsername(e.target.value.toLowerCase()); setSaveError('') }} maxLength={20} autoCapitalize="none" autoCorrect="off" style={{ width: '100%', border: `1px solid ${usernameBlocked ? '#e0455a' : 'var(--line)'}`, borderRadius: 12, padding: '0.75rem', background: 'var(--soft)', color: 'var(--ink)', marginTop: 4 }} />
            {/* What this handle costs - shown as you type; you pay when you save */}
            {handleEdited && usernameLockedUntil ? (
              <small className="un-msg un-error">{usernameLockText(usernameLockedUntil)}</small>
            ) : handleEdited && !usernameCheck.ok ? (
              <small className="un-msg un-error">{usernameCheck.message}</small>
            ) : handleEdited && usernameCost.kind === 'premium' ? (
              <small className="un-msg un-premium">
                Premium handle ({cleanUsername.length} character{cleanUsername.length > 1 ? 's' : ''}): one-time purchase of <strong>{premiumPlayPrice || `₹${usernameCost.rupees}`}</strong> with Google Play when you save. It stays yours until you change it.
              </small>
            ) : handleEdited && usernameCost.kind === 'free' ? (
              <small className="un-msg">Your first change is free.</small>
            ) : handleEdited && usernameCost.kind === 'credit' ? (
              <small className="un-msg">This uses 1 of your username changes.</small>
            ) : handleEdited && usernameCost.kind === 'standard' ? (
              <small className="un-msg">
                Changing your handle again costs <CoinIcon size={14} /> <strong>{usernameCost.coins}</strong> coins.
                {usernameShort && <> You need {usernameCost.coins - coinBalance} more.</>}
              </small>
            ) : (
              <small className="un-msg">
                Short handles: {Object.keys(PREMIUM_USERNAME_PRICES).map(Number).sort((a, b) => b - a).map((n) => `${n} character${n > 1 ? 's' : ''} ₹${PREMIUM_USERNAME_PRICES[n]}`).join(' · ')} (one-time, Google Play). {PREMIUM_USERNAME_MAX_LENGTH + 1}+ characters: {!profile.usernameChangeUsed ? 'first change free' : (profile.customUsernameCredits || 0) > 0 ? `${profile.customUsernameCredits} paid change${profile.customUsernameCredits > 1 ? 's' : ''} left` : 'a small fee per change'}.
                {' '}{usernameLockedUntil ? usernameLockText(usernameLockedUntil) : `You can change your username ${changesLeft} more time${changesLeft === 1 ? '' : 's'} before a ${USERNAME_CHANGE_LIMIT.lockDays}-day lock.`}
              </small>
            )}
            {handleEdited && leavingShortHandle && usernameCheck.ok && !usernameLockedUntil && (
              <small className="un-msg un-error">Warning: @{profile.username} is a short paid username. If you change it you lose it, and you must buy it again to get a short username back.</small>
            )}
            {usernameShort && <CoinShortAd needCoins={usernameCost.coins - coinBalance} />}
          </label>
          <label style={{ fontSize: '.76rem', color: 'var(--muted)' }}>Bio<textarea value={bio} onChange={(e) => setBio(e.target.value)} maxLength={160} rows={2} style={{ width: '100%', border: '1px solid var(--line)', borderRadius: 12, padding: '0.75rem', background: 'var(--soft)', color: 'var(--ink)', marginTop: 4 }} /></label>

          {/* Private Account toggle - when on, non-Linked operatives can't see the Transmission Vault on the full-screen profile */}
          <SettingsHeading>Privacy</SettingsHeading>
          <SettingToggle
            icon={<Lock size={16} />}
            title="Private Account"
            desc="Only your connected Allies can see your posts, reels, stories and bio."
            on={isPrivate}
            onChange={setIsPrivate}
          />
          <SettingToggle
            title="Hide online status"
            desc={hideLastSeen ? 'Friends can’t see when you’re online or your last seen.' : 'Friends can see when you’re online and your last seen.'}
            on={hideLastSeen}
            onChange={setHideLastSeen}
          />

          <SettingsHeading>Calls</SettingsHeading>
          <SettingToggle
            icon={<Phone size={16} />}
            title="Allow Calls"
            desc={callsEnabled ? 'Allies can voice/video call you.' : 'Off - nobody can call you right now.'}
            on={callsEnabled}
            onChange={setCallsEnabled}
          />
          {/* Call Do Not Disturb - inside this window calls auto-decline as "busy", the caller is told up front, and no ring push is sent. */}
          <SettingToggle
            title="Call Do Not Disturb"
            desc="Calls are declined as busy during these hours (e.g. night)."
            on={dndOn}
            onChange={setDndOn}
          >
            <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap', fontSize: '.8rem', color: 'var(--ink)' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>From
                <input type="time" value={minutesToTime(dndStart)} onChange={(e) => e.target.value && setDndStart(timeToMinutes(e.target.value))} />
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>To
                <input type="time" value={minutesToTime(dndEnd)} onChange={(e) => e.target.value && setDndEnd(timeToMinutes(e.target.value))} />
              </label>
            </div>
          </SettingToggle>

          {saveError && <p className="un-msg un-error" style={{ margin: 0 }}>{saveError}</p>}
          <button className="primary-button" disabled={busy || uploading || usernameBlocked || usernameShort || Boolean(handleEdited && usernameLockedUntil)} style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
            {uploading ? 'Transmitting Photo…' : usernameCharge > 0 ? <>Save · <CoinIcon size={16} /> {usernameCharge}</> : 'Save Changes'}
          </button>
        </form>
      </div>
    </div>
  )
}

/* =========================================================================
   COMMISSION GUILD MODAL
   ========================================================================= */
// Small two-line choice button used by the guild type / approval pickers.
function GuildChoice({ active, icon, title, hint, onClick, disabled }: { active: boolean; icon: React.ReactNode; title: string; hint?: string; onClick: () => void; disabled?: boolean }) {
  return (
    <button
      type="button"
      disabled={disabled}
      aria-pressed={active}
      onClick={onClick}
      style={{
        flex: 1, display: 'grid', gap: 2, justifyItems: 'center', textAlign: 'center', padding: '0.6rem 0.5rem', borderRadius: 14, cursor: 'pointer', color: 'var(--ink)',
        border: active ? '1.5px solid var(--cyan)' : '1px solid var(--line)', background: active ? 'rgba(255, 107, 53, 0.08)' : 'var(--soft)',
      }}
    >
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: '.84rem', fontWeight: 700 }}>{icon}{title}</span>
      {hint && <small style={{ fontSize: '.68rem', color: 'var(--muted)', lineHeight: 1.3 }}>{hint}</small>}
    </button>
  )
}

function GroupModal({ friends, busy, onClose, onCreate }: any) {
  const [name, setName] = useState('')
  const [selected, setSelected] = useState<string[]>([])
  const [privacy, setPrivacy] = useState<'public' | 'private'>('private')
  const [joinMode, setJoinMode] = useState<'auto' | 'manual'>('manual')

  return (
    <div className="modal-backdrop fp-backdrop">
      <div className="modal-card fp-card">
        <header className="fp-header">
          <button type="button" className="icon-button fp-back" onClick={onClose} aria-label="Back"><ArrowLeft size={20} /></button>
          <h2 className="fp-title">Commission Guild</h2>
        </header>

        <form onSubmit={(e) => { e.preventDefault(); onCreate(name, selected, privacy, joinMode) }} style={{ display: 'grid', gap: '1rem' }}>
          <label style={{ fontSize: '.76rem', color: 'var(--muted)' }}>
            Guild Name
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Squad Elite" maxLength={40} style={{ width: '100%', border: '1px solid var(--line)', borderRadius: 12, padding: '0.75rem', background: 'var(--soft)', color: 'var(--ink)', marginTop: 4 }} />
          </label>

          <div style={{ display: 'grid', gap: '0.5rem' }}>
            <p className="eyebrow" style={{ margin: 0 }}>GUILD TYPE</p>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <GuildChoice active={privacy === 'private'} icon={<Lock size={14} />} title="Private" hint="Only people you add" onClick={() => setPrivacy('private')} />
              <GuildChoice active={privacy === 'public'} icon={<Globe size={14} />} title="Public" hint="Anyone can find it and join" onClick={() => setPrivacy('public')} />
            </div>
            {privacy === 'public' && (
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <GuildChoice active={joinMode === 'manual'} icon={<ShieldCheck size={14} />} title="Manual approval" hint="Admins accept each request" onClick={() => setJoinMode('manual')} />
                <GuildChoice active={joinMode === 'auto'} icon={<Check size={14} />} title="Auto approval" hint="Anyone joins instantly" onClick={() => setJoinMode('auto')} />
              </div>
            )}
          </div>

          <p className="eyebrow" style={{ margin: 0 }}>{privacy === 'public' ? 'ADD FRIENDS (OPTIONAL)' : 'ADD FRIENDS'}</p>
          <div style={{ display: 'grid', gap: '0.5rem' }}>
            {friends.map((f: Chat) => {
              const isSelected = selected.includes(f.id)
              return (
                <button
                  key={f.id}
                  type="button"
                  onClick={() => setSelected((c) => isSelected ? c.filter((x) => x !== f.id) : [...c, f.id])}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '0.75rem 0.75rem',
                    borderRadius: 14,
                    border: isSelected ? '1.5px solid var(--cyan)' : '1px solid var(--line)',
                    background: isSelected ? 'rgba(255, 107, 53, 0.08)' : 'var(--soft)',
                    transition: 'all 0.15s ease',
                    width: '100%',
                    textAlign: 'left'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', minWidth: 0, flex: 1 }}>
                    <Avatar src={f.avatar} name={f.name} size="sm" uid={f.kind === 'group' ? undefined : f.id} showStatus={false} />
                    <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1 }}>
                      <strong style={{ fontSize: '.88rem', color: 'var(--ink)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {f.name}
                      </strong>
                      <small style={{ fontSize: '.72rem', color: 'var(--cyan)' }}>
                        {f.handle}
                      </small>
                    </div>
                  </div>

                  <div style={{
                    width: 24,
                    height: 24,
                    borderRadius: '50%',
                    display: 'grid',
                    placeItems: 'center',
                    flexShrink: 0,
                    marginLeft: 8,
                    background: isSelected ? 'var(--ink)' : 'var(--soft)',
                    border: isSelected ? 'none' : '1px solid var(--line-strong)',
                    color: isSelected ? '#fff' : 'var(--muted)'
                  }}>
                    {isSelected ? <Check size={16} strokeWidth={3} /> : <Plus size={14} />}
                  </div>
                </button>
              )
            })}
          </div>

          <button className="primary-button" disabled={busy || !name.trim() || (privacy === 'private' && !selected.length)}>
            {privacy === 'public' ? 'Create Public Guild' : 'Create Channel'}
          </button>
        </form>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Discover people (icon on every profile): 10 random people at a time, Shuffle brings 10 different ones.
// Same Link / Connect buttons as the Add friend page. People you already follow / are allied with are not offered.
// ---------------------------------------------------------------------------
const DISCOVER_PER_PAGE = 10
const USER_ID_CHARS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
function DiscoverPeopleModal({ userId, following, sentRequests, friendIds, requests, followBusy, hiddenIds, onAdd, onCancel, onFollow, onViewProfile, onClose }: any) {
  const [list, setList] = useState<Profile[]>([])
  const [loading, setLoading] = useState(true)
  const [hint, setHint] = useState('')
  const poolRef = useRef<Profile[]>([])
  const seenRef = useRef<Set<string>>(new Set())
  const aliveRef = useRef(true)
  // Who was already allied / followed when the sheet opened: fixed for this visit so the cards never jump under the thumb.
  const skipRef = useRef<Set<string>>(new Set([...(friendIds || []), ...(following || [])]))
  const incomingFrom = useMemo(() => new Set<string>((requests || []).map((r: any) => String(r.from))), [requests])
  useEffect(() => { aliveRef.current = true; return () => { aliveRef.current = false } }, [])

  const shuffle = <T,>(a: T[]): T[] => { const r = [...a]; for (let i = r.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1)); [r[i], r[j]] = [r[j], r[i]] } return r }
  const usable = (p: Profile) => p.uid !== userId && Boolean(p.name) && !skipRef.current.has(p.uid) && !hiddenIds?.has(p.uid) && !(p as any).deactivated

  const loadRandom = useCallback(async () => {
    setLoading(true); setHint('')
    try {
      // A random starting id + wrap-around gives a different window of people on every call, however many users exist.
      let docs: any[] = []
      try {
        const start = Array.from({ length: 28 }, () => USER_ID_CHARS[Math.floor(Math.random() * USER_ID_CHARS.length)]).join('')
        const base = collection(firestore, 'users')
        const [hi, lo] = await raceTimeout(Promise.all([
          getDocs(query(base, where('__name__', '>=', start), limit(60))),
          getDocs(query(base, where('__name__', '<', start), limit(60))),
        ]), 8000)
        docs = [...hi.docs, ...lo.docs]
      } catch {
        docs = (await raceTimeout(getDocs(query(collection(firestore, 'users'), limit(200))), 8000)).docs
      }
      const byId = new Map<string, Profile>()
      docs.forEach((d) => { const p = { uid: d.id, ...d.data() } as Profile; if (usable(p)) byId.set(p.uid, p) })
      const all = [...byId.values()]
      poolRef.current = all
      // People already shown are skipped until every one in the pool was shown once.
      let fresh = all.filter((p) => !seenRef.current.has(p.uid))
      if (fresh.length < Math.min(DISCOVER_PER_PAGE, all.length)) { seenRef.current.clear(); fresh = all }
      const pick = shuffle(fresh).slice(0, DISCOVER_PER_PAGE)
      pick.forEach((p) => { seenRef.current.add(p.uid); rememberLiveAvatar(p.uid, p.avatar) })
      if (aliveRef.current) setList(pick)
    } catch {
      if (aliveRef.current) { setList([]); setHint('Could not load people. Check your connection and tap Shuffle.') }
    } finally { if (aliveRef.current) setLoading(false) }
  }, [userId])
  useEffect(() => { void loadRandom() }, [loadRandom])

  return (
    <div className="modal-backdrop fp-backdrop" style={{ zIndex: 140 }}>
      <div className="modal-card fp-card">
        <header className="fp-header">
          <button type="button" className="icon-button fp-back" onClick={onClose} aria-label="Back"><ArrowLeft size={20} /></button>
          <h2 className="fp-title">Discover people</h2>
        </header>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
          <p className="eyebrow" style={{ margin: 0 }}>PEOPLE YOU MAY KNOW</p>
          <button type="button" className="text-button primary" disabled={loading} onClick={() => void loadRandom()}>
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Shuffle
          </button>
        </div>
        {hint && <p style={{ margin: '0 0 0.5rem', fontSize: '.78rem', color: 'var(--danger)' }}>{hint}</p>}
        <div className="discover-user-list" style={{ maxHeight: '64vh', overflowY: 'auto' }}>
          {loading && list.length === 0 && <p style={{ textAlign: 'center', color: 'var(--muted)', fontSize: '.84rem', padding: '1.25rem 0' }}>Looking for people…</p>}
          {!loading && list.length === 0 && !hint && (
            <p style={{ textAlign: 'center', color: 'var(--muted)', fontSize: '.84rem', padding: '1.25rem 0' }}>No new people to show right now. Try Shuffle again later.</p>
          )}
          {list.map((target) => (
            <OperativeRow
              key={target.uid}
              target={target}
              following={following}
              friendIds={friendIds}
              sentRequests={sentRequests}
              incomingFrom={incomingFrom}
              followBusy={followBusy}
              onFollow={onFollow}
              onAdd={onAdd}
              onCancel={onCancel}
              onViewProfile={onViewProfile}
            />
          ))}
        </div>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Public Guilds: random picks (10 at a time, Shuffle loads new ones) + search by name.
// auto-approval guild -> "Join" adds you at once; manual-approval guild -> "Request" notifies the admins.
// ---------------------------------------------------------------------------
type PublicGuild = { id: string; name: string; avatar: string; members: number; memberIds: string[]; admins: string[]; ownerId?: string; joinMode: 'auto' | 'manual' }
const GUILD_ID_CHARS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
const GUILDS_PER_PAGE = 10

function PublicGuildsModal({ userId, me, myGroupIds, onClose, onJoined, onRequested }: any) {
  const [q, setQ] = useState('')
  const [list, setList] = useState<PublicGuild[]>([])
  const [loading, setLoading] = useState(true)
  const [reqStatus, setReqStatus] = useState<Record<string, 'pending' | 'declined'>>({})
  const [busyId, setBusyId] = useState<string | null>(null)
  const [hint, setHint] = useState('')
  const poolRef = useRef<PublicGuild[]>([])
  const seenRef = useRef<Set<string>>(new Set())
  const mineRef = useRef<Set<string>>(new Set(myGroupIds || []))
  const aliveRef = useRef(true)
  useEffect(() => { aliveRef.current = true; return () => { aliveRef.current = false } }, [])
  useEffect(() => { mineRef.current = new Set(myGroupIds || []) }, [myGroupIds])

  const toGuild = (d: { id: string; data: () => any }): PublicGuild => {
    const x = d.data()
    const nm = String(x.name || 'Guild')
    const memberIds: string[] = Array.isArray(x.members) ? x.members : []
    return {
      id: d.id, name: nm, avatar: String(x.avatar || fallbackAvatarSvg(nm)), members: memberIds.length, memberIds,
      admins: Array.isArray(x.admins) ? x.admins : [], ownerId: x.ownerId, joinMode: x.joinMode === 'auto' ? 'auto' : 'manual',
    }
  }
  const notMine = (g: PublicGuild) => !mineRef.current.has(g.id) && !g.memberIds.includes(userId)
  const shuffle = <T,>(a: T[]): T[] => { const r = [...a]; for (let i = r.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1)); [r[i], r[j]] = [r[j], r[i]] } return r }

  // 10 random public guilds. A random starting id + wrap-around gives a different window of guilds on every call,
  // so it keeps working no matter how many public guilds exist; guilds already shown are skipped until all were seen.
  const loadRandom = useCallback(async () => {
    setLoading(true); setHint('')
    try {
      const start = Array.from({ length: 20 }, () => GUILD_ID_CHARS[Math.floor(Math.random() * GUILD_ID_CHARS.length)]).join('')
      const base = collection(firestore, 'groupChats')
      const [hi, lo] = await Promise.all([
        getDocs(query(base, where('privacy', '==', 'public'), where('__name__', '>=', start), limit(60))),
        getDocs(query(base, where('privacy', '==', 'public'), where('__name__', '<', start), limit(60))),
      ])
      const byId = new Map<string, PublicGuild>()
      ;[...hi.docs, ...lo.docs].forEach((d) => { const g = toGuild(d); if (notMine(g)) byId.set(g.id, g) })
      const all = [...byId.values()]
      poolRef.current = all
      let fresh = all.filter((g) => !seenRef.current.has(g.id))
      if (fresh.length < Math.min(GUILDS_PER_PAGE, all.length)) { seenRef.current.clear(); fresh = all }
      const pick = shuffle(fresh).slice(0, GUILDS_PER_PAGE)
      pick.forEach((g) => seenRef.current.add(g.id))
      if (aliveRef.current) setList(pick)
    } catch {
      if (aliveRef.current) { setList([]); setHint('Could not load public guilds. Check your connection and try Shuffle.') }
    } finally { if (aliveRef.current) setLoading(false) }
  }, [userId])

  useEffect(() => { void loadRandom() }, [loadRandom])
  useEffect(() => {
    let alive = true
    getDocs(query(collection(firestore, 'groupJoinRequests'), where('uid', '==', userId))).then((snap) => {
      if (!alive) return
      const m: Record<string, 'pending' | 'declined'> = {}
      snap.docs.forEach((d) => { const x = d.data(); if (x.groupId) m[String(x.groupId)] = x.status === 'declined' ? 'declined' : 'pending' })
      setReqStatus(m)
    }).catch(() => undefined)
    return () => { alive = false }
  }, [userId])

  // Search: names that START with the text (server side, any number of guilds) + names that CONTAIN it among the guilds already loaded.
  const term = q.trim().toLowerCase()
  const [found, setFound] = useState<PublicGuild[]>([])
  const [searching, setSearching] = useState(false)
  useEffect(() => {
    if (!term) { setFound([]); setSearching(false); return }
    setSearching(true)
    const timer = window.setTimeout(async () => {
      try {
        const snap = await getDocs(query(collection(firestore, 'groupChats'), where('privacy', '==', 'public'), where('nameLower', '>=', term), where('nameLower', '<=', `${term}\uf8ff`), limit(30)))
        const byId = new Map<string, PublicGuild>()
        snap.docs.forEach((d) => { const g = toGuild(d); if (notMine(g)) byId.set(g.id, g) })
        poolRef.current.forEach((g) => { if (g.name.toLowerCase().includes(term)) byId.set(g.id, g) })
        if (aliveRef.current) setFound([...byId.values()].slice(0, 30))
      } catch {
        if (aliveRef.current) setFound(poolRef.current.filter((g) => g.name.toLowerCase().includes(term)))
      } finally { if (aliveRef.current) setSearching(false) }
    }, 300)
    return () => window.clearTimeout(timer)
  }, [term])

  const join = async (g: PublicGuild) => {
    setBusyId(g.id); setHint('')
    try {
      if (g.joinMode === 'auto') {
        await updateDoc(doc(firestore, 'groupChats', g.id), { members: arrayUnion(userId) })
        onJoined(g)
      } else {
        // The avatar is only stored when it is a real image link (a generated data: avatar is rebuilt from the name instead).
        const avatar = typeof me.avatar === 'string' && !me.avatar.startsWith('data:') ? me.avatar.slice(0, 900) : ''
        await setDoc(doc(firestore, 'groupJoinRequests', `${g.id}_${userId}`), {
          uid: userId, groupId: g.id, groupName: g.name.slice(0, 80), name: String(me.name || 'Someone').slice(0, 80),
          username: String(me.username || '').slice(0, 40), avatar, status: 'pending', createdAt: serverTimestamp(),
        })
        setReqStatus((s) => ({ ...s, [g.id]: 'pending' }))
        onRequested(g)
      }
    } catch {
      setHint(g.joinMode === 'auto' ? 'Could not join - the guild may be full or no longer open.' : 'Could not send the request - the guild may have changed its settings.')
    } finally { setBusyId(null) }
  }
  const cancel = async (g: PublicGuild) => {
    setBusyId(g.id); setHint('')
    try {
      await deleteDoc(doc(firestore, 'groupJoinRequests', `${g.id}_${userId}`))
      setReqStatus((s) => { const n = { ...s }; delete n[g.id]; return n })
    } catch { setHint('Could not cancel the request.') } finally { setBusyId(null) }
  }

  const shown = term ? found : list
  const busyNow = term ? searching : loading
  return (
    <div className="modal-backdrop fp-backdrop">
      <div className="modal-card fp-card">
        <header className="fp-header">
          <button type="button" className="icon-button fp-back" onClick={onClose} aria-label="Back"><ArrowLeft size={20} /></button>
          <h2 className="fp-title">Public Guilds</h2>
        </header>

        <div className="search-box" style={{ marginBottom: '0.75rem' }}>
          <Search size={16} />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search public guilds" maxLength={40} />
        </div>

        {!term && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
            <p className="eyebrow" style={{ margin: 0 }}>PICKS FOR YOU</p>
            <button type="button" className="text-button primary" disabled={loading} onClick={() => void loadRandom()}>
              <RefreshCw size={14} /> Shuffle
            </button>
          </div>
        )}

        {hint && <p style={{ margin: '0 0 0.5rem', fontSize: '.78rem', color: 'var(--danger)' }}>{hint}</p>}

        <div style={{ display: 'grid', gap: '0.25rem', maxHeight: '52vh', overflowY: 'auto' }}>
          {busyNow && shown.length === 0 && <p style={{ textAlign: 'center', color: 'var(--muted)', fontSize: '.84rem', padding: '1.25rem 0' }}>Looking for guilds…</p>}
          {!busyNow && shown.length === 0 && (
            <p style={{ textAlign: 'center', color: 'var(--muted)', fontSize: '.84rem', padding: '1.25rem 0' }}>
              {term ? `No public guild found for "${q.trim()}".` : 'No public guilds to show yet. Create one and make it public!'}
            </p>
          )}
          {shown.map((g) => {
            const st = reqStatus[g.id]
            const busyRow = busyId === g.id
            return (
              <div key={g.id} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.6rem 0', }}>
                <Avatar src={g.avatar} name={g.name} size="sm" showStatus={false} />
                <div className="chat-copy" style={{ minWidth: 0, flex: 1 }}>
                  <strong style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{g.name}</strong>
                  <small>{g.members} {g.members === 1 ? 'member' : 'members'} · {g.joinMode === 'auto' ? 'Instant join' : 'Needs approval'}</small>
                </div>
                {g.joinMode === 'auto' ? (
                  <button type="button" className="primary-button" style={{ minHeight: 34, padding: '0 0.9rem' }} disabled={busyRow} onClick={() => void join(g)}>{busyRow ? '…' : 'Join'}</button>
                ) : st === 'pending' ? (
                  <button type="button" className="text-button" style={{ minHeight: 34 }} disabled={busyRow} onClick={() => void cancel(g)} title="Tap to cancel your request">{busyRow ? '…' : 'Requested'}</button>
                ) : st === 'declined' ? (
                  <span style={{ fontSize: '.74rem', color: 'var(--muted)' }}>Declined</span>
                ) : (
                  <button type="button" className="primary-button" style={{ minHeight: 34, padding: '0 0.9rem' }} disabled={busyRow} onClick={() => void join(g)}>{busyRow ? '…' : 'Request'}</button>
                )}
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

function FriendsListModal({ friends, busy, onClose, onOpenChat, onRemoveFriend }: any) {
  // Full-screen page (was a small popup card): the whole Allies list with search, so nothing overlaps or gets cut off.
  const [q, setQ] = useState('')
  const needle = q.trim().toLowerCase()
  const shown: Chat[] = needle ? friends.filter((f: Chat) => `${f.name} ${f.handle || ''}`.toLowerCase().includes(needle)) : friends
  return (
    <div className="fs-page" role="dialog" aria-modal="true" aria-label="Allies">
      <header className="fs-top">
        <button className="icon-button" onClick={onClose} aria-label="Back"><ArrowLeft size={18} /></button>
        <h2>Allies <span className="fs-count">{friends.length}</span></h2>
      </header>
      <div className="fs-body">
        {friends.length > 6 && (
          <div className="fs-search">
            <Search size={16} />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search allies" />
            {q && <button type="button" onClick={() => setQ('')} aria-label="Clear search"><X size={14} /></button>}
          </div>
        )}
        {shown.map((f: Chat) => (
          <div key={f.id} className="fs-row">
            <Avatar src={f.avatar} name={f.name} size="md" uid={f.kind === 'group' ? undefined : f.id} showStatus={false} />
            <div className="fs-row-main">
              <strong>{f.name}</strong>
              <small>{f.handle}</small>
            </div>
            <button className="text-button primary" onClick={() => onOpenChat(f)}>Chat</button>
            <button className="fs-x" onClick={() => onRemoveFriend(f)} aria-label={`Unlink ${f.name}`}>
              <UserMinus size={17} color="var(--danger)" />
            </button>
          </div>
        ))}
        {!shown.length && (
          <div className="fs-empty"><Users size={30} /><p>{friends.length ? 'No allies match your search.' : 'No allies yet. Find people in Discover and send a request.'}</p></div>
        )}
      </div>
    </div>
  )
}

/* =========================================================================
   FULL-SCREEN PROFILE VIEW (someone else's Identity Card) - same fixed,
   edge-to-edge overlay pattern as ChatView (own profile lives in the Orbit
   tab the same way), instead of a small popup window.
   Locks the Transmission Vault behind a "Private Account" wall when the
   owner has flipped their profile private and the viewer isn't Linked yet.
   ========================================================================= */
// Snapchat-style info chips on a profile: activity streak, coin balance and (for Allies) the relationship.
// The streak only counts while it is alive (last active today or yesterday), otherwise it shows 0.
function ProfileChips({ profile, showRelation = false, isBestFriend = false }: { profile: Profile; showRelation?: boolean; isBestFriend?: boolean }) {
  const alive = profile.lastActiveDay === localDayKey(0) || profile.lastActiveDay === localDayKey(-1)
  const streak = alive ? Math.max(0, Number(profile.activityStreak || 0)) : 0
  const chip: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 8, padding: '0.25rem 0.75rem', borderRadius: 999, border: '1px solid var(--line-strong)', background: 'var(--paper)', color: 'var(--ink)', fontSize: '.78rem', fontWeight: 700, whiteSpace: 'nowrap' }
  return (
    <div className="pfx-chips" style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '0.5rem', marginTop: '0.75rem', maxWidth: '100%' }}>
      <span style={chip} title="Daily activity streak"><Flame size={15} /> {streak.toLocaleString('en-IN')} {streak === 1 ? 'day' : 'days'}</span>
      <span style={chip} title="Coins"><CoinIcon size={15} /> {Math.max(0, Number(profile.publicCoins || 0)).toLocaleString('en-IN')}</span>
      {showRelation && (
        <span style={{ ...chip, color: isBestFriend ? '#fff' : 'var(--online-green)', background: isBestFriend ? 'var(--solid)' : 'var(--paper)' }}>
          {isBestFriend ? <><Heart size={14} /> Best Friends</> : <><Users size={14} /> Allies</>}
        </span>
      )}
    </div>
  )
}

function ProfileViewModal({ profile, loading, isOwnProfile, isFollowing, isFriend, isBestFriend = false, requested, posts, busy, followBusy, onClose, onFollow, onOpenDiscover, onRequest, onCancelRequest, onOpenFollowList, onOpenPostPreview, onOpenSupport, onChat, subscription, onUnsubscribe, blockMode = null, blockBusy = false, onBlockAction, onReportAccount, isAdmin = false, onAdminRestrict, onAdminBan }: any) {
  const [showSafety, setShowSafety] = useState(false)
  const [confirmBlock, setConfirmBlock] = useState(false)
  const [showUnsub, setShowUnsub] = useState(false)
  const [unsubBusy, setUnsubBusy] = useState(false)
  // Private account: posts, reels, stories, bio and the follow lists are for CONNECTED people (Allies) only. Following is not enough.
  const isLocked = Boolean(profile?.private) && !isFriend && !isOwnProfile
  const isUnavailable = Boolean(profile?.deactivated)

  return (
    <div
      className="profile-view"
      style={{
        position: 'fixed',
        left: 0,
        right: 0,
        top: 0,
        width: '100%',
        maxWidth: 520,
        margin: '0 auto',
        height: '100dvh',
        zIndex: 80,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        background: 'var(--bg)',
      }}
    >
      <header className="chat-topbar" style={{ flex: '0 0 58px', position: 'relative', zIndex: 10 }}>
        <button className="icon-button" onClick={onClose} aria-label="Back"><ArrowLeft size={18} /></button>
        <strong style={{ fontSize: '.95rem', color: 'var(--ink)' }}>{loading || !profile ? 'Identity Card' : `@${profile.username}`}</strong>
        {!loading && profile && !isOwnProfile && !profile.deactivated && (
          <button className="icon-button" style={{ marginLeft: 'auto', fontSize: '1.2rem', lineHeight: 1 }} onClick={() => { setConfirmBlock(false); setShowSafety(true) }} aria-label="More options">⋯</button>
        )}
      </header>

      {showSafety && typeof document !== 'undefined' && createPortal(
        <div className="action-sheet-backdrop" style={{ zIndex: 130 }} onClick={() => { if (!blockBusy) setShowSafety(false) }}>
          <div className="action-sheet" onClick={(e) => e.stopPropagation()}>
            {confirmBlock ? (
              <>
                <h3 style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--ink)', margin: '0 0 0.5rem' }}>Block @{profile?.username}?</h3>
                <p style={{ fontSize: '.8rem', color: 'var(--muted)', lineHeight: 1.6, margin: '0 0 1rem' }}>
                  You will both lose the link: no messages, requests, gifts, follows or new comments either way, and any existing follow or friendship is removed. They can no longer see your posts and stories. They are not notified.
                </p>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button className="text-button" style={{ flex: 1, justifyContent: 'center' }} disabled={blockBusy} onClick={() => setConfirmBlock(false)}>Back</button>
                  <button className="text-button danger" style={{ flex: 1, justifyContent: 'center' }} disabled={blockBusy}
                    onClick={async () => { if (await onBlockAction?.('block')) setShowSafety(false) }}>
                    {blockBusy ? 'Blocking…' : 'Block'}
                  </button>
                </div>
              </>
            ) : (
              <div style={{ display: 'grid', gap: '0.5rem' }}>
                <h3 style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--ink)', margin: '0 0 0.25rem' }}>@{profile?.username}</h3>
                {blockMode === 'block' ? (
                  <button className="text-button" style={{ justifyContent: 'flex-start', gap: 8 }} disabled={blockBusy}
                    onClick={async () => { if (await onBlockAction?.('unblock')) setShowSafety(false) }}>
                    <UserX size={16} /> Unblock
                  </button>
                ) : (
                  <>
                    <button className="text-button danger" style={{ justifyContent: 'flex-start', gap: 8 }} disabled={blockBusy} onClick={() => setConfirmBlock(true)}>
                      <Ban size={16} /> Block
                    </button>
                    <button className="text-button" style={{ justifyContent: 'flex-start', gap: 8 }} disabled={blockBusy}
                      onClick={async () => { if (await onBlockAction?.(blockMode === 'restrict' ? 'unrestrict' : 'restrict')) setShowSafety(false) }}>
                      <ShieldAlert size={16} /> {blockMode === 'restrict' ? 'Remove restriction' : 'Restrict'}
                    </button>
                    <small style={{ color: 'var(--muted)', fontSize: '.7rem', margin: '-.2rem 0 0.25rem 2rem' }}>Their alerts and requests stay quiet, and their comments on your posts are visible only to them and you.</small>
                    <button className="text-button" style={{ justifyContent: 'flex-start', gap: 8 }} disabled={blockBusy}
                      onClick={async () => { if (await onBlockAction?.(blockMode === 'hide' ? 'unhide' : 'hide')) setShowSafety(false) }}>
                      <EyeOff size={16} /> {blockMode === 'hide' ? 'Show their posts again' : 'Hide their posts'}
                    </button>
                  </>
                )}
                <button className="text-button" style={{ justifyContent: 'flex-start', gap: 8 }} onClick={() => { setShowSafety(false); onReportAccount?.() }}>
                  <Flag size={16} /> Report account
                </button>
                {isAdmin && profile && (
                  <div style={{ display: 'grid', gap: '0.5rem', paddingTop: '0.5rem', marginTop: '0.25rem' }}>
                    <small style={{ fontWeight: 700, color: 'var(--gold)', display: 'flex', alignItems: 'center', gap: 4 }}><ShieldAlert size={14} /> Admin actions</small>
                    <button className="text-button danger" style={{ justifyContent: 'flex-start', gap: 8 }} onClick={() => { setShowSafety(false); onAdminBan?.() }}>
                      <Ban size={16} /> Ban account…
                    </button>
                    <button className="text-button" style={{ justifyContent: 'flex-start', gap: 8 }} onClick={() => { setShowSafety(false); onAdminRestrict?.('chat') }}>
                      <MessageCircle size={16} /> {restrictLeft(profile.chatBanUntil) ? `Chat banned ${restrictWhen(restrictLeft(profile.chatBanUntil))} - change / lift` : 'Chat ban…'}
                    </button>
                    <button className="text-button" style={{ justifyContent: 'flex-start', gap: 8 }} onClick={() => { setShowSafety(false); onAdminRestrict?.('upload') }}>
                      <Upload size={16} /> {restrictLeft(profile.uploadHoldUntil) ? `Uploads held ${restrictWhen(restrictLeft(profile.uploadHoldUntil))} - change / lift` : 'Upload hold…'}
                    </button>
                    <small style={{ color: 'var(--muted)', fontSize: '.7rem' }}>To remove a post: open it from this profile and tap the red bin.</small>
                  </div>
                )}
                <button className="text-button" style={{ justifyContent: 'center' }} onClick={() => setShowSafety(false)}>Cancel</button>
              </div>
            )}
          </div>
        </div>,
        document.body
      )}

      <div style={{ flex: '1 1 0%', minHeight: 0, overflowY: 'auto', overflowX: 'hidden', padding: '0 0.75rem 2rem' }}>
        {loading || !profile ? (
          <div style={{ paddingTop: '1rem' }}><SkeletonRows count={4} avatar /></div>
        ) : blockMode === 'block' && !isOwnProfile ? (
          <div style={{ display: 'grid', justifyItems: 'center', gap: '0.5rem', padding: '3rem 1rem', textAlign: 'center' }}>
            <Ban size={34} color="var(--muted)" />
            <strong style={{ fontSize: '1rem', color: 'var(--ink)' }}>You blocked @{profile.username}</strong>
            <p style={{ margin: 0, fontSize: '.82rem', color: 'var(--muted)', maxWidth: 270 }}>
              You can&apos;t see each other&apos;s activity or message each other. Unblocking does not restore any earlier link or friendship.
            </p>
            <button className="text-button" style={{ marginTop: '0.5rem' }} disabled={blockBusy} onClick={() => onBlockAction?.('unblock')}>
              {blockBusy ? 'Unblocking…' : 'Unblock'}
            </button>
          </div>
        ) : isUnavailable ? (
          <div style={{ display: 'grid', justifyItems: 'center', gap: '0.5rem', padding: '3rem 1rem', textAlign: 'center' }}>
            <UserX size={34} color="var(--muted)" />
            <strong style={{ fontSize: '1rem', color: 'var(--ink)' }}>Account Unavailable</strong>
            <p style={{ margin: 0, fontSize: '.82rem', color: 'var(--muted)', maxWidth: 260 }}>
              This account is currently unavailable.
            </p>
          </div>
        ) : (
          <div className="pfx">
            {/* Clean edge-to-edge layout: full-bleed cover, circle avatar, left-aligned identity, stat numbers, action row, vault */}
            <div className={`pfx-cover${profile.coverUrl || profile.equipped?.banner ? ' has-img' : ''}`} aria-hidden="true">
              {profile.equipped?.banner
                ? <PrizeBanner id={profile.equipped.banner} />
                : profile.coverUrl && <img className="pfx-cover-img" src={profile.coverUrl} alt="" decoding="async" />}
            </div>

            <div className="pfx-id">
              <ProfileAvatarRing profile={profile} />
              <h2 className="pfx-name"><NameFx id={profile.equipped?.namefx}>{profile.name}</NameFx></h2>
              <div className="pfx-handle">
                <CountryFlag code={profile.country} height={14} />
                <span>@{profile.username}</span>
                {hasGreenTick(profile) && <GreenTick size={20} />}
                {profile.private && <Lock size={14} color="var(--muted)" />}
                {profile.equipped?.badge && <PrizeBadge id={profile.equipped.badge} />}
              </div>

              <ProfileChips profile={profile} showRelation={!isOwnProfile && isFriend} isBestFriend={isBestFriend} />

              {!isLocked && profile.bio && <p className="pfx-bio">{profile.bio}</p>}

              <div className="pfx-stats">
                <div className="pfx-stat"><b>{formatCount(posts.length)}</b><span>Posts</span></div>
                <button type="button" className="pfx-stat" disabled={isLocked} onClick={() => { if (!isLocked) onOpenFollowList(profile.uid, profile.name, 'followers') }}>
                  <b>{formatCount(Math.max(0, profile.followersCount || 0))}</b><span>Allies</span>
                </button>
                <button type="button" className="pfx-stat" disabled={isLocked} onClick={() => { if (!isLocked) onOpenFollowList(profile.uid, profile.name, 'following') }}>
                  <b>{formatCount(Math.max(0, profile.followingCount || 0))}</b><span>Linked</span>
                </button>
              </div>
            </div>

            {!isOwnProfile && (
              <>
                <div className="pfx-row">
                  <button type="button" className="pfx-btn pri" onClick={onChat}>
                    <MessageCircle size={18} /> <span>{isFriend ? 'Chat' : 'Message request'}</span>
                  </button>
                  <button type="button" className="pfx-btn" onClick={onFollow} disabled={followBusy === profile.uid}>
                    {!isFollowing && <UserPlus size={16} />} <span>{isFollowing ? 'Linked' : 'Link'}</span>
                  </button>
                  {onOpenDiscover && (
                    <button type="button" className="pfx-ico" onClick={onOpenDiscover} aria-label="Discover people" title="Discover people">
                      <UserPlus size={20} />
                    </button>
                  )}
                </div>

                {/* Allies / Best Friends status already shows as a chip above; only Pending and Connect need a button */}
                {!isFriend && (
                  <div className="pfx-row pfx-row-tight">
                    {requested ? (
                      <>
                        <span className="pfx-btn pfx-static">Pending</span>
                        <button type="button" className="pfx-btn" onClick={onCancelRequest}>Cancel</button>
                      </>
                    ) : (
                      <button type="button" className="pfx-btn" onClick={onRequest} disabled={busy}>Connect</button>
                    )}
                  </div>
                )}

                <div className="pfx-row pfx-row-tight">
                  <button type="button" className="pfx-btn gold" onClick={() => onOpenSupport(profile.uid, profile.name)}>
                    <Gem size={18} color="currentColor" /> <span>{subscription?.autoRenew ? 'Subscribed · Send Gift' : 'Subscribe / Send Gift'}</span>
                  </button>
                </div>

                {subscription?.autoRenew && (
                  <div className="pfx-row pfx-row-tight">
                    <button type="button" className="pfx-btn danger" onClick={() => setShowUnsub(true)}>Unsubscribe</button>
                  </div>
                )}
              </>
            )}
            {isOwnProfile && onOpenDiscover && (
              <div className="pfx-row">
                <button type="button" className="pfx-ico" onClick={onOpenDiscover} aria-label="Discover people" title="Discover people">
                  <UserPlus size={20} />
                </button>
              </div>
            )}
            {showUnsub && (
              <UnsubscribeConfirm
                creatorName={profile.name}
                until={subscription?.expiresAt}
                busy={unsubBusy}
                onCancel={() => setShowUnsub(false)}
                onConfirm={async () => {
                  setUnsubBusy(true)
                  try { await onUnsubscribe?.() } finally { setUnsubBusy(false); setShowUnsub(false) }
                }}
              />
            )}

            <div className="pfx-vault">
              {isLocked ? (
                <div style={{ display: 'grid', justifyItems: 'center', gap: '0.5rem', padding: '2.5rem 1rem', textAlign: 'center' }}>
                  <Lock size={30} color="var(--muted)" />
                  <strong style={{ fontSize: '.94rem', color: 'var(--ink)' }}>This Account is Private</strong>
                  <p style={{ margin: 0, fontSize: '.8rem', color: 'var(--muted)', maxWidth: 260 }}>
                    Connect with {profile.name.split(' ')[0]} and get accepted to see their posts, reels and stories.
                  </p>
                </div>
              ) : (
                <>
                  <VaultHeading count={posts.length} />
                  {/* Grid of posts - Tapping opens full preview modal! */}
                  <VaultGrid posts={posts} onOpenPostPreview={onOpenPostPreview} />
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

function ReferralCodeModal({ user, onClose }: { user: FirebaseUser; onClose: () => void }) {
  const [code, setCode] = useState(() => { try { return normalizeReferralCode(localStorage.getItem('animora_ref')) } catch { return '' } })
  const [state, setState] = useState<'loading' | 'open' | 'used'>('loading')
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState('')
  const [done, setDone] = useState(false)

  const call = async (payload: Record<string, unknown>) => {
    const res = await fetch('/api/referral', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${await user.getIdToken()}` },
      body: JSON.stringify(payload),
    })
    return { res, d: await res.json().catch(() => ({})) }
  }

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const { d } = await call({ action: 'status' })
        if (!cancelled) setState(d?.claimed ? 'used' : 'open')
      } catch { if (!cancelled) setState('open') }
    })()
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const submit = async () => {
    const c = normalizeReferralCode(code)
    if (!c) { setMsg('Please enter a valid 8-character referral code.'); return }
    setBusy(true); setMsg('')
    try {
      let deviceHash = ''
      try { deviceHash = (await getDeviceHash()) || '' } catch { /* optional */ }
      const { res, d } = await call({ action: 'claim', code: c, deviceHash })
      if (d?.ok) { try { localStorage.removeItem('animora_ref') } catch { /* ignore */ } setDone(true); setState('used'); return }
      const status = d?.status || d?.error
      setMsg(
        status === 'already-set' ? 'You have already used a referral code. Only one code per account is allowed.'
        : status === 'invalid-code' ? 'That referral code is not valid. Please check it and try again.'
        : status === 'too-late' ? 'Referral codes can only be added within 7 days of creating your account.'
        : status === 'age-required' ? 'Please finish your age verification first.'
        : res.status === 429 ? 'Too many attempts - please wait a minute.'
        : 'Could not apply the code. Please try again.'
      )
      if (status === 'already-set') setState('used')
    } catch { setMsg('Network problem. Please try again.') }
    finally { setBusy(false) }
  }

  return (
    <div className="modal-backdrop fp-backdrop">
      <div className="modal-card fp-card">
        <header className="fp-header">
          <button type="button" className="icon-button fp-back" onClick={onClose} disabled={busy} aria-label="Back"><ArrowLeft size={20} /></button>
          <h2 className="fp-title">Referral code</h2>
        </header>
        {state === 'loading' && <SkeletonRows count={3} />}
        {state === 'used' && (
          <p style={{ fontSize: '.85rem', color: 'var(--ink)', lineHeight: 1.55 }}>
            {done ? 'Referral code applied! Your welcome coin will show up in your wallet in a moment.' : 'A referral code is already applied to this account. Only one code can be used per account.'}
          </p>
        )}
        {state === 'open' && (
          <>
            <p style={{ fontSize: '.78rem', color: 'var(--muted)', lineHeight: 1.55, margin: '0 0 0.75rem' }}>
              Got a friend&apos;s referral code? Enter it here - you both earn coins. It can be used only once per account.
            </p>
            <input
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 8))}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void submit() } }}
              maxLength={8}
              placeholder="ABCD2345"
              autoCapitalize="characters"
              autoCorrect="off"
              style={{ width: '100%', border: '1px solid var(--line)', borderRadius: 12, padding: '0.75rem 0.75rem', background: 'var(--soft)', color: 'var(--ink)', letterSpacing: '.14em', marginBottom: '0.75rem' }}
            />
            {msg && <p style={{ color: 'var(--danger)', fontSize: '.76rem', margin: '0 0 0.75rem' }}>{msg}</p>}
            <button className="primary-button" style={{ width: '100%' }} disabled={busy || code.length < 8} onClick={() => void submit()}>
              {busy ? 'Applying…' : 'Apply code'}
            </button>
          </>
        )}
      </div>
    </div>
  )
}

function ReferralCongratsModal({ coins, onClose }: { coins: number; onClose: () => void }) {
  return (
    <div className="modal-backdrop" style={{ zIndex: 9999 }} onClick={onClose}>
      <div className="modal-card" onClick={(e) => e.stopPropagation()} style={{ textAlign: 'center', display: 'grid', gap: '0.5rem', justifyItems: 'center' }}>
        <div style={{ lineHeight: 1 }}><PosterIcon name="party" size={56} /></div>
        <h2 style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--ink)', margin: 0 }}>Congratulations!</h2>
        <p style={{ fontSize: '.95rem', color: 'var(--ink)', margin: 0 }}>
          You got <strong>{coins} {coins === 1 ? 'coin' : 'coins'}</strong> from referral.
        </p>
        <p style={{ fontSize: '.76rem', color: 'var(--muted)', margin: 0 }}>Use them in your Wallet to unlock premium themes.</p>
        <button className="primary-button" style={{ width: '100%', marginTop: '0.5rem' }} onClick={onClose}>Awesome</button>
      </div>
    </div>
  )
}

function CommentFilterModal({ uid, onClose, notify }: { uid: string; onClose: () => void; notify: (m: string) => void }) {
  const [on, setOn] = useState(true)
  const [words, setWords] = useState<string[]>([])
  const [draft, setDraft] = useState('')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const ref = doc(firestore, 'users', uid, 'private', 'commentFilter')

  useEffect(() => {
    let cancelled = false
    getDoc(ref).then((snap) => {
      if (cancelled) return
      const d: any = snap.exists() ? snap.data() : null
      if (d) { setOn(d.on !== false); setWords(cleanFilterWords(d.words)) }
    }).catch(() => {}).finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid])

  const save = async (nextOn: boolean, nextWords: string[]) => {
    setSaving(true)
    try {
      await setDoc(ref, { on: nextOn, words: nextWords })
      setOn(nextOn); setWords(nextWords)
    } catch { notify('Could not save the filter. Please try again.') }
    finally { setSaving(false) }
  }
  const add = async () => {
    const next = cleanFilterWords([...words, ...draft.split(',')])
    if (next.length === words.length) { setDraft(''); return }
    setDraft('')
    await save(on, next)
  }

  return (
    <div className="modal-backdrop fp-backdrop">
      <div className="modal-card fp-card">
        <header className="fp-header">
          <button type="button" className="icon-button fp-back" onClick={onClose} disabled={saving} aria-label="Back"><ArrowLeft size={20} /></button>
          <h2 className="fp-title">Comment filter</h2>
        </header>
        <p style={{ fontSize: '.78rem', color: 'var(--muted)', lineHeight: 1.55, margin: '0 0 0.75rem' }}>
          Comments on your posts and stories that contain any of these words can&apos;t be posted. Matching ignores capital letters and accents and also catches the word inside longer words. Your own comments are never filtered.
        </p>
        {loading ? <SkeletonRows count={4} avatar /> : (
          <>
            <button type="button" className={`text-button ${on ? 'primary' : ''}`} style={{ justifyContent: 'center', marginBottom: '0.75rem' }} disabled={saving} onClick={() => save(!on, words)}>
              {on ? 'Filter is ON' : 'Filter is OFF'}
            </button>
            <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem' }}>
              <input
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void add() } }}
                maxLength={COMMENT_FILTER_MAX_WORD_LEN * 4}
                placeholder="Add a word (or a few, separated by commas)"
                style={{ flex: 1, border: '1px solid var(--line)', borderRadius: 12, padding: '0.5rem 0.75rem', background: 'var(--soft)', color: 'var(--ink)' }}
              />
              <button type="button" className="primary-button" disabled={saving || !draft.trim() || words.length >= COMMENT_FILTER_MAX_WORDS} onClick={() => void add()}>Add</button>
            </div>
            <div style={{ overflowY: 'auto', display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
              {words.length === 0 && <span style={{ fontSize: '.78rem', color: 'var(--muted)' }}>No words yet.</span>}
              {words.map((w) => (
                <button key={w} type="button" disabled={saving} onClick={() => void save(on, words.filter((x) => x !== w))}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '0.25rem 0.5rem', borderRadius: 999, border: '1px solid var(--line)', background: 'var(--soft)', color: 'var(--ink)', fontSize: '.78rem' }}>
                  {w} <X size={14} />
                </button>
              ))}
            </div>
            <small style={{ marginTop: '0.5rem', fontSize: '.7rem', color: 'var(--muted)' }}>{words.length} / {COMMENT_FILTER_MAX_WORDS} words</small>
          </>
        )}
      </div>
    </div>
  )
}

// Requests folder: first DMs from people who are not friends / followers. Tap a row to read it (it is NOT marked read),
// then Accept (moves to normal chats), Delete or Block from here or inside the chat.
function MessageRequestsModal({ requests, busy, onClose, onOpen, onAccept, onDelete }: any) {
  return (
    <div className="modal-backdrop fp-backdrop">
      <div className="modal-card fp-card">
        <header className="fp-header">
          <button type="button" className="icon-button fp-back" onClick={onClose} aria-label="Back"><ArrowLeft size={20} /></button>
          <h2 className="fp-title">Message requests</h2>
        </header>
        {!requests.length ? (
          <div style={{ textAlign: 'center', display: 'grid', gap: 8, justifyItems: 'center', padding: '2rem 1rem', color: 'var(--muted)' }}>
            <MessageCircle size={30} />
            <strong style={{ fontSize: '.92rem', color: 'var(--ink)' }}>No requests</strong>
            <span style={{ fontSize: '.78rem' }}>When someone who is not your friend or follower messages you, it shows up here first.</span>
          </div>
        ) : (
          <div style={{ overflowY: 'auto', display: 'grid', gap: '0.5rem' }}>
            {requests.map((r: DmRequest) => (
              <div key={r.id} style={{ display: 'grid', gap: '0.5rem', padding: '0.5rem 0.75rem', borderRadius: 14, border: '1px solid var(--line)', background: 'var(--soft)' }}>
                <button type="button" onClick={() => onOpen(r)} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', minWidth: 0, background: 'transparent', textAlign: 'left' }}>
                  <Avatar src={r.avatar} name={r.name} size="sm" uid={r.otherId} showStatus={false} />
                  <span style={{ minWidth: 0, flex: 1 }}>
                    <strong style={{ display: 'block', fontSize: '.84rem', color: 'var(--ink)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.name}</strong>
                    <small style={{ display: 'block', fontSize: '.74rem', color: 'var(--muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}><IconText text={r.lastMessage || 'Sent you a message'} /></small>
                  </span>
                  <small style={{ fontSize: '.66rem', color: 'var(--muted)', flexShrink: 0 }}>{r.time}</small>
                </button>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button className="text-button primary" disabled={busy} onClick={() => onAccept(r)} style={{ flex: 1, justifyContent: 'center' }}>Accept</button>
                  <button className="text-button" disabled={busy} onClick={() => onDelete(r)} style={{ flex: 1, justifyContent: 'center' }}>Delete</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

const NOTIF_KINDS: { id: string; label: string; hint: string }[] = [
  { id: 'message', label: 'Messages', hint: 'Push alerts for new chat and guild messages' },
  { id: 'like', label: 'Likes', hint: 'When someone likes your post, reel or story' },
  { id: 'comment', label: 'Comments and replies', hint: 'On your posts, reels and stories' },
  { id: 'mention', label: 'Mentions', hint: 'When someone @mentions you' },
  { id: 'friend_request', label: 'Link requests', hint: 'New ally / link requests' },
  { id: 'group_request', label: 'Guild join requests', hint: 'People asking to join your guild' },
  { id: 'gift', label: 'Gifts', hint: 'When someone sends you a gift' },
]
function NotificationSettingsModal({ uid, off, onClose }: any) {
  const [local, setLocal] = useState<string[]>(off)
  const [err, setErr] = useState('')
  const toggle = async (id: string) => {
    const next = local.includes(id) ? local.filter((x) => x !== id) : [...local, id]
    const prev = local
    setLocal(next)
    setErr('')
    try { await updateDoc(doc(firestore, 'users', uid), { notifOff: next }) } catch { setLocal(prev); setErr('Could not save that. Try again.') }
  }
  return (
    <div className="modal-backdrop fp-backdrop">
      <div className="modal-card fp-card">
        <header className="fp-header">
          <button type="button" className="icon-button fp-back" onClick={onClose} aria-label="Back"><ArrowLeft size={20} /></button>
          <h2 className="fp-title">Notifications</h2>
        </header>
        <div style={{ display: 'grid', gap: 8 }}>
          {NOTIF_KINDS.map((k) => {
            const on = !local.includes(k.id)
            return (
              <button key={k.id} type="button" role="switch" aria-checked={on} onClick={() => void toggle(k.id)}
                style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px', borderRadius: 14, border: '1px solid var(--line)', background: 'var(--soft)', textAlign: 'left', width: '100%' }}>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <strong style={{ display: 'block', fontSize: '.86rem', color: 'var(--ink)' }}>{k.label}</strong>
                  <small style={{ fontSize: '.72rem', color: 'var(--muted)' }}>{k.hint}</small>
                </span>
                <span aria-hidden="true" style={{ width: 42, height: 24, borderRadius: 999, background: on ? 'var(--accent)' : 'var(--line-strong, #cbd5e1)', position: 'relative', flexShrink: 0, transition: 'background .15s ease' }}>
                  <span style={{ position: 'absolute', top: 3, left: on ? 21 : 3, width: 18, height: 18, borderRadius: '50%', background: '#fff', transition: 'left .15s ease' }} />
                </span>
              </button>
            )
          })}
        </div>
        {err && <p style={{ color: 'var(--danger, #dc2626)', fontSize: '.78rem', margin: '10px 0 0' }}>{err}</p>}
        <p style={{ color: 'var(--muted)', fontSize: '.72rem', margin: '12px 2px 0' }}>Calls and moderation notices from Animora always come through.</p>
      </div>
    </div>
  )
}

function HashtagModal({ tag, posts, onClose }: any) {
  const matches: Post[] = (posts as Post[])
    .filter((p: Post) => !p.promoted && Array.isArray(p.tags) && p.tags.some((t: string) => normTag(t) === tag))
    .sort((a: Post, b: Post) => (b.likes?.length || 0) - (a.likes?.length || 0))
  return (
    <div className="modal-backdrop fp-backdrop">
      <div className="modal-card fp-card">
        <header className="fp-header">
          <button type="button" className="icon-button fp-back" onClick={onClose} aria-label="Back"><ArrowLeft size={20} /></button>
          <h2 className="fp-title">#{tag}</h2>
        </header>
        {!matches.length ? (
          <div style={{ textAlign: 'center', display: 'grid', gap: 8, justifyItems: 'center', padding: '2rem 1rem', color: 'var(--muted)' }}>
            <strong style={{ fontSize: '.92rem', color: 'var(--ink)' }}>No posts with #{tag} yet</strong>
            <span style={{ fontSize: '.78rem' }}>Posts and reels you can see with this tag will show up here.</span>
          </div>
        ) : (
          <>
            <p style={{ margin: '0 0 8px', fontSize: '.78rem', color: 'var(--muted)' }}>{matches.length} {matches.length === 1 ? 'post' : 'posts'}</p>
            <div style={{ overflowY: 'auto', display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 6 }}>
              {matches.map((p: Post) => (
                <a key={p.id} href={`/reel/${p.id}`} style={{ position: 'relative', display: 'block', aspectRatio: '9 / 16', borderRadius: 10, overflow: 'hidden', background: 'var(--soft)', border: '1px solid var(--line)' }}>
                  {(p.thumbnailUrl || (p.mediaType !== 'video' && p.imageUrl)) ? <img src={(p.thumbnailUrl || p.imageUrl) as string} alt="" loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} /> : (p.mediaType === 'video' && p.imageUrl ? <VideoPoster src={p.imageUrl} /> : null)}
                  <span style={{ position: 'absolute', left: 0, right: 0, bottom: 0, padding: '14px 6px 5px', fontSize: '.64rem', color: '#fff', background: 'linear-gradient(transparent, rgba(0,0,0,.7))', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.authorName}</span>
                </a>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  )
}

function SavedPostsModal({ uid, onClose }: any) {
  const [items, setItems] = useState<any[] | null>(null)
  const [err, setErr] = useState('')
  useEffect(() => {
    let alive = true
    getDocs(query(collection(firestore, 'users', uid, 'savedPosts'), orderBy('savedAt', 'desc'), limit(60)))
      .then((snap: any) => { if (alive) setItems(snap.docs.map((d: any) => ({ id: d.id, ...d.data() }))) })
      .catch(() => { if (alive) { setErr('Could not load your saved items. Try again.'); setItems([]) } })
    return () => { alive = false }
  }, [uid])
  const remove = async (id: string) => {
    setItems((prev) => (prev || []).filter((x) => x.id !== id))
    try { await deleteDoc(doc(firestore, 'users', uid, 'savedPosts', id)) } catch { /* it comes back on next open */ }
  }
  return (
    <div className="modal-backdrop fp-backdrop">
      <div className="modal-card fp-card">
        <header className="fp-header">
          <button type="button" className="icon-button fp-back" onClick={onClose} aria-label="Back"><ArrowLeft size={20} /></button>
          <h2 className="fp-title">Saved</h2>
        </header>
        {items === null ? (
          <div style={{ padding: '2rem 1rem', textAlign: 'center', color: 'var(--muted)', fontSize: '.85rem' }}>Loading…</div>
        ) : !items.length ? (
          <div style={{ textAlign: 'center', display: 'grid', gap: 8, justifyItems: 'center', padding: '2rem 1rem', color: 'var(--muted)' }}>
            <Bookmark size={30} />
            <strong style={{ fontSize: '.92rem', color: 'var(--ink)' }}>{err ? 'Something went wrong' : 'Nothing saved yet'}</strong>
            <span style={{ fontSize: '.78rem' }}>{err || 'Tap ⋯ on a reel and choose Save to keep it here.'}</span>
          </div>
        ) : (
          <div style={{ overflowY: 'auto', display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 6 }}>
            {items.map((it) => (
              <div key={it.id} style={{ position: 'relative', aspectRatio: '9 / 16', borderRadius: 10, overflow: 'hidden', background: 'var(--soft)', border: '1px solid var(--line)' }}>
                <a href={`/reel/${it.id}`} style={{ position: 'absolute', inset: 0, display: 'block' }} aria-label={it.caption || 'Open saved reel'}>
                  {it.thumb ? <img src={resolveMediaUrl(it.thumb) as string} alt="" loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} /> : null}
                  <span style={{ position: 'absolute', left: 0, right: 0, bottom: 0, padding: '14px 6px 5px', fontSize: '.64rem', color: '#fff', background: 'linear-gradient(transparent, rgba(0,0,0,.7))', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{it.authorName || it.caption || ''}</span>
                </a>
                <button type="button" onClick={() => void remove(it.id)} aria-label="Remove from saved" style={{ position: 'absolute', top: 4, right: 4, width: 26, height: 26, borderRadius: '50%', display: 'grid', placeItems: 'center', background: 'rgba(0,0,0,.55)', color: '#fff' }}><BookmarkCheck size={14} /></button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

function BlockedAccountsModal({ entries, busy, onClose, onUndo, onOpenProfile }: any) {
  const LABEL: Record<BlockMode, { title: string; undo: string }> = {
    block: { title: 'Blocked', undo: 'Unblock' },
    restrict: { title: 'Restricted', undo: 'Unrestrict' },
    hide: { title: 'Posts hidden', undo: 'Show again' },
  }
  return (
    <div className="modal-backdrop fp-backdrop">
      <div className="modal-card fp-card">
        <header className="fp-header">
          <button type="button" className="icon-button fp-back" onClick={onClose} disabled={busy} aria-label="Back"><ArrowLeft size={20} /></button>
          <h2 className="fp-title">Blocked &amp; restricted</h2>
        </header>
        {!entries.length ? (
          <div style={{ textAlign: 'center', display: 'grid', gap: 8, justifyItems: 'center', padding: '2rem 1rem', color: 'var(--muted)' }}>
            <UserX size={30} />
            <strong style={{ fontSize: '.92rem', color: 'var(--ink)' }}>Nobody here</strong>
            <span style={{ fontSize: '.78rem' }}>Use the ⋯ menu on someone&apos;s profile to block, restrict or hide them.</span>
          </div>
        ) : (
          <div style={{ overflowY: 'auto', display: 'grid', gap: '0.5rem' }}>
            {entries.map((e: BlockEntry) => (
              <div key={e.uid} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.5rem 0.75rem', borderRadius: 14, border: '1px solid var(--line)', background: 'var(--soft)' }}>
                <button type="button" onClick={() => onOpenProfile(e.uid)} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flex: 1, minWidth: 0, background: 'transparent', textAlign: 'left' }}>
                  <Avatar src={e.avatar || fallbackAvatarSvg(e.name || '?')} name={e.name || e.username || '?'} size="sm" uid={e.uid} showStatus={false} />
                  <span style={{ minWidth: 0 }}>
                    <strong style={{ display: 'block', fontSize: '.84rem', color: 'var(--ink)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.name || `@${e.username}`}</strong>
                    <small style={{ fontSize: '.7rem', color: 'var(--muted)' }}>{e.username ? `@${e.username} · ` : ''}{LABEL[e.mode].title}</small>
                  </span>
                </button>
                <button className="text-button" disabled={busy} onClick={() => onUndo(e)}>{LABEL[e.mode].undo}</button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

function FollowListModal({ kind, name, items, loading, onClose, onViewProfile }: any) {
  // Same full-screen page layout as "Allies" (FriendsListModal): back + title + count, search, edge-to-edge rows with dividers.
  const [q, setQ] = useState('')
  const needle = q.trim().replace(/^@/, '').toLowerCase()
  const shown: Profile[] = needle
    ? (items as Profile[]).filter((it) => `${it.name || ''} ${it.username || ''}`.toLowerCase().includes(needle))
    : items
  const title = kind === 'followers' ? `${name}'s Allies` : `${name}'s Linked`
  return (
    <div className="fs-page" role="dialog" aria-modal="true" aria-label={title}>
      <header className="fs-top">
        <button className="icon-button" onClick={onClose} aria-label="Back"><ArrowLeft size={18} /></button>
        <h2>{title}{!loading && items.length > 0 && <span className="fs-count">{items.length}</span>}</h2>
      </header>
      <div className="fs-body">
        {loading ? (
          <SkeletonRows count={6} avatar />
        ) : !items.length ? (
          <div className="fs-empty">
            <Users size={30} />
            <p>{kind === 'followers' ? `No allies yet. People who link with ${name} will show up here.` : `No linked accounts yet. Accounts ${name} links with will show up here.`}</p>
          </div>
        ) : (
          <>
            {items.length > 6 && (
              <div className="fs-search">
                <Search size={16} />
                <input
                  type="text"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder={kind === 'followers' ? 'Search allies' : 'Search linked'}
                  aria-label="Search this list"
                  autoComplete="off"
                  autoCapitalize="none"
                  spellCheck={false}
                />
                {q && <button type="button" onClick={() => setQ('')} aria-label="Clear search"><X size={14} /></button>}
              </div>
            )}
            {shown.map((item: Profile) => (
              <div
                key={item.uid}
                className="fs-row is-tap"
                role="button"
                tabIndex={0}
                onClick={() => { onViewProfile(item.uid); onClose() }}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onViewProfile(item.uid); onClose() } }}
              >
                <Avatar src={item.avatar} name={item.name} size="md" uid={item.uid} showStatus={false} />
                <div className="fs-row-main">
                  <strong>{item.name}</strong>
                  <small>@{item.username}</small>
                </div>
                <ChevronRight size={18} color="var(--muted)" />
              </div>
            ))}
            {!shown.length && (
              <div className="fs-empty"><Search size={30} /><p>{`Nobody in this list matches "${q.trim()}".`}</p></div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

function ForwardPickerModal({ chats, busy, onClose, onForward }: any) {
  return (
    <div className="modal-backdrop fp-backdrop">
      <div className="modal-card fp-card">
        <header className="fp-header">
          <button type="button" className="icon-button fp-back" onClick={onClose} aria-label="Back"><ArrowLeft size={20} /></button>
          <h2 className="fp-title">Relay to…</h2>
        </header>
        <div className="chat-list">
          {chats.map((c: Chat) => (
            <button key={c.id} type="button" className="chat-row" disabled={busy} onClick={() => onForward(c)}>
              <Avatar src={c.avatar} name={c.name} size="sm" uid={c.kind === 'group' ? undefined : c.id} showStatus={false} />
              <div className="chat-copy">
                <strong>{c.name}</strong>
                <small>{c.kind === 'group' ? c.handle : 'Direct'}</small>
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  )
}

function GroupInfoModal({ chat, currentUserId, memberProfiles, presenceMap = {}, nowTick = 0, friendsNotInGroup, loading, busy, memberLimit, slotPriceCoins, slotShortCoins, slotIncrement, buyingSlot, onBuySlot, onClose, onRename, onChangePhoto, onPromote, onKick, onAddMember, onLeaveGroup, joinRequests = [], onSetPrivacy, onSetJoinMode, onApproveRequest, onDeclineRequest }: any) {
  const isAdmin = (chat.admins || []).includes(currentUserId)
  const isPublic = chat.privacy === 'public'
  const [reqBusy, setReqBusy] = useState<string | null>(null)
  const [name, setName] = useState(chat.name)
  const [editing, setEditing] = useState(false)
  const [showAdd, setShowAdd] = useState(false)
  // Online / offline text next to every member (you are always "Online" while this screen is open).
  const memberStatus = (uid: string): { online: boolean; text: string } => {
    if (uid === currentUserId) return { online: true, text: 'Online' }
    const seen = presenceMap[uid]
    if (seen && nowTick - seen < PRESENCE_ONLINE_MS) return { online: true, text: 'Online' }
    if (seen) return { online: false, text: `Offline · last seen ${formatTime(new Date(seen))}` }
    return { online: false, text: 'Offline' }
  }
  const onlineMembers = (chat.members || []).filter((uid: string) => memberStatus(uid).online).length

  return (
    <div className="modal-backdrop fp-backdrop">
      <div className="modal-card fp-card">
        <header className="fp-header">
          <button type="button" className="icon-button fp-back" onClick={onClose} aria-label="Back"><ArrowLeft size={20} /></button>
          <h2 className="fp-title">Guild Channel</h2>
        </header>
        <div style={{ display: 'grid', gap: '1rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <Avatar src={chat.avatar} name={chat.name} size="lg" showStatus={false} />
            {isAdmin && (
              <label className="text-button primary" style={{ cursor: 'pointer' }}>
                Change Flag
                <input type="file" accept="image/*" style={{ display: 'none' }} onChange={(e) => { const f = e.target.files?.[0]; if (f) onChangePhoto(f) }} />
              </label>
            )}
          </div>

          {editing ? (
            <form style={{ display: 'flex', gap: '0.5rem' }} onSubmit={(e) => { e.preventDefault(); onRename(name); setEditing(false) }}>
              <input value={name} onChange={(e) => setName(e.target.value)} maxLength={40} style={{ flex: 1, border: '1px solid var(--line)', borderRadius: 12, padding: '0.5rem', background: 'var(--soft)', color: 'var(--ink)' }} />
              <button className="primary-button" style={{ minHeight: 36 }}>Save</button>
            </form>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <strong style={{ fontSize: '1.1rem', color: 'var(--ink)' }}>{chat.name}</strong>
              {isAdmin && <button className="text-button" onClick={() => setEditing(true)}><Edit3 size={14} /> Edit</button>}
            </div>
          )}

          {isAdmin ? (
            <div style={{ display: 'grid', gap: '0.5rem' }}>
              <p className="eyebrow" style={{ margin: 0 }}>GUILD TYPE</p>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <GuildChoice active={!isPublic} icon={<Lock size={14} />} title="Private" hint="Only people you add" onClick={() => { if (isPublic) onSetPrivacy('private') }} />
                <GuildChoice active={isPublic} icon={<Globe size={14} />} title="Public" hint="Listed in Public Guilds" onClick={() => { if (!isPublic) onSetPrivacy('public') }} />
              </div>
              {isPublic && (
                <>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <GuildChoice active={chat.joinMode !== 'auto'} icon={<ShieldCheck size={14} />} title="Manual approval" hint="You accept each request" onClick={() => { if (chat.joinMode === 'auto') onSetJoinMode('manual') }} />
                    <GuildChoice active={chat.joinMode === 'auto'} icon={<Check size={14} />} title="Auto approval" hint="Anyone joins instantly" onClick={() => { if (chat.joinMode !== 'auto') onSetJoinMode('auto') }} />
                  </div>
                  <small style={{ fontSize: '.7rem', color: 'var(--muted)' }}>
                    {chat.joinMode === 'auto' ? 'Anyone who finds this guild can join without asking.' : 'People send a request and you decide who gets in.'}
                  </small>
                </>
              )}
            </div>
          ) : isPublic ? (
            <small style={{ fontSize: '.74rem', color: 'var(--muted)', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <Globe size={13} /> Public guild · {chat.joinMode === 'auto' ? 'anyone can join instantly' : 'an admin approves new members'}
            </small>
          ) : null}

          {isAdmin && isPublic && joinRequests.length > 0 && (
            <div>
              <p className="eyebrow" style={{ marginBottom: '0.5rem' }}>JOIN REQUESTS ({joinRequests.length})</p>
              <div style={{ display: 'grid', gap: '0.5rem' }}>
                {joinRequests.map((r: GroupJoinRequest) => (
                  <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.5rem 0', }}>
                    <Avatar src={r.avatar || fallbackAvatarSvg(r.name)} name={r.name} size="sm" uid={r.uid} showStatus={false} />
                    <div className="chat-copy">
                      <strong>{r.name}</strong>
                      {r.username && <small>@{r.username}</small>}
                    </div>
                    <button
                      type="button"
                      className="icon-button"
                      style={{ border: 0 }}
                      disabled={busy || reqBusy === r.id}
                      title="Accept"
                      aria-label={`Accept ${r.name}`}
                      onClick={async () => { setReqBusy(r.id); try { await onApproveRequest(r) } finally { setReqBusy(null) } }}
                    ><Check size={18} color="var(--cyan)" /></button>
                    <button
                      type="button"
                      className="icon-button"
                      style={{ border: 0 }}
                      disabled={busy || reqBusy === r.id}
                      title="Decline"
                      aria-label={`Decline ${r.name}`}
                      onClick={async () => { setReqBusy(r.id); try { await onDeclineRequest(r) } finally { setReqBusy(null) } }}
                    ><X size={18} color="var(--danger)" /></button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
              <p className="eyebrow">MEMBERS ({chat.members?.length || 0} / {memberLimit}){onlineMembers > 0 && <span style={{ color: 'var(--online-green, #34d399)', marginLeft: 8 }}>● {onlineMembers} online</span>}</p>
              {isAdmin && <button className="text-button primary" onClick={() => setShowAdd(!showAdd)}><Plus size={14} /> Add</button>}
            </div>

            {isAdmin && typeof memberLimit === 'number' && (chat.members?.length || 0) >= memberLimit - 5 && (
              <>
              <button
                type="button"
                className="text-button primary"
                disabled={busy || buyingSlot}
                onClick={onBuySlot}
                style={{ marginBottom: '0.75rem', justifyContent: 'center' }}
              >
                <Plus size={14} /> {buyingSlot ? 'Adding slots…' : `+${slotIncrement} more members (${slotPriceCoins} coins)`}
              </button>
              {typeof slotShortCoins === 'number' && slotShortCoins > 0 && <div style={{ marginBottom: '0.75rem' }}><CoinShortAd needCoins={slotShortCoins} /></div>}
              </>
            )}

            {showAdd && (
              <div className="picker-list" style={{ marginBottom: '0.75rem', maxHeight: 180, overflowY: 'auto' }}>
                {friendsNotInGroup.map((f: Chat) => (
                  <button key={f.id} type="button" className="picker-row" onClick={() => onAddMember(f.id)}>
                    <Avatar src={f.avatar} name={f.name} size="sm" uid={f.kind === 'group' ? undefined : f.id} showStatus={false} />
                    <div className="picker-row-info">
                      <strong>{f.name}</strong>
                      <small>{f.handle}</small>
                    </div>
                    <Plus size={16} color="var(--cyan)" />
                  </button>
                ))}
              </div>
            )}

            <div style={{ display: 'grid', gap: '0.5rem' }}>
              {memberProfiles.map((m: Profile) => {
                const isMemAdmin = (chat.admins || []).includes(m.uid)
                return (
                  <div key={m.uid} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.5rem 0', }}>
                    <Avatar src={m.avatar} name={m.name} size="sm" uid={m.uid} showStatus={false} />
                    <div className="chat-copy">
                      <strong>{m.name}</strong>
                      <small>@{m.username}</small>
                      {(() => {
                        const st = memberStatus(m.uid)
                        return <small style={{ display: 'block', marginTop: 2, fontWeight: 600, color: st.online ? 'var(--online-green, #34d399)' : 'var(--muted)' }}>{st.online ? '● ' : ''}{st.text}</small>
                      })()}
                    </div>
                    {isMemAdmin && <span style={{ fontSize: '.64rem', padding: '2px 8px', borderRadius: 999, background: 'var(--accent-soft)', color: 'var(--accent-ink)', fontWeight: 700 }}>ADMIN</span>}
                    {isAdmin && m.uid !== currentUserId && !isMemAdmin && (
                      <button className="icon-button" style={{ border: 0 }} onClick={() => onPromote(m.uid)} title="Promote Admin"><Crown size={16} color="var(--gold)" /></button>
                    )}
                    {isAdmin && m.uid !== currentUserId && (
                      <button className="icon-button" style={{ border: 0 }} onClick={() => onKick(m.uid)} title="Eject"><UserMinus size={16} color="var(--danger)" /></button>
                    )}
                  </div>
                )
              })}
            </div>
          </div>

          <button className="text-button danger" style={{ justifyContent: 'center' }} onClick={onLeaveGroup}>
            <UserMinus size={16} /> Leave Group Channel
          </button>
        </div>
      </div>
    </div>
  )
}




// ---------------------------------------------------------------------------
// Home theme picker: 6 free + 6 premium dark themes (flat colours, no pictures).
// ---------------------------------------------------------------------------
function HomeThemePicker({ current, isPremium, onPick, onGetPremium, onClose }: { current: string; isPremium: boolean; onPick: (id: string) => void; onGetPremium: () => void; onClose: () => void }) {
  const cur = resolveHomeThemeId(current)
  const tile = (t: HomeTheme) => {
    const locked = t.premium && !isPremium
    const active = cur === t.id
    return (
      <button
        key={t.id}
        type="button"
        onClick={() => (locked ? onGetPremium() : onPick(t.id))}
        style={{ border: active ? `2px solid ${t.a}` : '1px solid var(--line)', borderRadius: 14, padding: 0, overflow: 'hidden', background: 'var(--paper)', textAlign: 'left', cursor: 'pointer', position: 'relative' }}
      >
        <div style={{ height: 84, backgroundColor: t.bg, position: 'relative', padding: 8, display: 'flex', flexDirection: 'column', gap: 5 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 5, background: t.paper, borderRadius: 7, padding: '4px 5px' }}>
            <span style={{ width: 12, height: 12, borderRadius: '50%', background: t.a, flexShrink: 0 }} />
            <span style={{ display: 'grid', gap: 3, flex: 1 }}>
              <span style={{ height: 4, width: '70%', borderRadius: 2, background: t.ink }} />
              <span style={{ height: 3, width: '45%', borderRadius: 2, background: t.muted }} />
            </span>
          </div>
          <div style={{ height: 16, borderRadius: 6, background: t.accent ?? t.b, color: '#fff', fontSize: 9, fontWeight: 700, display: 'grid', placeItems: 'center' }}>Aa</div>
          <div style={{ height: 10, borderRadius: 5, background: t.paper, border: `1px solid ${t.line}` }} />
        </div>
        <div style={{ padding: '8px 8px', fontSize: '.74rem', fontWeight: 600, color: 'var(--ink)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 4 }}>
          <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{t.label}</span>
          {locked && <Lock size={13} style={{ flexShrink: 0, opacity: .75 }} />}
          {active && <span style={{ flexShrink: 0, background: t.accent ?? t.b, borderRadius: 999, width: 16, height: 16, display: 'grid', placeItems: 'center' }}><Check size={11} color="#fff" /></span>}
        </div>
      </button>
    )
  }
  const grid: React.CSSProperties = { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }
  const head: React.CSSProperties = { fontSize: '.72rem', letterSpacing: '.08em', textTransform: 'uppercase', color: 'var(--muted)', fontWeight: 700, margin: '16px 2px 8px' }
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 400, background: 'rgba(0,0,0,.5)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
      <div onClick={(e) => e.stopPropagation()} style={{ width: '100%', maxWidth: 460, maxHeight: '82dvh', overflowY: 'auto', background: 'var(--paper)', color: 'var(--ink)', borderRadius: '16px 16px 0 0', padding: '16px 16px 24px', boxShadow: 'var(--shadow-pop)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <strong style={{ fontSize: '1rem', display: 'flex', alignItems: 'center', gap: 8 }}><Palette size={18} /> App theme</strong>
          <button type="button" onClick={onClose} className="icon-button" aria-label="Close" style={{ width: 32, height: 32 }}><X size={16} /></button>
        </div>
        <div style={head}>Free</div>
        <div style={grid}>{HOME_THEMES.filter((t) => !t.premium).map(tile)}</div>
        <div style={{ ...head, display: 'flex', alignItems: 'center', gap: 8 }}><Crown size={14} /> Premium {!isPremium && <span style={{ textTransform: 'none', letterSpacing: 0, fontWeight: 500 }}>- tap to unlock</span>}</div>
        <div style={grid}>{HOME_THEMES.filter((t) => t.premium).map(tile)}</div>
      </div>
    </div>
  )
}


/* Calm loading placeholder: grey rows with a quiet pulse (replaces spinners / "Loading..." text). */
function SkeletonRows({ count = 3, avatar = false }: { count?: number; avatar?: boolean }) {
  return (
    <div className="sk-list" role="status" aria-label="Loading">
      {Array.from({ length: count }).map((_, i) => (
        <div className="sk-row" key={i}>
          {avatar && <span className="sk sk-avatar" />}
          <span className="sk-lines">
            <span className="sk sk-line" style={{ width: `${62 - (i % 3) * 10}%` }} />
            <span className="sk sk-line sk-line-sm" style={{ width: `${38 + (i % 2) * 14}%` }} />
          </span>
        </div>
      ))}
    </div>
  )
}

/* Restarts a 150ms fade on the tab container whenever the active tab changes. */
const TAB_ORDER = ['home', 'reels', 'contacts', 'profile', 'settings']
function fadeOnTabChange(el: HTMLElement | null, tabKey: string) {
  if (!el) return
  if (el.dataset.fadeTab === tabKey) return
  const first = el.dataset.fadeTab === undefined
  const prevTab = el.dataset.fadeTab || tabKey
  el.dataset.fadeTab = tabKey
  if (first) return
  // content eases in from the side the new tab sits on (right tab -> from right, left tab -> from left)
  el.style.setProperty('--tab-dx', `${Math.sign(TAB_ORDER.indexOf(tabKey) - TAB_ORDER.indexOf(prevTab)) * 16}px`)
  el.classList.remove('tab-fade')
  void el.offsetWidth
  el.classList.add('tab-fade')
}
